import React, { useState, useEffect, useRef, useMemo, Fragment } from "react";

/* ── SUPABASE LITE CLIENT (no SDK — pure fetch) ── */
const SUPABASE_URL = "https://eygfkoonconsfpogayea.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV5Z2Zrb29uY29uc2Zwb2dheWVhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM5MjAzOTQsImV4cCI6MjA4OTQ5NjM5NH0.--dFiSt7HaySKmRjQ4VwEQAWIXiuY2jjySakeKJEnwU";
// All AI calls go through this proxy so the Anthropic key stays server-side
const AI_PROXY_URL = `${SUPABASE_URL}/functions/v1/ai-proxy`;
const aiHeaders = () => ({ "Content-Type": "application/json", "apikey": SUPABASE_KEY });

// App version — shown in the footer ("Lancer by MMetrix" + version) and on the About page.
// Bump this when releasing a new build, and add a matching entry to APP_CHANGELOG (see the
// AboutPage component) so users see what changed in the "Latest Updates" tab.
const APP_VERSION = "1.0.1";

let _accessToken = null; // updated on sign-in
let _refreshToken = null;
// Detect password recovery mode synchronously at module load (before any React effects)
// Supports both implicit flow (hash: #access_token=...&type=recovery) and PKCE flow (query: ?code=...)
const _recoveryHash = window.location.hash && window.location.hash.includes("type=recovery") ? window.location.hash : null;
const _recoveryCode = new URLSearchParams(window.location.search).get("code");
let _recoveryMode = !!_recoveryHash || !!_recoveryCode;

// ── Persist tokens to localStorage ──
const _saveTokens = () => {
  try {
    if (_accessToken) localStorage.setItem("lancer_at", _accessToken);
    else localStorage.removeItem("lancer_at");
    if (_refreshToken) localStorage.setItem("lancer_rt", _refreshToken);
    else localStorage.removeItem("lancer_rt");
  } catch(e) {}
};
const _loadTokens = () => {
  try {
    _accessToken = localStorage.getItem("lancer_at") || null;
    _refreshToken = localStorage.getItem("lancer_rt") || null;
  } catch(e) {}
};
_loadTokens(); // restore on load

// Deduplicated refresh — prevents race conditions where concurrent callers
// both try to use the same refresh token (Supabase rotation invalidates the old one,
// causing the second request to get invalid_grant and log the user out).
let _refreshPromise = null;
let _refreshFailCount = 0;
const _refreshSession = () => {
  if (!_refreshToken) return Promise.resolve(false);
  // If a refresh is already in flight, piggyback on it instead of sending a duplicate
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
        body: JSON.stringify({ refresh_token: _refreshToken }),
      });
      const d = await r.json();
      if (d.access_token) {
        _accessToken = d.access_token;
        _refreshToken = d.refresh_token;
        _saveTokens();
        _refreshFailCount = 0;
        return true;
      }
      // Only clear tokens after multiple consecutive definitive failures
      // (a single invalid_grant can happen from a stale duplicate request)
      if (d.error === "invalid_grant" || d.error_description?.includes("expired") || d.error_description?.includes("revoked")) {
        _refreshFailCount++;
        if (_refreshFailCount >= 3) {
          console.warn("[Auth] Refresh failed 3 times, clearing session");
          _accessToken = null; _refreshToken = null; _saveTokens();
        } else {
          console.warn("[Auth] Refresh failed (" + _refreshFailCount + "/3), will retry");
        }
      }
      return false;
    } catch(e) {
      // Network error — do NOT clear tokens, user may regain connectivity
      console.warn("[Auth] Token refresh network error, will retry:", e.message);
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  "apikey": SUPABASE_KEY,
  "Authorization": `Bearer ${_accessToken || SUPABASE_KEY}`,
});

// ── AUTH ──
const sbAuth = {
  async signUp(email, password, name) {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ email, password, data: { name } }),
    });
    const d = await r.json();
    if (d.access_token) { _accessToken = d.access_token; _refreshToken = d.refresh_token; _saveTokens(); }
    return { data: d, error: d.error ? { message: d.msg || d.error_description || d.error } : null };
  },
  async signInWithPassword(email, password) {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json();
    if (d.access_token) {
      _accessToken = d.access_token; _refreshToken = d.refresh_token; _saveTokens();
      // Fetch full user object with fresh token
      const ur = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: authHeaders() });
      const u = await ur.json();
      const user = u.id ? u : (d.user || { id: d.sub || d.id, email: email, user_metadata: d.user_metadata || {} });
      return { data: { user, session: d }, error: null };
    }
    return { data: { user: null, session: null }, error: d.error ? { message: d.error_description || d.error } : { message: "Email or password is incorrect." } };
  },
  async signOut() {
    await fetch(`${SUPABASE_URL}/auth/v1/logout`, { method: "POST", headers: authHeaders() });
    _accessToken = null; _refreshToken = null; _saveTokens();
    return { error: null };
  },
  async getUser() {
    if (!_accessToken && _refreshToken) await _refreshSession();
    if (!_accessToken) return { data: { user: null } };
    const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: authHeaders() });
    if (r.status === 401 && _refreshToken) {
      const refreshed = await _refreshSession();
      if (refreshed) {
        const r2 = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: authHeaders() });
        const d2 = await r2.json();
        return { data: { user: d2.id ? d2 : null } };
      }
    }
    const d = await r.json();
    return { data: { user: d.id ? d : null } };
  },
  async getSession() {
    if (!_accessToken && _refreshToken) await _refreshSession();
    if (!_accessToken) return { data: { session: null } };
    // Fetch user so callers can use session.user
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: authHeaders() });
      if (r.status === 401 && _refreshToken) {
        const refreshed = await _refreshSession();
        if (refreshed) {
          const r2 = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: authHeaders() });
          const d2 = await r2.json();
          return { data: { session: d2.id ? { access_token: _accessToken, user: d2 } : null } };
        }
        return { data: { session: null } };
      }
      const d = await r.json();
      return { data: { session: d.id ? { access_token: _accessToken, user: d } : null } };
    } catch(e) {
      return { data: { session: { access_token: _accessToken } } };
    }
  },
  onAuthStateChange(cb) {
    // Simplified: no real-time listener in lite client
    return { data: { subscription: { unsubscribe: () => {} } } };
  },
};

// ── REST (PostgREST) ──
const sbFrom = (table) => ({
  _table: table, _filters: [], _select: "*", _order: null, _single: false, _limit: null,
  _method: "GET", _body: null, _upsert: false,
  // ── Chainable (sync) builders ──
  select(cols) { return { ...this, _select: cols }; },
  eq(col, val) { return { ...this, _filters: [...this._filters, `${col}=eq.${val}`] }; },
  order(col, opts) { return { ...this, _order: `${col}${opts?.ascending===false?'.desc':'.asc'}` }; },
  single() { return { ...this, _single: true }; },
  limit(n) { return { ...this, _limit: n }; },
  // Lazy write builders — do NOT execute immediately
  update(body) { return { ...this, _method: "PATCH", _body: body }; },
  delete() { return { ...this, _method: "DELETE" }; },
  insert(body) { return { ...this, _method: "POST", _body: body }; },
  upsert(body) { return { ...this, _method: "POST", _body: body, _upsert: true }; },
  // ── Terminal: actually runs the request ──
  async _exec(_retried) {
    const method = this._method;
    let url = `${SUPABASE_URL}/rest/v1/${this._table}`;
    const params = [...this._filters];
    if (method === "GET") {
      params.unshift(`select=${this._select}`);
      if (this._order) params.push(`order=${this._order}`);
      if (this._limit) params.push(`limit=${this._limit}`);
    }
    if (params.length > 0) url += "?" + params.join("&");
    const hdrs = { ...authHeaders() };
    if (method !== "DELETE") hdrs["Prefer"] = this._upsert ? "resolution=merge-duplicates,return=representation" : "return=representation";
    if (this._single) hdrs["Accept"] = "application/vnd.pgrst.object+json";
    const r = await fetch(url, {
      method,
      headers: hdrs,
      body: this._body ? JSON.stringify(this._body) : undefined,
    });
    // On 401/403, try refreshing the token once and retry
    if ((r.status === 401 || r.status === 403) && !_retried && _refreshToken) {
      await _refreshSession();
      return this._exec(true);
    }
    const text = await r.text();
    let d; try { d = JSON.parse(text); } catch { d = null; }
    const error = r.ok ? null : { message: d?.message || d?.msg || text };
    return { data: error ? null : d, error };
  },
  // then() and catch() make the object thenable — await / .then() / .catch() triggers execution
  then(resolve, reject) {
    return this._exec().then(resolve, reject);
  },
  catch(reject) {
    return this._exec().then(undefined, reject);
  },
});

// ── STORAGE ──
const sbStorage = {
  from(bucket) {
    return {
      async upload(path, file, opts) {
        const form = new FormData(); form.append("", file);
        const r = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`, {
          method: opts?.upsert ? "PUT" : "POST",
          headers: { "apikey": SUPABASE_KEY, "Authorization": `Bearer ${_accessToken || SUPABASE_KEY}` },
          body: form,
        });
        return r.ok ? { error: null } : { error: { message: await r.text() } };
      },
      getPublicUrl(path) {
        return { data: { publicUrl: `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}` } };
      },
    };
  },
};

// ── RPC ──
const sbRpc = async (fn, params, _retried) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify(params),
  });
  // On 401/403, refresh token once and retry (matches sbFrom behavior)
  if ((r.status === 401 || r.status === 403) && !_retried && _refreshToken) {
    await _refreshSession();
    return sbRpc(fn, params, true);
  }
  // RPCs that RETURN void produce an empty body; r.json() would throw
  // "Unexpected end of JSON input" on those. Read as text first, then
  // parse only if there's something to parse.
  const txt = await r.text();
  let d = null;
  if (txt) { try { d = JSON.parse(txt); } catch(_) { /* non-JSON body — treat as raw */ } }
  return { data: d, error: r.ok ? null : { message: (d && d.message) || txt || ("HTTP "+r.status) } };
};

// ── REALTIME (Supabase Realtime via WebSocket / Phoenix Channels) ──
function createRealtimeSubscription(tables, onChange) {
  let ws = null;
  let heartbeatTimer = null;
  let ref = 0;
  let reconnectTimer = null;
  let stopped = false;

  const connect = () => {
    if (stopped) return;
    const token = _accessToken || SUPABASE_KEY;
    const url = SUPABASE_URL.replace("https://","wss://") + "/realtime/v1/websocket?apikey=" + SUPABASE_KEY + "&vsn=1.0.0";
    try { ws = new WebSocket(url); } catch(e) { scheduleReconnect(); return; }

    ws.onopen = () => {
      // Set auth token for RLS
      ref++;
      ws.send(JSON.stringify({
        topic: "realtime:system",
        event: "access_token",
        payload: { access_token: token },
        ref: String(ref),
      }));

      // Subscribe to a single channel with all postgres_changes
      ref++;
      ws.send(JSON.stringify({
        topic: "realtime:lancer-changes",
        event: "phx_join",
        payload: {
          config: {
            postgres_changes: tables.map(table => ({ event: "*", schema: "public", table }))
          },
          access_token: token,
        },
        ref: String(ref),
      }));

      // Heartbeat every 30s to keep connection alive
      heartbeatTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ref++;
          ws.send(JSON.stringify({ topic: "phoenix", event: "heartbeat", payload: {}, ref: String(ref) }));
        }
      }, 30000);
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        // Supabase sends postgres_changes events with this event name
        if (msg.event === "postgres_changes" || msg.event === "INSERT" || msg.event === "UPDATE" || msg.event === "DELETE") {
          onChange(msg);
        }
      } catch(e) {}
    };

    ws.onclose = () => { cleanup(); scheduleReconnect(); };
    ws.onerror = () => { cleanup(); scheduleReconnect(); };
  };

  const cleanup = () => {
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
  };

  const scheduleReconnect = () => {
    if (stopped) return;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, 3000);
  };

  connect();

  return {
    close() {
      stopped = true;
      cleanup();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch(e) {} }
    }
  };
}

// ── Unified sb object (mirrors Supabase JS SDK shape) ──
const sb = {
  auth: sbAuth,
  from: sbFrom,
  storage: sbStorage,
  rpc: sbRpc,
};


/* ── STRIPE.JS LOADER (for production payment confirmation) ──
   In production React Native / Capacitor app, replace with @stripe/stripe-react-native
   or @stripe/stripe-js. This lazy-loads Stripe.js only when a payment is needed.        */
let _stripe = null;
const loadStripe = async (publishableKey) => {
  if (_stripe) return _stripe;
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.onload = () => { _stripe = window.Stripe(publishableKey); resolve(_stripe); };
    document.head.appendChild(script);
  });
};
// Replace with your Stripe Publishable Key (pk_live_xxx or pk_test_xxx)
const STRIPE_PUBLISHABLE_KEY = "pk_live_51TCeLMDoInHKinDDsbZqVBCZLNAchMnILngOl8VWAq8t4BbwQlmDKjazFJjeHgtvATBqY6GlRDDgbnanXxkXIDQ7008BzV56G9";


// Auth
const dbSignUp = (email, password, name) =>
  sb.auth.signUp(email, password, name);

const dbSignIn = (email, password) =>
  sb.auth.signInWithPassword(email, password);

const dbSignOut = () => sb.auth.signOut();

const dbDeleteAccount = async () => {
  // The RPC RETURNS void, so PostgREST replies with HTTP 200 and an empty body.
  // Don't go through sbRpc here — it has historically called r.json() which
  // throws "Unexpected end of JSON input" on empty bodies and would surface a
  // misleading "couldn't delete" alert even after the row was already gone.
  // Inline the fetch and read the body as text instead.
  const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/delete_own_account`, {
    method: "POST",
    headers: authHeaders(),
    body: "{}",
  });
  if (!r.ok) {
    let msg = "HTTP " + r.status;
    try {
      const txt = await r.text();
      if (txt) {
        try { msg = (JSON.parse(txt).message) || txt; } catch { msg = txt; }
      }
    } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  // Drain the (likely empty) body so the connection cleans up.
  try { await r.text(); } catch (_) { /* ignore */ }
  // Sign out locally — the server session is dead now.
  _accessToken = null; _refreshToken = null; _saveTokens();
};

const dbGetSession = () => sb.auth.getSession();

// Profile
const dbGetProfile = (userId) =>
  sb.from("profiles").select("*").eq("id", userId).single();

const dbUpsertProfile = (userId, fields) =>
  sb.from("profiles").upsert({ id: userId, ...fields, updated_at: new Date().toISOString() })._exec();

// Update existing profile (PATCH) — avoids NOT-NULL constraint errors that upsert hits
// when only sending partial fields (e.g. questionnaire_template without name/email)
const dbUpdateProfile = (userId, fields) =>
  sb.from("profiles").update({ ...fields, updated_at: new Date().toISOString() }).eq("id", userId)._exec();

// Lists every workspace the calling user is an active member of.
// Ordered: owned (personal) first, then by joined_at ASC. Used to populate
// the workspace switcher and to pick the right active workspace at login.
const dbListMyWorkspaces = async () => {
  const { data, error } = await sbRpc("studio_list_my_workspaces", {});
  return { data: Array.isArray(data) ? data : [], error };
};

const ACTIVE_WORKSPACE_KEY = "lancer_active_workspace";
const getStoredActiveWorkspace = () => {
  try { return localStorage.getItem(ACTIVE_WORKSPACE_KEY) || null; } catch { return null; }
};
const storeActiveWorkspace = (id) => {
  try { if (id) localStorage.setItem(ACTIVE_WORKSPACE_KEY, id); else localStorage.removeItem(ACTIVE_WORKSPACE_KEY); } catch {}
};

// Picks the active workspace from a list of memberships. Priority:
//   1. localStorage.lancer_active_workspace (if still in the list)
//   2. The user's owned (personal) workspace
//   3. Most recently joined
const pickActiveWorkspace = (workspaces) => {
  if (!Array.isArray(workspaces) || workspaces.length === 0) return null;
  const stored = getStoredActiveWorkspace();
  if (stored) {
    const found = workspaces.find(w => w.workspace_id === stored);
    if (found) return found;
  }
  const owned = workspaces.find(w => w.is_owner);
  if (owned) return owned;
  // Fallback: most recently joined (last in the joined_at ASC list).
  return workspaces[workspaces.length - 1];
};

// Slice D — Team management. Calls the studio_list_workspace_members RPC
// (SECURITY DEFINER) which joins workspace_members → auth.users → profiles
// while bypassing per-table RLS. The RPC enforces its own authorization
// check (caller must be an active member of the workspace).
//
// Note: this WAS a view query but PostgREST re-applied calling-role grants
// on auth.users + profiles, hiding teammates' rows. RPC sidesteps that.
const dbGetWorkspaceMembers = async (workspaceId) => {
  const { data, error } = await sbRpc("studio_list_workspace_members", { p_workspace_id: workspaceId });
  return { data: Array.isArray(data) ? data : [], error };
};

const dbGetPendingInvites = (workspaceId) =>
  sb.from("workspace_invites")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false });

// RPC helpers — these wrap the SECURITY DEFINER functions in the database.
// studio_create_invite is followed by a send-team-invite edge call so the
// invitee gets an email. The edge function ALWAYS returns accept_url so the
// inviter can copy/paste it manually if email delivery failed.
const _sendInviteEmail = async (workspaceId, email, token, expiresAt) => {
  const tok = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/send-team-invite`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
      body: JSON.stringify({
        workspace_id: workspaceId,
        email, token,
        expires_at: expiresAt,
        app_origin: window.location.origin,
      }),
    });
    return await r.json();
  } catch (e) {
    return { ok: false, email_sent: false, accept_url: `${window.location.origin}/?invite=${encodeURIComponent(token)}`, email_error: e.message };
  }
};

const studioCreateInvite = async (workspaceId, email, financialVisibility) => {
  const { data, error } = await sbRpc("studio_create_invite", {
    p_workspace_id: workspaceId,
    p_email: email,
    p_role: "member",
    p_financial_visibility: !!financialVisibility,
  });
  if (error) return { data: null, error };
  const row = Array.isArray(data) ? data[0] : data;
  // Fire the email; merge the accept_url + email_sent flag onto the returned row.
  if (row?.token) {
    const emailRes = await _sendInviteEmail(workspaceId, email, row.token, row.expires_at);
    return { data: { ...row, accept_url: emailRes.accept_url, email_sent: !!emailRes.email_sent, email_error: emailRes.email_error || null }, error: null };
  }
  return { data: row, error: null };
};

const studioRevokeInvite = (inviteId) =>
  sbRpc("studio_revoke_invite", { p_invite_id: inviteId });

const studioSetFinancialVisibility = (memberId, visible) =>
  sbRpc("studio_set_financial_visibility", { p_member_id: memberId, p_visible: !!visible });

// Phase 4 — Guest seats (project-scoped contractors).
// Returns { invite_id, token, expires_at, existing_member_id }.
// If existing_member_id is set, the email already had a guest seat in this
// workspace and was added to the project directly (no new invite, no new bill).
const studioCreateGuestInvite = async (workspaceId, email, clientId) => {
  const { data, error } = await sbRpc("studio_create_guest_invite", {
    p_workspace_id: workspaceId,
    p_email: email,
    p_client_id: clientId,
  });
  if (error) return { data: null, error };
  const row = Array.isArray(data) ? data[0] : data;
  // If the email belongs to an existing guest, no token/email needed —
  // they were just added to the project.
  if (row?.token) {
    const emailRes = await _sendInviteEmail(workspaceId, email, row.token, row.expires_at);
    return { data: { ...row, accept_url: emailRes.accept_url, email_sent: !!emailRes.email_sent, email_error: emailRes.email_error || null }, error: null };
  }
  return { data: row, error: null };
};

const studioListClientMembers = async (clientId) => {
  const { data, error } = await sbRpc("studio_list_client_members", { p_client_id: clientId });
  return { data: Array.isArray(data) ? data : [], error };
};

// Member-on-project assignment helpers. All owner-gated server-side.
const studioAddMemberToClient = (clientId, workspaceMemberId) =>
  sbRpc("studio_add_member_to_client", { p_client_id: clientId, p_workspace_member_id: workspaceMemberId });

const studioRemoveMemberFromClient = (clientId, workspaceMemberId) =>
  sbRpc("studio_remove_member_from_client", { p_client_id: clientId, p_workspace_member_id: workspaceMemberId });

const studioSetClientLead = (clientId, workspaceMemberId) =>
  sbRpc("studio_set_client_lead", { p_client_id: clientId, p_workspace_member_id: workspaceMemberId });

// Workspace-wide assignment map. Used by Per-seat analytics so a non-lead
// member still gets credit for projects they're on.
const studioListWorkspaceMemberClients = async (workspaceId) => {
  const { data, error } = await sbRpc("studio_workspace_member_clients", { p_workspace_id: workspaceId });
  return { data: Array.isArray(data) ? data : [], error };
};

// Goes through the edge function so Stripe seat/guest quantities are
// reconciled atomically with the DB write. Direct RPC would skip that.
const studioRemoveGuestFromProject = async (workspaceMemberId, clientId) => {
  const token = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-remove-guest-from-project`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ workspace_member_id: workspaceMemberId, client_id: clientId }),
    });
    const data = await r.json();
    if (!r.ok) return { data: null, error: { message: data?.error || "Could not remove guest" } };
    return { data, error: null };
  } catch (e) {
    return { data: null, error: { message: e.message } };
  }
};

// Phase 6 — Custom domain helpers (Vercel-backed). Each helper wraps an
// edge function that talks to the Vercel API + writes to workspaces.custom_domain.
const studioAddCustomDomain = async (workspaceId, domain) => {
  const tok = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-add-custom-domain`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
      body: JSON.stringify({ workspace_id: workspaceId, domain }),
    });
    const data = await r.json();
    if (!r.ok) return { error: { message: data?.error || "Could not add domain" } };
    return { data };
  } catch (e) { return { error: { message: e.message } }; }
};

const studioVerifyCustomDomain = async (workspaceId) => {
  const tok = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-verify-custom-domain`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
      body: JSON.stringify({ workspace_id: workspaceId }),
    });
    const data = await r.json();
    if (!r.ok) return { error: { message: data?.error || "Verification check failed" } };
    return { data };
  } catch (e) { return { error: { message: e.message } }; }
};

const studioRemoveCustomDomain = async (workspaceId) => {
  const tok = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-remove-custom-domain`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
      body: JSON.stringify({ workspace_id: workspaceId }),
    });
    const data = await r.json();
    if (!r.ok) return { error: { message: data?.error || "Could not remove domain" } };
    return { data };
  } catch (e) { return { error: { message: e.message } }; }
};

// Detect whether the current Host is the canonical Lancer app (vs. a
// customer's custom subdomain). Used on app boot to decide whether to
// render in portal-only mode.
const LANCER_CANONICAL_HOSTS = new Set(["lancer-app.com", "www.lancer-app.com", "localhost", "127.0.0.1"]);
const isLancerCanonicalHost = (host) => {
  if (!host) return true;
  const h = host.toLowerCase().split(":")[0];
  if (LANCER_CANONICAL_HOSTS.has(h)) return true;
  if (h.endsWith(".vercel.app")) return true;       // preview deployments
  if (h.endsWith(".lancer-app.com")) return true;   // any subdomain we own
  return false;
};

// Anonymous lookup: resolves a custom Host to the freelancer slug so the
// existing portal RPC (get_portal_data) can take over.
const dbGetWorkspaceByCustomDomain = async (host) => {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_workspace_by_custom_domain`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY, "Authorization": `Bearer ${SUPABASE_KEY}` },
      body: JSON.stringify({ p_host: host }),
    });
    const data = await r.json();
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch { return null; }
};

// Phase 11 — Stripe customer portal. Returns the hosted-portal URL the
// browser should redirect to. Customer can update card, download invoices,
// and cancel from there. We return the URL rather than redirect server-side
// so the browser keeps the JSX alive and can route back on return.
const studioOpenCustomerPortal = async () => {
  const token = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/stripe-customer-portal`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ return_url: window.location.href.split("?")[0].split("#")[0] }),
    });
    const data = await r.json();
    if (!r.ok || !data.url) return { error: { message: data?.error || "Could not open portal" } };
    return { url: data.url };
  } catch (e) { return { error: { message: e.message } }; }
};

// Phase 8 — Support tickets. Customer + admin paths share these helpers;
// authorization is enforced inside the SECURITY DEFINER RPCs.
const supportCreateTicket = (subject, body, severity) =>
  sbRpc("studio_create_ticket", { p_subject: subject, p_body: body, p_severity: severity || "medium" });
const supportListMyTickets = async () => {
  const { data, error } = await sbRpc("studio_list_my_tickets", {});
  return { data: Array.isArray(data) ? data : [], error };
};
const supportGetTicket = async (ticketId) => {
  const { data, error } = await sbRpc("studio_get_ticket", { p_ticket_id: ticketId });
  return { data: Array.isArray(data) ? data : [], error };
};
const supportPostUserReply = (ticketId, body) =>
  sbRpc("studio_post_user_reply", { p_ticket_id: ticketId, p_body: body });

// Admin
const adminListTickets = async () => {
  const { data, error } = await sbRpc("admin_list_tickets", {});
  return { data: Array.isArray(data) ? data : [], error };
};
const adminPostTicketReply = async (ticketId, body, close) => {
  const { data, error } = await sbRpc("admin_post_reply", { p_ticket_id: ticketId, p_body: body, p_close: !!close });
  if (error) return { data: null, error };
  // Fire-and-forget email notification (don't block the reply UX on email failures).
  try {
    const tok = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
    fetch(`${SUPABASE_URL}/functions/v1/send-ticket-notification`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
      body: JSON.stringify({ ticket_id: ticketId, reply_body: body, closed: !!close, app_origin: window.location.origin }),
    }).catch(()=>{});
  } catch {}
  return { data, error: null };
};
const adminCloseTicket = (ticketId) =>
  sbRpc("admin_close_ticket", { p_ticket_id: ticketId });

// SLA badge text + color
const slaBadgeForPlan = (plan) => {
  if (plan === "studio") return { label: "Priority · 1 BD SLA", color: "var(--gm)", bg: "var(--gl)" };
  if (plan === "pro") return { label: "Pro · 3 BD response", color: "var(--gm)", bg: "var(--gl)" };
  return { label: "Best effort", color: "var(--mu)", bg: "var(--gl)" };
};

// Removal goes through the edge function so Stripe seat quantity is updated
// in the same call. JSX never talks to Stripe directly.
const studioRemoveMember = async (memberId, reassignLeadTo) => {
  const token = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-remove-member`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}`, "apikey": SUPABASE_KEY },
    body: JSON.stringify({ member_id: memberId, reassign_lead_to: reassignLeadTo || null }),
  });
  const data = await r.json();
  if (!r.ok) return { data: null, error: { message: data?.error || "Remove failed" } };
  return { data, error: null };
};

const studioAcceptInvite = async (token) => {
  const accessToken = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-accept-invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}`, "apikey": SUPABASE_KEY },
    body: JSON.stringify({ token }),
  });
  const data = await r.json();
  if (!r.ok) return { data: null, error: { message: data?.error || "Accept failed" } };
  return { data, error: null };
};

// Owner-triggered Stripe seat reconciliation. Idempotent — computes target
// seat + guest quantities from current workspace_members counts and updates
// the Stripe subscription in place. Net upgrades invoice + charge immediately
// via proration_behavior='always_invoice'; net downgrades credit the next
// invoice via 'create_prorations'. See supabase/functions/studio-resync-seats.
const studioResyncSeats = async () => {
  const accessToken = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/studio-resync-seats`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}`, "apikey": SUPABASE_KEY },
      body: JSON.stringify({}),
    });
    const data = await r.json();
    if (!r.ok) return { data: null, error: { message: data?.error || `Resync failed (HTTP ${r.status})` } };
    return { data, error: null };
  } catch (e) {
    return { data: null, error: { message: e.message || "Resync failed" } };
  }
};

// Sends the invite email (and returns the accept_url as a fallback the inviter can copy).
const studioSendInviteEmail = async (workspaceId, email, token, expiresAt) => {
  const accessToken = _accessToken || (await sb.auth.getSession().then(r => r.data?.session?.access_token)) || SUPABASE_KEY;
  const appOrigin = window.location.origin;
  const r = await fetch(`${SUPABASE_URL}/functions/v1/send-team-invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}`, "apikey": SUPABASE_KEY },
    body: JSON.stringify({ workspace_id: workspaceId, email, token, expires_at: expiresAt, app_origin: appOrigin }),
  });
  const data = await r.json();
  if (!r.ok) return { data: null, error: { message: data?.error || "Send invite failed" } };
  return { data, error: null };
};

// Clients
// Slice B-2: queries now scope by workspace_id. RLS on the new policies
// (see migration 20260427120000_studio_workspace_rls.sql) enforces the same.
const dbGetClients = (workspaceId) =>
  sb.from("clients").select("*").eq("workspace_id", workspaceId).order("created_at");

const dbInsertClient = (client) =>
  sb.from("clients").insert(client).select().single()._exec();

const dbUpdateClient = (clientId, fields) =>
  sb.from("clients").update({ ...fields, updated_at: new Date().toISOString() }).eq("id", clientId)._exec();

const dbDeleteClient = (clientId) =>
  sb.from("clients").delete().eq("id", clientId)._exec();

// Timeline steps
const dbGetTimeline = (clientId) =>
  sb.from("timeline_steps").select("*").eq("client_id", clientId).order("position");

const dbReplaceTimeline = async (clientId, steps) => {
  await sb.from("timeline_steps").delete().eq("client_id", clientId)._exec();
  if (!steps || steps.length === 0) return;
  return sb.from("timeline_steps").insert(
    steps.map((s, i) => ({
      client_id: clientId,
      position: i,
      label: s.l,
      step_date: s.d || null,
      status: s.s || "pend"
    }))
  )._exec();
};

// Intake
const dbUpsertIntake = (clientId, answers, submitted) =>
  sb.from("client_intake").upsert({
    client_id: clientId,
    answers,
    submitted_at: submitted ? new Date().toISOString() : null,
    updated_at: new Date().toISOString()
  })._exec();

// Contracts
const dbGetContracts = (workspaceId) =>
  sb.from("contracts").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: false });

const dbInsertContract = (contract) =>
  sb.from("contracts").insert(contract).select().single()._exec();

const dbUpdateContract = (contractId, fields) =>
  sb.from("contracts").update({ ...fields, updated_at: new Date().toISOString() }).eq("id", contractId)._exec();

const dbDeleteContract = (contractId) =>
  sb.from("contracts").delete().eq("id", contractId)._exec();

// Invoices
const dbGetInvoices = (clientId) =>
  sb.from("invoices").select("*").eq("client_id", clientId).order("created_at");

const dbInsertInvoice = (invoice) =>
  sb.from("invoices").insert(invoice).select().single()._exec();

const dbUpdateInvoice = (invoiceId, fields) =>
  sb.from("invoices").update(fields).eq("id", invoiceId)._exec();

// Refunds
const dbGetRefunds = (clientId) =>
  sb.from("refunds").select("*").eq("client_id", clientId).order("created_at");

const dbInsertRefund = (refund) =>
  sb.from("refunds").insert(refund).select().single()._exec();

// Stripe Connect
const dbGetStripeAccount = (workspaceId) =>
  sb.from("stripe_accounts").select("*").eq("workspace_id", workspaceId).single();

const dbGetPayments = (clientId) =>
  sb.from("payments").select("*").eq("client_id", clientId).order("created_at", { ascending: false });

// All payments for the current freelancer — RLS filters automatically to payments whose client is owned by auth.uid()
const dbGetAllPayments = () =>
  sb.from("payments").select("*").order("created_at", { ascending: false });

// All refunds for the current freelancer — RLS filters via clients.freelancer_id = auth.uid()
const dbGetAllRefunds = () =>
  sb.from("refunds").select("*").order("created_at", { ascending: false });

// Messages / threads
const dbGetThreads = (workspaceId) =>
  sb.from("threads").select("*, messages(*)").eq("workspace_id", workspaceId);

// Slice B-2: workspaceId is the new scope. We still set freelancer_id on insert
// to keep the legacy column populated until the cleanup migration drops it.
// freelancerId stays in the signature so callers can pass acct.id alongside
// acct.workspaceId without a separate plumbing change.
const dbUpsertThread = async (workspaceId, freelancerId, clientId) => {
  const { data: existing } = await sb.from("threads")
    .select("id").eq("workspace_id", workspaceId).eq("client_id", clientId).single();
  if (existing) return existing.id;
  const { data } = await sb.from("threads").insert({ workspace_id: workspaceId, freelancer_id: freelancerId, client_id: clientId }).select().single()._exec();
  return data?.id;
};

// fromUserId: the auth.users id of the studio-side sender. Lets the client
// portal show "Sent by [team member]" when it isn't the workspace owner.
const dbInsertMessage = (threadId, fromRole, body, fromUserId) =>
  sb.from("messages").insert({ thread_id: threadId, from_role: fromRole, body, from_user_id: fromUserId || null })._exec();

// Calendar entries (notes only — not included in contracts or AI)
const dbGetCalEntries = (workspaceId) =>
  sb.from("calendar_entries").select("*").eq("workspace_id", workspaceId).order("entry_date");

const dbInsertCalEntry = (entry) =>
  sb.from("calendar_entries").insert(entry).select().single()._exec();

const dbUpdateCalEntry = (entryId, fields) =>
  sb.from("calendar_entries").update({ ...fields, updated_at: new Date().toISOString() }).eq("id", entryId)._exec();

const dbDeleteCalEntry = (entryId) =>
  sb.from("calendar_entries").delete().eq("id", entryId)._exec();

// Contract audit log
const dbGetAuditLog = (contractId) =>
  sb.from("contract_audit_log").select("*").eq("contract_id", contractId).order("created_at", { ascending: false });

const dbGetAuditLogByClient = (clientId) =>
  sb.from("contract_audit_log").select("*").eq("client_id", clientId).order("created_at", { ascending: false });

// Renamed from dbGetAuditLogByFreelancer — workspace-scoped per Slice B-2.
const dbGetAuditLogByWorkspace = (workspaceId) =>
  sb.from("contract_audit_log").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: false });

const dbInsertAuditLog = (entry) =>
  sb.from("contract_audit_log").insert(entry)._exec();

// Storage (profile pictures)
const dbUploadProfilePic = async (userId, file) => {
  const ext = file.name.split(".").pop();
  const path = `${userId}/profile.${ext}`;
  const { error } = await sb.storage.from("profile-pictures").upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = sb.storage.from("profile-pictures").getPublicUrl(path);
  return data.publicUrl;
};

// ── ADMIN helpers ──
const isAdminEmail = (email) => email && email.toLowerCase().endsWith("@lancer-app.com");
const isAdmin = (acct) => isAdminEmail(acct?.email) || acct?.userFlags?.is_admin === true;
const dbAdminGetAllUsers = () => sbRpc("admin_get_all_users", {});
const dbAdminGetMetrics = () => sbRpc("admin_get_metrics", {});
const dbAdminGetUserDetail = (userId) => sbRpc("admin_get_user_detail", { p_user_id: userId });
const dbAdminBanUser = (userId, banned, reason) => sbRpc("admin_ban_user", { p_user_id: userId, p_banned: banned, p_reason: reason || "" });
const dbAdminSuspendUser = (userId, suspended) => sbRpc("admin_suspend_user", { p_user_id: userId, p_suspended: suspended });
const dbAdminDeleteUser = (userId) => sbRpc("admin_delete_user", { p_user_id: userId });
const dbAdminGetPlatformActivity = () => sbRpc("admin_get_platform_activity", {});
const dbAdminGetAuditLog = () => sb.from("admin_audit_log").select("*").order("created_at", { ascending: false }).limit(200);
const dbAdminInsertAuditLog = (entry) => sb.from("admin_audit_log").insert(entry)._exec();
const dbAdminDeleteClient = (clientId) => sbRpc("admin_delete_client", { p_client_id: clientId });
const dbAdminUnarchiveClient = (clientId, restoredStatus) => sbRpc("admin_unarchive_client", { p_client_id: clientId, p_restored_status: restoredStatus || "inquiry" });

const dbUploadClientPhoto = async (freelancerId, clientId, file) => {
  const ext = file.name.split(".").pop();
  const path = `${freelancerId}/${clientId}.${ext}`;
  const { error } = await sb.storage.from("client-photos").upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = sb.storage.from("client-photos").getPublicUrl(path);
  return data.publicUrl;
};

const dbUploadContractPdf = async (freelancerId, clientId, file) => {
  // Use a timestamped path so each upload gets its own unique storage key.
  // Without this, uploading PDF #2 would overwrite PDF #1 at the same path,
  // breaking the ability to view/download older signed contracts later.
  const ts = Date.now();
  const path = `${freelancerId}/${clientId}-contract-${ts}.pdf`;
  const { error } = await sb.storage.from("contract-pdfs").upload(path, file, { upsert: false });
  if (error) throw error;
  const { data } = sb.storage.from("contract-pdfs").getPublicUrl(path);
  return data.publicUrl;
};

/* ── CLIENT PROGRESS — derives pct from timeline steps ── */
const calcClientPct = (tl = [], status = "inquiry") => {
  if (status === "complete" || status === "delivered") return 100;
  // Each status stage anchors a baseline percentage, so a brand-new inquiry
  // shows 5% (not 0%) and visibly fills as the client moves Pending → Signed
  // → Active. Without these floors a fresh inquiry rendered as an empty bar
  // which read like the client wasn't tracked at all.
  const statusFloor = { inquiry: 5, pending: 20, signed: 40, active: 60, delivered: 90 }[status] ?? 5;
  if (!tl || tl.length === 0) return statusFloor;
  const done = tl.filter(s => s.s === "done" || s.status === "done").length;
  const total = tl.length;
  const fromTimeline = Math.round((done / total) * 100);
  return Math.max(statusFloor, fromTimeline);
};

/* ── CLIENT step label from status ── */
const clientStepLabel = (status = "inquiry", tl = []) => {
  const nextActive = tl.find(s => (s.s === "active" || s.status === "active"));
  if (nextActive) return nextActive.l || nextActive.label || "In progress";
  const nextPend = tl.find(s => (s.s === "pend" || s.status === "pend"));
  if (nextPend) return nextPend.l || nextPend.label || "Pending";
  const map = { inquiry:"Inquiry",pending:"Contract sent",signed:"Deposit due",active:"In progress",delivered:"Delivered",complete:"Complete",archived:"Archived" };
  return map[status] || "Inquiry";
};

/* ── PROFILE → ACCT converter (DB row → app shape) ── */
const profileToAcct = (profile, clients = [], contracts = [], threads = []) => ({
  id: profile.id,
  name: profile.name || "",
  email: profile.email || "",
  ini: profile.ini || (profile.name||"").slice(0,2).toUpperCase(),
  slug: profile.slug || "",
  vert: profile.vert || "",
  verts: profile.verts || [],
  customRole: (()=>{
    // Backward compat: custom_role may be a JSON-stringified array or a plain string
    const raw=profile.custom_role;
    if(!raw) return null;
    if(Array.isArray(raw)) return raw;
    if(typeof raw==="string"){
      const trimmed=raw.trim();
      if(trimmed.startsWith("[")){
        try{ const arr=JSON.parse(trimmed); return Array.isArray(arr)?arr:[trimmed]; }catch(e){ return [trimmed]; }
      }
      return [trimmed];
    }
    return null;
  })(),
  role: profile.role || "",
  country: profile.country || "",
  phone: profile.phone || "",
  currency: profile.currency || "USD",
  currencySymbol: profile.currency_symbol || "$",
  billingPlan: (()=>{
    // Grace period: if user cancelled but expiry is in the future, keep them on the paid plan
    const expiry = profile.plan_expiry_date;
    const cancelled = profile.cancelled_plan;
    if (expiry && cancelled && expiry >= localToday()) return cancelled;
    return profile.billing_plan || "free";
  })(),
  billingPlanRaw: profile.billing_plan || "free", // the actual DB value (may be "free" during grace period)
  billingStartDate: profile.billing_start_date || null,
  planExpiryDate: profile.plan_expiry_date || null,
  cancelledPlan: profile.cancelled_plan || null,
  // scheduledPlan = a paid plan that will start automatically when the current
  // subscription ends. Used for Studio → Pro downgrade-at-period-end so the
  // freelancer keeps Studio features until period_end and Pro starts after.
  scheduledPlan: profile.scheduled_plan || null,
  // One-trial-per-account flag. Mirrors profiles.studio_trial_used — set true
  // the first time a Studio sub lands (stripe-webhook). When true, the UI must
  // stop promising a 14-day free trial: no badge, no trial copy in confirm
  // modal, button label flips to a normal upgrade CTA. The server-side
  // stripe-checkout edge function also gates trial_period_days on this flag,
  // so the UI is just keeping the promise honest.
  studioTrialUsed: profile.studio_trial_used === true,
  payoutHoldDays: profile.payout_hold_days ?? 7,
  twoFA: profile.two_fa || false,
  profilePic: profile.profile_pic || null,
  tagline: profile.tagline || "",
  businessName: profile.business_name || profile.name || "",
  // Pass through DB value as-is; the portal renderer + branding settings
  // each handle their own "Powered by Lancer" fallback. Auto-defaulting
  // here would leak the default into the DB on save (breaking the
  // Studio white-label rule).
  footerText: profile.footer_text || "",
  darkMode: profile.dark_mode || false,
  cancellationPolicy: profile.cancellation_policy || {},
  userFlags: profile.user_flags || {},
  questionnaireTemplate: (profile.questionnaire_template&&profile.questionnaire_template.length>0) ? profile.questionnaire_template : null,
  questionnaireTemplates: profile.questionnaire_templates || [],
  studioTheme: profile.studio_theme || null,
  stripeConnect: null, // populated by loadAcctFromDB after profile load
  clients,
  contracts,
  threads,
  calEntries: [],
  auditLog: [],
  payments: [],
  cal: {},
  revenue: "",
  revChange: "",
  stats: [],
  bars: [],
  agenda: [],
  actions: [],
});

const BANNER_B64="data:image/png;base64,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";

/* ── SVG ICONS ── */
const I = {
  home: <svg width="28" height="24" viewBox="473 555 1019 866" fill="currentColor" stroke="none"><path transform="translate(1471,575)" d="m0 0 1 3-8 10-11 14-10 13-12 15-13 17-14 17-10 13-11 14-13 16-13 17-39 48-11 14-11 13-11 14-8 10-10 13-14 17-11 14-11 13-10 13-11 13-7 9-14 17-9 11-14 17-22 26-8 10-9 11-12 14-9 11-13 15-7 8-11 13-9 10-7 8-16 17-14 14h-2v2l-8 7-8 8-8 7-12 11-11 9-13 11-14 11-17 13-16 11-24 16-19 12-24 13-32 15-25 9-35 12-10 3 6 1 40 2 43 1 154 2 174 1 89 2 129 1 29 1-1 2-51 8-47 7-119 16-93 11-40 5-67 8-62 7-41 4-28 3-44 4-34 4-34 3-50 5-59 5-37 3-44 3-64 5h-7l2-4 11-10 11-9 13-12 11-9 11-10 11-9 14-12 11-10 11-9 12-11 10-8 30-26 14-12 11-10 11-9 11-10 11-9 10-9 11-9 12-11 8-7 10-9 11-9 28-24 15-13 13-11 14-12 13-11 14-12 15-13 8-7 14-12 11-9 10-9 11-9 14-12 11-9 10-9 14-11 10-9 10-8 15-13 13-11 11-9 13-11 11-9 14-12 14-11 14-12 11-9 14-12 11-9 13-11 11-9 13-11 22-18 17-14 10-8 16-13 14-12 14-11 14-12 11-9 13-11 14-11 34-28 11-9 32-26 13-11 10-8 13-11 17-13z" fill="currentColor"/></svg>,
  clients: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  calendar: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  inbox: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  contracts: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  mail: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  phone: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.35 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.63a16 16 0 0 0 6 6l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  link: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  search: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  send: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  close: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  edit: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  eye: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  star: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  refresh: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  arrow: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  arrowLeft: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  chevron: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  dot: <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3" fill="currentColor"/></svg>,
  circleO: <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="4" cy="4" r="3"/></svg>,
  bell: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  invoice: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  message: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  settings: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  logout: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  palette: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
  creditcard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  metrix: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>,
  helpChat: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  caldate: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  lock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  unlock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>,
  clipboard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>,
  fileText: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  upload: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>,
  download: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  dollar: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  penTool: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>,
  sign: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 19.5c-1 .5-2.68.42-3.5-.5-1-1.12-.5-3 1-3.5 1.76-.58 3.28.75 3 2.5-.14.87-.78 1.58-1.5 2"/><path d="M4 19.5c1-1 2-3.5 2-5.5 0-2.5-1-4-2.5-4C2 10 1 11 1 12.5S2 16 4 19.5z"/><line x1="7" y1="20" x2="17" y2="20"/></svg>,
  sparkle: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"/></svg>,
  alertTri: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  archive: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
  chatBubble: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  user: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  ban: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
  gift: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>,
  bulb: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>,
  mailOpen: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6z"/><polyline points="22 10 12 16 2 10"/></svg>,
  hourglass: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>,
  checkCircle: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  xCircle: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
  linkExt: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  sendUp: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  undo: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>,
  party: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5.8 11.3L2 22l10.7-3.8"/><path d="M4 3h.01"/><path d="M22 8h.01"/><path d="M15 2h.01"/><path d="M22 20h.01"/><path d="M22 2l-2.24.75a2.9 2.9 0 0 0-1.96 3.12v0c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10"/><path d="M22 13l-1-.67c-.71-.48-1.68-.24-2.05.55a1.4 1.4 0 0 1-2.22.46l-.87-.76a1.88 1.88 0 0 0-2.57.17L12 14"/></svg>,
  fingerprint: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4"/><path d="M5 19.5C5.5 18 6 15 6 12c0-3.5 2.5-6 6-6 3 0 5.5 2 6 5"/><path d="M12 12v7.5"/><path d="M8 15c0-2.21 1.79-4 4-4"/></svg>,
  arrowPointer: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3l14 9-6 1-4 6L5 3z"/></svg>,
  camera: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  film: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></svg>,
  penNib: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>,
  monitor: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  code: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  smartphone: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>,
  headphones: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z"/><path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z"/></svg>,
  musicNote: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  heart: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  activity: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  compass: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  zap: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  pause: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  globe: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  triangle: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 22 20 2 20"/></svg>,
};

/* ── DATE HELPERS ── */
const MONTHS=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
// Return today's date as YYYY-MM-DD in LOCAL timezone (not UTC)
const localToday=()=>{const d=new Date();return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;};
// Round to 2 decimal places for money calculations
const round2=(n)=>Math.round((n+Number.EPSILON)*100)/100;
// Format a number as money string with 2 decimal places (e.g. "1,250.00")
const fmtMoney=(n)=>Number(n||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});

// Slice E — financial-visibility helpers.
// Owners always see financials. Members may have visibility=false (set by
// owner via Team page). Guests never see financials. Use canSeeFinancials(acct)
// at every gating decision; <MoneyAmount> for individual amounts that aren't
// hidden by section gating.
const canSeeFinancials = (acct) => {
  if (!acct) return false;
  if (acct.workspaceRole === "guest") return false;
  // financialVisibility is undefined for legacy single-user accounts — treat as visible.
  return acct.financialVisibility !== false;
};

const MoneyAmount = ({ value, symbol, prefix, suffix, masked, className, style }) => {
  if (masked) {
    return <span className={className} style={style}>{prefix||""}{symbol||""}••••{suffix||""}</span>;
  }
  return <span className={className} style={style}>{prefix||""}{symbol||""}{fmtMoney(value)}{suffix||""}</span>;
};

// Phase 10 — Reusable Studio upgrade card. JIT-displayed wherever a Pro/Free
// user attempts a Studio-only action. Per spec: includes the user's concrete
// projected price, not a generic "from £39/mo".
const STUDIO_DISMISS_KEY = "lancer_studio_ambient_dismissed_until";

const isStudioAmbientDismissed = () => {
  try {
    const v = localStorage.getItem(STUDIO_DISMISS_KEY);
    if (!v) return false;
    return new Date(v) > new Date();
  } catch { return false; }
};

const dismissStudioAmbient = () => {
  // 90-day suppression per spec. After it reappears once and is dismissed
  // again, the next dismiss stores a 100-year timestamp = "forever".
  try {
    const now = new Date();
    const existing = localStorage.getItem(STUDIO_DISMISS_KEY);
    const wasShownBefore = existing && new Date(existing) < now;
    const days = wasShownBefore ? 365*100 : 90;
    const next = new Date(now.getTime() + days*86400000);
    localStorage.setItem(STUDIO_DISMISS_KEY, next.toISOString());
  } catch {}
};

const _calcStudioBaseExample = () => {
  // Example for the JIT card: 2 seats + 0 guests = base price.
  // The Billing & Plan calculator lets them adjust from there.
  // Hard-coded to mirror the PRICING object in SettingsSheet (single source
  // of truth lives in the Billing view; this is a forward-looking preview).
  return 39;
};

function StudioUpgradeCard({acct,title,body,onUpgrade}){
  const sym=acct?.currencySymbol||"£";
  const fx=({GBP:1,USD:1.27,EUR:1.17,AUD:1.93,CAD:1.73,NZD:2.12,SEK:13.5,NOK:13.8,DKK:8.7,SGD:1.70,INR:106,ZAR:23.5,AED:4.66,NGN:2030,KES:164}[acct?.currency||"GBP"])||1;
  const localBase=Math.round(_calcStudioBaseExample()*fx);
  const currentPlan=effectivePlan(acct);
  const proCost=Math.round(12*fx);
  const delta=localBase-proCost;
  // Trial is one-time. Once consumed, the JIT card stops advertising "14-day
  // free trial · No charge today" and the CTA flips to a normal upgrade label
  // so we don't promise something stripe-checkout will refuse to grant.
  const trialAvailable=!acct?.studioTrialUsed;
  return (
    <div style={{background:"var(--gl)",border:"1.5px solid var(--t)",borderRadius:14,padding:16,marginBottom:12}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
        <span style={{display:"inline-flex",color:"var(--g)",flexShrink:0,marginTop:2}}>{I.lock}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginBottom:4}}>{title}</div>
          <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5,marginBottom:10}}>{body}</div>
          <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px 12px",marginBottom:12,display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:10}}>
            <div>
              <div style={{fontSize:18,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{sym}{localBase}<span style={{fontSize:11,fontWeight:500,color:"var(--mu)"}}>/mo</span></div>
              {trialAvailable&&<div style={{fontSize:10,color:"var(--mu)",letterSpacing:".04em",textTransform:"uppercase",fontWeight:600,marginTop:2}}>14-day free trial · No charge today</div>}
            </div>
            {currentPlan==="pro"&&delta>0&&(
              <div style={{fontSize:11,color:"var(--mu)",textAlign:"right",lineHeight:1.4}}>
                {sym}{delta}/mo more<br/>than your Pro plan
              </div>
            )}
          </div>
          <button onClick={onUpgrade} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:10,padding:"10px 16px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            {trialAvailable?"Start 14-day free trial":"Upgrade to Studio"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Phase 11 — Payment failure banner. Shows when Stripe reports the
// subscription as past_due / unpaid / incomplete. Owner-only because they're
// the seat that needs to fix the card. Click → opens Stripe customer portal.
function PaymentFailureBanner({acct,onOpenBilling}){
  const status=acct?.subscriptionStatus;
  if(!status) return null;
  if(!canEditWorkspaceBranding(acct)) return null;
  const isFailed=status==="past_due"||status==="unpaid"||status==="incomplete";
  if(!isFailed) return null;
  const launchPortal=async()=>{
    const {url,error}=await studioOpenCustomerPortal();
    if(error){ if(onOpenBilling) onOpenBilling(); return; }
    window.location.href=url;
  };
  return (
    <div onClick={launchPortal} style={{margin:"12px 24px 0",padding:"12px 14px",background:"var(--err-bg)",border:"1.5px solid var(--err-bo)",borderRadius:10,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
      <span style={{display:"inline-flex",color:"var(--err-tx)",flexShrink:0}}>{I.alertTri}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:700,color:"var(--err-tx)"}}>Payment issue · Update your card to keep your plan active</div>
        <div style={{fontSize:11,color:"var(--err-tx)",opacity:.85}}>We couldn't charge your card. Tap to update payment details.</div>
      </div>
      <span style={{display:"inline-flex",color:"var(--err-tx)",flexShrink:0}}>{I.arrow}</span>
    </div>
  );
}

// Trial banner — persistent across pages while a Studio trial is active.
// Owner-only: members + guests don't control billing and shouldn't see
// "Studio trial · X days remaining · first charge on..." copy aimed at the
// person who entered the card.
function StudioTrialBanner({acct,onOpenBilling}){
  const trialEndsRaw=acct?.workspaceTrialEndsAt;
  const trialEnd=trialEndsRaw?new Date(trialEndsRaw):null;
  if(!trialEnd||trialEnd<=new Date()) return null;
  if(effectivePlan(acct)!=="studio") return null;
  if(!canEditWorkspaceBranding(acct)) return null;
  const daysLeft=Math.max(0,Math.ceil((trialEnd-new Date())/86400000));
  const trialEndDisplay=trialEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
  return (
    <div onClick={onOpenBilling} style={{margin:"12px 24px 0",padding:"10px 14px",background:"var(--tl)",border:"1px solid var(--t)",borderRadius:10,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
      <span style={{display:"inline-flex",color:"var(--gm)",flexShrink:0}}>{I.check}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:700,color:"var(--gm)"}}>Studio trial · {daysLeft} day{daysLeft===1?"":"s"} remaining</div>
        <div style={{fontSize:11,color:"var(--gm)",opacity:.8}}>First charge on {trialEndDisplay}. Tap to manage.</div>
      </div>
      <span style={{display:"inline-flex",color:"var(--gm)",flexShrink:0}}>{I.arrow}</span>
    </div>
  );
}

// Phase 7 — Studio analytics aggregations. All computed on-the-fly from
// acct.clients; no nightly aggregation table yet (Phase 7b).
const _clientNetRevenue = (c) => {
  if (c.archived) return 0;
  const raw = parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
  let r = 0;
  if (c.depositPaid) r += Number(c.depositAmt || (raw * (c.depositPct || 50) / 100)) || 0;
  if (c.balancePaid) r += Number(c.balanceAmt || (raw * (100 - (c.depositPct || 50)) / 100)) || 0;
  (c.invoices||[]).filter(inv => inv.paid).forEach(inv => { r += Number(inv.amount)||0; });
  (c.refunds||[]).forEach(rf => { r -= Number(rf.amount)||0; });
  return r;
};

// Per-seat aggregation. Two metrics per seat:
//   * Lead-only: revenue + projects led. Counted exactly once per client (one
//     lead per project) so summing across rows = workspace totals — no
//     double-counting.
//   * Assigned: total projects the seat is on (lead OR via client_members).
//     Members get credit here even when they aren't lead. May exceed workspace
//     project count if multiple seats are on the same project — that's the point.
//
// "Active" definition (matches user model): any non-archived client. Since the
// loop already skips archived clients at the top, every counted client is
// active by definition. activeCount === projectCount and
// assignedActiveCount === assignedCount; kept as separate fields so any future
// finer-grained definition (e.g. excluding "complete") can be added without
// changing call sites.
//
// `assignments` is the array returned by studio_workspace_member_clients:
//   [{ workspace_member_id, client_id, is_lead }]. When null/empty we fall
//   back to lead-only behaviour so the function is safe to call before the
//   assignment fetch lands.
const computePerSeatStats = (clients, members, assignments) => {
  const byId = {};
  (members||[]).forEach(m => {
    byId[m.member_id] = {
      memberId: m.member_id,
      userId: m.user_id,
      name: m.user_name || (m.user_email||"").split("@")[0],
      email: m.user_email || "",
      role: m.role,
      revenue: 0,
      projectCount: 0,        // Projects led (non-archived)
      activeCount: 0,         // Active projects led (= projectCount under current "active = !archived" rule)
      assignedCount: 0,       // Active projects assigned (lead OR on the team)
      assignedActiveCount: 0, // Same as assignedCount under current rule
      archivedAssignedCount: 0, // Archived projects this seat is/was on
    };
  });
  const activeClientById = {};
  const archivedClientById = {};
  (clients||[]).forEach(c => {
    if (c.archived) {
      archivedClientById[c.id] = c;
      return;
    }
    activeClientById[c.id] = c;
    const lead = c.leadSeatId;
    if (lead && byId[lead]) {
      const s = byId[lead];
      s.projectCount++;
      s.activeCount++;
      s.revenue += _clientNetRevenue(c);
    }
  });
  if (assignments && assignments.length) {
    // Bucket each seat's assignments into active vs archived sets so a seat
    // that's both lead and in client_members on the same client doesn't get
    // double-counted in either bucket.
    const activeSet = {};
    const archivedSet = {};
    assignments.forEach(a => {
      const mid = a.workspace_member_id;
      const cid = a.client_id;
      if (!byId[mid]) return;
      if (activeClientById[cid]) {
        if (!activeSet[mid]) activeSet[mid] = new Set();
        activeSet[mid].add(cid);
      } else if (archivedClientById[cid]) {
        if (!archivedSet[mid]) archivedSet[mid] = new Set();
        archivedSet[mid].add(cid);
      }
    });
    Object.keys(byId).forEach(mid => {
      const seat = byId[mid];
      seat.assignedCount = activeSet[mid]?.size || 0;
      seat.assignedActiveCount = seat.assignedCount;
      seat.archivedAssignedCount = archivedSet[mid]?.size || 0;
    });
  } else {
    Object.values(byId).forEach(s => {
      s.assignedCount = s.projectCount;
      s.assignedActiveCount = s.activeCount;
      // No assignments fetch → can't tell which archived clients the seat was on.
      s.archivedAssignedCount = 0;
    });
  }
  return Object.values(byId).filter(s => s.role !== "guest").sort((a,b) => b.revenue - a.revenue);
};

const computeServiceLineStats = (clients) => {
  const byVert = {};
  (clients||[]).forEach(c => {
    if (c.archived) return;
    // Prefer c.verts[0] over c.vert because client creation at line ~7525
    // normalises "custom:RoleName" → "custom" on the primary vert field for
    // backward compat. The full string ("custom:EPM") is preserved on the
    // verts array — that's where the typed-in custom service name lives.
    const rawVid = (c.verts && c.verts.length > 0 ? c.verts[0] : c.vert) || "";
    const displayLabel = rawVid ? getVertLabel(rawVid) : "Uncategorised";
    // Group by lower-cased label so different casings of the same custom name
    // ("EPM" / "epm") aggregate together. We keep the first-seen casing as
    // the canonical display label.
    const key = displayLabel.toLowerCase();
    if (!byVert[key]) byVert[key] = { vert: displayLabel, revenue: 0, count: 0, clientNames: [] };
    byVert[key].count++;
    byVert[key].revenue += _clientNetRevenue(c);
    if (c.name) byVert[key].clientNames.push(c.name);
  });
  Object.values(byVert).forEach(s => s.clientNames.sort((a,b) => a.localeCompare(b)));
  return Object.values(byVert).sort((a,b) => b.revenue - a.revenue);
};

// Stage labels for the Pipeline conversion view. "Active" is intentionally
// labelled "In progress" here even though the underlying status id stays
// "active" — otherwise the row name conflates with the user concept of "active
// client" (any non-archived client across any stage) and people read the
// reached number as a totals row.
const PIPELINE_STAGES = [
  { id: "inquiry",   label: "Inquiry" },
  { id: "pending",   label: "Contract sent" },
  { id: "signed",    label: "Signed" },
  { id: "active",    label: "In progress" },
  { id: "delivered", label: "Delivered" },
  { id: "complete",  label: "Complete" },
];

const computePipelineStats = (clients) => {
  const counts = {};
  PIPELINE_STAGES.forEach(s => counts[s.id] = 0);
  (clients||[]).forEach(c => {
    if (c.archived) return;
    if (counts[c.status] != null) counts[c.status]++;
  });
  // Cumulative reach: a client at stage N has passed through N-1, N-2, ... so each
  // earlier stage's "reached" count includes them. We compute reached by summing
  // counts[i..end].
  let cumulative = 0;
  const reached = {};
  for (let i = PIPELINE_STAGES.length - 1; i >= 0; i--) {
    cumulative += counts[PIPELINE_STAGES[i].id];
    reached[PIPELINE_STAGES[i].id] = cumulative;
  }
  return PIPELINE_STAGES.map((s, i) => {
    const r = reached[s.id];
    // Conversion is "what % of clients in the previous stage progressed to
    // this one". Only meaningful for stages 1+; the first stage has no prior
    // to compare against. Also null when zero clients ever reached the
    // previous stage — defaulting to 100% there was misleading (showed
    // 100% conversion on stages no one had reached yet).
    let conv = null;
    if (i > 0) {
      const prev = reached[PIPELINE_STAGES[i-1].id];
      if (prev > 0) conv = Math.round((r / prev) * 100);
    }
    return { ...s, count: counts[s.id], reached: r, conversion: conv };
  });
};

// Generic CSV download — used by all three Studio analytics panels.
// Phase 9b — Lazy-load jsPDF (single CDN script tag, cached after first load).
let _jsPdfLoading = null;
const loadJsPdf = () => {
  if (typeof window !== "undefined" && window.jspdf?.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
  if (_jsPdfLoading) return _jsPdfLoading;
  _jsPdfLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = () => resolve(window.jspdf.jsPDF);
    s.onerror = () => { _jsPdfLoading = null; reject(new Error("Could not load jsPDF")); };
    document.head.appendChild(s);
  });
  return _jsPdfLoading;
};

// Phase 9b — Generic PDF table export. Mirrors downloadCsv's signature but
// produces a styled PDF with title, generated-date, header row, and data.
//
// Layout rules:
//   * Column widths are measured from actual content (header + body) so long
//     emails/names don't overflow into the next column. If total content
//     exceeds page width, columns are scaled down proportionally and any
//     individual cell that's too wide gets ellipsis-truncated.
//   * Numeric columns auto-detect (any non-empty value matching a money/number
//     regex) and right-align — both the header label AND the data cells, so
//     they actually line up visually.
//   * Callers can pass `align: ["left","right",...]` to override auto-detection
//     when the heuristic gets it wrong (e.g. mixed columns).
const downloadPdf = async ({ filename, title, subtitle, header, rows, align }) => {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const usableW = pageW - marginX * 2;
  let y = 60;

  // Brand wordmark
  doc.setFont("Times", "Bold");
  doc.setFontSize(20);
  doc.setTextColor(28, 77, 68);
  doc.text("Lancer", marginX, y);
  y += 24;
  // Title
  doc.setFont("Helvetica", "Bold");
  doc.setFontSize(14);
  doc.setTextColor(11, 14, 13);
  doc.text(title, marginX, y);
  y += 16;
  // Subtitle / generated stamp
  doc.setFont("Helvetica", "Normal");
  doc.setFontSize(9);
  doc.setTextColor(122, 144, 133);
  if (subtitle) { doc.text(subtitle, marginX, y); y += 10; }
  doc.text(`Generated ${new Date().toLocaleString("en-GB",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}`, marginX, y);
  y += 22;

  const colCount = header.length;
  // Decide alignment per column. Caller override beats auto-detect.
  const isNumLike = (s) => /^[-+]?[£$€]?[0-9][0-9.,]*\s*%?$/.test(String(s||"").trim());
  const colAlign = header.map((_, i) => {
    if (align && align[i]) return align[i];
    // Look at the first 5 non-empty rows for this column
    let numCount = 0, sampleCount = 0;
    for (const r of rows) {
      const c = r[i];
      if (c == null || String(c).trim() === "") continue;
      sampleCount++;
      if (isNumLike(c)) numCount++;
      if (sampleCount >= 5) break;
    }
    return sampleCount > 0 && numCount === sampleCount ? "right" : "left";
  });

  // Measure desired column widths from the widest cell (header or body).
  doc.setFontSize(10);
  const cellPad = 12; // extra room so adjacent columns can't kiss
  const desiredW = header.map((h, i) => {
    doc.setFont("Helvetica", "Bold");
    let maxW = doc.getTextWidth(String(h));
    doc.setFont("Helvetica", "Normal");
    for (const r of rows) {
      const w = doc.getTextWidth(String(r[i] ?? ""));
      if (w > maxW) maxW = w;
    }
    return maxW + cellPad;
  });
  const totalDesired = desiredW.reduce((a, b) => a + b, 0);
  // If we exceed usable width, scale all columns down proportionally; if
  // we're under, leave them at natural width and absorb the slack on the right.
  const scale = totalDesired > usableW ? usableW / totalDesired : 1;
  const colW = desiredW.map(w => w * scale);
  // Per-column starting x.
  const colX = [];
  { let x = marginX; for (let i = 0; i < colCount; i++) { colX.push(x); x += colW[i]; } }

  // Truncate cell text to fit its column with an ellipsis. Reserves a small
  // pad so we don't visually butt up against the next column.
  const fitCell = (text, widthPt) => {
    const limit = widthPt - 4;
    if (doc.getTextWidth(text) <= limit) return text;
    let lo = 0, hi = text.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const candidate = text.slice(0, mid) + "…";
      if (doc.getTextWidth(candidate) <= limit) lo = mid + 1; else hi = mid;
    }
    return text.slice(0, Math.max(0, lo - 1)) + "…";
  };

  const drawCell = (text, i, yy) => {
    const t = fitCell(String(text ?? ""), colW[i]);
    if (colAlign[i] === "right") {
      doc.text(t, colX[i] + colW[i] - 4, yy, { align: "right" });
    } else {
      doc.text(t, colX[i] + 2, yy);
    }
  };

  // Header row
  doc.setFont("Helvetica", "Bold");
  doc.setFontSize(10);
  doc.setTextColor(11, 14, 13);
  header.forEach((h, i) => drawCell(h, i, y));
  y += 6;
  doc.setDrawColor(192, 206, 201);
  doc.line(marginX, y, pageW - marginX, y);
  y += 14;

  // Body rows
  doc.setFont("Helvetica", "Normal");
  doc.setFontSize(10);
  doc.setTextColor(11, 14, 13);
  for (const row of rows) {
    if (y > pageH - 60) {
      doc.addPage();
      y = 60;
      // Re-draw header on each new page for readability
      doc.setFont("Helvetica", "Bold");
      header.forEach((h, i) => drawCell(h, i, y));
      y += 6;
      doc.line(marginX, y, pageW - marginX, y);
      y += 14;
      doc.setFont("Helvetica", "Normal");
    }
    row.forEach((cell, i) => drawCell(cell, i, y));
    y += 16;
  }
  doc.save(filename);
};

const downloadCsv = (filename, header, rows) => {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const csv = [header, ...rows].map(r => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

// Phase 5 — White-label gate. Studio plan removes "Powered by Lancer" and
// applies the workspace's custom theme. Pro/Free always show Lancer branding.
// Currently keyed off the freelancer's billingPlan because portal rendering
// builds acct from profile data; once portal RPC returns workspace plan we'll
// switch to workspacePlan as the source of truth.
// Resolve the effective plan tier across all sources. Workspace.plan and
// profiles.billing_plan can drift (e.g. when an upgrade takes the JSX
// fallback path that only touches profiles), so we pick the highest tier
// available rather than blindly trusting whichever appears first.
const PLAN_RANK = { free: 0, pro: 1, studio: 2 };
const effectivePlan = (acct) => {
  if (!acct) return "free";
  // During the cancellation grace period the user has paid through plan_expiry_date,
  // so they should keep the cancelled plan's features even though billing_plan is
  // already showing as 'free'. Same shape applies to the workspace-level expiry
  // (cancelledPlan stored on the workspace row, used by Studio).
  let cancelledDuringGrace = null;
  const today = new Date();
  const todayISO = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
  if (acct.cancelledPlan && acct.planExpiryDate && acct.planExpiryDate >= todayISO) {
    cancelledDuringGrace = acct.cancelledPlan;
  } else if (acct.workspaceCancelledPlan && acct.workspacePlanExpiryDate && acct.workspacePlanExpiryDate >= todayISO) {
    cancelledDuringGrace = acct.workspaceCancelledPlan;
  }
  const candidates = [acct.workspacePlan, acct.billingPlan, acct.billingPlanRaw, cancelledDuringGrace].filter(Boolean);
  return candidates.reduce((best, p) => (PLAN_RANK[p] ?? 0) > (PLAN_RANK[best] ?? 0) ? p : best, "free");
};

const isWhiteLabel = (acct) => effectivePlan(acct) === "studio";

// Phase 5 — Branding write-access. Workspace-level settings (business name,
// tagline, footer, theme, custom domain) are owned by the seat that purchased
// the plan. Members + guests inherit the visual experience but cannot change it.
const canEditWorkspaceBranding = (acct) => {
  if (!acct) return false;
  // Legacy single-user accounts don't have workspaceRole set yet — treat
  // them as owners of their own workspace, which they are.
  return acct.workspaceRole === "owner" || acct.workspaceRole == null;
};

// Footer text editing is the lighter restriction: any paying plan owner
// can change it (Pro + Studio); Free is locked to the default.
const canEditFooterText = (acct) => {
  if (!canEditWorkspaceBranding(acct)) return false;
  const plan = effectivePlan(acct);
  return plan === "pro" || plan === "studio";
};
// Validate that a name has at least first + last (two word tokens, each >= 2 chars)
const isFullName=(n)=>{
  if(!n) return false;
  const parts=String(n).trim().split(/\s+/).filter(p=>p.length>=2);
  return parts.length>=2;
};
// PDF-contract body marker. Shape: "[PDF Contract::<url>]" (new) or "[PDF Contract]" (legacy).
// Storing the URL inside each contract row's body means new uploads can't overwrite
// older contracts' references — each signed/archived contract keeps its own PDF link.
const PDF_MARKER_RX=/^\[PDF Contract(?:::([^\]]+))?\]/;
const isPdfContract=(c)=>!!c&&PDF_MARKER_RX.test(c.body||"");
const getContractPdfUrl=(contract,client)=>{
  if(contract){
    const m=(contract.body||"").match(PDF_MARKER_RX);
    if(m&&m[1]) return m[1];
    // Legacy "[PDF Contract]" body (no embedded URL) → fall back to client-level URL.
    // These pre-date the per-contract URL fix, so the latest client URL is the best we can do.
    if(m) return client?.contractPdfUrl||null;
    // Contract is non-PDF (AI/manual text) — explicitly return null so callers don't
    // mistakenly use a stale client.contractPdfUrl left over from a previous PDF send.
    return null;
  }
  // No contract context provided (rare) → client-level URL is the best guess.
  return client?.contractPdfUrl||null;
};
// True when a contract is a legacy `[PDF Contract]` row (no embedded URL) whose
// physical file was overwritten by a later PDF upload for the same client. The
// pre-fix uploader wrote every PDF to the same non-timestamped storage path, so
// when two or more legacy PDF contracts exist for one client, all but the most
// recently created one have lost their backing file. `siblings` is the full
// list of contracts for that client (archived + current) — typically
// `c._portalContracts` in the portal or `acct.contracts` filtered by client_id
// on the freelancer side.
const isLegacyLostPdf=(contract,siblings)=>{
  if(!contract) return false;
  const m=(contract.body||"").match(PDF_MARKER_RX);
  if(!m) return false;           // Not a PDF contract
  if(m[1]) return false;         // Has its own embedded URL — new format, safe
  if(!Array.isArray(siblings)||siblings.length===0) return false;
  const thisMs=Date.parse(contract.created_at||contract.sent_at||0)||0;
  // Any newer legacy PDF for the same client means this one's storage was overwritten
  return siblings.some(o=>{
    if(!o||o.id===contract.id) return false;
    const om=(o.body||"").match(PDF_MARKER_RX);
    if(!om||om[1]) return false; // Only legacy PDFs share the overwritten path
    const oMs=Date.parse(o.created_at||o.sent_at||0)||0;
    return oMs>thisMs;
  });
};
// Format a date as "DD MMM YYYY" (4-digit year) for contract signature blocks
function fmtDateLong(d){
  if(!d) return "";
  if(typeof d==="string"&&d.includes("T")){
    const dt=new Date(d);
    if(!isNaN(dt)) return `${String(dt.getDate()).padStart(2,"0")} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  }
  if(typeof d==="string"&&d.match(/^\d{4}-\d{2}-\d{2}$/)){
    const [y,m,dy]=d.split("-");
    return `${String(parseInt(dy)).padStart(2,"0")} ${MONTHS[parseInt(m)-1]} ${y}`;
  }
  // Legacy "DD-MM-YYYY"
  if(typeof d==="string"&&d.match(/^\d{2}-\d{2}-\d{4}$/)){
    const [dy,m,y]=d.split("-");
    return `${dy} ${MONTHS[parseInt(m)-1]} ${y}`;
  }
  return d;
}
function fmtDate(d){ // any format -> "DD MMM YY" display
  if(!d) return "";
  // Full ISO timestamp (e.g. "2026-04-10T21:53:21.074044+00:00") → parse as Date and use local time
  if(d.includes("T")){
    const dt=new Date(d);
    if(!isNaN(dt)) return `${String(dt.getDate()).padStart(2,"0")} ${MONTHS[dt.getMonth()]} ${String(dt.getFullYear()).slice(2)}`;
  }
  if(d.match(/^\d{4}-\d{2}-\d{2}$/)){
    const [y,m,dy]=d.split("-");
    return `${String(parseInt(dy)).padStart(2,"0")} ${MONTHS[parseInt(m)-1]} ${y.slice(2)}`;
  }
  // Legacy "DD-MM-YYYY" format
  if(d.match(/^\d{2}-\d{2}-\d{4}$/)){
    const [dy,m,y]=d.split("-");
    return `${dy} ${MONTHS[parseInt(m)-1]} ${y.slice(2)}`;
  }
  // Legacy "14 Sep 2026" or "Sep 14" — convert to DD MMM YY
  const moIdx=MONTHS.findIndex(mn=>d.includes(mn));
  if(moIdx>=0){
    const numMatch=d.match(/(\d+)/g);
    if(numMatch){
      const day=parseInt(numMatch[0]);
      const year=numMatch.find(n=>n.length===4)||"2026";
      return `${String(day).padStart(2,"0")} ${MONTHS[moIdx]} ${year.slice(2)}`;
    }
  }
  return d;
}
// Full date + time display: "11 Apr 26 · 10:53 PM" — always local timezone
function fmtDateTime(d){
  if(!d) return "";
  // Only show time component for full ISO timestamps (contain "T")
  if(typeof d==="string"&&d.includes("T")){
    const dt=new Date(d);
    if(!isNaN(dt)){
      const date=`${String(dt.getDate()).padStart(2,"0")} ${MONTHS[dt.getMonth()]} ${String(dt.getFullYear()).slice(2)}`;
      const time=dt.toLocaleTimeString(undefined,{hour:"numeric",minute:"2-digit"});
      return `${date} · ${time}`;
    }
  }
  return fmtDate(d);
}
function toISO(d){ // any date string -> "YYYY-MM-DD" for input[type=date]
  if(!d) return "";
  if(d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
  // legacy "Sep 14" or "14 Sep" — assume 2026
  const mi=MONTHS.findIndex(mn=>d.includes(mn));
  if(mi<0) return "";
  const numMatch=d.match(/(\d+)/);
  if(!numMatch) return "";
  const day=parseInt(numMatch[1]);
  return `2026-${String(mi+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
}
function isoDay(iso){ // "2026-09-14" -> 14
  if(!iso) return null;
  if(iso.match(/^\d{4}-\d{2}-\d{2}$/)) return parseInt(iso.split("-")[2]);
  const m=iso.match(/(\d+)\s*$/);
  return m?parseInt(m[1]):null;
}
/* ── DRAGGABLE BOTTOM SHEET ── */
function BottomSheet({onClose, children, title="", headerAction=null}){
  return (
    <div className="tmov">
      {/* Page header */}
      <div className="page-header">
        <button className="page-header-back" onClick={onClose}>{I.arrowLeft}</button>
        <div className="page-header-title">{title}</div>
        {headerAction}
      </div>
      <div className="tmod" onClick={e=>e.stopPropagation()}>
        <div className="tmod-scroll"
          onTouchStart={e=>e.stopPropagation()}
          onTouchMove={e=>e.stopPropagation()}>
          {children}
        </div>
      </div>
    </div>
  );
}

 
function buildCal(clients, calEntries){ // derive calendar from client timelines + custom entries — keyed by "YYYY-M-D"
  const cal={};
  (clients||[]).forEach(c=>{
    if(c.archived) return; // skip archived clients
    (c.tl||[]).forEach(step=>{
      if(!step.d) return;
      let y,m,d;
      if(step.d.match(/^\d{4}-\d{2}-\d{2}$/)){
        [y,m,d]=[parseInt(step.d.split("-")[0]),parseInt(step.d.split("-")[1]),parseInt(step.d.split("-")[2])];
      } else {
        // legacy "Sep 14" format — assume 2026
        const mi=MONTHS.findIndex(mn=>step.d.includes(mn));
        const dm=step.d.match(/(\d+)/);
        if(mi<0||!dm) return;
        y=2026; m=mi+1; d=parseInt(dm[1]);
      }
      const key=`${y}-${m}-${d}`;
      if(!cal[key]) cal[key]=[];
      cal[key].push({name:c.name,type:step.l,col:c.col,clientId:c.id});
    });
  });
  // Merge custom calendar entries
  const clientMap={};
  (clients||[]).forEach(c=>{clientMap[c.id]=c;});
  (calEntries||[]).forEach(e=>{
    if(!e.date) return;
    if(e.clientId&&clientMap[e.clientId]?.archived) return; // skip archived client entries
    const [y,m,d]=[parseInt(e.date.split("-")[0]),parseInt(e.date.split("-")[1]),parseInt(e.date.split("-")[2])];
    const key=`${y}-${m}-${d}`;
    if(!cal[key]) cal[key]=[];
    const client=clientMap[e.clientId];
    cal[key].push({name:client?.name||"Note",type:e.subject,col:client?.col||"var(--gm)",clientId:e.clientId||null,calEntryId:e.id,time:e.time,notes:e.notes,date:e.date,isCalEntry:true});
  });
  return cal;
}

// Status labels surface as pills/chips on client cards, kanban columns,
// pipeline rows, and the client portal timeline. The underlying status id
// stays "active" (every RPC, every status filter, the DB itself all use the
// id) — only the human label moves to "In progress" so it stops conflating
// with the "active client = non-archived" mental model.
const STATUS_STAGES=[
  {key:"inquiry",   label:"Inquiry",     col:"#6B7280"},
  {key:"pending",   label:"Pending",     col:"var(--warn-tx)"},
  {key:"signed",    label:"Signed",      col:"var(--ok-tx)"},
  {key:"active",    label:"In progress", col:"#1C4D44"},
  {key:"delivered", label:"Delivered",   col:"#6B4F9E"},
  {key:"complete",  label:"Complete",    col:"#1e7e54"},
];

function buildRevenue(clients){
  const _now=new Date(), TODAY_M=_now.getMonth()+1, TODAY_Y=_now.getFullYear();
  // Build 13-month window: 6 months back, current, 6 months forward
  const WINDOW=[];
  for(let i=-6;i<=6;i++){
    let m=TODAY_M+i, y=TODAY_Y;
    while(m<1){m+=12;y--;}
    while(m>12){m-=12;y++;}
    WINDOW.push({mo:MONTHS[m-1].toUpperCase(),y,m,idx:i+6});
  }
  const totals=WINDOW.map(w=>({...w,rev:0}));
  (clients||[]).forEach(c=>{
    const raw=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
    const dep=round2(raw*(c.depositPct||50)/100);
    const bal=round2(raw*(100-(c.depositPct||50))/100);

    const getPayMonth=(paidDateField, tlKeyword)=>{
      const pd=c[paidDateField];
      if(pd&&pd.match(/^\d{4}-\d{2}-\d{2}$/)){
        return {m:parseInt(pd.split("-")[1]),y:parseInt(pd.split("-")[0])};
      }
      const tl=c.tl||[];
      const step=tl.find(s=>s.l&&s.l.toLowerCase().includes(tlKeyword)&&s.s==="done"&&s.d);
      if(step?.d){const iso=toISO(step.d);if(iso)return{m:parseInt(iso.split("-")[1]),y:parseInt(iso.split("-")[0])};}
      return {m:TODAY_M,y:TODAY_Y};
    };

    if(c.depositPaid&&dep>0){
      const mo=getPayMonth("depositPaidDate","deposit");
      const slot=totals.find(w=>w.m===mo.m&&w.y===mo.y);
      if(slot) slot.rev+=dep;
    }
    if(c.balancePaid&&bal>0){
      const mo=getPayMonth("balancePaidDate","balance");
      const slot=totals.find(w=>w.m===mo.m&&w.y===mo.y);
      if(slot) slot.rev+=bal;
    }
    // Paid additional invoices count as revenue in the month they were paid
    // (or created, if paidAt wasn't recorded — older data / in-app mark-paid).
    (c.invoices||[]).forEach(inv=>{
      if(!inv||!inv.paid) return;
      const amt=Number(inv.amount)||0;
      if(amt<=0) return;
      const raw=inv.paidAt||inv.createdAt||"";
      const iso=toISO(raw);
      let im=TODAY_M, iy=TODAY_Y;
      if(iso&&iso.match(/^\d{4}-\d{2}-\d{2}$/)){
        im=parseInt(iso.split("-")[1]);
        iy=parseInt(iso.split("-")[0]);
      } else if(raw){
        const dt=new Date(raw);
        if(!isNaN(dt)){ im=dt.getMonth()+1; iy=dt.getFullYear(); }
      }
      const slot=totals.find(w=>w.m===im&&w.y===iy);
      if(slot) slot.rev+=amt;
    });
    // Subtract refunds from the month they occurred
    (c.refunds||[]).forEach(r=>{
      const amt=parseFloat(r.amount||0)||0;
      if(amt<=0) return;
      const rd=r.date?toISO(r.date):null;
      const rm=rd?parseInt(rd.split("-")[1]):TODAY_M;
      const ry=rd?parseInt(rd.split("-")[0]):TODAY_Y;
      const slot=totals.find(w=>w.m===rm&&w.y===ry);
      if(slot) slot.rev-=amt;
    });
  });
  const maxRev=Math.max(...totals.map(w=>w.rev),1);
  return totals.map(w=>({...w,h:Math.max(8,Math.round((w.rev/maxRev)*56))}));
}


const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&display=swap');
*{margin:0;padding:0;box-sizing:border-box;}
:root{--cr:#E0E6E4;--g:#7aa19a;--gm:#7aa19a;--gl:#D0DBD8;--t:#9CCBBB;--tc:#0B0E0D;--tl:#E0F9EF;--ink:#0B0E0D;--mu:#5A6B65;--bo:#C0CEC9;--card:#ffffff;--input-bg:#ffffff;--warn-bg:#FEF3E2;--warn-bo:#F59E0B;--warn-tx:#B45309;--err-bg:#FEF2F2;--err-bo:#FECACA;--err-tx:#DC2626;--info-bg:#FFFBEB;--info-bo:#FDE68A;--info-tx:#92400E;--ok-tx:#059669;--ok-bg:#ECFDF5;--auth-bg:#E0E6E4;--auth-ink:#0B0E0D;--auth-mu:#5A6B65;--auth-input-bg:#fff;--auth-input-bo:#C0CEC9;--scheme:light;}
body.dark{--cr:#121B1A;--g:#1C4D44;--gm:#9CCBBB;--gl:#1A2926;--t:#9CCBBB;--tc:#121B1A;--tl:#1A3028;--ink:#E0E6E4;--mu:#7A9085;--bo:#2A3D36;--card:#182521;--input-bg:#1E2922;--warn-bg:#2A2314;--warn-bo:#78590A;--warn-tx:#F5B942;--err-bg:#2A1616;--err-bo:#7C2D2D;--err-tx:#F87171;--info-bg:#2A2314;--info-bo:#78590A;--info-tx:#FCD34D;--ok-tx:#34D399;--ok-bg:#0F2A1F;--auth-bg:#121B1A;--auth-ink:#E0E6E4;--auth-mu:#7A9085;--auth-input-bg:#1E2922;--auth-input-bo:#2A3D36;--scheme:dark;}
html,body{height:100%;overflow:hidden;font-family:'Inter',sans-serif;font-variant-numeric:lining-nums tabular-nums;-moz-font-feature-settings:'tnum','lnum';-webkit-font-feature-settings:'tnum','lnum';font-feature-settings:'tnum','lnum';color:var(--ink);}
button,input,select,textarea{color:inherit;}
.num{font-variant-numeric:tabular-nums lining-nums;font-feature-settings:'tnum','lnum';letter-spacing:-.01em;}
.app{background:var(--cr);height:100vh;height:100dvh;max-width:430px;margin:0 auto;position:relative;overflow:hidden;display:flex;flex-direction:column;}
/* Each overlay page is a flex column that fills the app — only the inner scroll area scrolls */
.page{flex:1;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding-bottom:max(env(safe-area-inset-bottom,16px),16px);scrollbar-width:none;}
.page::-webkit-scrollbar{display:none;}
.page-slide{position:absolute;inset:0;z-index:400;background:var(--cr);display:flex;flex-direction:column;overflow:hidden;}
.page-slide-inner{flex:1;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding-bottom:40px;scrollbar-width:none;}
.page-slide-inner::-webkit-scrollbar{display:none;}
.page-header{background:var(--g);padding:max(env(safe-area-inset-top),48px) 20px 20px;display:flex;align-items:center;gap:12px;flex-shrink:0;}
.page-header-back{width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,.15);display:flex;align-items:center;justify-content:center;cursor:pointer;border:none;color:#fff;font-size:18px;font-weight:700;flex-shrink:0;line-height:1;padding:0;text-align:center;}
.page-header-title{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:#fff;flex:1;}
.page-header-action{background:rgba(255,255,255,.15);border:none;color:#fff;border-radius:10px;padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;}

/* AUTH */
.auth{min-height:100dvh;background:var(--auth-bg);padding:52px 24px 48px;position:fixed;inset:0;overflow-y:auto;overflow-x:hidden;z-index:100;color:var(--auth-ink);scrollbar-width:none;-ms-overflow-style:none;-webkit-overflow-scrolling:touch;box-sizing:border-box;}
.auth::-webkit-scrollbar{display:none;width:0;height:0;}
.auth-inner{width:100%;position:relative;z-index:1;box-sizing:border-box;}
.auth::before{content:'';position:absolute;top:-80px;right:-80px;width:300px;height:300px;border-radius:50%;background:rgba(122,161,154,.1);pointer-events:none;}
.alogo{font-family:'Cormorant Garamond',serif;font-size:26px;font-weight:600;color:var(--auth-ink);margin-bottom:40px;position:relative;z-index:1;}
.atitle{font-family:'Cormorant Garamond',serif;font-size:32px;font-weight:400;color:var(--auth-ink);line-height:1.2;margin-bottom:8px;position:relative;z-index:1;}
.asub{font-size:13px;color:var(--auth-mu);font-weight:300;line-height:1.6;margin-bottom:28px;position:relative;z-index:1;}
.alabel{font-size:11px;text-transform:uppercase;letter-spacing:.09em;color:var(--auth-mu);font-weight:500;margin-bottom:10px;display:block;position:relative;z-index:1;}
.ainput{width:100%;background:var(--auth-input-bg);border:1.5px solid var(--auth-input-bo);border-radius:14px;padding:14px 18px;font-size:15px;font-family:'Inter',sans-serif;color:var(--auth-ink);outline:none;margin-bottom:14px;position:relative;z-index:1;display:block;}
.ainput::placeholder{color:var(--mu);}
.ainput:focus{border-color:#7AA19A;box-shadow:0 0 0 3px rgba(156,203,187,.3);}
.abtn{background:#1C4D44;color:#fff;border:none;border-radius:14px;padding:15px;font-size:15px;font-weight:600;font-family:'Inter',sans-serif;cursor:pointer;width:100%;position:relative;z-index:1;display:block;margin-bottom:12px;}
.abtn:disabled{opacity:.4;cursor:default;}
.abtn.sec{background:var(--gl);color:var(--gm);border:1px solid var(--bo);}
.anote{font-size:12px;color:var(--auth-mu);text-align:center;line-height:1.6;position:relative;z-index:1;}
.anote span{color:#C96A1A;cursor:pointer;}
.sent-box{background:var(--auth-input-bg);border:1px solid var(--bo);border-radius:12px;padding:14px 16px;display:flex;align-items:center;gap:12px;margin-bottom:24px;position:relative;z-index:1;}
.sent-txt{font-size:13px;color:var(--auth-mu);line-height:1.5;}
.sent-em{font-weight:600;color:var(--auth-ink);}
.divider{display:flex;align-items:center;gap:12px;margin:20px 0;position:relative;z-index:1;}
.divline{flex:1;height:1px;background:var(--bo);}
.divtxt{font-size:11px;color:var(--mu);text-transform:uppercase;letter-spacing:.08em;}

/* DEMO ACCOUNT CARDS */

/* CODE BOXES */
.code-row{display:flex;gap:5px;margin-bottom:24px;position:relative;z-index:1;}
.cbox{flex:1;height:46px;background:var(--auth-input-bg);border:1.5px solid var(--auth-input-bo);border-radius:10px;text-align:center;font-size:18px;font-weight:600;color:var(--auth-ink);font-family:'Inter',sans-serif;outline:none;min-width:0;}
.cbox:focus{border-color:#7AA19A;background:rgba(156,203,187,.1);}
.resend-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;position:relative;z-index:1;}
.rtimer{font-size:12px;color:var(--mu);}
.rlink{font-size:12px;color:var(--gm);cursor:pointer;font-weight:500;}
.rlink.off{color:var(--bo);cursor:default;}

/* ONBOARDING */
.ob{min-height:100dvh;background:var(--auth-bg);color:var(--auth-ink);padding:60px 28px 48px;position:fixed;inset:0;overflow-x:hidden;overflow-y:auto;-webkit-overflow-scrolling:touch;z-index:100;box-sizing:border-box;scrollbar-width:none;-ms-overflow-style:none;}
.ob::-webkit-scrollbar{display:none;width:0;height:0;}
.ob-inner{width:100%;position:relative;z-index:1;box-sizing:border-box;}
.ob::before{content:'';position:absolute;top:-80px;right:-80px;width:300px;height:300px;border-radius:50%;background:rgba(156,203,187,.12);pointer-events:none;}
.ob-logo{font-family:'Cormorant Garamond',serif;font-size:26px;font-weight:600;color:var(--auth-ink);margin-bottom:48px;position:relative;z-index:1;}
.ob-title{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:400;color:var(--auth-ink);line-height:1.2;margin-bottom:10px;position:relative;z-index:1;}
.ob-sub{font-size:14px;color:var(--auth-mu);font-weight:300;line-height:1.6;margin-bottom:36px;position:relative;z-index:1;}
/* Responsive lists: single column on mobile, grid on tablet+ */
.ob-verts{display:flex;flex-direction:column;gap:8px;margin-bottom:16px;position:relative;z-index:1;}
.ob-vert-item{display:flex;align-items:center;gap:14px;background:var(--auth-input-bg);border:1.5px solid var(--auth-input-bo);border-radius:14px;padding:13px 16px;cursor:pointer;position:relative;transition:all .15s;box-sizing:border-box;min-width:0;}
.ob-vert-item.sel{background:rgba(156,203,187,.15);border-color:#7AA19A;}
.ob-countries{display:flex;flex-direction:column;gap:6px;margin-bottom:20px;position:relative;z-index:1;}
.ob-country-item{display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:12px;background:var(--auth-input-bg);border:1.5px solid var(--auth-input-bo);cursor:pointer;box-sizing:border-box;min-width:0;}
.ob-country-item.sel{background:rgba(156,203,187,.2);border-color:#7AA19A;}
/* Signup field grids: single column on mobile, 2 col on tablet+ */
.auth-grid,.auth-grid-pair{display:flex;flex-direction:column;gap:0;}
.ob-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:28px;position:relative;z-index:1;}
.ob-card{background:var(--card);border:1.5px solid var(--bo);border-radius:16px;padding:18px 14px;cursor:pointer;}
.ob-card.sel{background:rgba(156,203,187,.2);border-color:#7AA19A;}
.ob-icon{font-size:26px;margin-bottom:10px;}
.ob-ct{font-size:14px;font-weight:600;color:#0B0E0D;margin-bottom:3px;}
.ob-cs{font-size:11px;color:#5A6B65;}
.ob-btn{background:#1C4D44;color:#fff;border:none;border-radius:14px;padding:16px;font-size:15px;font-weight:600;font-family:'Inter',sans-serif;cursor:pointer;width:100%;position:relative;z-index:1;}
.ob-btn:disabled{opacity:.4;cursor:default;}

/* TOPBAR */
.topbar{background:var(--g);padding:48px 24px 24px;position:relative;}
.topbar::before{content:'';position:absolute;top:-40px;right:-40px;width:180px;height:180px;border-radius:50%;background:rgba(156,203,187,.15);}
.trow{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
.logo{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:#fff;letter-spacing:.02em;}
.logo span{color:var(--t);font-style:italic;}
.ava{width:52px;height:52px;border-radius:50%;background:var(--t);display:flex;align-items:center;justify-content:center;font-size:17px;font-weight:600;color:var(--tc);cursor:pointer;position:relative;z-index:10;}
.greet{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:400;color:#fff;line-height:1.2;margin-bottom:4px;}
.greet em{font-style:italic;color:rgba(255,255,255,.7);}
.dline{font-size:12px;color:rgba(255,255,255,.5);font-weight:300;letter-spacing:.06em;text-transform:uppercase;}
.srow{display:flex;gap:10px;padding:20px 20px 28px;background:var(--g);}
.spill{flex:1 1 0;min-width:0;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.15);border-radius:12px;padding:14px 8px;text-align:center;}
.snum{font-family:'Cormorant Garamond',serif;font-size:clamp(20px,6.2vw,28px);font-weight:600;color:#fff;line-height:1.1;margin-bottom:4px;font-variant-numeric:lining-nums tabular-nums;font-feature-settings:'tnum','lnum';overflow-wrap:anywhere;}
.snum.t{color:#E5484D;}
.slbl{font-size:10px;color:rgba(255,255,255,.55);text-transform:uppercase;letter-spacing:.06em;line-height:1.3;overflow-wrap:break-word;}
.ttop{background:var(--g);padding:max(env(safe-area-inset-top),48px) 24px 20px;}
.ttrow{display:flex;align-items:center;justify-content:space-between;}
.ttitle{font-family:'Cormorant Garamond',serif;font-size:26px;font-weight:600;color:#fff;}
.ticn{width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,.12);border:none;display:flex;align-items:center;justify-content:center;font-size:18px;color:#fff;cursor:pointer;}
.body{padding:0 0 24px;background:var(--cr);border-radius:24px 24px 0 0;margin-top:0;overflow-x:hidden;}
.sh{display:flex;align-items:baseline;justify-content:space-between;padding:28px 24px 14px;}
.chips+.sh{padding-top:6px;}
.stitle{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:600;color:var(--ink);}
.slink{font-size:12px;color:#F08A24;cursor:pointer;font-weight:500;}

/* USER MENU */
/* z-index:50 keeps the avatar + its dropdown above unstyled home content
   (anything sitting at the default auto stack) while staying BELOW the
   SettingsSheet overlay (z-index:600), the upgrade/confirm dialog inside
   it (z-index:700), and every other modal in the app. Previously this was
   1000, which made the avatar punch through the SettingsSheet's dim layer
   and float on top of "Billing & Plan", "Account Settings", etc.
   The .umenu z-index is relative to .ava-wrap's local stacking context —
   it must stay higher than the inline 998-z-index backdrop sibling so
   menu items remain clickable, even though globally the entire subtree
   sits at z-index:50. */
.ava-wrap{position:relative;z-index:50;}
.umenu{position:absolute;top:100%;right:0;margin-top:8px;background:var(--card);border-radius:14px;box-shadow:0 8px 32px rgba(0,0,0,.18);border:1px solid var(--bo);z-index:1001;min-width:220px;overflow:hidden;}
.umh{padding:14px 16px;background:var(--gl);border-bottom:1px solid var(--bo);}
.umn{font-size:14px;font-weight:600;color:var(--ink);}
.ume{font-size:12px;color:var(--mu);margin-top:2px;}
.umi{padding:13px 16px;font-size:14px;color:var(--ink);cursor:pointer;display:flex;align-items:center;gap:10px;border-bottom:1px solid var(--bo);}
.umi:last-child{border-bottom:none;}
.umi.red{color:var(--err-tx);}

/* ACTION CARDS */
.alist{padding:0 24px;display:flex;flex-direction:column;gap:10px;}
.acard{background:var(--card);border-radius:14px;padding:14px 16px;display:flex;align-items:center;gap:14px;box-shadow:0 2px 16px rgba(28,77,68,.08);cursor:pointer;border:1px solid var(--bo);}
.aico{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0;}
.aico.g{background:var(--gl);}
.aico.t{background:var(--tl);}
.aico.y{background:var(--warn-bg);}
.atxt{flex:1;}
.at{font-size:14px;font-weight:600;color:var(--ink);margin-bottom:2px;}
.as{font-size:12px;color:var(--mu);font-weight:300;}
.badge{background:#E5484D;color:#fff;font-size:11px;font-weight:600;border-radius:20px;padding:2px 8px;white-space:nowrap;}
.badge.g{background:var(--gm);}
.arr{font-size:16px;color:var(--bo);}

/* CLIENT SCROLL */
.cscroll{display:flex;gap:14px;padding:0 24px;overflow-x:auto;scrollbar-width:none;}
.cscroll::-webkit-scrollbar{display:none;}
.ccard{background:var(--card);border-radius:18px;padding:18px;min-width:200px;box-shadow:0 2px 16px rgba(28,77,68,.08);border:1px solid var(--bo);cursor:pointer;flex-shrink:0;}
.ccard.ft{background:var(--g);border-color:transparent;}
.cavr{display:flex;align-items:center;gap:10px;margin-bottom:14px;}
.cav{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:14px;color:#fff;flex-shrink:0;}
.cn{font-size:14px;font-weight:600;color:var(--ink);}
.cn.li{color:#fff;}
.ct{font-size:11px;color:var(--mu);}
.ct.li{color:rgba(255,255,255,.55);}
.prow{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;}
.ps{font-size:11px;color:var(--mu);}
.ps.li{color:rgba(255,255,255,.6);}
.pp{font-size:11px;font-weight:600;color:var(--gm);}
.pp.li{color:var(--t);}
.pbar{height:4px;background:var(--gl);border-radius:99px;overflow:hidden;margin-bottom:12px;}
.pbar.dk{background:rgba(255,255,255,.15);}
.pfil{height:100%;border-radius:99px;background:var(--gm);}
.pfil.t{background:var(--t);}
.tags{display:flex;gap:6px;flex-wrap:wrap;}
.tag{font-size:10px;padding:3px 8px;border-radius:20px;font-weight:500;background:var(--gl);color:var(--gm);}
.tag.t{background:var(--tl);color:var(--t);}
.tag.dk{background:rgba(255,255,255,.15);color:rgba(255,255,255,.85);}

/* REVENUE */
.rcrd{margin:0 24px;background:var(--card);border-radius:16px;padding:18px;border:1px solid var(--bo);box-shadow:0 2px 16px rgba(28,77,68,.08);}
.rhdr{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:16px;}
.rlbl{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--mu);}
.rval{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:600;color:var(--ink);font-variant-numeric:lining-nums tabular-nums;font-feature-settings:'tnum','lnum';}
.rsub{font-size:11px;color:var(--ok-tx);font-weight:500;}
.bch{display:flex;align-items:flex-end;gap:6px;height:56px;}
.bcol{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;}
.bar{border-radius:4px 4px 0 0;width:100%;}
.bmo{font-size:9px;color:var(--mu);letter-spacing:.04em;}
.adm-chart{display:flex;align-items:flex-end;gap:8px;height:220px;padding:24px 0 0;position:relative;}
.adm-chart-col{flex:1;display:flex;flex-direction:column;align-items:center;gap:6px;position:relative;z-index:1;}
.adm-chart-col:hover .adm-chart-bar{opacity:.85;transform:scaleX(1.08);}
.adm-chart-bar{border-radius:6px 6px 0 0;width:100%;transition:opacity .15s,transform .15s;transform-origin:bottom center;}
.adm-chart-val{font-size:11px;font-weight:700;color:var(--ink);min-height:16px;}
.adm-chart-lbl{font-size:11px;color:var(--mu);font-weight:500;letter-spacing:.02em;}
.adm-chart-grid{position:absolute;inset:24px 0 22px 0;display:flex;flex-direction:column;justify-content:space-between;pointer-events:none;}
.adm-chart-grid div{border-bottom:1px solid var(--bo);opacity:.4;}

/* AGENDA */
.aglist{padding:0 24px;display:flex;flex-direction:column;gap:8px;}
.agitem{background:var(--card);border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:12px;border:1px solid var(--bo);}
.agn{font-size:13px;font-weight:500;color:var(--ink);flex:1;}
.agt{font-size:11px;color:var(--mu);}
.agd{font-size:11px;font-weight:600;color:var(--gm);}

/* CLIENTS TAB */
.swrap{position:relative;padding:16px 24px 0;}
.sico{position:absolute;left:36px;top:50%;transform:translateY(-50%);font-size:14px;color:var(--mu);pointer-events:none;}
.sinput{width:100%;background:var(--input-bg);border:1.5px solid var(--bo);border-radius:12px;padding:11px 14px 11px 38px;font-size:14px;font-family:'Inter',sans-serif;color:var(--ink);outline:none;}
.chips{display:flex;gap:8px;padding:14px 24px;overflow-x:auto;scrollbar-width:none;}
.chips::-webkit-scrollbar{display:none;}
.chip{padding:6px 14px;border-radius:99px;font-size:12px;font-weight:500;border:1.5px solid var(--bo);background:var(--card);color:var(--mu);cursor:pointer;white-space:nowrap;flex-shrink:0;}
.chip.on{background:var(--g);border-color:var(--g);color:#fff;}
.clist{padding:0 24px;display:flex;flex-direction:column;gap:10px;}
.crow{background:var(--card);border-radius:14px;padding:14px 16px;display:flex;align-items:center;gap:14px;border:1px solid var(--bo);box-shadow:0 2px 16px rgba(28,77,68,.08);cursor:pointer;}
.cravg{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:15px;color:#fff;flex-shrink:0;}
.crn{font-size:14px;font-weight:600;color:var(--ink);margin-bottom:2px;}
.crs{font-size:11px;color:var(--mu);}
.crr{margin-left:auto;text-align:right;}
.cra{font-size:13px;font-weight:600;color:var(--ink);margin-bottom:3px;}
.spil{font-size:10px;font-weight:600;padding:3px 8px;border-radius:99px;display:inline-block;}
.spil.signed{background:var(--gl);color:var(--gm);}
.spil.pending{background:var(--warn-bg);color:var(--warn-tx);}
.spil.inquiry{background:var(--tl);color:var(--t);}
.spil.cancelled{background:var(--err-bg);color:var(--err-tx);}
.spil.refund-pending{background:var(--warn-bg);color:var(--warn-tx);}
.spil.archived{background:var(--cr);color:var(--mu);}
.spil.free{background:var(--tl);color:var(--t);}
.spil.pro{background:var(--gl);color:var(--gm);}
.spil.studio{background:var(--gl);color:var(--gm);}

/* CALENDAR */
.cnav{display:flex;align-items:center;justify-content:space-between;padding:20px 24px 12px;}
.cmo{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:var(--ink);font-variant-numeric:lining-nums tabular-nums;}
.carr{width:32px;height:32px;border-radius:50%;background:var(--card);border:1.5px solid var(--bo);display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;color:var(--ink);}
.cdow{display:grid;grid-template-columns:repeat(7,1fr);padding:0 24px 8px;}
.cdowc{text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:var(--mu);padding:4px 0;}
.cgrid{display:grid;grid-template-columns:repeat(7,1fr);gap:3px;padding:0 24px;}
.cday{min-height:46px;display:flex;flex-direction:column;align-items:center;padding:7px 2px 5px;border-radius:10px;cursor:pointer;position:relative;}
.cday.tod{background:var(--g);}
.cday.sel{background:var(--tl);}
.cday.oth .cdaynum{color:var(--bo);}
.cdaynum{font-size:13px;font-weight:500;color:var(--ink);line-height:1;margin-bottom:4px;font-variant-numeric:tabular-nums;}
.cday.tod .cdaynum{color:#fff;}
.cdots{display:flex;gap:3px;flex-wrap:wrap;justify-content:center;max-width:32px;}
.cdot{width:6px;height:6px;border-radius:2px;flex-shrink:0;}
.cday.tod .cdot{opacity:.85;}
.cevts{padding:20px 24px 0;}
.cevttl{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--mu);margin-bottom:12px;}
.cevt{background:var(--card);border-radius:12px;padding:12px 14px;margin-bottom:8px;display:flex;align-items:center;gap:12px;border:1px solid var(--bo);}
.cest{width:3px;border-radius:99px;flex-shrink:0;align-self:stretch;min-height:32px;}
.cen{font-size:14px;font-weight:500;color:var(--ink);}
.cety{font-size:11px;color:var(--mu);}
.ceti{margin-left:auto;font-size:11px;font-weight:600;color:var(--gm);}
.clegend{display:flex;flex-wrap:wrap;gap:8px;padding:0 24px 16px;}
.cleg{display:flex;align-items:center;gap:5px;font-size:11px;color:var(--ink);font-weight:500;}
.clegdot{width:8px;height:8px;border-radius:2px;flex-shrink:0;}

/* INBOX */
.ilist{padding:0 24px;display:flex;flex-direction:column;}
.ithr{display:flex;align-items:center;gap:14px;padding:16px 0;border-bottom:1px solid var(--bo);cursor:pointer;}
.ithr:last-child{border-bottom:none;}
.iava{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:15px;color:#fff;flex-shrink:0;position:relative;}
.iudot{position:absolute;top:0;right:0;width:12px;height:12px;background:#E5484D;border-radius:50%;border:2px solid var(--cr);}
.ibod{flex:1;min-width:0;}
.inam{font-size:14px;font-weight:600;color:var(--ink);margin-bottom:3px;}
.ipre{font-size:12px;color:var(--mu);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ipre.u{color:var(--ink);font-weight:500;}
.irt{text-align:right;flex-shrink:0;}
.itm{font-size:11px;color:var(--mu);margin-bottom:6px;}
.icnt{background:#E5484D;color:#fff;font-size:10px;font-weight:700;border-radius:99px;padding:2px 7px;display:inline-block;}
.msgs{flex:1;overflow-y:auto;padding:20px 24px;display:flex;flex-direction:column;gap:12px;scrollbar-width:none;}
.msgs::-webkit-scrollbar{display:none;}
.msg{max-width:75%;}
.msg.me{align-self:flex-end;}
.mbub{padding:11px 14px;border-radius:16px;font-size:13px;line-height:1.5;}
.msg.them .mbub{background:var(--card);color:var(--ink);border:1px solid var(--bo);border-bottom-left-radius:4px;}
.msg.me .mbub{background:var(--g);color:#fff;border-bottom-right-radius:4px;}
.mtm{font-size:10px;color:var(--mu);margin-top:4px;padding:0 4px;}
.msg.me .mtm{text-align:right;}
.cfooter{padding:12px 24px;padding-bottom:max(env(safe-area-inset-bottom,12px),12px);background:var(--cr);border-top:1px solid var(--bo);flex-shrink:0;}
.aisugg{background:var(--gl);border-radius:10px;padding:10px 12px;margin-bottom:10px;font-size:12px;color:var(--gm);display:flex;align-items:center;gap:8px;cursor:pointer;}
.cinrow{display:flex;gap:10px;align-items:center;}
.cinput{flex:1;background:var(--input-bg);border:1.5px solid var(--bo);border-radius:12px;padding:11px 14px;font-size:14px;font-family:'Inter',sans-serif;color:var(--ink);outline:none;}
.sbtn{width:40px;height:40px;background:var(--g);border:none;border-radius:10px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;flex-shrink:0;color:#fff;}

/* CONTRACTS */
.ktlist{padding:0 24px;display:flex;flex-direction:column;gap:10px;}
.kcard{background:var(--card);border-radius:14px;padding:16px;border:1px solid var(--bo);box-shadow:0 2px 16px rgba(28,77,68,.08);cursor:pointer;}
.ktop{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:12px;}
.kn{font-size:14px;font-weight:600;color:var(--ink);margin-bottom:3px;}
.ks{font-size:12px;color:var(--mu);}
.kst{font-size:10px;font-weight:700;padding:4px 10px;border-radius:99px;white-space:nowrap;margin-left:8px;}
.kst.signed{background:var(--gl);color:var(--gm);}
.kst.sent{background:var(--warn-bg);color:var(--warn-tx);}
.kst.draft{background:var(--bo);color:var(--mu);}
.kmeta{display:flex;gap:14px;flex-wrap:wrap;}
.kmi{font-size:11px;color:var(--mu);}
.tmov{position:fixed;inset:0;z-index:300;background:var(--cr);display:flex;flex-direction:column;}
.tmod{flex:1;overflow:hidden;display:flex;flex-direction:column;width:100%;}
.tmod-handle{display:none;}
.tmod-handle-bar{display:none;}
.tmod-scroll{flex:1;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;padding:20px 24px 40px;touch-action:pan-y;scrollbar-width:none;}
.tmod-scroll::-webkit-scrollbar{display:none;}
.tmtl{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:var(--ink);margin-bottom:4px;}
.tmsb{font-size:13px;color:var(--mu);margin-bottom:20px;}
.tmlist{display:flex;flex-direction:column;gap:10px;}
.tmitem{background:var(--card);border:1.5px solid var(--bo);border-radius:14px;padding:16px;display:flex;align-items:center;gap:14px;cursor:pointer;}
.tmico{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;}
.tmitn{font-size:14px;font-weight:600;color:var(--ink);margin-bottom:2px;}
.tmits{font-size:12px;color:var(--mu);}

/* CLIENT MODAL — full width */
.mov{position:absolute;inset:0;z-index:200;background:var(--cr);display:flex;flex-direction:column;}
.mov-inner{display:flex;flex-direction:column;flex:1;overflow:hidden;background:var(--cr);}
.mod{flex:1;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding-bottom:40px;width:100%;scrollbar-width:none;}
.mod::-webkit-scrollbar{display:none;}
.mhdr{padding:16px 20px;display:flex;align-items:center;gap:14px;border-bottom:1px solid var(--bo);}
.mava{width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:600;color:#fff;flex-shrink:0;}
.mcn{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:var(--ink);}
.mcs{font-size:12px;color:var(--mu);margin-top:2px;}
.mclose{margin-left:auto;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;background:var(--gl);color:var(--gm);border:none;flex-shrink:0;}
.msec{padding:18px 20px 0;}
.mstl{font-size:11px;text-transform:uppercase;letter-spacing:.09em;color:var(--mu);font-weight:500;margin-bottom:12px;}
.tls{display:flex;flex-direction:column;position:relative;}
.tls::before{content:'';position:absolute;left:14px;top:16px;bottom:16px;width:2px;background:var(--bo);}
.tlstep{display:flex;align-items:flex-start;gap:16px;padding:10px 0;position:relative;}
.tldot{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0;position:relative;z-index:1;}
.tldot.done{background:var(--g);color:#fff;}
.tldot.active{background:var(--t);color:var(--tc);}
.tldot.pend{background:var(--card);border:2px solid var(--bo);color:var(--mu);}
.tll{font-size:14px;font-weight:500;color:var(--ink);}
.tll.mu{color:var(--mu);font-weight:400;}
.tld{font-size:11px;color:var(--mu);margin-top:2px;}
.dgrid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.dcell{background:var(--card);border-radius:12px;padding:14px;border:1px solid var(--bo);}
.dclbl{font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:var(--mu);margin-bottom:6px;}
.dcval{font-size:16px;font-weight:600;color:var(--ink);}
.dcval.t{color:var(--t);}
.dcval.g{color:var(--gm);}
.aicard{background:var(--g);border-radius:14px;padding:16px;display:flex;gap:12px;align-items:flex-start;}
.aiico{width:32px;height:32px;background:rgba(156,203,187,.25);border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;}
.aitxt{font-size:13px;color:rgba(255,255,255,.85);line-height:1.5;}
.ailbl{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:var(--t);margin-bottom:4px;font-weight:600;}
.actbtns{display:flex;gap:10px;padding:14px 20px 0;}
.btn{flex:1;padding:13px 10px;border-radius:12px;font-size:13px;font-weight:600;text-align:center;cursor:pointer;border:none;font-family:'Inter',sans-serif;}
.btn.p{background:var(--g);color:#fff;}
.btn.s{background:var(--gl);color:var(--gm);}
.btn.t{background:var(--t);color:var(--tc);}
/* INVOICE PANEL */
.inv-panel{margin:10px 20px 0;background:var(--card);border-radius:14px;border:1px solid var(--bo);overflow:hidden;}
.inv-row{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid var(--bo);}
.inv-row:last-child{border-bottom:none;}
.inv-label{font-size:13px;color:var(--mu);}
.inv-val{font-size:13px;font-weight:600;color:var(--ink);}
.inv-val.g{color:var(--gm);}
.inv-val.t{color:var(--t);}
.inv-send{width:100%;background:var(--g);color:#fff;border:none;padding:13px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;}
/* AI DRAFT PANEL */
.ai-panel{margin:10px 20px 0;background:var(--g);border-radius:14px;padding:16px;}
.ai-panel-lbl{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:var(--t);font-weight:600;margin-bottom:10px;}
.ai-draft-txt{font-size:13px;color:rgba(255,255,255,.85);line-height:1.6;background:rgba(255,255,255,.07);border-radius:10px;padding:12px;margin-bottom:12px;}
.ai-panel-btns{display:flex;gap:8px;}
.ai-pbtn{flex:1;padding:10px;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer;border:none;font-family:'Inter',sans-serif;}
.ai-pbtn.send{background:var(--t);color:var(--tc);}
.ai-pbtn.edit{background:rgba(255,255,255,.12);color:rgba(255,255,255,.8);}
.ai-pbtn.regen{background:rgba(255,255,255,.08);color:rgba(255,255,255,.6);}

/* PORTAL LINK CARD */
.portal-card{background:var(--card);border-radius:14px;border:1px solid var(--bo);overflow:hidden;}
.portal-url{display:flex;align-items:center;gap:10px;padding:12px 14px;border-bottom:1px solid var(--bo);}
.portal-link-text{font-size:12px;font-weight:500;color:var(--gm);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:'Inter',sans-serif;}
.copy-btn{background:var(--gl);border:none;border-radius:8px;padding:6px 12px;font-size:11px;font-weight:600;color:var(--gm);cursor:pointer;flex-shrink:0;font-family:'Inter',sans-serif;}
.copy-btn.copied{background:var(--g);color:#fff;}
.portal-actions{display:flex;}
.portal-action{flex:1;padding:12px;display:flex;flex-direction:column;align-items:center;gap:4px;cursor:pointer;border-right:1px solid var(--bo);}
.portal-action:last-child{border-right:none;}
.portal-action-icon{font-size:18px;}
.portal-action-label{font-size:10px;color:var(--mu);font-weight:500;text-transform:uppercase;letter-spacing:.05em;}

/* PORTAL SCREEN */
.portal-screen{height:100vh;height:100dvh;background:var(--cr);display:flex;flex-direction:column;width:100%;max-width:430px;margin:0 auto;overflow:hidden;}
.portal-header{background:var(--g);padding:max(env(safe-area-inset-top),48px) 24px 28px;position:relative;overflow:hidden;width:100%;flex-shrink:0;}
.portal-header::before{content:'';position:absolute;top:-40px;right:-40px;width:160px;height:160px;border-radius:50%;background:rgba(156,203,187,.15);}
.portal-brand{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:600;color:rgba(255,255,255,.6);margin-bottom:16px;position:relative;z-index:1;}
.portal-brand span{color:var(--t);font-style:italic;}
.portal-client-name{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:600;color:#fff;margin-bottom:4px;position:relative;z-index:1;}
.portal-client-sub{font-size:13px;color:rgba(255,255,255,.55);position:relative;z-index:1;}
.portal-tabs{display:flex;background:var(--g);padding:0 24px 0;border-bottom:1px solid rgba(255,255,255,.1);width:100%;flex-shrink:0;}
.portal-tab{padding:12px 16px;font-size:13px;font-weight:500;color:rgba(255,255,255,.5);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;}
.portal-tab.on{color:#fff;border-bottom-color:var(--t);}
.portal-body{flex:1;min-height:0;padding:24px;width:100%;display:block;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding-bottom:max(env(safe-area-inset-bottom),24px);}
.portal-section-title{font-family:'Cormorant Garamond',serif;font-size:18px;font-weight:600;color:var(--ink);margin-bottom:4px;}
.portal-section-sub{font-size:13px;color:var(--mu);margin-bottom:20px;line-height:1.5;}
/* INTAKE FORM */
.iform{display:flex;flex-direction:column;gap:16px;}
.ifield{display:flex;flex-direction:column;gap:6px;}
.iflabel{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--mu);font-weight:500;}
.ifinput{background:var(--input-bg);border:1.5px solid var(--bo);border-radius:12px;padding:12px 14px;font-size:14px;font-family:'Inter',sans-serif;color:var(--ink);outline:none;width:100%;}
.ifinput:focus{border-color:var(--gm);}
.iftarea{background:var(--input-bg);border:1.5px solid var(--bo);border-radius:12px;padding:12px 14px;font-size:14px;font-family:'Inter',sans-serif;color:var(--ink);outline:none;width:100%;resize:none;min-height:80px;line-height:1.6;}
.iftarea:focus{border-color:var(--gm);}
.isubmit{background:var(--g);color:#fff;border:none;border-radius:14px;padding:15px;font-size:15px;font-weight:600;font-family:'Inter',sans-serif;cursor:pointer;width:100%;margin-top:8px;}
.isubmit:disabled{opacity:.4;cursor:default;}
.intake-done{background:var(--gl);border-radius:16px;padding:20px;text-align:center;}
.intake-done-icon{font-size:36px;margin-bottom:12px;}
.intake-done-title{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:var(--gm);margin-bottom:6px;}
.intake-done-sub{font-size:13px;color:var(--mu);line-height:1.6;}
/* INTAKE STATUS in modal */
.intake-status-card{background:var(--card);border-radius:14px;border:1px solid var(--bo);overflow:hidden;}
.intake-status-row{display:flex;align-items:center;gap:12px;padding:14px 16px;}
.intake-badge{font-size:10px;font-weight:700;padding:4px 10px;border-radius:99px;white-space:nowrap;}
.intake-badge.sent{background:var(--warn-bg);color:var(--warn-tx);}
.intake-badge.received{background:var(--gl);color:var(--gm);}
.intake-badge.notsent{background:var(--bo);color:var(--mu);}
.intake-send-btn{width:100%;background:var(--g);color:#fff;border:none;padding:12px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;}
.intake-view-btn{width:100%;background:var(--gl);color:var(--gm);border:none;padding:12px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;border-top:1px solid var(--bo);}
/* PORTAL OVERVIEW CARD */
.pov-card{background:var(--card);border-radius:14px;border:1px solid var(--bo);padding:16px;margin-bottom:12px;}
.pov-row{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--bo);}
.pov-row:last-child{border-bottom:none;}
.pov-label{font-size:13px;color:var(--mu);}
.pov-val{font-size:13px;font-weight:600;color:var(--ink);}
.pov-val.g{color:var(--gm);}
.pov-val.t{color:var(--t);}
/* Portal invoice / refund grid — base (mobile) = flex column; widens on tablet+ */
.portal-invoices-grid{display:flex;flex-direction:column;gap:10px;}
.portal-refunds-grid{display:flex;flex-direction:column;gap:10px;}

.bnav{background:var(--cr);border-top:1px solid var(--bo);display:flex;flex-direction:column;align-items:center;padding:10px 0 0;z-index:100;flex-shrink:0;padding-bottom:max(env(safe-area-inset-bottom),4px);width:100%;}
.bnav-tabs{display:flex;width:100%;padding-bottom:20px;}
.bnav-footer{font-size:9px;color:var(--bo);letter-spacing:.08em;text-transform:uppercase;padding-bottom:8px;text-align:center;}
.ni{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;cursor:pointer;color:var(--mu);}
.ni.on{color:var(--gm);}
.nicon{font-size:20px;}
.nlbl{font-size:10px;font-weight:500;color:inherit;letter-spacing:.04em;}
.ni.on .nlbl{color:var(--gm);}
.fab{position:fixed;bottom:80px;right:calc(50% - 210px + 20px);width:52px;height:52px;border-radius:50%;background:var(--t);display:flex;align-items:center;justify-content:center;font-size:24px;box-shadow:0 4px 20px rgba(156,203,187,.35);cursor:pointer;z-index:99;color:var(--tc);border:none;}

/* ── DARK MODE OVERRIDES ── */
/* Native date/time pickers: flip the browser's calendar/clock icon to white in dark mode.
   color-scheme:dark tells the UA to render form-control internals with the dark palette. */
body.dark input[type="date"],body.dark input[type="time"],body.dark input[type="datetime-local"],body.dark input[type="month"],body.dark input[type="week"]{color-scheme:dark;}
body.dark input[type="date"]::-webkit-calendar-picker-indicator,body.dark input[type="time"]::-webkit-calendar-picker-indicator,body.dark input[type="datetime-local"]::-webkit-calendar-picker-indicator{filter:invert(1) brightness(1.2);opacity:.9;cursor:pointer;}
body.dark .body{background:var(--cr);}
body.dark .page-slide{background:var(--cr);}
body.dark .tmod-scroll{background:var(--cr);}
body.dark .tmod{background:var(--cr);}
body.dark .msgs{background:var(--cr);}
body.dark .ilist{background:var(--cr);}
body.dark .ithr{border-color:var(--bo);}
body.dark .kcard{background:var(--card);border-color:var(--bo);}
body.dark .kn{color:var(--ink);}
body.dark .rcrd{background:var(--card);border-color:var(--bo);}
body.dark .ttop{background:var(--g);}
body.dark .cfooter{border-top:1px solid var(--bo);background:var(--card);}
/* Force ALL text inside app to respect dark mode ink colour */
body.dark .body *:not(button):not(input):not(textarea):not(select):not(a[class]):not(.abtn):not(.portal-tab):not(.page-header *):not(.topbar *):not(.ttop *):not(.portal-header *){color:var(--ink);}
body.dark .body h1,body.dark .body h2,body.dark .body h3{color:var(--ink);}
/* Fix specific containers that hold scrollable content */
body.dark .page{background:var(--cr);}
body.dark .page-slide-inner{background:var(--cr);}
/* Cards and surfaces */
body.dark div[style*="background:#fff"],body.dark div[style*='background:"var(--card)"']{background:var(--card) !important;}
/* Restore green header bars */
body.dark .page-header{background:var(--g);}
body.dark .topbar{background:var(--g);}
/* Auth (login/signup) dark bg handled by --auth-bg variable */
/* Inline white backgrounds -> card in dark */
body.dark .msec{background:transparent;}
body.dark .iform{background:var(--cr);}
/* Dark mode nav: active icon = Electric Mint */
body.dark .ni.on{color:var(--t);}
body.dark .ni.on .nlbl{color:var(--t);}
/* Dark mode header glow */
body.dark .topbar::before{background:rgba(156,203,187,.12);}
body.dark .bnav{background:var(--cr);}

/* ── RESPONSIVE: TABLET (768px+) ── */
@media (min-width: 768px) {
  .app{max-width:100%;}
  .portal-screen{max-width:100%;}
  /* Auth screens: centred card on tablet/desktop */
  .auth{display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:48px 40px;}
  .auth::before{right:calc(50% - 320px);top:-100px;}
  .auth-inner{width:100%;max-width:640px;background:var(--card);border:1px solid var(--bo);border-radius:22px;padding:48px 52px 42px;box-shadow:0 12px 48px rgba(0,0,0,.1);margin:auto 0;}
  /* Onboarding: wider card on tablet — starts to utilise the extra space */
  .ob{display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:48px 40px;max-width:none;margin:0;}
  .ob::before{right:calc(50% - 360px);top:-100px;}
  .ob-inner{width:100%;max-width:720px;background:var(--card);border:1px solid var(--bo);border-radius:22px;padding:44px 48px 40px;box-shadow:0 12px 48px rgba(0,0,0,.1);margin:auto 0;}
  /* Tone down heading sizes inside the tablet card */
  .auth-inner .alogo{margin-bottom:28px;}
  .auth-inner .atitle{font-size:30px;}
  .auth-inner .asub{margin-bottom:26px;}
  .ob-inner .ob-logo{margin-bottom:28px;}
  .ob-inner .ob-title{font-size:30px;}
  .ob-inner .ob-sub{margin-bottom:28px;}
  /* Two-column lists on tablet — spreads items horizontally */
  .ob-verts{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .ob-countries{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
  /* Two-column field grids for SignUp on tablet+ */
  .auth-grid,.auth-grid-pair{display:grid;grid-template-columns:1fr 1fr;gap:0 18px;}
  .auth-grid>*,.auth-grid-pair>*{min-width:0;}
  .auth-grid-pair>.auth-col{display:flex;flex-direction:column;}
  /* Spacing scale-up */
  .topbar{padding:48px 40px 24px;}
  .srow{padding:20px 40px 28px;}
  .greet{font-size:32px;}
  .sh{padding:28px 32px 14px;}
  .alist,.clist,.ktlist,.aglist{padding:0 32px;}
  .cscroll{padding:0 32px;}
  .swrap{padding:16px 32px 0;}
  .chips{padding:14px 32px;}
  .ilist{padding:0 32px;}
  .msgs{padding:20px 32px;}
  .cfooter{padding:12px 32px 20px;}
  .msec{padding:18px 28px 0;}
  .ttop{padding:max(env(safe-area-inset-top),48px) 32px 20px;}
  .cnav{padding:20px 32px 12px;}
  .cdow{padding:0 32px 8px;}
  .cgrid{padding:0 32px;}
  .cevts{padding:20px 32px 0;}
  .clegend{padding:0 32px 16px;}
  .mhdr{padding:16px 28px;}
  .actbtns{padding:14px 28px 0;}
  /* FAB reposition */
  .fab{right:32px;bottom:32px;}
  /* User menu — already anchored via base .ava-wrap */
  /* Client list grid */
  .clist{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  /* Contract cards grid */
  .ktlist{display:grid;grid-template-columns:1fr 1fr;gap:20px;}
  /* Portal layout */
  .portal-header{padding-left:10%;padding-right:10%;}
  .portal-tabs{padding-left:10%;padding-right:10%;}
  .portal-body{max-width:960px;margin:0 auto;width:100%;padding:28px 32px;}
  .iform{max-width:none;}
  /* Portal: 2-col invoice/refund grids at tablet+ */
  .portal-invoices-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .portal-refunds-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  /* Portal tab layouts start widening on tablet — breaks the mobile shell feel */
  .portal-overview-grid{display:grid;grid-template-columns:minmax(0,1.4fr) minmax(260px,1fr);gap:20px;align-items:start;}
  .portal-overview-main{min-width:0;}
  .portal-overview-aside{min-width:0;}
  .portal-payments-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(280px,1fr);gap:20px;align-items:start;}
  .portal-payments-left{min-width:0;}
  .portal-payments-right{min-width:0;}
  .portal-contract-wrap{max-width:820px;margin:0 auto;}
  .portal-messages-wrap{max-width:760px;margin:0 auto;}
  .portal-intake-wrap{max-width:820px;margin:0 auto;}
  .portal-intake-wrap .iform{display:grid;grid-template-columns:1fr 1fr;gap:14px 18px;}
  .portal-intake-wrap .ifield.ifield-wide{grid-column:1 / -1;}
  /* Client scroll wrap */
  .cscroll{flex-wrap:wrap;overflow-x:visible;}
  .ccard{min-width:220px;flex:1 1 220px;}
}

/* ── RESPONSIVE: DESKTOP (1024px+) ── */
@media (min-width: 1024px) {
  /* App becomes row layout: sidebar + content */
  .app{flex-direction:row;}
  /* Auth: comfortable centred card — small forms don't need full width */
  .auth{padding:56px 48px;}
  .auth-inner{max-width:700px;padding:56px 64px 48px;}
  .auth-inner .atitle{font-size:32px;}
  /* Onboarding: much wider card so content can spread across the screen */
  .ob{padding:56px 48px;}
  .ob-inner{max-width:980px;padding:56px 64px 48px;}
  /* 3-column grids on desktop — full use of horizontal space, no scroll */
  .ob-verts{grid-template-columns:1fr 1fr 1fr;gap:12px;}
  .ob-countries{grid-template-columns:1fr 1fr 1fr;gap:10px;}
  .topbar{padding:32px 40px 24px;}
  .greet{font-size:36px;}
  .logo{font-size:24px;}
  /* Bottom nav → sidebar */
  .bnav{flex-direction:column;width:220px;min-width:220px;border-top:none;border-right:1px solid var(--bo);padding:0;order:-1;height:100dvh;overflow-y:auto;overflow-x:hidden;justify-content:flex-start;}
  .bnav-logo{display:block;font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:600;color:var(--gm);padding:28px 24px 24px;letter-spacing:.02em;}
  .bnav-logo span{color:var(--t);font-style:italic;}
  .bnav-tabs{flex-direction:column;padding:0 8px;gap:2px;padding-bottom:20px;}
  .ni{flex-direction:row;padding:11px 16px;gap:12px;justify-content:flex-start;border-radius:10px;}
  .ni.on{background:var(--gl);}
  .nlbl{font-size:13px;letter-spacing:0;}
  .nicon{font-size:18px;}
  .bnav-footer{padding:12px 24px 16px;margin-top:auto;}
  /* User menu on desktop — already anchored via base .ava-wrap */
  /* Client list 3 columns */
  .clist{grid-template-columns:1fr 1fr 1fr;}
  /* Contract cards 3 columns */
  .ktlist{grid-template-columns:1fr 1fr 1fr;}
  /* Client cards grid */
  .ccard{min-width:240px;flex:1 1 240px;}
  /* Home dashboard grid */
  .home-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 24px;padding:0;}
  .home-col{min-width:0;}
  /* Calendar layout */
  .cal-layout{display:flex;gap:0;}
  .cal-main{flex:1;min-width:0;}
  .cal-side{width:340px;flex-shrink:0;border-left:1px solid var(--bo);overflow-y:auto;max-height:calc(100dvh - 200px);}
  .cal-side .cevts{padding:20px 20px 0;}
  .cday{min-height:72px;}
  .cdaynum{font-size:14px;}
  /* Inbox master-detail */
  .inbox-layout{display:flex;flex:1;min-height:0;}
  .inbox-list{width:360px;flex-shrink:0;border-right:1px solid var(--bo);overflow-y:auto;align-self:stretch;}
  .inbox-detail{flex:1;display:flex;flex-direction:column;overflow:hidden;}
  .inbox-empty{display:flex;align-items:center;justify-content:center;flex:1;color:var(--mu);font-size:14px;}
  /* Modals as centered panels */
  .mov{display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.4);}
  .mov-inner{max-width:700px;width:100%;max-height:90vh;border-radius:16px;overflow:hidden;background:var(--cr);display:flex;flex-direction:column;}
  .tmov{background:rgba(0,0,0,.4);padding:5vh 0;align-items:flex-start;justify-content:center;}
  .tmov>.page-header,.tmov>.tmod{max-width:800px;width:100%;margin-left:auto;margin-right:auto;}
  .tmov>.page-header{border-radius:16px 16px 0 0;}
  .tmov>.tmod{border-radius:0 0 16px 16px;overflow:hidden;max-height:calc(90vh - 80px);background:var(--cr);}
  .page-slide{background:rgba(0,0,0,.4);padding:5vh 0;align-items:flex-start;justify-content:center;}
  .page-slide>.page-header,.page-slide>.page-slide-inner{max-width:640px;width:100%;margin-left:auto;margin-right:auto;}
  .page-slide>.page-header{border-radius:16px 16px 0 0;}
  .page-slide>.page-slide-inner{border-radius:0 0 16px 16px;max-height:calc(90vh - 80px);background:var(--cr);}
  /* Portal wider header */
  .portal-header{padding-left:max(8%,40px);padding-right:max(8%,40px);}
  .portal-tabs{padding-left:max(8%,40px);padding-right:max(8%,40px);}
  /* Portal body uses full desktop width with breathable padding */
  .portal-body{max-width:1200px;margin:0 auto;width:100%;padding:32px 40px max(env(safe-area-inset-bottom),32px);}
  /* Overview tab — 2-col grid on desktop (main content | aside) */
  .portal-overview-grid{display:grid;grid-template-columns:minmax(0,1.5fr) minmax(300px,1fr);gap:24px;align-items:start;}
  .portal-overview-main{min-width:0;}
  .portal-overview-aside{min-width:0;}
  /* Payments tab — 2-col grid on desktop (summary | action) */
  .portal-payments-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(320px,1.2fr);gap:24px;align-items:start;}
  .portal-payments-left{min-width:0;}
  .portal-payments-right{min-width:0;}
  /* Payments: invoice/refund cards in 3 columns on desktop */
  .portal-invoices-grid{grid-template-columns:1fr 1fr 1fr;gap:12px;}
  .portal-refunds-grid{grid-template-columns:1fr 1fr;gap:12px;}
  /* Contract tab — constrain reader width for readable legal text */
  .portal-contract-wrap{max-width:900px;margin:0 auto;}
  /* Messages tab — constrain chat width for readability */
  .portal-messages-wrap{max-width:820px;margin:0 auto;height:100%;display:flex;flex-direction:column;}
  /* Questionnaire — 2-col form grid; textarea fields span full width */
  .portal-intake-wrap{max-width:900px;margin:0 auto;}
  .portal-intake-wrap .iform{display:grid;grid-template-columns:1fr 1fr;gap:16px 20px;}
  .portal-intake-wrap .ifield.ifield-wide{grid-column:1 / -1;}
}

/* ── HOVER STATES ── */
@media (hover: hover) {
  .crow:hover{border-color:var(--gm);transform:translateY(-1px);transition:all .15s;}
  .kcard:hover{border-color:var(--gm);transform:translateY(-1px);transition:all .15s;}
  .ccard:hover{border-color:var(--gm);transform:translateY(-1px);transition:all .15s;}
  .acard:hover{border-color:var(--gm);transition:border-color .15s;}
  .ithr:hover{background:var(--gl);transition:background .15s;}
  .chip:hover:not(.on){border-color:var(--gm);transition:border-color .15s;}
  .ni:hover:not(.on){color:var(--gm);transition:color .15s;}
  .btn:hover{opacity:.9;transition:opacity .15s;}
  .portal-tab:hover:not(.on){color:rgba(255,255,255,.8);transition:color .15s;}
  .crow,.kcard,.ccard{transition:all .15s;}
}

/* ── DARK MODE + DESKTOP ── */
@media (min-width: 1024px) {
  body.dark .bnav{border-right-color:var(--bo);}
  body.dark .ni.on{background:var(--gl);}
  body.dark .inbox-list{border-right-color:var(--bo);}
  body.dark .cal-side{border-left-color:var(--bo);}
  body.dark .mov{background:rgba(0,0,0,.6);}
  body.dark .tmov{background:rgba(0,0,0,.6);}
  body.dark .page-slide{background:rgba(0,0,0,.6);}
  body.dark .mov-inner{background:var(--cr);}
}

/* ── ADMIN THEME (navy) ── */
body.admin{
  --cr:#E8EDF2;--g:#1E3A5F;--gm:#2C5282;--gl:#EBF4FF;
  --t:#4299E1;--tc:#FFFFFF;--tl:#EBF8FF;
  --ink:#1A202C;--mu:#718096;--bo:#CBD5E0;
  --card:#FFFFFF;--input-bg:#FFFFFF;--input-bo:#CBD5E0;
  --auth-bg:#E8EDF2;--auth-ink:#1A202C;
  --warn-bg:#FFFBEB;--warn-bo:#F6AD55;--warn-tx:#C05621;
  --err-bg:#FFF5F5;--err-bo:#FEB2B2;--err-tx:#C53030;
  --ok-tx:#38A169;--ok-bg:#F0FFF4;
  --scheme:light;
}
body.admin.dark{
  --cr:#0F1419;--g:#2B5A8F;--gm:#90CDF4;--gl:#1A365D;
  --t:#63B3ED;--tc:#0F1419;--tl:#1A365D;
  --ink:#E2E8F0;--mu:#A0AEC0;--bo:#2D3748;
  --card:#1A202C;--input-bg:#2D3748;--input-bo:#2D3748;
  --auth-bg:#0F1419;--auth-ink:#E2E8F0;
  --err-bg:#2D2020;--err-tx:#FC8181;--warn-bg:#2D2A1A;--warn-tx:#ECC94B;--ok-tx:#68D391;--ok-bg:#1A2D1A;
  --scheme:dark;
}
body.admin .app{overflow-x:hidden;}
.admin-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;padding:0 24px;max-width:100%;box-sizing:border-box;}
.admin-grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;max-width:100%;box-sizing:border-box;}
.admin-stat{background:var(--card);border:1px solid var(--bo);border-radius:14px;padding:18px 14px;cursor:pointer;overflow:hidden;min-width:0;}
.admin-stat .admin-stat-val{font-size:26px;font-weight:700;color:var(--ink);font-variant-numeric:tabular-nums;}
.admin-stat .admin-stat-lbl{font-size:11px;color:var(--mu);margin-top:2px;text-transform:uppercase;letter-spacing:.04em;}
.admin-urow{background:var(--card);border:1px solid var(--bo);border-radius:12px;padding:14px 16px;margin-bottom:8px;display:flex;align-items:center;gap:12px;cursor:pointer;overflow:hidden;max-width:100%;box-sizing:border-box;}
.admin-badge{font-size:9px;font-weight:700;padding:2px 6px;border-radius:4px;text-transform:uppercase;letter-spacing:.04em;}
.admin-badge.ban{background:var(--err-bg);color:var(--err-tx);}
.admin-badge.sus{background:var(--warn-bg);color:var(--warn-tx);}
.admin-act-row{background:var(--card);border:1px solid var(--bo);border-radius:10px;padding:12px 14px;margin-bottom:6px;display:flex;align-items:flex-start;gap:10px;overflow:hidden;max-width:100%;box-sizing:border-box;}
@media (min-width:600px){
  .admin-grid{grid-template-columns:repeat(4,1fr);gap:14px;}
}
@media (min-width:768px){
  .admin-grid{padding:0 32px;}
}
@media (min-width:1024px){
  .admin-grid{padding:0 40px;}
  body.admin .bnav{border-right-color:var(--bo);}
}
/* Admin dark mode: ensure all text reads correctly against dark backgrounds */
body.admin.dark .admin-stat{background:var(--card);border-color:var(--bo);}
body.admin.dark .admin-urow{background:var(--card);border-color:var(--bo);}
body.admin.dark .admin-act-row{background:var(--card);border-color:var(--bo);}
body.admin.dark .spil.free{background:#1A365D;color:#90CDF4;}
body.admin.dark .spil.pro{background:#1A365D;color:#63B3ED;}
body.admin.dark .spil.studio{background:#1A365D;color:#90CDF4;}
`;

/* ── VERTICALS ── */
const VERTS = [
  {id:"photographer",    icon:I.camera, title:"Photographer",       sub:"Weddings, portraits, events"},
  {id:"videographer",    icon:I.film, title:"Videographer",        sub:"Weddings, reels, commercials"},
  {id:"designer",        icon:I.penTool, title:"Graphic Designer",    sub:"Branding, print, digital"},
  {id:"copywriter",      icon:I.penNib, title:"Copywriter",          sub:"Web, content, strategy"},
  {id:"illustrator",     icon:I.palette, title:"Illustrator",         sub:"Editorial, books, brand art"},
  {id:"uxdesigner",      icon:I.monitor, title:"UX / Product Designer",sub:"Apps, wireframes, systems"},
  {id:"webdeveloper",    icon:I.code, title:"Web Developer",       sub:"Sites, apps, Webflow, Shopify"},
  {id:"socialmedia",     icon:I.smartphone, title:"Social Media Manager",sub:"Content, strategy, scheduling"},
  {id:"musicproducer",   icon:I.headphones, title:"Music Producer",      sub:"Beats, mixing, mastering"},
  {id:"dj",              icon:I.musicNote, title:"DJ / Performer",      sub:"Events, weddings, festivals"},
  {id:"makeup",          icon:I.sparkle, title:"Make-up Artist",      sub:"Weddings, editorial, film"},
  {id:"florist",         icon:I.heart, title:"Florist / Stylist",   sub:"Events, weddings, editorial"},
  {id:"eventplanner",    icon:I.party, title:"Event Planner",       sub:"Weddings, corporate, parties"},
  {id:"personaltrainer", icon:I.activity, title:"Personal Trainer",    sub:"1-to-1, online, group"},
  {id:"coach",           icon:I.compass, title:"Coach / Consultant",  sub:"Business, life, career"},
  {id:"custom",          icon:I.zap, title:"Something else",      sub:"Blank slate — your own templates"},
];

// ── Vert display helpers — resolve "custom:RoleName" to proper labels ──
const getVertLabel=(vid)=>{
  if(!vid) return "";
  if(vid.startsWith("custom:")) return vid.slice(7);
  if(vid==="custom") return "Custom";
  const v=VERTS.find(x=>x.id===vid);
  return v?v.title:vid;
};
const getVertIcon=(vid)=>{
  if(!vid) return I.zap;
  if(vid.startsWith("custom:")) return I.zap;
  const v=VERTS.find(x=>x.id===vid);
  return v?v.icon:I.zap;
};
// Get display label(s) for a client's creative types (uses verts array, falls back to vert)
const getClientVertDisplay=(c)=>{
  const vids=(c.verts&&c.verts.length>0?c.verts:[c.vert]).filter(Boolean);
  return { labels:vids.map(getVertLabel), icons:vids.map(getVertIcon), vids };
};

// In-memory email registry — tracks signed-up emails for this session
// In production this would be a database lookup





/* ── SIGN IN ── */
function Login({onSignIn, onGoSignUp}){
  const [em,setEm]=useState("");
  const [pw,setPw]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [error,setError]=useState("");
  // 2FA state
  const [tfaStep,setTfaStep]=useState(false); // false = normal login, true = showing 2FA code entry
  const TFA_LEN=8;
  const [tfaDigits,setTfaDigits]=useState(Array(TFA_LEN).fill(""));
  const [tfaError,setTfaError]=useState("");
  const [tfaVerifying,setTfaVerifying]=useState(false);
  const [tfaSending,setTfaSending]=useState(false);
  const [tfaTimer,setTfaTimer]=useState(0);
  const [tfaUser,setTfaUser]=useState(null); // stashed user object from successful password auth
  const tfaRefs=useRef([]);
  if(tfaRefs.current.length!==TFA_LEN) tfaRefs.current=Array(TFA_LEN).fill(null);
  useEffect(()=>{if(tfaTimer>0){const iv=setInterval(()=>setTfaTimer(t=>t>0?t-1:0),1000);return()=>clearInterval(iv);}},[tfaTimer]);
  useEffect(()=>{if(tfaStep&&tfaRefs.current[0])setTimeout(()=>tfaRefs.current[0]?.focus(),100);},[tfaStep]);
  const [forgotStep,setForgotStep]=useState(null); // null | "email" | "sent" | "newpw" | "done"
  const [forgotEmail,setForgotEmail]=useState("");
  const [forgotError,setForgotError]=useState("");
  const [forgotSending,setForgotSending]=useState(false);
  const [forgotNewPw,setForgotNewPw]=useState("");
  const [forgotConfirmPw,setForgotConfirmPw]=useState("");
  const [forgotShowPw,setForgotShowPw]=useState(false);
  const [forgotShowPw2,setForgotShowPw2]=useState(false);

  // Detect Supabase password recovery redirect
  // Supports both implicit flow (#access_token=...&type=recovery) and PKCE flow (?code=...)
  useEffect(()=>{
    const handleRecovery=async()=>{
      // Method 1: Implicit flow — token in hash
      const hash=_recoveryHash||window.location.hash;
      if(hash&&hash.includes("type=recovery")){
        const params=new URLSearchParams(hash.replace("#",""));
        const accessToken=params.get("access_token");
        const refreshToken=params.get("refresh_token");
        if(accessToken){
          _accessToken=accessToken;
          if(refreshToken) _refreshToken=refreshToken;
          try{localStorage.removeItem("lancer_at");localStorage.removeItem("lancer_rt");}catch(e){}
          _recoveryMode=true;
          setForgotStep("newpw");
          window.history.replaceState(null,"",window.location.pathname);
          return;
        }
      }
      // Method 2: PKCE flow — code in query string, exchange for session
      const code=_recoveryCode||new URLSearchParams(window.location.search).get("code");
      if(code){
        try{
          const res=await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`,{
            method:"POST",
            headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
            body:JSON.stringify({auth_code:code,code_verifier:""})
          });
          // If PKCE with code_verifier fails, try exchange as authorization_code
          let d=null;
          if(!res.ok){
            const res2=await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`,{
              method:"POST",
              headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
              body:JSON.stringify({auth_code:code})
            });
            if(res2.ok) d=await res2.json();
          } else {
            d=await res.json();
          }
          if(d&&d.access_token){
            _accessToken=d.access_token;
            if(d.refresh_token) _refreshToken=d.refresh_token;
            try{localStorage.removeItem("lancer_at");localStorage.removeItem("lancer_rt");}catch(e){}
            _recoveryMode=true;
            setForgotStep("newpw");
            window.history.replaceState(null,"",window.location.pathname);
            return;
          }
        }catch(e){console.error("PKCE code exchange failed:",e);}
      }
    };
    if(_recoveryMode) handleRecovery();
  },[]);

  const okEmail=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
  const okForm=okEmail&&pw.length>=1;

  const send2FACode=async()=>{
    setTfaSending(true);
    try{
      // Use Supabase's reauthenticate endpoint — sends a verification CODE (not a magic link)
      // Requires the user to already be authenticated (we have _accessToken from password auth)
      await fetch(`${SUPABASE_URL}/auth/v1/reauthenticate`,{
        method:"GET",
        headers:{"apikey":SUPABASE_KEY,"Authorization":`Bearer ${_accessToken}`}
      });
      setTfaTimer(60);
    }catch(e){console.error("send 2FA error:",e);}
    setTfaSending(false);
  };

  const handleSignIn=async ()=>{
    setError("");
    try {
      const {data,error:err}=await dbSignIn(em,pw);
      if(err){
        const msg=err.message||"";
        if(msg.toLowerCase().includes("invalid login credentials")||msg.toLowerCase().includes("invalid_grant")||msg.toLowerCase().includes("invalid email or password"))
          setError("Incorrect email or password. Please try again.");
        else if(msg.toLowerCase().includes("email not confirmed"))
          setError("Please verify your email before signing in. Check your inbox for a verification code.");
        else setError(msg||"Incorrect email or password. Please try again.");
        return;
      }
      if(data?.user) {
        // Check if user has 2FA enabled by fetching their profile
        try{
          const profRes=await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${data.user.id}&select=two_fa`,{
            headers:{"apikey":SUPABASE_KEY,"Authorization":`Bearer ${_accessToken||SUPABASE_KEY}`}
          });
          const profArr=await profRes.json();
          if(profArr&&profArr[0]&&profArr[0].two_fa){
            // 2FA is enabled — clear persisted tokens so auto-login can't bypass 2FA
            // Keep _accessToken in memory for the reauthenticate API call
            try{localStorage.removeItem("lancer_at");localStorage.removeItem("lancer_rt");}catch(e){}
            setTfaUser(data.user);
            setTfaStep(true);
            setTfaDigits(Array(TFA_LEN).fill(""));
            setTfaError("");
            await send2FACode();
            return;
          }
        }catch(e){/* if profile fetch fails, skip 2FA and proceed */}
        try { await onSignIn(data.user, em, pw); }
        catch(e2){ setError("Sign-in succeeded but failed to load your account. Please try again."); }
      }
      else setError("Incorrect email or password. Please try again.");
    } catch(e){
      setError("Something went wrong. Please check your connection and try again.");
    }
  };

  const handleTfaVerify=async(codeStr)=>{
    setTfaError("");setTfaVerifying(true);
    try{
      const res=await fetch(`${SUPABASE_URL}/rest/v1/rpc/verify_reauth_code`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY,"Authorization":`Bearer ${_accessToken}`},
        body:JSON.stringify({p_user_id:tfaUser.id,p_code:codeStr})
      });
      const result=await res.json();
      if(result?.ok){
        // Code verified — persist tokens and mark 2FA as completed for this session
        _saveTokens();
        try{localStorage.setItem("lancer_2fa_ok","1");}catch(e){}
        try{ await onSignIn(tfaUser, em, pw); }
        catch(e2){ setTfaError("Verification succeeded but failed to load your account. Please try again."); }
      } else {
        setTfaError("Invalid or expired code. Please try again.");
        setTfaDigits(Array(TFA_LEN).fill(""));
        setTimeout(()=>{if(tfaRefs.current[0])tfaRefs.current[0].focus();},100);
      }
    }catch(e){setTfaError("Network error. Please try again.");}
    setTfaVerifying(false);
  };

  const tfaOnChange=(i,val)=>{
    if(!/^\d?$/.test(val))return;
    const next=[...tfaDigits];next[i]=val;setTfaDigits(next);
    if(val&&i<TFA_LEN-1&&tfaRefs.current[i+1])tfaRefs.current[i+1].focus();
    if(next.filter(Boolean).length===TFA_LEN) setTimeout(()=>handleTfaVerify(next.join("")),200);
  };
  const tfaOnKeyDown=(i,e)=>{if(e.key==="Backspace"&&!tfaDigits[i]&&i>0&&tfaRefs.current[i-1])tfaRefs.current[i-1].focus();};
  const tfaOnPaste=(e)=>{
    const txt=e.clipboardData.getData("text").replace(/\D/g,"").slice(0,TFA_LEN);
    if(txt.length===TFA_LEN){setTfaDigits(txt.split(""));setTimeout(()=>handleTfaVerify(txt),200);}
  };

  // ── Forgot password handlers ──
  const handleForgotSend=async()=>{
    setForgotError("");setForgotSending(true);
    try{
      const res=await fetch(`${SUPABASE_URL}/auth/v1/recover`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body:JSON.stringify({email:forgotEmail,gotrue_meta_security:{captcha_token:""},redirect_to:window.location.origin})
      });
      if(!res.ok){const d=await res.json().catch(()=>({}));setForgotError(d.msg||d.error_description||"Could not send reset email. Please try again.");setForgotSending(false);return;}
      setForgotStep("sent");
    }catch(e){setForgotError("Network error. Please try again.");}
    setForgotSending(false);
  };

  const handleForgotReset=async()=>{
    setForgotError("");
    if(forgotNewPw.length<8){setForgotError("Password must be at least 8 characters.");return;}
    if(!/[A-Z]/.test(forgotNewPw)||!/[0-9]/.test(forgotNewPw)||!/[^A-Za-z0-9]/.test(forgotNewPw)){setForgotError("Password must include a capital letter, a number, and a special character.");return;}
    if(forgotNewPw!==forgotConfirmPw){setForgotError("Passwords don't match.");return;}
    setForgotSending(true);
    // Extract a useful error message from Supabase's various response shapes
    const extractErr=(data)=>{
      if(!data||typeof data!=="object") return null;
      const code=data.error_code||data.code||null;
      if(code==="same_password") return "New password must be different from your current password.";
      if(code==="weak_password") return "Password is too weak. Use a longer or more complex password.";
      if(code==="reauthentication_needed") return "Recovery session expired. Please request a new reset link.";
      return data.msg||data.message||data.error_description||data.error||(code?`Error: ${code}`:null);
    };
    try{
      // 1) Implicit flow — token came via URL hash
      if(!_accessToken&&_recoveryHash){
        const params=new URLSearchParams(_recoveryHash.replace("#",""));
        _accessToken=params.get("access_token");
        if(params.get("refresh_token")) _refreshToken=params.get("refresh_token");
      }
      // 2) PKCE flow — code came via ?code=... query param. Exchange it for a session.
      if(!_accessToken&&_recoveryCode){
        try{
          const rr=await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`,{
            method:"POST",
            headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
            body:JSON.stringify({auth_code:_recoveryCode})
          });
          if(rr.ok){
            const rd=await rr.json();
            if(rd.access_token){ _accessToken=rd.access_token; if(rd.refresh_token) _refreshToken=rd.refresh_token; }
          } else {
            const errData=await rr.json().catch(()=>({}));
            console.error("PKCE code exchange failed:",rr.status,errData);
          }
        }catch(pkceErr){ console.error("PKCE exchange threw:",pkceErr); }
      }
      if(!_accessToken){setForgotError("Recovery session expired. Please request a new reset link.");setForgotSending(false);return;}
      const res=await fetch(`${SUPABASE_URL}/auth/v1/user`,{
        method:"PUT",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY,"Authorization":`Bearer ${_accessToken}`},
        body:JSON.stringify({password:forgotNewPw})
      });
      if(!res.ok){
        const errData=await res.json().catch(()=>({}));
        console.error("Password update failed:",res.status,errData);
        if(res.status===401||res.status===403){
          // Token expired — try refreshing
          if(_refreshToken){
            const rr=await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`,{
              method:"POST",headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
              body:JSON.stringify({refresh_token:_refreshToken})
            });
            const rd=await rr.json();
            if(rd.access_token){
              _accessToken=rd.access_token;
              if(rd.refresh_token) _refreshToken=rd.refresh_token;
              // Retry with new token
              const res2=await fetch(`${SUPABASE_URL}/auth/v1/user`,{
                method:"PUT",
                headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY,"Authorization":`Bearer ${_accessToken}`},
                body:JSON.stringify({password:forgotNewPw})
              });
              if(res2.ok){
                _accessToken=null;_refreshToken=null;_saveTokens();_recoveryMode=false;
                setForgotStep("done");setForgotSending(false);return;
              }
              const err2=await res2.json().catch(()=>({}));
              console.error("Password update retry failed:",res2.status,err2);
              setForgotError(extractErr(err2)||"Recovery session expired. Please request a new reset link.");
              setForgotSending(false);return;
            }
          }
          setForgotError("Recovery session expired. Please request a new reset link.");setForgotSending(false);return;
        }
        setForgotError(extractErr(errData)||"Could not update password. Please try again.");setForgotSending(false);return;
      }
      // Clear tokens so user must log in with new password
      _accessToken=null;_refreshToken=null;_saveTokens();
      _recoveryMode=false;
      setForgotStep("done");
    }catch(e){console.error("Forgot password threw:",e);setForgotError("Network error. Please try again.");}
    setForgotSending(false);
  };

  const fpwHasUpper=/[A-Z]/.test(forgotNewPw);
  const fpwHasNumber=/[0-9]/.test(forgotNewPw);
  const fpwHasSpecial=/[^A-Za-z0-9]/.test(forgotNewPw);
  const fpwValid=forgotNewPw.length>=8&&fpwHasUpper&&fpwHasNumber&&fpwHasSpecial&&forgotNewPw===forgotConfirmPw;

  // ── 2FA code entry screen ──
  if(tfaStep) return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Two-factor authentication.</div>
      <div className="asub">Enter the 8-digit code sent to <strong>{tfaUser?.email||em}</strong>.</div>
      <div style={{display:"flex",gap:6,justifyContent:"center",margin:"24px 0 16px"}}>
        {tfaDigits.map((d,i)=>(
          <input key={i} ref={el=>tfaRefs.current[i]=el}
            type="text" inputMode="numeric" maxLength={1} value={d}
            onChange={e=>tfaOnChange(i,e.target.value)}
            onKeyDown={e=>tfaOnKeyDown(i,e)}
            onPaste={i===0?tfaOnPaste:undefined}
            onFocus={e=>e.target.select()}
            disabled={tfaVerifying}
            style={{width:38,height:48,textAlign:"center",fontSize:20,fontWeight:700,borderRadius:10,
              border:`1.5px solid ${tfaError?"#E5484D":"var(--bo)"}`,background:"var(--bg2)",color:"var(--ink)",
              outline:"none",transition:"border .2s",fontVariantNumeric:"tabular-nums lining-nums"}}
          />
        ))}
      </div>
      {tfaVerifying&&(
        <div style={{textAlign:"center",fontSize:13,color:"var(--mu)",marginBottom:12}}>Verifying…</div>
      )}
      {tfaError&&(
        <div style={{background:"rgba(229,72,77,.1)",border:"1px solid rgba(229,72,77,.25)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#E5484D",textAlign:"center"}}>{tfaError}</div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontSize:12,color:"var(--mu)"}}>
          {tfaTimer>0?`Resend in 0:${String(tfaTimer).padStart(2,"0")}`:"Didn't receive it?"}
        </span>
        <span onClick={()=>{if(tfaTimer>0||tfaSending)return;send2FACode();}}
          style={{fontSize:12,color:tfaTimer>0||tfaSending?"var(--mu)":"#C96A1A",cursor:tfaTimer>0?"default":"pointer",fontWeight:600}}>
          {tfaSending?"Sending…":"Resend code"}
        </span>
      </div>
      <button className="abtn sec" onClick={()=>{_accessToken=null;_refreshToken=null;_saveTokens();setTfaStep(false);setTfaUser(null);setTfaDigits(Array(TFA_LEN).fill(""));setTfaError("");}} style={{marginTop:4}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.arrowLeft}</span>Back to sign in</button>
      </div>
    </div>
  );

  // ── Forgot password: enter email ──
  if(forgotStep==="email") return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Reset your password.</div>
      <div className="asub">Enter your email and we'll send you a secure reset link.</div>
      <label className="alabel">Email address</label>
      <input className="ainput" type="email" placeholder="you@example.com" value={forgotEmail}
        onChange={e=>{setForgotEmail(e.target.value);setForgotError("");}}
        onKeyDown={e=>e.key==="Enter"&&/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)&&handleForgotSend()}/>
      {forgotError&&(
        <div style={{background:"rgba(229,72,77,.1)",border:"1px solid rgba(229,72,77,.25)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#E5484D"}}>{forgotError}</div>
      )}
      <button className="abtn" disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)||forgotSending} onClick={handleForgotSend}>
        {forgotSending?"Sending…":<>Send reset link <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span></>}
      </button>
      <button className="abtn sec" onClick={()=>{setForgotStep(null);setForgotError("");}} style={{marginTop:8}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.arrowLeft}</span>Back to sign in</button>
      </div>
    </div>
  );

  // ── Forgot password: email sent confirmation ──
  if(forgotStep==="sent") return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Check your inbox.</div>
      <div className="sent-box">
        <span style={{display:"inline-flex"}}>{I.mailOpen}</span>
        <div className="sent-txt">
          We sent a password reset link to<br/>
          <span className="sent-em">{forgotEmail}</span><br/>
          <span style={{fontSize:11,opacity:.6}}>Click the link in the email to reset your password. Check your spam folder if you don't see it.</span>
        </div>
      </div>
      <div style={{background:"var(--gl)",borderRadius:12,padding:"14px 16px",marginBottom:16}}>
        <div style={{fontSize:12,color:"var(--gm)",lineHeight:1.6}}>
          <strong>How it works:</strong><br/>
          1. Open the email from Lancer<br/>
          2. Click the reset link<br/>
          3. You'll be brought back here to set a new password
        </div>
      </div>
      <button className="abtn" onClick={handleForgotSend} disabled={forgotSending}>
        {forgotSending?"Sending…":"Resend reset link"}
      </button>
      <button className="abtn sec" onClick={()=>{setForgotStep("email");setForgotError("");}} style={{marginTop:8}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.arrowLeft}</span>Use a different email</button>
      <div className="anote" style={{marginTop:8}}>Reset links expire after 1 hour.</div>
      </div>
    </div>
  );

  // ── Forgot password: set new password ──
  if(forgotStep==="newpw") return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Set a new password.</div>
      <div className="asub">Choose a strong password for your account.</div>
      <label className="alabel">New password</label>
      <div style={{position:"relative",marginBottom:6}}>
        <input className="ainput" type={forgotShowPw?"text":"password"} placeholder="8+ chars, uppercase, number, special" value={forgotNewPw}
          onChange={e=>{setForgotNewPw(e.target.value);setForgotError("");}} style={{marginBottom:0,paddingRight:56}}/>
        <button type="button" onClick={()=>setForgotShowPw(s=>!s)}
          style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--auth-mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1}}>
          {forgotShowPw?"Hide":"Show"}
        </button>
      </div>
      {forgotNewPw.length>0&&(
        <div style={{marginBottom:14,display:"flex",flexDirection:"column",gap:3}}>
          {[
            {ok:forgotNewPw.length>=8,label:"At least 8 characters"},
            {ok:fpwHasUpper,label:"One capital letter"},
            {ok:fpwHasNumber,label:"One number"},
            {ok:fpwHasSpecial,label:"One special character (!@#$...)"},
          ].map((r,i)=>(
            <div key={i} style={{fontSize:11,color:r.ok?"#10B981":"#7A9085",display:"flex",alignItems:"center",gap:6}}>
              <span style={{display:"inline-flex",alignItems:"center"}}>{r.ok?I.check:I.circleO}</span>{r.label}
            </div>
          ))}
        </div>
      )}
      <label className="alabel">Confirm new password</label>
      <div style={{position:"relative",marginBottom:14}}>
        <input className="ainput" type={forgotShowPw2?"text":"password"} placeholder="Repeat your new password" value={forgotConfirmPw}
          onChange={e=>{setForgotConfirmPw(e.target.value);setForgotError("");}}
          onKeyDown={e=>e.key==="Enter"&&fpwValid&&handleForgotReset()}
          style={{marginBottom:0,paddingRight:56}}/>
        <button type="button" onClick={()=>setForgotShowPw2(s=>!s)}
          style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--auth-mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1}}>
          {forgotShowPw2?"Hide":"Show"}
        </button>
      </div>
      {forgotConfirmPw.length>0&&forgotNewPw!==forgotConfirmPw&&(
        <div style={{fontSize:12,color:"#E5484D",marginBottom:10,marginTop:4}}>Passwords don't match</div>
      )}
      {forgotError&&(
        <div style={{background:"rgba(229,72,77,.1)",border:"1px solid rgba(229,72,77,.25)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#E5484D"}}>{forgotError}</div>
      )}
      <button className="abtn" disabled={!fpwValid||forgotSending} onClick={handleForgotReset}>
        {forgotSending?"Updating…":<>Set new password <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span></>}
      </button>
      </div>
    </div>
  );

  // ── Forgot password: success ──
  if(forgotStep==="done") return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{marginBottom:12,color:"var(--gm)",display:"flex",justifyContent:"center"}}>{I.checkCircle}</div>
        <div className="atitle" style={{marginBottom:8}}>Password updated!</div>
        <div className="asub">Your password has been reset. Sign in with your new password.</div>
      </div>
      <button className="abtn" onClick={()=>{setForgotStep(null);setForgotError("");setForgotNewPw("");setForgotConfirmPw("");}}>
        Sign in with new password <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span>
      </button>
      </div>
    </div>
  );

  return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Welcome back.</div>
      <div className="asub">Sign in to your Lancer account.</div>
      <label className="alabel">Email address</label>
      <input className="ainput" type="email" placeholder="you@example.com" value={em}
        onChange={e=>{setEm(e.target.value);setError("");}}
        onKeyDown={e=>e.key==="Enter"&&okForm&&handleSignIn()}/>
      <label className="alabel">Password</label>
      <div style={{position:"relative",marginBottom:6}}>
        <input className="ainput" type={showPw?"text":"password"} placeholder="Your password" value={pw}
          onChange={e=>{setPw(e.target.value);setError("");}}
          onKeyDown={e=>e.key==="Enter"&&okForm&&handleSignIn()}
          style={{marginBottom:0,paddingRight:56}}/>
        <button type="button" onClick={()=>setShowPw(s=>!s)}
          style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--auth-mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1}}>
          {showPw?"Hide":"Show"}
        </button>
      </div>
      <div style={{textAlign:"right",marginBottom:14}}>
        <span onClick={()=>{setForgotStep("email");setForgotEmail(em);setForgotError("");}}
          style={{fontSize:12,color:"#C96A1A",cursor:"pointer",fontWeight:600}}>
          Forgot password?
        </span>
      </div>
      {error&&(
        <div style={{background:"rgba(229,72,77,.1)",border:"1px solid rgba(229,72,77,.25)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#E5484D"}}>
          {error}
        </div>
      )}
      <button className="abtn" disabled={!okForm} onClick={handleSignIn}>Sign in <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span></button>
      <div className="anote" style={{marginTop:10}}>
        Don't have an account?{" "}
        <span onClick={onGoSignUp} style={{color:"#C96A1A",textDecoration:"underline",cursor:"pointer",fontWeight:600}}>
          Create your account <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:3}}>{I.arrow}</span>
        </span>
      </div>
      </div>
    </div>
  );
}

/* ── SIGN UP ── */
function SignUp({onNext, onGoSignIn}){
  const [name,setName]=useState("");
  const [em,setEm]=useState("");
  const [pw,setPw]=useState("");
  const [pw2,setPw2]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [showPw2,setShowPw2]=useState(false);
  const [error,setError]=useState("");
  // Match the app-wide isFullName rule: first + last, each at least 2 chars.
  const okName=isFullName(name);
  const okEmail=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
  const pwHasUpper=/[A-Z]/.test(pw);
  const pwHasNumber=/[0-9]/.test(pw);
  const pwHasSpecial=/[^A-Za-z0-9]/.test(pw);
  const pwStrong=pw.length>=8&&pwHasUpper&&pwHasNumber&&pwHasSpecial;
  const pwMatch=pw===pw2&&pw2.length>0;
  const okForm=okName&&okEmail&&pwStrong&&pwMatch;

  const pwChecks=[pw.length>=8,pwHasUpper,pwHasNumber,pwHasSpecial].filter(Boolean).length;
  const pwStrength=pw.length===0?"":pwChecks<=1?"weak":pwChecks<=3?"fair":"strong";
  const strengthCol={weak:"#E5484D",fair:"#F59E0B",strong:"#10B981"}[pwStrength]||"transparent";
  const strengthW={weak:"25%",fair:"60%",strong:"100%"}[pwStrength]||"0%";

  const handleSignUp=async ()=>{
    setError("");
    if(pw.length<8||!/[A-Z]/.test(pw)||!/[0-9]/.test(pw)||!/[^A-Za-z0-9]/.test(pw)){
      setError("Password must be at least 8 characters and include a capital letter, a number, and a special character.");
      return;
    }
    if(pw!==pw2){ setError("Passwords don't match."); return; }
    try {
      // signup-send-otp is the single signup entrypoint now. It creates the
      // auth user via the admin API (which does NOT send any Supabase email)
      // and emails our 8-digit OTP via Resend. The previous flow also called
      // /auth/v1/signup which auto-sent Supabase's built-in confirmation
      // email — leaving freelancers with two emails. This collapses to one.
      const res=await fetch(`${SUPABASE_URL}/functions/v1/signup-send-otp`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body:JSON.stringify({email:em.trim().toLowerCase(),password:pw,name:name.trim()}),
      });
      const data=await res.json().catch(()=>({}));
      if(!res.ok||data?.error){
        const msg=(data?.error||"").toString();
        if(/already.*registered/i.test(msg))
          setError("This email is already registered. Please sign in instead.");
        else
          setError(msg||"Sign up failed. Please try again.");
        return;
      }
      onNext(em,pw,name.trim());
    } catch(e){
      setError(e.message||"Sign up failed. Please try again.");
    }
  };

  return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Create your account.</div>
      <div className="asub">Join Lancer and manage your freelance business in one place.</div>
      <div className="auth-grid">
        <div>
          <label className="alabel">Your full name</label>
          <input className="ainput" type="text" placeholder="First and Last name" value={name}
            onChange={e=>{setName(e.target.value);setError("");}}/>
          {name.trim().length>0&&!okName&&<div style={{fontSize:11,color:"#E5484D",marginTop:-10,marginBottom:10}}>Please enter both first and last name</div>}
        </div>
        <div>
          <label className="alabel">Email address</label>
          <input className="ainput" type="email" placeholder="you@example.com" value={em}
            onChange={e=>{setEm(e.target.value);setError("");}}/>
        </div>
      </div>
      <div className="auth-grid-pair">
        <div className="auth-col">
          <label className="alabel">Create a password</label>
          <div style={{position:"relative",marginBottom:6}}>
            <input className="ainput" type={showPw?"text":"password"} placeholder="8+ chars, uppercase, number, special" value={pw}
              onChange={e=>{setPw(e.target.value);setError("");}}
              style={{marginBottom:0,paddingRight:56}}/>
            <button type="button" onClick={()=>setShowPw(s=>!s)}
              style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--auth-mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1}}>
              {showPw?"Hide":"Show"}
            </button>
          </div>
          {pw.length>0&&(
            <div style={{marginBottom:14}}>
              <div style={{height:3,background:"#C0CEC9",borderRadius:99,overflow:"hidden",marginBottom:6}}>
                <div style={{height:"100%",width:strengthW,background:strengthCol,borderRadius:99,transition:"all .3s"}}/>
              </div>
              <div style={{fontSize:11,color:strengthCol,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>{pwStrength}</div>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                {[
                  {ok:pw.length>=8,label:"At least 8 characters"},
                  {ok:pwHasUpper,label:"One capital letter"},
                  {ok:pwHasNumber,label:"One number"},
                  {ok:pwHasSpecial,label:"One special character (!@#$...)"},
                ].map((r,i)=>(
                  <div key={i} style={{fontSize:11,color:r.ok?"#10B981":"#7A9085",display:"flex",alignItems:"center",gap:6}}>
                    <span style={{display:"inline-flex",alignItems:"center"}}>{r.ok?I.check:I.circleO}</span>{r.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="auth-col">
          <label className="alabel">Confirm password</label>
          <div style={{position:"relative",marginBottom:6}}>
            <input className="ainput" type={showPw2?"text":"password"} placeholder="Repeat your password" value={pw2}
              onChange={e=>{setPw2(e.target.value);setError("");}}
              onKeyDown={e=>e.key==="Enter"&&okForm&&handleSignUp()}
              style={{marginBottom:0,paddingRight:56}}/>
            <button type="button" onClick={()=>setShowPw2(s=>!s)}
              style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--auth-mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1}}>
              {showPw2?"Hide":"Show"}
            </button>
          </div>
          {pw2.length>0&&pw!==pw2&&(
            <div style={{fontSize:12,color:"#E5484D",marginBottom:10,marginTop:4}}>Passwords don't match</div>
          )}
        </div>
      </div>
      {error&&(
        <div style={{fontSize:13,color:"#E5484D",fontWeight:600,marginBottom:14,textAlign:"center"}}>
          {error}
        </div>
      )}
      <button className="abtn" disabled={!okForm} onClick={handleSignUp}>Continue <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span></button>
      <div className="anote" style={{marginTop:10}}>
        Already have an account?{" "}
        <span onClick={onGoSignIn} style={{color:"#C96A1A",textDecoration:"underline",cursor:"pointer",fontWeight:600}}>
          Sign in <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:3}}>{I.arrow}</span>
        </span>
      </div>
      </div>
    </div>
  );
}

/* ── VERIFY ── */
// password is the freelancer's chosen password from the signup form. We pass
// it through so signup-verify-otp can mint a session for them on success
// (after marking email_confirmed via the admin API). If it's missing the
// edge function still confirms the email but returns signed_in:false and we
// route the user to the login screen.
function Verify({email,password,onDone,onBack}){
  const NUM=8;
  const [digits,setDigits]=useState(Array(NUM).fill(""));
  const [timer,setTimer]=useState(60);
  const [error,setError]=useState("");
  const [verifying,setVerifying]=useState(false);
  const [resending,setResending]=useState(false);
  const boxRefs=useRef([]);
  if(boxRefs.current.length!==NUM) boxRefs.current=Array(NUM).fill(null);
  useEffect(()=>{const iv=setInterval(()=>setTimer(t=>t>0?t-1:0),1000);return()=>clearInterval(iv);},[]);
  const focus=i=>{if(boxRefs.current[i])boxRefs.current[i].focus();};
  const onChange=(i,val)=>{
    if(!/^\d?$/.test(val))return;
    const next=[...digits];next[i]=val;setDigits(next);
    if(val&&i<NUM-1)focus(i+1);
    if(next.filter(Boolean).length===NUM) setTimeout(()=>doVerify(next.join("")),200);
  };
  const onKeyDown=(i,e)=>{if(e.key==="Backspace"&&!digits[i]&&i>0)focus(i-1);};
  const onPaste=e=>{
    const txt=e.clipboardData.getData("text").replace(/\D/g,"").slice(0,NUM);
    if(txt.length===NUM){setDigits(txt.split(""));setTimeout(()=>doVerify(txt),200);}
  };
  const doVerify=async(token)=>{
    if(verifying)return;
    setVerifying(true);setError("");
    try{
      // Custom signup OTP: signup-verify-otp validates against our signup_otps
      // table, marks email_confirmed_at via the admin API, and (when password
      // is passed) returns a session so we can sign the user straight in.
      // Replaces /auth/v1/verify, which depends on Supabase's built-in email
      // service that was silently rate-limiting codes.
      const res=await fetch(`${SUPABASE_URL}/functions/v1/signup-verify-otp`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body:JSON.stringify({email:(email||"").trim().toLowerCase(),token,password:password||null}),
      });
      const data=await res.json().catch(()=>({}));
      if(!res.ok||!data?.ok){
        setError(data?.error||"Invalid code. Please try again.");
        setDigits(Array(NUM).fill(""));
        focus(0);
      } else if(data.signed_in&&data.access_token){
        _accessToken=data.access_token; if(data.refresh_token) _refreshToken=data.refresh_token; _saveTokens();
        onDone(data);
      } else if(data.confirmed){
        // Email confirmed but no session minted (e.g. password not passed).
        // The login flow will pick up from here.
        onDone({ confirmed: true });
      } else {
        setError("Verification failed. Please try again.");
        setDigits(Array(NUM).fill(""));
        focus(0);
      }
    }catch(e){
      setError("Network error. Please check your connection and try again.");
      setDigits(Array(NUM).fill(""));
      focus(0);
    }
    setVerifying(false);
  };
  const resend=async()=>{
    if(resending||timer>0)return;
    setResending(true);setError("");
    try{
      // Re-issue an OTP via our own edge function (Resend-backed). Voids any
      // prior live OTP for this email server-side so the new one wins.
      await fetch(`${SUPABASE_URL}/functions/v1/signup-send-otp`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body:JSON.stringify({email:(email||"").trim().toLowerCase()}),
      });
      setTimer(60);setDigits(Array(NUM).fill(""));focus(0);
    }catch(e){}
    setResending(false);
  };
  return (
    <div className="auth">
      <div className="auth-inner">
      <div className="alogo">Lancer</div>
      <div className="atitle">Check your inbox.</div>
      <div className="sent-box">
        <span style={{display:"inline-flex"}}>{I.mailOpen}</span>
        <div className="sent-txt">
          We sent an 8-digit code to<br/>
          <span className="sent-em">{email}</span><br/>
          <span style={{fontSize:11,opacity:.6}}>Check your spam folder if it's not in your inbox.</span>
        </div>
      </div>
      <label className="alabel">Enter your 8-digit code</label>
      <div className="code-row" onPaste={onPaste}>
        {digits.map((d,i)=>(
          <input key={i} className="cbox" type="tel" inputMode="numeric" maxLength={1} value={d} autoFocus={i===0}
            ref={el=>{boxRefs.current[i]=el;}} onChange={e=>onChange(i,e.target.value)} onKeyDown={e=>onKeyDown(i,e)}/>
        ))}
      </div>
      {error&&(
        <div style={{background:"rgba(229,72,77,.1)",border:"1px solid rgba(229,72,77,.25)",borderRadius:10,padding:"10px 14px",marginBottom:8,fontSize:13,color:"#E5484D",textAlign:"center"}}>
          {error}
        </div>
      )}
      {verifying&&(
        <div style={{fontSize:13,color:"var(--auth-mu)",textAlign:"center",marginBottom:8}}>Verifying…</div>
      )}
      <div className="resend-row">
        <span className="rtimer">{timer>0?`Resend in 0:${String(timer).padStart(2,"0")}`:"Didn't receive it?"}</span>
        <span className={`rlink${timer>0||resending?" off":""}`} onClick={resend}>{resending?"Sending…":"Resend code"}</span>
      </div>
      <button className="abtn sec" onClick={onBack} style={{marginTop:8}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.arrowLeft}</span>Use a different email</button>
      <div className="anote" style={{marginTop:8}}>Codes expire after 1 hour.</div>
      </div>
    </div>
  );
}

/* ── COUNTRIES ── */
const COUNTRIES=[
  {code:"US",name:"United States",flag:"🇺🇸",currency:"USD",symbol:"$",stripe:true},
  {code:"GB",name:"United Kingdom",flag:"🇬🇧",currency:"GBP",symbol:"£",stripe:true},
  {code:"AU",name:"Australia",flag:"🇦🇺",currency:"AUD",symbol:"A$",stripe:true},
  {code:"CA",name:"Canada",flag:"🇨🇦",currency:"CAD",symbol:"C$",stripe:true},
  {code:"NZ",name:"New Zealand",flag:"🇳🇿",currency:"NZD",symbol:"NZ$",stripe:true},
  {code:"IE",name:"Ireland",flag:"🇮🇪",currency:"EUR",symbol:"€",stripe:true},
  {code:"DE",name:"Germany",flag:"🇩🇪",currency:"EUR",symbol:"€",stripe:true},
  {code:"FR",name:"France",flag:"🇫🇷",currency:"EUR",symbol:"€",stripe:true},
  {code:"NL",name:"Netherlands",flag:"🇳🇱",currency:"EUR",symbol:"€",stripe:true},
  {code:"ES",name:"Spain",flag:"🇪🇸",currency:"EUR",symbol:"€",stripe:true},
  {code:"IT",name:"Italy",flag:"🇮🇹",currency:"EUR",symbol:"€",stripe:true},
  {code:"SE",name:"Sweden",flag:"🇸🇪",currency:"SEK",symbol:"kr",stripe:true},
  {code:"NO",name:"Norway",flag:"🇳🇴",currency:"NOK",symbol:"kr",stripe:true},
  {code:"DK",name:"Denmark",flag:"🇩🇰",currency:"DKK",symbol:"kr",stripe:true},
  {code:"SG",name:"Singapore",flag:"🇸🇬",currency:"SGD",symbol:"S$",stripe:true},
  {code:"IN",name:"India",flag:"🇮🇳",currency:"INR",symbol:"₹",stripe:true},
  {code:"ZA",name:"South Africa",flag:"🇿🇦",currency:"ZAR",symbol:"R",stripe:true},
  {code:"AE",name:"UAE",flag:"🇦🇪",currency:"AED",symbol:"د.إ",stripe:false},
  {code:"NG",name:"Nigeria",flag:"🇳🇬",currency:"NGN",symbol:"₦",stripe:false},
  {code:"KE",name:"Kenya",flag:"🇰🇪",currency:"KES",symbol:"KSh",stripe:false},
];

const DIAL_CODES={
  US:"+1",GB:"+44",AU:"+61",CA:"+1",NZ:"+64",IE:"+353",
  DE:"+49",FR:"+33",NL:"+31",ES:"+34",IT:"+39",SE:"+46",
  NO:"+47",DK:"+45",SG:"+65",IN:"+91",ZA:"+27",
  AE:"+971",NG:"+234",KE:"+254",
};

function PhoneInput({value,onChange,countryCode,placeholder,className,style}){
  const dial=DIAL_CODES[countryCode]||"+1";
  // Strip the dial code from stored value to get just the local number
  const stripDial=(v)=>{
    if(!v) return "";
    const stripped=v.replace(/\s/g,"");
    if(stripped.startsWith(dial)) return v.slice(dial.length).trimStart();
    // Strip any dial code pattern (+XX or +XXX)
    return v.replace(/^\+\d{1,4}\s*/,"");
  };
  const localNum=stripDial(value);

  const handleChange=(e)=>{
    const v=e.target.value.replace(/[^0-9\s\-\(\)]/g,"");
    onChange(v ? dial+" "+v : "");
  };

  return (
    <div style={{position:"relative",display:"flex",alignItems:"center",...(style||{})}}>
      <div style={{
        position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",
        fontSize:13,fontWeight:600,color:"var(--gm)",pointerEvents:"none",
        fontFamily:"'Inter',sans-serif",zIndex:1,whiteSpace:"nowrap"
      }}>
        {dial}
      </div>
      <input
        className={className}
        type="tel"
        placeholder={placeholder||"Phone number"}
        value={localNum}
        onChange={handleChange}
        style={{paddingLeft:`${(dial.length*8)+20}px`}}
      />
    </div>
  );
}
const TERMS_TEXT=`Lancer Terms of Service
Last updated: March 14, 2026

1. ACCEPTANCE OF TERMS
By creating an account and using Lancer, you agree to be bound by these Terms of Service. If you do not agree, do not use the platform.

2. DESCRIPTION OF SERVICE
Lancer is a client management platform for freelance creatives. We provide tools for contracts, invoicing, client communication, and payments. We are not a party to any agreement between you and your clients.

3. FREELANCER RESPONSIBILITIES
You are solely responsible for: the services you provide to your clients; the accuracy of contracts, invoices, and communications sent through the platform; complying with all applicable laws in your jurisdiction, including tax obligations, consumer protection laws, and data protection regulations; obtaining any necessary licenses or permits for your professional activities.

4. PAYMENTS & FEES
Payments are processed via Stripe. By accepting payments through Lancer, you also agree to Stripe's Terms of Service.  You are responsible for any applicable taxes on your earnings.

5. CURRENCY & JURISDICTION
Your account currency is set at sign-up based on your country of residence. This determines the currency displayed to clients in portals, contracts, and invoices. You are responsible for ensuring your pricing and tax treatment comply with local laws.

6. DATA & PRIVACY
We collect and process data in accordance with our Privacy Policy. Client data you enter is owned by you. We do not sell your data or your clients' data to third parties. Data is stored securely and processed in accordance with applicable data protection laws including GDPR where applicable.

7. INTELLECTUAL PROPERTY
Lancer and all associated trademarks, designs, and technology are owned by MMetrix Ltd. You retain ownership of all content you create using the platform, including contracts, questionnaires, and client communications.

8. TERMINATION
We reserve the right to suspend or terminate your account if you violate these terms, engage in fraudulent activity, or use the platform in a manner harmful to other users or third parties.

9. LIMITATION OF LIABILITY
Lancer is provided "as is". We are not liable for any indirect, incidental, or consequential damages arising from your use of the platform, including lost revenue, client disputes, or data loss.

10. GOVERNING LAW
These terms are governed by the laws of the jurisdiction in which MMetrix Ltd is registered. Disputes shall be resolved in the courts of that jurisdiction, except where local consumer protection laws require otherwise.

11. CHANGES TO TERMS
We may update these terms from time to time. We will notify you by email and in-app notice. Continued use of Lancer after changes constitutes acceptance of the updated terms.

For questions, contact us at legal@lancer-app.com`;

/* ── ONBOARDING ── */
function Onboarding({onDone}){
  const [step,setStep]=useState(1);
  const [verts,setVerts]=useState([]); // multi-select array
  const [customRoles,setCustomRoles]=useState([]); // array of custom creative types
  const [customRoleDraft,setCustomRoleDraft]=useState(""); // current input text
  const [country,setCountry]=useState(null);
  const [countryQ,setCountryQ]=useState("");
  const [agreed,setAgreed]=useState(false);
  const [showTerms,setShowTerms]=useState(false);

  const filteredCountries=COUNTRIES.filter(c=>
    c.name.toLowerCase().includes(countryQ.toLowerCase())||
    c.currency.toLowerCase().includes(countryQ.toLowerCase())
  );

  const selectedCountry=COUNTRIES.find(c=>c.code===country);
  // primary vert = first selected, or "custom" if only custom chosen
  const primaryVert=verts.includes("custom")&&verts.length===1?"custom":verts.find(v=>v!=="custom")||verts[0]||null;
  const hasCustom=verts.includes("custom");
  // Valid if at least one chip entered OR there's something in the draft input
  const canNext=(verts.length>0)&&(!hasCustom||customRoles.length>0||customRoleDraft.trim().length>0);

  const toggleVert=(id)=>setVerts(prev=>prev.includes(id)?prev.filter(v=>v!==id):[...prev,id]);

  const commitCustomRoleDraft=()=>{
    const v=customRoleDraft.trim();
    if(!v) return;
    if(customRoles.some(r=>r.toLowerCase()===v.toLowerCase())){ setCustomRoleDraft(""); return; }
    setCustomRoles(prev=>[...prev,v]);
    setCustomRoleDraft("");
  };
  const removeCustomRole=(idx)=>setCustomRoles(prev=>prev.filter((_,i)=>i!==idx));
  const handleCustomRoleKey=(e)=>{
    if(e.key==="Enter"||e.key==="Tab"){
      if(customRoleDraft.trim()){ e.preventDefault(); commitCustomRoleDraft(); }
    } else if(e.key==="Backspace"&&!customRoleDraft&&customRoles.length>0){
      removeCustomRole(customRoles.length-1);
    }
  };

  return (
    <div className="ob">
      <div className="ob-inner">
      <div className="ob-logo">Lancer</div>

      {/* Step indicators */}
      <div style={{display:"flex",gap:6,marginBottom:32,position:"relative",zIndex:1}}>
        {[1,2,3].map(s=>(
          <div key={s} style={{height:3,flex:1,borderRadius:99,background:s<=step?"var(--g)":"var(--auth-input-bo)",transition:"background .3s"}}/>
        ))}
      </div>

      {/* ── STEP 1: Creative types (multi-select) ── */}
      {step===1&&(
        <>
          <div className="ob-title">What do you do?</div>
          <div className="ob-sub">Select all that apply — you can pick multiple. We'll set up the right templates for your work.</div>
          <div className="ob-verts">
            {VERTS.map(v=>{
              const selected=verts.includes(v.id);
              return (
                <div key={v.id} onClick={()=>toggleVert(v.id)} className={`ob-vert-item${selected?" sel":""}`}>
                  {/* Checkbox */}
                  <div style={{width:20,height:20,borderRadius:6,border:`2px solid ${selected?"var(--g)":"var(--auth-input-bo)"}`,background:selected?"var(--g)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                    {selected&&<svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:"var(--auth-ink)",marginBottom:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v.title}</div>
                    <div style={{fontSize:11,color:"var(--auth-mu)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v.sub}</div>
                  </div>
                  {v.id==="custom"&&<div style={{fontSize:9,fontWeight:700,color:"#7AA19A",textTransform:"uppercase",letterSpacing:".06em",flexShrink:0}}>Custom</div>}
                </div>
              );
            })}
          </div>
          {hasCustom&&(
            <div style={{position:"relative",zIndex:1,marginBottom:14}}>
              <div style={{width:"100%",background:"var(--auth-input-bg)",border:"1.5px solid var(--auth-input-bo)",borderRadius:12,padding:"8px 10px",display:"flex",flexWrap:"wrap",gap:6,alignItems:"center",minHeight:46}}>
                {customRoles.map((r,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:6,background:"rgba(156,203,187,.18)",border:"1px solid #7AA19A",borderRadius:99,padding:"4px 4px 4px 10px",fontSize:12,fontWeight:600,color:"var(--g)"}}>
                    <span>{r}</span>
                    <button onClick={()=>removeCustomRole(i)} type="button" style={{width:18,height:18,borderRadius:"50%",background:"rgba(28,77,68,.15)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"var(--g)",fontFamily:"'Inter',sans-serif",padding:0,lineHeight:1}}>×</button>
                  </div>
                ))}
                <input
                  autoFocus
                  placeholder={customRoles.length===0?"e.g. Tattoo Artist, Florist, Tutor…":"Add another…"}
                  value={customRoleDraft}
                  onChange={e=>setCustomRoleDraft(e.target.value)}
                  onKeyDown={handleCustomRoleKey}
                  onBlur={commitCustomRoleDraft}
                  style={{flex:1,minWidth:120,background:"transparent",border:"none",padding:"6px 4px",fontSize:14,fontFamily:"'Inter',sans-serif",color:"var(--auth-ink)",outline:"none"}}
                />
              </div>
              <div style={{fontSize:11,color:"#7A9085",marginTop:6}}>Press Enter or Tab to add multiple. You'll start with a blank slate — no pre-set templates.</div>
            </div>
          )}
          <button className="ob-btn" disabled={!canNext} onClick={()=>setStep(2)}>
            {canNext?<>Next <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span> ({verts.length} selected)</>:"Select at least one"}
          </button>
        </>
      )}

      {/* ── STEP 2: Country ── */}
      {step===2&&(
        <>
          <div className="ob-title">Where are you based?</div>
          <div className="ob-sub">This sets your currency and ensures contracts and payments comply with local laws.</div>
          {/* Search */}
          <div style={{position:"relative",marginBottom:14,zIndex:1}}>
            <input
              value={countryQ}
              onChange={e=>setCountryQ(e.target.value)}
              placeholder="Search country or currency…"
              style={{width:"100%",background:"var(--auth-input-bg)",border:"1.5px solid var(--auth-input-bo)",borderRadius:12,padding:"12px 14px",fontSize:14,fontFamily:"'Inter',sans-serif",color:"var(--auth-ink)",outline:"none"}}
            />
          </div>
          {/* Country list */}
          <div className="ob-countries">
            {filteredCountries.map(c=>(
              <div key={c.code} onClick={()=>setCountry(c.code)} className={`ob-country-item${country===c.code?" sel":""}`}>
                <img src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`} alt={c.flag} style={{width:28,height:20,objectFit:"cover",borderRadius:3,flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--auth-ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
                  <div style={{fontSize:11,color:"var(--auth-mu)"}}>{c.currency} · {c.symbol}</div>
                </div>
                {!c.stripe&&<div style={{fontSize:10,color:"rgba(255,165,0,.8)",fontWeight:600,flexShrink:0}} title="Limited payments">Limited</div>}
                {country===c.code&&<div style={{color:"var(--t)",display:"flex",alignItems:"center",flexShrink:0}}>{I.check}</div>}
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10,position:"relative",zIndex:1}}>
            <button onClick={()=>setStep(1)} style={{flex:1,background:"rgba(28,77,68,.08)",color:"var(--g)",border:"1px solid var(--auth-input-bo)",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.arrowLeft}</span>Back</button>
            <button onClick={()=>setStep(3)} disabled={!country} style={{flex:2,background:country?"var(--g)":"var(--auth-input-bo)",color:country?"#fff":"#7A9085",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:country?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:country?1:.5}}>
              {country?<span style={{display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}><img src={`https://flagcdn.com/w40/${country.toLowerCase()}.png`} alt="" style={{width:20,height:14,objectFit:"cover",borderRadius:2}}/> Continue with {selectedCountry?.name} <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span></span>:"Select your country"}
            </button>
          </div>
        </>
      )}

      {/* ── STEP 3: Terms ── */}
      {step===3&&(
        <>
          <div className="ob-title">Almost there.</div>
          <div className="ob-sub">Review and accept the terms to complete your account setup.</div>
          {/* Summary card */}
          <div style={{background:"var(--auth-input-bg)",borderRadius:14,padding:16,marginBottom:20,position:"relative",zIndex:1,border:"1px solid var(--auth-input-bo)"}}>
            <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12,paddingBottom:12,borderBottom:"1px solid var(--gl)"}}>
              <div style={{width:20,height:20,borderRadius:6,background:"rgba(156,203,187,.25)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="10" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4L4 7.5L10 1" stroke="#7AA19A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"var(--auth-ink)"}}>
                  {verts.map(vid=>vid==="custom"?(customRoles.length>0?customRoles.join(", "):(customRoleDraft.trim()||null)):VERTS.find(v=>v.id===vid)?.title).filter(Boolean).join(", ")}
                </div>
                <div style={{fontSize:11,color:"var(--auth-mu)"}}>Your creative {verts.length>1?"types":"type"}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              {selectedCountry&&<img src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`} alt={selectedCountry.flag} style={{width:28,height:20,objectFit:"cover",borderRadius:3,flexShrink:0}}/>}
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"var(--auth-ink)"}}>{selectedCountry?.name} · {selectedCountry?.currency} ({selectedCountry?.symbol})</div>
                <div style={{fontSize:11,color:"var(--auth-mu)"}}>Your country and currency</div>
              </div>
            </div>
          </div>
          {/* T&C checkbox */}
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:24,position:"relative",zIndex:1,cursor:"pointer"}} onClick={()=>setAgreed(a=>!a)}>
            <div style={{width:22,height:22,borderRadius:6,border:"2px solid "+(agreed?"var(--g)":"var(--auth-input-bo)"),background:agreed?"var(--g)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all .15s"}}>
              {agreed&&<span style={{color:"#fff",display:"inline-flex",alignItems:"center"}}>{I.check}</span>}
            </div>
            <div style={{fontSize:13,color:"var(--auth-mu)",lineHeight:1.5}}>
              I have read and agree to the{" "}
              <span onClick={e=>{e.stopPropagation();setShowTerms(true);}} style={{color:"#C96A1A",textDecoration:"underline",cursor:"pointer",fontWeight:600}}>
                Terms of Service
              </span>
              . I understand that my account currency is set to <strong style={{color:"var(--auth-ink)"}}>{selectedCountry?.currency}</strong> and I am responsible for complying with the laws of <strong style={{color:"var(--auth-ink)"}}>{selectedCountry?.name}</strong>.
            </div>
          </div>
          <div style={{display:"flex",gap:10,position:"relative",zIndex:1}}>
            <button onClick={()=>setStep(2)} style={{flex:1,background:"rgba(28,77,68,.08)",color:"var(--g)",border:"1px solid var(--auth-input-bo)",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.arrowLeft}</span>Back</button>
            <button onClick={()=>{
              // Commit any pending draft before submitting
              const pending=customRoleDraft.trim();
              const finalRoles=pending&&!customRoles.some(r=>r.toLowerCase()===pending.toLowerCase())?[...customRoles,pending]:customRoles;
              onDone({vert:primaryVert,verts,customRole:hasCustom?finalRoles:null,country,currency:selectedCountry?.currency,currencySymbol:selectedCountry?.symbol});
            }} disabled={!agreed}
              style={{flex:2,background:agreed?"var(--g)":"var(--auth-input-bo)",color:agreed?"#fff":"#7A9085",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:agreed?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:agreed?1:.5}}>
              Create my account <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span>
            </button>
          </div>
        </>
      )}
      </div>{/* /.ob-inner */}

      {/* Terms modal */}
      {showTerms&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:999,display:"flex",flexDirection:"column"}} onClick={()=>setShowTerms(false)}>
          <div style={{background:"var(--card)",borderRadius:"20px 20px 0 0",marginTop:"auto",maxHeight:"85vh",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"20px 24px 16px",borderBottom:"1px solid var(--bo)",flexShrink:0}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,color:"var(--ink)"}}>Terms of Service</div>
              <button onClick={()=>setShowTerms(false)} style={{background:"var(--gl)",border:"none",borderRadius:8,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,color:"var(--mu)"}}>✕</button>
            </div>
            <div style={{overflowY:"auto",padding:"20px 24px 40px",flex:1}}>
              <pre style={{fontSize:12,color:"var(--ink)",lineHeight:1.8,fontFamily:"'Inter',sans-serif",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{TERMS_TEXT}</pre>
            </div>
            <div style={{padding:"16px 24px 32px",borderTop:"1px solid var(--bo)",flexShrink:0}}>
              <button onClick={()=>{setShowTerms(false);setAgreed(true);}} style={{width:"100%",background:"#1C4D44",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                I Agree <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:5}}>{I.arrow}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SETTINGS SHEET ── */
/* ── SETTINGS HELPER COMPONENTS (defined outside to prevent remount on rerender) ── */
function SettingsSection({title,children}){
  return (
    <div style={{marginBottom:24}}>
      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",fontWeight:600,marginBottom:12}}>{title}</div>
      {children}
    </div>
  );
}
function SettingsField({label,children}){
  return (
    <div style={{marginBottom:14}}>
      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",marginBottom:5}}>{label}</div>
      {children}
    </div>
  );
}

/* ── TEAM (Slice D) — workspace member management ── */
function TeamPage({acct,onAcctUpdate,onSwitchSection}){
  const [members,setMembers]=useState([]);
  const [invites,setInvites]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showInvite,setShowInvite]=useState(false);
  const [inviteEmail,setInviteEmail]=useState("");
  const [inviteFinViz,setInviteFinViz]=useState(true);
  const [inviteSending,setInviteSending]=useState(false);
  const [inviteError,setInviteError]=useState("");
  const [inviteSuccess,setInviteSuccess]=useState(null); // { email, accept_url, email_sent }
  const [removeTarget,setRemoveTarget]=useState(null); // member row
  const [reassignTo,setReassignTo]=useState("");
  const [removing,setRemoving]=useState(false);
  const [resyncing,setResyncing]=useState(false);
  const [actionMsg,setActionMsg]=useState(null); // { type, text }

  const isOwner=acct.workspaceRole==="owner";
  // effectivePlan keeps Studio active through the grace period after a
  // Studio user cancels (until plan_expiry_date). Raw workspacePlan would
  // flip to "free" the moment the webhook fires, locking them out of Team
  // management before they're actually unsubscribed.
  const isStudio=effectivePlan(acct)==="studio";

  const refresh=async()=>{
    if(!acct.workspaceId) return;
    setLoading(true);
    const [mRes,iRes]=await Promise.all([
      dbGetWorkspaceMembers(acct.workspaceId),
      dbGetPendingInvites(acct.workspaceId),
    ]);
    // The Team page manages FULL seats only — guests are project-scoped and
    // managed from the "Team on this project" panel in the client modal.
    // Filtering here keeps the "Full seats" list + count accurate and means
    // guests never get the financial-visibility toggle (which per spec does
    // not apply to them — guests never see financials regardless).
    const mList=((mRes?.data)||[]).filter(m=>m.role==="owner"||m.role==="member");
    const iList=(iRes?.data||[]).filter(i=>!i.accepted_at);
    setMembers(mList);
    setInvites(iList);
    setLoading(false);
  };

  useEffect(()=>{ refresh(); /* eslint-disable-next-line */ },[acct.workspaceId]);

  const showMsg=(type,text)=>{
    setActionMsg({type,text});
    setTimeout(()=>setActionMsg(null),4000);
  };

  const handleInvite=async()=>{
    setInviteError("");
    const email=(inviteEmail||"").trim();
    if(!email||!email.includes("@")){ setInviteError("Enter a valid email"); return; }
    setInviteSending(true);
    try {
      const {data:invite,error}=await studioCreateInvite(acct.workspaceId,email,inviteFinViz);
      if(error){ setInviteError(error.message||"Invite failed"); setInviteSending(false); return; }
      // Send the email (non-blocking on failure — the accept_url falls back to copy/paste).
      const {data:sendRes}=await studioSendInviteEmail(acct.workspaceId,email,invite.token,invite.expires_at);
      setInviteSuccess({email,accept_url:sendRes?.accept_url||"",email_sent:!!sendRes?.email_sent});
      setInviteEmail("");
      setInviteFinViz(true);
      await refresh();
    } catch(e){ setInviteError(e.message||"Invite failed"); }
    setInviteSending(false);
  };

  const handleRevoke=async(inviteId,email)=>{
    if(!confirm(`Revoke invite for ${email}?`)) return;
    const {error}=await studioRevokeInvite(inviteId);
    if(error){ showMsg("err",error.message); return; }
    showMsg("ok","Invite revoked");
    await refresh();
  };

  // Owner-triggered Stripe reconciliation. Normally the sync happens
  // automatically on accept/remove, but if an older version of the edge
  // function deferred billing (or if Stripe fell out of sync for any reason)
  // this forces a fresh calculation and immediate charge for any net upgrade.
  const handleResyncSeats=async()=>{
    setResyncing(true);
    const {data,error}=await studioResyncSeats();
    setResyncing(false);
    if(error){ showMsg("err",error.message); return; }
    if(data?.already_in_sync){
      showMsg("ok",`Already in sync · ${data.full_count} full seat${data.full_count===1?"":"s"}, ${data.guest_count} guest${data.guest_count===1?"":"s"}`);
    } else {
      const extras=data?.target_seat_qty||0;
      const guestExtras=data?.target_guest_qty||0;
      const isUpgrade=data?.proration_behavior==="always_invoice";
      showMsg("ok",`Stripe updated · ${extras} extra seat${extras===1?"":"s"}${guestExtras>0?`, ${guestExtras} extra guest${guestExtras===1?"":"s"}`:""}${isUpgrade?" · charged immediately":" · credited to next invoice"}`);
    }
  };

  const handleToggleFinViz=async(member)=>{
    const next=!member.financial_visibility;
    const {error}=await studioSetFinancialVisibility(member.member_id,next);
    if(error){ showMsg("err",error.message); return; }
    showMsg("ok",`${member.user_name||member.user_email} ${next?"can now see":"can no longer see"} financials`);
    await refresh();
  };

  const handleRemoveConfirm=async()=>{
    if(!removeTarget) return;
    setRemoving(true);
    const {error}=await studioRemoveMember(removeTarget.member_id,reassignTo||null);
    if(error){ showMsg("err",error.message); setRemoving(false); return; }
    showMsg("ok",`${removeTarget.user_name||removeTarget.user_email} removed from workspace`);
    setRemoveTarget(null);
    setReassignTo("");
    setRemoving(false);
    await refresh();
  };

  const copyLink=async(url)=>{
    try { await navigator.clipboard.writeText(url); showMsg("ok","Invite link copied"); }
    catch(e){ showMsg("err","Could not copy"); }
  };

  // Pro/Free workspace → JIT upsell card. Per spec: includes the user's
  // concrete projected price and routes them to Billing & Plan with the
  // Studio card / 14-day trial flow.
  if(!isStudio){
    return (
      <div style={{padding:"4px 0"}}>
        <SettingsSection title="Team">
          <StudioUpgradeCard
            acct={acct}
            title="Multi-user is a Studio feature"
            body="Studio adds full seats (with financial-visibility controls) and project-scoped guest access for contractors. Includes white-label portal, per-seat analytics, and priority support."
            onUpgrade={()=>{ if(onSwitchSection) onSwitchSection("billing"); }}
          />
        </SettingsSection>
      </div>
    );
  }

  return (
    <div style={{padding:"4px 0"}}>
      {actionMsg&&(
        <div style={{background:actionMsg.type==="err"?"var(--err-bg)":"var(--gl)",borderRadius:12,padding:12,marginBottom:14,fontSize:13,fontWeight:600,color:actionMsg.type==="err"?"var(--err-tx)":"var(--gm)"}}>{actionMsg.text}</div>
      )}

      <SettingsSection title="Workspace">
        <div style={{background:"var(--gl)",borderRadius:14,padding:14,marginBottom:4}}>
          <div style={{fontSize:16,fontWeight:700,color:"var(--ink)"}}>{acct.workspaceName||"Workspace"}</div>
          <div style={{fontSize:12,color:"var(--mu)",marginTop:4}}>
            {members.length} active seat{members.length===1?"":"s"} · {invites.length} pending invite{invites.length===1?"":"s"}
          </div>
        </div>
        {/* Owner-only: force reconcile Stripe seat quantity with the DB member
            count. Normally happens automatically on invite-accept + member-remove;
            surfaced here as a fallback when a subscription got out of sync. */}
        {isOwner&&(
          <div style={{marginTop:12,padding:"10px 14px",background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Sync seats with Stripe</div>
              <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5}}>Recalculates your Studio subscription from your current member count. Extra seats get charged immediately, pro-rated.</div>
            </div>
            <button onClick={handleResyncSeats} disabled={resyncing} style={{background:"var(--gl)",color:"var(--gm)",border:"1px solid var(--bo)",borderRadius:8,padding:"7px 12px",fontSize:11,fontWeight:600,cursor:resyncing?"default":"pointer",opacity:resyncing?.6:1,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",flexShrink:0}}>{resyncing?"Syncing…":"Resync now"}</button>
          </div>
        )}
      </SettingsSection>

      <SettingsSection title={`Full seats (${members.length})`}>
        {loading?(
          <div style={{fontSize:12,color:"var(--mu)",padding:"8px 0"}}>Loading…</div>
        ):members.map(m=>{
          const isOwnerRow=m.role==="owner";
          const isMe=m.user_id===acct.id;
          return (
            <div key={m.member_id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,marginBottom:8}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"var(--g)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,flexShrink:0,overflow:"hidden"}}>
                {m.profile_pic?<img src={m.profile_pic} alt={m.user_name||m.user_email} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(m.user_name||m.user_email).slice(0,2).toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",display:"flex",alignItems:"center",gap:6}}>
                  {m.user_name||m.user_email.split("@")[0]}
                  {isMe&&<span style={{fontSize:10,fontWeight:600,color:"var(--mu)",background:"var(--gl)",padding:"1px 6px",borderRadius:99}}>You</span>}
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:".03em",textTransform:"uppercase",color:isOwnerRow?"var(--g)":"var(--mu)",background:isOwnerRow?"var(--tl)":"var(--gl)",padding:"2px 7px",borderRadius:99}}>{isOwnerRow?"Owner":"Member"}</span>
                </div>
                <div style={{fontSize:11,color:"var(--mu)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.user_email}</div>
              </div>
              {!isOwnerRow&&isOwner&&(
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <div onClick={()=>handleToggleFinViz(m)} title={m.financial_visibility?"Hide financials from this member":"Show financials to this member"} style={{width:42,height:24,borderRadius:99,background:m.financial_visibility?"var(--t)":"var(--bo)",position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:2,left:m.financial_visibility?20:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                  </div>
                  <button onClick={()=>{setRemoveTarget(m);setReassignTo("");}} style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1px solid var(--err-bo)",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Remove</button>
                </div>
              )}
            </div>
          );
        })}
        {isOwner&&(
          <div style={{fontSize:10,color:"var(--mu)",marginTop:4,lineHeight:1.5}}>Toggle controls whether a member can see invoices, payment amounts, and revenue. Owners always see financials.</div>
        )}
      </SettingsSection>

      {invites.length>0&&(
        <SettingsSection title={`Pending invites (${invites.length})`}>
          {invites.map(inv=>{
            const expiresFormatted=inv.expires_at?new Date(inv.expires_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):"";
            const acceptUrl=`${window.location.origin}/?invite=${encodeURIComponent(inv.token)}`;
            return (
              <div key={inv.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"var(--warn-bg)",border:"1px solid var(--warn-bo)",borderRadius:12,marginBottom:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{inv.email}</div>
                  <div style={{fontSize:11,color:"var(--warn-tx)"}}>Invite expires {expiresFormatted}</div>
                </div>
                <button onClick={()=>copyLink(acceptUrl)} style={{background:"var(--card)",color:"var(--ink)",border:"1px solid var(--bo)",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Copy link</button>
                <button onClick={()=>handleRevoke(inv.id,inv.email)} style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1px solid var(--err-bo)",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Revoke</button>
              </div>
            );
          })}
        </SettingsSection>
      )}

      <button onClick={()=>{setShowInvite(true);setInviteError("");setInviteSuccess(null);}} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"13px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginTop:8}}>+ Add full seat</button>

      {showInvite&&(()=>{
        // Seat-billing preview. Studio includes 2 full seats in the £39 base;
        // every seat beyond that (paid members + pending invites) adds £12/mo,
        // pro-rated on accept via stripe-accept-invite → syncStripeSeats.
        // Compute the delta this specific invite will introduce so the modal
        // can call it out explicitly rather than making the owner guess.
        const STUDIO_SEATS_INCLUDED=2;
        const STUDIO_PER_SEAT_GBP=12;
        const currentFilled=members.length+invites.length; // paid + pending
        const currentExtras=Math.max(0,currentFilled-STUDIO_SEATS_INCLUDED);
        const nextExtras=Math.max(0,currentFilled+1-STUDIO_SEATS_INCLUDED);
        const inviteAdds=(nextExtras-currentExtras)*STUDIO_PER_SEAT_GBP;
        const projectedTotalExtras=nextExtras;
        return (
        <div onClick={()=>!inviteSending&&setShowInvite(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--cr)",borderRadius:20,padding:24,width:"100%",maxWidth:380}}>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:"var(--ink)",marginBottom:6}}>Invite a teammate</div>
            <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6,marginBottom:16}}>
              {inviteAdds>0
                ?`They'll get an email with a link to join your workspace. You've filled ${currentFilled}/${STUDIO_SEATS_INCLUDED} included seats — this invite becomes a paid extra seat once accepted.`
                :`They'll get an email with a link to join your workspace. Your Studio plan includes ${STUDIO_SEATS_INCLUDED} full seats; this invite fits within that allowance at no extra cost.`}
            </div>

            {/* Billing impact chip — only rendered when this invite will
                actually increase the next invoice. Kept out of the success
                view so it doesn't confuse post-send. */}
            {inviteAdds>0&&!inviteSuccess&&(
              <div style={{background:"var(--warn-bg)",border:"1px solid var(--warn-bo)",borderRadius:12,padding:"12px 14px",marginBottom:14,display:"flex",alignItems:"flex-start",gap:10}}>
                <span style={{display:"inline-flex",color:"var(--warn-tx)",flexShrink:0,marginTop:1}}>{I.creditcard}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--warn-tx)",marginBottom:2}}>Adds £{inviteAdds}/mo to your Studio subscription</div>
                  <div style={{fontSize:11,color:"var(--warn-tx)",opacity:.85,lineHeight:1.5}}>Charged pro-rated from the day they accept. After this invite you'll be paying for {projectedTotalExtras} extra seat{projectedTotalExtras===1?"":"s"} on top of the included {STUDIO_SEATS_INCLUDED}. If they never accept, no charge applies — revoke the invite to cancel it.</div>
                </div>
              </div>
            )}

            {inviteSuccess?(
              <>
                <div style={{background:"var(--ok-bg)",border:"1px solid var(--ok-tx)",borderRadius:12,padding:14,marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ok-tx)",marginBottom:4}}>Invite sent</div>
                  <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>{inviteSuccess.email_sent?`We emailed ${inviteSuccess.email}.`:`Email delivery failed — copy the link below and send it manually.`}</div>
                </div>
                {inviteSuccess.accept_url&&(
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:11,color:"var(--mu)",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Invite link</div>
                    <div style={{display:"flex",gap:6}}>
                      <input readOnly value={inviteSuccess.accept_url} style={{flex:1,minWidth:0,background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"8px 10px",fontSize:11,color:"var(--ink)",fontFamily:"monospace"}}/>
                      <button onClick={()=>copyLink(inviteSuccess.accept_url)} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Copy</button>
                    </div>
                  </div>
                )}
                <button onClick={()=>{setShowInvite(false);setInviteSuccess(null);}} style={{width:"100%",background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Done</button>
              </>
            ):(
              <>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:"var(--mu)",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Email</div>
                  <input type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="teammate@example.com" autoFocus style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:14,color:"var(--ink)",fontFamily:"'Inter',sans-serif"}}/>
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:14,padding:"10px 12px",background:"var(--gl)",borderRadius:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Can see financials</div>
                    <div style={{fontSize:10,color:"var(--mu)",lineHeight:1.4}}>Invoices, payment amounts, revenue analytics. Toggle off to hide from junior staff.</div>
                  </div>
                  <div onClick={()=>setInviteFinViz(!inviteFinViz)} style={{width:42,height:24,borderRadius:99,background:inviteFinViz?"var(--t)":"var(--bo)",position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:2,left:inviteFinViz?20:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                  </div>
                </div>
                {inviteError&&<div style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1px solid var(--err-bo)",borderRadius:10,padding:"10px 12px",fontSize:12,marginBottom:12}}>{inviteError}</div>}
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setShowInvite(false)} disabled={inviteSending} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:inviteSending?"default":"pointer",fontFamily:"'Inter',sans-serif",opacity:inviteSending?.5:1}}>Cancel</button>
                  <button onClick={handleInvite} disabled={inviteSending} style={{flex:2,background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:inviteSending?"default":"pointer",fontFamily:"'Inter',sans-serif",opacity:inviteSending?.7:1}}>{inviteSending?"Sending…":inviteAdds>0?`Send invite · +£${inviteAdds}/mo`:"Send invite"}</button>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {removeTarget&&(
        <div onClick={()=>!removing&&setRemoveTarget(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--cr)",borderRadius:20,padding:24,width:"100%",maxWidth:380}}>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:"var(--ink)",marginBottom:8}}>Remove {removeTarget.user_name||removeTarget.user_email.split("@")[0]}?</div>
            <div style={{fontSize:13,color:"var(--mu)",lineHeight:1.6,marginBottom:14}}>They'll lose access to the workspace immediately. Their seat charge stops at the next billing cycle.</div>

            {(()=>{
              const ledClients=members.filter(m=>m.member_id!==removeTarget.member_id);
              return (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:"var(--mu)",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Reassign their lead clients to</div>
                  <select value={reassignTo} onChange={e=>setReassignTo(e.target.value)} style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"var(--ink)",fontFamily:"'Inter',sans-serif"}}>
                    <option value="">Owner (default)</option>
                    {ledClients.filter(m=>m.role!=="owner").map(m=>(
                      <option key={m.member_id} value={m.member_id}>{m.user_name||m.user_email.split("@")[0]}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setRemoveTarget(null)} disabled={removing} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:removing?"default":"pointer",fontFamily:"'Inter',sans-serif",opacity:removing?.5:1}}>Cancel</button>
              <button onClick={handleRemoveConfirm} disabled={removing} style={{flex:2,background:"var(--err-tx)",color:"#fff",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:removing?"default":"pointer",fontFamily:"'Inter',sans-serif",opacity:removing?.7:1}}>{removing?"Removing…":"Remove member"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Phase 6 — Custom Domain section in Branding & Portal. Studio + owner only.
// Renders one of three states:
//   1. Locked (non-Studio) — upgrade card pointing to Billing
//   2. Empty (Studio, no domain set) — entry form
//   3. Configured — current domain, verification status, DNS instructions, remove button
function CustomDomainSection({acct,onSwitchSection}){
  const [domain,setDomain]=useState(acct.workspaceCustomDomain||"");
  const [status,setStatus]=useState(null); // { domain, verified, ssl, verification: [{type,domain,value,reason}], recommended_cname_target }
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [polling,setPolling]=useState(false);
  const isStudioOwner = isWhiteLabel(acct) && canEditWorkspaceBranding(acct);

  // On mount + when workspace changes, pull current verification state.
  useEffect(()=>{
    if(!isStudioOwner||!acct.workspaceId) return;
    let cancelled=false;
    studioVerifyCustomDomain(acct.workspaceId).then(({data})=>{
      if(cancelled||!data) return;
      setStatus(data);
      if(data.domain) setDomain(data.domain);
    });
    return()=>{ cancelled=true; };
  },[acct.workspaceId,isStudioOwner]);

  // Poll while verification is pending.
  useEffect(()=>{
    if(!polling||!status||status.verified) return;
    const iv=setInterval(async()=>{
      const {data}=await studioVerifyCustomDomain(acct.workspaceId);
      if(!data) return;
      setStatus(data);
      if(data.verified){ setPolling(false); }
    },5000);
    return()=>clearInterval(iv);
  },[polling,status,acct.workspaceId]);

  if(!isStudioOwner){
    return (
      <SettingsSection title="Custom Domain">
        <div style={{background:"var(--gl)",border:"1px solid var(--bo)",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
          <span style={{display:"inline-flex",color:"var(--mu)",flexShrink:0,marginTop:2}}>{I.lock}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}>Custom domain is a Studio feature</div>
            <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Serve your client portal from your own domain (e.g. <code style={{fontFamily:"monospace"}}>clients.yourstudio.com</code>) instead of <code style={{fontFamily:"monospace"}}>lancer-app.com/yourslug</code>.</div>
            <button onClick={()=>{ if(onSwitchSection) onSwitchSection("billing"); }} style={{marginTop:10,background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>See Studio</button>
          </div>
        </div>
      </SettingsSection>
    );
  }

  const submitAdd=async()=>{
    setErr("");
    setBusy(true);
    const {data,error}=await studioAddCustomDomain(acct.workspaceId,domain);
    setBusy(false);
    if(error){ setErr(error.message||"Could not add domain"); return; }
    setStatus(data);
    if(!data.verified) setPolling(true);
  };
  const submitRemove=async()=>{
    if(!confirm(`Remove ${status?.domain||domain}? Your portal will go back to lancer-app.com/${acct.slug||""}/...`)) return;
    setBusy(true);
    const {error}=await studioRemoveCustomDomain(acct.workspaceId);
    setBusy(false);
    if(error){ setErr(error.message||"Could not remove domain"); return; }
    setStatus(null);
    setDomain("");
    setPolling(false);
  };
  const refreshNow=async()=>{
    setBusy(true);
    const {data}=await studioVerifyCustomDomain(acct.workspaceId);
    setBusy(false);
    if(data) setStatus(data);
  };

  const cnameRecord=status?.verification?.find(v=>v.type==="CNAME");
  const txtRecord=status?.verification?.find(v=>v.type==="TXT");

  return (
    <SettingsSection title="Custom Domain">
      {/* Empty state — no domain configured */}
      {!status?.domain&&(
        <div>
          <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5,marginBottom:10}}>
            Use your own domain so clients see <code style={{fontFamily:"monospace"}}>clients.yourstudio.com</code> instead of Lancer's URL. You bring your own domain — Lancer never registers, renews, or transfers domains.
          </div>
          <div style={{display:"flex",gap:8}}>
            <input value={domain} onChange={e=>setDomain(e.target.value)} placeholder="clients.yourstudio.com" type="text"
              style={{flex:1,background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif",boxSizing:"border-box"}}/>
            <button onClick={submitAdd} disabled={busy||!domain.trim()} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontSize:13,fontWeight:600,cursor:(busy||!domain.trim())?"default":"pointer",opacity:(busy||!domain.trim())?.6:1,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>{busy?"…":"Add domain"}</button>
          </div>
          {err&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:8}}>{err}</div>}
        </div>
      )}

      {/* Configured — verified state */}
      {status?.domain&&status.verified&&(
        <div style={{background:"var(--ok-bg)",border:"1.5px solid var(--ok-tx)",borderRadius:12,padding:14}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <span style={{display:"inline-flex",color:"var(--ok-tx)"}}>{I.check}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"var(--ok-tx)"}}>Live · SSL active</div>
              <div style={{fontSize:13,color:"var(--ink)",fontFamily:"monospace",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{status.domain}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <a href={`https://${status.domain}`} target="_blank" rel="noopener noreferrer" style={{flex:1,textDecoration:"none",textAlign:"center",background:"var(--card)",color:"var(--ink)",border:"1px solid var(--bo)",borderRadius:8,padding:"8px 10px",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif"}}>Test portal</a>
            <button onClick={submitRemove} disabled={busy} style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1px solid var(--err-bo)",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:busy?"default":"pointer",fontFamily:"'Inter',sans-serif"}}>Remove</button>
          </div>
        </div>
      )}

      {/* Configured — pending verification */}
      {status?.domain&&!status.verified&&(
        <div style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:12,padding:14}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <span style={{display:"inline-flex",color:"var(--warn-tx)"}}>{I.alertTri}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"var(--warn-tx)"}}>Waiting for DNS</div>
              <div style={{fontSize:13,color:"var(--ink)",fontFamily:"monospace",marginTop:2}}>{status.domain}</div>
            </div>
          </div>
          <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.6,marginBottom:10}}>
            Add the following DNS record at your domain registrar (Cloudflare / Namecheap / GoDaddy / etc):
          </div>
          {cnameRecord&&(
            <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:8,padding:"10px 12px",marginBottom:8,fontFamily:"monospace",fontSize:11,color:"var(--ink)"}}>
              <div><strong>Type:</strong> CNAME</div>
              <div><strong>Name:</strong> {cnameRecord.domain}</div>
              <div><strong>Value:</strong> {cnameRecord.value}</div>
            </div>
          )}
          {!cnameRecord&&status.recommended_cname_target&&(
            <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:8,padding:"10px 12px",marginBottom:8,fontFamily:"monospace",fontSize:11,color:"var(--ink)"}}>
              <div><strong>Type:</strong> CNAME</div>
              <div><strong>Name:</strong> {status.domain}</div>
              <div><strong>Value:</strong> {status.recommended_cname_target}</div>
            </div>
          )}
          {txtRecord&&(
            <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:8,padding:"10px 12px",marginBottom:8,fontFamily:"monospace",fontSize:11,color:"var(--ink)"}}>
              <div><strong>Type:</strong> TXT (verification)</div>
              <div><strong>Name:</strong> {txtRecord.domain}</div>
              <div><strong>Value:</strong> {txtRecord.value}</div>
            </div>
          )}
          <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5,marginBottom:10}}>DNS changes can take a few minutes to a few hours. SSL is provisioned automatically once we verify the record. We'll re-check every 5 seconds.</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={refreshNow} disabled={busy} style={{flex:1,background:"var(--card)",color:"var(--ink)",border:"1px solid var(--bo)",borderRadius:8,padding:"8px 10px",fontSize:12,fontWeight:600,cursor:busy?"default":"pointer",fontFamily:"'Inter',sans-serif"}}>{busy?"Checking…":"Check now"}</button>
            <button onClick={submitRemove} disabled={busy} style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1px solid var(--err-bo)",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:busy?"default":"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
          </div>
          {err&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:8}}>{err}</div>}
        </div>
      )}
    </SettingsSection>
  );
}

/* ── CONTACT & HELP — proper component so hooks work correctly ── */
function ContactHelp({acct}){
  const [search,setSearch]=useState("");
  const [openId,setOpenId]=useState(null);
  const CATS=[
    {id:"getting-started",label:"Getting Started",icon:I.zap,items:[
      {q:"How do I add my first client?",a:"Go to the Clients tab and tap the + button. Fill in their name, email, session type, project date, and fee. Once added, you can send them a contract, questionnaire, and manage their entire project from their client page."},
      {q:"How do I set up my cancellation policy?",a:"Go to Account Settings (tap your profile picture → Account Settings). Scroll to the Cancellation Policy section. Select your notice period and late cancellation policy. This must be completed before you can send contracts."},
      {q:"How do I customise my client portal?",a:"Go to Branding & Portal from the profile menu. You can set your business name, tagline, and footer text. These appear on all client-facing portal pages."},
    ]},
    {id:"contracts",label:"Contracts",icon:I.fileText,items:[
      {q:"How do I create and send a contract?",a:"Open a client's page and go to the Contracts tab. Tap 'New Contract', write or paste your terms, then tap 'Send to Portal'. The client receives it in their portal where they can review, comment, or sign."},
      {q:"What happens when a client signs a contract?",a:"Their status updates to 'Signed', the contract is locked, and a signed copy is stored. You'll see the signature and timestamp on the contract. The deposit payment CTA will then appear in their portal."},
      {q:"Can I send a revised contract?",a:"Yes. If you edit and resend a contract, the previous version is automatically archived. The client sees only the latest version. You can view version history from the contract detail page."},
      {q:"What does 'Sent Back' mean?",a:"If a client has comments or concerns about a contract, they can send it back with notes. You'll see these comments on the contract and can revise and resend."},
    ]},
    {id:"payments",label:"Payments",icon:I.creditcard,items:[
      {q:"How do payments work?",a:"Payments are split into deposit and final balance (configurable percentage). After signing, clients see a 'Pay Deposit' button in their portal. Once the deposit is paid, the balance payment unlocks. All payments are processed via Stripe."},
      {q:"How do I issue a refund?",a:"Open the client's page, go to the Payments section, and tap 'Issue Refund'. Enter the amount and reason. Refunds are processed via Stripe and typically arrive within 5-10 business days."},
      {q:"Why isn't the payment button showing for my client?",a:"The deposit payment button only appears after the contract is signed. The balance button appears after the deposit is paid. Ensure the contract has been sent and signed first."},
      {q:"What happens when a payment is made?",a:"The corresponding milestone is automatically marked as complete, the payment status updates in real time, and both you and the client see the change immediately."},
    ]},
    {id:"clients",label:"Clients & Projects",icon:I.clients,items:[
      {q:"How do I archive a client?",a:"Open the client's page, scroll to the bottom, and tap 'Archive Client'. You'll be asked to select a reason (Completed, Cancellation, or Other). Archived clients become read-only and their contracts are also archived."},
      {q:"Can I edit a client's fee after creation?",a:"Yes. Open the client's page, tap 'Edit Client Details', update the fee, and save. The final balance will update automatically — the deposit amount stays the same."},
      {q:"What is the client portal?",a:"Each client gets a unique portal link where they can view their contract, make payments, complete questionnaires, upload photos, and communicate with you. Share the link from the client's page."},
      {q:"How do project timelines work?",a:"Each client has a timeline with milestones (contract, deposit, session, delivery, final balance). You can set dates for each step. Milestones auto-complete when relevant actions occur (e.g., payment received)."},
    ]},
    {id:"account",label:"Account & Billing",icon:I.settings,items:[
      {q:"How do I change my plan?",a:"Go to Billing & Plan from the profile menu. You can upgrade to Pro or Studio for unlimited clients, AI features, and advanced analytics. Downgrading to Free requires having 3 or fewer active clients."},
      {q:"How do I enable dark mode?",a:"Go to Branding & Portal from the profile menu and scroll to App Appearance. Toggle the Dark Mode switch. The entire app will adapt to a dark colour scheme."},
      {q:"How do I delete my account?",a:"Go to Account Settings, scroll to the Danger Zone at the bottom, and tap 'Delete Account'. This action is permanent and cannot be undone — all your data will be removed."},
    ]},
    {id:"cancellations",label:"Cancellations",icon:I.ban,items:[
      {q:"How does a client cancel?",a:"Clients can request cancellation from their portal. If they're within the notice period, it's a free cancellation. If they're inside the notice window (late notice), your cancellation policy terms apply automatically."},
      {q:"What is a late cancellation fee?",a:"If a client cancels inside your required notice period, the late cancellation policy you set applies. Options include: no refund (full fee retained), partial refund, full refund, or at your discretion."},
      {q:"Where do I see cancellation requests?",a:"Cancellation requests appear as messages in the client's thread (Inbox tab) with a 'CANCELLATION REQUEST' prefix. You'll also see a banner on their client page."},
    ]},
  ];
  const filtered=search.trim()?CATS.map(cat=>({...cat,items:cat.items.filter(i=>i.q.toLowerCase().includes(search.toLowerCase())||i.a.toLowerCase().includes(search.toLowerCase()))})).filter(cat=>cat.items.length>0):CATS;
  return (
    <>
      {/* Search */}
      <div style={{position:"relative",marginBottom:20}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search help topics…"
          style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"12px 14px 12px 40px",fontSize:14,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}/>
        <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",display:"inline-flex",color:"var(--mu)",pointerEvents:"none"}}>{I.search}</span>
      </div>
      {/* FAQ Categories */}
      {filtered.length===0&&(
        <div style={{textAlign:"center",padding:"32px 0",color:"var(--mu)"}}>
          <div style={{fontSize:28,marginBottom:8}}>🤔</div>
          <div style={{fontSize:13}}>No results found. Try a different search term.</div>
        </div>
      )}
      {filtered.map(cat=>(
        <div key={cat.id} style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <span style={{display:"inline-flex",color:"var(--gm)"}}>{cat.icon}</span>
            <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",textTransform:"uppercase",letterSpacing:".04em"}}>{cat.label}</div>
          </div>
          <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden"}}>
            {cat.items.map((item,i)=>{
              const uid=cat.id+"-"+i;
              const isOpen=openId===uid;
              return (
                <div key={uid}>
                  {i>0&&<div style={{borderTop:"1px solid var(--bo)"}}/>}
                  <div onClick={()=>setOpenId(isOpen?null:uid)}
                    style={{padding:"13px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                    <div style={{flex:1,fontSize:13,fontWeight:600,color:"var(--ink)",lineHeight:1.4}}>{item.q}</div>
                    <span style={{fontSize:12,color:"var(--mu)",flexShrink:0,transition:"transform .2s",transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
                  </div>
                  {isOpen&&(
                    <div style={{padding:"0 16px 14px",fontSize:12,color:"var(--mu)",lineHeight:1.7}}>
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {/* Phase 8 — Support tickets surface */}
      <SupportTicketsPanel acct={acct}/>
      <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginTop:12,lineHeight:1.6}}>
        For suggestions or feedback: <a href="mailto:hello@lancer-app.com" style={{color:"var(--gm)",fontWeight:600}}>hello@lancer-app.com</a>
      </div>
    </>
  );
}

function SupportTicketsPanel({acct}){
  const [tickets,setTickets]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showForm,setShowForm]=useState(false);
  const [openTicketId,setOpenTicketId]=useState(null);
  const sla=slaBadgeForPlan(effectivePlan(acct));
  const refresh=async()=>{
    setLoading(true);
    const {data}=await supportListMyTickets();
    setTickets(data||[]);
    setLoading(false);
  };
  useEffect(()=>{ refresh(); },[]);

  if(openTicketId){
    return <SupportTicketThread acct={acct} ticketId={openTicketId} onBack={()=>{setOpenTicketId(null);refresh();}}/>;
  }
  if(showForm){
    return <SupportTicketForm acct={acct} sla={sla} onCancel={()=>setShowForm(false)} onSubmitted={(id)=>{setShowForm(false);refresh();setOpenTicketId(id);}}/>;
  }

  return (
    <>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8,marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em"}}>Support tickets</div>
        <span style={{fontSize:10,fontWeight:700,color:sla.color,background:sla.bg,padding:"3px 8px",borderRadius:99,textTransform:"uppercase",letterSpacing:".04em"}}>{sla.label}</span>
      </div>
      <button onClick={()=>setShowForm(true)} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"13px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:14}}>
        + New ticket
      </button>
      {loading?(
        <div style={{fontSize:12,color:"var(--mu)",padding:"10px 0"}}>Loading…</div>
      ):tickets.length===0?(
        <div style={{fontSize:12,color:"var(--mu)",padding:"10px 0",lineHeight:1.5}}>No open tickets. Tap + New ticket above when you need help.</div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {tickets.map(t=>{
            const statusLabel=t.status==="open"?"Awaiting reply":t.status==="awaiting_user"?"New reply":"Closed";
            const statusColor=t.status==="awaiting_user"?"var(--gm)":t.status==="closed"?"var(--mu)":"var(--warn-tx)";
            return (
              <div key={t.id} onClick={()=>setOpenTicketId(t.id)} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"12px 14px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8,marginBottom:4}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",flex:1,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.subject}</div>
                  <div style={{fontSize:10,fontWeight:700,color:statusColor,whiteSpace:"nowrap",flexShrink:0}}>{statusLabel}</div>
                </div>
                <div style={{fontSize:11,color:"var(--mu)"}}>
                  {new Date(t.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})} · {t.reply_count||0} repl{t.reply_count===1?"y":"ies"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function SupportTicketForm({acct,sla,onCancel,onSubmitted}){
  const [subject,setSubject]=useState("");
  const [body,setBody]=useState("");
  const [severity,setSeverity]=useState("medium");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const submit=async()=>{
    setErr("");
    if(subject.trim().length<3){ setErr("Subject too short"); return; }
    if(body.trim().length<10){ setErr("Please describe the issue (at least 10 characters)"); return; }
    setBusy(true);
    const {data,error}=await supportCreateTicket(subject,body,severity);
    setBusy(false);
    if(error){ setErr(error.message||"Could not submit"); return; }
    if(onSubmitted) onSubmitted(data);
  };
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
        <button onClick={onCancel} style={{background:"none",border:"none",color:"var(--mu)",cursor:"pointer",fontSize:18,padding:"4px 6px",lineHeight:1}}>←</button>
        <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>New ticket</div>
        <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,color:sla.color,background:sla.bg,padding:"3px 8px",borderRadius:99,textTransform:"uppercase",letterSpacing:".04em"}}>{sla.label}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <div>
          <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>Subject</div>
          <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Short summary"
            style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>Severity</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {[{id:"low",l:"Low"},{id:"medium",l:"Medium"},{id:"high",l:"High"},{id:"urgent",l:"Urgent"}].map(opt=>{
              const on=severity===opt.id;
              return <button key={opt.id} onClick={()=>setSeverity(opt.id)} style={{flex:"1 1 auto",background:on?"var(--gl)":"var(--card)",border:`1.5px solid ${on?"var(--g)":"var(--bo)"}`,color:on?"var(--gm)":"var(--mu)",borderRadius:8,padding:"8px 10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{opt.l}</button>;
            })}
          </div>
        </div>
        <div>
          <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>What's happening?</div>
          <textarea value={body} onChange={e=>setBody(e.target.value)} placeholder="Describe what you tried, what you expected, and what you saw."
            style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif",resize:"vertical",minHeight:140,boxSizing:"border-box",lineHeight:1.5}}/>
        </div>
        {err&&<div style={{fontSize:11,color:"var(--err-tx)"}}>{err}</div>}
        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancel} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
          <button onClick={submit} disabled={busy} style={{flex:2,background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:busy?"default":"pointer",opacity:busy?.6:1,fontFamily:"'Inter',sans-serif"}}>{busy?"Submitting…":"Submit ticket"}</button>
        </div>
      </div>
    </div>
  );
}

function SupportTicketThread({acct,ticketId,onBack}){
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [reply,setReply]=useState("");
  const [busy,setBusy]=useState(false);
  const refresh=async()=>{
    setLoading(true);
    const {data}=await supportGetTicket(ticketId);
    setRows(data||[]);
    setLoading(false);
  };
  useEffect(()=>{ refresh(); /* eslint-disable-next-line */ },[ticketId]);
  if(loading) return <div style={{padding:14,fontSize:12,color:"var(--mu)"}}>Loading…</div>;
  const ticket=rows[0];
  if(!ticket) return <div style={{padding:14}}><button onClick={onBack} style={{background:"none",border:"none",color:"var(--mu)",cursor:"pointer",fontSize:14}}>← Back</button><div style={{fontSize:12,color:"var(--mu)",marginTop:10}}>Ticket not found.</div></div>;
  const replies=rows.slice(1).filter(r=>r.reply_id);
  const closed=ticket.status==="closed";
  const submitReply=async()=>{
    if(reply.trim().length<1) return;
    setBusy(true);
    const {error}=await supportPostUserReply(ticketId,reply);
    setBusy(false);
    if(error){ alert(error.message||"Could not send reply"); return; }
    setReply("");
    await refresh();
  };
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"var(--mu)",cursor:"pointer",fontSize:18,padding:"4px 6px",lineHeight:1}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ticket.subject}</div>
          <div style={{fontSize:11,color:"var(--mu)"}}>Opened {new Date(ticket.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}{closed?" · Closed":""}</div>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,padding:"12px 14px"}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>You · {new Date(ticket.created_at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
          <div style={{fontSize:13,color:"var(--ink)",whiteSpace:"pre-wrap",lineHeight:1.5}}>{ticket.body}</div>
        </div>
        {replies.map(r=>(
          <div key={r.reply_id} style={{background:r.reply_author_role==="admin"?"var(--gl)":"var(--card)",border:`1px solid ${r.reply_author_role==="admin"?"var(--g)":"var(--bo)"}`,borderRadius:12,padding:"12px 14px"}}>
            <div style={{fontSize:10,fontWeight:700,color:r.reply_author_role==="admin"?"var(--gm)":"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>
              {r.reply_author_role==="admin"?"Lancer Support":"You"} · {new Date(r.reply_created_at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
            </div>
            <div style={{fontSize:13,color:"var(--ink)",whiteSpace:"pre-wrap",lineHeight:1.5}}>{r.reply_body}</div>
          </div>
        ))}
      </div>
      {!closed&&(
        <div>
          <textarea value={reply} onChange={e=>setReply(e.target.value)} placeholder="Add a reply…"
            style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif",resize:"vertical",minHeight:80,boxSizing:"border-box",lineHeight:1.5,marginBottom:8}}/>
          <button onClick={submitReply} disabled={busy||reply.trim().length<1} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:(busy||reply.trim().length<1)?"default":"pointer",opacity:(busy||reply.trim().length<1)?.5:1,fontFamily:"'Inter',sans-serif"}}>{busy?"Sending…":"Send reply"}</button>
        </div>
      )}
      {closed&&(
        <div style={{fontSize:12,color:"var(--mu)",textAlign:"center",padding:"10px",background:"var(--gl)",borderRadius:10}}>This ticket is closed. Open a new ticket if you need more help.</div>
      )}
    </div>
  );
}

/* ── ABOUT PAGE ──
   Four sub-tabs rendered as chips, mirroring the Metrix settings pattern:
   - "about"   : What Lancer is + who built it (MMetrix Ltd.)
   - "privacy" : Privacy Policy
   - "terms"   : Terms of Use (re-uses the shared TERMS_TEXT constant above)
   - "updates" : Release notes / changelog, with version labels driven by APP_CHANGELOG
   Version numbers on this page always read from the exported APP_VERSION constant
   so a single bump at the top of the file updates the footer AND the About screen. */
const APP_CHANGELOG=[
  {v:"1.0.1",date:"April 2026",notes:[
    "Added this About page with Privacy Policy, Terms of Use and Latest Updates.",
    "Footer now shows the current app version beneath \"Lancer by MMetrix\".",
    "Consistent first & last name validation across sign-up, client creation and profile editing (minimum 2 letters each).",
    "Client portal messaging now polls for new freelancer messages every 2 seconds, with an animated red-dot unread indicator on the Messages tab.",
    "Legacy PDF contracts whose files can no longer be located are now clearly flagged as \"Older version unavailable\" in both the portal and the freelancer view.",
    "Stronger audit trail coverage — every contract send and signature (AI, manual or PDF) now records both the freelancer and the client signing event.",
  ]},
  {v:"1.0.0",date:"March 2026",notes:[
    "Initial public release of Lancer — client management for freelance creatives.",
    "AI, manual and PDF contract creation with e-signature and audit trail.",
    "Stripe-powered deposit, balance and custom-invoice payments with refunds.",
    "Branded client portal with questionnaire, messaging and timeline tracking.",
    "Cancellation policy builder with late-notice refund rules.",
    "Metrix analytics — income, refunds and invoice reporting by year.",
  ]},
];

function AboutPage({acct}){
  const [tab,setTab]=useState("about");
  const thisYear=new Date().getFullYear();

  // Shared section heading used inside each tab (kept minimal — matches the rest of the settings sheet).
  const H=({icon,title,sub})=>(
    <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:16}}>
      <div style={{width:40,height:40,borderRadius:10,background:"var(--gl)",color:"var(--gm)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{icon}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:15,fontWeight:700,color:"var(--ink)",lineHeight:1.3}}>{title}</div>
        {sub&&<div style={{fontSize:12,color:"var(--mu)",marginTop:2,lineHeight:1.5}}>{sub}</div>}
      </div>
    </div>
  );

  // Body card — light card background with gentle border; used for long-form text blocks.
  const Card=({children,style})=>(
    <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:16,marginBottom:14,fontSize:13,lineHeight:1.7,color:"var(--ink)",...(style||{})}}>
      {children}
    </div>
  );

  // A compact label row for key/value lines (e.g. "Version · 1.0.1").
  const Row=({label,value})=>(
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid var(--bo)"}}>
      <div style={{fontSize:12,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",fontWeight:600}}>{label}</div>
      <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{value}</div>
    </div>
  );

  return (
    <>
      {/* Sub-tabs — re-uses the same .chips / .chip classes as the Metrix tab */}
      <div className="chips" style={{marginTop:0,flexWrap:"wrap"}}>
        <div className={`chip${tab==="about"?" on":""}`} onClick={()=>setTab("about")}>About</div>
        <div className={`chip${tab==="privacy"?" on":""}`} onClick={()=>setTab("privacy")}>Privacy Policy</div>
        <div className={`chip${tab==="terms"?" on":""}`} onClick={()=>setTab("terms")}>Terms of Use</div>
        <div className={`chip${tab==="updates"?" on":""}`} onClick={()=>setTab("updates")}>Latest Updates</div>
      </div>

      {/* ── ABOUT ── */}
      {tab==="about"&&(
        <>
          <H icon={I.bulb} title="About Lancer" sub="Client management for freelance creatives."/>
          <Card>
            Lancer is an all-in-one platform built for freelancers and creative studios to run their client work from first enquiry through to final payment. It brings together contracts, e‑signature, client questionnaires, a branded client portal, Stripe-powered deposit and balance payments, timeline tracking and a full inbox — so you never have to juggle a dozen different tools to keep a project moving.
            <div style={{height:10}}/>
            Whether you're a photographer, videographer, designer, developer, coach or consultant, Lancer adapts to how you work: choose your profession at sign-up, write contracts with AI assistance (or upload your own PDFs), set your cancellation policy once, and give each client a personalised portal where they can review their agreement, sign, pay and chat with you in real time.
          </Card>

          <H icon={I.sparkle} title="Built by MMetrix Ltd." sub="Software for small creative businesses."/>
          <Card>
            Lancer is designed, developed and maintained by <strong>MMetrix Ltd.</strong> — a UK-based software company focused on building practical tools that help independent creatives and small studios spend less time on admin and more time doing the work they love.
            <div style={{height:10}}/>
            All Lancer trademarks, designs, and underlying technology are owned by MMetrix Ltd. The content you create on the platform — your contracts, questionnaires, client data and communications — always remains owned by you.
          </Card>

          <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:"4px 16px",marginBottom:14}}>
            <Row label="App" value="Lancer"/>
            <Row label="Version" value={`v${APP_VERSION}`}/>
            <Row label="Published by" value="MMetrix Ltd."/>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0"}}>
              <div style={{fontSize:12,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",fontWeight:600}}>Copyright</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>&copy; {thisYear} MMetrix Ltd.</div>
            </div>
          </div>

          {/* Quick contact links — email MMetrix directly from the About page */}
          <div style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:10}}>Get in touch</div>
          <div style={{background:"var(--card)",borderRadius:16,border:"1px solid var(--bo)",overflow:"hidden"}}>
            <a href="mailto:hello@lancer-app.com" style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",textDecoration:"none",borderBottom:"1px solid var(--bo)"}}>
              <span style={{display:"inline-flex"}}>{I.mail}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>General enquiries</div>
                <div style={{fontSize:11,color:"var(--mu)"}}>hello@lancer-app.com</div>
              </div>
            </a>
            <a href="mailto:legal@lancer-app.com" style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",textDecoration:"none"}}>
              <span style={{display:"inline-flex"}}>{I.fileText}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>Legal &amp; privacy</div>
                <div style={{fontSize:11,color:"var(--mu)"}}>legal@lancer-app.com</div>
              </div>
            </a>
          </div>
        </>
      )}

      {/* ── PRIVACY POLICY ── */}
      {tab==="privacy"&&(
        <>
          <H icon={I.lock} title="Privacy Policy" sub={`Last updated: April 2026 · v${APP_VERSION}`}/>
          <Card>
            <strong>1. Who we are</strong><br/>
            Lancer is operated by MMetrix Ltd. ("MMetrix", "we", "us"). We are the data controller for personal information you provide when you use Lancer.
          </Card>
          <Card>
            <strong>2. What we collect</strong><br/>
            We collect the information you choose to give us when creating an account (name, email address, country, profession), information you add about your clients (names, contact details, project details, contracts and messages you author), and technical information that is automatically generated when you use the service (IP address, device type, browser, timestamps, login history) so we can keep the service secure and reliable.
          </Card>
          <Card>
            <strong>3. How we use it</strong><br/>
            We use your information to provide the Lancer service — to show you your account, process payments through Stripe, deliver contracts and portal links to your clients, and to send you important service notifications. We also use aggregated, anonymised usage data to improve the product. We do <strong>not</strong> sell your data or your clients' data to third parties, and we do not use it to train third‑party AI models.
          </Card>
          <Card>
            <strong>4. Who we share it with</strong><br/>
            We share limited data only with the sub‑processors we rely on to run the service — for example Supabase (database &amp; authentication hosting), Stripe (payment processing), and Anthropic (AI contract and reply drafting, via a server-side proxy so your Anthropic key is never exposed). Each sub-processor is bound by a data processing agreement and handles your data only on our instructions.
          </Card>
          <Card>
            <strong>5. Your rights</strong><br/>
            You can access, correct or delete your personal information at any time from Account Settings. You can delete your account permanently from the Danger Zone at the bottom of Account Settings, which removes all your data from our active systems. If you are in the UK or EEA, you also have the right to data portability, to object to processing, and to lodge a complaint with a supervisory authority.
          </Card>
          <Card>
            <strong>6. Data retention</strong><br/>
            We retain your account information for as long as your account is active. When you delete your account, we delete your personal information from our active systems within 30 days. Signed contracts and payment records may be retained in secure backups for up to 7 years where required by law (tax, accounting, dispute resolution).
          </Card>
          <Card>
            <strong>7. Cookies &amp; local storage</strong><br/>
            We use browser local storage to keep you signed in and to remember your preferences (such as dark mode). We do not use third‑party advertising cookies or cross‑site tracking.
          </Card>
          <Card>
            <strong>8. Contact</strong><br/>
            Questions about this policy, or requests regarding your data, can be sent to <a href="mailto:legal@lancer-app.com" style={{color:"var(--gm)",fontWeight:600}}>legal@lancer-app.com</a>.
          </Card>
        </>
      )}

      {/* ── TERMS OF USE ── */}
      {tab==="terms"&&(
        <>
          <H icon={I.fileText} title="Terms of Use" sub={`Last updated: April 2026 · v${APP_VERSION}`}/>
          {/* Re-uses the shared TERMS_TEXT constant so the Terms shown here match the ones
              on the sign-up / onboarding screen — single source of truth. */}
          <Card style={{whiteSpace:"pre-wrap",fontSize:12.5,lineHeight:1.7}}>
            {TERMS_TEXT}
          </Card>
        </>
      )}

      {/* ── LATEST UPDATES ── */}
      {tab==="updates"&&(
        <>
          <H icon={I.sparkle} title="Latest Updates" sub={`You're on v${APP_VERSION}. Here's what's changed in recent releases.`}/>
          {APP_CHANGELOG.map((rel,idx)=>{
            const isCurrent=rel.v===APP_VERSION;
            return (
              <div key={rel.v} style={{background:"var(--card)",borderRadius:14,border:`1px solid ${isCurrent?"var(--g)":"var(--bo)"}`,padding:16,marginBottom:14,position:"relative"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>v{rel.v}</div>
                  {isCurrent&&<span style={{fontSize:10,fontWeight:700,background:"var(--gl)",color:"var(--gm)",padding:"3px 8px",borderRadius:20,textTransform:"uppercase",letterSpacing:".06em"}}>Current</span>}
                  {idx===0&&!isCurrent&&<span style={{fontSize:10,fontWeight:700,background:"var(--tl)",color:"var(--t)",padding:"3px 8px",borderRadius:20,textTransform:"uppercase",letterSpacing:".06em"}}>Latest</span>}
                  <div style={{flex:1,textAlign:"right",fontSize:11,color:"var(--mu)"}}>{rel.date}</div>
                </div>
                <ul style={{margin:0,paddingLeft:18,fontSize:13,lineHeight:1.7,color:"var(--ink)"}}>
                  {rel.notes.map((n,i)=><li key={i} style={{marginBottom:4}}>{n}</li>)}
                </ul>
              </div>
            );
          })}
          <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginTop:8,lineHeight:1.6}}>
            Have a suggestion for the next release? Email <a href="mailto:hello@lancer-app.com" style={{color:"var(--gm)",fontWeight:600}}>hello@lancer-app.com</a>.
          </div>
        </>
      )}
    </>
  );
}

function SettingsSheet({which,acct,onClose,onAcctUpdate,onSignOut,onDeleteAccount,onOpen,initialMetrixTab,initialInvoiceFilter,onSwitchSection}){
  const isDesktop=typeof window!=="undefined"&&window.matchMedia("(min-width:1024px)").matches;
  // Plans list. Prices are rendered via PLAN_PRICES inside the billing view so
  // they get localised to acct.currency; the .price fields here are fallback only.
  const PLANS=[
    {id:"free",name:"Free",price:"Free",cut:"",features:["3 active clients","Basic contracts","Stripe payments","Branded portal","Client questionnaire"],highlight:false},
    {id:"pro",name:"Pro",price:"",cut:"",features:["Unlimited clients","AI contract & timeline","AI reply in inbox","AI client briefings","Intake questionnaires","Priority support"],highlight:true},
    {id:"studio",name:"Studio",price:"",cut:"",features:["Everything in Pro","2 full seats included (£12/mo each after)","Project-scoped guest seats (£6/mo, 5 included)","White-label portal","Per-seat & service-line analytics","Priority support · 1 BD SLA"],highlight:false,badge:"Best for teams"},
  ];
  const currentPlan="pro";

  // Account settings state
  const [name,setName]=useState(acct.name);
  const [email,setEmail]=useState(acct.email);
  const [role,setRole]=useState(acct.role||"");
  const [phone,setPhone]=useState(acct.phone||"");
  const [acctSaved,setAcctSaved]=useState(false);
  const [nameError,setNameError]=useState("");
  const [profilePic,setProfilePic]=useState(acct.profilePic||null);
  const [settingVerts,setSettingVerts]=useState(acct.verts||[acct.vert].filter(Boolean));
  const [settingCustomRoles,setSettingCustomRoles]=useState(()=>{
    const cr=acct.customRole;
    if(Array.isArray(cr)) return cr;
    if(typeof cr==="string"&&cr.trim()) return [cr.trim()];
    return [];
  });
  // Track which custom roles were already saved — these cannot be deleted
  const [savedCustomRoles]=useState(()=>{
    const cr=acct.customRole;
    if(Array.isArray(cr)) return new Set(cr.map(r=>r.toLowerCase()));
    if(typeof cr==="string"&&cr.trim()) return new Set([cr.trim().toLowerCase()]);
    return new Set();
  });
  const [settingCustomRoleDraft,setSettingCustomRoleDraft]=useState("");
  const commitSettingCustomRoleDraft=()=>{
    const v=settingCustomRoleDraft.trim();
    if(!v) return;
    if(settingCustomRoles.some(r=>r.toLowerCase()===v.toLowerCase())){ setSettingCustomRoleDraft(""); return; }
    setSettingCustomRoles(prev=>[...prev,v]);
    setSettingCustomRoleDraft("");
  };
  const removeSettingCustomRole=(idx)=>{
    const role=settingCustomRoles[idx];
    if(role&&savedCustomRoles.has(role.toLowerCase())) return; // cannot delete previously saved roles
    setSettingCustomRoles(prev=>prev.filter((_,i)=>i!==idx));
  };
  const handleSettingCustomRoleKey=(e)=>{
    if(e.key==="Enter"||e.key==="Tab"){
      if(settingCustomRoleDraft.trim()){ e.preventDefault(); commitSettingCustomRoleDraft(); }
    } else if(e.key==="Backspace"&&!settingCustomRoleDraft&&settingCustomRoles.length>0){
      const lastRole=settingCustomRoles[settingCustomRoles.length-1];
      if(!lastRole||!savedCustomRoles.has(lastRole.toLowerCase())) removeSettingCustomRole(settingCustomRoles.length-1);
    }
  };

  const toggleSettingVert=(id)=>setSettingVerts(prev=>prev.includes(id)?prev.filter(v=>v!==id):[...prev,id]);

  // Security state
  const [twoFA,setTwoFA]=useState(acct.twoFA||false);
  const [showChangePw,setShowChangePw]=useState(false);
  const [showSettingsPw,setShowSettingsPw]=useState(false);
  const [showSettingsNewPw,setShowSettingsNewPw]=useState(false);
  const [showSettingsConfirmPw,setShowSettingsConfirmPw]=useState(false);
  const [oldPw,setOldPw]=useState("");
  const [newPw,setNewPw]=useState("");
  const [confirmPw,setConfirmPw]=useState("");
  const [pwError,setPwError]=useState("");
  const [pwSuccess,setPwSuccess]=useState(false);
  const [metrixYear,setMetrixYear]=useState(new Date().getFullYear());

  // Profile picture file ref — at component level (Rules of Hooks)
  const profilePicRef=useRef(null);
  const [cancelNotice,setCancelNotice]=useState(acct.cancellationPolicy?.notice||"");
  const [cancelLatePolicy,setCancelLatePolicy]=useState(acct.cancellationPolicy?.latePolicy||"");
  const [cancelLateRefundPct,setCancelLateRefundPct]=useState(acct.cancellationPolicy?.lateRefundPct||50);
  const [cancelDepositRefund,setCancelDepositRefund]=useState(acct.cancellationPolicy?.depositRefund||false);
  const [confirmDelete,setConfirmDelete]=useState(false);

  // Branding state — businessName is separate from the account holder name
  const [businessName,setBusinessName]=useState(acct.businessName||"");
  const [tagline,setTagline]=useState(acct.tagline||"");
  // Phase 5 — Studio white-label theme. 4 colour overrides applied to the
  // client-facing portal via CSS variables. Defaults match Lancer's deep teal.
  const STUDIO_THEME_DEFAULTS = { primary:"#1C4D44", accent:"#9CCBBB", surface:"#FFFFFF", ink:"#0B0E0D" };
  const [studioTheme,setStudioTheme]=useState({ ...STUDIO_THEME_DEFAULTS, ...(acct.studioTheme||{}) });
  const setThemeKey=(k,v)=>setStudioTheme(prev=>({...prev,[k]:v}));
  // Pass DB value through. Empty = use platform default ("Powered by Lancer"
  // for Pro/Free; hidden for Studio). The placeholder shows the default
  // without storing it.
  const [footerText,setFooterText]=useState(acct.footerText||"");
  const [brandSaved,setBrandSaved]=useState(false);
  const [darkMode,setDarkMode]=useState(acct.darkMode||false);

  // Billing state — lifted from conditional IIFE so hooks are always called in the same order (fixes blank screen when switching profile tabs)
  const [currentBillingPlan,setCurrentBillingPlan]=useState(acct.billingPlanRaw||"free");
  const [upgradeTarget,setUpgradeTarget]=useState(null);
  const [planChanged,setPlanChanged]=useState(null);
  const [downgradeBlocked,setDowngradeBlocked]=useState(false);
  // Studio calculator state — Slice A: in-app price preview for the Studio tier.
  // Backend seat/guest enforcement ships in Phase 3 (full seats) and Phase 4 (guests).
  const [studioCalcOpen,setStudioCalcOpen]=useState(false);
  const [studioCalcSeats,setStudioCalcSeats]=useState(2);   // includes 2 base seats
  const [studioCalcGuests,setStudioCalcGuests]=useState(2); // example anchor: 2 contractors
  const [studioCadence,setStudioCadence]=useState("monthly"); // "monthly" | "annual"

  // Templates state
  const [tplEditing,setTplEditing]=useState(null); // null=list, "new"=creating, {id,...}=editing existing
  const [tplName,setTplName]=useState("");
  const [tplVert,setTplVert]=useState("");
  const [tplQuestions,setTplQuestions]=useState([]);

  // Stripe Connect state — lifted from nested IIFE
  const [connectLoading,setConnectLoading]=useState(false);
  const [connectError,setConnectError]=useState(null);
  const [connectStatus,setConnectStatus]=useState(acct.stripeConnect||null);

  // Metrix tab state — lifted from conditional IIFE
  const [metrixTab,setMetrixTab]=useState(initialMetrixTab||"analytics");
  useEffect(()=>{ if(initialMetrixTab) setMetrixTab(initialMetrixTab); },[initialMetrixTab]);

  // Stripe Connect status fetch on mount
  useEffect(()=>{
    (async()=>{
      try{
        const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
        const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-connect-onboard`,{
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
          body:JSON.stringify({action:"status"}),
        });
        const data=await res.json();
        if(data.connected){
          const status={accountId:data.stripe_account_id,chargesEnabled:data.charges_enabled,payoutsEnabled:data.payouts_enabled,detailsSubmitted:data.details_submitted};
          setConnectStatus(status);
          if(data.charges_enabled&&onAcctUpdate) onAcctUpdate({stripeConnect:status});
        }
      }catch(e){}
    })();
  },[]);

  const saveAccount=()=>{
    // Require full name: first + last (at least two words, each >= 2 chars)
    if(!isFullName(name)){
      setNameError("Please enter your first and last name.");
      return;
    }
    setNameError("");
    const primaryVert=settingVerts.includes("custom")&&settingVerts.length===1?"custom":settingVerts.find(v=>v!=="custom")||settingVerts[0]||acct.vert;
    // Include any pending draft text from the custom-role chip input
    const pendingDraft=settingCustomRoleDraft.trim();
    const finalCustomRoles=pendingDraft&&!settingCustomRoles.some(r=>r.toLowerCase()===pendingDraft.toLowerCase())?[...settingCustomRoles,pendingDraft]:settingCustomRoles;
    const customRoleDisplay=finalCustomRoles.join(" & ");
    const derivedRole=settingVerts.map(vid=>vid==="custom"?customRoleDisplay:VERTS.find(v=>v.id===vid)?.title).filter(Boolean).join(" & ");
    const cancellationPolicy={notice:cancelNotice,latePolicy:cancelLatePolicy,lateRefundPct:cancelLateRefundPct,depositRefund:cancelDepositRefund};
    if(onAcctUpdate) onAcctUpdate({name:name.trim(),email,role:role||derivedRole,phone,profilePic,twoFA,verts:settingVerts,vert:primaryVert,customRole:finalCustomRoles.length>0?finalCustomRoles:null,cancellationPolicy});
    setAcctSaved(true);
    setTimeout(()=>setAcctSaved(false),2000);
  };

  const saveBranding=()=>{
    // Workspace branding is only writable by the owner. Members of a Studio
    // workspace can VIEW Branding (we hide the menu item, but if they URL-deep-link)
    // but their save call is a no-op.
    if(!canEditWorkspaceBranding(acct)) {
      setBrandSaved(true);
      setTimeout(()=>setBrandSaved(false),2000);
      return;
    }
    const fields={businessName,tagline,darkMode};
    // Footer text is editable on Pro + Studio. Free is locked.
    if(canEditFooterText(acct)) fields.footerText=footerText;
    // Theme is editable on Studio only.
    if(isWhiteLabel(acct)) fields.studioTheme=studioTheme;
    if(onAcctUpdate) onAcctUpdate(fields);
    setBrandSaved(true);
    setTimeout(()=>setBrandSaved(false),2000);
  };

  const inp={width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"11px 14px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"};

  const TITLES={account:"Account Settings",team:"Team",branding:"Branding & Portal",billing:"Billing & Plan",contact:"Contact & Help",about:"About",metrix:"Metrix",templates:"Templates"};

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} onTouchStart={e=>e.stopPropagation()} onTouchMove={e=>e.stopPropagation()} style={{position:"fixed",inset:0,zIndex:600,background:"rgba(0,0,0,.4)",display:"flex",alignItems:isDesktop?"center":"stretch",justifyContent:"center",padding:isDesktop?"5vh 0":0}}>
      <div style={{width:"100%",maxWidth:isDesktop?700:undefined,height:isDesktop?undefined:"100%",background:"var(--cr)",display:"flex",flexDirection:"column",position:"relative",boxShadow:isDesktop?"0 0 40px rgba(0,0,0,.15)":"none",borderRadius:isDesktop?16:0,overflow:"hidden",maxHeight:isDesktop?"90vh":undefined}}>
      {/* Header */}
      <div style={{background:"var(--g)",padding:isDesktop?"18px 24px 14px":"20px 20px 16px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <button className="page-header-back" onClick={onClose}>{I.arrowLeft}</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:18,fontWeight:600,color:"#fff",fontFamily:"'Cormorant Garamond',serif"}}>{TITLES[which]}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.6)",marginTop:2}}>{acct.name} · {acct.email}</div>
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",overflowX:"hidden",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain",padding:isDesktop?"28px 32px 48px":"24px 24px 48px",scrollbarWidth:"none"}}>

        {/* ── ACCOUNT SETTINGS ── */}
        {which==="account"&&(
          <>
            {/* Profile Picture */}
            <SettingsSection title="Profile Picture">
              <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:8}}>
                <div style={{position:"relative",flexShrink:0}}>
                  {profilePic
                    ?<img src={profilePic} alt="Profile" style={{width:72,height:72,borderRadius:"50%",objectFit:"cover",border:"3px solid var(--bo)"}}/>
                    :<div style={{width:72,height:72,borderRadius:"50%",background:"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:700,color:"#fff",border:"3px solid var(--bo)"}}>
                      {acct.ini}
                    </div>
                  }
                  <div onClick={()=>profilePicRef.current&&profilePicRef.current.click()}
                    style={{position:"absolute",bottom:0,right:0,width:28,height:28,borderRadius:"50%",background:"var(--t)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"2px solid #fff",userSelect:"none"}}>
                    <span style={{display:"inline-flex",color:"#fff"}}>{I.edit}</span>
                  </div>
                  {profilePic&&<div onClick={()=>setProfilePic(null)}
                    style={{position:"absolute",top:0,right:0,width:22,height:22,borderRadius:"50%",background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"1.5px solid rgba(255,255,255,.4)"}}>
                    <span style={{fontSize:11,color:"#fff",lineHeight:1}}>✕</span>
                  </div>}
                  <input ref={profilePicRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{display:"none"}}
                    onChange={e=>{
                      const file=e.target.files?.[0];
                      if(!file) return;
                      const reader=new FileReader();
                      reader.onload=ev=>{ setProfilePic(ev.target.result); e.target.value=""; };
                      reader.readAsDataURL(file);
                    }}/>
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{acct.name}</div>
                  <div style={{fontSize:12,color:"var(--mu)",marginTop:2}}>{acct.email}</div>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection title="Personal Details">
              <SettingsField label="Full name">
                <input style={{...inp,...(nameError?{borderColor:"var(--err-bo)"}:null)}} value={name} onChange={e=>{setName(e.target.value);if(nameError&&isFullName(e.target.value)) setNameError("");}} placeholder="e.g. Jane Smith"/>
                {nameError&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}>{nameError}</div>}
              </SettingsField>
              <SettingsField label="Email address">
                <input style={inp} type="email" value={email} onChange={e=>setEmail(e.target.value)}/>
              </SettingsField>
              <SettingsField label="Phone number">
              <PhoneInput className="ifinput" countryCode={acct.country} value={phone} onChange={setPhone} placeholder="Phone number" style={{width:"100%"}}/>
              </SettingsField>
              <SettingsField label="Professional role / title">
                <input style={inp} placeholder="e.g. Wedding & Portrait Photographer" value={role} onChange={e=>setRole(e.target.value)}/>
              </SettingsField>
            </SettingsSection>

            <SettingsSection title="Creative Types">
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:8}}>
                {VERTS.map(v=>{
                  const selected=settingVerts.includes(v.id);
                  return (
                    <div key={v.id} onClick={()=>toggleSettingVert(v.id)}
                      style={{display:"flex",alignItems:"center",gap:12,background:selected?"var(--gl)":"var(--card)",border:`1.5px solid ${selected?"var(--g)":"var(--bo)"}`,borderRadius:12,padding:"11px 14px",cursor:"pointer",transition:"all .15s"}}>
                      <div style={{width:20,height:20,borderRadius:6,border:`2px solid ${selected?"var(--g)":"var(--bo)"}`,background:selected?"var(--g)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                        {selected&&<svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:20,height:20,flexShrink:0,color:"var(--ink)"}}>{v.icon}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{v.title}</div>
                        <div style={{fontSize:11,color:"var(--mu)"}}>{v.sub}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {settingVerts.includes("custom")&&(
                <div style={{marginTop:4}}>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:5}}>Custom profession{settingCustomRoles.length>1?"s":""}</div>
                  <div style={{...inp,display:"flex",flexWrap:"wrap",gap:6,alignItems:"center",padding:"8px 10px",minHeight:46}}>
                    {settingCustomRoles.map((r,i)=>{
                      const isSaved=savedCustomRoles.has(r.toLowerCase());
                      return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:6,background:"var(--gl)",border:"1px solid var(--g)",borderRadius:99,padding:isSaved?"4px 10px":"4px 4px 4px 10px",fontSize:12,fontWeight:600,color:"var(--gm)"}}>
                        <span>{r}</span>
                        {!isSaved&&<button onClick={()=>removeSettingCustomRole(i)} type="button" style={{width:18,height:18,borderRadius:"50%",background:"rgba(28,77,68,.15)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"var(--g)",fontFamily:"'Inter',sans-serif",padding:0,lineHeight:1}}>×</button>}
                      </div>);
                    })}
                    <input
                      placeholder={settingCustomRoles.length===0?"e.g. Tattoo Artist, Florist…":"Add another…"}
                      value={settingCustomRoleDraft}
                      onChange={e=>setSettingCustomRoleDraft(e.target.value)}
                      onKeyDown={handleSettingCustomRoleKey}
                      onBlur={commitSettingCustomRoleDraft}
                      style={{flex:1,minWidth:120,background:"transparent",border:"none",padding:"6px 4px",fontSize:14,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}
                    />
                  </div>
                  <div style={{fontSize:10,color:"var(--mu)",marginTop:4,fontStyle:"italic"}}>Press Enter or Tab to add multiple.</div>
                </div>
              )}
            </SettingsSection>

            <SettingsSection title="Security">
              <div style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",overflow:"hidden",marginBottom:12}}>
                {/* Change password */}
                <div style={{padding:"13px 14px",borderBottom:showChangePw?"none":"1px solid var(--bo)"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>Password</div>
                      <div style={{fontSize:11,color:"var(--mu)"}}>Update your sign-in password</div>
                    </div>
                    <button onClick={()=>{setShowChangePw(s=>!s);setShowSettingsPw(false);setShowSettingsNewPw(false);setShowSettingsConfirmPw(false);setPwError("");setPwSuccess(false);setOldPw("");setNewPw("");setConfirmPw("");}}
                      style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      {showChangePw?"Cancel":"Change"}
                    </button>
                  </div>
                  {showChangePw&&(
                    <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
                      <div style={{position:"relative"}}>
                        <input type={showSettingsPw?"text":"password"} placeholder="Current password" value={oldPw} onChange={e=>setOldPw(e.target.value)}
                          style={{...inp,marginBottom:0,paddingRight:52}}/>
                        <button type="button" onClick={()=>setShowSettingsPw(s=>!s)}
                          style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"var(--mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1,fontFamily:"'Inter',sans-serif"}}>
                          {showSettingsPw?"Hide":"Show"}
                        </button>
                      </div>
                      <div style={{position:"relative"}}>
                        <input type={showSettingsNewPw?"text":"password"} placeholder="New password (8+ chars)" value={newPw} onChange={e=>setNewPw(e.target.value)}
                          style={{...inp,marginBottom:0,paddingRight:52}}/>
                        <button type="button" onClick={()=>setShowSettingsNewPw(s=>!s)}
                          style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"var(--mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1,fontFamily:"'Inter',sans-serif"}}>
                          {showSettingsNewPw?"Hide":"Show"}
                        </button>
                      </div>
                      {newPw.length>0&&(
                        <div style={{display:"flex",flexDirection:"column",gap:2,marginTop:-4}}>
                          {[
                            {ok:newPw.length>=8,label:"At least 8 characters"},
                            {ok:/[A-Z]/.test(newPw),label:"One capital letter"},
                            {ok:/[0-9]/.test(newPw),label:"One number"},
                            {ok:/[^A-Za-z0-9]/.test(newPw),label:"One special character (!@#$...)"},
                          ].map((r,i)=>(
                            <div key={i} style={{fontSize:10,color:r.ok?"var(--ok-tx)":"var(--err-tx)",display:"flex",alignItems:"center",gap:5}}>
                              <span style={{display:"inline-flex",alignItems:"center"}}>{r.ok?I.check:I.circleO}</span>{r.label}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{position:"relative"}}>
                        <input type={showSettingsConfirmPw?"text":"password"} placeholder="Confirm new password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)}
                          style={{...inp,marginBottom:0,paddingRight:52}}/>
                        <button type="button" onClick={()=>setShowSettingsConfirmPw(s=>!s)}
                          style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"var(--mu)",cursor:"pointer",userSelect:"none",fontWeight:600,background:"none",border:"none",padding:"4px 6px",zIndex:10,lineHeight:1,fontFamily:"'Inter',sans-serif"}}>
                          {showSettingsConfirmPw?"Hide":"Show"}
                        </button>
                      </div>
                      {confirmPw.length>0&&newPw!==confirmPw&&(
                        <div style={{fontSize:11,color:"var(--err-tx)",marginTop:-4}}>Passwords don't match</div>
                      )}
                      {pwError&&<div style={{fontSize:12,color:"var(--err-tx)"}}>{pwError}</div>}
                      {pwSuccess&&<div style={{fontSize:12,color:"var(--ok-tx)",display:"flex",alignItems:"center",gap:5}}>{I.check} Password updated successfully</div>}
                      <button onClick={async ()=>{
                        setPwError("");
                        if(newPw.length<8){ setPwError("New password must be at least 8 characters."); return; }
                        if(!/[A-Z]/.test(newPw)||!/[0-9]/.test(newPw)||!/[^A-Za-z0-9]/.test(newPw)){ setPwError("Password must include a capital letter, a number, and a special character."); return; }
                        if(newPw!==confirmPw){ setPwError("Passwords don't match."); return; }
                        // Verify current password by attempting sign-in
                        const verifyRes=await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`,{
                          method:"POST",
                          headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                          body:JSON.stringify({email:acct.email,password:oldPw}),
                        });
                        if(!verifyRes.ok){ setPwError("Current password is incorrect."); return; }
                        // Use Supabase to update password
                        const res=await fetch(`${SUPABASE_URL}/auth/v1/user`,{
                          method:"PUT",
                          headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY,"Authorization":`Bearer ${_accessToken||SUPABASE_KEY}`},
                          body:JSON.stringify({password:newPw}),
                        });
                        if(!res.ok){ setPwError("Could not update password. Please try again."); return; }
                        setPwSuccess(true);
                        setOldPw("");setNewPw("");setConfirmPw("");
                        setTimeout(()=>{setShowChangePw(false);setPwSuccess(false);},2000);
                      }} disabled={!oldPw||!newPw||!confirmPw||newPw!==confirmPw||newPw.length<8||!/[A-Z]/.test(newPw)||!/[0-9]/.test(newPw)||!/[^A-Za-z0-9]/.test(newPw)}
                        style={{background:oldPw&&newPw&&confirmPw&&newPw===confirmPw&&newPw.length>=8&&/[A-Z]/.test(newPw)&&/[0-9]/.test(newPw)&&/[^A-Za-z0-9]/.test(newPw)?"var(--g)":"var(--bo)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Update Password
                      </button>
                    </div>
                  )}
                </div>
                {/* 2FA */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"13px 14px"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>Two-factor authentication</div>
                    <div style={{fontSize:11,color:twoFA?"var(--gm)":"var(--mu)"}}>{twoFA?"Enabled — verification required on sign-in":"Add an extra layer of security"}</div>
                  </div>
                  <div onClick={()=>setTwoFA(s=>!s)}
                    style={{width:40,height:24,borderRadius:99,background:twoFA?"var(--g)":"var(--bo)",position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                    <div style={{width:20,height:20,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:twoFA?18:2,boxShadow:"0 1px 4px rgba(0,0,0,.15)",transition:"left .2s"}}/>
                  </div>
                </div>
                {twoFA&&(
                  <div style={{padding:"0 14px 13px",fontSize:12,color:"var(--mu)",lineHeight:1.5,borderTop:"1px solid var(--bo)",paddingTop:10}}>
                    <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> 2FA is enabled. On your next sign-in, you'll be asked to enter an 8-digit code sent to <strong>{email||acct.email}</strong>.
                  </div>
                )}
              </div>
            </SettingsSection>

            <SettingsSection title="Cancellation Policy *">
              <div style={{fontSize:11,color:"var(--mu)",marginBottom:10}}>This policy is automatically included in AI and manual contracts and remains editable when you create each contract. PDF contracts are sent as‑is and will not include this policy. All fields are required.</div>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {/* Notice period */}
                <div>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600}}>Minimum notice period required</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {["1 week","2 weeks","30 days","6 weeks","3 months","No minimum"].map(n=>(
                      <div key={n} onClick={()=>setCancelNotice(n)}
                        style={{padding:"7px 14px",borderRadius:99,fontSize:12,fontWeight:500,cursor:"pointer",border:`1.5px solid ${cancelNotice===n?"var(--g)":"var(--bo)"}`,background:cancelNotice===n?"var(--g)":"var(--card)",color:cancelNotice===n?"#fff":"var(--mu)"}}>
                        {n}
                      </div>
                    ))}
                  </div>
                  {!cancelNotice&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.alertTri}</span>Required — please select a notice period</div>}
                </div>
                {/* Deposit refundable toggle */}
                <div>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600}}>Deposit refund on cancellation</div>
                  <div onClick={()=>setCancelDepositRefund(!cancelDepositRefund)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:10,cursor:"pointer",border:`1.5px solid ${cancelDepositRefund?"var(--g)":"var(--bo)"}`,background:cancelDepositRefund?"var(--gl)":"var(--card)",transition:"all .2s"}}>
                    {/* Toggle switch */}
                    <div style={{width:40,height:22,borderRadius:11,background:cancelDepositRefund?"var(--g)":"#ccc",position:"relative",flexShrink:0,transition:"background .2s"}}>
                      <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:cancelDepositRefund?20:2,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:13,color:"var(--ink)",fontWeight:500}}>Deposit is refundable</div>
                      <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{cancelDepositRefund?"If the client adheres to the required notice period, the deposit will be refunded":"The deposit is non-refundable, even if the client adheres to the required notice period"}</div>
                    </div>
                  </div>
                </div>
                {/* Late notice policy */}
                <div>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600}}>If cancelled with late notice (inside the required period)</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {[
                      {key:"no_refund",label:"No refund — full fee retained"},
                      {key:"partial",label:"Partial refund (set %)"},
                      {key:"full",label:"Full refund issued"},
                      {key:"discretion",label:"Freelancer's discretion (case by case)"},
                    ].map(opt=>(
                      <div key={opt.key} onClick={()=>setCancelLatePolicy(opt.key)}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,cursor:"pointer",border:`1.5px solid ${cancelLatePolicy===opt.key?"var(--g)":"var(--bo)"}`,background:cancelLatePolicy===opt.key?"var(--gl)":"var(--card)"}}>
                        <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${cancelLatePolicy===opt.key?"var(--g)":"var(--bo)"}`,background:cancelLatePolicy===opt.key?"var(--g)":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {cancelLatePolicy===opt.key&&<div style={{width:7,height:7,borderRadius:"50%",background:"#fff"}}/>}
                        </div>
                        <span style={{fontSize:13,color:"var(--ink)"}}>{opt.label}</span>
                      </div>
                    ))}
                  </div>
                  {!cancelLatePolicy&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.alertTri}</span>Required — please select a late notice policy</div>}
                </div>
                {cancelLatePolicy==="partial"&&(
                  <div>
                    <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,fontWeight:600}}>Partial refund percentage</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {[25,50,75].map(pct=>(
                        <div key={pct} onClick={()=>setCancelLateRefundPct(pct)}
                          style={{padding:"7px 14px",borderRadius:99,fontSize:12,fontWeight:500,cursor:"pointer",border:`1.5px solid ${cancelLateRefundPct===pct?"var(--t)":"var(--bo)"}`,background:cancelLateRefundPct===pct?"var(--t)":"var(--card)",color:cancelLateRefundPct===pct?"var(--tc)":"var(--mu)"}}>
                          {pct}%
                        </div>
                      ))}
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <input type="number" min={1} max={99} value={cancelLateRefundPct} onChange={e=>setCancelLateRefundPct(Math.max(1,Math.min(99,parseInt(e.target.value)||1)))}
                          style={{width:64,border:"1.5px solid var(--bo)",borderRadius:10,padding:"7px 10px",fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                        <span style={{fontSize:12,color:"var(--mu)"}}>%</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* Policy preview */}
                {cancelNotice&&cancelLatePolicy&&(
                  <div style={{background:"var(--cr)",borderRadius:10,border:"1px solid var(--bo)",padding:"12px 14px"}}>
                    <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:6}}>Policy preview (added to contracts)</div>
                    <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.7}}>
                      Cancellations require a minimum of <strong>{cancelNotice}</strong> written notice.
                      {" "}{cancelDepositRefund
                        ?"If the client provides the required notice, the deposit is fully refundable."
                        :"The deposit is non-refundable, even if the client adheres to the required notice period."
                      }
                      {" "}If cancelled with late notice (inside the required period): {
                        cancelLatePolicy==="no_refund"?"no refund will be issued — the full fee, including the deposit, is retained.":
                        cancelLatePolicy==="full"?"a full refund, including the deposit, will be issued.":
                        cancelLatePolicy==="discretion"?"a refund is at the freelancer's discretion and will be assessed on a case-by-case basis.":
                        `a ${cancelLateRefundPct}% partial refund of the total fee will be issued.`
                      }
                    </div>
                  </div>
                )}
              </div>
            </SettingsSection>

            <button onClick={saveAccount}
              style={{width:"100%",background:acctSaved?"var(--ok-tx)":"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              {acctSaved?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Saved!</>:"Save Changes"}
            </button>
            {(!cancelNotice||!cancelLatePolicy)&&(
              <div style={{fontSize:11,color:"var(--warn-tx)",textAlign:"center",marginTop:-6,marginBottom:12}}>Tip: Complete your cancellation policy before sending contracts.</div>
            )}

            <SettingsSection title="Danger Zone">
              <div style={{background:"var(--err-bg)",borderRadius:12,border:"1px solid var(--err-bo)",padding:16}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--err-tx)",marginBottom:4}}>Delete account</div>
                <div style={{fontSize:12,color:"#B91C1C",marginBottom:12,lineHeight:1.5}}>This will permanently delete your account and all client data. This cannot be undone.</div>
                {!confirmDelete?(
                  <button onClick={()=>setConfirmDelete(true)}
                    style={{background:"#DC2626",color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Delete my account
                  </button>
                ):(
                  <>
                    <div style={{fontSize:12,color:"#B91C1C",fontWeight:600,marginBottom:10}}>Are you absolutely sure? This cannot be reversed.</div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setConfirmDelete(false)}
                        style={{flex:1,background:"var(--card)",color:"var(--mu)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Cancel
                      </button>
                      <button onClick={async()=>{
                        onClose();
                        if(onDeleteAccount) await onDeleteAccount(acct.email);
                      }}
                        style={{flex:1,background:"#DC2626",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Yes, delete everything
                      </button>
                    </div>
                  </>
                )}
              </div>
            </SettingsSection>
          </>
        )}

        {/* ── TEAM (Slice D) ── */}
        {which==="team"&&<TeamPage acct={acct} onAcctUpdate={onAcctUpdate} onSwitchSection={onSwitchSection}/>}

        {/* ── BRANDING & PORTAL ── */}
        {which==="branding"&&(
          <>
            <SettingsSection title="Business Identity">
              <SettingsField label="Business / trading name (optional)">
                <input style={inp} placeholder="Optional — e.g. Sarah Lowe Photography" value={businessName} onChange={e=>setBusinessName(e.target.value)}/>
                <div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>Leave blank to display your name only. Does not change your account name.</div>
              </SettingsField>
              <SettingsField label="Tagline">
                <input style={inp} placeholder="e.g. Timeless wedding photography" value={tagline} onChange={e=>setTagline(e.target.value)}/>
              </SettingsField>
            </SettingsSection>

            <SettingsSection title="Client Portal">
              <div style={{background:"var(--g)",borderRadius:14,padding:16,marginBottom:16}}>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"rgba(255,255,255,.5)",marginBottom:6}}>Your portal URL</div>
                <div style={{fontSize:14,fontWeight:600,color:"#fff",fontFamily:"'Inter',sans-serif"}}>lancer-app.com/{acct.slug}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,.5)",marginTop:4}}>Clients access their portal at lancer-app.com/{acct.slug}/[client-name]</div>
              </div>
              <SettingsField label="Portal footer text">
                {(()=>{
                  const editable=canEditFooterText(acct);
                  const wl=isWhiteLabel(acct);
                  const placeholder=wl?"Hidden when blank":"Powered by Lancer";
                  return (
                    <>
                      <input
                        style={{...inp,opacity:editable?1:.6,cursor:editable?"text":"not-allowed"}}
                        value={footerText}
                        onChange={e=>editable&&setFooterText(e.target.value)}
                        readOnly={!editable}
                        placeholder={placeholder}
                      />
                      {!editable&&(
                        <div style={{fontSize:11,color:"var(--mu)",marginTop:6,lineHeight:1.5,display:"flex",alignItems:"center",gap:6}}>
                          <span style={{display:"inline-flex",color:"var(--mu)"}}>{I.lock}</span>
                          Custom footer is included with Pro and Studio. Free plans show "Powered by Lancer".
                        </div>
                      )}
                      {editable&&wl&&(
                        <div style={{fontSize:11,color:"var(--mu)",marginTop:6,lineHeight:1.5}}>
                          On Studio, the footer line is hidden entirely when blank — no Lancer attribution shown.
                        </div>
                      )}
                    </>
                  );
                })()}
              </SettingsField>
            </SettingsSection>

            <SettingsSection title="Portal Preview">
              {(()=>{
                // Mirror what the live portal will actually render. On Studio
                // the client portal applies studioTheme.{primary,accent,surface,ink}
                // via CSS variable overrides (see PortalRouter effect earlier);
                // the preview needs to read the same live values so the user
                // sees their colour edits reflected before saving. Non-Studio
                // users get Lancer defaults (STUDIO_THEME_DEFAULTS mirrors the
                // built-in --g / --card / --ink palette).
                const primary=studioTheme.primary||STUDIO_THEME_DEFAULTS.primary;
                const surface=studioTheme.surface||STUDIO_THEME_DEFAULTS.surface;
                const ink=studioTheme.ink||STUDIO_THEME_DEFAULTS.ink;
                const accent=studioTheme.accent||STUDIO_THEME_DEFAULTS.accent;
                return (
              <div style={{background:surface,borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden"}}>
                <div style={{background:primary,padding:"16px 20px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,.7)",letterSpacing:".05em"}}>Lancer · {businessName}</div>
                  <div style={{fontSize:18,fontWeight:600,color:"#fff",marginTop:4,fontFamily:"'Cormorant Garamond',serif"}}>Client Portal</div>
                  {tagline&&<div style={{fontSize:11,color:"rgba(255,255,255,.6)",marginTop:4,fontStyle:"italic"}}>{tagline}</div>}
                </div>
                {/* Accent-tinted CTA row so the accent colour is visible in
                    the preview — otherwise picking a new accent looks like a
                    no-op until the user opens a real client portal. */}
                <div style={{padding:"12px 20px",display:"flex",alignItems:"center",gap:10,borderBottom:"1px solid var(--bo)"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:accent,flexShrink:0}}/>
                  <div style={{fontSize:12,color:ink,opacity:.7}}>Next step</div>
                  <div style={{marginLeft:"auto",background:accent,color:"#fff",fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:99}}>Continue</div>
                </div>
                {(()=>{
                  const wl=isWhiteLabel(acct);
                  const trimmed=(footerText||"").trim();
                  if(wl&&!trimmed) return null;
                  const display=wl?trimmed:(trimmed||"Powered by Lancer");
                  return (
                    <div style={{padding:16,fontSize:12,color:ink,opacity:.55,textAlign:"center"}}>
                      {display}
                    </div>
                  );
                })()}
              </div>
                );
              })()}
            </SettingsSection>

            <SettingsSection title="App Appearance">
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:22}}>{darkMode?"🌙":"☀️"}</span>
                  <div>
                    <div style={{fontSize:14,fontWeight:600,color:"var(--ink)"}}>{darkMode?"Dark Mode":"Light Mode"}</div>
                    <div style={{fontSize:12,color:"var(--mu)"}}>Toggle app appearance</div>
                  </div>
                </div>
                <div onClick={()=>{
                  const next=!darkMode;
                  setDarkMode(next);
                  if(next) document.body.classList.add("dark");
                  else document.body.classList.remove("dark");
                  if(onAcctUpdate) onAcctUpdate({darkMode:next});
                }} style={{width:52,height:28,borderRadius:99,background:darkMode?"var(--t)":"var(--bo)",position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:darkMode?26:3,width:22,height:22,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 4px rgba(0,0,0,.2)"}}/>
                </div>
              </div>
            </SettingsSection>

            {/* Phase 6 — Custom domain (Studio-only, owner-only).
                DISABLED for now — Vercel auth scoping needs revisiting.
                To re-enable: uncomment the line below.
                Backend (edge functions, RPC, workspaces.custom_domain column,
                CustomDomainSection component) all remain intact and ready. */}
            {/* <CustomDomainSection acct={acct} onSwitchSection={onSwitchSection}/> */}

            {/* Phase 5 — Studio white-label theme. Locked behind upgrade for Pro/Free. */}
            <SettingsSection title="Portal Theme">
              {!isWhiteLabel(acct)?(
                <div style={{background:"var(--gl)",border:"1px solid var(--bo)",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
                  <span style={{display:"inline-flex",color:"var(--mu)",flexShrink:0,marginTop:2}}>{I.lock}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}>Custom theme is a Studio feature</div>
                    <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Choose your portal's primary, accent, surface, and text colours. The "Powered by Lancer" footer is also stripped on Studio.</div>
                    <button onClick={()=>{ if(onSwitchSection) onSwitchSection("billing"); }} style={{marginTop:10,background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>See Studio</button>
                  </div>
                </div>
              ):(
                <>
                  <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5,marginBottom:12}}>These colours apply to your client portal — not the in-app dashboard.</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                    {[
                      {key:"primary",label:"Primary",hint:"Header / buttons"},
                      {key:"accent", label:"Accent", hint:"Highlights / CTAs"},
                      {key:"surface",label:"Surface",hint:"Card background"},
                      {key:"ink",    label:"Text",   hint:"Primary text"},
                    ].map(({key,label,hint})=>(
                      <div key={key} style={{display:"flex",alignItems:"center",gap:10,background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px 12px"}}>
                        <input type="color" value={studioTheme[key]||STUDIO_THEME_DEFAULTS[key]} onChange={e=>setThemeKey(key,e.target.value)}
                          style={{width:36,height:36,border:"none",borderRadius:8,cursor:"pointer",background:"transparent",padding:0}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>{label}</div>
                          <div style={{fontSize:10,color:"var(--mu)"}}>{hint}</div>
                          <div style={{fontSize:10,color:"var(--mu)",fontFamily:"monospace",marginTop:2}}>{studioTheme[key]||STUDIO_THEME_DEFAULTS[key]}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={()=>setStudioTheme({...STUDIO_THEME_DEFAULTS})} style={{background:"transparent",border:"none",color:"var(--gm)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",textDecoration:"underline"}}>
                    Reset to Lancer defaults
                  </button>
                </>
              )}
            </SettingsSection>

            <button onClick={saveBranding} style={{width:"100%",background:brandSaved?"var(--ok-tx)":"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              {brandSaved?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Saved!</>:"Save Branding"}
            </button>
          </>
        )}

        {/* ── BILLING & PLAN ── */}
        {which==="billing"&&(()=>{
          // Hooks lifted to top of SettingsSheet to keep call order stable across tab switches.
          // Aliases preserve the existing local variable names in the rest of this block.
          const currentPlan=currentBillingPlan;
          const setCurrentPlan=setCurrentBillingPlan;

          const TODAY=new Date();
          const todayISO=localToday();

          const PLAN_FEATURES={
            free:["3 active clients","Basic contracts","Stripe payments","Branded portal","Client questionnaire"],
            pro:["Unlimited clients","AI features","Intake questionnaires","Priority support","Custom domain portal"],
            studio:["Everything in Pro","2 full seats included (£12/mo each after)","Project-scoped guest seats (£6/mo, 5 included)","White-label portal","Per-seat & service-line analytics","Priority support · 1 BD SLA"],
          };
          // PRICING — single source of truth for all plan amounts (GBP).
          // Studio uses a base + per-seat + per-guest model; annual discount
          // applies to the base only (additional seats/guests stay monthly).
          // See studio-membership-spec.md §2 and studio-membership-build-plan.md
          // Phase 0 for context. NEVER hard-code prices in JSX — derive from this.
          const PRICING={
            free:   { base: 0,                       transactionFee: 1.5 },
            pro:    { base: 12,                      transactionFee: 0.8 },
            studio: {
              base: 39,
              annualBase: 390,
              seatsIncluded: 2,
              perSeat: 12,
              guestsIncluded: 5,
              perGuest: 6,
              softCapGuests: 15,
              transactionFee: 0.5,
            },
          };
          // Helpers — Studio price calculation. Annual = annualBase + monthly add-ons × 12.
          const calcStudioMonthly=(seats,guests)=>PRICING.studio.base
            +Math.max(0,seats-PRICING.studio.seatsIncluded)*PRICING.studio.perSeat
            +Math.max(0,guests-PRICING.studio.guestsIncluded)*PRICING.studio.perGuest;
          const calcStudioAnnualTotal=(seats,guests)=>PRICING.studio.annualBase
            +Math.max(0,seats-PRICING.studio.seatsIncluded)*PRICING.studio.perSeat*12
            +Math.max(0,guests-PRICING.studio.guestsIncluded)*PRICING.studio.perGuest*12;
          // Plans are priced in GBP (the platform's Stripe currency). For a non-GBP
          // signup, convert to the user's currency for display so they see a
          // number they recognise. The actual Stripe charge is still GBP, so
          // the "Next charge" line also shows the GBP amount as a disclosure.
          const PLAN_AMOUNTS={free:PRICING.free.base,pro:PRICING.pro.base,studio:PRICING.studio.base}; // GBP — base only for Studio
          const GBP_TO_LOCAL={GBP:1,USD:1.27,EUR:1.17,AUD:1.93,CAD:1.73,NZD:2.12,SEK:13.5,NOK:13.8,DKK:8.7,SGD:1.70,INR:106,ZAR:23.5,AED:4.66,NGN:2030,KES:164};
          const userCurrency=acct.currency||"GBP";
          const userSymbol=acct.currencySymbol||"£";
          const fxRate=GBP_TO_LOCAL[userCurrency]||1;
          const localAmount=gbp=>Math.round((Number(gbp)||0)*fxRate);
          const fmtLocalPrice=gbp=>gbp===0?"Free":`${userSymbol}${localAmount(gbp)}/mo`;
          // Studio headline = "From £39/mo" since true price depends on seats/guests.
          const PLAN_PRICES={free:"Free",pro:fmtLocalPrice(PRICING.pro.base),studio:`From ${userSymbol}${localAmount(PRICING.studio.base)}/mo`};
          const PLAN_CUTS={free:`${PRICING.free.transactionFee}% platform fee per transaction`,pro:`${PRICING.pro.transactionFee}% platform fee per transaction`,studio:`${PRICING.studio.transactionFee}% platform fee per transaction`};

          // Studio calculator derived values — lifted to billing-view scope so
          // both the Plans card and the Upgrade-confirmation modal can read them.
          const calcSeats=Math.max(PRICING.studio.seatsIncluded,studioCalcSeats);
          const calcGuests=Math.max(0,studioCalcGuests);
          const calcMonthlyTotal=calcStudioMonthly(calcSeats,calcGuests);
          const calcAnnualTotal=calcStudioAnnualTotal(calcSeats,calcGuests);
          const calcShownTotal=studioCadence==="annual"?calcAnnualTotal:calcMonthlyTotal;
          const cadenceUnit=studioCadence==="annual"?"/yr":"/mo";

          // Grace period: user cancelled a paid plan but it's still active until expiry
          const expiryDate=acct.planExpiryDate||null;
          const cancelledPlan=acct.cancelledPlan||null;
          const inGracePeriod=expiryDate&&cancelledPlan&&expiryDate>=todayISO;
          // Effective plan = if in grace period, use the cancelled plan's features
          const effectivePlan=inGracePeriod?cancelledPlan:currentPlan;

          // ISO helpers used by both invoice generation and next-billing math.
          const _isoFromDate=(dd)=>`${dd.getFullYear()}-${String(dd.getMonth()+1).padStart(2,"0")}-${String(dd.getDate()).padStart(2,"0")}`;
          const _isoToDate=(iso)=>{ const [y,m,dd]=String(iso).split("-").map(Number); return new Date(y,m-1,dd); };
          // Advance one billing period — same day-of-month, clamped to the
          // target month's last day. Avoids JS Date.setMonth's rollover
          // (May 31 → setMonth(5) → Jun 31 → Jul 1).
          const _advanceOneMonth=(dt)=>{
            const day=dt.getDate();
            const target=new Date(dt.getFullYear(),dt.getMonth()+1,1);
            const lastDay=new Date(target.getFullYear(),target.getMonth()+1,0).getDate();
            target.setDate(Math.min(day,lastDay));
            return target;
          };

          // Seat-aware monthly amount for Studio; flat base for Pro/Free. We
          // don't have historical seat counts so older Studio invoices reflect
          // the current seat count — close enough for a synthesised list.
          const _studioMonthlyGBP=(()=>{
            const roster=Array.isArray(acct.workspaceMembers)?acct.workspaceMembers:[];
            const activeFull=roster.filter(m=>m.status==="active"&&(m.role==="owner"||m.role==="member")).length||1;
            const activeGuests=roster.filter(m=>m.status==="active"&&m.role==="guest").length;
            return calcStudioMonthly(activeFull,activeGuests);
          })();
          const _amtFor=(planId)=>planId==="studio"?_studioMonthlyGBP:(PLAN_AMOUNTS[planId]||0);

          // Generate the invoices list. Three flavours stacked oldest → newest:
          //   1. Optional Studio free-trial row (£0) covering billing_start_date →
          //      trial_ends_at - 1 day. Only emitted when both fields are set
          //      and the trial actually started before the trial end.
          //   2. One row per fully-started billing period (start date <= today).
          //      Period covers period_start → next_period_start - 1 day; charge
          //      lands on period_start so it counts as "Paid" by today.
          //   3. Reversed before return so the freshest invoice is at the top.
          const generateInvoices=(plan,startISO)=>{
            if(!startISO||plan==="free") return [];
            const trialEndsRaw=acct.workspaceTrialEndsAt;
            const trialEndISO=trialEndsRaw?_isoFromDate(new Date(trialEndsRaw)):null;
            const invoices=[];
            let d=_isoToDate(startISO);

            // Free-trial row (Studio only). When the start date equals the
            // trial end (no trial granted on re-subscribe), this branch is
            // skipped and the loop below picks up from the start date.
            if(plan==="studio"&&trialEndISO&&_isoFromDate(d)<trialEndISO){
              const trialEndDate=_isoToDate(trialEndISO);
              const lastTrialDay=new Date(trialEndDate.getTime()-86400000);
              invoices.push({
                date:`${fmtDate(_isoFromDate(d))} – ${fmtDate(_isoFromDate(lastTrialDay))}`,
                desc:"Studio · Free trial",
                amt:`${userSymbol}0.00`,
                status:"Free trial",
              });
              d=trialEndDate; // first paid period starts the day the trial ends
            }

            const amt=_amtFor(plan);
            while(d<=TODAY){
              // Range end = next charge date (Stripe convention "31 May - 30 Jun"
              // for a charge that lands on 31 May with renewal on 30 Jun). For
              // the very latest period, prefer Stripe's authoritative
              // current_period_end if it lines up — handles month-end clamps
              // (May 31 → Jun 30) without relying on JS Date math alone.
              const stripePeriodEnd=acct.subscriptionCurrentPeriodEnd?_isoFromDate(new Date(acct.subscriptionCurrentPeriodEnd)):null;
              const computedEnd=_advanceOneMonth(d);
              const computedEndISO=_isoFromDate(computedEnd);
              const useStripeEnd=stripePeriodEnd
                && Math.abs(_isoToDate(stripePeriodEnd).getTime()-computedEnd.getTime())<3*86400000;
              const periodEndDate=useStripeEnd?_isoToDate(stripePeriodEnd):computedEnd;
              invoices.push({
                date:`${fmtDate(_isoFromDate(d))} – ${fmtDate(_isoFromDate(periodEndDate))}`,
                desc:`${plan.charAt(0).toUpperCase()+plan.slice(1)} · Monthly`,
                amt:`${userSymbol}${localAmount(amt)}.00`,
                status:"Paid",
              });
              d=computedEnd;
            }
            return invoices.reverse();
          };

          const billingInvoices=generateInvoices(inGracePeriod?cancelledPlan:currentPlan,acct.billingStartDate);

          // Next billing date — Stripe's current_period_end is the source of
          // truth. The legacy "billing_start_date + N months" fallback only
          // kicks in for accounts without a Stripe sub on file (admin grants,
          // legacy data) and is best-effort.
          const _fallbackNextBillingISO=()=>{
            if(!acct.billingStartDate) return null;
            const start=new Date(acct.billingStartDate);
            const day=start.getDate();
            // Walk month-by-month, snapping to the original day-of-month (or the
            // last day of the month when the target month is shorter). Avoids
            // setMonth's rollover where May 31 → Jul 1.
            let probe=new Date(start);
            while(probe<=TODAY){
              const nextMonth=probe.getMonth()+1;
              probe=new Date(probe.getFullYear(),nextMonth,1);
              const lastDay=new Date(probe.getFullYear(),probe.getMonth()+1,0).getDate();
              probe.setDate(Math.min(day,lastDay));
            }
            return _isoFromDate(probe);
          };
          const nextBillingISO=(()=>{
            const stripeEnd=acct.subscriptionCurrentPeriodEnd;
            if(stripeEnd){ const d=new Date(stripeEnd); if(!isNaN(d)) return _isoFromDate(d); }
            return _fallbackNextBillingISO();
          })();
          const nextBillingDate=(()=>{
            if(inGracePeriod) return fmtDate(expiryDate);
            if(currentPlan==="free") return null;
            return nextBillingISO?fmtDate(nextBillingISO):null;
          })();

          // Next-charge amount. Reuses the seat-aware helper above so the
          // invoice synthesis and the "Next charge" display can never drift
          // apart. _amtFor handles Studio (base + seat + guest overage) and
          // falls back to PLAN_AMOUNTS for Pro/Free.
          const nextChargeGBP=_amtFor(effectivePlan);

          const confirmUpgrade=async (planId)=>{
            // Re-upgrade during grace period: undo the scheduled cancel on Stripe
            // (cancel_at_period_end → false) so the user keeps their paid plan
            // without being charged again. The CRM previously did this purely
            // client-side, leaving Stripe to still cancel at period end despite
            // the UI claiming the plan was reactivated.
            if(inGracePeriod&&planId===cancelledPlan){
              setUpgradeTarget(null);
              setPlanChanged("loading");
              try {
                const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
                const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-subscription`,{
                  method:"POST",
                  headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
                  body:JSON.stringify({action:"reactivate"}),
                });
                const data=await res.json().catch(()=>({}));
                if(!res.ok||!data?.ok) throw new Error(data?.error||`Reactivate failed (HTTP ${res.status})`);
              } catch(err){
                // Fall through to local-only update so the UI still recovers.
                // Stripe stays scheduled to cancel until someone retries — we
                // surface the error so it isn't silent.
                console.error("[reactivate] Stripe reactivate failed:",err);
                try{ alert("Couldn't reactivate on Stripe — your subscription may still cancel. Please try again or contact support."); }catch(_){}
              }
              setCurrentPlan(cancelledPlan);
              if(onAcctUpdate) onAcctUpdate({billingPlan:cancelledPlan,cancelledPlan:null,planExpiryDate:null});
              setPlanChanged(cancelledPlan);
              setTimeout(()=>setPlanChanged(null),3000);
              return;
            }

            // Downgrade-to-free blocker
            if(planId==="free"&&(currentPlan!=="free"||inGracePeriod)){
              // If already in grace period, reconcile any legacy state and confirm.
              // Pre-v2 cancel attempts could leave billing_plan='pro' even though
              // cancelled_plan + plan_expiry_date were set; clicking "Downgrade to
              // Free" again should flip billing_plan to 'free' so the Plans grid
              // shows Free as current and surfaces the "Re-upgrade for free"
              // affordance on the Pro card.
              if(inGracePeriod){
                setUpgradeTarget(null);
                if(currentPlan!=="free"){
                  setCurrentPlan("free");
                  if(onAcctUpdate) onAcctUpdate({billingPlan:"free"});
                }
                setPlanChanged("free");
                setTimeout(()=>setPlanChanged(null),3000);
                return;
              }
              const totalClients=(acct.clients||[]).filter(c=>!c.archived).length;
              if(totalClients>3){
                setUpgradeTarget(null);
                setDowngradeBlocked(true);
                setTimeout(()=>setDowngradeBlocked(false),6000);
                return;
              }
              // Cancel: set cancel_at_period_end=true on the actual Stripe
              // subscription so the user really stops being billed at period end.
              // Locally we flip billingPlan to "free" so the Plans grid renders
              // Free as the current plan and the Pro card surfaces a clickable
              // "Re-upgrade for free" button. Features remain enabled because
              // effectivePlan() treats cancelledPlan + planExpiryDate as the
              // active plan during grace.
              const previousPlan=currentPlan;
              setUpgradeTarget(null);
              setPlanChanged("loading");
              setCurrentPlan("free");
              try {
                const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
                const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-subscription`,{
                  method:"POST",
                  headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
                  body:JSON.stringify({action:"cancel"}),
                });
                const data=await res.json().catch(()=>({}));
                if(!res.ok||!data?.ok) throw new Error(data?.error||`Cancel failed (HTTP ${res.status})`);
                // The edge function returns plan_expiry_date from Stripe when a real
                // subscription exists; for admin-granted plans without a sub it returns
                // billing_start_date + 1 month; if even that's missing we fall back to
                // the locally-computed nextBillingISO so the user still gets a sensible
                // grace window rather than a null expiry.
                if(onAcctUpdate) onAcctUpdate({
                  billingPlan:"free",
                  cancelledPlan: data.cancelled_plan||previousPlan,
                  planExpiryDate: data.plan_expiry_date||nextBillingISO||todayISO,
                });
                setPlanChanged("downgraded");
              } catch(err){
                // Fallback: edge function unreachable, no Stripe sub row, or
                // some other error. Apply the cancel locally so the UI still
                // reflects the user's intent — Stripe will keep billing until
                // the issue is resolved, so we surface the error rather than
                // failing silently. Common cause: profile.billing_plan='pro'
                // without a corresponding stripe_subscriptions row (admin grant
                // or test data).
                console.error("[cancel] Stripe cancel failed, falling back to local:",err);
                try{ alert("Couldn't reach Stripe to cancel — your card may still be charged. Please try again or contact support."); }catch(_){}
                const expiryISO=nextBillingISO||todayISO;
                if(onAcctUpdate) onAcctUpdate({
                  billingPlan:"free",
                  cancelledPlan:previousPlan,
                  planExpiryDate:expiryISO,
                });
                setPlanChanged("downgraded");
              }
              setTimeout(()=>setPlanChanged(null),5000);
              return;
            }

            // Paid downgrade (Studio → Pro): keep current Studio sub running
            // until period_end, then auto-start the target plan on the same
            // saved card. Going through stripe-checkout would cancel Studio
            // immediately and start Pro now, which forfeits the remaining
            // Studio features the user already paid for.
            if(planId!=="free"&&currentPlan!=="free"&&planId!==currentPlan&&!inGracePeriod){
              const RANK={free:0,pro:1,studio:2};
              if((RANK[planId]??0)<(RANK[currentPlan]??0)){
                const previousPlan=currentPlan;
                setUpgradeTarget(null);
                setPlanChanged("loading");
                // Keep currentPlan = previousPlan so the Plans grid renders the
                // user's CURRENT (still active) plan as "Current plan" — they're
                // still on Studio (with full Studio features) until period_end.
                // The scheduledPlan field tells the UI Pro will start automatically.
                try {
                  const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
                  const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-subscription`,{
                    method:"POST",
                    headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
                    body:JSON.stringify({action:"schedule_downgrade",target_plan:planId}),
                  });
                  const data=await res.json().catch(()=>({}));
                  if(!res.ok||!data?.ok) throw new Error(data?.error||`Schedule downgrade failed (HTTP ${res.status})`);
                  // billingPlan stays on previousPlan (Studio). The grace UI
                  // fires off cancelledPlan + planExpiryDate together; effectivePlan()
                  // returns cancelledPlan during grace which still equals previousPlan.
                  if(onAcctUpdate) onAcctUpdate({
                    billingPlan: previousPlan,
                    cancelledPlan: data.cancelled_plan||previousPlan,
                    planExpiryDate: data.plan_expiry_date||nextBillingISO||todayISO,
                    scheduledPlan: data.scheduled_plan||planId,
                  });
                  setPlanChanged("downgraded");
                } catch(err){
                  console.error("[schedule_downgrade] failed",err);
                  try{ alert("Couldn't schedule the downgrade on Stripe — please try again or contact support."); }catch(_){}
                  // No state to roll back — currentPlan was never changed.
                }
                setTimeout(()=>setPlanChanged(null),5000);
                return;
              }
            }

            // Paid upgrade: open Stripe Checkout
            setUpgradeTarget(null);
            setPlanChanged("loading");
            try {
              const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
              const baseUrl=window.location.href.split("?")[0].split("#")[0];
              const successUrl=baseUrl+"?plan_upgraded="+planId;
              const cancelUrl=baseUrl+"?plan_cancelled=1";
              // Slice C: Studio passes seat/guest/cadence so the edge function builds
              // the right multi-line subscription. Pro/Free ignore these params.
              const checkoutBody={plan_id:planId, success_url:successUrl, cancel_url:cancelUrl};
              if(planId==="studio"){
                checkoutBody.seat_count=calcSeats;
                checkoutBody.guest_count=calcGuests;
                checkoutBody.cadence=studioCadence;
              }
              const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`,{
                method:"POST",
                headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
                body:JSON.stringify(checkoutBody),
              });
              const data=await res.json();
              if(data.url){
                // Redirect to Stripe Checkout in same window so we return to success_url
                window.location.href=data.url;
                return;
              } else {
                throw new Error(data.error||"Could not open checkout");
              }
            } catch(err){
              // Fallback: apply locally if Stripe not yet configured
              const startDate=localToday();
              setCurrentPlan(planId);
              if(onAcctUpdate) onAcctUpdate({billingPlan:planId,billingStartDate:startDate});
              setPlanChanged(planId);
              setTimeout(()=>setPlanChanged(null),3000);
            }
          };

          return (
            <>
              {planChanged&&(
                <div style={{background:planChanged==="loading"||planChanged==="checkout_opened"?"#EFF6FF":planChanged==="downgraded"?"var(--warn-bg)":"var(--gl)",borderRadius:12,padding:12,marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{color:planChanged==="loading"||planChanged==="checkout_opened"?"#1D4ED8":planChanged==="downgraded"?"var(--warn-tx)":"var(--gm)",display:"flex"}}>{planChanged==="loading"?I.hourglass:planChanged==="checkout_opened"?I.linkExt:planChanged==="downgraded"?I.caldate:I.check}</span>
                  <span style={{fontSize:13,fontWeight:600,color:planChanged==="loading"||planChanged==="checkout_opened"?"#1D4ED8":planChanged==="downgraded"?"var(--warn-tx)":"var(--g)"}}>
                    {planChanged==="loading"&&"Opening Stripe checkout\u2026"}
                    {planChanged==="checkout_opened"&&"Stripe checkout opened in a new tab. Your plan will update automatically once payment is complete."}
                    {planChanged==="downgraded"&&`Cancelled. You'll keep ${(cancelledPlan||"pro").charAt(0).toUpperCase()+(cancelledPlan||"pro").slice(1)} features until ${nextBillingDate||"your next billing date"}.`}
                    {planChanged!=="loading"&&planChanged!=="checkout_opened"&&planChanged!=="downgraded"&&`Plan updated to ${planChanged.charAt(0).toUpperCase()+planChanged.slice(1)}!`}
                  </span>
                </div>
              )}

              {/* Slice D fix: surface trial countdown when Studio trial is active. */}
              {(()=>{
                const trialEndsRaw=acct.workspaceTrialEndsAt;
                const trialEnd=trialEndsRaw?new Date(trialEndsRaw):null;
                // inTrial must NOT be true during grace — when a Studio user
                // schedules a downgrade, trialEndsAt may still be set on the
                // workspace; the trial copy ("You won't be charged until X")
                // would otherwise leak into the cancellation banner.
                const inTrial=!!(trialEnd&&trialEnd>new Date()&&effectivePlan==="studio"&&!inGracePeriod);
                const trialDaysLeft=inTrial?Math.max(0,Math.ceil((trialEnd-new Date())/86400000)):0;
                const trialEndISO=trialEnd?`${trialEnd.getFullYear()}-${String(trialEnd.getMonth()+1).padStart(2,"0")}-${String(trialEnd.getDate()).padStart(2,"0")}`:null;
                return (
                  <SettingsSection title="Current Plan">
                    <div style={{background:inGracePeriod?"var(--warn-bg)":inTrial?"var(--tl)":(effectivePlan==="free"?"var(--card)":"var(--gl)"),borderRadius:14,border:"1.5px solid "+(inGracePeriod?"var(--warn-bo)":inTrial?"var(--t)":(effectivePlan==="free"?"var(--bo)":"var(--g)")),padding:16,display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:16,fontWeight:700,color:effectivePlan==="free"?"var(--mu)":"var(--ink)"}}>{effectivePlan.charAt(0).toUpperCase()+effectivePlan.slice(1)}</div>
                        <div style={{fontSize:12,color:inGracePeriod?"var(--warn-tx)":inTrial?"var(--gm)":(effectivePlan==="free"?"var(--mu)":"var(--gm)")}}>
                          {inGracePeriod
                            ?`Cancels ${fmtDate(expiryDate)} · features active until then`
                            :inTrial
                              ?`Free trial · ${trialDaysLeft} day${trialDaysLeft===1?"":"s"} remaining · first charge ${fmtDate(trialEndISO)}`
                              :`${effectivePlan==="studio"?`${userSymbol}${localAmount(nextChargeGBP)}/mo`:PLAN_PRICES[effectivePlan]}${nextBillingDate?` · renews ${nextBillingDate}`:""}`}
                          {!inGracePeriod&&!inTrial&&effectivePlan==="free"&&" · no subscription"}
                        </div>
                      </div>
                      <div style={{background:inGracePeriod?"var(--warn-tx)":inTrial?"var(--g)":(effectivePlan==="free"?"var(--bo)":"var(--g)"),color:"#fff",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                        {inGracePeriod?"Cancelling":inTrial?"Trial":effectivePlan==="free"?"Free":"Active"}
                      </div>
                    </div>
                    {inGracePeriod&&<div style={{fontSize:11,color:"var(--warn-tx)",lineHeight:1.5}}>Your {cancelledPlan.charAt(0).toUpperCase()+cancelledPlan.slice(1)} plan is cancelled but features remain active until {fmtDate(expiryDate)}.{acct.scheduledPlan&&acct.scheduledPlan!=="free"?` Your ${acct.scheduledPlan.charAt(0).toUpperCase()+acct.scheduledPlan.slice(1)} plan starts automatically on ${fmtDate(expiryDate)} (${PLAN_PRICES[acct.scheduledPlan]}).`:" Re-upgrade for free before then to keep your plan."}</div>}
                    {inTrial&&<div style={{fontSize:11,color:"var(--gm)",lineHeight:1.5}}>You won't be charged until {fmtDate(trialEndISO)}. Cancel anytime before then with no charge.</div>}
                    {!inGracePeriod&&!inTrial&&nextBillingDate&&effectivePlan!=="free"&&<div style={{fontSize:11,color:"var(--mu)"}}>Next charge: {userSymbol}{localAmount(nextChargeGBP)}.00 on {nextBillingDate}{userCurrency!=="GBP"?` (billed as £${nextChargeGBP}.00 GBP)`:""}.</div>}
                    {!inGracePeriod&&!inTrial&&effectivePlan==="free"&&<div style={{fontSize:11,color:"var(--mu)"}}>Upgrade to Pro or Studio to unlock unlimited clients, AI features, and more.</div>}
                  </SettingsSection>
                );
              })()}

              {downgradeBlocked&&(
                <div id="downgrade-blocked-msg" style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:14,padding:"14px 16px",marginBottom:12,display:"flex",alignItems:"flex-start",gap:12}}>
                  <span style={{display:"inline-flex",flexShrink:0,color:"var(--warn-tx)"}}>{I.alertTri}</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--info-tx)",marginBottom:4}}>Cannot downgrade to Free</div>
                    <div style={{fontSize:12,color:"var(--warn-tx)",lineHeight:1.6}}>
                      You have <strong>{(acct.clients||[]).filter(c=>!c.archived).length} active clients</strong>. The Free plan allows a maximum of 3 active clients.
                      Archive clients down to 3 or fewer, then try again.
                    </div>
                  </div>
                </div>
              )}
              <SettingsSection title="Plans">
                {PLANS.map(p=>{
                  // Use effectivePlan (Studio during grace) instead of the raw
                  // profile.billing_plan (which the edge function flips to "free"
                  // when scheduling a downgrade). Otherwise the Plans grid shows
                  // Free as "Current plan" while the user is still on Studio.
                  const isCurrent=p.id===effectivePlan;
                  const isDowngrade=["pro","studio"].indexOf(p.id)<["pro","studio"].indexOf(effectivePlan);
                  const isStudio=p.id==="studio";
                  // During the grace period the user has technically downgraded but the
                  // cancelled plan's card should expose a clickable "Re-upgrade for free"
                  // affordance so they can undo the cancel before period_end.
                  // NOTE: don't gate on !isCurrent — effectivePlan keeps returning the
                  // cancelled plan during grace, so its card *is* current. We render
                  // the reactivate button INSTEAD of the "Current plan" label below.
                  const isReactivateOption=inGracePeriod&&p.id===cancelledPlan;
                  // Studio → Pro scheduled downgrade: mark the queued target plan so its
                  // card shows "Pro starts [date]" instead of an upgrade button. Clicking
                  // would otherwise drop into stripe-checkout and create a duplicate.
                  const isScheduledNext=inGracePeriod&&acct.scheduledPlan===p.id&&!isCurrent;
                  // Concrete anchor example: 3-person studio + 2 contractors → £63/mo
                  const exampleMonthly=calcStudioMonthly(3,2);
                  return (
                    <div key={p.id} style={{background:isCurrent?"var(--g)":"var(--card)",borderRadius:14,border:"1.5px solid "+(isCurrent?"var(--g)":"var(--bo)"),padding:18,marginBottom:10,position:"relative"}}>
                      {isStudio&&!isCurrent&&p.badge&&(
                        <div style={{position:"absolute",top:-9,right:14,background:"var(--t)",color:"var(--tc)",fontSize:10,fontWeight:700,letterSpacing:".04em",textTransform:"uppercase",padding:"3px 8px",borderRadius:99}}>{p.badge}</div>
                      )}
                      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:4}}>
                        <div style={{fontSize:16,fontWeight:700,color:isCurrent?"#fff":"var(--ink)"}}>{p.name}</div>
                        <div style={{fontSize:14,fontWeight:700,color:isCurrent?"#fff":"var(--ink)"}}>{PLAN_PRICES[p.id]}</div>
                      </div>
                      {isStudio&&!isCurrent&&(
                        <>
                          {/* Trial badge only when this account hasn't used it yet —
                              once consumed, the server skips trial_period_days and
                              charges immediately, so promising "no charge today"
                              would be wrong. */}
                          {!acct.studioTrialUsed&&(
                            <div style={{display:"inline-flex",alignItems:"center",gap:5,background:"var(--tl)",color:"var(--gm)",fontSize:10,fontWeight:700,letterSpacing:".04em",textTransform:"uppercase",padding:"3px 8px",borderRadius:99,marginBottom:8}}>
                              <span style={{display:"inline-flex"}}>{I.check}</span> 14-day free trial · No charge today
                            </div>
                          )}
                          <div style={{fontSize:11,color:"var(--mu)",marginBottom:8,lineHeight:1.5}}>
                            {acct.studioTrialUsed
                              ?`A 3-person studio with 2 contractors pays ${userSymbol}${localAmount(exampleMonthly)}/mo.`
                              :`A 3-person studio with 2 contractors pays ${userSymbol}${localAmount(exampleMonthly)}/mo after the trial.`}
                          </div>
                        </>
                      )}
                      {PLAN_CUTS[p.id]&&<div style={{fontSize:10,color:isCurrent?"rgba(255,255,255,.45)":"var(--mu)",marginBottom:10,fontStyle:"italic"}}>{PLAN_CUTS[p.id]}</div>}

                      <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
                        {PLAN_FEATURES[p.id].map((f,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"flex-start",gap:6,fontSize:12,color:isCurrent?"rgba(255,255,255,.85)":"var(--ink)",lineHeight:1.5}}>
                            <span style={{color:isCurrent?"var(--t)":"var(--gm)",display:"flex",flexShrink:0,marginTop:2}}>{I.check}</span>{f}
                          </div>
                        ))}
                      </div>

                      {/* Studio calculator — visible only when previewing the Studio plan as a non-current option */}
                      {isStudio&&!isCurrent&&(
                        <div style={{borderTop:"1px solid var(--bo)",paddingTop:12,marginBottom:12}}>
                          {/* Monthly / Annual toggle */}
                          <div style={{display:"flex",gap:6,marginBottom:10,background:"var(--gl)",padding:3,borderRadius:10}}>
                            {[{id:"monthly",label:"Monthly"},{id:"annual",label:"Annual · save 17%"}].map(opt=>{
                              const on=studioCadence===opt.id;
                              return(
                                <button key={opt.id} onClick={()=>setStudioCadence(opt.id)} style={{flex:1,background:on?"var(--card)":"transparent",color:on?"var(--ink)":"var(--mu)",border:"none",borderRadius:8,padding:"7px 8px",fontSize:11,fontWeight:on?700:500,cursor:"pointer",fontFamily:"'Inter',sans-serif",boxShadow:on?"0 1px 3px rgba(0,0,0,.08)":"none"}}>
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>

                          {/* Calculator open/close trigger */}
                          <button onClick={()=>setStudioCalcOpen(!studioCalcOpen)} style={{width:"100%",background:"transparent",border:"none",color:"var(--gm)",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",padding:"4px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                            {studioCalcOpen?"Hide calculator":"Estimate your price"}
                            <span style={{display:"inline-flex",transform:studioCalcOpen?"rotate(180deg)":"rotate(0)",transition:"transform .2s"}}>{I.arrow}</span>
                          </button>

                          {studioCalcOpen&&(
                            <div style={{background:"var(--gl)",borderRadius:10,padding:12,marginTop:8,display:"flex",flexDirection:"column",gap:10}}>
                              {/* Full seats stepper */}
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Full seats</div>
                                  <div style={{fontSize:10,color:"var(--mu)",lineHeight:1.4}}>2 included · {userSymbol}{localAmount(PRICING.studio.perSeat)}/mo each after</div>
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                  <button onClick={()=>setStudioCalcSeats(Math.max(PRICING.studio.seatsIncluded,studioCalcSeats-1))} disabled={studioCalcSeats<=PRICING.studio.seatsIncluded} style={{width:28,height:28,borderRadius:8,border:"1px solid var(--bo)",background:"var(--card)",color:studioCalcSeats<=PRICING.studio.seatsIncluded?"var(--bo)":"var(--ink)",fontSize:14,fontWeight:700,cursor:studioCalcSeats<=PRICING.studio.seatsIncluded?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif",lineHeight:1}}>−</button>
                                  <div style={{minWidth:24,textAlign:"center",fontSize:13,fontWeight:700,color:"var(--ink)"}}>{calcSeats}</div>
                                  <button onClick={()=>setStudioCalcSeats(Math.min(20,studioCalcSeats+1))} style={{width:28,height:28,borderRadius:8,border:"1px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",lineHeight:1}}>+</button>
                                </div>
                              </div>

                              {/* Guests stepper */}
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Guests <span style={{fontWeight:400,color:"var(--mu)"}}>(project-scoped contractors)</span></div>
                                  <div style={{fontSize:10,color:"var(--mu)",lineHeight:1.4}}>5 included · {userSymbol}{localAmount(PRICING.studio.perGuest)}/mo each after</div>
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                  <button onClick={()=>setStudioCalcGuests(Math.max(0,studioCalcGuests-1))} disabled={studioCalcGuests<=0} style={{width:28,height:28,borderRadius:8,border:"1px solid var(--bo)",background:"var(--card)",color:studioCalcGuests<=0?"var(--bo)":"var(--ink)",fontSize:14,fontWeight:700,cursor:studioCalcGuests<=0?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif",lineHeight:1}}>−</button>
                                  <div style={{minWidth:24,textAlign:"center",fontSize:13,fontWeight:700,color:"var(--ink)"}}>{calcGuests}</div>
                                  <button onClick={()=>setStudioCalcGuests(studioCalcGuests+1)} style={{width:28,height:28,borderRadius:8,border:"1px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",lineHeight:1}}>+</button>
                                </div>
                              </div>

                              {/* Soft-cap warning at PRICING.studio.softCapGuests */}
                              {studioCalcGuests>=PRICING.studio.softCapGuests&&(
                                <div style={{fontSize:10,color:"var(--warn-tx)",background:"var(--warn-bg)",border:"1px solid var(--warn-bo)",borderRadius:8,padding:"6px 8px",lineHeight:1.4}}>
                                  Heavy guest usage — we'll reach out about your scaling needs.
                                </div>
                              )}

                              {/* Live total */}
                              <div style={{borderTop:"1px solid var(--bo)",paddingTop:10,display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
                                <div style={{fontSize:11,color:"var(--mu)"}}>Your monthly cost</div>
                                <div style={{fontSize:18,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>
                                  {userSymbol}{localAmount(calcShownTotal)}<span style={{fontSize:11,fontWeight:500,color:"var(--mu)"}}>{cadenceUnit}</span>
                                </div>
                              </div>
                              {studioCadence==="annual"&&(
                                <div style={{fontSize:10,color:"var(--mu)",lineHeight:1.5,marginTop:-4}}>
                                  17% off the {userSymbol}{localAmount(PRICING.studio.base)}/mo base. Additional seats and guests billed monthly at full price.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Render order matters: reactivation beats "Current plan" so the
                          cancelled plan's card shows a clickable button (not just a label)
                          during the grace period — without this, a Pro user mid-cancel sees
                          "Current plan" with no way to undo. */}
                      {isReactivateOption?(
                        <button onClick={()=>setUpgradeTarget(p.id)} style={{width:"100%",background:"var(--ok-tx)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          Re-upgrade for free
                        </button>
                      ):isCurrent?(
                        <div style={{fontSize:11,color:"rgba(255,255,255,.5)",fontWeight:600,textAlign:"center"}}>Current plan</div>
                      ):isScheduledNext?(
                        <div style={{fontSize:11,color:"var(--mu)",fontWeight:600,textAlign:"center",padding:"10px",background:"var(--gl)",borderRadius:10}}>{p.name} starts {fmtDate(expiryDate)}</div>
                      ):(
                        <button onClick={()=>{
                          // Downgrade-to-free: check client count before showing confirmation
                          if(isDowngrade&&p.id==="free"){
                            const totalClients=(acct.clients||[]).filter(c=>!c.archived).length;
                            if(totalClients>3){
                              setDowngradeBlocked(true);
                              setTimeout(()=>{
                                document.getElementById("downgrade-blocked-msg")?.scrollIntoView({behavior:"smooth",block:"center"});
                              },50);
                              setTimeout(()=>setDowngradeBlocked(false),7000);
                              return;
                            }
                          }
                          setUpgradeTarget(p.id);
                        }} style={{width:"100%",background:isReactivateOption?"var(--ok-tx)":isDowngrade?"var(--gl)":"var(--t)",color:isReactivateOption?"#fff":isDowngrade?"var(--g)":"var(--tc)",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          {isReactivateOption?"Re-upgrade for free":isDowngrade?"Downgrade to "+p.name:isStudio?(acct.studioTrialUsed?"Upgrade to Studio":"Start 14-day free trial"):"Upgrade to "+p.name}
                        </button>
                      )}
                    </div>
                  );
                })}
              </SettingsSection>



              {/* Phase 11 — Stripe customer portal: card updates, invoice
                  downloads, and cancellation all via Stripe's hosted UI. Only
                  shown when the user actually has a Stripe subscription.
                  NOTE: this IIFE has a local `effectivePlan` string (line ~5249)
                  that shadows the global helper function, so calling
                  `effectivePlan(acct)` here would throw "string is not a
                  function" → blank-screen crash on the Billing page. Use the
                  local string directly. */}
              {effectivePlan!=="free"&&canEditWorkspaceBranding(acct)&&(
                <SettingsSection title="Manage subscription">
                  <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:14,padding:14,display:"flex",alignItems:"center",gap:12}}>
                    <span style={{display:"inline-flex",color:"var(--mu)",flexShrink:0}}>{I.creditcard}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>Card, invoices &amp; cancellation</div>
                      <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5}}>Update your payment method, download past invoices, or cancel — all via Stripe's secure portal.</div>
                    </div>
                    <button onClick={async()=>{
                      const {url,error}=await studioOpenCustomerPortal();
                      if(error){ alert(error.message||"Could not open portal"); return; }
                      window.location.href=url;
                    }} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"10px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",flexShrink:0}}>Open portal</button>
                  </div>
                </SettingsSection>
              )}

              <SettingsSection title="Invoices">
                {billingInvoices.length>0?billingInvoices.map((inv,i)=>{
                  // Distinguish the free-trial row visually so users don't read
                  // £0.00 with a green "Paid" pill and assume the charge fell
                  // through. Teal-on-card for trial; green-on-mint for paid.
                  const isTrial=inv.status==="Free trial";
                  return (
                    <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px",background:"var(--card)",borderRadius:10,border:"1px solid var(--bo)",marginBottom:6}}>
                      <div><div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{inv.desc}</div><div style={{fontSize:11,color:"var(--mu)"}}>{inv.date}</div></div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:13,fontWeight:600,color:isTrial?"var(--mu)":"var(--gm)"}}>{inv.amt}</span>
                        <span style={{fontSize:10,fontWeight:600,color:isTrial?"var(--t)":"var(--gm)",background:isTrial?"var(--tl)":"var(--gl)",padding:"2px 8px",borderRadius:99,whiteSpace:"nowrap"}}>{inv.status}</span>
                      </div>
                    </div>
                  );
                }):(
                  <div style={{fontSize:13,color:"var(--mu)",padding:"8px 0"}}>
                    {currentPlan==="free"?"No invoices on the Free plan. Upgrade to start your subscription.":"No invoices yet. Your first charge will be on your next billing date."}
                  </div>
                )}
              </SettingsSection>

              {/* ── Stripe Connect — Receive Payments ── */}
              <SettingsSection title="Receive Payments (Stripe Connect)">
                {(()=>{
                  // State and mount-effect lifted to top of SettingsSheet (Rules of Hooks).
                  const handleConnect=async()=>{
                    setConnectLoading(true);setConnectError(null);
                    try{
                      const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
                      const returnUrl=window.location.origin+"/?stripe_connect=return";
                      const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-connect-onboard`,{
                        method:"POST",
                        headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
                        body:JSON.stringify({action:"onboard",return_url:returnUrl,refresh_url:returnUrl}),
                      });
                      const data=await res.json();
                      if(data.url) window.location.href=data.url;
                      else throw new Error(data.error||"Failed to start onboarding");
                    }catch(e){setConnectError(e.message);}finally{setConnectLoading(false);}
                  };

                  const isConnected=connectStatus?.chargesEnabled&&connectStatus?.payoutsEnabled;
                  const isPending=connectStatus?.detailsSubmitted&&!isConnected;

                  return <>
                    <div style={{padding:"14px 0",fontSize:13,color:"var(--mu)",lineHeight:1.6}}>
                      Connect your Stripe account to receive payments directly from clients through the portal. Lancer uses Stripe Connect Express for secure, compliant payouts.
                    </div>

                    {isConnected?(
                      <div style={{background:"var(--ok-bg,#F0FFF4)",border:"1px solid var(--ok-tx,#38A169)",borderRadius:12,padding:16,display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:"var(--ok-tx,#38A169)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        </div>
                        <div>
                          <div style={{fontSize:14,fontWeight:600,color:"var(--ok-tx,#38A169)"}}>Stripe Connected</div>
                          <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>Payments enabled · Payouts enabled · Account {connectStatus.accountId?.slice(-6)}</div>
                        </div>
                      </div>
                    ):isPending?(
                      <div style={{background:"var(--warn-bg,#FFFBEB)",border:"1px solid var(--warn-bo,#F6AD55)",borderRadius:12,padding:16}}>
                        <div style={{fontSize:14,fontWeight:600,color:"var(--warn-tx,#C05621)",marginBottom:6}}>Onboarding In Progress</div>
                        <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5,marginBottom:12}}>Your Stripe account details have been submitted and are under review. This usually takes a few minutes.</div>
                        <button onClick={handleConnect} disabled={connectLoading} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:10,padding:"10px 20px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",opacity:connectLoading?.6:1}}>
                          {connectLoading?"Loading...":"Update Details"}
                        </button>
                      </div>
                    ):(
                      <div>
                        <button onClick={handleConnect} disabled={connectLoading} style={{background:"#635BFF",color:"#fff",border:"none",borderRadius:12,padding:"14px 24px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:10,opacity:connectLoading?.6:1}}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H3"/><path d="M21 4v12a2 2 0 01-2 2H5a2 2 0 01-2-2V4"/><path d="M3 10h18"/><path d="M7 15h.01"/><path d="M11 15h2"/></svg>
                          {connectLoading?"Connecting...":"Connect with Stripe"}
                        </button>
                        {connectError&&<div style={{fontSize:12,color:"var(--err-tx,#C53030)",marginTop:8}}>{connectError}</div>}
                      </div>
                    )}
                  </>;
                })()}
              </SettingsSection>

              {/* Payout Schedule */}
              {acct.stripeConnect?.chargesEnabled&&(
                <SettingsSection title="Payout Schedule">
                  <div style={{padding:"14px 0",fontSize:13,color:"var(--mu)",lineHeight:1.6}}>
                    Control how long payments are held before being paid out to your bank. A longer hold gives you a risk-free refund window — if a refund is needed, it comes from the held funds instead of debiting your bank.
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {[{v:7,l:"7 days"},{v:14,l:"14 days"},{v:30,l:"30 days"}].map(opt=>{
                      const active=(acct.payoutHoldDays??7)===(opt.v);
                      return(<button key={opt.v} onClick={async()=>{
                        if(active) return;
                        if(onAcctUpdate) onAcctUpdate({payoutHoldDays:opt.v});
                        try{
                          const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
                          await fetch(`${SUPABASE_URL}/functions/v1/stripe-connect-onboard`,{
                            method:"POST",
                            headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
                            body:JSON.stringify({action:"update_payout_schedule",delay_days:opt.v}),
                          });
                        }catch(e){console.error("Payout schedule update failed:",e);}
                      }} style={{flex:1,minWidth:80,padding:"12px 8px",borderRadius:10,border:"1.5px solid "+(active?"var(--g)":"var(--bo)"),background:active?"var(--gl)":"var(--card)",color:active?"var(--g)":"var(--ink)",fontSize:13,fontWeight:active?700:500,cursor:active?"default":"pointer",fontFamily:"'Inter',sans-serif"}}>{opt.l}</button>);
                    })}
                  </div>
                  <div style={{fontSize:11,color:"var(--mu)",marginTop:8}}>
                    {`Payments are held for ${acct.payoutHoldDays??7} days before being paid out to your bank. Refunds within this window won't debit your bank account.`}
                  </div>
                </SettingsSection>
              )}

              {/* Upgrade confirmation modal */}
              {upgradeTarget&&(()=>{
                const isReactivation=inGracePeriod&&upgradeTarget===cancelledPlan;
                const isUpgrade=!isReactivation&&["pro","studio"].indexOf(upgradeTarget)>["pro","studio"].indexOf(effectivePlan);
                const isDowngrade=!isReactivation&&!isUpgrade;
                return(
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
                  <div style={{background:"var(--cr)",borderRadius:20,padding:28,width:"100%",maxWidth:360}}>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:"var(--ink)",marginBottom:8}}>
                      {isReactivation?"Reactivate":isUpgrade?(upgradeTarget==="studio"?(acct.studioTrialUsed?"Upgrade to Studio":"Start your Studio trial"):"Upgrade"):"Downgrade"} {upgradeTarget!=="studio"||!isUpgrade?"to "+(upgradeTarget.charAt(0).toUpperCase()+upgradeTarget.slice(1)):""}?
                    </div>
                    <div style={{fontSize:13,color:"var(--mu)",lineHeight:1.6,marginBottom:16}}>
                      {isReactivation
                        ?`Your ${cancelledPlan.charAt(0).toUpperCase()+cancelledPlan.slice(1)} plan is still active until ${fmtDate(expiryDate)}. Reactivating is free \u2014 you've already paid for this period.`
                        :isUpgrade
                        ?(upgradeTarget==="studio"
                          ?(()=>{
                            const seatStr=`${calcSeats} seat${calcSeats===1?"":"s"}${calcGuests>0?` + ${calcGuests} guest${calcGuests===1?"":"s"}`:""}`;
                            // Trial-used accounts: skip "no charge today" copy and tell
                            // them they'll be billed immediately. The server-side
                            // stripe-checkout will not grant a second trial.
                            if(acct.studioTrialUsed){
                              return `Your card will be charged ${userSymbol}${localAmount(calcShownTotal)}${cadenceUnit} immediately for ${seatStr}, billed ${studioCadence}. Studio features are available the moment payment goes through.`;
                            }
                            const trialEnd=new Date(); trialEnd.setDate(trialEnd.getDate()+14);
                            const trialEndISO=`${trialEnd.getFullYear()}-${String(trialEnd.getMonth()+1).padStart(2,"0")}-${String(trialEnd.getDate()).padStart(2,"0")}`;
                            return `You won't be charged today. After your free trial ends on ${fmtDate(trialEndISO)}, your card will be charged ${userSymbol}${localAmount(calcShownTotal)}${cadenceUnit} for ${seatStr}, billed ${studioCadence}. Cancel anytime before then with no charge.`;
                          })()
                          :`Your card will be charged ${PLAN_PRICES[upgradeTarget].replace("/mo","")} immediately today (${fmtDate(todayISO)}). You'll be billed on this date every month from then on. Features are available the moment payment goes through.`)
                        :(()=>{
                          // For paid downgrades (e.g. Studio → Pro) and free downgrades,
                          // figure out when the user's current period actually ends. If
                          // they're currently in a Studio trial, "until your next billing
                          // date" is misleading — the trial ends in days, not a month.
                          // Use workspaceTrialEndsAt when trialing; otherwise nextBillingDate.
                          const trialEndsRaw=acct?.workspaceTrialEndsAt;
                          const trialEndDate=trialEndsRaw?new Date(trialEndsRaw):null;
                          const trialing=!!(trialEndDate&&trialEndDate>new Date()&&effectivePlan==="studio");
                          const trialEndISOLocal=trialEndDate?`${trialEndDate.getFullYear()}-${String(trialEndDate.getMonth()+1).padStart(2,"0")}-${String(trialEndDate.getDate()).padStart(2,"0")}`:null;
                          const periodEndLabel=trialing&&trialEndISOLocal?`your trial ends on ${fmtDate(trialEndISOLocal)}`:nextBillingDate?`your next billing date (${nextBillingDate})`:"your next billing date";
                          if(upgradeTarget!=="free"){
                            return `Your ${effectivePlan.charAt(0).toUpperCase()+effectivePlan.slice(1)} features will remain active until ${periodEndLabel}. After that, your ${upgradeTarget.charAt(0).toUpperCase()+upgradeTarget.slice(1)} plan starts automatically at ${PLAN_PRICES[upgradeTarget]} on the same card.`;
                          }
                          return `Your ${effectivePlan.charAt(0).toUpperCase()+effectivePlan.slice(1)} features will remain active until ${periodEndLabel}. No further charges will be made after that.`;
                        })()
                      }
                    </div>
                    <div style={{background:isReactivation?"var(--ok-bg)":"var(--gl)",borderRadius:12,padding:14,marginBottom:20}}>
                      <div style={{fontSize:13,fontWeight:600,color:isReactivation?"var(--ok-tx)":"var(--gm)",marginBottom:4}}>{upgradeTarget.charAt(0).toUpperCase()+upgradeTarget.slice(1)} Plan</div>
                      <div style={{fontSize:12,color:isReactivation?"var(--ok-tx)":"var(--gm)"}}>{isReactivation?"Free \u2014 already paid":upgradeTarget==="studio"?`${userSymbol}${localAmount(calcShownTotal)}${cadenceUnit} \u00b7 ${calcSeats} seat${calcSeats===1?"":"s"}${calcGuests>0?` + ${calcGuests} guest${calcGuests===1?"":"s"}`:""}`:PLAN_PRICES[upgradeTarget]}</div>
                      <div style={{fontSize:11,color:"var(--mu)",marginTop:6}}>{PLAN_FEATURES[upgradeTarget].join(" \u00B7 ")}</div>
                    </div>
                    <div style={{display:"flex",gap:10}}>
                      <button onClick={()=>setUpgradeTarget(null)} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"13px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                      <button onClick={()=>confirmUpgrade(upgradeTarget)} style={{flex:2,background:isReactivation?"var(--ok-tx)":"var(--t)",color:"#fff",border:"none",borderRadius:12,padding:"13px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        {isReactivation?"Reactivate for Free":isUpgrade?(upgradeTarget==="studio"?(acct.studioTrialUsed?"Confirm Upgrade":"Start free trial"):"Confirm Upgrade"):"Confirm Downgrade"}
                      </button>
                    </div>
                  </div>
                </div>);
              })()}
            </>
          );
        })()}

        {/* ── CONTACT & HELP ── */}
        {which==="contact"&&<ContactHelp acct={acct}/>}

        {/* ── ABOUT ── */}
        {which==="about"&&<AboutPage acct={acct}/>}

        {/* ── METRIX ── */}
        {which==="metrix"&&(()=>{
          // metrixTab state lifted to top of SettingsSheet (Rules of Hooks).
          const sym=acct.currencySymbol||"$";
          const clients=acct.clients||[];
          const now=new Date();
          const selY=metrixYear;

          // Collect all years that have data
          const dataYears=new Set([now.getFullYear()]);
          clients.forEach(c=>{
            if(c.depositPaidDate){const y=parseInt(c.depositPaidDate.split("-")[0]);if(y)dataYears.add(y);}
            if(c.balancePaidDate){const y=parseInt(c.balancePaidDate.split("-")[0]);if(y)dataYears.add(y);}
            (c.invoices||[]).forEach(inv=>{const d=new Date(inv.paidAt||inv.createdAt||"");if(!isNaN(d))dataYears.add(d.getFullYear());});
            (c.refunds||[]).forEach(r=>{const d=new Date(r.date||"");if(!isNaN(d))dataYears.add(d.getFullYear());});
          });
          const yearList=[...dataYears].sort((a,b)=>b-a);

          // All 12 months of selected year
          const months=[];
          for(let i=0;i<12;i++){
            months.push({m:i+1,y:selY,label:MONTHS[i].slice(0,3).toUpperCase()});
          }

          const monthData=months.map(({m,y,label})=>{
            let income=0,refunds=0;
            clients.forEach(c=>{
              const raw=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
              const dep=c.depositAmt||round2(raw*(c.depositPct||50)/100);
              const bal=c.balanceAmt||round2(raw*(100-(c.depositPct||50))/100);
              if(c.depositPaid&&c.depositPaidDate){const[cy,cm]=c.depositPaidDate.split("-");if(parseInt(cm)===m&&parseInt(cy)===y) income+=dep;}
              if(c.balancePaid&&c.balancePaidDate){const[cy,cm]=c.balancePaidDate.split("-");if(parseInt(cm)===m&&parseInt(cy)===y) income+=bal;}
              (c.invoices||[]).filter(inv=>inv.paid).forEach(inv=>{
                const d=new Date(inv.paidAt||inv.createdAt||"");
                if(!isNaN(d)&&d.getMonth()+1===m&&d.getFullYear()===y) income+=Number(inv.amount)||0;
              });
              (c.refunds||[]).forEach(r=>{
                const d=new Date(r.date||"");
                if(d.getMonth()+1===m&&d.getFullYear()===y) refunds+=r.amount;
              });
            });
            return {label,income,refunds,net:income-refunds};
          });

          const totalIncome=monthData.reduce((s,m)=>s+m.income,0);
          const totalRefunds=monthData.reduce((s,m)=>s+m.refunds,0);
          const netRevenue=totalIncome-totalRefunds;
          const maxVal=Math.max(...monthData.map(m=>m.income+m.refunds),1);

          // Top earners (filtered by selected year)
          const topClients=[...clients]
            .map(c=>{
              let earned=0;
              if(c.depositPaid&&c.depositPaidDate&&parseInt(c.depositPaidDate.split("-")[0])===selY) earned+=(c.depositAmt||0);
              if(c.balancePaid&&c.balancePaidDate&&parseInt(c.balancePaidDate.split("-")[0])===selY) earned+=(c.balanceAmt||0);
              (c.invoices||[]).filter(inv=>inv.paid).forEach(inv=>{const d=new Date(inv.paidAt||inv.createdAt||"");if(!isNaN(d)&&d.getFullYear()===selY) earned+=Number(inv.amount)||0;});
              return {...c,earned};
            })
            .filter(c=>c.earned>0)
            .sort((a,b)=>b.earned-a.earned)
            .slice(0,5);

          // Refund & invoice lists (filtered by selected year)
          const allRefunds=clients.flatMap(c=>(c.refunds||[]).map(r=>({...r,clientName:c.name,clientCol:c.col}))).filter(r=>{const d=new Date(r.date||"");return !isNaN(d)&&d.getFullYear()===selY;});
          const allInvoices=clients.flatMap(c=>(c.invoices||[]).filter(inv=>inv.paid).map(inv=>({...inv,clientName:c.name,clientCol:c.col}))).filter(inv=>{const d=new Date(inv.paidAt||inv.createdAt||"");return !isNaN(d)&&d.getFullYear()===selY;});

          return (
            <>
              {/* Metrix sub-tabs.
                  Phase 7: Studio analytics tab is fully visible to Studio owners.
                  Phase 10: Pro/Free owners see the chip too — clicking shows a
                  JIT upgrade card (visible discovery, not gated invisibility). */}
              {(()=>{
                const isStudioOwner = effectivePlan(acct)==="studio" && canEditWorkspaceBranding(acct);
                const showStudioChip = canEditWorkspaceBranding(acct); // owners only — never to members or guests
                return (
                  <div className="chips" style={{marginTop:0}}>
                    <div className={`chip${metrixTab==="analytics"?" on":""}`} onClick={()=>setMetrixTab("analytics")}>Analytics</div>
                    {showStudioChip&&<div className={`chip${metrixTab==="studio"?" on":""}`} onClick={()=>setMetrixTab("studio")}>{isStudioOwner?"Studio":"Studio 🔒"}</div>}
                    <div className={`chip${metrixTab==="invoices"?" on":""}`} onClick={()=>setMetrixTab("invoices")}>Invoices</div>
                    <div className={`chip${metrixTab==="payouts"?" on":""}`} onClick={()=>setMetrixTab("payouts")}>Payouts</div>
                  </div>
                );
              })()}

              {metrixTab==="invoices"&&<InvoicesTab acct={acct} onOpen={(id)=>{onClose();if(onOpen) onOpen(id);}} initialFilter={initialInvoiceFilter}/>}
              {metrixTab==="payouts"&&<PayoutsTab acct={acct} onOpen={(id)=>{onClose();if(onOpen) onOpen(id);}}/>}
              {metrixTab==="studio"&&(effectivePlan(acct)==="studio"
                ?<StudioAnalyticsTab acct={acct} onOpen={(id)=>{onClose();if(onOpen) onOpen(id);}}/>
                :<div style={{padding:"14px 0"}}>
                  <StudioUpgradeCard
                    acct={acct}
                    title="Per-seat analytics is a Studio feature"
                    body="See revenue closed and projects completed by each teammate. Plus service-line profitability and pipeline conversion — all impossible without multi-seat data, so they only exist on Studio."
                    onUpgrade={()=>{ if(onSwitchSection) onSwitchSection("billing"); }}
                  />
                </div>
              )}

              {metrixTab==="analytics"&&<>
              {/* KPI Cards */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:20}}>
                {[
                  {label:"Total Income",val:`${sym}${fmtMoney(totalIncome)}`,col:"var(--g)",bg:"var(--gl)"},
                  {label:"Total Refunds",val:`${sym}${fmtMoney(totalRefunds)}`,col:"var(--err-tx)",bg:"var(--err-bg)"},
                  {label:"Net Revenue",val:`${sym}${fmtMoney(netRevenue)}`,col:netRevenue>=0?"var(--g)":"var(--err-tx)",bg:netRevenue>=0?"var(--gl)":"var(--err-bg)"},
                ].map((k,i)=>(
                  <div key={i} style={{background:k.bg,borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
                    <div style={{fontSize:11,color:"var(--mu)",marginBottom:4,textTransform:"uppercase",letterSpacing:".06em"}}>{k.label}</div>
                    <div className="num" style={{fontSize:16,fontWeight:700,color:k.col}}>{k.val}</div>
                  </div>
                ))}
              </div>

              {/* Year filter pills */}
              <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
                {yearList.map(yr=>(
                  <button key={yr} onClick={()=>setMetrixYear(yr)} style={{padding:"6px 16px",borderRadius:20,border:yr===selY?"2px solid var(--g)":"1.5px solid var(--bo)",background:yr===selY?"var(--gl)":"var(--card)",color:yr===selY?"var(--g)":"var(--mu)",fontSize:13,fontWeight:yr===selY?700:500,cursor:"pointer"}}>{yr}</button>
                ))}
              </div>

              {/* Income vs Refunds bar chart */}
              <SettingsSection title={`Income vs Refunds — ${selY}`}>
                <div style={{display:"flex",alignItems:"flex-end",gap:6,height:120,marginBottom:8}}>
                  {monthData.map((m,i)=>(
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                      <div style={{width:"100%",display:"flex",flexDirection:"column",justifyContent:"flex-end",gap:2,height:100}}>
                        {m.income>0&&<div style={{width:"100%",height:Math.max(4,Math.round((m.income/maxVal)*90)),background:"var(--g)",borderRadius:"4px 4px 0 0",transition:"height .3s"}}/>}
                        {m.refunds>0&&<div style={{width:"100%",height:Math.max(4,Math.round((m.refunds/maxVal)*90)),background:"#FCA5A5",borderRadius:"0 0 4px 4px",transition:"height .3s"}}/>}
                        {m.income===0&&m.refunds===0&&<div style={{width:"100%",height:4,background:"var(--bo)",borderRadius:4}}/>}
                      </div>
                      <div style={{fontSize:9,color:"var(--mu)",fontWeight:600}}>{m.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:16,justifyContent:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--mu)"}}>
                    <div style={{width:10,height:10,borderRadius:2,background:"var(--g)"}}/>Income
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--mu)"}}>
                    <div style={{width:10,height:10,borderRadius:2,background:"#FCA5A5"}}/>Refunds
                  </div>
                </div>
              </SettingsSection>

              {/* Monthly breakdown table */}
              <SettingsSection title="Monthly Breakdown">
                <div style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",overflow:"hidden"}}>
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",padding:"8px 14px",background:"var(--cr)",borderBottom:"1px solid var(--bo)"}}>
                    {["Month","Income","Refunds","Net"].map(h=><div key={h} style={{fontSize:10,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em"}}>{h}</div>)}
                  </div>
                  {monthData.map((m,i)=>(
                    <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",padding:"10px 14px",borderBottom:i<monthData.length-1?"1px solid var(--bo)":"none"}}>
                      <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{m.label}</div>
                      <div className="num" style={{fontSize:13,color:"var(--gm)",fontWeight:m.income>0?600:400}}>{m.income>0?`${sym}${fmtMoney(m.income)}`:"—"}</div>
                      <div className="num" style={{fontSize:13,color:m.refunds>0?"var(--err-tx)":"var(--mu)"}}>{m.refunds>0?`${sym}${fmtMoney(m.refunds)}`:"—"}</div>
                      <div className="num" style={{fontSize:13,fontWeight:600,color:m.net>=0?"var(--g)":"var(--err-tx)"}}>{m.net!==0?`${sym}${fmtMoney(m.net)}`:"—"}</div>
                    </div>
                  ))}
                </div>
              </SettingsSection>

              {/* Top clients */}
              {topClients.length>0&&(
                <SettingsSection title="Top Earning Clients">
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {topClients.map((c,i)=>(
                      <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",padding:"10px 14px"}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:c.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>{c.portalPhoto?<img src={c.portalPhoto} alt={c.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:c.ini}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{c.name}</div>
                          <div style={{fontSize:11,color:"var(--mu)"}}>{c.type?.split("·")[0]?.trim()}</div>
                        </div>
                        <div className="num" style={{fontSize:14,fontWeight:700,color:"var(--gm)"}}>{sym}{fmtMoney(c.earned)}</div>
                      </div>
                    ))}
                  </div>
                </SettingsSection>
              )}

              {/* Refund log */}
              <SettingsSection title="Refund Log">
                {allRefunds.length===0?(
                  <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"12px 14px",fontSize:12,color:"var(--mu)",textAlign:"center"}}>No refunds issued yet.</div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {allRefunds.map((r,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:12,background:"var(--err-bg)",borderRadius:12,border:"1.5px solid var(--err-bo)",padding:"10px 14px"}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:r.clientCol||"#DC2626",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>↩</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--err-tx)"}}>{r.reason}</div>
                          <div style={{fontSize:11,color:"var(--mu)"}}>{r.clientName} · {r.date}</div>
                        </div>
                        <div className="num" style={{fontSize:14,fontWeight:700,color:"var(--err-tx)"}}>{sym}{fmtMoney(r.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </SettingsSection>

              {/* Invoice log */}
              {allInvoices.length>0&&(
                <SettingsSection title="Additional Invoice Income">
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {allInvoices.map((inv,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:12,background:"var(--gl)",borderRadius:12,border:"1px solid var(--bo)",padding:"10px 14px"}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{inv.description}</div>
                          <div style={{fontSize:11,color:"var(--mu)"}}>{inv.clientName} · {inv.createdAt}</div>
                        </div>
                        <div className="num" style={{fontSize:14,fontWeight:700,color:"var(--gm)"}}>{sym}{fmtMoney(inv.amount)}</div>
                      </div>
                    ))}
                  </div>
                </SettingsSection>
              )}

              </>}
            </>
          );
        })()}

        {which==="templates"&&(()=>{
          /* ── Questionnaire Templates CRUD ── */
          const freelancerVerts=(acct.verts&&acct.verts.length>0?acct.verts:[acct.vert]).filter(Boolean);
          const expandedVerts=freelancerVerts.flatMap(vid=>{
            if(vid==="custom"){
              const roles=Array.isArray(acct.customRole)?acct.customRole:(acct.customRole?[acct.customRole]:[]);
              return roles.length>0?roles.map(r=>"custom:"+r):["custom"];
            }
            return [vid];
          });
          const vertLabel=getVertLabel;
          const vertIcon=getVertIcon;

          const templates=acct.questionnaireTemplates||[];
          const grouped={};
          expandedVerts.forEach(v=>{grouped[v]=[];});
          templates.forEach(t=>{
            if(!grouped[t.vert]) grouped[t.vert]=[];
            grouped[t.vert].push(t);
          });

          return (
            <>
              {!tplEditing?(
                /* ── List view ── */
                <>
                  <div style={{fontSize:13,color:"var(--mu)",marginBottom:20,lineHeight:1.5}}>
                    Create reusable questionnaire templates for each creative type. Apply them to clients from the client page.
                  </div>
                  {expandedVerts.map(vid=>{
                    const list=grouped[vid]||[];
                    return (
                      <SettingsSection key={vid} title={<span style={{display:"flex",alignItems:"center",gap:6}}><span>{vertIcon(vid)}</span> {vertLabel(vid)}</span>}>
                        {list.length===0&&(
                          <div style={{fontSize:12,color:"var(--mu)",fontStyle:"italic",marginBottom:8}}>No templates yet</div>
                        )}
                        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                          {list.map(t=>(
                            <div key={t.id} style={{background:"var(--card)",borderRadius:12,border:"1.5px solid var(--bo)",padding:"12px 14px",display:"flex",alignItems:"center",gap:12}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{t.name}</div>
                                <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{t.questions.length} question{t.questions.length!==1?"s":""}</div>
                              </div>
                              <button onClick={()=>{setTplEditing(t);setTplName(t.name);setTplVert(t.vert);setTplQuestions([...t.questions.map(q=>({...q}))]);}}
                                style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                                Edit
                              </button>
                              <button onClick={()=>{
                                const next=templates.filter(x=>x.id!==t.id);
                                onAcctUpdate({questionnaireTemplates:next});
                              }}
                                style={{background:"none",border:"none",color:"var(--mu)",cursor:"pointer",fontSize:14,padding:"4px"}}>✕</button>
                            </div>
                          ))}
                        </div>
                        <button onClick={()=>{setTplEditing("new");setTplName("");setTplVert(vid);setTplQuestions([{key:`q${Date.now()}`,label:"",placeholder:"",type:"input"}]);}}
                          style={{background:"var(--cr)",border:"1.5px dashed var(--bo)",borderRadius:12,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",color:"var(--gm)",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6,width:"100%"}}>
                          + New template
                        </button>
                      </SettingsSection>
                    );
                  })}
                </>
              ):(
                /* ── Edit / Create view ── */
                <>
                  <button onClick={()=>setTplEditing(null)}
                    style={{background:"none",border:"none",color:"var(--t)",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif",marginBottom:16,display:"flex",alignItems:"center",gap:4}}>
                    {I.arrowLeft} Back to templates
                  </button>
                  <SettingsSection title={tplEditing==="new"?"New Template":"Edit Template"}>
                    <div style={{marginBottom:14}}>
                      <label style={{fontSize:11,fontWeight:600,color:"var(--mu)",display:"block",marginBottom:6}}>Template name</label>
                      <input value={tplName} onChange={e=>setTplName(e.target.value)} placeholder="e.g. Wedding Photography Intake"
                        style={{...inp}}/>
                    </div>
                    <div style={{marginBottom:14}}>
                      <label style={{fontSize:11,fontWeight:600,color:"var(--mu)",display:"block",marginBottom:6}}>Creative type</label>
                      <select value={tplVert} onChange={e=>setTplVert(e.target.value)}
                        style={{...inp,cursor:"pointer"}}>
                        {expandedVerts.map(vid=>(
                          <option key={vid} value={vid}>{vertLabel(vid)}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{marginBottom:14}}>
                      <label style={{fontSize:11,fontWeight:600,color:"var(--mu)",display:"block",marginBottom:10}}>Questions</label>
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {tplQuestions.map((q,i)=>(
                          <div key={q.key} style={{background:"var(--card)",borderRadius:12,border:"1.5px solid var(--bo)",padding:12}}>
                            <div style={{display:"flex",gap:8,marginBottom:8}}>
                              <input value={q.label} onChange={e=>{const v=e.target.value;setTplQuestions(d=>d.map((x,j)=>j===i?{...x,label:v}:x));}}
                                placeholder="Question label e.g. Location or setting"
                                style={{flex:1,border:"1px solid var(--bo)",borderRadius:8,padding:"7px 10px",fontSize:12,fontFamily:"'Inter',sans-serif",color:"var(--ink)",background:"var(--input-bg)",outline:"none"}}/>
                              <select value={q.type} onChange={e=>{const v=e.target.value;setTplQuestions(d=>d.map((x,j)=>j===i?{...x,type:v}:x));}}
                                style={{border:"1px solid var(--bo)",borderRadius:8,padding:"7px 8px",fontSize:11,fontFamily:"'Inter',sans-serif",color:"var(--mu)",outline:"none",background:"var(--card)"}}>
                                <option value="input">Short</option>
                                <option value="textarea">Long</option>
                              </select>
                              <button onClick={()=>setTplQuestions(d=>d.filter((_,j)=>j!==i))}
                                style={{background:"none",border:"none",color:"var(--bo)",cursor:"pointer",fontSize:16,padding:"0 4px"}}>✕</button>
                            </div>
                            <input value={q.placeholder} onChange={e=>{const v=e.target.value;setTplQuestions(d=>d.map((x,j)=>j===i?{...x,placeholder:v}:x));}}
                              placeholder="Placeholder hint text for client"
                              style={{width:"100%",border:"1px solid var(--bo)",borderRadius:8,padding:"7px 10px",fontSize:11,fontFamily:"'Inter',sans-serif",color:"var(--mu)",background:"var(--input-bg)",outline:"none"}}/>
                          </div>
                        ))}
                        <button onClick={()=>setTplQuestions(d=>[...d,{key:`q${Date.now()}`,label:"",placeholder:"",type:"input"}])}
                          style={{background:"var(--cr)",border:"1.5px dashed var(--bo)",borderRadius:12,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",color:"var(--gm)",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                          + Add question
                        </button>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setTplEditing(null)}
                        style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"11px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Cancel
                      </button>
                      <button onClick={()=>{
                        const cleaned=tplQuestions.filter(q=>q.label.trim());
                        if(!tplName.trim()||cleaned.length===0) return;
                        let next=[...templates];
                        if(tplEditing==="new"){
                          next.push({id:"tpl_"+Date.now(),name:tplName.trim(),vert:tplVert,questions:cleaned});
                        } else {
                          next=next.map(t=>t.id===tplEditing.id?{...t,name:tplName.trim(),vert:tplVert,questions:cleaned}:t);
                        }
                        onAcctUpdate({questionnaireTemplates:next});
                        setTplEditing(null);
                      }}
                        disabled={!tplName.trim()||!tplQuestions.some(q=>q.label.trim())}
                        style={{flex:2,background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",opacity:(!tplName.trim()||!tplQuestions.some(q=>q.label.trim()))?0.5:1}}>
                        {tplEditing==="new"?"Create Template":"Save Changes"}
                      </button>
                    </div>
                  </SettingsSection>
                </>
              )}
            </>
          );
        })()}

      </div>
      </div>{/* inner max-width panel */}
    </div>
  );
}
/* ── RESPONSIVE HOOK ── */
function useIsDesktop(){
  const [d,setD]=useState(()=>typeof window!=="undefined"&&window.matchMedia("(min-width:1024px)").matches);
  useEffect(()=>{
    const mq=window.matchMedia("(min-width:1024px)");
    const h=(e)=>setD(e.matches);
    mq.addEventListener("change",h);
    return()=>mq.removeEventListener("change",h);
  },[]);
  return d;
}

function PullToRefresh({onRefresh, children}){
  const [pullY,setPullY]=useState(0);
  const [refreshing,setRefreshing]=useState(false);
  const [done,setDone]=useState(false);
  const startY=useRef(0);
  const pulling=useRef(false);
  const containerRef=useRef(null);
  const THRESHOLD=72;

  const onTouchStart=(e)=>{
    // Don't activate if touch starts inside a bottom sheet or modal
    // Also bail out inside any subtree that opts out of pull-to-refresh — e.g. an open chat
    // thread, where a downward drag should belong to the chat itself, not trigger a refresh.
    if(e.target.closest('.tmod,.mod,.ptr-skip')) return;
    const el=containerRef.current;
    if(!el||el.scrollTop>0) return;
    // Don't activate if touch starts inside a fixed-position overlay (settings sheet, etc.)
    let node=e.target;
    while(node&&node!==el){
      if(node.style&&node.style.position==="fixed") return;
      node=node.parentElement;
    }
    startY.current=e.touches[0].clientY;
    pulling.current=true;
  };
  const onTouchMove=(e)=>{
    if(!pulling.current||refreshing) return;
    const el=containerRef.current;
    if(!el||el.scrollTop>0){pulling.current=false;setPullY(0);return;}
    const delta=e.touches[0].clientY-startY.current;
    if(delta<=0){pulling.current=false;setPullY(0);return;}
    // Only prevent default if actually pulling down (not scrolling content)
    if(delta>8) e.preventDefault();
    setPullY(Math.min(Math.sqrt(delta)*5,THRESHOLD+20));
  };
  const onTouchEnd=async()=>{
    if(!pulling.current) return;
    pulling.current=false;
    if(pullY>=THRESHOLD){
      setRefreshing(true);
      setPullY(THRESHOLD);
      try { if(onRefresh) await onRefresh(); }
      catch(e){ console.warn("Refresh failed:",e); }
      setDone(true);
      setPullY(0);
      setTimeout(()=>{setRefreshing(false);setDone(false);},600);
    } else {
      setPullY(0);
    }
  };
  const indicatorOpacity=Math.min(pullY/THRESHOLD,1);
  const spinDeg=(pullY/THRESHOLD)*360;
  return (
    <div ref={containerRef} style={{flex:1,overflowY:"auto",overflowX:"hidden",WebkitOverflowScrolling:"touch",position:"relative",scrollbarWidth:"none"}}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:THRESHOLD,display:"flex",alignItems:"center",justifyContent:"center",transform:`translateY(${Math.min(pullY-THRESHOLD,0)}px)`,opacity:indicatorOpacity,pointerEvents:"none",zIndex:10,transition:refreshing||done?"transform .3s ease":"none"}}>
        <div style={{width:36,height:36,borderRadius:"50%",background:"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 12px rgba(43,68,56,.25)"}}>
          {refreshing&&!done
            ?<div style={{width:18,height:18,border:"2.5px solid rgba(255,255,255,.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"ptr-spin .7s linear infinite"}}/>
            :done
              ?<span style={{display:"inline-flex",alignItems:"center",color:"#fff"}}>{I.check}</span>
              :<div style={{width:18,height:18,border:"2.5px solid rgba(255,255,255,.3)",borderTopColor:"#fff",borderRadius:"50%",transform:`rotate(${spinDeg}deg)`}}/>
          }
        </div>
      </div>
      <style>{`@keyframes ptr-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{transform:refreshing||pullY>0?`translateY(${refreshing?THRESHOLD:pullY}px)`:undefined,transition:refreshing||done||pullY===0?"transform .3s ease":"none",display:"flex",flexDirection:"column",minHeight:"100%"}}>
        {children}
        <div style={{height:"max(env(safe-area-inset-bottom, 16px), 16px)"}} aria-hidden="true"/>
      </div>
    </div>
  );
}

function Home({acct,onOpen,onSignOut,onNavChange,onAcctUpdate,onDeleteAccount,planOverLimit}){
  const [menu,setMenu]=useState(false);
  const [settingsOpen,setSettingsOpen]=useState(null); // null | 'account' | 'branding' | 'billing'
  const [studioCardDismissedAt,setStudioCardDismissedAt]=useState(0); // bump to force re-eval after dismiss
  const [settingsInitial,setSettingsInitial]=useState(null); // {metrixTab?:string, invoiceFilter?:string} — forwarded when settings sheet opens
  const [selBar,setSelBar]=useState(6); // index 6 = current month (centre of 13-month window)
  const [planUpgradeMsg,setPlanUpgradeMsg]=useState(null);

  // ── Detect return from Stripe Checkout (plan upgrade) or Connect onboarding ──
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const upgraded=params.get("plan_upgraded");
    const cancelled=params.get("plan_cancelled");
    const connectReturn=params.get("stripe_connect");
    const inviteToken=params.get("invite");
    if(inviteToken){
      // Slice D — accept-invite flow. The invitee is signed in (this useEffect
      // runs in the authenticated app), so call the edge function which
      // validates email match + adds them to the workspace + syncs Stripe.
      (async()=>{
        const {data,error}=await studioAcceptInvite(inviteToken);
        if(error){
          setPlanUpgradeMsg(`Couldn't accept invite: ${error.message}`);
          setTimeout(()=>setPlanUpgradeMsg(null),8000);
        } else {
          setPlanUpgradeMsg(`Welcome to ${data?.workspace_name||"the workspace"}! Reload to see your new workspace.`);
          // Soft-reload so loadAcctFromDB picks up the new primary workspace.
          setTimeout(()=>{ window.location.href=window.location.pathname; },2000);
        }
        window.history.replaceState({},"",window.location.pathname);
      })();
      return;
    }
    if(upgraded){
      const label=upgraded.charAt(0).toUpperCase()+upgraded.slice(1);
      setPlanUpgradeMsg(`You've been upgraded to ${label}! Your new features are now active.`);
      // Clear any leftover cancel state from a previous subscription cycle —
      // otherwise a user who cancelled, hit period_end, and then re-upgraded
      // would still have stale cancelled_plan / plan_expiry_date rows.
      if(onAcctUpdate) onAcctUpdate({
        billingPlan: upgraded,
        billingStartDate: localToday(),
        cancelledPlan: null,
        planExpiryDate: null,
      });
      setTimeout(()=>setPlanUpgradeMsg(null),6000);
      window.history.replaceState({},"",window.location.pathname);
    } else if(cancelled){
      setPlanUpgradeMsg(null);
      window.history.replaceState({},"",window.location.pathname);
    } else if(connectReturn){
      setSettingsOpen("billing");
      window.history.replaceState({},"",window.location.pathname);
      // Check Stripe status immediately after return from onboarding
      (async()=>{
        try{
          const token=_accessToken||(await sb.auth.getSession().then(r=>r.data?.session?.access_token))||SUPABASE_KEY;
          const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-connect-onboard`,{
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`,"apikey":SUPABASE_KEY},
            body:JSON.stringify({action:"status"}),
          });
          const data=await res.json();
          if(data.connected&&data.charges_enabled&&onAcctUpdate){
            onAcctUpdate({stripeConnect:{accountId:data.stripe_account_id,chargesEnabled:data.charges_enabled,payoutsEnabled:data.payouts_enabled,detailsSubmitted:data.details_submitted}});
          }
        }catch(e){}
      })();
    }
  },[]);

  // Dynamic stats — drafts are incomplete WIP, not real active clients
  const allActiveClients=acct.clients.filter(c=>!c.archived&&!c._draft&&(c.status==="signed"||c.status==="inquiry"||c.status==="pending"||c.status==="active"));
  const activeClients=allActiveClients.length;
  const planLimit=acct.billingPlan==="free"?3:acct.billingPlan==="pro"?null:null;
  const overLimit=planLimit&&activeClients>planLimit;
  const actionNeeded=acct.clients.filter(c=>{
    if(c.archived||c._draft) return false;
    const hasUnpaidInvoice=(c.invoices||[]).some(i=>i&&!i.paid&&(Number(i.amount)||0)>0);
    return (c.status==="pending"&&!c.contractSentToPortal)||
    (c.contractSentToPortal&&c.status!=="signed"&&c.status!=="active"&&c.status!=="complete"&&c.status!=="delivered")||
    (c.status==="signed"&&!c.depositPaid)||
    (c.depositPaid&&!c.balancePaid&&c.pct>=80)||
    (c.cancellationRequested&&c.depositPaid&&!(c.refunds||[]).length)||
    hasUnpaidInvoice;
  }).length;
  const outstanding=acct.clients.reduce((sum,c)=>{
    if(c.archived||c._draft||c.cancellationRequested) return sum;
    // Deposit/balance only count as outstanding while the engagement is open (pre-delivery)
    if(["inquiry","signed","active","pending"].includes(c.status)){
      const raw=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
      if(raw>0){
        if(!c.depositPaid) sum+=round2(raw*(c.depositPct||50)/100);
        if(!c.balancePaid) sum+=round2(raw*(100-(c.depositPct||50))/100);
      }
    }
    // Unpaid additional invoices are always owed — even after delivery (e.g. revisions, scope bumps)
    (c.invoices||[]).forEach(inv=>{
      if(inv&&!inv.paid){
        const amt=Number(inv.amount)||0;
        if(amt>0) sum+=amt;
      }
    });
    // Subtract any refunds already issued
    const refunded=(c.refunds||[]).reduce((s,r)=>s+(parseFloat(r.amount||0)||0),0);
    sum-=refunded;
    return sum;
  },0);

  // Dynamic revenue bars
  const revBars=buildRevenue(acct.clients);
  const selMonth=revBars[selBar];
  const mtd=selMonth.rev;
  const prevRev=selBar>0?revBars[selBar-1].rev:0;
  const revDelta=prevRev>0?Math.round(((mtd-prevRev)/prevRev)*100):null;
  const revChange=revDelta!==null?(revDelta>=0?`↑ ${revDelta}% vs ${revBars[selBar-1].mo}`:`↓ ${Math.abs(revDelta)}% vs ${revBars[selBar-1].mo}`):"First month";

  const sym=acct.currencySymbol||"$";

  const openOutstanding=()=>{
    setSettingsInitial({metrixTab:"invoices",invoiceFilter:"pending"});
    setSettingsOpen("metrix");
  };
  const stats=[
    {n:overLimit?`${activeClients} (${planLimit} limit)`:String(activeClients),l:"Active",t:overLimit},
    {n:String(actionNeeded),l:"Action needed",t:actionNeeded>0},
    // Members with financial visibility off see ••• instead of the outstanding £
    {n:canSeeFinancials(acct)?`${sym}${fmtMoney(outstanding)}`:"••••",l:"Outstanding",t:canSeeFinancials(acct)&&outstanding>0,click:canSeeFinancials(acct)?openOutstanding:undefined},
  ];
  return (
    <>
      <div className="topbar" style={{position:"relative"}}>
        <div className="trow">
          <div className="logo"><img src={BANNER_B64} alt="Lancer" style={{height:36,objectFit:"contain",mixBlendMode:"screen"}}/></div>
          <div className="ava-wrap">
            <div className="ava" onClick={()=>setMenu(m=>!m)}>
              {acct.profilePic
                ?<img src={acct.profilePic} alt="" style={{width:"100%",height:"100%",borderRadius:"50%",objectFit:"cover"}}/>
                :acct.ini}
            </div>
            {menu&&<>
              <div style={{position:"fixed",inset:0,zIndex:998}} onClick={()=>setMenu(false)}/>
              <div className="umenu">
                <div className="umh"><div className="umn">{acct.name}</div><div className="ume">{acct.email}</div></div>
                {/* Workspace switcher — visible only when the user belongs to >1 workspace */}
                {(acct.workspaces||[]).length>1&&(
                  <>
                    <div style={{padding:"8px 14px 4px",fontSize:9,fontWeight:700,letterSpacing:".09em",textTransform:"uppercase",color:"var(--mu)"}}>Workspaces</div>
                    {acct.workspaces.map(w=>{
                      const isActive=w.workspace_id===acct.workspaceId;
                      const sub=w.is_owner?"Your account":(w.role==="member"?"Member":w.role==="guest"?"Guest":"");
                      return (
                        <div key={w.workspace_id} className="umi" onClick={()=>{
                          if(isActive){ setMenu(false); return; }
                          storeActiveWorkspace(w.workspace_id);
                          setMenu(false);
                          // Hard reload so every component re-fetches with the new workspace context.
                          window.location.reload();
                        }} style={{display:"flex",alignItems:"center",gap:10,paddingTop:6,paddingBottom:6}}>
                          <span style={{display:"inline-flex",width:16,color:isActive?"var(--gm)":"transparent",flexShrink:0}}>{I.check}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:isActive?700:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{w.workspace_name}</div>
                            <div style={{fontSize:10,color:"var(--mu)"}}>{sub}{w.plan&&w.plan!=="free"?` · ${w.plan.charAt(0).toUpperCase()+w.plan.slice(1)}`:""}</div>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{height:1,background:"var(--bo)",margin:"4px 0"}}/>
                  </>
                )}
                <div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("account");}}><span style={{display:"flex"}}>{I.settings}</span> Account settings</div>
                {acct.workspaceId&&acct.workspaceRole!=="guest"&&<div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("team");}}><span style={{display:"flex"}}>{I.clients}</span> Team</div>}
                {canEditWorkspaceBranding(acct)&&<div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("branding");}}><span style={{display:"flex"}}>{I.palette}</span> Branding &amp; portal</div>}
                {acct.workspaceRole!=="guest"&&<div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("billing");}}><span style={{display:"flex"}}>{I.creditcard}</span> Billing &amp; plan</div>}
                {acct.workspaceRole!=="guest"&&<div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("templates");}}><span style={{display:"flex"}}>{I.clipboard}</span> Templates</div>}
                {canSeeFinancials(acct)&&<div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("metrix");}}><span style={{display:"flex"}}>{I.metrix}</span> Metrix</div>}
                <div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("contact");}}><span style={{display:"flex"}}>{I.helpChat}</span> Contact &amp; help</div>
                <div className="umi" onClick={()=>{setMenu(false);setSettingsOpen("about");}}><span style={{display:"flex"}}>{I.bulb}</span> About</div>
                <div className="umi red" onClick={()=>{setMenu(false);onSignOut();}}><span style={{display:"flex"}}>{I.logout}</span> Sign out</div>
              </div>
            </>}
          </div>
        </div>
        <div className="greet">{(()=>{const h=new Date().getHours();return h<12?"Good morning,":h<17?"Good afternoon,":"Good evening,";})()}<br/><em>{acct.name.split(" ")[0]}.</em>
          {acct.businessName&&acct.businessName!==acct.name&&<div style={{fontSize:12,color:"rgba(255,255,255,.5)",fontWeight:400,marginTop:2,fontFamily:"'Inter',sans-serif",letterSpacing:".03em"}}>{acct.businessName}</div>}
        </div>
        <div className="dline" style={{marginTop:6}}>{(()=>{const n=new Date();const DAY_N=["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];return `${DAY_N[n.getDay()]} ${String(n.getDate()).padStart(2,"0")} ${MONTHS[n.getMonth()]} ${String(n.getFullYear()).slice(2)}`;})()}</div>
      </div>
      <div className="srow">
        {stats.map((s,i)=>(
          <div className="spill" key={i} onClick={s.click||undefined} style={s.click?{cursor:"pointer"}:undefined}><div className={`snum${s.t?" t":""}`}>{s.n}</div><div className="slbl">{s.l}</div></div>
        ))}
      </div>
      {planUpgradeMsg&&(
        <div style={{margin:"0 24px 12px",background:"linear-gradient(135deg,var(--g) 0%,var(--gm) 100%)",borderRadius:14,padding:"16px 18px",display:"flex",alignItems:"center",gap:14}}>
          <div style={{display:"flex",justifyContent:"center"}}>{I.party}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{planUpgradeMsg}</div>
          </div>
          <div onClick={()=>setPlanUpgradeMsg(null)} style={{cursor:"pointer",color:"rgba(255,255,255,.5)",fontSize:18,fontWeight:700,lineHeight:1}}>×</div>
        </div>
      )}
      {canEditWorkspaceBranding(acct)&&(!acct.cancellationPolicy?.notice||!acct.cancellationPolicy?.latePolicy)&&(
        <div onClick={()=>setSettingsOpen("account")} style={{margin:"12px 24px 12px",background:"linear-gradient(135deg,#D97706 0%,#F59E0B 100%)",borderRadius:14,padding:"16px 18px",display:"flex",alignItems:"center",gap:14,cursor:"pointer"}}>
          <div style={{width:40,height:40,borderRadius:12,background:"rgba(255,255,255,.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>Set up your cancellation policy</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.7)",marginTop:2,lineHeight:1.4}}>Head to Account Settings to set your policy. Required before you can send contracts to clients.</div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      )}
      {canEditWorkspaceBranding(acct)&&!acct.stripeConnect?.chargesEnabled&&(
        <div onClick={()=>setSettingsOpen("billing")} style={{margin:"12px 24px 12px",background:"linear-gradient(135deg,#635BFF 0%,#7A73FF 100%)",borderRadius:14,padding:"16px 18px",display:"flex",alignItems:"center",gap:14,cursor:"pointer"}}>
          <div style={{width:40,height:40,borderRadius:12,background:"rgba(255,255,255,.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H3"/><path d="M21 4v12a2 2 0 01-2 2H5a2 2 0 01-2-2V4"/><path d="M3 10h18"/><path d="M7 15h.01"/><path d="M11 15h2"/></svg>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>Set up Stripe to get paid</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.7)",marginTop:2,lineHeight:1.4}}>Connect your Stripe account so clients can pay you directly through the portal.</div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      )}
      <div className="body">
        {/* Phase 11 — Payment failure banner takes priority over the trial banner. */}
        <PaymentFailureBanner acct={acct} onOpenBilling={()=>setSettingsOpen("billing")}/>
        {/* Phase 10 — Trial banner across the app while a Studio trial is active */}
        <StudioTrialBanner acct={acct} onOpenBilling={()=>setSettingsOpen("billing")}/>
        {planOverLimit&&(
          <div style={{margin:"0 24px 16px",background:"var(--err-bg)",border:"1.5px solid var(--err-bo)",borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
            <span style={{flexShrink:0,display:"flex"}}>{I.lock}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:"var(--err-tx)",marginBottom:4}}>Clients locked — plan expired</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>You have {(acct.clients||[]).filter(c=>!c.archived).length} active clients but the Free plan allows 3. Archive clients or <span onClick={()=>setSettingsOpen("billing")} style={{color:"var(--gm)",textDecoration:"underline",cursor:"pointer",fontWeight:600}}>upgrade your plan</span> to unlock.</div>
            </div>
          </div>
        )}
        <div className="home-grid">
        <div className="home-col">
        {canSeeFinancials(acct)&&<>
        <div className="sh"><div className="stitle">Revenue</div><div className="slink" onClick={()=>setSettingsOpen("metrix")} style={{display:"flex",alignItems:"center",gap:4}}>Metrix {I.arrow}</div></div>
        <div className="rcrd">
          <div className="rhdr">
            <div>
              <div className="rlbl">{selMonth.mo}{selMonth.y!==new Date().getFullYear()?" '"+String(selMonth.y).slice(2):""} revenue</div>
              <div className="rval">{mtd>0?`${sym}${fmtMoney(mtd)}`:"—"}</div>
            </div>
            <div className="rsub" style={{color:revDelta!==null&&revDelta>=0?"var(--gm)":"var(--t)"}}>{revChange}</div>
          </div>
          <div style={{overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",margin:"0 -18px",padding:"0 18px"}}
            ref={el=>{if(el&&!el._scrolled){const barW=el.scrollWidth/revBars.length;el.scrollLeft=Math.max(0,(6-3)*barW);el._scrolled=true;}}}>
            <div className="bch" style={{minWidth:revBars.length*46,width:revBars.length*46}}>
              {revBars.map((b,i)=>(
                <div className="bcol" key={i} onClick={()=>setSelBar(i)} style={{cursor:"pointer",minWidth:42}}>
                  <div className="bar" style={{height:b.h,background:i===selBar?"var(--t)":b.rev>0?"var(--g)":"var(--gl)",opacity:i===selBar?1:b.rev>0?.7:.4,transition:"all .15s"}}/>
                  <div className="bmo" style={{color:i===selBar?"var(--t)":"var(--mu)",fontWeight:i===selBar?600:400}}>{b.mo}</div>
                </div>
              ))}
            </div>
          </div>
          {mtd===0&&<div style={{fontSize:11,color:"var(--mu)",textAlign:"center",paddingTop:4}}>No payments recorded for {selMonth.mo}. Mark payments as received to track revenue.</div>}
        </div>
        {/* Stripe payouts snapshot — In hold vs Paid out */}
        {acct.stripeConnect?.chargesEnabled&&(()=>{
          const pays=(acct.payments||[]).filter(p=>p.status==="succeeded"||p.status==="refunded");
          if(pays.length===0) return null;
          // Only succeeded (non-refunded) payments contribute to in-hold/paid-out totals.
          // A refunded row pre-payout never hits your balance; a refunded row post-payout is debited back.
          const live=pays.filter(p=>p.status==="succeeded");
          const inHoldAmt=live.filter(p=>!p.paidOutAt).reduce((s,p)=>s+Number(p.amount||0),0);
          const paidOutAmt=live.filter(p=>p.paidOutAt).reduce((s,p)=>s+Number(p.amount||0),0);
          const inHoldCount=live.filter(p=>!p.paidOutAt).length;
          const paidOutCount=live.filter(p=>p.paidOutAt).length;
          return(<>
            <div className="sh"><div className="stitle">Stripe Payouts</div><div className="slink" onClick={()=>setSettingsOpen("metrix")} style={{display:"flex",alignItems:"center",gap:4}}>Details {I.arrow}</div></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12,padding:"0 24px"}}>
              <div style={{background:"var(--ok-bg)",border:"1.5px solid var(--ok-tx)",borderRadius:14,padding:"14px 14px"}}>
                <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".08em",color:"var(--ok-tx)",fontWeight:700,marginBottom:4}}>In hold</div>
                <div style={{fontSize:20,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(inHoldAmt)}</div>
                <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{inHoldCount} payment{inHoldCount===1?"":"s"} pending payout</div>
              </div>
              <div style={{background:"var(--gl)",border:"1.5px solid var(--bo)",borderRadius:14,padding:"14px 14px"}}>
                <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".08em",color:"var(--gm)",fontWeight:700,marginBottom:4}}>Paid out</div>
                <div style={{fontSize:20,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(paidOutAmt)}</div>
                <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{paidOutCount} transferred to bank</div>
              </div>
            </div>
          </>);
        })()}
        {/* Recent Transactions snapshot */}
        {(()=>{
          const recent=buildTransactions(acct.clients,sym).slice(0,4);
          if(recent.length===0) return null;
          return(<>
            <div className="sh"><div className="stitle">Recent Transactions</div><div className="slink" onClick={()=>setSettingsOpen("metrix")} style={{display:"flex",alignItems:"center",gap:4}}>See all {I.arrow}</div></div>
            <div style={{marginBottom:8,padding:"0 24px"}}>
              {recent.map(t=>(
                <div key={t.id} onClick={()=>onOpen(t.clientId)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid var(--bo)",cursor:"pointer"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:t.type==="refund"?"var(--err-tx)":t.col||"var(--g)",flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.clientName}</div>
                  </div>
                  <div style={{fontSize:11,color:"var(--mu)",flexShrink:0}}>{t.desc}</div>
                  <div style={{fontSize:12,fontWeight:700,color:t.amount<0?"var(--err-tx)":"var(--g)",fontVariantNumeric:"tabular-nums",flexShrink:0}}>{t.amount<0?"-":""}{sym}{fmtMoney(Math.abs(t.amount))}</div>
                </div>
              ))}
            </div>
          </>);
        })()}
        </>}{/* end canSeeFinancials gate */}
        {!canSeeFinancials(acct)&&(
          <div style={{margin:"0 24px 16px",padding:"12px 14px",background:"var(--gl)",border:"1px solid var(--bo)",borderRadius:12,display:"flex",alignItems:"center",gap:10}}>
            <span style={{display:"inline-flex",color:"var(--mu)"}}>{I.lock}</span>
            <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Revenue, payouts, and transactions are hidden. Your studio owner controls financial visibility.</div>
          </div>
        )}
        {/* Phase 10 — Ambient Studio card.
            Shown to Pro owners ≥30 days in, with localStorage-tracked
            dismissal (90d suppression on first dismiss, permanent on second). */}
        {(()=>{
          if(effectivePlan(acct)!=="pro") return null;
          if(!canEditWorkspaceBranding(acct)) return null; // owners only
          if(!acct.billingStartDate) return null;
          const start=new Date(acct.billingStartDate);
          const daysOnPro=Math.floor((Date.now()-start.getTime())/86400000);
          if(daysOnPro<30) return null;
          if(isStudioAmbientDismissed()) return null;
          return (
            <div style={{margin:"0 24px 16px",padding:"14px 16px",background:"var(--card)",border:"1.5px solid var(--t)",borderRadius:14}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                <span style={{display:"inline-flex",color:"var(--g)",flexShrink:0,marginTop:2}}>{I.clients}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginBottom:4}}>Working with a team?</div>
                  <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5,marginBottom:10}}>Studio adds multi-seat, project-scoped contractors, white-label portal, and per-seat analytics{acct.studioTrialUsed?"":". 14-day free trial"}.</div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setSettingsOpen("billing")} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Learn more</button>
                    <button onClick={()=>{ dismissStudioAmbient(); setStudioCardDismissedAt(Date.now()); }} style={{background:"transparent",color:"var(--mu)",border:"none",borderRadius:8,padding:"8px 10px",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Not now</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        <div className="sh"><div className="stitle">Next 7 Days</div></div>
        <div className="aglist">
          {(()=>{
            const today=new Date();
            const weekEnd=new Date(today); weekEnd.setDate(today.getDate()+7);
            const items=[];
            acct.clients.forEach(c=>{
              if(c.archived) return;
              (c.tl||[]).forEach(step=>{
                if(!step.d) return;
                const iso=toISO(step.d);
                if(!iso) return;
                const d=new Date(iso);
                if(d>=today&&d<=weekEnd){
                  items.push({n:c.name,tp:step.l,col:c.col,d,clientId:c.id});
                }
              });
            });
            items.sort((a,b)=>a.d-b.d);
            if(items.length===0) return <div style={{padding:"8px 0",fontSize:13,color:"var(--mu)"}}>Nothing in the next 7 days.</div>;
            const DAY_NAMES=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
            const MONTH_ABBR=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            return items.map((a,i)=>(
              <div className="agitem" key={i} onClick={()=>onOpen(a.clientId)} style={{cursor:"pointer"}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:a.col,flexShrink:0}}/>
                <div className="agn">{a.n}</div>
                <div className="agt">{a.tp}</div>
                <div className="agd">{DAY_NAMES[a.d.getDay()]} {String(a.d.getDate()).padStart(2,"0")} {MONTH_ABBR[a.d.getMonth()]} {String(a.d.getFullYear()).slice(2)}</div>
              </div>
            ));
          })()}
        </div>
        </div>{/* end home-col left */}
        <div className="home-col">
        <div className="sh"><div className="stitle">Needs Attention</div><div className="slink" onClick={()=>onNavChange("clients")}>See all</div></div>
        <div className="alist">
          {(()=>{
            const TODAY=new Date();
            const items=[];
            acct.clients.forEach(c=>{
              if(c.archived||c._draft) return;
              if(c.status==="inquiry"){
                const thread=acct.threads.find(t=>t.clientId===c.id);
                const hasReply=thread?.msgs?.some(m=>m.f==="me");
                if(!hasReply) items.push({clientId:c.id,col:c.col,title:`Follow-up: ${c.name}`,sub:`New inquiry — no reply yet`,badge:"AI Draft"});
              }
              if(c.contractSentToPortal&&c.status==="pending"){
                items.push({clientId:c.id,col:c.col,title:`Contract awaiting signature`,sub:`${c.name} · sent recently`,badge:""});
              }
              if(c.status==="signed"&&!c.depositPaid&&c.depositAmt){
                items.push({clientId:c.id,col:c.col,title:`Deposit not yet paid`,sub:`${c.name} · ${sym}${fmtMoney(c.depositAmt)} due`,badge:"Remind"});
              }
              if(c.depositPaid&&!c.balancePaid&&c.pct>=80){
                items.push({clientId:c.id,col:c.col,title:`Balance overdue`,sub:`${c.name} · ${sym}${fmtMoney(c.balanceAmt)} outstanding`,badge:"Remind"});
              }
              // Unpaid additional invoices — surface each client once with the total owed across all unpaid invoices
              {
                const unpaidInvs=(c.invoices||[]).filter(inv=>inv&&!inv.paid&&(Number(inv.amount)||0)>0);
                if(unpaidInvs.length>0){
                  const unpaidTotal=unpaidInvs.reduce((s,inv)=>s+Number(inv.amount||0),0);
                  items.push({clientId:c.id,col:c.col,title:unpaidInvs.length===1?`Invoice unpaid`:`${unpaidInvs.length} invoices unpaid`,sub:`${c.name} · ${sym}${fmtMoney(unpaidTotal)} outstanding`,badge:"Remind"});
                }
              }
              // Cancellation refund required
              if(c.cancellationRequested&&!c.archived&&c.depositPaid&&!(c.refunds||[]).length){
                const depAmt=c.depositAmt||round2((parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0)*(c.depositPct||50)/100);
                items.unshift({clientId:c.id,col:"#DC2626",title:`Refund required`,sub:`${c.name} · ${sym}${fmtMoney(depAmt)} deposit refund due`,badge:"↩ Refund"});
              }
            });
            if(items.length===0) return <div style={{padding:"16px 0",textAlign:"center",fontSize:13,color:"var(--mu)"}}>All clear — nothing needs attention right now.</div>;
            return items.map((a,i)=>(
              <div className="acard" key={i} onClick={()=>onOpen(a.clientId)} style={{cursor:"pointer"}}>
                <div style={{width:36,height:36,borderRadius:10,background:a.col,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700,overflow:"hidden"}}>
                  {(()=>{const cl=acct.clients.find(c=>c.id===a.clientId);return cl?.portalPhoto?<img src={cl.portalPhoto} alt={cl.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(cl?.ini||"?");})()}
                </div>
                <div className="atxt"><div className="at">{a.title}</div><div className="as">{a.sub}</div></div>
                {a.badge?<div className="badge">{a.badge}</div>:<div className="arr">›</div>}
              </div>
            ));
          })()}
        </div>
        {(()=>{
          // Exclude drafts — they're incomplete WIP and lack the fields rendered below (tags/step/pct).
          const active=acct.clients.filter(c=>!c.archived&&!c._draft&&["inquiry","pending","signed","active","delivered"].includes(c.status));
          return (
            <>
              <div className="sh"><div className="stitle">Active Clients</div><div className="slink" onClick={()=>onNavChange("clients")}>All {active.length}</div></div>
              <div className="cscroll">
                {active.map(c=>(
                  <div key={c.id} className={`ccard${c.feat?" ft":""}`} onClick={()=>onOpen(c.id)}>
                    <div className="cavr">
                      <div className="cav" style={{background:c.feat?"rgba(156,203,187,.8)":c.col,overflow:"hidden"}}>{c.portalPhoto?<img src={c.portalPhoto} alt={c.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:c.ini}</div>
                      <div><div className={`cn${c.feat?" li":""}`}>{c.name}</div><div className={`ct${c.feat?" li":""}`}>{c.type}</div></div>
                    </div>
                    {c.cancellationRequested
                      ?<div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}>
                        <span className={`spil ${(c.refunds||[]).length?"refund-pending":"cancelled"}`}>{(c.refunds||[]).length?"↩ Refunded":"Cancelled"}</span>
                        {!!(c.refunds||[]).length||!c.depositPaid?null:<span style={{fontSize:10,color:"var(--err-tx)",fontWeight:600}}>Refund due</span>}
                      </div>
                      :<>
                        <div className="prow"><div className={`ps${c.feat?" li":""}`}>{c.step}</div><div className={`pp${c.feat?" li":""}`}>{c.pct}%</div></div>
                        <div className={`pbar${c.feat?" dk":""}`}><div className={`pfil${c.feat?" t":""}`} style={{width:c.pct+"%"}}/></div>
                        <div className="tags">{c.tags.map((tg,i)=><div key={i} className={`tag${c.feat?" dk":i===0?" t":""}`}>{tg}</div>)}</div>
                      </>}
                  </div>
                ))}
                {active.length===0&&<div style={{fontSize:13,color:"var(--mu)",padding:"8px 0"}}>No active clients.</div>}
              </div>
            </>
          );
        })()}
        </div>{/* end home-col right */}
        </div>{/* end home-grid */}
      </div>
      {settingsOpen&&(
        <SettingsSheet which={settingsOpen} acct={acct} onClose={()=>{setSettingsOpen(null);setSettingsInitial(null);}} onAcctUpdate={onAcctUpdate} onSignOut={onSignOut} onDeleteAccount={onDeleteAccount} onOpen={onOpen} initialMetrixTab={settingsInitial?.metrixTab} initialInvoiceFilter={settingsInitial?.invoiceFilter} onSwitchSection={(target)=>{setSettingsInitial(null);setSettingsOpen(target);}}/>
      )}
    </>
  );
}

/* ── ADD CLIENT SHEET ── */
function AddClientSheet({acct, onAdd, onClose, initialData=null, initialStep=1, draftId=null}){
  const [step,setStep]=useState(initialStep);
  const [form,setForm]=useState(initialData||{name:"",email:"",phone:"",verts:[],sessionTypes:[],sessionType:"",date:"",amt:"",depositPct:50,clientCountry:acct.country||"US"});
  const [serviceDraft,setServiceDraft]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const emailIsValid=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email||"").trim())&&(form.email||"").trim().length>=5;
  const nameIsFull=isFullName(form.name);
  const canNext=nameIsFull&&emailIsValid;
  const multiService=form.verts.length>1||(form.sessionTypes&&form.sessionTypes.length>1);
  // Primary vert for template (first selected)
  const primaryVert=form.verts[0]||"";
  const amtIsZero=!form.amt||parseFloat(form.amt||0)<=0;
  const zeroAmtNeedsFull=amtIsZero&&form.depositPct!==100;
  const canSubmit=(form.sessionTypes?.length>0||form.sessionType.trim().length>1||serviceDraft.trim().length>1)&&!zeroAmtNeedsFull;
  const sym=acct.currencySymbol||"$";

  const freelancerVerts=(acct.verts&&acct.verts.length>0?acct.verts:[acct.vert]).filter(Boolean);
  // Expand "custom" into individual selectable creative types (e.g. "custom:Tattoo Artist", "custom:Florist")
  const expandedVerts=freelancerVerts.flatMap(vid=>{
    if(vid==="custom"){
      const roles=Array.isArray(acct.customRole)?acct.customRole:(acct.customRole?[acct.customRole]:[]);
      return roles.length>0?roles.map(r=>"custom:"+r):["custom"];
    }
    return [vid];
  });
  const skipVertPick=expandedVerts.length===1;

  const toggleFormVert=(id)=>setForm(f=>{
    const next=f.verts.includes(id)?f.verts.filter(v=>v!==id):[...f.verts,id];
    return {...f,verts:next};
  });
  const toggleSessionType=(s)=>setForm(f=>{
    const next=f.sessionTypes.includes(s)?f.sessionTypes.filter(x=>x!==s):[...f.sessionTypes,s];
    return {...f,sessionTypes:next,sessionType:next.join(", ")};
  });
  const commitServiceDraft=()=>{
    const v=serviceDraft.trim();
    if(!v) return;
    setForm(f=>{
      if(f.sessionTypes.some(s=>s.toLowerCase()===v.toLowerCase())){ return f; }
      const next=[...f.sessionTypes,v];
      return {...f,sessionTypes:next,sessionType:next.join(", ")};
    });
    setServiceDraft("");
  };
  const handleServiceKey=(e)=>{
    if(e.key==="Enter"||e.key==="Tab"){
      if(serviceDraft.trim()){ e.preventDefault(); commitServiceDraft(); }
    } else if(e.key==="Backspace"&&!serviceDraft&&form.sessionTypes.length>0){
      setForm(f=>{const next=f.sessionTypes.slice(0,-1);return {...f,sessionTypes:next,sessionType:next.join(", ")};});
    }
  };
  const removeSessionType=(idx)=>setForm(f=>{const next=f.sessionTypes.filter((_,i)=>i!==idx);return {...f,sessionTypes:next,sessionType:next.join(", ")};});

  const SESSION_SUGGESTIONS={
    photographer:["Wedding","Elopement","Portrait","Newborn","Engagement","Family","Headshots","Event"],
    videographer:["Wedding Film","Elopement Film","Brand Video","Music Video","Event Coverage","Documentary"],
    designer:["Brand Identity","Logo Design","Website Design","Packaging","Print Design","Social Media Kit"],
    copywriter:["Website Copy","Email Sequence","Blog Posts","Brand Strategy","Social Content","Press Release"],
    illustrator:["Editorial Illustration","Book Illustration","Brand Artwork","Character Design","Map / Infographic"],
    uxdesigner:["App Design","Website UX","Wireframes","Design System","Prototype"],
    webdeveloper:["Website Build","E-commerce","Landing Page","Web App","Webflow Site"],
    socialmedia:["Monthly Management","Campaign","Content Calendar","Strategy Session","Audit"],
    musicproducer:["Beat / Track","Full Album","Mixing & Mastering","Sound Design","Jingle"],
    dj:["Wedding","Corporate Event","Festival Set","Private Party","Club Night"],
    makeup:["Wedding Day","Bridal Trial","Editorial","Film / TV","Special Occasion"],
    florist:["Wedding Flowers","Event Styling","Corporate Arrangement","Funeral Tribute","Subscription"],
    eventplanner:["Wedding","Corporate Event","Private Party","Product Launch","Charity Gala"],
    personaltrainer:["1-to-1 Sessions","Online Coaching","Programme Design","Group Class","Nutrition Plan"],
    coach:["1-to-1 Coaching","Group Programme","Workshop","Strategy Day","Online Course"],
    custom:[]
  };

  // Aggregate suggestions from all selected verts (deduplicated); custom:* verts have no suggestions
  const allSuggestions=[...new Set(form.verts.flatMap(v=>SESSION_SUGGESTIONS[v]||SESSION_SUGGESTIONS[v.startsWith("custom:")?"custom":v]||[]))];
  const vertInfo=primaryVert?VERTS.find(v=>v.id===primaryVert):null;

  // Generate profession-based timeline template
  const buildTimeline=(vert,sessionType,date,depAmt,balAmt)=>{
    const d=date||"";
    const ds=sym;
    const isFullUpfront=!balAmt||balAmt<=0;
    const depLabel=isFullUpfront?`Full payment due — ${ds}${fmtMoney(depAmt)}`:`Deposit due — ${ds}${fmtMoney(depAmt)}`;
    const balStep=isFullUpfront?null:{l:`Final balance — ${ds}${fmtMoney(balAmt)}`,d:"",s:"pend"};
    const templates={
      photographer:[
        {l:"Inquiry received",d:localToday(),s:"done"},
        {l:"Contract sent",d:"",s:"pend"},
        {l:depLabel,d:"",s:"pend"},
        {l:`${sessionType||"Session"} day`,d:d,s:"pend"},
        {l:"Gallery delivered",d:"",s:"pend"},
        balStep,
      ].filter(Boolean),
      videographer:[
        {l:"Inquiry received",d:localToday(),s:"done"},
        {l:"Contract sent",d:"",s:"pend"},
        {l:depLabel,d:"",s:"pend"},
        {l:`${sessionType||"Film"} day`,d:d,s:"pend"},
        {l:"Rough cut delivered",d:"",s:"pend"},
        {l:"Final delivery",d:"",s:"pend"},
        balStep,
      ].filter(Boolean),
      designer:[
        {l:"Inquiry received",d:localToday(),s:"done"},
        {l:"Contract sent",d:"",s:"pend"},
        {l:depLabel,d:"",s:"pend"},
        {l:"Brief & discovery call",d:d,s:"pend"},
        {l:"Concepts presented",d:"",s:"pend"},
        {l:"Revisions complete",d:"",s:"pend"},
        {l:"Final files delivered",d:"",s:"pend"},
        balStep,
      ].filter(Boolean),
      copywriter:[
        {l:"Inquiry received",d:localToday(),s:"done"},
        {l:"Contract sent",d:"",s:"pend"},
        {l:depLabel,d:"",s:"pend"},
        {l:"Brief agreed",d:d,s:"pend"},
        {l:"First draft delivered",d:"",s:"pend"},
        {l:"Revisions complete",d:"",s:"pend"},
        balStep,
      ].filter(Boolean),
    };
    // Default for custom/unknown verticals — just basic steps
    const def=[
      {l:"Inquiry received",d:localToday(),s:"done"},
      {l:"Contract sent",d:"",s:"pend"},
      {l:depLabel,d:"",s:"pend"},
      {l:`${sessionType||"Project"} start`,d:d,s:"pend"},
      {l:"Work delivered",d:"",s:"pend"},
      balStep,
    ].filter(Boolean);
    return (templates[vert]||def);
  };

  return (
    <BottomSheet onClose={()=>{
      // Top-left ← arrow: if past step 1, go back one step (respecting skipVertPick).
      // Only close the sheet entirely when already on step 1.
      if(step>1){
        if(step===3) setStep(skipVertPick?1:2);
        else setStep(1);
        return;
      }
      // Only save a draft if the name is a real full name (first + last)
      onClose(isFullName(form.name)?form:null);
    }} title={step===1?"New Client":step===2?"What's this for?":"Project Details"}>
        <div style={{fontSize:13,color:"rgba(255,255,255,.6)",marginBottom:0,marginTop:-4,paddingLeft:0}}>
          {step===1?"Start with their contact details.":step===2?"Select the creative service for this client.":"Tell us about the project."}
        </div>

        {step===1&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>Full name *</div>
              <input className="ifinput" placeholder="e.g. Sarah Johnson" value={form.name} onChange={e=>set("name",e.target.value)}
                style={form.name&&!nameIsFull?{borderColor:"var(--err-bo)"}:undefined}/>
              {form.name&&!nameIsFull&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}>Please enter both first and last name.</div>}
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>Email address *</div>
              <input className="ifinput" type="email" placeholder="sarah@email.com" value={form.email} onChange={e=>set("email",e.target.value)} style={form.email&&!emailIsValid?{borderColor:"var(--err-bo)"}:undefined}/>
              {form.email&&!emailIsValid&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}>Please enter a valid email address.</div>}
              {!form.email&&<div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>Required — clients need a valid email to receive payment receipts.</div>}
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>Phone number</div>
              <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                <select value={form.clientCountry} onChange={e=>set("clientCountry",e.target.value)}
                  style={{background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"13px 8px",fontSize:18,fontFamily:"'Inter',sans-serif",color:"var(--mu)",outline:"none",flexShrink:0,width:58}}>
                  {COUNTRIES.map(co=><option key={co.code} value={co.code}>{co.flag}</option>)}
                </select>
                <PhoneInput className="ifinput" countryCode={form.clientCountry} value={form.phone} onChange={v=>set("phone",v)} placeholder="Phone number"/>
              </div>
            </div>
            <button onClick={()=>{
              if(skipVertPick){ setForm(f=>({...f,verts:[expandedVerts[0]]})); setStep(3); }
              else setStep(2);
            }} disabled={!canNext} style={{background:canNext?"var(--g)":"var(--bo)",color:canNext?"#fff":"var(--mu)",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:canNext?"pointer":"default",fontFamily:"'Inter',sans-serif",marginTop:4,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              Next {I.arrow}
            </button>
          </div>
        )}

        {/* ── STEP 2: What is this for? (only shown if multiple verts) ── */}
        {step===2&&(
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:4}}>What services are needed? *</div>
              <div style={{fontSize:12,color:"var(--mu)",marginBottom:10}}>Select all that apply — multiple services will use a custom project timeline.</div>
            </div>
            {expandedVerts.map(vid=>{
              const isCustom=vid.startsWith("custom:");
              const customName=isCustom?vid.slice(7):"";
              const v=isCustom?VERTS.find(x=>x.id==="custom"):VERTS.find(x=>x.id===vid);
              if(!v&&!isCustom) return null;
              const selected=form.verts.includes(vid);
              const title=isCustom?customName:(v?.title||vid);
              const sub=isCustom?"Custom creative type":(v?.sub||"");
              const icon=isCustom?I.zap:(v?.icon||I.zap);
              return (
                <div key={vid} onClick={()=>toggleFormVert(vid)}
                  style={{display:"flex",alignItems:"center",gap:14,background:selected?"var(--gl)":"var(--card)",border:`1.5px solid ${selected?"var(--g)":"var(--bo)"}`,borderRadius:14,padding:"14px 16px",cursor:"pointer",transition:"all .15s"}}>
                  <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${selected?"var(--g)":"var(--bo)"}`,background:selected?"var(--g)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {selected&&<svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:22,height:22,flexShrink:0,color:"var(--ink)"}}>{icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:600,color:"var(--ink)"}}>{title}</div>
                    <div style={{fontSize:11,color:"var(--mu)"}}>{sub}</div>
                  </div>
                </div>
              );
            })}
            {form.verts.length>1&&(
              <div style={{background:"var(--warn-bg)",borderRadius:10,padding:"9px 12px",display:"flex",gap:8,alignItems:"center"}}>
                <span style={{display:"inline-flex"}}>{I.bulb}</span>
                <span style={{fontSize:12,color:"var(--warn-tx)"}}>Multiple services selected — a custom project timeline will be created.</span>
              </div>
            )}
            <div style={{display:"flex",gap:10,marginTop:4}}>
              <button onClick={()=>setStep(1)} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>{I.arrowLeft} Back</button>
              <button onClick={()=>setStep(3)} disabled={form.verts.length===0}
                style={{flex:2,background:form.verts.length>0?"var(--g)":"var(--bo)",color:form.verts.length>0?"#fff":"var(--mu)",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:form.verts.length>0?"pointer":"default",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <span>{form.verts.length>1?`Next (${form.verts.length} services)`:"Next"}</span>{I.arrow}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Project details ── */}
        {step===3&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Show selected verts badges */}
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {form.verts.map(vid=>{
                const isCustom=vid.startsWith("custom:");
                const customName=isCustom?vid.slice(7):"";
                const v=isCustom?VERTS.find(x=>x.id==="custom"):VERTS.find(x=>x.id===vid);
                if(!v&&!isCustom) return null;
                const displayTitle=isCustom?customName:(v?.title||vid);
                const icon=isCustom?I.zap:(v?.icon||I.zap);
                return <div key={vid} style={{display:"flex",alignItems:"center",gap:6,background:"var(--gl)",borderRadius:8,padding:"5px 10px"}}>
                  <span style={{display:"inline-flex",color:"var(--gm)"}}>{icon}</span>
                  <span style={{fontSize:11,fontWeight:600,color:"var(--gm)"}}>{displayTitle}</span>
                </div>;
              })}
              {!skipVertPick&&<button onClick={()=>setStep(2)} style={{background:"none",border:"1.5px solid var(--bo)",borderRadius:8,padding:"5px 10px",fontSize:11,color:"var(--mu)",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Edit</button>}
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>
                Service type{multiService?"s":""} * {multiService&&<span style={{fontSize:10,color:"var(--t)",fontWeight:400,textTransform:"none",letterSpacing:0}}>(multiple — custom timeline)</span>}
              </div>
              {allSuggestions.length>0&&(
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
                  {allSuggestions.map(s=>{
                    const sel=form.sessionTypes.includes(s);
                    return (
                      <div key={s} onClick={()=>toggleSessionType(s)}
                        style={{padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:500,cursor:"pointer",border:`1.5px solid ${sel?"var(--t)":"var(--bo)"}`,background:sel?"var(--t)":"var(--card)",color:sel?"var(--tc)":"var(--mu)",transition:"all .12s",position:"relative",display:"inline-flex",alignItems:"center",gap:4}}>
                        {sel&&<span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span>}{s}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"8px 10px",display:"flex",flexWrap:"wrap",gap:6,alignItems:"center",minHeight:44,boxSizing:"border-box"}}>
                {form.sessionTypes.map((s,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:6,background:"var(--gl)",border:"1px solid var(--g)",borderRadius:99,padding:"4px 4px 4px 10px",fontSize:12,fontWeight:600,color:"var(--gm)"}}>
                    <span>{s}</span>
                    <button onClick={()=>removeSessionType(i)} type="button" style={{width:18,height:18,borderRadius:"50%",background:"rgba(28,77,68,.15)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"var(--gm)",fontFamily:"'Inter',sans-serif",padding:0,lineHeight:1}}>×</button>
                  </div>
                ))}
                <input
                  placeholder={form.sessionTypes.length>0?"Add another…":(allSuggestions.length>0?"Or type service type…":"e.g. Wedding, Brand Film…")}
                  value={serviceDraft}
                  onChange={e=>setServiceDraft(e.target.value)}
                  onKeyDown={handleServiceKey}
                  onBlur={commitServiceDraft}
                  style={{flex:1,minWidth:100,background:"transparent",border:"none",padding:"6px 4px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}
                />
              </div>
              {multiService&&(
                <div style={{marginTop:8,background:"var(--warn-bg)",borderRadius:10,padding:"8px 12px",fontSize:12,color:"var(--warn-tx)"}}>
                  <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.bulb}</span>Multiple services selected — the project timeline will start as a custom template for you to fill in.
                </div>
              )}
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>Project / session date</div>
              <input className="ifinput" type="date" value={form.date} min={localToday()} onChange={e=>set("date",e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>Total fee ({sym}) *</div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:14,color:"var(--mu)",fontWeight:600}}>{sym}</span>
                <input className="ifinput" type="number" placeholder="0.00" value={form.amt} onChange={e=>set("amt",e.target.value)} style={{paddingLeft:14+sym.length*10}}/>
              </div>
            </div>
            <div>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",marginBottom:6}}>Deposit split</div>
              <div style={{display:"flex",gap:6,alignItems:"stretch"}}>
                {[25,50,100,"other"].map(pct=>{
                  const isSelected=form.depositPct===pct||(!["other",25,50,100].includes(form.depositPct)&&pct==="other");
                  const isSplit=pct!==100;
                  const disabled=amtIsZero&&isSplit;
                  const isOtherSelected=pct==="other"&&isSelected&&!amtIsZero;
                  // When Other is selected, the button becomes a compact inline input + % label
                  // instead of dropping the input onto a second row. Keeps all four options on
                  // one line and makes the typed value clearly visible against the selected
                  // button's filled background.
                  return(
                  <div key={pct} onClick={()=>{if(!disabled) set("depositPct",pct==="other"?"other":pct);}} style={{flex:"1 1 0",minWidth:0,padding:isOtherSelected?"0":"9px 0",borderRadius:10,fontSize:12,fontWeight:600,cursor:disabled?"not-allowed":"pointer",textAlign:"center",border:"1.5px solid "+(isSelected?"var(--g)":"var(--bo)"),background:isSelected?"var(--g)":disabled?"var(--cr)":"#fff",color:isSelected?"#fff":disabled?"var(--bo)":"var(--mu)",opacity:disabled?.5:1,transition:"all .12s",display:"flex",alignItems:"center",justifyContent:"center",gap:2,overflow:"hidden"}}>
                    {isOtherSelected?(
                      <>
                        <input type="number" min={1} max={99} placeholder="30"
                          value={typeof form.depositPct==="number"&&![25,50,100].includes(form.depositPct)?form.depositPct:""}
                          onChange={e=>{const v=Math.max(1,Math.min(99,parseInt(e.target.value)||1));set("depositPct",v);}}
                          onClick={e=>e.stopPropagation()}
                          style={{width:"100%",minWidth:0,background:"transparent",border:"none",padding:"9px 0 9px 6px",fontSize:13,fontWeight:600,color:"#fff",textAlign:"right",outline:"none",fontFamily:"'Inter',sans-serif",MozAppearance:"textfield"}}/>
                        <span style={{fontSize:12,fontWeight:600,color:"#fff",paddingRight:6}}>%</span>
                      </>
                    ):(pct===100?"Full":pct==="other"?"Other":pct+"%")}
                  </div>);
                })}
              </div>
              {zeroAmtNeedsFull&&(
                <div style={{fontSize:12,color:"var(--err-tx)",fontWeight:600,marginTop:8,lineHeight:1.5}}>
                  A {sym}0 fee requires full payment. Please select "Full" above or enter a fee amount.
                </div>
              )}
            </div>
            {form.amt&&(()=>{
              const depositPct=form.depositPct||50;
              const isFullUpfront=depositPct>=100;
              const rows=isFullUpfront?[
                {l:"Total fee",v:`${sym}${fmtMoney(parseFloat(form.amt||0))}`,bold:true},
                {l:"Full payment (100%)",v:`${sym}${fmtMoney(round2(parseFloat(form.amt||0)))}`},
              ]:[
                {l:"Total fee",v:`${sym}${fmtMoney(parseFloat(form.amt||0))}`,bold:true},
                {l:`Deposit (${depositPct}%)`,v:`${sym}${fmtMoney(round2(parseFloat(form.amt||0)*depositPct/100))}`},
                {l:`Balance (${100-depositPct}%)`,v:`${sym}${fmtMoney(round2(parseFloat(form.amt||0)*(100-depositPct)/100))}`},
              ];
              return(
                <div style={{background:"var(--gl)",borderRadius:12,padding:14,border:"1px solid var(--bo)"}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--gm)",fontWeight:600,marginBottom:10}}>Payment schedule</div>
                  {rows.map((r,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:i<rows.length-1?"1px dashed var(--bo)":"none"}}>
                      <span style={{fontSize:12,color:"var(--gm)"}}>{r.l}</span>
                      <span className="num" style={{fontSize:13,fontWeight:r.bold?700:600,color:"var(--ink)"}}>{r.v}</span>
                    </div>
                  ))}
                  <div style={{fontSize:10,color:"var(--mu)",marginTop:10,lineHeight:1.5,fontStyle:"italic"}}>
                    {isFullUpfront?"Remember to set a due date for the payment on the project timeline after this client is added.":"Remember to set due dates for the deposit and balance on the project timeline after this client is added."}
                  </div>
                </div>
              );
            })()}
            <div style={{display:"flex",gap:10,marginTop:4}}>
              <button onClick={()=>setStep(skipVertPick?1:2)} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                {I.arrowLeft} Back
              </button>
              <button onClick={()=>{
                if(canSubmit){
                  // Commit any pending draft text from the service-type chip input
                  const pendingService=serviceDraft.trim();
                  const finalTypes=pendingService&&!form.sessionTypes.some(s=>s.toLowerCase()===pendingService.toLowerCase())?[...form.sessionTypes,pendingService]:form.sessionTypes;
                  const finalSessionType=finalTypes.length>0?finalTypes.join(", "):(pendingService||form.sessionType);
                  const rawAmt=parseFloat(form.amt||0);
                  const depPct=typeof form.depositPct==="number"?form.depositPct:50;
                  const depAmt=round2(rawAmt*depPct/100);
                  const balAmt=round2(rawAmt*(100-depPct)/100);
                  const isMulti=form.verts.length>1||(finalTypes&&finalTypes.length>1);
                  const isFullUpfront=depPct>=100||balAmt<=0;
                  const tl=isMulti
                    // Custom blank timeline for multi-service
                    ?[
                        {l:"Inquiry received",d:localToday(),s:"done"},
                        {l:"Contract sent",d:"",s:"pend"},
                        {l:isFullUpfront?`Full payment due — ${sym}${fmtMoney(depAmt)}`:`Deposit due — ${sym}${fmtMoney(depAmt)}`,d:"",s:"pend"},
                        {l:"Project kick-off",d:form.date||"",s:"pend"},
                        {l:"Work in progress",d:"",s:"pend"},
                        {l:"Delivery",d:"",s:"pend"},
                        isFullUpfront?null:{l:`Final balance — ${sym}${fmtMoney(balAmt)}`,d:"",s:"pend"},
                      ].filter(Boolean)
                    :buildTimeline((form.verts[0]||"custom").replace(/^custom:.+$/,"custom"),finalSessionType,form.date,depAmt,balAmt);
                  // Normalize custom:RoleName → "custom" for the primary vert field (backward compat)
                  const normalizedVert=(form.verts[0]||"custom").replace(/^custom:.+$/,"custom");
                  onAdd({...form,
                    vert:normalizedVert,
                    verts:form.verts,
                    sessionTypes:finalTypes,
                    sessionType:finalSessionType,
                    _timeline:tl, _depAmt:depAmt, _balAmt:balAmt
                  });
                }
              }} disabled={!canSubmit} style={{flex:2,background:canSubmit?"var(--t)":"var(--bo)",color:canSubmit?"var(--tc)":"var(--mu)",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:canSubmit?"pointer":"default",fontFamily:"'Inter',sans-serif"}}>
                Add Client
              </button>
            </div>
          </div>
        )}
    </BottomSheet>
  );
}
/* ── CLIENTS TAB ── */
function ClientsTab({acct,onOpen,onAddClient,onAcctUpdate,onSignOut,onDeleteAccount,planOverLimit,onNavChange}){
  const [q,setQ]=useState("");
  const [fil,setFil]=useState("All");
  const [showAdd,setShowAdd]=useState(false);
  const [editingDraft,setEditingDraft]=useState(null); // {id, form, step}
  const [showLimitMsg,setShowLimitMsg]=useState(false);
  const [settingsOpen,setSettingsOpen]=useState(null);
  // Visible chip label → status id. "In progress" displays for the underlying
  // status "active" so the filter chip stays in sync with the renamed pill.
  const statusMap={"Inquiry":"inquiry","Pending":"pending","Signed":"signed","In progress":"active","Delivered":"delivered","Complete":"complete"};

  // Drafts are incomplete WIP and shouldn't count against the free-plan limit.
  const activeClientCount=(acct.clients||[]).filter(c=>!c.archived&&!c._draft).length;
  const atFreeLimit=acct.billingPlan==="free"&&activeClientCount>=3;
  const isPro=acct.billingPlan==="pro"||acct.billingPlan==="studio";

  const handlePlusClick=()=>{
    if(atFreeLimit){ setShowLimitMsg(true); setTimeout(()=>setShowLimitMsg(false),3500); }
    else setShowAdd(true);
  };
  const getClientDate=(cl)=>{
    const dateStep=cl.tl?.find(s=>s.d&&s.d.match(/^\d{4}-\d{2}-\d{2}$/)&&s.l&&!s.l.toLowerCase().includes("inquiry"));
    if(dateStep) return dateStep.d;
    const m=cl.type?.match(/(\w{3})\s+(\d{1,2})/);
    if(m){const mi=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].indexOf(m[1]);return mi>=0?`2026-${String(mi+1).padStart(2,"0")}-${String(m[2]).padStart(2,"0")}`:"9999";}
    return "9999";
  };
  const filtered=acct.clients.filter(c=>{
    // Archived tab: only show archived
    if(fil==="Archived") return !!c.archived&&c.name.toLowerCase().includes(q.toLowerCase());
    // All other tabs: never show archived
    if(c.archived) return false;
    const mq=c.name.toLowerCase().includes(q.toLowerCase());
    const mf=fil==="All"||c.status===statusMap[fil];
    return mq&&mf;
  }).sort((a,b)=>getClientDate(a).localeCompare(getClientDate(b)));
  return (
    <>
      <div className="ttop"><div className="ttrow"><div className="ttitle">Clients</div><button className="ticn" onClick={handlePlusClick} style={{opacity:atFreeLimit?.5:1}}>＋</button></div></div>
      {atFreeLimit&&showLimitMsg&&(
        <div style={{padding:"8px 16px 4px",flexShrink:0}}>
          <div style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:12,padding:"12px 14px",display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{flexShrink:0,display:"flex"}}>{I.lock}</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>Free plan limit reached</div>
              <div style={{fontSize:12,color:"var(--info-tx)",marginTop:3,lineHeight:1.5}}>You have {activeClientCount} active clients. The Free plan allows 3 active clients. Archive a client or <span onClick={()=>setSettingsOpen("billing")} style={{textDecoration:"underline",cursor:"pointer",fontWeight:600}}>Upgrade to Pro</span> for unlimited clients.</div>
            </div>
          </div>
        </div>
      )}
      {atFreeLimit&&!showLimitMsg&&!planOverLimit&&(
        <div style={{padding:"8px 16px 4px",flexShrink:0}}>
          <div style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:10,padding:"9px 12px",display:"flex",alignItems:"center",gap:8}}>
            <span style={{flexShrink:0,display:"flex"}}>{I.lock}</span>
            <span style={{fontSize:12,color:"var(--warn-tx)",fontWeight:500,lineHeight:1.4}}>Free plan: {activeClientCount}/3 active clients — Archive a client or <span onClick={()=>setSettingsOpen("billing")} style={{textDecoration:"underline",cursor:"pointer",fontWeight:600}}>Upgrade to add more</span></span>
          </div>
        </div>
      )}
      {planOverLimit&&(
        <div style={{padding:"8px 16px 4px",flexShrink:0}}>
          <div style={{background:"var(--err-bg)",border:"1.5px solid var(--err-bo)",borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
            <span style={{flexShrink:0,display:"flex"}}>{I.lock}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:"var(--err-tx)",marginBottom:4}}>All clients locked — plan expired</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>You have <strong style={{color:"var(--ink)"}}>{activeClientCount} active clients</strong> but the Free plan allows 3. All client cards are read-only until you archive down to 3 or <span onClick={()=>setSettingsOpen("billing")} style={{color:"var(--gm)",textDecoration:"underline",cursor:"pointer",fontWeight:600}}>upgrade your plan</span>.</div>
            </div>
          </div>
        </div>
      )}
      <div className="body">
        {/* banners moved above .body */}
        <div className="swrap"><span className="sico" style={{display:"inline-flex"}}>{I.search}</span><input className="sinput" placeholder="Search clients…" value={q} onChange={e=>setQ(e.target.value)}/></div>
        <div className="chips">
          {["All","Inquiry","Pending","Signed","In progress","Delivered","Complete","Archived"].map(c=><div key={c} className={`chip${fil===c?" on":""}`} onClick={()=>setFil(c)}>{c}</div>)}
        </div>
        <div className="clist">
          {filtered.length===0&&<div style={{padding:"20px 0",textAlign:"center",fontSize:13,color:"var(--mu)"}}>No clients match this filter.</div>}
          {filtered.map(cl=>{
            const _clvd=getClientVertDisplay(cl);
            const cardLocked=planOverLimit&&!cl.archived;
            return (
              <div className="crow" key={cl.id} style={{position:"relative",opacity:cardLocked?.6:1,cursor:cardLocked?"not-allowed":"pointer"}} onClick={()=>{
                if(cardLocked) { onOpen(cl.id); return; } // onOpen is guarded — shows lock banner
                if(cl._draft){
                  // Drafts resume on step 1 (contact details) so the user re-verifies
                  // name + email before progressing — matches the normal new-client flow.
                  setEditingDraft({id:cl.id, form:{
                    name:cl.name||"", email:cl.email||"", phone:cl.phone||"",
                    verts:cl.verts||[], sessionTypes:[], sessionType:cl.type?.split("·")[0]?.trim()||"",
                    date:"", amt:"", depositPct:50, clientCountry:acct.country||"US"
                  }, step:1});
                } else {
                  onOpen(cl.id);
                }
              }}>
                <div className="cravg" style={{background:cl.col,overflow:"hidden"}}>{cl.portalPhoto?<img src={cl.portalPhoto} alt={cl.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:cl.ini}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div className="crn">{cl.name}{cl._draft&&<span style={{fontSize:9,fontWeight:700,background:"var(--warn-bg)",color:"var(--warn-tx)",borderRadius:4,padding:"2px 5px",marginLeft:6,verticalAlign:"middle",textTransform:"uppercase",letterSpacing:".04em"}}>Draft</span>}{cardLocked&&<span style={{fontSize:9,fontWeight:700,background:"var(--err-bg)",color:"var(--err-tx)",borderRadius:4,padding:"2px 5px",marginLeft:6,verticalAlign:"middle",textTransform:"uppercase",letterSpacing:".04em"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Locked</span>}</div>
                  <div className="crs">{cl._draft?<>Tap to continue <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span></>:(<>{_clvd.icons[0]} {cl.type}</>)}</div>
                </div>
                <div className="crr">
                  {cl._draft
                    ?<button onClick={e=>{e.stopPropagation();onAddClient({_removeDraft:true,id:cl.id});}}
                      style={{background:"none",border:"none",fontSize:18,color:"var(--mu)",cursor:"pointer",padding:"2px 6px",lineHeight:1}}>✕</button>
                    :<><div className="cra">{canSeeFinancials(acct)?cl.amt:"••••"}</div>{cl.archived
                      ?<div className="spil archived">Archived</div>
                      :cl.cancellationRequested
                      ?<div className={`spil ${(cl.refunds||[]).length?"refund-pending":"cancelled"}`}>{(cl.refunds||[]).length?"Refunded":"Cancelled"}</div>
                      :<div className={`spil ${cl.status}`}>{(STATUS_STAGES.find(s=>s.key===cl.status)?.label)||(cl.status.charAt(0).toUpperCase()+cl.status.slice(1))}</div>}</>
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showAdd&&<AddClientSheet acct={acct}
        onAdd={data=>{setShowAdd(false);onAddClient(data);}}
        onClose={(draftData)=>{setShowAdd(false);if(draftData&&isFullName(draftData.name)){const tempId="draft_"+Date.now();onAddClient({...draftData,_draft:true,_draftId:tempId});}}}/>}
      {editingDraft&&<AddClientSheet acct={acct}
        initialData={editingDraft.form}
        initialStep={editingDraft.step}
        draftId={editingDraft.id}
        onAdd={data=>{
          // Remove the old draft, then add as real client
          onAddClient({_removeDraft:true,id:editingDraft.id});
          setEditingDraft(null);
          onAddClient(data);
        }}
        onClose={(draftData)=>{
          setEditingDraft(null);
          // Update the draft with latest form data — require first + last name
          if(draftData&&isFullName(draftData.name)){
            onAddClient({_removeDraft:true,id:editingDraft.id});
            onAddClient({...draftData,_draft:true,_draftId:editingDraft.id});
          }
        }}/>}
      {settingsOpen&&<SettingsSheet which={settingsOpen} acct={acct} onClose={()=>setSettingsOpen(null)} onAcctUpdate={onAcctUpdate} onSignOut={onSignOut} onDeleteAccount={onDeleteAccount} onOpen={onOpen} onSwitchSection={(target)=>setSettingsOpen(target)}/>}
    </>
  );
}

/* ── CALENDAR TAB ── */
function CalTab({acct, onOpen, onAddCalEntry, onDeleteCalEntry}){
  const today=new Date();
  const [year,setYear]=useState(today.getFullYear());
  const [month,setMonth]=useState(today.getMonth()+1);
  const [day,setDay]=useState(null);
  const [showForm,setShowForm]=useState(false);
  const [formClient,setFormClient]=useState("");
  const [formSubject,setFormSubject]=useState("");
  const [formDate,setFormDate]=useState("");
  const [formTime,setFormTime]=useState("");
  const [formNotes,setFormNotes]=useState("");
  const [viewEntry,setViewEntry]=useState(null);
  const [showPicker,setShowPicker]=useState(false);
  const [pickerYear,setPickerYear]=useState(year);

  const cal=buildCal(acct.clients, acct.calEntries);

  const MNAMES=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const daysInMonth=new Date(year,month,0).getDate();
  const firstDow=new Date(year,month-1,1).getDay();

  const prevMonth=()=>{ if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1); setDay(null); };
  const nextMonth=()=>{ if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1); setDay(null); };

  const cells=[];
  for(let i=0;i<firstDow;i++) cells.push({d:null});
  for(let d=1;d<=daysInMonth;d++) cells.push({d});
  while(cells.length%7!==0) cells.push({d:null});

  const evts=day ? (cal[`${year}-${month}-${day}`]||[]) : [];
  const isToday=(d)=> d===today.getDate()&&month===today.getMonth()+1&&year===today.getFullYear();

  // Clients who have events this month — for legend
  const thisMonthClients=[];
  const seen=new Set();
  for(let d=1;d<=daysInMonth;d++){
    (cal[`${year}-${month}-${d}`]||[]).forEach(e=>{
      if(!seen.has(e.clientId)){ seen.add(e.clientId); thisMonthClients.push({name:e.name,col:e.col,clientId:e.clientId}); }
    });
  }

  return (
    <>
      <div className="ttop"><div className="ttrow"><div className="ttitle">Calendar</div><button onClick={()=>{setShowForm(true);setFormDate(day?`${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`:"");setFormClient("");setFormSubject("");setFormTime("");setFormNotes("");}} style={{width:32,height:32,borderRadius:"50%",background:"var(--g)",color:"#fff",border:"none",fontSize:20,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",lineHeight:1}}>+</button></div></div>
      <div className="body">
        <div className="cnav" style={{position:"relative"}}>
          <div className="carr" onClick={prevMonth} style={{cursor:"pointer"}}>‹</div>
          <div className="cmo" onClick={()=>{setPickerYear(year);setShowPicker(p=>!p);}} style={{cursor:"pointer",userSelect:"none",display:"flex",alignItems:"center",gap:4}} title="Tap to pick month & year">{MNAMES[month-1]} {year}<span style={{fontSize:10,opacity:0.5,transition:"transform .2s",transform:showPicker?"rotate(180deg)":"none"}}>▼</span></div>
          <div className="carr" onClick={nextMonth} style={{cursor:"pointer"}}>›</div>
          {showPicker&&<>
            <div style={{position:"fixed",inset:0,zIndex:49}} onClick={()=>setShowPicker(false)}/>
            <div style={{position:"absolute",top:"100%",left:"50%",transform:"translateX(-50%)",zIndex:50,background:"var(--card)",border:"1px solid var(--bo)",borderRadius:14,boxShadow:"0 8px 30px rgba(0,0,0,.12)",padding:16,width:280,marginTop:6}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <div onClick={()=>setPickerYear(y=>y-1)} style={{cursor:"pointer",width:28,height:28,borderRadius:"50%",background:"var(--gl)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:600,color:"var(--gm)"}}>‹</div>
                <div style={{fontSize:16,fontWeight:700,color:"var(--ink)",fontFamily:"'Cormorant Garamond',serif"}}>{pickerYear}</div>
                <div onClick={()=>setPickerYear(y=>y+1)} style={{cursor:"pointer",width:28,height:28,borderRadius:"50%",background:"var(--gl)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:600,color:"var(--gm)"}}>›</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                {MNAMES.map((mn,i)=>{
                  const isCurrent=i+1===month&&pickerYear===year;
                  const isToday2=i===today.getMonth()&&pickerYear===today.getFullYear();
                  return <div key={mn} onClick={()=>{setMonth(i+1);setYear(pickerYear);setDay(null);setShowPicker(false);}}
                    style={{padding:"8px 4px",borderRadius:8,textAlign:"center",cursor:"pointer",fontSize:12,fontWeight:isCurrent?700:500,
                      background:isCurrent?"var(--g)":isToday2?"var(--gl)":"transparent",
                      color:isCurrent?"#fff":isToday2?"var(--gm)":"var(--ink)",
                      border:isToday2&&!isCurrent?"1px solid var(--gm)":"1px solid transparent"}}>{mn.slice(0,3)}</div>;
                })}
              </div>
              <div onClick={()=>{setMonth(today.getMonth()+1);setYear(today.getFullYear());setDay(null);setShowPicker(false);}}
                style={{marginTop:10,textAlign:"center",fontSize:11,fontWeight:600,color:"var(--gm)",cursor:"pointer",padding:"6px",borderRadius:8,background:"var(--gl)"}}>Today</div>
            </div>
          </>}
        </div>

        {/* Client colour legend */}
        {thisMonthClients.length>0&&(
          <div className="clegend">
            {thisMonthClients.map((cl,i)=>(
              <div key={i} className="cleg">
                <div className="clegdot" style={{background:cl.col}}/>
                <span>{cl.name.split(" ")[0]}</span>
              </div>
            ))}
          </div>
        )}

        <div className="cal-layout">
        <div className="cal-main">
        <div className="cdow">{["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=><div className="cdowc" key={d}>{d}</div>)}</div>
        <div className="cgrid">
          {cells.map((x,i)=>{
            if(!x.d) return <div key={i} className="cday oth" style={{cursor:"default"}}/>;
            const key=`${year}-${month}-${x.d}`;
            const es=cal[key]||[];
            const tod=isToday(x.d);
            const sel=x.d===day&&!tod;
            const dots=es.filter((e,idx,arr)=>arr.findIndex(a=>a.clientId===e.clientId)===idx);
            return (
              <div key={i} className={`cday${tod?" tod":""}${sel?" sel":""}`} onClick={()=>setDay(x.d===day?null:x.d)}>
                <div className="cdaynum">{x.d}</div>
                {dots.length>0&&(
                  <div className="cdots">
                    {dots.slice(0,4).map((e,j)=>(
                      <div key={j} className="cdot" style={{background:e.col}}/>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>{/* end cal-main */}
        <div className="cal-side">
        <div className="cevts">
          {day?(
            <>
              <div className="cevttl">{MNAMES[month-1]} {day} · {evts.length||"No"} event{evts.length!==1?"s":""}</div>
              {evts.length===0&&<div style={{fontSize:13,color:"var(--mu)",padding:"8px 0"}}>Nothing scheduled.</div>}
              {evts.map((e,i)=>(
                <div className="cevt" key={i} onClick={()=>{if(e.isCalEntry){setViewEntry(e);}else if(e.clientId){onOpen(e.clientId);}}} style={{cursor:(e.clientId||e.isCalEntry)?"pointer":"default"}}>
                  <div className="cest" style={{background:e.col}}/>
                  <div style={{flex:1}}>
                    <div className="cen">{e.name}</div>
                    <div className="cety">{e.type}{e.time?` · ${e.time}`:""}</div>
                  </div>
                  {(e.clientId||e.isCalEntry)&&<span style={{color:"var(--gm)",display:"flex",alignItems:"center"}}>{I.chevron}</span>}
                </div>
              ))}
            </>
          ):(
            <>
              <div className="cevttl">{MNAMES[month-1]} {year}</div>
              {(()=>{
                const all=[];
                for(let d=1;d<=daysInMonth;d++){
                  (cal[`${year}-${month}-${d}`]||[]).forEach(e=>all.push({...e,d}));
                }
                if(all.length===0) return <div style={{fontSize:13,color:"var(--mu)",padding:"8px 0"}}>No events this month.</div>;
                return all.map((e,i)=>(
                  <div className="cevt" key={i} onClick={()=>{ setDay(e.d); if(e.isCalEntry){setViewEntry(e);}else if(e.clientId){onOpen(e.clientId);} }} style={{cursor:"pointer"}}>
                    <div className="cest" style={{background:e.col}}/>
                    <div style={{flex:1}}>
                      <div className="cen">{e.name}</div>
                      <div className="cety">{e.type}{e.time?` · ${e.time}`:""} · {String(e.d).padStart(2,"0")} {MONTHS[month-1]} {String(year).slice(2)}</div>
                    </div>
                    <span style={{color:"var(--gm)",display:"flex",alignItems:"center"}}>{I.chevron}</span>
                  </div>
                ));
              })()}
            </>
          )}
        </div>
        </div>{/* end cal-side */}
        </div>{/* end cal-layout */}
      </div>

      {/* ── CREATE ENTRY FORM ── */}
      {showForm&&(
        <div className="page-slide" style={{zIndex:500}}>
          <div className="page-header">
            <button className="page-header-back" onClick={()=>setShowForm(false)}>{I.arrowLeft}</button>
            <div className="page-header-title">New Calendar Entry</div>
          </div>
          <div className="page-slide-inner" style={{padding:20}}>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6,display:"block"}}>Client</label>
              <select value={formClient} onChange={e=>setFormClient(e.target.value)} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--input-bg)",color:"var(--ink)",fontSize:14,fontFamily:"'Inter',sans-serif"}}>
                <option value="">— Select client —</option>
                {acct.clients.filter(c=>!c.archived).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6,display:"block"}}>Subject *</label>
              <input value={formSubject} onChange={e=>setFormSubject(e.target.value)} placeholder="e.g. Discovery call, Review session" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--input-bg)",color:"var(--ink)",fontSize:14,fontFamily:"'Inter',sans-serif"}}/>
            </div>
            <div style={{display:"flex",gap:10,marginBottom:14}}>
              <div style={{flex:1}}>
                <label style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6,display:"block"}}>Date *</label>
                <input type="date" value={formDate} onChange={e=>setFormDate(e.target.value)} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--input-bg)",color:"var(--ink)",fontSize:14,fontFamily:"'Inter',sans-serif"}}/>
              </div>
              <div style={{flex:1}}>
                <label style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6,display:"block"}}>Time</label>
                <input type="time" value={formTime} onChange={e=>setFormTime(e.target.value)} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--input-bg)",color:"var(--ink)",fontSize:14,fontFamily:"'Inter',sans-serif"}}/>
              </div>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6,display:"block"}}>Notes</label>
              <textarea value={formNotes} onChange={e=>setFormNotes(e.target.value)} placeholder="Additional details..." rows={4} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--input-bg)",color:"var(--ink)",fontSize:14,fontFamily:"'Inter',sans-serif",resize:"vertical"}}/>
            </div>
            <button disabled={!formSubject.trim()||!formDate} onClick={()=>{
              if(onAddCalEntry){
                onAddCalEntry({clientId:formClient||null,subject:formSubject.trim(),date:formDate,time:formTime,notes:formNotes.trim()});
              }
              setShowForm(false);
            }} style={{width:"100%",padding:"14px",borderRadius:12,border:"none",background:(!formSubject.trim()||!formDate)?"var(--bo)":"var(--g)",color:(!formSubject.trim()||!formDate)?"var(--mu)":"#fff",fontSize:14,fontWeight:600,cursor:(!formSubject.trim()||!formDate)?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif"}}>
              Create Entry
            </button>
          </div>
        </div>
      )}

      {/* ── VIEW ENTRY DETAIL ── */}
      {viewEntry&&(
        <div className="page-slide" style={{zIndex:500}}>
          <div className="page-header">
            <button className="page-header-back" onClick={()=>setViewEntry(null)}>{I.arrowLeft}</button>
            <div className="page-header-title">Calendar Entry</div>
            {viewEntry.calEntryId&&onDeleteCalEntry&&(
              <button className="page-header-action" onClick={()=>{onDeleteCalEntry(viewEntry.calEntryId);setViewEntry(null);}}>Delete</button>
            )}
          </div>
          <div className="page-slide-inner" style={{padding:20}}>
            <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:16}}>
              <div style={{fontSize:17,fontWeight:600,color:"var(--ink)",marginBottom:12}}>{viewEntry.type}</div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{display:"inline-flex",color:"var(--mu)"}}>{I.user}</span>
                <span style={{fontSize:14,color:"var(--ink)"}}>{viewEntry.name||"No client"}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{display:"inline-flex",color:"var(--mu)"}}>{I.caldate}</span>
                <span style={{fontSize:14,color:"var(--ink)"}}>{viewEntry.date?fmtDate(viewEntry.date):""}</span>
              </div>
              {viewEntry.time&&(
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:13,color:"var(--mu)"}}>🕐</span>
                  <span style={{fontSize:14,color:"var(--ink)"}}>{viewEntry.time}</span>
                </div>
              )}
              {viewEntry.notes&&(
                <div style={{marginTop:12,padding:12,background:"var(--gl)",borderRadius:10}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Notes</div>
                  <div style={{fontSize:14,color:"var(--ink)",lineHeight:1.5,whiteSpace:"pre-wrap"}}>{viewEntry.notes}</div>
                </div>
              )}
            </div>
            {viewEntry.clientId&&(
              <button onClick={()=>{setViewEntry(null);onOpen(viewEntry.clientId);}} style={{width:"100%",marginTop:14,padding:"12px",borderRadius:12,border:"1.5px solid var(--bo)",background:"var(--gl)",color:"var(--gm)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                View Client {I.arrow}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ── INBOX TAB ── */
function InboxTab({acct, onThreadMessage, onMarkRead, isDesktop}){
  const [open,setOpen]=useState(null);
  const [composeClient,setComposeClient]=useState(null); // client to start new thread with
  const [msg,setMsg]=useState("");
  const [aiDraft,setAiDraft]=useState("");
  const [aiGenerating,setAiGenerating]=useState(false);
  const [msgs,setMsgs]=useState(null); // local override for open thread

  const archivedIds=new Set((acct.clients||[]).filter(c=>c.archived).map(c=>c.id));
  const activeThreads=acct.threads.filter(t=>!archivedIds.has(t.clientId));
  const thr=acct.threads.find(t=>t.id===open);
  const unreadCount=activeThreads.filter(t=>t.unread>0).length;

  // Mark thread as read when opened
  const openThread=(threadId)=>{
    setOpen(threadId);
    setMsgs(null);
    const t=acct.threads.find(x=>x.id===threadId);
    if(t?.unread>0 && onMarkRead) onMarkRead(threadId);
  };

  // When a compose-mode first message creates a tmp_ thread, we set open="tmp_"+clientId.
  // Once dbUpsertThread returns and the real id replaces the tmp id in acct.threads, this
  // effect syncs `open` to the real id so the lookup keeps working.
  useEffect(()=>{
    if(!open||!String(open).startsWith("tmp_")) return;
    // Still a valid tmp thread? Nothing to do.
    if(acct.threads.some(t=>t.id===open)) return;
    // tmp id has been upgraded — find the replacement by clientId
    const clientId=String(open).slice(4);
    const real=acct.threads.find(t=>t.clientId===clientId);
    if(real) setOpen(real.id);
  },[acct.threads,open]);

  // Always read from acct.threads for live sync — local msgs only used for optimistic new messages
  const threadMsgs=thr?.msgs||[];
  const displayMsgs=msgs ? [...threadMsgs, ...msgs.filter(m=>!threadMsgs.some(t=>t.t===m.t&&t.ts===m.ts))] : threadMsgs;

  // Auto-scroll to the latest message when a thread opens or a new message appears. Schedules
  // inside requestAnimationFrame so the browser has laid out the newly-rendered messages and
  // scrollHeight reflects them. Without this, opening a long thread lands on the oldest
  // message at the top — the user would have to scroll down to see recent messages + input.
  const msgsRef=useRef(null);
  useEffect(()=>{
    if(!open) return;
    const el=msgsRef.current;
    if(!el) return;
    requestAnimationFrame(()=>{
      if(msgsRef.current) msgsRef.current.scrollTop=msgsRef.current.scrollHeight;
    });
  },[open,displayMsgs.length]);

  const sendMsg=()=>{
    if(!msg.trim()||!thr) return;
    if(archivedIds.has(thr.clientId)) return; // block sending to archived clients
    if(onThreadMessage) onThreadMessage(thr.clientId,msg.trim());
    setMsg("");
    setAiDraft("");
  };

  const generateReply=async()=>{
    if(!thr) return;
    setAiGenerating(true);
    setAiDraft("");
    // Build conversation context from last 6 messages
    const context=displayMsgs.slice(-6).map(m=>`${m.f==="me"?"You":"Client"}: ${m.t}`).join("\n");
    const prompt=`You are helping a creative freelancer (${acct.name}, a ${acct.vert}) reply to a client message in their inbox.

Client name: ${thr.name}
Recent conversation:
${context}

Write a short, warm, professional reply from ${acct.name.split(" ")[0]}. Keep it under 50 words. Respond naturally to the last message. No greeting prefix needed — just the reply body.`;
    try{
      const res=await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:aiHeaders(),
        body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:150,stream:true,messages:[{role:"user",content:prompt}]})
      });
      if(!res.ok){ setAiDraft("Thanks so much for reaching out! I'll get back to you shortly."); setAiGenerating(false); return; }
      const reader=res.body.getReader(); const dec=new TextDecoder(); let buf="";
      while(true){
        const {done,value}=await reader.read(); if(done) break;
        buf+=dec.decode(value,{stream:true});
        const lines=buf.split("\n"); buf=lines.pop();
        for(const line of lines){
          if(line.startsWith("data: ")){
            try{ const p=JSON.parse(line.slice(6)); if(p.type==="content_block_delta"&&p.delta?.type==="text_delta") setAiDraft(t=>t+p.delta.text); }catch(e){}
          }
        }
      }
    }catch(e){ setAiDraft("Thanks so much for reaching out! I'll get back to you shortly."); }
    setAiGenerating(false);
  };

  const useAiDraft=()=>{ setMsg(aiDraft); setAiDraft(""); };

  // ── Thread list panel (reused in both mobile and desktop) ──
  const threadListPanel = (
    <div className={isDesktop?"inbox-list":""}>
      <div className="ilist">
        {(()=>{
          const activeClients = acct.clients.filter(cl => !cl.archived);
          if(activeClients.length === 0) return (
            <div style={{padding:"40px 20px",textAlign:"center"}}>
              <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.chatBubble}</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>No messages yet</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6}}>Add a client from the Clients tab to start messaging. All conversations will appear here.</div>
            </div>
          );
          return activeClients.map(cl => {
            const t = acct.threads.find(th => th.clientId === cl.id);
            const unread = t?.unread || 0;
            const preview = t?.pre || "Tap to start a conversation";
            const time = t?.time || "";
            const hasThread = !!t;
            const isActive = isDesktop && ((open && thr?.clientId===cl.id) || (composeClient?.id===cl.id));
            return (
              <div className="ithr" key={cl.id} style={isActive?{background:"var(--gl)"}:{}} onClick={()=>{
                if(hasThread) openThread(t.id);
                else { setComposeClient(cl); setOpen(null); }
              }}>
                <div className="iava" style={{background:cl.col,overflow:"hidden"}}>
                  {cl.portalPhoto?<img src={cl.portalPhoto} alt={cl.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:cl.ini}
                  {unread>0&&<div className="iudot"/>}
                </div>
                <div className="ibod">
                  <div className="inam">{cl.name}</div>
                  <div className={`ipre${unread>0?" u":""}`} style={{fontStyle:hasThread?"normal":"italic"}}>{preview}</div>
                </div>
                <div className="irt">
                  <div className="itm">{time}</div>
                  {unread>0&&<div className="icnt">{unread}</div>}
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );

  // ── Thread detail panel (open thread or compose) ──
  const threadDetailPanel = (open&&thr) ? (
    <div className={"ptr-skip"+(isDesktop?" inbox-detail":"")} style={{display:"flex",flexDirection:"column",height:isDesktop?"100%":undefined,flex:isDesktop?undefined:1,minHeight:0}}>
      {!isDesktop&&<div className="ttop">
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{display:"inline-flex",alignItems:"center",color:"rgba(255,255,255,.7)",cursor:"pointer"}} onClick={()=>{setOpen(null);setMsgs(null);setAiDraft("");}}>{I.arrowLeft}</span>
          <div style={{width:36,height:36,borderRadius:10,background:thr.col,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,color:"#fff",overflow:"hidden"}}>{(()=>{const cl=acct.clients.find(c=>c.id===thr.clientId);return cl?.portalPhoto?<img src={cl.portalPhoto} alt={thr.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:thr.ini;})()}</div>
          <div>
            <div style={{fontSize:15,fontWeight:600,color:"#fff"}}>{thr.name}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.5)"}}>Client</div>
          </div>
        </div>
      </div>}
      {isDesktop&&<div style={{padding:"16px 20px",borderBottom:"1px solid var(--bo)",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <div style={{width:36,height:36,borderRadius:10,background:thr.col,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,color:"#fff",overflow:"hidden"}}>{(()=>{const cl=acct.clients.find(c=>c.id===thr.clientId);return cl?.portalPhoto?<img src={cl.portalPhoto} alt={thr.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:thr.ini;})()}</div>
        <div><div style={{fontSize:15,fontWeight:600,color:"var(--ink)"}}>{thr.name}</div><div style={{fontSize:11,color:"var(--mu)"}}>Client</div></div>
      </div>}
      <div className="msgs" ref={msgsRef} style={{flex:1}}>
        {displayMsgs.map((m,i)=>{
          // For freelancer-side messages sent by a teammate OTHER than the current
          // viewer, surface the sender's name so the viewer can tell who replied
          // (owner sees "Sent by [member]"; member sees "Sent by [owner]").
          // Hidden when the current user sent it — that's already implicit.
          const showSender = m.f==="me" && m.fromUserId && m.fromUserId!==acct.id && m.sentBy;
          return (
            <div key={i} className={`msg ${m.f==="me"?"me":"them"}`}>
              {showSender&&<div style={{fontSize:10,color:"var(--mu)",marginBottom:3,paddingRight:4,textAlign:"right"}}>Sent by {m.sentBy}</div>}
              <div className="mbub">{m.t}</div><div className="mtm">{m.ts}</div>
            </div>
          );
        })}
      </div>
      <div className="cfooter">
        {archivedIds.has(thr.clientId)?(
          <div style={{background:"var(--warn-bg)",borderRadius:10,padding:"10px 14px",fontSize:12,color:"var(--info-tx)",textAlign:"center"}}>This client is archived. Messaging is disabled.</div>
        ):<>
        {aiDraft?(
          <div style={{padding:"10px 16px",background:"var(--g)",display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".07em",color:"rgba(255,255,255,.5)",marginBottom:4}}>✦ AI draft</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.85)",lineHeight:1.5}}>{aiDraft}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
              <button onClick={useAiDraft} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>Use {I.arrow}</button>
              <button onClick={()=>{setAiDraft("");generateReply();}} style={{background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.6)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>↻ Redo</button>
              <button onClick={()=>setAiDraft("")} style={{background:"none",color:"rgba(255,255,255,.3)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>✕</button>
            </div>
          </div>
        ):aiGenerating?(
          <div style={{padding:"10px 16px",background:"var(--g)",display:"flex",alignItems:"center",gap:8}}>
            <div style={{display:"flex",gap:3}}>{[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:"50%",background:"rgba(255,255,255,.4)",animation:`pulse 1.2s ease-in-out ${i*.2}s infinite`}}/>)}</div>
            <span style={{fontSize:12,color:"rgba(255,255,255,.5)"}}>Drafting reply…</span>
            <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
          </div>
        ):(
          <div className="aisugg" onClick={acct.billingPlan!=="free"?generateReply:undefined} style={{cursor:acct.billingPlan==="free"?"default":"pointer",opacity:acct.billingPlan==="free"?0.5:1}} title={acct.billingPlan==="free"?"Upgrade to Pro for AI replies":"Generate AI reply"}>
            ✦ <span>Tap to generate AI reply</span>
            <span style={{marginLeft:"auto",fontSize:11,opacity:.6}}>✦ AI</span>
          </div>
        )}
        <div className="cinrow">
          <input className="cinput" placeholder="Type a message…" value={msg} onChange={e=>setMsg(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); sendMsg(); }}}/>
          <button className="sbtn" onClick={sendMsg}>➤</button>
        </div>
        </>}
      </div>
    </div>
  ) : (composeClient&&(!open)) ? (
    <div className={"ptr-skip"+(isDesktop?" inbox-detail":"")} style={{display:"flex",flexDirection:"column",height:isDesktop?"100%":undefined,flex:isDesktop?undefined:1,minHeight:0}}>
      {!isDesktop&&<div className="ttop">
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{display:"inline-flex",alignItems:"center",color:"rgba(255,255,255,.7)",cursor:"pointer"}} onClick={()=>{setComposeClient(null);setMsg("");}}>{I.arrowLeft}</span>
          <div style={{width:36,height:36,borderRadius:10,background:composeClient.col,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,color:"#fff",overflow:"hidden"}}>{composeClient.portalPhoto?<img src={composeClient.portalPhoto} alt={composeClient.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:composeClient.ini}</div>
          <div><div style={{fontSize:15,fontWeight:600,color:"#fff"}}>{composeClient.name}</div><div style={{fontSize:11,color:"rgba(255,255,255,.5)"}}>New conversation</div></div>
        </div>
      </div>}
      {isDesktop&&<div style={{padding:"16px 20px",borderBottom:"1px solid var(--bo)",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <div style={{width:36,height:36,borderRadius:10,background:composeClient.col,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,color:"#fff",overflow:"hidden"}}>{composeClient.portalPhoto?<img src={composeClient.portalPhoto} alt={composeClient.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:composeClient.ini}</div>
        <div><div style={{fontSize:15,fontWeight:600,color:"var(--ink)"}}>{composeClient.name}</div><div style={{fontSize:11,color:"var(--mu)"}}>New conversation</div></div>
      </div>}
      <div className="msgs" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center",color:"var(--mu)",fontSize:13,padding:24}}>
          <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.chatBubble}</div>
          Start a conversation with {composeClient.name.split(" ")[0]}.<br/>
          <span style={{fontSize:11,opacity:.7}}>They'll see your messages in their client portal.</span>
        </div>
      </div>
      <div className="cfooter">
        <div className="cinrow">
          <input className="cinput" value={msg} onChange={e=>setMsg(e.target.value)}
            placeholder={`Message ${composeClient.name.split(" ")[0]}…`}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey&&msg.trim()){e.preventDefault();
              const cid=composeClient.id;
              if(onThreadMessage) onThreadMessage(cid,msg.trim());
              setMsg("");
              // Transition from compose → open thread so the new message is visible immediately
              setOpen("tmp_"+cid);
              setComposeClient(null);
            }}}/>
          <button className="csend" onClick={()=>{
            if(!msg.trim()) return;
            const cid=composeClient.id;
            if(onThreadMessage) onThreadMessage(cid,msg.trim());
            setMsg("");
            // Transition from compose → open thread so the new message is visible immediately
            setOpen("tmp_"+cid);
            setComposeClient(null);
          }} disabled={!msg.trim()}>{I.send}</button>
        </div>
      </div>
    </div>
  ) : null;

  // ── MOBILE: original behavior ──
  if(!isDesktop){
    // Thread / compose views use the same absolute-positioned wrapper trick as desktop to
    // bypass PullToRefresh's inner wrapper (minHeight:100%, indefinite height). Without this,
    // .msgs has no definite height to shrink against, expands to fit its content, and pushes
    // the .cfooter (input field) below the viewport — forcing the user to scroll down to type.
    if(composeClient&&!open) return (
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column"}}>
        {threadDetailPanel}
      </div>
    );
    if(open&&thr) return (
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column"}}>
        {threadDetailPanel}
      </div>
    );
    return (
      <>
        <div className="ttop"><div className="ttrow"><div className="ttitle">Inbox</div><div style={{fontSize:12,color:"rgba(255,255,255,.6)"}}>{unreadCount} unread</div></div></div>
        <div className="body">{threadListPanel}</div>
      </>
    );
  }

  // ── DESKTOP: master-detail layout ──
  // Absolute wrapper bypasses PullToRefresh inner wrapper's indefinite height (minHeight:100%)
  // so flex children get a definite height from the PullToRefresh outer container (position:relative + flex:1)
  return (
    <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column"}}>
      <div className="ttop"><div className="ttrow"><div className="ttitle">Inbox</div><div style={{fontSize:12,color:"rgba(255,255,255,.6)"}}>{unreadCount} unread</div></div></div>
      <div className="inbox-layout" style={{flex:1,minHeight:0}}>
        {threadListPanel}
        {threadDetailPanel || (
          <div className="inbox-detail">
            <div className="inbox-empty">
              <div style={{textAlign:"center"}}>
                <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.chatBubble}</div>
                <div style={{fontSize:14,color:"var(--mu)"}}>Select a conversation</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── CONTRACTS TAB ── */
/* ── STRUCTURED CONTRACT DISPLAY ── */
function ContractDisplay({text,maxHeight}){
  if(!text) return null;
  const lines=text.split("\n");
  return(
    <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:"24px 20px",...(maxHeight!==null?{maxHeight:maxHeight||400,overflowY:"auto"}:{}),marginBottom:12}}>
      {lines.map((line,i)=>{
        const trimmed=line.trim();
        if(!trimmed) return <div key={i} style={{height:10}}/>;
        // Bold section headers (e.g. "**1. Parties**" or "## Section")
        const boldMatch=trimmed.match(/^\*\*(.+?)\*\*$/);
        const mdHeader=trimmed.match(/^#{1,3}\s+(.+)$/);
        // Numbered section headers like "1. Parties & Agreement"
        const numberedHeader=trimmed.match(/^(\d{1,2})\.\s+([A-Z].*)/);
        if(boldMatch) return <div key={i} style={{fontSize:14,fontWeight:700,color:"var(--ink)",margin:"18px 0 8px",paddingBottom:6,borderBottom:"1px solid var(--bo)"}}>{boldMatch[1]}</div>;
        if(mdHeader) return <div key={i} style={{fontSize:14,fontWeight:700,color:"var(--ink)",margin:"18px 0 8px",paddingBottom:6,borderBottom:"1px solid var(--bo)"}}>{mdHeader[1]}</div>;
        if(numberedHeader) return <div key={i} style={{fontSize:14,fontWeight:700,color:"var(--ink)",margin:"18px 0 8px",paddingBottom:6,borderBottom:"1px solid var(--bo)"}}>{trimmed}</div>;
        // Inline bold: replace **text** with <strong>
        const parts=line.split(/(\*\*[^*]+\*\*)/g);
        const rendered=parts.map((p,j)=>{
          const bm=p.match(/^\*\*(.+)\*\*$/);
          return bm?<strong key={j}>{bm[1]}</strong>:<span key={j}>{p}</span>;
        });
        // Bullet or dash lines
        if(/^\s*[-•–]\s/.test(line)) return <div key={i} style={{fontSize:13,color:"var(--ink)",lineHeight:1.7,paddingLeft:16,position:"relative"}}><span style={{position:"absolute",left:4,color:"var(--gm)"}}>•</span>{rendered}</div>;
        // Sub-items (indented with spaces/tabs)
        if(/^\s{3,}/.test(line)) return <div key={i} style={{fontSize:12,color:"var(--mu)",lineHeight:1.7,paddingLeft:24}}>{rendered}</div>;
        // Signature lines
        if(/^_{3,}/.test(trimmed)) return <div key={i} style={{borderBottom:"1px solid #333",width:"60%",margin:"16px 0 4px"}}/>;
        return <div key={i} style={{fontSize:13,color:"var(--ink)",lineHeight:1.7}}>{rendered}</div>;
      })}
    </div>
  );
}

/* ── PDF GENERATION FROM TEXT ── */
function downloadContractAsPdf(text,filename){
  const win=window.open("","_blank");
  if(!win) return;
  const lines=text.split("\n");
  let html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${filename}</title><style>
    body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;max-width:700px;margin:40px auto;padding:0 30px;color:#1a1a1a;font-size:13px;line-height:1.8}
    h2{font-size:15px;font-weight:700;margin:24px 0 8px;padding-bottom:4px;border-bottom:1px solid #ddd}
    .bullet{padding-left:20px;position:relative}.bullet::before{content:'•';position:absolute;left:6px;color:#888}
    .indent{padding-left:32px;font-size:12px;color:#444}
    .sig{border-bottom:1px solid #333;width:60%;margin:20px 0 4px}
    .spacer{height:8px}
    @media print{body{margin:20px}}
  </style></head><body>`;
  for(const line of lines){
    const t=line.trim();
    if(!t){html+='<div class="spacer"></div>';continue;}
    const bm=t.match(/^\*\*(.+?)\*\*$/);
    const mdh=t.match(/^#{1,3}\s+(.+)$/);
    const nh=t.match(/^(\d{1,2})\.\s+([A-Z].*)/);
    if(bm){html+=`<h2>${bm[1]}</h2>`;continue;}
    if(mdh){html+=`<h2>${mdh[1]}</h2>`;continue;}
    if(nh){html+=`<h2>${t}</h2>`;continue;}
    const inlined=line.replace(/\*\*([^*]+)\*\*/g,'<strong>$1</strong>');
    if(/^\s*[-•–]\s/.test(line)){html+=`<div class="bullet">${inlined.trim()}</div>`;continue;}
    if(/^\s{3,}/.test(line)){html+=`<div class="indent">${inlined.trim()}</div>`;continue;}
    if(/^_{3,}/.test(t)){html+='<div class="sig"></div>';continue;}
    html+=`<div>${inlined}</div>`;
  }
  html+=`</body></html>`;
  win.document.write(html);
  win.document.close();
  setTimeout(()=>{win.print();},400);
}

// ── Build flat transaction list from clients (deposits, balances, refunds, invoices) ──
function buildTransactions(clients,sym){
  const txns=[];
  (clients||[]).forEach(c=>{
    const raw=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
    const depAmt=round2(raw*(c.depositPct||50)/100);
    const balAmt=round2(raw*(100-(c.depositPct||50))/100);
    const isLive=!c.archived&&!c._draft&&!c.cancellationRequested;
    const oweAllowed=isLive&&["inquiry","signed","active","pending"].includes(c.status);
    if(c.depositPaid&&depAmt>0){
      txns.push({id:c.id+"-dep",clientId:c.id,clientName:c.name,col:c.col,type:"deposit",desc:"Deposit",amount:depAmt,date:c.depositPaidDate||c.createdAt||"",status:"paid"});
    }else if(oweAllowed&&depAmt>0){
      txns.push({id:c.id+"-dep",clientId:c.id,clientName:c.name,col:c.col,type:"deposit",desc:"Deposit",amount:depAmt,date:c.createdAt||"",status:"pending"});
    }
    if(c.balancePaid&&balAmt>0){
      txns.push({id:c.id+"-bal",clientId:c.id,clientName:c.name,col:c.col,type:"balance",desc:"Final balance",amount:balAmt,date:c.balancePaidDate||"",status:"paid"});
    }else if(oweAllowed&&balAmt>0){
      txns.push({id:c.id+"-bal",clientId:c.id,clientName:c.name,col:c.col,type:"balance",desc:"Final balance",amount:balAmt,date:c.createdAt||"",status:"pending"});
    }
    (c.refunds||[]).forEach((r,ri)=>{
      txns.push({id:c.id+"-ref-"+ri,clientId:c.id,clientName:c.name,col:c.col,type:"refund",desc:r.reason||"Refund",amount:-(parseFloat(r.amount||0)||0),date:r.date||"",status:"refunded"});
    });
    (c.invoices||[]).forEach((inv,ii)=>{
      txns.push({id:c.id+"-inv-"+ii,clientId:c.id,clientName:c.name,col:c.col,type:"invoice",desc:inv.description||"Invoice",amount:Number(inv.amount||0),date:inv.createdAt||"",status:inv.paid?"paid":"pending"});
    });
  });
  txns.sort((a,b)=>{
    const da=a.date?new Date(toISO(a.date)||a.date):new Date(0);
    const db=b.date?new Date(toISO(b.date)||b.date):new Date(0);
    return db-da;
  });
  return txns;
}

// Phase 7 — Studio analytics: per-seat / service-line / pipeline. Visible
// only to Studio owners (the "leaderboard private to owner" rule from the
// spec). Sub-tab selector inside one panel.
function StudioAnalyticsTab({acct,onOpen,initialView}){
  const sym=acct.currencySymbol||"$";
  const [view,setView]=useState(initialView||"perseat");
  const [members,setMembers]=useState([]);
  const [assignments,setAssignments]=useState([]);
  const [loadingMembers,setLoadingMembers]=useState(false);

  useEffect(()=>{
    if(!acct.workspaceId) return;
    let cancelled=false;
    setLoadingMembers(true);
    // Fetch roster + per-workspace assignment map in parallel — both feed
    // computePerSeatStats. Assignments power the "Projects assigned" column
    // so a member who isn't lead but is on a project still gets credit.
    Promise.all([
      dbGetWorkspaceMembers(acct.workspaceId),
      studioListWorkspaceMemberClients(acct.workspaceId),
    ]).then(([memRes,assignRes])=>{
      if(cancelled) return;
      setMembers(Array.isArray(memRes?.data)?memRes.data:[]);
      setAssignments(Array.isArray(assignRes?.data)?assignRes.data:[]);
      setLoadingMembers(false);
    });
    return()=>{ cancelled=true; };
  },[acct.workspaceId]);

  const perSeat = useMemo(()=>computePerSeatStats(acct.clients||[],members,assignments),[acct.clients,members,assignments]);
  const serviceLines = useMemo(()=>computeServiceLineStats(acct.clients||[]),[acct.clients]);
  const pipeline = useMemo(()=>computePipelineStats(acct.clients||[]),[acct.clients]);

  const exportPerSeat=()=>{
    downloadCsv("per-seat-performance.csv",
      ["Member","Email","Role","Revenue (as lead)","Projects led","Active assigned","Archived assigned"],
      perSeat.map(s=>[s.name,s.email,s.role,s.revenue.toFixed(2),s.projectCount,s.assignedActiveCount,s.archivedAssignedCount]));
  };
  const exportPerSeatPdf=async()=>{
    try {
      // PDF: matched to the in-app row content — Active assigned, Lead, Archived.
      // Email dropped (CSV has it for lookup); Role still useful for the owner
      // badge.
      await downloadPdf({
        filename:"per-seat-performance.pdf",
        title:"Per-seat performance",
        subtitle:`${acct.workspaceName||"Workspace"} · ${perSeat.length} seat${perSeat.length===1?"":"s"}`,
        header:["Member","Role","Revenue","Active","Lead","Archived"],
        rows:perSeat.map(s=>[s.name,s.role,`${sym}${s.revenue.toFixed(2)}`,String(s.assignedActiveCount),String(s.projectCount),String(s.archivedAssignedCount)]),
      });
    } catch(e){ alert(e.message||"Could not generate PDF"); }
  };
  const exportServiceLines=()=>{
    // CSV keeps count + client names in separate columns so users can pivot.
    downloadCsv("service-lines.csv", ["Service","Client count","Clients","Revenue"],
      serviceLines.map(s=>[s.vert,s.count,(s.clientNames||[]).join("; "),s.revenue.toFixed(2)]));
  };
  const exportServiceLinesPdf=async()=>{
    try {
      // PDF "Clients" column shows names (comma-separated). Long lists will
      // ellipsis-truncate per-cell; full list is always in the CSV.
      await downloadPdf({
        filename:"service-lines.pdf",
        title:"Service-line profitability",
        subtitle:`${acct.workspaceName||"Workspace"} · ${serviceLines.length} service line${serviceLines.length===1?"":"s"}`,
        header:["Service","Clients","Revenue"],
        rows:serviceLines.map(s=>[s.vert,(s.clientNames||[]).join(", ")||"—",`${sym}${s.revenue.toFixed(2)}`]),
        align:["left","left","right"],
      });
    } catch(e){ alert(e.message||"Could not generate PDF"); }
  };
  const exportPipeline=()=>{
    // CSV leaves conversion empty (not 0/100) when undefined — preserves
    // the "no meaningful value" distinction for spreadsheet formulas.
    downloadCsv("pipeline.csv", ["Stage","Currently at","Reached","Conversion %"],
      pipeline.map(p=>[p.label,p.count,p.reached,p.conversion===null?"":p.conversion]));
  };
  const exportPipelinePdf=async()=>{
    try {
      const totalActive=(acct.clients||[]).filter(c=>!c.archived).length;
      await downloadPdf({
        filename:"pipeline.pdf",
        title:"Pipeline conversion",
        subtitle:`${acct.workspaceName||"Workspace"} · ${totalActive} active client${totalActive===1?"":"s"}`,
        header:["Stage","Currently at","Reached","Conversion %"],
        // Em-dash for null conversion so the column stays right-aligned
        // and visually communicates "no value" without leaking a spurious 100%.
        rows:pipeline.map(p=>[p.label,String(p.count),String(p.reached),p.conversion===null?"—":`${p.conversion}%`]),
      });
    } catch(e){ alert(e.message||"Could not generate PDF"); }
  };

  const totalRevenue = perSeat.reduce((a,s)=>a+s.revenue,0);
  const maxRevenue = Math.max(1,...perSeat.map(s=>s.revenue));
  const maxServiceRevenue = Math.max(1,...serviceLines.map(s=>s.revenue));
  const maxPipelineReached = Math.max(1,...pipeline.map(p=>p.reached));

  return (
    <div style={{padding:"0 0 20px"}}>
      <div className="chips" style={{marginTop:0}}>
        <div className={`chip${view==="perseat"?" on":""}`} onClick={()=>setView("perseat")}>Per-seat</div>
        <div className={`chip${view==="services"?" on":""}`} onClick={()=>setView("services")}>Service Lines</div>
        <div className={`chip${view==="pipeline"?" on":""}`} onClick={()=>setView("pipeline")}>Pipeline</div>
      </div>

      <div style={{fontSize:10,color:"var(--mu)",letterSpacing:".05em",textTransform:"uppercase",marginTop:14,marginBottom:10}}>Updated daily · {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>

      {view==="perseat"&&(
        <>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>Per-seat performance</div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={exportPerSeat} disabled={perSeat.length===0} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:perSeat.length===0?"not-allowed":"pointer",opacity:perSeat.length===0?.5:1,fontFamily:"'Inter',sans-serif"}}>CSV</button>
              <button onClick={exportPerSeatPdf} disabled={perSeat.length===0} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:perSeat.length===0?"not-allowed":"pointer",opacity:perSeat.length===0?.5:1,fontFamily:"'Inter',sans-serif"}}>PDF</button>
            </div>
          </div>
          {loadingMembers?(
            <div style={{fontSize:12,color:"var(--mu)",padding:"14px 0"}}>Loading…</div>
          ):perSeat.length===0?(
            <div style={{fontSize:12,color:"var(--mu)",padding:"14px 0"}}>No seat-level data yet. Invite teammates to start tracking per-seat performance.</div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {perSeat.map((s,i)=>(
                <div key={s.memberId} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,padding:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <div style={{width:24,height:24,borderRadius:"50%",background:i===0?"var(--g)":"var(--gl)",color:i===0?"#fff":"var(--mu)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {s.name}{s.role==="owner"&&<span style={{marginLeft:6,fontSize:9,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",color:"var(--gm)",background:"var(--gl)",padding:"2px 6px",borderRadius:99}}>Owner</span>}
                      </div>
                      <div style={{fontSize:11,color:"var(--mu)"}}>{s.email}</div>
                    </div>
                    <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums",flexShrink:0}}>{sym}{fmtMoney(s.revenue)}</div>
                  </div>
                  <div style={{height:6,background:"var(--gl)",borderRadius:99,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${(s.revenue/maxRevenue)*100}%`,background:"var(--g)",transition:"width .3s"}}/>
                  </div>
                  <div style={{display:"flex",gap:14,marginTop:8,fontSize:11,color:"var(--mu)",flexWrap:"wrap"}}>
                    {/* Active assignments first, then lead count, then archived
                        as a trailing chip when > 0 so historical work doesn't
                        clutter the row for fresh seats. */}
                    <span title="Projects this seat is assigned to (lead or on the team) — non-archived.">
                      {s.assignedActiveCount} active project{s.assignedActiveCount===1?"":"s"}
                    </span>
                    <span>·</span>
                    <span title="Projects where this seat is the lead. Revenue is attributed here.">
                      Lead on {s.projectCount}
                    </span>
                    {s.archivedAssignedCount>0&&<><span>·</span>
                      <span title="Archived projects this seat is/was assigned to.">
                        {s.archivedAssignedCount} archived
                      </span>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view==="services"&&(
        <>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>Service-line profitability</div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={exportServiceLines} disabled={serviceLines.length===0} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:serviceLines.length===0?"not-allowed":"pointer",opacity:serviceLines.length===0?.5:1,fontFamily:"'Inter',sans-serif"}}>CSV</button>
              <button onClick={exportServiceLinesPdf} disabled={serviceLines.length===0} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:serviceLines.length===0?"not-allowed":"pointer",opacity:serviceLines.length===0?.5:1,fontFamily:"'Inter',sans-serif"}}>PDF</button>
            </div>
          </div>
          {serviceLines.length===0?(
            <div style={{fontSize:12,color:"var(--mu)",padding:"14px 0"}}>No revenue tracked yet by service line.</div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {serviceLines.map(s=>(
                <div key={s.vert} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:12}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",textTransform:"capitalize"}}>{s.vert}</div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(s.revenue)}</div>
                  </div>
                  <div style={{height:5,background:"var(--gl)",borderRadius:99,overflow:"hidden",marginBottom:5}}>
                    <div style={{height:"100%",width:`${(s.revenue/maxServiceRevenue)*100}%`,background:"var(--g)"}}/>
                  </div>
                  <div style={{fontSize:11,color:"var(--mu)"}}>{s.count} client{s.count===1?"":"s"}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view==="pipeline"&&(()=>{
        // Total active = non-archived clients across any pipeline stage.
        // Shown above the stage rows so users see the headline number
        // separately from per-stage reach counts — earlier wording overloaded
        // "Active" as both a stage name and a totals concept.
        const totalActive=(acct.clients||[]).filter(c=>!c.archived).length;
        return (
        <>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>Pipeline conversion</div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={exportPipeline} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>CSV</button>
              <button onClick={exportPipelinePdf} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>PDF</button>
            </div>
          </div>
          {/* Active-client summary chip */}
          <div style={{background:"var(--gl)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
            <div style={{fontSize:11,color:"var(--mu)",letterSpacing:".04em",textTransform:"uppercase",fontWeight:600}}>Active clients</div>
            <div style={{fontSize:18,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{totalActive}</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {pipeline.map((p,i)=>(
              <div key={p.id} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:6}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{p.label}</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:10,flexShrink:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{p.reached}</div>
                    {p.conversion!==null&&<div style={{fontSize:11,fontWeight:600,color:p.conversion>=70?"var(--ok-tx)":p.conversion>=40?"var(--gm)":"var(--err-tx)"}}>{p.conversion}%</div>}
                  </div>
                </div>
                <div style={{height:5,background:"var(--gl)",borderRadius:99,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${(p.reached/maxPipelineReached)*100}%`,background:"var(--g)"}}/>
                </div>
                <div style={{fontSize:10,color:"var(--mu)",marginTop:4}}>{p.count} currently at this stage</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:10,color:"var(--mu)",lineHeight:1.5,marginTop:10}}>
            "Reached" counts every client that has passed through this stage, including those now further down. Conversion = % of previous-stage clients that progressed.
          </div>
        </>
        );
      })()}
    </div>
  );
}

function InvoicesTab({acct,onOpen,initialFilter}){
  const [filter,setFilter]=useState(initialFilter||"all");
  useEffect(()=>{ if(initialFilter) setFilter(initialFilter); },[initialFilter]);
  const sym=acct.currencySymbol||"$";
  const txns=buildTransactions(acct.clients,sym);
  const filtered=filter==="all"?txns
    :filter==="pending"?txns.filter(t=>t.status==="pending")
    :txns.filter(t=>t.type===filter);
  // Income = only what's actually been received (deposit/balance always settled; invoices require paid status)
  const totalIncome=txns.filter(t=>t.amount>0&&t.status==="paid").reduce((s,t)=>s+t.amount,0);
  const totalOutstanding=txns.filter(t=>t.amount>0&&t.status==="pending").reduce((s,t)=>s+t.amount,0);
  const totalRefunds=txns.filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0);
  const net=totalIncome-totalRefunds;
  const filters=["all","pending","deposit","balance","refund","invoice"];

  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div className="topbar" style={{position:"relative"}}>
      <div className="greet" style={{fontSize:28}}>Invoices</div>
      <div style={{fontSize:13,color:"var(--mu)",marginTop:2}}>{txns.length} transactions</div>
    </div>
    {/* Totals */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,padding:"16px 24px 0"}}>
      <div style={{background:"var(--gl)",borderRadius:12,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:18,fontWeight:700,color:"var(--g)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(totalIncome)}</div>
        <div style={{fontSize:10,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".04em",marginTop:2}}>Received</div>
      </div>
      <div style={{background:totalOutstanding>0?"var(--warn-bg)":"var(--cr)",borderRadius:12,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:18,fontWeight:700,color:totalOutstanding>0?"var(--warn-tx)":"var(--mu)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(totalOutstanding)}</div>
        <div style={{fontSize:10,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".04em",marginTop:2}}>Outstanding</div>
      </div>
      <div style={{background:"var(--err-bg)",borderRadius:12,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:18,fontWeight:700,color:"var(--err-tx)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(totalRefunds)}</div>
        <div style={{fontSize:10,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".04em",marginTop:2}}>Refunds</div>
      </div>
      <div style={{background:net>=0?"var(--gl)":"var(--err-bg)",borderRadius:12,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:18,fontWeight:700,color:net>=0?"var(--g)":"var(--err-tx)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(net)}</div>
        <div style={{fontSize:10,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".04em",marginTop:2}}>Net</div>
      </div>
    </div>
    {/* Filters */}
    <div className="chips">
      {filters.map(f=><div key={f} className={`chip${filter===f?" on":""}`} onClick={()=>setFilter(f)}>{f==="all"?"All":f==="pending"?"Pending":f.charAt(0).toUpperCase()+f.slice(1)+"s"}</div>)}
    </div>
    {/* Transaction list */}
    <div style={{padding:"0 24px 80px"}}>
      {filtered.length===0&&<div style={{textAlign:"center",padding:40,color:"var(--mu)",fontSize:14}}>No transactions found.</div>}
      {filtered.map(t=>(
        <div key={t.id} onClick={()=>onOpen&&onOpen(t.clientId)} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,padding:"12px 14px",marginBottom:6,display:"flex",alignItems:"center",gap:12,cursor:onOpen?"pointer":"default"}}>
          <div style={{width:36,height:36,borderRadius:10,background:t.type==="refund"?"var(--err-bg)":t.col||"var(--gl)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,color:t.type==="refund"?"var(--err-tx)":"#fff",fontWeight:700}}>
            {t.type==="refund"?"\u21A9":t.type==="invoice"?"\u2709":(t.clientName||"?").slice(0,2).toUpperCase()}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.clientName}</div>
            <div style={{fontSize:11,color:"var(--mu)"}}>{t.desc}{t.date?" \u00B7 "+fmtDate(t.date):""}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:14,fontWeight:700,color:t.amount<0?"var(--err-tx)":"var(--g)",fontVariantNumeric:"tabular-nums"}}>{t.amount<0?"-":""}{sym}{fmtMoney(Math.abs(t.amount))}</div>
            <div style={{fontSize:10,color:t.status==="paid"?"var(--ok-tx)":t.status==="refunded"?"var(--err-tx)":"var(--warn-tx)",fontWeight:600,textTransform:"uppercase"}}>{t.status}</div>
          </div>
        </div>
      ))}
    </div>
  </div>);
}

function PayoutsTab({acct,onOpen}){
  const [filter,setFilter]=useState("all"); // all | inhold | paidout | refunded
  const sym=acct.currencySymbol||"$";
  const payments=(acct.payments||[]).filter(p=>p.status==="succeeded"||p.status==="refunded");
  const clientMap=Object.fromEntries((acct.clients||[]).map(c=>[c.id,c]));

  // Totals — refunded rows do NOT contribute to in-hold, paid-out or lifetime net.
  // A refund issued before payout never hits the Stripe balance (money is returned direct);
  // a refund issued post-payout is debited from future payouts, so lifetime earnings drop.
  const live=payments.filter(p=>p.status==="succeeded");
  const refundedList=payments.filter(p=>p.status==="refunded");
  const inHold=live.filter(p=>!p.paidOutAt);
  const paidOut=live.filter(p=>p.paidOutAt);
  const inHoldAmt=inHold.reduce((s,p)=>s+Number(p.amount||0),0);
  const paidOutAmt=paidOut.reduce((s,p)=>s+Number(p.amount||0),0);
  const refundedAmt=refundedList.reduce((s,p)=>s+Number(p.amount||0),0);
  const totalAmt=inHoldAmt+paidOutAmt; // lifetime net (succeeded only)

  // Filtered list
  let filtered=payments;
  if(filter==="inhold") filtered=inHold;
  else if(filter==="paidout") filtered=paidOut;
  else if(filter==="refunded") filtered=refundedList;

  // Group paid out payments by payout batch for clarity
  const payoutGroups={};
  paidOut.forEach(p=>{
    const key=p.stripePayoutId||"unknown";
    if(!payoutGroups[key]){
      payoutGroups[key]={id:key,total:0,count:0,date:p.paidOutAt};
    }
    payoutGroups[key].total+=Number(p.amount||0);
    payoutGroups[key].count+=1;
    if(p.paidOutAt&&(!payoutGroups[key].date||new Date(p.paidOutAt)<new Date(payoutGroups[key].date))) payoutGroups[key].date=p.paidOutAt;
  });
  const payoutBatches=Object.values(payoutGroups).sort((a,b)=>new Date(b.date||0)-new Date(a.date||0));

  // Check if Stripe is connected first
  if(!acct.stripeConnect?.chargesEnabled){
    return(<div style={{padding:"40px 24px",textAlign:"center"}}>
      <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>Stripe not connected</div>
      <div style={{fontSize:12,color:"var(--mu)"}}>Connect Stripe in Billing &amp; Plan to track payouts.</div>
    </div>);
  }

  return(<div style={{paddingBottom:40}}>
    {/* Totals — In hold / Paid out / Lifetime */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
      <div style={{background:"var(--ok-bg)",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:10,color:"var(--ok-tx)",marginBottom:4,textTransform:"uppercase",letterSpacing:".06em",fontWeight:700}}>In hold</div>
        <div className="num" style={{fontSize:16,fontWeight:700,color:"var(--ok-tx)"}}>{sym}{fmtMoney(inHoldAmt)}</div>
        <div style={{fontSize:10,color:"var(--mu)",marginTop:2}}>{inHold.length} pending</div>
      </div>
      <div style={{background:"var(--gl)",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:10,color:"var(--gm)",marginBottom:4,textTransform:"uppercase",letterSpacing:".06em",fontWeight:700}}>Paid out</div>
        <div className="num" style={{fontSize:16,fontWeight:700,color:"var(--g)"}}>{sym}{fmtMoney(paidOutAmt)}</div>
        <div style={{fontSize:10,color:"var(--mu)",marginTop:2}}>{paidOut.length} transferred</div>
      </div>
      <div style={{background:"var(--cr)",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
        <div style={{fontSize:10,color:"var(--mu)",marginBottom:4,textTransform:"uppercase",letterSpacing:".06em",fontWeight:700}}>Lifetime</div>
        <div className="num" style={{fontSize:16,fontWeight:700,color:"var(--ink)"}}>{sym}{fmtMoney(totalAmt)}</div>
        <div style={{fontSize:10,color:refundedAmt>0?"var(--err-tx)":"var(--mu)",marginTop:2}}>{refundedAmt>0?`-${sym}${fmtMoney(refundedAmt)} refunded`:`${live.length} total`}</div>
      </div>
    </div>

    {/* Filters */}
    <div className="chips" style={{marginTop:0,marginBottom:12}}>
      <div className={`chip${filter==="all"?" on":""}`} onClick={()=>setFilter("all")}>All ({payments.length})</div>
      <div className={`chip${filter==="inhold"?" on":""}`} onClick={()=>setFilter("inhold")}>In hold ({inHold.length})</div>
      <div className={`chip${filter==="paidout"?" on":""}`} onClick={()=>setFilter("paidout")}>Paid out ({paidOut.length})</div>
      {refundedList.length>0&&<div className={`chip${filter==="refunded"?" on":""}`} onClick={()=>setFilter("refunded")}>Refunded ({refundedList.length})</div>}
    </div>

    {/* Payout batches summary when viewing Paid out */}
    {filter==="paidout"&&payoutBatches.length>0&&(
      <div style={{marginBottom:16}}>
        <div style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8,fontWeight:600}}>Payout Batches</div>
        {payoutBatches.map(b=>(
          <div key={b.id} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px 12px",marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:34,height:34,borderRadius:8,background:"var(--gl)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"var(--g)",fontWeight:700,fontSize:12}}>PO</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>{b.date?new Date(b.date).toLocaleDateString():"Unknown date"}</div>
              <div style={{fontSize:10,color:"var(--mu)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.count} payment{b.count===1?"":"s"} · {b.id==="unknown"?"Batch unknown":b.id.slice(0,18)+"..."}</div>
            </div>
            <div style={{fontSize:14,fontWeight:700,color:"var(--g)",fontVariantNumeric:"tabular-nums",flexShrink:0}}>{sym}{fmtMoney(b.total)}</div>
          </div>
        ))}
      </div>
    )}

    {/* Payment list */}
    <div style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8,fontWeight:600}}>{filter==="inhold"?"In Hold Payments":filter==="paidout"?"Paid Out Payments":filter==="refunded"?"Refunded Payments":"All Payments"}</div>
    {filtered.length===0&&<div style={{textAlign:"center",padding:30,color:"var(--mu)",fontSize:13}}>{filter==="inhold"?"No payments currently in hold.":filter==="paidout"?"No payments have been paid out yet.":filter==="refunded"?"No refunded payments.":"No Stripe payments yet."}</div>}
    {filtered.map(p=>{
      const cl=clientMap[p.clientId];
      const isRefunded=p.status==="refunded";
      const isHeld=!p.paidOutAt;
      const typeLabel=(p.type||"").charAt(0).toUpperCase()+(p.type||"").slice(1);
      const badgeBg=isRefunded?"var(--err-bg)":isHeld?"var(--ok-bg)":"var(--gl)";
      const badgeCol=isRefunded?"var(--err-tx)":isHeld?"var(--ok-tx)":"var(--gm)";
      const badgeText=isRefunded?"Refunded":isHeld?"In hold":"Paid out "+(p.paidOutAt?new Date(p.paidOutAt).toLocaleDateString():"");
      return(<div key={p.id} onClick={()=>cl&&onOpen&&onOpen(p.clientId)} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,padding:"12px 14px",marginBottom:6,display:"flex",alignItems:"center",gap:12,cursor:cl&&onOpen?"pointer":"default",opacity:isRefunded?0.7:1}}>
        <div style={{width:36,height:36,borderRadius:10,background:cl?.col||"var(--gl)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,color:"#fff",fontWeight:700,overflow:"hidden"}}>
          {cl?.portalPhoto?<img src={cl.portalPhoto} alt={cl.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(cl?.ini||"?")}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cl?.name||"Unknown client"}</div>
          <div style={{fontSize:11,color:"var(--mu)"}}>{typeLabel||"Payment"} · {p.createdAt?new Date(p.createdAt).toLocaleDateString():""}{isRefunded?" · Refunded":""}</div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontSize:14,fontWeight:700,color:isRefunded?"var(--err-tx)":"var(--ink)",fontVariantNumeric:"tabular-nums",textDecoration:isRefunded?"line-through":"none"}}>{sym}{fmtMoney(Number(p.amount||0))}</div>
          <div style={{fontSize:9,fontWeight:600,padding:"2px 6px",borderRadius:4,background:badgeBg,color:badgeCol,display:"inline-block",marginTop:2}}>
            {badgeText}
          </div>
        </div>
      </div>);
    })}
  </div>);
}

function ContractsTab({acct,onContractSave,onContractSend,onContractArchive,onContractDelete,onPdfUpload}){
  const [show,setShow]=useState(false);
  const [viewId,setViewId]=useState(null);
  const [step,setStep]=useState("pick-client");
  const [selectedClientId,setSelectedClientId]=useState(null);
  const [contractText,setContractText]=useState("");
  const [contractStatus,setContractStatus]=useState("idle");
  const [contractEditMode,setContractEditMode]=useState(false);
  const [actionState,setActionState]=useState(null); // null | 'saved' | 'sent'
  const [pdfUploading,setPdfUploading]=useState(false);
  const [pdfUrl,setPdfUrl]=useState(null);
  const [pdfFileName,setPdfFileName]=useState("");
  const [contractFilter,setContractFilter]=useState("active");

  const archivedClientIds=new Set((acct.clients||[]).filter(c=>c.archived).map(c=>c.id));
  const sym=acct.currencySymbol||"$";

  const STAGE_LABELS={
    draft:              {label:"Draft",               bg:"var(--gl)",col:"var(--mu)",     order:0},
    sent:               {label:"Sent",                bg:"var(--warn-bg)",col:"var(--warn-tx)",  order:1},
    awaiting_signature: {label:"Awaiting Signature",  bg:"var(--warn-bg)",col:"var(--warn-tx)",  order:2},
    sent_back:          {label:"Sent Back",           bg:"var(--err-bg)",col:"var(--err-tx)",    order:3},
    signed:             {label:"Signed",              bg:"var(--gl)",col:"var(--gm)",            order:4},
    completed:          {label:"Completed",           bg:"var(--gl)",col:"var(--ok-tx)",         order:5},
  };

  const openSheet=()=>{ setShow(true); setViewId(null); setStep("pick-client"); setSelectedClientId(null); setContractText(""); setContractStatus("idle"); setActionState(null); setPdfUrl(null); setPdfFileName(""); setPdfUploading(false); };
  const closeSheet=()=>{ setShow(false); setViewId(null); };

  const selectedClient=acct.clients.find(c=>c.id===selectedClientId);
  const viewContract=viewId!==null?acct.contracts.find(c=>c.id===viewId):null;
  const viewClient=viewContract?acct.clients.find(c=>c.id===viewContract.client_id):null;
  const [editText,setEditText]=useState("");
  const [viewActionState,setViewActionState]=useState(null);

  const openView=(contractId)=>{
    const c=acct.contracts.find(x=>x.id===contractId);
    const cl=acct.clients.find(x=>x.id===c?.client_id);
    // Always prefer the specific contract's body — cl.aiContract is the LATEST version's text
    setEditText(c?.body||(c&&!c.archived?cl?.aiContract:"")||"");
    setViewId(contractId);
    setViewActionState(null);
    setShow(true);
  };

  const saveDraftFromView=()=>{
    if(!viewClient||!editText.trim()) return;
    onContractSave(viewClient.id,editText);
    setViewActionState("saved");
    setTimeout(()=>setViewActionState(null),2000);
  };

  const hasCancelPolicy=acct.cancellationPolicy?.notice&&acct.cancellationPolicy?.latePolicy;
  const sendFromView=()=>{
    if(!viewClient||!editText.trim()||!hasCancelPolicy) return;
    onContractSend(viewClient.id,editText);
    setViewActionState("sent");
  };

  const saveDraft=()=>{
    if(!selectedClient||!contractText.trim()) return;
    onContractSave(selectedClient.id,contractText);
    setActionState("saved");
    setTimeout(()=>closeSheet(),1200);
  };

  const sendToPortal=()=>{
    if(!selectedClient||!contractText.trim()||!hasCancelPolicy) return;
    onContractSend(selectedClient.id,contractText);
    setActionState("sent");
  };

  const contractLock=useRef(false);
  const generateAiContract=async()=>{
    if(!selectedClient||contractLock.current) return;
    contractLock.current=true;
    setContractStatus("generating");
    setContractText("");
    const c=selectedClient;
    const intake=c.intake||{};
    const questions=(c.questionnaireQuestions&&c.questionnaireQuestions.length>0)?c.questionnaireQuestions:getDefaultQuestionnaire(c.vert||acct.vert);
    const sym=acct.currencySymbol||"$";
    const _contractRaw=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
    const depPct=_contractRaw<=0?100:(c.depositPct||50);
    const depAmt=_contractRaw<=0?0:(c.depositAmt||round2(_contractRaw*depPct/100));
    const balAmt=_contractRaw<=0?0:(c.balanceAmt||round2(_contractRaw*(100-depPct)/100));

    // Build questionnaire section from answered questions
    const answeredQs=questions
      .filter(q=>intake[q.key]?.trim())
      .map(q=>`- ${q.label}: ${intake[q.key]}`)
      .join("\n");

    // Build project timeline section
    const tlSteps=(c.tl||[])
      .map((s,i)=>`${i+1}. ${s.l}${s.d?" — "+fmtDate(s.d):""}`)
      .join("\n");

    const cancelPolicy=acct.cancellationPolicy;
    const cancelPolicyText=cancelPolicy?.notice?`CANCELLATION POLICY (reproduce this verbatim in Section 6):
- Minimum notice required: ${cancelPolicy.notice}
- Deposit (with required notice): ${cancelPolicy.depositRefund?"If the client provides the required notice period, the deposit is fully refundable":"The deposit is non-refundable, even if the client adheres to the required notice period"}
- Late cancellation (inside the required notice period): ${
  cancelPolicy.latePolicy==="no_refund"?"No refund — the full fee, including the deposit, is retained":
  cancelPolicy.latePolicy==="full"?"A full refund, including the deposit, will be issued":
  cancelPolicy.latePolicy==="discretion"?"At freelancer's discretion, assessed case-by-case":
  `A ${cancelPolicy.lateRefundPct||50}% partial refund of the total fee will be issued`
}`:"No specific cancellation policy provided — use professional standard terms.";

    const additionalInvoices=(c.invoices||[]).filter(i=>i&&i.description&&i.amount);
    const invoicesBlock=additionalInvoices.length>0?`

ADDITIONAL INVOICES (include each as a separate line item in Section 3 Payment Terms):
${additionalInvoices.map(inv=>`- ${inv.description}: ${sym}${fmtMoney(inv.amount)} (${inv.paid?"paid":"unpaid"})`).join("\n")}`:"";

    const depPctIsFull=depPct>=100;
    const paymentTermsLine=depPctIsFull
      ?`- Full payment (100%): ${sym}${fmtMoney(depAmt)} — due on signing`
      :`- Deposit (${depPct}%): ${sym}${fmtMoney(depAmt)} — due on signing\n- Balance (${100-depPct}%): ${sym}${fmtMoney(balAmt)} — due on delivery`;

    const prompt=`You are drafting a professional service contract between ${acct.name} (${acct.role||acct.vert}) and their client ${c.name}.

PROJECT DETAILS:
- Project type: ${c.type}
- Total fee: ${c.amt}
${paymentTermsLine}${invoicesBlock}

CLIENT QUESTIONNAIRE ANSWERS:
${answeredQs||"No questionnaire answers provided."}

PROJECT TIMELINE (include all milestones verbatim in Section 4):
${tlSteps||"No timeline milestones set."}${c.freelancerNotes?.trim()?`

FREELANCER'S PRIVATE NOTES (use these to inform specific clauses — do not reproduce verbatim, but reflect any special arrangements, sensitivities or agreed terms):
${c.freelancerNotes.trim()}`:""}

${cancelPolicyText}

Write a complete, professional contract with these exact sections:
1. Parties & Agreement Overview
2. Scope of Services & Deliverables (use questionnaire answers to be specific)
3. Payment Terms (${depPctIsFull?"full payment amount and due date":"deposit amount, due date, balance amount, due date"}${additionalInvoices.length>0?", AND each additional invoice above as a separate line item with description and amount":""} — use exact figures above)
4. Project Timeline & Milestones (reproduce every milestone from the timeline above with its date)
5. Special Arrangements${c.freelancerNotes?.trim()?" (include any specific terms or agreements from the freelancer notes above)":"(if any)"}
6. Cancellation & Rescheduling Policy (use the CANCELLATION POLICY section above exactly — do not soften or change it)
7. Intellectual Property & Usage Rights
8. Limitation of Liability
9. Governing Law (use ${acct.country||"the applicable jurisdiction"})
10. Signatures (write the following EXACTLY, do NOT include sub-sections for Service Provider or Client, do NOT include signature lines or blanks — the system handles signatures automatically:
"By signing this contract, all parties acknowledge and agree to the terms, conditions, and obligations set forth in Sections 1 through 9 above. Electronic signatures collected through this platform are legally binding and carry the same legal effect as handwritten signatures. Signatures will be collected electronically upon sending.")

Be specific — use actual names, dates, and amounts throughout. Bold section headers. Professional language. You MUST complete all 10 sections fully — do not stop early.`;

    // Stream a single request, then auto-continue if the model hit the token limit
    const streamRequest=async(messages,appendToExisting=false)=>{
      const res=await fetch(AI_PROXY_URL,{method:"POST",headers:aiHeaders(),body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:8192,stream:true,messages})});
      if(!res.ok) throw new Error("AI error "+res.status);
      const reader=res.body.getReader(); const dec=new TextDecoder(); let buf=""; let chunkText=""; let stopReason="end_turn";
      while(true){
        const {done,value}=await reader.read(); if(done) break;
        buf+=dec.decode(value,{stream:true});
        const lines=buf.split("\n"); buf=lines.pop()||"";
        for(const line of lines){
          if(line.startsWith("data: ")){
            try{
              const p=JSON.parse(line.slice(6));
              if(p.type==="content_block_delta"&&p.delta?.type==="text_delta"){ chunkText+=p.delta.text; setContractText(t=>t+p.delta.text); }
              if(p.type==="message_delta"&&p.delta?.stop_reason) stopReason=p.delta.stop_reason;
            }catch(e){}
          }
        }
      }
      // Process any remaining data in the buffer
      if(buf.trim().startsWith("data: ")){
        try{
          const p=JSON.parse(buf.trim().slice(6));
          if(p.type==="content_block_delta"&&p.delta?.type==="text_delta"){ chunkText+=p.delta.text; setContractText(t=>t+p.delta.text); }
          if(p.type==="message_delta"&&p.delta?.stop_reason) stopReason=p.delta.stop_reason;
        }catch(e){}
      }
      return {chunkText,stopReason};
    };

    try{
      // Initial generation
      let fullText="";
      const {chunkText,stopReason}=await streamRequest([{role:"user",content:prompt}]);
      fullText=chunkText;

      // Auto-continue if the model was cut off by max_tokens (up to 3 continuations)
      let messages=[{role:"user",content:prompt},{role:"assistant",content:fullText}];
      for(let i=0;i<3&&stopReason==="max_tokens";i++){
        const contMsg={role:"user",content:"Continue writing the contract exactly from where you stopped. Do not repeat any text — pick up mid-sentence if needed. Complete all remaining sections."};
        messages=[...messages,contMsg];
        const cont=await streamRequest(messages,true);
        fullText+=cont.chunkText;
        messages=[...messages.slice(0,-1),{role:"user",content:contMsg.content},{role:"assistant",content:cont.chunkText}];
        if(cont.stopReason!=="max_tokens") break;
      }
      setContractStatus("done");
    }catch(e){
      console.error("[Contract] generation error:",e);
      setContractStatus("error");
    }
    contractLock.current=false;
  };

  // Shared bottom bar for new contract creation steps (ai / scratch)
  const ContractActionBar=({text})=>(
    <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
      <div style={{display:"flex",gap:8}}>
        {actionState!=="sent"&&<button onClick={saveDraft} disabled={!text.trim()} style={{flex:1,background:actionState==="saved"?"var(--ok-tx)":text.trim()?"var(--g)":"var(--bo)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:text.trim()?"pointer":"default",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
          {actionState==="saved"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Saved</>:<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.save}</span> Save Draft</>}
        </button>}
        <button onClick={sendToPortal} disabled={!text.trim()||!hasCancelPolicy} style={{flex:actionState==="sent"?1:2,background:actionState==="sent"?"var(--ok-tx)":text.trim()&&hasCancelPolicy?"var(--t)":"var(--bo)",color:actionState==="sent"?"#fff":text.trim()&&hasCancelPolicy?"var(--tc)":"var(--mu)",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:text.trim()&&hasCancelPolicy?"pointer":"default",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
          {actionState==="sent"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Sent</>:!hasCancelPolicy?"Set cancellation policy first":<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.sendUp}</span> Send to Portal</>}
        </button>
      </div>
      {actionState!=="sent"&&text.trim()&&hasCancelPolicy&&<div style={{fontSize:11,color:"var(--mu)",textAlign:"center",lineHeight:1.5,padding:"0 4px"}}>
        By sending to portal, this contract will be automatically signed on your behalf as the service provider.
      </div>}
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>setStep("pick-method")} style={{flex:1,background:"var(--cr)",color:"var(--mu)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
          {I.arrowLeft} Back
        </button>
        <button onClick={closeSheet} style={{flex:1,background:"var(--cr)",color:"var(--mu)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
          {I.close} Close
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="ttop">
        <div className="ttrow">
          <div className="ttitle">Contracts</div>
          <button onClick={openSheet} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:10,padding:"8px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            ＋ New Contract
          </button>
        </div>
      </div>
      <div className="body">
        {/* Filter chips — Active/Signed/Archive views are grouped 1 card per client,
             so their counts use unique client_ids (Set.size), not raw contract rows.
             Drafts are shown 1 card per contract, so its count uses .length. */}
        <div className="chips" style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {[{k:"active",l:"Active"},{k:"drafts",l:"Drafts"},{k:"signed",l:"Signed"},{k:"archive",l:"Archive"}].map(f=>{
            const uniqueClients=(list)=>new Set(list.map(c=>c.client_id||"unknown")).size;
            const count=f.k==="active"?uniqueClients(acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status!=="draft")):f.k==="drafts"?acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status==="draft").length:f.k==="signed"?uniqueClients(acct.contracts.filter(c=>{if(c.archived||archivedClientIds.has(c.client_id)) return false;const cl=acct.clients.find(x=>x.id===c.client_id);const stage=cl?.contractStage||c.status;return stage==="signed"||c.status==="signed"||c.status==="completed";})):uniqueClients(acct.contracts.filter(c=>c.archived||archivedClientIds.has(c.client_id)));
            return <div key={f.k} className={"chip"+(contractFilter===f.k?" on":"")} onClick={()=>setContractFilter(f.k)} style={{cursor:"pointer"}}>{f.l} ({count})</div>;
          })}
        </div>

        {/* Active contracts — grouped by client, 1 card per client showing latest version */}
        {contractFilter==="active"&&(<>
          <div className="sh"><div className="stitle">Active Contracts</div><div className="slink">{new Set(acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status!=="draft").map(c=>c.client_id||"unknown")).size}</div></div>
          {acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status!=="draft").length===0?(
            <div style={{padding:"40px 20px",textAlign:"center"}}>
              <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.fileText}</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>No active contracts</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6}}>Send a contract to a client to see it here.</div>
            </div>
          ):(()=>{
            const activeList=acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status!=="draft");
            const grouped={};
            activeList.forEach(c=>{const key=c.client_id||"unknown";if(!grouped[key]) grouped[key]=[];grouped[key].push(c);});
            return (<div className="ktlist">{Object.entries(grouped).map(([clientId,contracts])=>{
              const client=acct.clients.find(x=>x.id===clientId);
              contracts.sort((a,b)=>(b.version||1)-(a.version||1));
              const latest=contracts[0];
              const stage=client?.contractStage||latest.status;
              const stageInfo=STAGE_LABELS[stage]||{label:latest.status?latest.status.charAt(0).toUpperCase()+latest.status.slice(1):"Unknown",bg:"var(--gl)",col:"var(--mu)"};
              const hasComments=client?.contractComments;
              const isSigned=stage==="signed"||stage==="completed";
              return (
                <div className="kcard" key={clientId} onClick={()=>openView(latest.id)} style={{cursor:"pointer"}}>
                    <div className="ktop">
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:client?.col||"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                          {client?.portalPhoto?<img src={client.portalPhoto} alt={latest.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(client?.ini||latest.name?.slice(0,2).toUpperCase())}
                        </div>
                        <div>
                          <div className="kn">{latest.name}{contracts.length>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>{contracts.length} versions</span>:(latest.version||1)>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>v{latest.version}</span>:null}</div>
                          <div className="ks">{latest.type}</div>
                        </div>
                      </div>
                      <div style={{padding:"4px 10px",borderRadius:99,fontSize:11,fontWeight:600,background:stageInfo.bg,color:stageInfo.col,whiteSpace:"nowrap"}}>
                        {stageInfo.label}
                      </div>
                    </div>
                    {isSigned&&(
                      <div style={{background:"var(--gl)",borderRadius:8,padding:"6px 10px",margin:"6px 0",display:"flex",alignItems:"center",gap:6}}>
                        <span style={{color:"var(--gm)",display:"inline-flex",alignItems:"center"}}>{I.check}</span>
                        <span style={{fontSize:11,fontWeight:600,color:"var(--gm)",flex:1}}>Signed by {latest.signed_by||client?.signedBy||latest.name}{(latest.signed_at||client?.signedAt)?` · ${fmtDateTime(latest.signed_at||client?.signedAt)}`:""}</span>
                      </div>
                    )}
                    {stage==="awaiting_signature"&&(
                      <div style={{background:"var(--warn-bg)",borderRadius:8,padding:"6px 10px",margin:"6px 0",display:"flex",alignItems:"center",gap:6}}>
                        <span style={{display:"inline-flex"}}>{I.hourglass}</span>
                        <span style={{fontSize:11,fontWeight:600,color:"var(--warn-tx)"}}>Pending Signature</span>
                      </div>
                    )}
                    <div className="kmeta">
                      <div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{latest.val}</div>
                      {(latest.sent_at||latest.date)&&<div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{I.sendUp}Sent {fmtDateTime(latest.sent_at||latest.date)}</div>}
                      {hasComments&&<div className="kmi" style={{color:"var(--err-tx)",display:"flex",alignItems:"center",gap:4}}>{I.chatBubble}Sent back</div>}
                    </div>
                    <div style={{marginTop:6,fontSize:11,color:"var(--mu)",display:"flex",alignItems:"center",gap:4}}>Tap to view {I.arrow}</div>
                  </div>
              );
            })}</div>);
          })()}
        </>)}

        {/* Draft contracts */}
        {contractFilter==="drafts"&&(<>
          <div className="sh"><div className="stitle">Drafts</div><div className="slink">{acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status==="draft").length}</div></div>
          <div className="ktlist">
            {acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status==="draft").length===0&&(
              <div style={{padding:"40px 20px",textAlign:"center"}}>
                <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.edit}</div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>No drafts</div>
                <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6}}>Create a new contract to get started.</div>
              </div>
            )}
            {acct.contracts.filter(c=>!c.archived&&!archivedClientIds.has(c.client_id)&&c.status==="draft").map(c=>{
              const client=acct.clients.find(x=>x.id===c.client_id);
              return (
                <div className="kcard" key={c.id} onClick={()=>openView(c.id)} style={{cursor:"pointer"}}>
                  <div className="ktop">
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:32,height:32,borderRadius:"50%",background:client?.col||"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                        {client?.portalPhoto?<img src={client.portalPhoto} alt={c.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(client?.ini||c.name?.slice(0,2).toUpperCase())}
                      </div>
                      <div>
                        <div className="kn">{c.name}{(c.version||1)>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>v{c.version}</span>:null}</div>
                        <div className="ks">{c.type}</div>
                      </div>
                    </div>
                    <div style={{padding:"4px 10px",borderRadius:99,fontSize:11,fontWeight:600,background:"var(--gl)",color:"var(--mu)"}}>Draft</div>
                  </div>
                  <div className="kmeta">
                    <div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{c.val}</div>
                    <div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{I.edit}Created {fmtDate(c.date||c.created_at)}</div>
                  </div>
                  <div style={{marginTop:6,fontSize:11,color:"var(--mu)",display:"flex",alignItems:"center",gap:4}}>Tap to edit {I.arrow}</div>
                </div>
              );
            })}
          </div>
        </>)}

        {/* Signed contracts — exclude archived contracts AND archived clients */}
        {contractFilter==="signed"&&(<>
          <div className="sh"><div className="stitle">Signed Contracts</div><div className="slink">{new Set(acct.contracts.filter(c=>{if(c.archived||archivedClientIds.has(c.client_id)) return false;const cl=acct.clients.find(x=>x.id===c.client_id);const stage=cl?.contractStage||c.status;return stage==="signed"||c.status==="signed"||c.status==="completed";}).map(c=>c.client_id||"unknown")).size}</div></div>
          {acct.contracts.filter(c=>{if(c.archived||archivedClientIds.has(c.client_id)) return false;const cl=acct.clients.find(x=>x.id===c.client_id);const stage=cl?.contractStage||c.status;return stage==="signed"||c.status==="signed"||c.status==="completed";}).length===0?(
            <div style={{padding:"40px 20px",textAlign:"center"}}>
              <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.sign}</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>No signed contracts yet</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6}}>Signed contracts appear here as a permanent record.</div>
            </div>
          ):(()=>{
            const signedList=acct.contracts.filter(c=>{if(c.archived||archivedClientIds.has(c.client_id)) return false;const cl=acct.clients.find(x=>x.id===c.client_id);const stage=cl?.contractStage||c.status;return stage==="signed"||c.status==="signed"||c.status==="completed";});
            const grouped={};
            signedList.forEach(c=>{const key=c.client_id||"unknown";if(!grouped[key]) grouped[key]=[];grouped[key].push(c);});
            return (<div className="ktlist">{Object.entries(grouped).map(([clientId,contracts])=>{
              const client=acct.clients.find(x=>x.id===clientId);
              contracts.sort((a,b)=>(b.version||1)-(a.version||1));
              const latest=contracts[0];
              return (
                <div className="kcard" key={clientId} onClick={()=>openView(latest.id)} style={{cursor:"pointer"}}>
                    <div className="ktop">
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:client?.col||"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                          {client?.portalPhoto?<img src={client.portalPhoto} alt={latest.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(client?.ini||latest.name?.slice(0,2).toUpperCase())}
                        </div>
                        <div>
                          <div className="kn">{latest.name}{contracts.length>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>{contracts.length} versions</span>:(latest.version||1)>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>v{latest.version}</span>:null}</div>
                          <div className="ks">{latest.type}</div>
                        </div>
                      </div>
                      <div style={{padding:"4px 10px",borderRadius:99,fontSize:11,fontWeight:600,background:"var(--gl)",color:"var(--gm)"}}>
                        {latest.status==="completed"?"Completed":"Signed"}
                      </div>
                    </div>
                    <div style={{background:"var(--gl)",borderRadius:8,padding:"6px 10px",margin:"6px 0",display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:"var(--gm)",display:"inline-flex",alignItems:"center"}}>{I.check}</span>
                      <span style={{fontSize:11,fontWeight:600,color:"var(--gm)",flex:1}}>Signed by {latest.signed_by||client?.signedBy||latest.name}{(latest.signed_at||client?.signedAt)?` · ${fmtDateTime(latest.signed_at||client?.signedAt)}`:""}</span>
                    </div>
                    <div className="kmeta">
                      <div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{latest.val}</div>
                      {(latest.sent_at||latest.date)&&<div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{I.sendUp}Sent {fmtDateTime(latest.sent_at||latest.date)}</div>}
                    </div>
                    <div style={{marginTop:6,fontSize:11,color:"var(--mu)",display:"flex",alignItems:"center",gap:4}}>Tap to view {I.arrow}</div>
                  </div>
              );
            })}</div>);
          })()}
        </>)}

        {/* Archived contracts — 1 card per client, latest version shown */}
        {contractFilter==="archive"&&(<>
          <div className="sh"><div className="stitle" style={{color:"var(--mu)"}}>Archived Contracts</div><div className="slink">{(()=>{const seen=new Set();acct.contracts.filter(c=>c.archived||archivedClientIds.has(c.client_id)).forEach(c=>seen.add(c.client_id||"unknown"));return seen.size;})()}</div></div>
          {acct.contracts.filter(c=>c.archived||archivedClientIds.has(c.client_id)).length===0?(
            <div style={{padding:"40px 20px",textAlign:"center"}}>
              <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.archive}</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>No archived contracts</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6}}>When contracts are replaced or archived, they appear here.</div>
            </div>
          ):(()=>{
            const grouped={};
            acct.contracts.filter(c=>c.archived||archivedClientIds.has(c.client_id)).forEach(c=>{
              const key=c.client_id||"unknown";
              if(!grouped[key]) grouped[key]=[];
              grouped[key].push(c);
            });
            return (<div className="ktlist">{Object.entries(grouped).map(([clientId,contracts])=>{
              const client=acct.clients.find(x=>x.id===clientId);
              contracts.sort((a,b)=>(b.version||1)-(a.version||1));
              const latest=contracts[0];
              const latestSigned=latest.status==="signed"||latest.status==="completed";
              const archivedAt=contracts.reduce((max,c)=>c.archived_at&&(!max||c.archived_at>max)?c.archived_at:max,null);
              return (
                <div className="kcard" key={clientId} onClick={()=>openView(latest.id)} style={{cursor:"pointer",opacity:0.85}}>
                    <div className="ktop">
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:client?.col||"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                          {client?.portalPhoto?<img src={client.portalPhoto} alt={client?.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(client?.ini||"?")}
                        </div>
                        <div>
                          <div className="kn">{latest.name}{contracts.length>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>{contracts.length} versions</span>:(latest.version||1)>1?<span style={{fontSize:10,color:"var(--mu)",marginLeft:4}}>v{latest.version}</span>:null}</div>
                          <div className="ks">{latest.type}</div>
                        </div>
                      </div>
                      <div style={{padding:"4px 10px",borderRadius:99,fontSize:11,fontWeight:600,background:latestSigned?"var(--gl)":"#f5f5f5",color:latestSigned?"var(--gm)":"var(--mu)",display:"inline-flex",alignItems:"center",gap:4}}>
                        {latestSigned?"Signed":<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.archive}</span> Archived</>}
                      </div>
                    </div>
                    {latestSigned&&(
                      <div style={{background:"var(--gl)",borderRadius:8,padding:"6px 10px",margin:"6px 0",display:"flex",alignItems:"center",gap:6}}>
                        <span style={{color:"var(--gm)",display:"inline-flex",alignItems:"center"}}>{I.check}</span>
                        <span style={{fontSize:11,fontWeight:600,color:"var(--gm)",flex:1}}>Signed by {latest.signed_by||latest.name}{latest.signed_at?` · ${fmtDateTime(latest.signed_at)}`:""}</span>
                      </div>
                    )}
                    <div className="kmeta">
                      <div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{latest.val}</div>
                      {(latest.sent_at||latest.date)&&<div className="kmi" style={{display:"flex",alignItems:"center",gap:4}}>{I.sendUp}Sent {fmtDateTime(latest.sent_at||latest.date)}</div>}
                      {archivedAt&&<div className="kmi"><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.archive}</span> Archived {fmtDate(archivedAt)}</div>}
                    </div>
                    <div style={{marginTop:6,fontSize:11,color:"var(--mu)",display:"flex",alignItems:"center",gap:4}}>Tap to view {I.arrow}</div>
                  </div>
              );
            })}</div>);
          })()}
        </>)}
      </div>

      {show&&(
        <BottomSheet onClose={closeSheet} title={viewId!==null&&viewContract?viewContract.name:step==="pick-client"?"New Contract":step==="pick-method"?`${selectedClient?.name||"Contract"}`:step==="ai"||step==="ai-gen"?"AI Contract":step==="upload-pdf"?"Upload PDF":"Write Contract"}>
            {/* ── VIEW EXISTING CONTRACT ── */}
            {viewId!==null&&viewContract&&(()=>{
              // Use the viewed contract's own status — NOT the client's current contractStage
              // (which reflects the newest contract, not the one being viewed)
              const isCurrentContract=!viewContract.archived&&viewContract.status!=="archived";
              const stage=isCurrentContract?(viewClient?.contractStage||viewContract.status):viewContract.status;
              const stageInfo=STAGE_LABELS[stage]||{label:stage,bg:"var(--gl)",col:"var(--mu)"};
              const isDraft=stage==="draft";
              const isSentBack=stage==="sent_back";
              const isSigned=stage==="signed"||stage==="completed";
              const isAwaiting=stage==="awaiting_signature";
              const isCompleted=stage==="completed";
              const FLOW_STEPS=["draft","awaiting_signature","signed","completed"];
              const currentIdx=FLOW_STEPS.indexOf(stage==="sent_back"?"awaiting_signature":stage==="sent"?"draft":stage);
              // Legacy pre-fix PDFs share a non-timestamped storage path per client.
              // When a newer PDF was uploaded it overwrote this one's backing file,
              // so download/view must be disabled with an explanatory banner.
              const viewSiblingContracts=(acct.contracts||[]).filter(x=>x.client_id===viewContract.client_id);
              const viewPdfLost=isLegacyLostPdf(viewContract,viewSiblingContracts);
              return (
                <>
                  {/* Status flow indicator */}
                  <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:14,padding:"8px 0"}}>
                    {FLOW_STEPS.map((s,i)=>{
                      const lbl={draft:"Draft",awaiting_signature:"Sent",signed:"Signed",completed:"Complete"}[s];
                      const active=i<=currentIdx;
                      return <React.Fragment key={s}>
                        {i>0&&<div style={{flex:1,height:2,background:active?"var(--gm)":"var(--bo)",borderRadius:1}}/>}
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                          <div style={{width:20,height:20,borderRadius:"50%",background:active?"var(--gm)":"var(--bo)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:active?"#fff":"var(--mu)",fontWeight:700}}>{i<currentIdx?<span style={{display:"inline-flex",alignItems:"center",transform:"scale(.75)"}}>{I.check}</span>:i+1}</div>
                          <div style={{fontSize:9,color:active?"var(--ink)":"var(--mu)",fontWeight:active?600:400,whiteSpace:"nowrap"}}>{lbl}</div>
                        </div>
                      </React.Fragment>;
                    })}
                  </div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:36,height:36,borderRadius:"50%",background:viewClient?.col||"var(--g)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                        {viewClient?.portalPhoto?<img src={viewClient.portalPhoto} alt={viewContract.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(viewClient?.ini||viewContract.name?.slice(0,2).toUpperCase())}
                      </div>
                      <div className="tmsb" style={{margin:0}}>{viewContract.type} · {viewContract.val}</div>
                    </div>
                    <div style={{padding:"4px 12px",borderRadius:99,fontSize:11,fontWeight:600,background:stageInfo.bg,color:stageInfo.col}}>{stageInfo.label}</div>
                  </div>

                  {/* Signature requirement notice */}
                  {isAwaiting&&(
                    <div style={{background:"var(--info-bg)",borderRadius:10,padding:10,marginBottom:10,border:"1px solid var(--info-bo)",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{display:"flex"}}>{I.sign}</span>
                      <span style={{fontSize:12,color:"var(--info-tx)",fontWeight:500}}>Client signature required before this contract is valid</span>
                    </div>
                  )}

                  {/* Sent back banner */}
                  {isSentBack&&viewClient?.contractComments&&(
                    <div style={{background:"var(--err-bg)",borderRadius:12,padding:14,marginBottom:14,border:"1.5px solid var(--err-bo)"}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--err-tx)",fontWeight:600,marginBottom:6}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.chatBubble}</span> Client sent back with comments</div>
                      <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.6,marginBottom:10}}>{viewClient.contractComments}</div>
                      <div style={{fontSize:11,color:"var(--mu)"}}>Edit the contract below, then resend.</div>
                    </div>
                  )}

                  {isSigned&&(
                    <div style={{background:"var(--gl)",borderRadius:12,padding:12,marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
                      <span style={{color:"var(--gm)",display:"flex"}}>{I.check}</span>
                      <span style={{fontSize:13,fontWeight:600,color:"var(--gm)",flex:1}}>Signed by {viewContract.signed_by||viewClient?.signedBy||viewContract.name}{(viewContract.signed_at||viewClient?.signedAt)?` on ${fmtDateTime(viewContract.signed_at||viewClient?.signedAt)}`:""}</span>
                      {viewPdfLost?(
                        <span title="PDF unavailable — file was overwritten by a later upload" style={{background:"var(--warn-bg)",color:"var(--warn-tx)",border:"1px solid var(--warn-bo)",borderRadius:8,padding:"5px 10px",fontSize:10,fontWeight:600,fontFamily:"'Inter',sans-serif",flexShrink:0}}>Unavailable</span>
                      ):(
                      <button onClick={(e)=>{
                        e.stopPropagation();
                        // Resolve this contract's own PDF URL (embedded in body marker).
                        // Falls back to client-level URL only for legacy pre-fix contracts.
                        const pdfUrl=getContractPdfUrl(viewContract,viewClient);
                        if(pdfUrl){
                          const a=document.createElement("a"); a.href=pdfUrl; a.download=`${viewContract.name.replace(/\s+/g,"-")}-signed-contract.pdf`; a.target="_blank"; a.click();
                        } else {
                          downloadContractAsPdf(viewContract?.body||viewClient?.signedContract||viewClient?.aiContract||"",`${viewContract.name.replace(/\s+/g,"-")}-signed-contract`);
                        }
                      }} style={{background:"var(--gm)",color:"#fff",border:"none",borderRadius:8,padding:"5px 10px",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                        <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.download}</span> PDF
                      </button>
                      )}
                    </div>
                  )}
                  {viewPdfLost&&(
                    <div style={{background:"var(--warn-bg)",border:"1px solid var(--warn-bo)",borderRadius:10,padding:"10px 12px",marginBottom:14,display:"flex",gap:10,alignItems:"flex-start"}}>
                      <span style={{color:"var(--warn-tx)",display:"flex",flexShrink:0,marginTop:1}}>{I.alertTri}</span>
                      <div style={{fontSize:12,color:"var(--warn-tx)",lineHeight:1.5}}>
                        <strong>PDF unavailable for this version.</strong> This contract predates per-version PDF storage, and its file was overwritten when a newer PDF was uploaded for the same client. The signature metadata is preserved, but the original PDF can no longer be viewed or downloaded.
                      </div>
                    </div>
                  )}

                  {isAwaiting&&(
                    <div style={{background:"var(--warn-bg)",borderRadius:12,padding:12,marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
                      <span style={{display:"inline-flex"}}>{I.hourglass}</span>
                      <span style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>Awaiting signature from {viewContract.name}</span>
                    </div>
                  )}

                  {/* Contract body — editable if draft or sent_back */}
                  {(isDraft||isSentBack)?(
                    <>
                      <div style={{fontSize:11,color:"var(--mu)",marginBottom:8}}>{isDraft?"Draft — edit freely. Save to keep as draft, or send to the client portal.":"Edit in response to client comments, then resend."}</div>
                      {viewPdfLost?(
                        <div style={{background:"var(--warn-bg)",borderRadius:12,padding:16,marginBottom:12,display:"flex",alignItems:"center",gap:12,border:"1px solid var(--warn-bo)"}}>
                          <span style={{display:"flex",color:"var(--warn-tx)"}}>{I.fileText}</span>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>PDF Contract — Unavailable</div>
                            <div style={{fontSize:11,color:"var(--warn-tx)",opacity:.85}}>This version's file was overwritten by a later upload.</div>
                          </div>
                        </div>
                      ):isPdfContract(viewContract)&&getContractPdfUrl(viewContract,viewClient)?(
                        <div style={{background:"var(--gl)",borderRadius:12,padding:16,marginBottom:12,display:"flex",alignItems:"center",gap:12}}>
                          <span style={{display:"flex",color:"var(--gm)"}}>{I.fileText}</span>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>PDF Contract</div>
                            <div style={{fontSize:11,color:"var(--mu)"}}>Uploaded PDF document</div>
                          </div>
                          <button onClick={()=>{const u=getContractPdfUrl(viewContract,viewClient);if(u) window.open(u,"_blank");}} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>View</button>
                        </div>
                      ):(
                        <textarea
                          value={editText}
                          onChange={e=>setEditText(e.target.value)}
                          placeholder="Contract text…"
                          style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:14,fontSize:12,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.7,minHeight:300,marginBottom:12}}
                        />
                      )}
                      <div style={{display:"flex",gap:8,marginBottom:8}}>
                        <button onClick={closeSheet} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>{I.close} Close</button>
                        {isDraft&&<button onClick={saveDraftFromView} style={{flex:1,background:viewActionState==="saved"?"var(--ok-tx)":"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                          {viewActionState==="saved"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Saved</>:<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.save}</span> Save Draft</>}
                        </button>}
                        <button onClick={sendFromView} disabled={!hasCancelPolicy} style={{flex:2,background:viewActionState==="sent"?"var(--ok-tx)":hasCancelPolicy?"var(--t)":"var(--bo)",color:viewActionState==="sent"?"#fff":hasCancelPolicy?"var(--tc)":"var(--mu)",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:hasCancelPolicy?"pointer":"default",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                          {viewActionState==="sent"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Sent</>:!hasCancelPolicy?"Set cancellation policy first":<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.sendUp}</span> Send to Portal</>}
                        </button>
                      </div>
                      {isDraft&&(
                        <button onClick={()=>{if(window.confirm("Delete this draft contract? This cannot be undone.")){onContractDelete(viewContract.id);closeSheet();}}}
                          style={{width:"100%",background:"var(--err-bg)",color:"var(--err-tx)",border:"1.5px solid var(--err-bo)",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",marginBottom:8}}>
                          <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.trash}</span> Delete Draft
                        </button>
                      )}
                      {viewActionState==="sent"&&<div style={{fontSize:12,color:"var(--ok-tx)",textAlign:"center",fontWeight:600,marginBottom:8}}>Contract sent — now awaiting signature.</div>}
                    </>
                  ):(
                    <>
                      {viewPdfLost?(
                        <div style={{background:"var(--warn-bg)",borderRadius:12,padding:16,marginBottom:12,display:"flex",alignItems:"center",gap:12,border:"1px solid var(--warn-bo)"}}>
                          <span style={{display:"flex",color:"var(--warn-tx)"}}>{I.fileText}</span>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>PDF Contract — Unavailable</div>
                            <div style={{fontSize:11,color:"var(--warn-tx)",opacity:.85}}>This version's file was overwritten by a later PDF upload for the same client.</div>
                          </div>
                        </div>
                      ):isPdfContract(viewContract)&&getContractPdfUrl(viewContract,viewClient)?(
                        <div style={{background:"var(--gl)",borderRadius:12,padding:16,marginBottom:12,display:"flex",alignItems:"center",gap:12}}>
                          <span style={{display:"flex",color:"var(--gm)"}}>{I.fileText}</span>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>PDF Contract</div>
                            <div style={{fontSize:11,color:"var(--mu)"}}>Uploaded PDF document</div>
                          </div>
                          <button onClick={()=>{const u=getContractPdfUrl(viewContract,viewClient);if(u) window.open(u,"_blank");}} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>View</button>
                        </div>
                      ):(
                        <ContractDisplay text={viewContract?.body||viewClient?.signedContract||viewClient?.aiContract||"No contract text on file."} maxHeight={null}/>
                      )}
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={closeSheet} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>✕ Close</button>
                        {viewPdfLost?(
                          <div style={{flex:1,background:"var(--warn-bg)",color:"var(--warn-tx)",border:"1px solid var(--warn-bo)",borderRadius:12,padding:"12px",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                            PDF unavailable
                          </div>
                        ):(
                        <button onClick={()=>{
                          const pdfUrl=getContractPdfUrl(viewContract,viewClient);
                          if(pdfUrl){
                            const a=document.createElement("a");
                            a.href=pdfUrl; a.download=`${viewContract.name.replace(/\s+/g,"-")}-contract.pdf`; a.target="_blank"; a.click();
                          } else {
                            downloadContractAsPdf(viewContract?.body||viewClient?.signedContract||viewClient?.aiContract||"Contract not available.",`${viewContract.name.replace(/\s+/g,"-")}-contract`);
                          }
                        }}
                          style={{flex:1,background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.download}</span> Download PDF
                        </button>
                        )}
                        {/* Complete button removed — contracts complete via client workflow */}
                        {(isSigned||isCompleted)&&!viewContract.archived&&(
                          <button onClick={()=>{onContractArchive(viewContract.id);closeSheet();}}
                            style={{flex:1,background:"var(--gl)",color:"var(--mu)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                            <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.archive}</span> Archive
                          </button>
                        )}
                        {viewContract.archived&&(
                          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"var(--mu)"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.archive}</span> Archived</div>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── VERSION HISTORY ── */}
                  {(()=>{
                    const clientContracts=acct.contracts.filter(c=>c.client_id===viewContract.client_id&&c.id!==viewContract.id).sort((a,b)=>(b.version||1)-(a.version||1));
                    if(clientContracts.length===0) return null;
                    return (
                      <div style={{marginTop:16}}>
                        <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:10}}>Contract History</div>
                        {clientContracts.map(c=>{
                          const cSigned=c.status==="signed"||c.status==="completed";
                          return (
                            <div key={c.id} onClick={()=>openView(c.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,border:"1px solid var(--bo)",marginBottom:6,cursor:"pointer",background:c.archived?"var(--cr)":"var(--card)"}}>
                              <div style={{display:"flex",color:"var(--mu)"}}>{cSigned?I.sign:c.status==="draft"?I.edit:c.archived?I.archive:I.sendUp}</div>
                              <div style={{flex:1}}>
                                <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>v{c.version||1} · {c.status==="draft"?"Draft":cSigned?"Signed":c.status==="awaiting_signature"?"Awaiting Signature":"Sent"}</div>
                                <div style={{fontSize:11,color:"var(--mu)"}}>
                                  {cSigned&&c.signed_at?`Signed ${fmtDateTime(c.signed_at)}`:(c.sent_at||c.date)?`Sent ${fmtDateTime(c.sent_at||c.date)}`:`Created ${fmtDate(c.created_at)}`}
                                  {c.archived?" · Archived":""}
                                </div>
                              </div>
                              {cSigned&&<div style={{fontSize:10,color:"var(--gm)",fontWeight:600,display:"flex",alignItems:"center",gap:3}}>{I.check} Signed</div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* ── AUDIT TRAIL ── */}
                  {(()=>{
                    const logs=(acct.auditLog||[]).filter(l=>l.contractId===viewContract.id||l.clientId===viewContract.client_id);
                    if(logs.length===0) return null;
                    const AUDIT_ICONS={created:I.edit,draft_saved:I.save,sent:I.sendUp,resubmitted:I.refresh,sent_back:I.chatBubble,signed:I.sign,completed:I.checkCircle,archived:I.archive,deleted:I.trash};
                    return (
                      <div style={{marginTop:16}}>
                        <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:10}}>Audit Trail</div>
                        <div style={{borderLeft:"2px solid var(--bo)",marginLeft:8,paddingLeft:14}}>
                          {logs.slice(0,20).map((l,i)=>{
                            return (
                              <div key={l.id||i} style={{marginBottom:12,position:"relative"}}>
                                <div style={{position:"absolute",left:-22,top:2,width:14,height:14,borderRadius:"50%",background:"var(--card)",border:"2px solid var(--bo)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8}}>{AUDIT_ICONS[l.action]||"•"}</div>
                                <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>{l.detail}</div>
                                <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>
                                  {fmtDateTime(l.createdAt)} · {l.actor==="client"?"Client":"You"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            })()}

            {/* ── NEW CONTRACT FLOW ── */}
            {viewId===null&&<>
              {step==="pick-client"&&(
                <>
                  <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                    {acct.clients.filter(c=>!c.archived).map(c=>(
                      <div key={c.id} onClick={()=>{ setSelectedClientId(c.id); setStep("pick-method"); }}
                        style={{display:"flex",alignItems:"center",gap:12,padding:"13px 14px",background:selectedClientId===c.id?"var(--gl)":"var(--card)",borderRadius:12,border:"1.5px solid "+(selectedClientId===c.id?"var(--gm)":"var(--bo)"),cursor:"pointer"}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:c.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>{c.portalPhoto?<img src={c.portalPhoto} alt={c.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:c.ini}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{c.name}</div>
                          <div style={{fontSize:11,color:"var(--mu)"}}>{c.type}</div>
                        </div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--gm)"}}>{c.amt}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {step==="pick-method"&&selectedClient&&(()=>{
                const hasQ=selectedClient.intakeSubmitted&&Object.keys(selectedClient.intake||{}).some(k=>(selectedClient.intake[k]||"").trim());
                const tlSteps=(selectedClient.tl||[]);
                const hasTl=tlSteps.length>1;
                const hasTlDates=hasTl&&tlSteps.filter(s=>!/inquiry|deposit|balance|final payment/i.test(s.l)).every(s=>s.d&&s.d.trim());
                const canAiPlan=acct.billingPlan!=="free"; const canAi=hasQ&&hasTl&&hasTlDates&&canAiPlan&&hasCancelPolicy;
                const missing=[...(!hasQ?["questionnaire"]:[]),((!hasTl)?["project timeline"]:!hasTlDates?["timeline dates"]:[]),(!hasCancelPolicy?["cancellation policy"]:[])].flat();
                return(
                <>
                  <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
                    <div onClick={canAi?()=>{ setStep("ai"); generateAiContract(); }:undefined}
                      style={{background:canAi?"var(--g)":"#EEF0EE",borderRadius:14,padding:20,cursor:canAi?"pointer":"default",position:"relative",overflow:"hidden"}}>
                      <div style={{position:"absolute",top:-20,right:-20,width:80,height:80,borderRadius:"50%",background:"rgba(156,203,187,.15)"}}/>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:canAi?"var(--t)":"#999",fontWeight:600,marginBottom:6}}>✦ Recommended</div>
                      <div style={{fontSize:16,fontWeight:600,color:canAi?"#fff":"#555",marginBottom:4}}>AI Draft</div>
                      <div style={{fontSize:12,color:canAi?"rgba(255,255,255,.6)":"#888",lineHeight:1.5}}>
                        {!canAiPlan?"Upgrade to Pro to unlock AI contract drafting.":canAi?`Claude drafts a personalised contract using ${selectedClient.name.split(" ")[0]}'s questionnaire answers and project timeline milestones.`:`Needs ${missing.join(" and ")} before AI can generate.`}
                      </div>
                      {!canAi&&<div style={{marginTop:10,display:"flex",flexDirection:"column",gap:4}}>
                        <div style={{fontSize:11,color:hasQ?"var(--ok-tx)":"var(--err-tx)",display:"flex",alignItems:"center",gap:4}}>{hasQ?I.check:I.close} Questionnaire submitted by client</div>
                        <div style={{fontSize:11,color:hasTl?"var(--ok-tx)":"var(--err-tx)",display:"flex",alignItems:"center",gap:4}}>{hasTl?I.check:I.close} Project timeline created</div>
                        {hasTl&&<div style={{fontSize:11,color:hasTlDates?"var(--ok-tx)":"var(--err-tx)",display:"flex",alignItems:"center",gap:4}}>{hasTlDates?I.check:I.close} Timeline dates selected</div>}
                        <div style={{fontSize:11,color:hasCancelPolicy?"var(--ok-tx)":"var(--err-tx)",display:"flex",alignItems:"center",gap:4}}>{hasCancelPolicy?I.check:I.close} Cancellation policy set (Settings)</div>
                      </div>}
                    </div>
                    <div onClick={()=>{
                      const cp=acct.cancellationPolicy;
                      const cpSection=cp?.notice?`\n\n3. Cancellation Policy\n   - Minimum notice required: ${cp.notice}\n   - Deposit (with required notice): ${cp.depositRefund?"If the client provides the required notice period, the deposit is fully refundable":"The deposit is non-refundable, even if the client adheres to the required notice period"}\n   - Late cancellation (inside the required notice period): ${
                        cp.latePolicy==="no_refund"?"No refund — the full fee, including the deposit, is retained":
                        cp.latePolicy==="full"?"A full refund, including the deposit, will be issued":
                        cp.latePolicy==="discretion"?"At freelancer's discretion, assessed case-by-case":
                        `A ${cp.lateRefundPct||50}% partial refund of the total fee will be issued`
                      }`:"";
                      const sc=selectedClient;
                      const template=`${sc?.type?.split("·")[0]?.trim()||"Service"} Agreement\n\nBetween ${acct.name} and ${sc?.name}\n\n1. Services\n\n2. Payment Terms\n   Total: ${sc?.amt||"TBD"}${cpSection}\n\n${cp?.notice?"4":"3"}. Delivery\n\n${cp?.notice?"5":"4"}. Rights & Usage\n\n${cp?.notice?"6":"5"}. Signatures\n\n___________________________\n${acct.name}\n\n___________________________\n${sc?.name}`;
                      setStep("scratch"); setContractText(template);
                    }} style={{background:"var(--card)",borderRadius:14,padding:20,cursor:"pointer",border:"1.5px solid var(--bo)"}}>
                      <div style={{fontSize:16,fontWeight:600,color:"var(--ink)",marginBottom:4}}>Write from Scratch</div>
                      <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Start with a blank contract and write your own terms. Always available.</div>
                    </div>
                    <div onClick={()=>setStep("upload-pdf")} style={{background:"var(--card)",borderRadius:14,padding:20,cursor:"pointer",border:"1.5px solid var(--bo)"}}>
                      <div style={{fontSize:16,fontWeight:600,color:"var(--ink)",marginBottom:4}}>📎 Upload PDF</div>
                      <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Upload an existing contract as a PDF file to send to your client portal.</div>
                    </div>
                  </div>
                  <button onClick={()=>setStep("pick-client")} style={{width:"100%",background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>{I.arrowLeft} Back</button>
                </>
                );
              })()}

              {step==="ai"&&(
                <>
                  {contractStatus==="generating"&&!contractText&&(
                    <div style={{padding:"20px 0",display:"flex",alignItems:"center",gap:10}}>
                      <div style={{display:"flex",gap:4}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:"var(--g)",animation:`pulse 1.2s ease-in-out ${i*.2}s infinite`}}/>)}</div>
                      <span style={{fontSize:13,color:"var(--mu)"}}>Drafting your contract…</span>
                      <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
                    </div>
                  )}
                  {contractText&&(
                    <>
                      <div style={{display:"flex",gap:8,marginBottom:10}}>
                        <button onClick={()=>setContractEditMode(false)} style={{flex:1,background:!contractEditMode?"var(--g)":"var(--gl)",color:!contractEditMode?"#fff":"var(--g)",border:"none",borderRadius:8,padding:"8px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Preview</button>
                        <button onClick={()=>setContractEditMode(true)} style={{flex:1,background:contractEditMode?"var(--g)":"var(--gl)",color:contractEditMode?"#fff":"var(--gm)",border:"none",borderRadius:8,padding:"8px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Edit</button>
                        <button onClick={()=>{ contractLock.current=false; setContractEditMode(false); setContractText(""); setContractStatus("idle"); generateAiContract(); }} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"8px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>↻ Regenerate</button>
                      </div>
                      {contractEditMode?(
                        <textarea value={contractText} onChange={e=>setContractText(e.target.value)}
                          style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:14,fontSize:12,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.7,minHeight:260,marginBottom:12}}
                        />
                      ):(
                        <ContractDisplay text={contractText} maxHeight={null}/>
                      )}
                    </>
                  )}
                  {contractStatus==="error"&&(
                    <div style={{padding:"16px 0",textAlign:"center"}}>
                      <div style={{fontSize:13,color:"var(--t)",marginBottom:12}}>Generation failed.</div>
                      <button onClick={generateAiContract} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px 20px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Try again</button>
                    </div>
                  )}
                  <ContractActionBar text={contractText}/>
                  {actionState==="sent"&&<div style={{fontSize:12,color:"var(--ok-tx)",textAlign:"center",fontWeight:600,marginBottom:8}}>Sent — now awaiting signature from {selectedClient?.name}.</div>}
                </>
              )}

              {step==="scratch"&&(
                <>
                  <div className="tmtl">Write Contract</div>
                  <div className="tmsb">For {selectedClient?.name}</div>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:10,lineHeight:1.5}}>Tip: cover Services, Payment Terms, Cancellation, Delivery, Rights, and Signatures.</div>
                  <textarea
                    autoFocus
                    value={contractText}
                    onChange={e=>setContractText(e.target.value)}
                    placeholder="Start writing your contract…"
                    style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:14,fontSize:12,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.7,minHeight:300,marginBottom:12}}
                  />
                  <ContractActionBar text={contractText}/>
                  {actionState==="sent"&&<div style={{fontSize:12,color:"var(--ok-tx)",textAlign:"center",fontWeight:600,marginBottom:8}}>Sent — now awaiting signature from {selectedClient?.name}.</div>}
                </>
              )}

              {step==="upload-pdf"&&(
                <>
                  <div className="tmtl">Upload Contract PDF</div>
                  <div className="tmsb">For {selectedClient?.name}</div>
                  {!pdfUrl?(
                    <div style={{marginBottom:16}}>
                      <div
                        onClick={()=>document.getElementById("pdf-upload-input")?.click()}
                        style={{background:"var(--card)",border:"2px dashed var(--bo)",borderRadius:14,padding:"40px 20px",textAlign:"center",cursor:"pointer",marginBottom:12}}
                      >
                        <div style={{marginBottom:10,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.fileText}</div>
                        <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:4}}>{pdfUploading?"Uploading…":"Tap to select a PDF file"}</div>
                        <div style={{fontSize:12,color:"var(--mu)"}}>PDF format only · Max 10 MB</div>
                        {pdfUploading&&<div style={{display:"flex",justifyContent:"center",gap:4,marginTop:12}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:"var(--g)",animation:`pulse 1.2s ease-in-out ${i*.2}s infinite`}}/>)}</div>}
                      </div>
                      <input
                        id="pdf-upload-input"
                        type="file"
                        accept="application/pdf"
                        style={{display:"none"}}
                        onChange={async(e)=>{
                          const file=e.target.files?.[0];
                          if(!file) return;
                          if(file.type!=="application/pdf"){alert("Please select a PDF file.");return;}
                          if(file.size>10*1024*1024){alert("File is too large. Max 10 MB.");return;}
                          setPdfUploading(true);
                          try{
                            const url=await dbUploadContractPdf(acct.id,selectedClient.id,file);
                            setPdfUrl(url);
                            setPdfFileName(file.name);
                          }catch(err){alert("Upload failed: "+(err.message||"Unknown error"));}
                          finally{setPdfUploading(false);}
                        }}
                      />
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>setStep("pick-method")} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>{I.arrowLeft} Back</button>
                        <button onClick={closeSheet} style={{flex:1,background:"var(--cr)",color:"var(--mu)",border:"1px solid var(--bo)",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>{I.close} Close</button>
                      </div>
                    </div>
                  ):(
                    <div style={{marginBottom:16}}>
                      <div style={{background:"var(--gl)",borderRadius:12,padding:16,marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
                        <span style={{display:"flex",color:"var(--gm)"}}>{I.fileText}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>{pdfFileName||"Contract.pdf"}</div>
                          <div style={{fontSize:11,color:"var(--mu)"}}>PDF uploaded successfully</div>
                        </div>
                        <button onClick={()=>{window.open(pdfUrl,"_blank");}} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>View</button>
                      </div>
                      <div style={{display:"flex",gap:8,marginBottom:8}}>
                        <button onClick={()=>{setPdfUrl(null);setPdfFileName("");}} style={{flex:1,background:"var(--cr)",color:"var(--mu)",border:"1px solid var(--bo)",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Replace PDF</button>
                        <button onClick={()=>{
                          // Embed the PDF URL directly in the contract body marker so
                          // each contract row owns its own URL (prevents later uploads
                          // from shadowing this one when viewed historically).
                          onContractSave(selectedClient.id,`[PDF Contract::${pdfUrl}]`);
                          if(onPdfUpload) onPdfUpload(selectedClient.id,pdfUrl);
                          setActionState("saved");
                          setTimeout(()=>closeSheet(),1200);
                        }} style={{flex:1,background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.save}</span> Save Draft
                        </button>
                      </div>
                      <button onClick={()=>{
                        onContractSend(selectedClient.id,`[PDF Contract::${pdfUrl}]`);
                        if(onPdfUpload) onPdfUpload(selectedClient.id,pdfUrl);
                        setActionState("sent");
                      }} disabled={!hasCancelPolicy} style={{width:"100%",background:hasCancelPolicy?"var(--t)":"var(--bo)",color:hasCancelPolicy?"var(--tc)":"var(--mu)",border:"none",borderRadius:10,padding:"12px",fontSize:12,fontWeight:600,cursor:hasCancelPolicy?"pointer":"default",fontFamily:"'Inter',sans-serif",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                        {!hasCancelPolicy?"Set cancellation policy first":<><span style={{display:"inline-flex",alignItems:"center"}}>{I.sendUp}</span> Send to Portal</>}
                      </button>
                      {actionState==="sent"&&<div style={{fontSize:12,color:"var(--ok-tx)",textAlign:"center",fontWeight:600,marginBottom:8}}>Sent — now awaiting signature from {selectedClient?.name}.</div>}
                    </div>
                  )}
                </>
              )}
            </>}

        </BottomSheet>
      )}
    </>
  );
}

/* ── INTAKE QUESTIONS by vertical ── */
// Default questionnaire template — generic fallback for any profession
const DEFAULT_QUESTIONNAIRE=[
  {key:"q1",label:"Project / session overview",placeholder:"Describe what you need in a few sentences.",type:"textarea"},
  {key:"q2",label:"Location or setting",placeholder:"Where will this take place, or where is the work for?",type:"input"},
  {key:"q3",label:"Style or direction",placeholder:"Any references, aesthetic preferences, or things to avoid?",type:"input"},
  {key:"q4",label:"Key deliverables or must-haves",placeholder:"What are the most important outcomes you're expecting?",type:"textarea"},
  {key:"q5",label:"Timeline and key dates",placeholder:"When do you need things completed by?",type:"input"},
  {key:"q6",label:"Budget or constraints",placeholder:"Are there any budget or scope constraints we should know about?",type:"input"},
  {key:"q7",label:"Anything else?",placeholder:"Special requests, sensitivities, or anything else helpful to know.",type:"textarea"},
];

// Per-profession default questionnaires — used to seed new clients
const VERT_QUESTIONNAIRES = {
  photographer: [
    {key:"vq1",label:"What type of shoot is this?",placeholder:"e.g. Wedding, portrait, family, event, product, headshot…",type:"input"},
    {key:"vq2",label:"Location / venue",placeholder:"Where will the shoot take place? Indoor or outdoor?",type:"input"},
    {key:"vq3",label:"Date and time",placeholder:"When would you like the shoot? Any timing preferences?",type:"input"},
    {key:"vq4",label:"How many people / subjects?",placeholder:"Number of people, pets, or products to photograph.",type:"input"},
    {key:"vq5",label:"Style and mood",placeholder:"Any references, Pinterest boards, or styles you love?",type:"textarea"},
    {key:"vq6",label:"Deliverables",placeholder:"How many edited photos do you expect? Digital only or prints too?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Wardrobe changes, props, special moments to capture, etc.",type:"textarea"},
  ],
  videographer: [
    {key:"vq1",label:"What type of video?",placeholder:"e.g. Wedding film, promo, music video, corporate, reel…",type:"input"},
    {key:"vq2",label:"Location / venue",placeholder:"Where will filming take place?",type:"input"},
    {key:"vq3",label:"Shoot date(s)",placeholder:"When? Single day or multi-day?",type:"input"},
    {key:"vq4",label:"Desired video length",placeholder:"e.g. 60-second reel, 3-5 min highlight, full-length…",type:"input"},
    {key:"vq5",label:"Style and references",placeholder:"Share any videos you love or the vibe you're going for.",type:"textarea"},
    {key:"vq6",label:"Audio / music preferences",placeholder:"Licensed music, voiceover, speeches, specific songs?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Drone shots, special equipment, deadlines, etc.",type:"textarea"},
  ],
  designer: [
    {key:"vq1",label:"What do you need designed?",placeholder:"e.g. Logo, brand kit, social templates, packaging, flyer…",type:"input"},
    {key:"vq2",label:"Brand overview",placeholder:"Tell me about your brand, audience, and what you do.",type:"textarea"},
    {key:"vq3",label:"Existing brand assets",placeholder:"Do you have a logo, colour palette, fonts, or style guide?",type:"input"},
    {key:"vq4",label:"Style and references",placeholder:"Share examples of designs you love or the look you want.",type:"textarea"},
    {key:"vq5",label:"Deliverable format",placeholder:"Print, digital, or both? Specific sizes or dimensions?",type:"input"},
    {key:"vq6",label:"Deadline",placeholder:"When do you need the final files by?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Copy/text to include, do's and don'ts, special requests.",type:"textarea"},
  ],
  copywriter: [
    {key:"vq1",label:"What do you need written?",placeholder:"e.g. Website copy, blog posts, ad copy, email sequence…",type:"input"},
    {key:"vq2",label:"About your business / brand",placeholder:"What do you do? Who is your target audience?",type:"textarea"},
    {key:"vq3",label:"Tone of voice",placeholder:"e.g. Warm and conversational, formal, witty, authoritative…",type:"input"},
    {key:"vq4",label:"Key messages / USPs",placeholder:"What are the most important points to communicate?",type:"textarea"},
    {key:"vq5",label:"Word count / scope",placeholder:"Approximate length or number of pieces needed.",type:"input"},
    {key:"vq6",label:"Deadline",placeholder:"When do you need the copy delivered by?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"SEO keywords, competitor examples, existing copy to reference.",type:"textarea"},
  ],
  makeup: [
    {key:"vq1",label:"What's the occasion?",placeholder:"e.g. Wedding, editorial, prom, special event, film…",type:"input"},
    {key:"vq2",label:"Date, time, and location",placeholder:"When and where do you need makeup done?",type:"input"},
    {key:"vq3",label:"How many people?",placeholder:"Just you, or bridesmaids / group? Trial included?",type:"input"},
    {key:"vq4",label:"Skin type and concerns",placeholder:"Oily, dry, sensitive? Any allergies or product preferences?",type:"input"},
    {key:"vq5",label:"Makeup style",placeholder:"Natural, glam, editorial, soft, bold? Share reference photos.",type:"textarea"},
    {key:"vq6",label:"Hair styling needed?",placeholder:"Do you also need hair, or just makeup?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Lashes, airbrush, special requests, travel requirements.",type:"textarea"},
  ],
  eventplanner: [
    {key:"vq1",label:"What type of event?",placeholder:"e.g. Wedding, corporate event, birthday, product launch…",type:"input"},
    {key:"vq2",label:"Date and venue",placeholder:"When is the event? Venue booked or need help finding one?",type:"input"},
    {key:"vq3",label:"Expected guest count",placeholder:"Approximate number of guests.",type:"input"},
    {key:"vq4",label:"Budget range",placeholder:"What is your overall budget for the event?",type:"input"},
    {key:"vq5",label:"Vision and theme",placeholder:"Describe the look, feel, and vibe you're going for.",type:"textarea"},
    {key:"vq6",label:"Vendors already booked",placeholder:"Caterer, DJ, florist, photographer — anyone confirmed?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Dietary needs, special moments, accessibility, surprises.",type:"textarea"},
  ],
  florist: [
    {key:"vq1",label:"What's the occasion?",placeholder:"e.g. Wedding, corporate event, editorial, sympathy…",type:"input"},
    {key:"vq2",label:"Date and delivery location",placeholder:"When and where do you need the flowers?",type:"input"},
    {key:"vq3",label:"What do you need?",placeholder:"e.g. Bridal bouquet, centrepieces, arch, buttonholes…",type:"input"},
    {key:"vq4",label:"Colour palette and style",placeholder:"Any colours, themes, or references?",type:"textarea"},
    {key:"vq5",label:"Favourite / disliked flowers",placeholder:"Any must-haves or flowers to avoid?",type:"input"},
    {key:"vq6",label:"Budget",placeholder:"What's your budget for florals?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Allergies, venue constraints, installation/teardown needs.",type:"textarea"},
  ],
  personaltrainer: [
    {key:"vq1",label:"What are your fitness goals?",placeholder:"e.g. Lose weight, build muscle, improve flexibility, rehab…",type:"textarea"},
    {key:"vq2",label:"Current fitness level",placeholder:"Beginner, intermediate, advanced? How often do you train?",type:"input"},
    {key:"vq3",label:"Preferred training style",placeholder:"e.g. Weight training, HIIT, yoga, outdoor, at-home…",type:"input"},
    {key:"vq4",label:"Any injuries or health conditions?",placeholder:"Anything I should know about for programming safety.",type:"textarea"},
    {key:"vq5",label:"Availability",placeholder:"Which days/times work best for you?",type:"input"},
    {key:"vq6",label:"Training location",placeholder:"Gym, home, outdoor, online? Which gym if applicable?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Dietary goals, past training experience, motivation level.",type:"textarea"},
  ],
  coach: [
    {key:"vq1",label:"What do you need help with?",placeholder:"e.g. Career transition, business growth, mindset, leadership…",type:"textarea"},
    {key:"vq2",label:"What does success look like?",placeholder:"What outcome would make this coaching worthwhile?",type:"textarea"},
    {key:"vq3",label:"Current situation",placeholder:"Where are you now? What's your biggest challenge?",type:"textarea"},
    {key:"vq4",label:"Have you worked with a coach before?",placeholder:"Any past experience with coaching or mentoring?",type:"input"},
    {key:"vq5",label:"Preferred format",placeholder:"In-person, video call, phone? How often?",type:"input"},
    {key:"vq6",label:"Availability",placeholder:"Best days/times for sessions?",type:"input"},
    {key:"vq7",label:"Anything else?",placeholder:"Deadlines, accountability preferences, communication style.",type:"textarea"},
  ],
};

// Get the default questionnaire for a given profession / vertical
const getDefaultQuestionnaire = (vert) => {
  if (vert === "custom") return []; // Custom = blank slate for freelancer to create
  return VERT_QUESTIONNAIRES[vert] || DEFAULT_QUESTIONNAIRE;
};

/* ── CLIENT PORTAL SCREEN ── */
function ClientPortal({c, acct, onBack, onIntakeSubmit, onContractComments, onPaymentUpdate, onPortalMessage, onContractSigned, onContactUpdate}){
  const [tab,setTab]=useState("overview");
  const [form,setForm]=useState(c.intake||{});
  const [submitted,setSubmitted]=useState(c.intakeSubmitted||false);
  // Contract-signed truth: once signed, never reopen for signing — not even if deposit is
  // marked unpaid or the client status regresses. The truth is scoped to the CURRENT contract:
  // 1) If the freelancer re-sends a NEW contract, contractStage is reset to "awaiting_signature"
  //    and the old signed contract is archived. The new one has no signed_at → signing is required.
  // 2) Primary source: contractStage === "signed"/"completed" (the server-persisted per-client stage).
  // 3) Fallback source: any non-archived contract in _portalContracts has a signed_at timestamp.
  //    This handles legacy clients where contractStage wasn't populated.
  // We intentionally DO NOT use c.status as a truth source — status tracks project lifecycle
  // (inquiry→pending→signed→active→delivered→complete) and can be stuck "active" even after
  // a new contract has been sent for re-signing.
  const hasNonArchivedSignedContract=(c._portalContracts||[]).some(ct=>!ct.archived&&(ct.signed_at||ct.status==="signed"||ct.status==="completed"));
  const isSignedByStage=c.contractStage==="signed"||c.contractStage==="completed";
  const [signed,setSigned]=useState(isSignedByStage||hasNonArchivedSignedContract);
  // Keep signed state sticky — if fresh portal data ever shows the contract was signed,
  // upgrade local state. Never transition back to false.
  useEffect(()=>{
    if(!signed&&(isSignedByStage||hasNonArchivedSignedContract)) setSigned(true);
  },[isSignedByStage,hasNonArchivedSignedContract,signed]);
  const [paidDeposit,setPaidDeposit]=useState(c.depositPaid||false);
  const [paidBalance,setPaidBalance]=useState(c.balancePaid||false);
  const [cancelStep,setCancelStep]=useState(null); // null | "confirm" | "charge" | "done"
  const [cancelReason,setCancelReason]=useState("");
  const [clientPhoto,setClientPhoto]=useState(c.portalPhoto||null);
  const clientPhotoRef=useRef(null);
  const portalBodyRef=useRef(null);
  // Reset scroll position when switching tabs so content always starts at the top
  useEffect(()=>{ if(portalBodyRef.current) portalBodyRef.current.scrollTop=0; },[tab]);
  // Sync payment state from fresh props (when portalData polls refresh c.depositPaid/balancePaid)
  // Only upgrades to true; never reverts from true to false so users don't lose success state.
  useEffect(()=>{ if(c.depositPaid&&!paidDeposit) setPaidDeposit(true); },[c.depositPaid]);
  useEffect(()=>{ if(c.balancePaid&&!paidBalance) setPaidBalance(true); },[c.balancePaid]);
  useEffect(()=>{ if(c.portalPhoto!==clientPhoto) setClientPhoto(c.portalPhoto||null); },[c.portalPhoto]);

  // ── Cancellation logic ──
  const policy=acct.cancellationPolicy||{};

  // Parse notice period string → days
  const noticeToDays=(n)=>{
    if(!n) return 0;
    const map={"1 week":7,"2 weeks":14,"30 days":30,"6 weeks":42,"3 months":90,"No minimum":0};
    return map[n]??0;
  };
  const noticeDays=noticeToDays(policy.notice);

  // Find key project date (first upcoming date with 'session'/'day'/'start'/'shoot'/'film'/'event'/'wedding' or just first future pend step)
  const projectDateStr=(()=>{
    const intake=c.intake||{};
    const raw=intake.date||(c.tl||[]).find(s=>s.d&&s.s!=="done"&&s.l&&!/deposit|balance|contract|inquiry|brief/i.test(s.l))?.d||"";
    return raw;
  })();

  const projectDate=projectDateStr?(()=>{
    // Parse YYYY-MM-DD or dd/mm/yyyy or "Sep 14" etc
    if(/^\d{4}-\d{2}-\d{2}$/.test(projectDateStr)) return new Date(projectDateStr);
    if(/^\d{2}\/\d{2}\/\d{4}$/.test(projectDateStr)){const [d,m,y]=projectDateStr.split("/");return new Date(`${y}-${m}-${d}`);}
    const parsed=new Date(projectDateStr);
    return isNaN(parsed)?null:parsed;
  })():null;

  const today=new Date(); today.setHours(0,0,0,0);
  const daysUntilProject=projectDate?Math.round((projectDate-today)/(1000*60*60*24)):null;
  const isLateNotice=daysUntilProject!==null&&noticeDays>0&&daysUntilProject<noticeDays;
  const canCancelFree=!isLateNotice; // outside notice window = free cancellation

  // Compute late charge amount
  const totalAmt=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
  const sym=acct.currencySymbol||"$";
  const depAmt=c.depositAmt||round2(totalAmt*(c.depositPct||50)/100);

  const lateChargeAmt=(()=>{
    if(!isLateNotice) return 0;
    const lp=policy.latePolicy;
    if(lp==="no_refund") return totalAmt; // full fee retained
    if(lp==="full") return 0; // full refund = no charge
    if(lp==="partial") return Math.round(totalAmt*(1-(policy.lateRefundPct||50)/100));
    if(lp==="discretion") return null; // null = TBD
    return totalAmt;
  })();

  const depositRetained=!policy.depositRefund&&depAmt>0;

  // Seed msgs — prefer _portalMsgs (from public URL RPC), else from thread in freelancer view
  const thread=acct.threads.find(t=>t.clientId===c.id);
  const seedMsgs=c._portalMsgs||(
    thread?.msgs && thread.msgs.length > 0
      ? thread.msgs.map(m=>({
          f: m.f==="me" ? "them" : "client", // flip: "me"(freelancer) → "them" in portal
          t: m.t,
          ts: m.ts,
        }))
      : [] // empty — no auto welcome message that gets overwritten
  );

  const [msgs,setMsgs]=useState(seedMsgs);
  // Sync messages when portal data refreshes (realtime/pull-to-refresh)
  // Merge server list with any pending optimistic client messages so fast-poll
  // doesn't wipe a just-sent message before the DB has committed it.
  useEffect(()=>{
    if(!c._portalMsgs) return;
    setMsgs(prev=>{
      // Dedup pending optimistic sends: an optimistic entry earns an _mid
      // from the portal_send_message RPC response, and the server list
      // also carries _mid on every row. Matching by id is exact — no
      // ambiguity with duplicate-body or clock-skew comparisons.
      //
      // Keep pending when:
      //   - the RPC hasn't responded yet (no _mid on optimistic), OR
      //   - the RPC returned an id but that id isn't in the server list yet
      //     (commit/replication lag between RPC return and next poll).
      // Drop pending when: _mid is present AND the server has echoed it.
      const serverIds=new Set(c._portalMsgs.filter(m=>m._mid).map(m=>m._mid));
      const pendingLocal=prev.filter(m=>m._pending&&!(m._mid&&serverIds.has(m._mid)));
      // Preserve optimistic _read:true from prev — when the user opens the
      // Messages tab we mark locally immediately (so the badge clears) and
      // fire the RPC; the server echo may lag by one poll, and without this
      // preservation the badge would briefly flicker back on.
      const prevReadIds=new Set(prev.filter(m=>m.f==="them"&&m._read&&m._mid).map(m=>m._mid));
      const merged=c._portalMsgs.map(m=>prevReadIds.has(m._mid)?{...m,_read:true}:m);
      // Sort by raw timestamp when available, else preserve order
      const combined=[...merged,...pendingLocal];
      combined.sort((a,b)=>{
        const ta=a._sk?Date.parse(a._sk):0;
        const tb=b._sk?Date.parse(b._sk):0;
        return ta-tb;
      });
      return combined;
    });
  },[c._portalMsgs]);
  const [newMsg,setNewMsg]=useState("");
  const [payState,setPayState]=useState(null);
  const [payError,setPayError]=useState(null);
  const [emailPrompt,setEmailPrompt]=useState(null); // { pendingType, pendingInvoiceId, draftEmail }

  // Auto-scroll portal messages view to the latest message when the tab opens or a new
  // message lands. Without this, long threads open with the OLDEST message at the top and the
  // client has to scroll down past the history to see recent messages + the input field.
  const portalMsgsRef=useRef(null);
  useEffect(()=>{
    if(tab!=="messages") return;
    const el=portalMsgsRef.current;
    if(!el) return;
    requestAnimationFrame(()=>{
      if(portalMsgsRef.current) portalMsgsRef.current.scrollTop=portalMsgsRef.current.scrollHeight;
    });
  },[tab,msgs.length]);

  // ── Detect return from Stripe Checkout ──
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const paymentStatus=params.get("payment");
    const paymentType=params.get("type");
    const invId=params.get("inv_id");
    if(paymentStatus==="success"){
      // Client returned from successful Stripe Checkout
      if(paymentType==="deposit"){ setPaidDeposit(true); if(onPaymentUpdate) onPaymentUpdate(c.id,"depositPaid",true); setPayState("depositDone"); setTimeout(()=>setPayState(null),3000); }
      else if(paymentType==="balance"){ setPaidBalance(true); if(onPaymentUpdate) onPaymentUpdate(c.id,"balancePaid",true); setPayState("done"); }
      else if(paymentType==="invoice"&&invId){
        const today=localToday();
        const updated=(c.invoices||[]).map(i=>String(i.id)===String(invId)?{...i,paid:true,paidAt:today}:i);
        if(onPaymentUpdate) onPaymentUpdate(c.id,"invoicePaid",{id:invId,invoices:updated});
        setPayState("done");
        setTimeout(()=>setPayState(null),3000);
      }
      // Clean URL params without reload
      const clean=window.location.pathname+window.location.hash;
      window.history.replaceState({},"",clean);
    } else if(paymentStatus==="cancelled"){
      setPayState(null);
      const clean=window.location.pathname+window.location.hash;
      window.history.replaceState({},"",clean);
    }
  },[]);

  const [commentMode,setCommentMode]=useState(false);
  const [commentText,setCommentText]=useState("");
  const [commentSent,setCommentSent]=useState(false);
  const questions=(c.questionnaireQuestions&&c.questionnaireQuestions.length>0)?c.questionnaireQuestions:getDefaultQuestionnaire(c.vert||acct.vert);
  const filled=questions.filter(q=>form[q.key]&&form[q.key].trim()).length;
  const canSubmit=filled>=1;

  const handleIntakeSubmit=()=>{ onIntakeSubmit(c.id,form); setSubmitted(true); setTab("overview"); };
  // Pre-populate signature state from DB if contract is already signed
  const _signedContract=(c._portalContracts||[]).find(ct=>!ct.archived&&ct.signed_at);
  const [sigName,setSigName]=useState(_signedContract?.signed_by||"");
  const [sigError,setSigError]=useState("");
  const [signedDate,setSignedDate]=useState(_signedContract?.signed_at||null);
  const [signedContractText,setSignedContractText]=useState(signed&&c.aiContract?c.aiContract:null);
  const nameMatches=sigName.trim().toLowerCase()===c.name.trim().toLowerCase();
  const handleSign=()=>{
    if(!sigName.trim()) return;
    if(!nameMatches){
      setSigError(`Name must match "${c.name}". Please enter your registered name exactly.`);
      return;
    }
    setSigError("");
    const today=localToday();
    setSignedDate(today);
    // Build signed contract with signature block appended
    const original=c.aiContract||"";
    const sigBlock=`\n\n**Client Signature**\n\nSigned by: **${sigName.trim()}**\nDate: **${fmtDateLong(today)}**\n\nThis contract has been electronically signed and is legally binding.`;
    setSignedContractText(original+sigBlock);
    setSigned(true);
    if(onContractSigned) onContractSigned(c.id,sigName.trim(),today);
  };
  const handlePay=async (type,invoiceId=null)=>{
    setPayError(null);
    setPayState("processing");
    try {
      const rawAmt=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
      const depPct=rawAmt<=0?100:(c.depositPct||50);
      let amountInUnits;
      if(type==="deposit"){
        amountInUnits=rawAmt<=0?0:(c.depositAmt||round2(rawAmt*depPct/100));
      } else if(type==="balance"){
        amountInUnits=rawAmt<=0?0:(c.balanceAmt||round2(rawAmt*(100-depPct)/100));
      } else if(type==="invoice"){
        const inv=(c.invoices||[]).find(i=>String(i.id)===String(invoiceId));
        if(!inv) throw new Error("Invoice not found");
        amountInUnits=Number(inv.amount)||0;
      } else {
        throw new Error("Unknown payment type: "+type);
      }
      if(!amountInUnits||amountInUnits<=0){
        throw new Error("Amount is zero or invalid");
      }

      // Pre-flight email validation (client-side mirror of the edge function rule).
      // If no valid email on the client record, open the email prompt modal and stop here.
      const emailOk=typeof c.email==="string"&&/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim())&&c.email.trim().length>=5;
      if(!emailOk){
        setPayState(null);
        setEmailPrompt({pendingType:type,pendingInvoiceId:invoiceId,draftEmail:""});
        return;
      }

      // Convert to pence/cents (smallest currency unit)
      const amountPence=Math.round(amountInUnits*100);
      const currency=(acct.currency||"GBP").toLowerCase();

      // Build success/cancel URLs for Stripe Checkout redirect
      const currentUrl=window.location.href.split("?")[0].split("#")[0];
      const successUrl=currentUrl+"?payment=success&type="+type+(invoiceId?"&inv_id="+encodeURIComponent(invoiceId):"");
      const cancelUrl=currentUrl+"?payment=cancelled";

      const res=await fetch(`${SUPABASE_URL}/functions/v1/stripe-client-checkout`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body:JSON.stringify({client_id:c.id, payment_type:type, amount_pence:amountPence, currency, success_url:successUrl, cancel_url:cancelUrl, ...(invoiceId?{invoice_id:String(invoiceId)}:{})}),
      });
      const data=await res.json();
      if(!res.ok){
        console.error("[handlePay] edge function error:",res.status,data);
        // Server-side email validation — open prompt and retry after the client enters one
        if(data?.code==="email_required"){
          setPayState(null);
          setEmailPrompt({pendingType:type,pendingInvoiceId:invoiceId,draftEmail:""});
          return;
        }
        throw new Error(data?.error||`Checkout failed (${res.status})`);
      }

      if(data.checkout_url){
        // Redirect to Stripe Checkout hosted page
        window.location.href=data.checkout_url;
        return; // Page will navigate away
      } else {
        throw new Error(data.error||"Failed to create checkout session");
      }
    } catch(err){
      console.error("[handlePay] Checkout failed:",type,invoiceId,err);
      setPayState(null);
      setPayError(err?.message||"Could not open Stripe Checkout. Please try again.");
      setTimeout(()=>setPayError(null),7000);
    }
  };
  const handleMsg=()=>{
    if(!newMsg.trim()) return;
    const text=newMsg.trim();
    const now=new Date();
    const ts=now.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
    // Unique per-send key so we can re-find this exact optimistic entry once
    // the RPC returns and attach the server-assigned _mid to it.
    const tempKey="tmp_"+now.getTime()+"_"+Math.random().toString(36).slice(2,8);
    // Optimistic update — flagged _pending so the sync effect keeps it until
    // the server echoes it back in the polled portal data.
    setMsgs(m=>[...m,{f:"client",t:text,ts,_pending:true,_sk:now.toISOString(),_tempKey:tempKey}]);
    setNewMsg("");
    // Push client message back to freelancer inbox — pass thread_id if available
    const threadId=thread?.id || null;
    if(onPortalMessage){
      const ret=onPortalMessage(c.id, text, threadId);
      // If the callback returned a Promise with the server's new message row
      // ({id, thread_id, ok}), stamp that id onto the optimistic entry. The
      // sync effect uses _mid to drop the pending once polling echoes the
      // same row back, preventing the "message appears twice" bug.
      if(ret&&typeof ret.then==="function"){
        ret.then(res=>{
          if(res&&res.id){
            setMsgs(prev=>prev.map(m=>m._tempKey===tempKey?{...m,_mid:res.id}:m));
          }
        }).catch(()=>{});
      }
    }
  };

  const [contactEmail,setContactEmail]=useState(c.email||"");
  const [contactPhone,setContactPhone]=useState(c.phone||"");
  const [contactConfirmed,setContactConfirmed]=useState(!!(c.email&&c.phone));
  const [showContactForm,setShowContactForm]=useState(false);
  // Sync contact info from fresh props (when polling brings updated data from freelancer edits)
  useEffect(()=>{ if(c.email&&c.email!==contactEmail) setContactEmail(c.email); },[c.email]);
  useEffect(()=>{ if(c.phone&&c.phone!==contactPhone) setContactPhone(c.phone); },[c.phone]);
  // Unread count is DERIVED from the server-side read_by_client flag
  // (exposed through _read on each message). This means the badge survives a
  // page refresh — the old delta-counter approach reset to 0 on reload and
  // lost state the user hadn't actually seen.
  const portalUnread=msgs.filter(m=>m.f==="them"&&!m._read).length;

  // When the Messages tab is open, mark any unread freelancer messages as
  // read on the server. Fires both when the user first clicks the tab AND
  // when a new message arrives while the tab is already open. We update
  // locally first (so the badge clears instantly) and then fire the RPC;
  // the sync effect above preserves the optimistic _read:true across polls
  // until the server echoes it back.
  useEffect(()=>{
    if(tab!=="messages") return;
    if(portalUnread===0) return;
    setMsgs(prev=>prev.map(m=>m.f==="them"&&!m._read?{...m,_read:true}:m));
    const token=c.portalToken;
    if(!token||!c.id||String(c.id).startsWith("tmp_")||String(c.id).startsWith("draft_")) return;
    // Pull the session from localStorage rather than threading it through as
    // a prop — keeps the portal-side change scoped without rewriting every
    // ClientPortal caller (the freelancer dashboard uses this same component
    // and wouldn't have a session).
    const _sess = (typeof _getPortalSession==="function") ? _getPortalSession(c.id)?.token : null;
    if(!_sess) return;
    fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_mark_messages_read_v2`,{
      method:"POST",
      headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
      body:JSON.stringify({p_session_token:_sess,p_client_id:c.id,p_portal_token:token})
    }).catch(()=>{});
  },[tab,portalUnread]);

  const tabs=[
    {id:"overview",icon:I.home,label:"Overview"},
    {id:"contract",icon:I.contracts,label:"Contract"},
    {id:"payments",icon:I.creditcard,label:"Payments"},
    {id:"messages",icon:I.chatBubble,label:"Messages"},
    {id:"intake",icon:I.clipboard,label:"Questionnaire"},
  ];

  const rawTotalAmt=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
  const depPct=rawTotalAmt<=0?100:(c.depositPct||50);
  const halfDepAmt=rawTotalAmt<=0?0:(c.depositAmt||round2(rawTotalAmt*depPct/100));
  const halfBalAmt=rawTotalAmt<=0?0:(c.balanceAmt||round2(rawTotalAmt*(100-depPct)/100));
  const portSym=acct.currencySymbol||"$";

  const completedSteps=[
    {l:"Questionnaire",done:submitted},
    {l:"Contract signed",done:signed},
    {l:depPct>=100?"Full payment":"Deposit paid",done:paidDeposit},
    ...(depPct<100?[{l:"Balance paid",done:paidBalance}]:[]),
  ];

  return (
    <div className="portal-screen">
      {/* HEADER */}
      <div className="portal-header" style={{background:"var(--g)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <img src={BANNER_B64} alt="Lancer" style={{height:36,objectFit:"contain",mixBlendMode:"screen"}}/>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:700,color:"#fff",letterSpacing:".02em",lineHeight:1.2}}>
                {acct.businessName&&acct.businessName!==acct.name?acct.businessName:acct.name}
              </div>
              {acct.tagline&&<div style={{fontSize:11,color:"rgba(255,255,255,.6)",fontFamily:"'Inter',sans-serif",fontWeight:400,fontStyle:"italic",marginTop:3}}>{acct.tagline}</div>}
            </div>
            <div style={{width:44,height:44,borderRadius:"50%",background:"var(--t)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:600,color:"var(--tc)",flexShrink:0,overflow:"hidden"}}>
              {acct.profilePic
                ?<img src={acct.profilePic} alt={acct.name} style={{width:"100%",height:"100%",borderRadius:"50%",objectFit:"cover"}}/>
                :(acct.ini||acct.name?.slice(0,2).toUpperCase())}
            </div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
          <div style={{position:"relative",flexShrink:0}}>
            {clientPhoto
              ?<img src={clientPhoto} alt={c.name} style={{width:56,height:56,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(255,255,255,.4)"}}/>
              :<div style={{width:56,height:56,borderRadius:"50%",background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:700,color:"#fff",border:"2px solid rgba(255,255,255,.3)"}}>
                {c.name?.split(" ").map(w=>w[0]).join("").slice(0,2)}
              </div>
            }
            {!c.archived&&<div onClick={()=>clientPhotoRef.current&&clientPhotoRef.current.click()}
              style={{position:"absolute",bottom:0,right:0,width:22,height:22,borderRadius:"50%",background:"rgba(255,255,255,.9)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"1.5px solid rgba(255,255,255,.6)"}}>
              <span style={{display:"inline-flex"}}>{I.edit}</span>
            </div>}
            {!c.archived&&clientPhoto&&<div onClick={()=>{setClientPhoto(null);if(onPaymentUpdate) onPaymentUpdate(c.id,"portalPhoto",null);}}
              style={{position:"absolute",top:0,right:0,width:20,height:20,borderRadius:"50%",background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"1.5px solid rgba(255,255,255,.4)"}}>
              <span style={{fontSize:10,color:"#fff",lineHeight:1}}>✕</span>
            </div>}
            <input ref={clientPhotoRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{display:"none"}}
              onChange={e=>{
                const file=e.target.files?.[0];
                if(!file) return;
                // Resize to 200x200 max before saving to keep data small
                const img=new Image();
                const url=URL.createObjectURL(file);
                img.onload=()=>{
                  const max=200;
                  let w=img.width,h=img.height;
                  if(w>max||h>max){
                    if(w>h){h=Math.round(h*max/w);w=max;}
                    else{w=Math.round(w*max/h);h=max;}
                  }
                  const canvas=document.createElement("canvas");
                  canvas.width=w;canvas.height=h;
                  canvas.getContext("2d").drawImage(img,0,0,w,h);
                  const dataUrl=canvas.toDataURL("image/jpeg",0.85);
                  URL.revokeObjectURL(url);
                  setClientPhoto(dataUrl);
                  if(onPaymentUpdate) onPaymentUpdate(c.id,"portalPhoto",dataUrl);
                  e.target.value="";
                };
                img.src=url;
              }}/>
          </div>
          <div>
            <div className="portal-client-name" style={{marginBottom:2}}>{c.name}</div>
            <div className="portal-client-sub">{c.type} · Your client portal</div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="portal-tabs" style={{overflowX:"auto",scrollbarWidth:"none",background:"var(--g)"}}>
        {tabs.map(t=>(
          <div key={t.id} className={`portal-tab${tab===t.id?" on":""}`}
            style={{flexShrink:0, borderBottomColor:tab===t.id?"rgba(255,255,255,.9)":"transparent",position:"relative"}}
            onClick={()=>setTab(t.id)}>
            <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4,position:"relative"}}>
              {t.icon}
            </span>{t.label}
            {t.id==="messages"&&portalUnread>0&&(
              <span aria-label={`${portalUnread} new message${portalUnread>1?"s":""}`} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",minWidth:16,height:16,padding:"0 5px",borderRadius:8,background:"#E5484D",color:"#fff",fontSize:9,fontWeight:700,marginLeft:5,border:"1.5px solid rgba(255,255,255,.35)"}}>{portalUnread}</span>
            )}
          </div>
        ))}
      </div>

      <div className="portal-body" ref={portalBodyRef}>

        {/* ARCHIVED BANNER */}
        {c.archived&&(
          <div style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:12,padding:"12px 14px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
            <span style={{flexShrink:0,display:"flex"}}>{I.archive}</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>Project Archived</div>
              <div style={{fontSize:12,color:"var(--info-tx)",marginTop:2}}>This project has been archived by {acct.name}. No further changes can be made.</div>
            </div>
          </div>
        )}

        {/* ── OVERVIEW ── */}
        {tab==="overview"&&(
          <>
            <div className="portal-section-title">Welcome, {c.name.split(" ")[0]}!</div>
            <div className="portal-section-sub">Here's everything you need for your {c.type.split("·")[0].trim().toLowerCase()} with {acct.name}.</div>

            <div className="portal-overview-grid">
              <div className="portal-overview-main">
            {/* Progress checklist */}
            <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:"4px 16px",marginBottom:16}}>
              {completedSteps.map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:i<completedSteps.length-1?"1px solid var(--bo)":"none"}}>
                  <div style={{width:24,height:24,borderRadius:"50%",background:s.done?"var(--g)":"#fff",border:s.done?"none":"2px solid var(--bo)",display:"flex",alignItems:"center",justifyContent:"center",color:s.done?"#fff":"var(--mu)",flexShrink:0}}>{s.done?<span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span>:<span style={{display:"inline-flex",alignItems:"center"}}>{I.circleO}</span>}</div>
                  <div style={{flex:1,fontSize:13,fontWeight:500,color:s.done?"var(--ink)":"var(--mu)"}}>{s.l}</div>
                  {s.done?<span style={{fontSize:11,color:"var(--gm)",fontWeight:600}}>Done</span>:<span style={{fontSize:11,color:"var(--t)",fontWeight:600}}>Pending</span>}
                </div>
              ))}
            </div>

            {/* Contact confirmation */}
            {!c.archived&&(!contactConfirmed||showContactForm)&&(
              <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid var(--t)",padding:16,marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.mailOpen}</span>Confirm your contact details</div>
                <div style={{fontSize:12,color:"var(--mu)",marginBottom:12}}>So {acct.name} can send you updates, reminders, and your final gallery link.</div>
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                  <input className="ifinput" type="email" placeholder="Your email address" value={contactEmail} onChange={e=>setContactEmail(e.target.value)}/>
                  <PhoneInput className="ifinput" countryCode={acct.country} value={contactPhone} onChange={setContactPhone} placeholder="Your phone number"/>
                </div>
                <button onClick={()=>{ if(contactEmail){ setContactConfirmed(true); setShowContactForm(false); if(onContactUpdate) onContactUpdate(c.id,contactEmail,contactPhone); else onIntakeSubmit(c.id,{}); }}} disabled={!contactEmail} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",opacity:!contactEmail?.5:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  Confirm details {I.check}
                </button>
              </div>
            )}
            {contactConfirmed&&!showContactForm&&(
              <div style={{background:"var(--gl)",borderRadius:12,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
                <span style={{display:"flex",color:"var(--gm)"}}>{I.checkCircle}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:"var(--gm)"}}>Contact details confirmed</div>
                  <div style={{fontSize:11,color:"var(--mu)"}}>{contactEmail}{contactPhone?` · ${contactPhone}`:""}</div>
                </div>
                <span style={{fontSize:11,color:"var(--t)",cursor:"pointer",fontWeight:600}} onClick={()=>setShowContactForm(true)}>Edit</span>
              </div>
            )}

            {/* Next action prompt */}
            {!c.archived&&!submitted&&(
              <div style={{background:"var(--tl)",borderRadius:14,padding:16,marginBottom:12,display:"flex",gap:12,alignItems:"flex-start"}}>
                <span style={{display:"inline-flex"}}>{I.clipboard}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--t)",marginBottom:4}}>Complete your questionnaire</div>
                  <div style={{fontSize:12,color:"var(--t)",opacity:.8,marginBottom:10}}>Help {acct.name} prepare for your session by answering a few questions.</div>
                  <button onClick={()=>setTab("intake")} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:10,padding:"9px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"inline-flex",alignItems:"center",gap:5}}>Fill in now {I.arrow}</button>
                </div>
              </div>
            )}
            {!c.archived&&!signed&&submitted&&c.contractSentToPortal&&(
              <div style={{background:"var(--gl)",borderRadius:14,padding:16,marginBottom:12,display:"flex",gap:12,alignItems:"flex-start"}}>
                <span style={{display:"flex"}}>{I.sign}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--gm)",marginBottom:4}}>Sign your contract</div>
                  <div style={{fontSize:12,color:"var(--gm)",marginBottom:10}}>Your contract is ready to review and sign.</div>
                  <button onClick={()=>setTab("contract")} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"9px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"inline-flex",alignItems:"center",gap:5}}>Review contract {I.arrow}</button>
                </div>
              </div>
            )}
            {!c.archived&&!signed&&submitted&&!c.contractSentToPortal&&(
              <div style={{background:"var(--cr)",borderRadius:14,padding:16,marginBottom:12,display:"flex",gap:12,alignItems:"flex-start"}}>
                <span style={{display:"flex"}}>{I.fileText}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--mu)",marginBottom:4}}>Contract in progress</div>
                  <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Your freelancer is preparing your contract. You'll be notified when it's ready to sign.</div>
                </div>
              </div>
            )}
            {!c.archived&&signed&&!paidDeposit&&(
              <div style={{background:"var(--warn-bg)",borderRadius:14,padding:16,marginBottom:12,display:"flex",gap:12,alignItems:"flex-start"}}>
                <span style={{display:"inline-flex"}}>{I.creditcard}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)",marginBottom:4}}>{depPct>=100?"Pay full payment":"Pay your deposit"}</div>
                  <div style={{fontSize:12,color:"var(--warn-tx)",opacity:.8,marginBottom:10}}>{depPct>=100?`The full fee of ${portSym}${fmtMoney(halfDepAmt)} secures your booking.`:`A deposit of ${portSym}${fmtMoney(halfDepAmt)} secures your date.`}</div>
                  <button onClick={()=>setTab("payments")} style={{background:"#B45309",color:"#fff",border:"none",borderRadius:10,padding:"9px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"inline-flex",alignItems:"center",gap:5}}>{depPct>=100?"Pay full payment":"Pay deposit"} {I.arrow}</button>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",fontWeight:500,marginBottom:12,marginTop:8}}>Project Timeline</div>
            <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:"8px 16px"}}>
              {(c.tl&&c.tl.length>0)?c.tl.map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<c.tl.length-1?"1px solid var(--bo)":"none"}}>
                  <div style={{width:24,height:24,borderRadius:"50%",background:s.s==="done"?"var(--g)":s.s==="active"?"var(--t)":"var(--card)",border:s.s==="pend"?"2px solid var(--bo)":"none",display:"flex",alignItems:"center",justifyContent:"center",color:s.s==="done"?"#fff":s.s==="active"?"var(--tc)":"var(--mu)",flexShrink:0}}>
                    <span style={{display:"inline-flex",alignItems:"center"}}>{s.s==="done"?I.check:s.s==="active"?I.dot:I.circleO}</span>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:s.s==="pend"?"var(--mu)":"var(--ink)"}}>{s.l}</div>
                    {s.d&&<div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{s.d}</div>}
                  </div>
                </div>
              )):<div style={{padding:"12px 0",fontSize:12,color:"var(--mu)",textAlign:"center"}}>Timeline will appear here once your project is planned.</div>}
            </div>
              </div>
              <div className="portal-overview-aside">
            {/* Freelancer contact info */}
            {(acct.email||acct.phone)&&(
              <div style={{marginTop:16}}>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",fontWeight:500,marginBottom:8}}>Contact {acct.name.split(" ")[0]}</div>
                <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden"}}>
                  {acct.email&&(
                    <a href={`mailto:${acct.email}`} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:acct.phone?"1px solid var(--bo)":"none",textDecoration:"none",color:"inherit"}}>
                      <span style={{color:"var(--gm)",display:"flex"}}>{I.mail}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,color:"var(--mu)",marginBottom:1}}>Email</div>
                        <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{acct.email}</div>
                      </div>
                    </a>
                  )}
                  {acct.phone&&(
                    <a href={`tel:${acct.phone}`} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",textDecoration:"none",color:"inherit"}}>
                      <span style={{color:"var(--gm)",display:"flex"}}>{I.phone}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,color:"var(--mu)",marginBottom:1}}>Phone</div>
                        <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{acct.phone}</div>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            )}

            {!c.archived&&policy.notice&&cancelStep===null&&c.status!=="complete"&&c.status!=="delivered"&&(
              <div style={{marginTop:24}}>
                <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".08em",color:"var(--mu)",fontWeight:600,marginBottom:8}}>Need to Cancel?</div>
                {canCancelFree?(
                  <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid var(--bo)",padding:16}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}>Cancel booking</div>
                    <div style={{fontSize:12,color:"var(--mu)",marginBottom:12,lineHeight:1.6}}>
                      {daysUntilProject!==null
                        ?`You're outside the ${policy.notice} cancellation window (${daysUntilProject} days until your project). ${policy.depositRefund?"Since you are within the required notice period, your deposit will be fully refunded.":"Please note: the deposit is non-refundable, even with sufficient notice provided."}`
                        :`Cancellation requires ${policy.notice} written notice. ${policy.depositRefund?"If you provide the required notice, your deposit will be fully refunded.":"Please note: the deposit is non-refundable, even if the required notice period is met."}`
                      }
                    </div>
                    <button onClick={()=>setCancelStep("confirm")}
                      style={{width:"100%",background:"var(--err-bg)",color:"var(--err-tx)",border:"1.5px solid var(--err-bo)",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Request cancellation
                    </button>
                  </div>
                ):(
                  <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid #F59E0B",padding:16}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)",marginBottom:4}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.alertTri}</span>Late notice cancellation</div>
                    <div style={{fontSize:12,color:"var(--info-tx)",marginBottom:12,lineHeight:1.6}}>
                      Your project is {daysUntilProject} day{daysUntilProject!==1?"s":""} away — inside the <strong>{policy.notice}</strong> cancellation window.
                      {" "}{policy.latePolicy==="no_refund"
                        ?`No refund will be issued — the full fee, including the deposit, is retained.`
                        :policy.latePolicy==="full"
                          ?`A full refund, including the deposit, will be issued per policy.`
                          :policy.latePolicy==="partial"
                            ?`A ${policy.lateRefundPct||50}% partial refund of the total fee will be issued. A cancellation charge of ${sym}${lateChargeAmt?fmtMoney(lateChargeAmt):null||"—"} applies.`
                            :"A fee may apply at the freelancer's discretion."
                      }
                    </div>
                    <button onClick={()=>setCancelStep("charge")}
                      style={{width:"100%",background:"var(--warn-bg)",color:"var(--warn-tx)",border:"1.5px solid var(--warn-bo)",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      View cancellation terms & proceed
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Cancellation confirm step */}
            {cancelStep==="confirm"&&(
              <div style={{marginTop:16,background:"var(--err-bg)",borderRadius:14,border:"1.5px solid var(--err-bo)",padding:16}}>
                <div style={{fontSize:14,fontWeight:600,color:"var(--err-tx)",marginBottom:8}}>Confirm cancellation</div>
                <div style={{fontSize:12,color:"var(--info-tx)",marginBottom:12,lineHeight:1.6}}>
                  Please let {(acct.name||"your freelancer").split(" ")[0]} know why you're cancelling. This will be sent to them as a message.
                  {depositRetained&&<><br/><strong>Note:</strong> your deposit of {sym}{fmtMoney(depAmt)} is non-refundable per the booking policy, even with sufficient notice provided.</>}
                  {policy.depositRefund&&paidDeposit&&<><br/>Since you have provided the required notice, your deposit of {sym}{fmtMoney(depAmt)} will be fully refunded.</>}
                </div>
                <textarea value={cancelReason} onChange={e=>setCancelReason(e.target.value)}
                  placeholder="e.g. Change of plans, venue issue, personal circumstances…"
                  style={{width:"100%",background:"var(--card)",border:"1.5px solid #FECACA",borderRadius:10,padding:"11px 13px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:80,marginBottom:12}}/>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setCancelStep(null)}
                    style={{flex:1,background:"var(--card)",color:"var(--mu)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Go back
                  </button>
                  <button disabled={!cancelReason.trim()} onClick={()=>{
                    onPortalMessage&&onPortalMessage(c.id,`CANCELLATION REQUEST: ${cancelReason.trim()}`);
                    onPaymentUpdate&&onPaymentUpdate(c.id,"cancellationRequested",true);
                    setCancelStep("done");
                  }}
                    style={{flex:2,background:cancelReason.trim()?"#DC2626":"var(--bo)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:cancelReason.trim()?"pointer":"default",fontFamily:"'Inter',sans-serif"}}>
                    Send cancellation request
                  </button>
                </div>
              </div>
            )}

            {/* Late notice charge step */}
            {cancelStep==="charge"&&(
              <div style={{marginTop:16,background:"var(--warn-bg)",borderRadius:14,border:"1.5px solid var(--warn-bo)",padding:16}}>
                <div style={{fontSize:14,fontWeight:600,color:"var(--warn-tx)",marginBottom:8}}>Late notice cancellation terms</div>
                <div style={{background:"var(--card)",borderRadius:10,border:"1px solid var(--info-bo)",padding:12,marginBottom:12}}>
                  <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.8}}>
                    <div><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.caldate}</span>Notice period: <strong>{policy.notice}</strong></div>
                    <div>📆 Days until project: <strong>{daysUntilProject!==null?daysUntilProject:"Unknown"}</strong></div>
                    <div><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.creditcard}</span>Deposit: <strong>{
                      policy.latePolicy==="no_refund"?"Retained (no refund)":
                      policy.latePolicy==="full"?"Fully refunded":
                      policy.latePolicy==="partial"?`Included in ${policy.lateRefundPct||50}% partial refund`:
                      "At freelancer's discretion"
                    }</strong></div>
                    <div>⚖️ Late cancellation policy: <strong style={{color:"var(--err-tx)"}}>
                      {policy.latePolicy==="no_refund"?"No refund — full fee retained":
                       policy.latePolicy==="full"?"Full refund issued":
                       policy.latePolicy==="partial"?`${policy.lateRefundPct||50}% partial refund — charge of ${sym}${lateChargeAmt?fmtMoney(lateChargeAmt):null||"—"}`:
                       "At freelancer's discretion"}
                    </strong></div>
                    {policy.latePolicy==="partial"&&<div>↩ Refund amount: <strong>{sym}{fmtMoney(round2(totalAmt*(policy.lateRefundPct||50)/100))}</strong></div>}
                  </div>
                </div>
                <div style={{fontSize:12,color:"var(--info-tx)",marginBottom:12}}>By proceeding you acknowledge the cancellation terms above. {
                  policy.latePolicy==="no_refund"?`The full fee of ${sym}${fmtMoney(totalAmt)}, including your deposit, will be retained.`:
                  policy.latePolicy==="full"?"A full refund, including your deposit, will be issued.":
                  policy.latePolicy==="partial"?`A cancellation charge of ${sym}${lateChargeAmt?fmtMoney(lateChargeAmt):null||"—"} will be applied. You will receive a ${policy.lateRefundPct||50}% partial refund of ${sym}${fmtMoney(round2(totalAmt*(policy.lateRefundPct||50)/100))}.`:
                  "The freelancer will assess the charge on a case-by-case basis."
                }</div>
                <textarea value={cancelReason} onChange={e=>setCancelReason(e.target.value)}
                  placeholder="Please explain why you need to cancel…"
                  style={{width:"100%",background:"var(--card)",border:"1.5px solid #FDE68A",borderRadius:10,padding:"11px 13px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:70,marginBottom:12}}/>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setCancelStep(null)}
                    style={{flex:1,background:"var(--card)",color:"var(--mu)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Go back
                  </button>
                  <button disabled={!cancelReason.trim()} onClick={()=>{
                    const msg=`LATE CANCELLATION REQUEST: ${cancelReason.trim()}${lateChargeAmt&&lateChargeAmt>0?` [Charge: ${sym}${fmtMoney(lateChargeAmt)} per cancellation policy]`:" [Freelancer to assess charge]"}`;
                    onPortalMessage&&onPortalMessage(c.id,msg);
                    onPaymentUpdate&&onPaymentUpdate(c.id,"cancellationRequested",true);
                    setCancelStep("done");
                  }}
                    style={{flex:2,background:cancelReason.trim()?"#B45309":"var(--bo)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:cancelReason.trim()?"pointer":"default",fontFamily:"'Inter',sans-serif"}}>
                    I accept — send request
                  </button>
                </div>
              </div>
            )}

            {/* Cancellation sent confirmation */}
            {cancelStep==="done"&&(
              <div style={{marginTop:16,background:"var(--gl)",borderRadius:14,padding:20,textAlign:"center"}}>
                <div style={{fontSize:28,marginBottom:8}}>📨</div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--gm)",marginBottom:4}}>Cancellation request sent</div>
                <div style={{fontSize:12,color:"var(--mu)"}}>
                  {(acct.name||"Your freelancer").split(" ")[0]} has been notified. They'll be in touch shortly to confirm and process any applicable refunds.
                </div>
              </div>
            )}
              </div>
            </div>
          </>
        )}

        {/* ── CONTRACT ── */}
        {tab==="contract"&&(
          <div className="portal-contract-wrap">
            <div className="portal-section-title" style={{display:"flex",alignItems:"center",gap:8}}>Your Contract{(c._portalContracts||[]).some(ct=>ct.archived)?<span style={{fontSize:10,fontWeight:600,color:"var(--gm)",background:"var(--gl)",padding:"2px 8px",borderRadius:99}}>Current</span>:null}</div>

            {/* Not sent yet */}
            {!c.contractSentToPortal&&!signed&&(
              <div style={{background:"var(--card)",borderRadius:14,border:"1.5px dashed var(--bo)",padding:32,textAlign:"center",marginBottom:16}}>
                <div style={{marginBottom:12,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.fileText}</div>
                <div style={{fontSize:15,fontWeight:600,color:"var(--mu)",marginBottom:6}}>Contract not yet sent</div>
                <div style={{fontSize:13,color:"var(--mu)",lineHeight:1.6}}>Your freelancer is preparing your contract. You'll be able to read and sign it here once it's ready. Feel free to send a message if you have questions.</div>
              </div>
            )}

            {/* Contract sent or already signed */}
            {(c.contractSentToPortal||signed)&&(
              <>
                <div className="portal-section-sub">{signed?"Your contract is signed and stored below.":"Please read your contract carefully and sign below."}</div>

                {signed&&(
                  <div style={{background:"var(--gl)",borderRadius:12,padding:14,marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
                    <span style={{color:"var(--gm)",display:"flex"}}>{I.check}</span>
                    <div style={{flex:1,fontSize:13,fontWeight:600,color:"var(--gm)"}}>Signed by {sigName||c.name} on {fmtDateTime(signedDate||localToday())}</div>
                    <button
                      onClick={()=>{
                        // For PDF contracts, download the actual PDF file; for text
                        // contracts, render the signed text to PDF.
                        const body=signedContractText||c.aiContract||"";
                        const pdfUrl=getContractPdfUrl({body},c);
                        if(pdfUrl){
                          const a=document.createElement("a");
                          a.href=pdfUrl; a.download=`${c.name.replace(/\s+/g,"-")}-signed-contract.pdf`; a.target="_blank"; a.click();
                        } else {
                          downloadContractAsPdf(body||"Contract not available.",`${c.name.replace(/\s+/g,"-")}-signed-contract`);
                        }
                      }}
                      style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}
                    >
                      Download PDF
                    </button>
                  </div>
                )}

                {/* Render PDF vs text based on the LATEST contract body (c.aiContract),
                     NOT on the stale client-level c.contractPdfUrl. Otherwise a previously
                     uploaded PDF would keep showing even after the freelancer replaces
                     it with an AI/manual contract. */}
                {isPdfContract({body:c.aiContract||""})?(()=>{
                  const portalPdfUrl=getContractPdfUrl({body:c.aiContract||""},c);
                  return (
                  <>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <span style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em"}}>Contract document (PDF)</span>
                      {portalPdfUrl&&<button
                        onClick={()=>{
                          const a=document.createElement("a");
                          a.href=portalPdfUrl; a.download=`${c.name.replace(/\s+/g,"-")}-contract.pdf`; a.target="_blank"; a.click();
                        }}
                        style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:5}}
                      >
                        {I.arrow} Download PDF
                      </button>}
                    </div>
                    <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:20,marginBottom:16,textAlign:"center"}}>
                      <div style={{marginBottom:10,color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.fileText}</div>
                      <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:6}}>PDF Contract</div>
                      <div style={{fontSize:12,color:"var(--mu)",marginBottom:12}}>Tap "Download PDF" above to view the full contract document.</div>
                      {portalPdfUrl&&<button onClick={()=>window.open(portalPdfUrl,"_blank")} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Open PDF</button>}
                    </div>
                  </>
                  );
                })():c.aiContract?(
                  <>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <span style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em"}}>{signed?"Signed contract":"Contract document"}</span>
                      <button
                        onClick={()=>downloadContractAsPdf(signedContractText||c.aiContract,`${c.name.replace(/\s+/g,"-")}-${signed?"signed-":""}contract`)}
                        style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:5}}
                      >
                        {I.arrow} Download PDF
                      </button>
                    </div>
                    <ContractDisplay text={signedContractText||c.aiContract} maxHeight={null}/>
                  </>
                ):!signed?(
                  <div style={{background:"var(--cr)",borderRadius:12,border:"1px solid var(--bo)",padding:16,marginBottom:16,fontSize:13,color:"var(--mu)",textAlign:"center"}}>
                    Contract is on its way — your freelancer will upload the document shortly.
                  </div>
                ):null}
              </>
            )}

            {/* Signing section — only when contract is sent and not yet signed */}
            {!c.archived&&c.contractSentToPortal&&!signed?(
              <>
                {/* Sign */}
                {!commentMode&&(
                  <div style={{background:"var(--cr)",borderRadius:14,border:"1.5px dashed var(--bo)",padding:16,marginBottom:12}}>
                    <div style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>E-sign with your name</div>
                    <input
                      id="sig-input"
                      className="ifinput"
                      value={sigName}
                      onChange={e=>{setSigName(e.target.value);setSigError("");}}
                      placeholder={`Type your full name: ${c.name}`}
                      style={{marginBottom:4,fontStyle:"italic",fontFamily:"'Cormorant Garamond',serif",fontSize:18,borderColor:sigError?"#E5484D":sigName.trim()&&nameMatches?"#10B981":undefined}}
                    />
                    {sigName.trim()&&!nameMatches&&!sigError&&(
                      <div style={{fontSize:11,color:"#E5484D",marginBottom:8}}>Name must match: <strong>{c.name}</strong></div>
                    )}
                    {sigName.trim()&&nameMatches&&(
                      <div style={{fontSize:11,color:"#10B981",marginBottom:8,display:"flex",alignItems:"center",gap:4}}>{I.check} Name matches</div>
                    )}
                    {sigError&&<div style={{fontSize:12,color:"#E5484D",marginBottom:8,fontWeight:600}}>{sigError}</div>}
                    {!sigName.trim()&&<div style={{height:8}}/>}
                    <button onClick={handleSign} disabled={!sigName.trim()||!nameMatches} style={{width:"100%",background:sigName.trim()&&nameMatches?"var(--g)":"var(--bo)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:sigName.trim()&&nameMatches?"pointer":"default",fontFamily:"'Inter',sans-serif",marginBottom:8}}>
                      {!sigName.trim()?"Enter your name to sign":!nameMatches?"Name does not match":<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.sign}</span> Sign & Confirm Contract</>}
                    </button>
                    <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginBottom:12}}>By signing you agree to all terms above. Your name must match your registered name.</div>
                    <div style={{height:1,background:"var(--bo)",marginBottom:12}}/>
                    <button onClick={()=>setCommentMode(true)} style={{width:"100%",background:"var(--tl)",color:"var(--t)",border:"1.5px solid var(--t)",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.chatBubble}</span> Send back with comments
                    </button>
                    <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginTop:6}}>Have questions or changes? Send your comments to {acct.name.split(" ")[0]}.</div>
                  </div>
                )}

                {/* Comments form */}
                {commentMode&&!commentSent&&(
                  <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid var(--t)",padding:16,marginBottom:12}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}>Send comments to {acct.name.split(" ")[0]}</div>
                    <div style={{fontSize:12,color:"var(--mu)",marginBottom:12}}>Your comments will be sent directly to {acct.name.split(" ")[0]} who will update the contract and resend.</div>
                    <textarea
                      value={commentText}
                      onChange={e=>setCommentText(e.target.value)}
                      placeholder="e.g. Section 3 — can the deposit be 40% instead of 50%? Also please confirm the delivery date."
                      style={{width:"100%",background:"var(--cr)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"11px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:100,marginBottom:12}}
                    />
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setCommentMode(false)} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                      <button
                        disabled={!commentText.trim()}
                        onClick={()=>{ if(onContractComments) onContractComments(c.id,commentText); setCommentSent(true); }}
                        style={{flex:2,background:commentText.trim()?"var(--t)":"var(--bo)",color:commentText.trim()?"var(--tc)":"var(--mu)",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:commentText.trim()?"pointer":"default",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
                      >
                        Send Comments {I.arrow}
                      </button>
                    </div>
                  </div>
                )}

                {/* Comments sent confirmation */}
                {commentSent&&(
                  <div style={{background:"var(--gl)",borderRadius:14,padding:16,marginBottom:12,textAlign:"center"}}>
                    <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>{I.mail}</div>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--gm)",marginBottom:4}}>Comments sent!</div>
                    <div style={{fontSize:12,color:"var(--mu)"}}>
                      {acct.name.split(" ")[0]} will review your feedback and send an updated contract.
                    </div>
                  </div>
                )}
              </>
            ):signed&&!c.archived?(
              <button onClick={()=>setTab("payments")} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.creditcard}</span>Proceed to payment <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span>
              </button>
            ):null}

            {/* ── Previous Contracts ── */}
            {(()=>{
              const allContracts=(c._portalContracts||[]);
              // Current contract is the non-archived one; previous are archived or older signed versions
              const previous=allContracts.filter(ct=>ct.archived&&(ct.status==="signed"||ct.status==="completed"||ct.status==="awaiting_signature"));
              if(previous.length===0) return null;
              return (
                <div style={{marginTop:24}}>
                  <div className="portal-section-title" style={{fontSize:14}}>Previous Contracts</div>
                  <div style={{fontSize:12,color:"var(--mu)",marginBottom:12}}>Older versions of your contract are archived below for your records.</div>
                  {previous.map((ct,i)=>{
                    const ctSigned=ct.status==="signed"||ct.status==="completed";
                    // Detect legacy PDFs whose file was overwritten on the server —
                    // we can't show/download them because the physical file is gone.
                    const lostPdf=isLegacyLostPdf(ct,allContracts);
                    return (
                      <div key={ct.id||i} style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",padding:14,marginBottom:8}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                            <span style={{fontSize:14}}>{ctSigned?I.sign:I.fileText}</span>
                            <span style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>Version {ct.version||1}</span>
                            <span style={{padding:"2px 8px",borderRadius:99,fontSize:10,fontWeight:600,background:ctSigned?"var(--gl)":"#f5f5f5",color:ctSigned?"var(--gm)":"var(--mu)"}}>{ctSigned?"Signed":"Archived"}</span>
                          </div>
                          {lostPdf?(
                            <span aria-label="Older version unavailable" title="The PDF for this version is no longer available because a newer PDF was uploaded before per-version storage was introduced." style={{background:"var(--warn-bg)",color:"var(--warn-tx)",border:"1px solid var(--warn-bo)",borderRadius:8,padding:"4px 10px",fontSize:10,fontWeight:600,fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                              Unavailable
                            </span>
                          ):(
                          <button onClick={()=>{
                            // Archived contracts may be PDFs — use the URL embedded in
                            // their body (or client fallback for legacy) rather than
                            // rendering the marker text as a PDF.
                            const ctPdfUrl=getContractPdfUrl(ct,c);
                            if(ctPdfUrl){
                              const a=document.createElement("a");
                              a.href=ctPdfUrl; a.download=`${c.name.replace(/\s+/g,"-")}-contract-v${ct.version||1}.pdf`; a.target="_blank"; a.click();
                            } else if(ct.body) {
                              downloadContractAsPdf(ct.body,`${c.name.replace(/\s+/g,"-")}-contract-v${ct.version||1}`);
                            }
                          }} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"4px 10px",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                            <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.download}</span> PDF
                          </button>
                          )}
                        </div>
                        {ctSigned&&ct.signed_by&&(
                          <div style={{fontSize:11,color:"var(--gm)",fontWeight:600,marginBottom:2,display:"flex",alignItems:"center",gap:4}}>{I.check} Signed by {ct.signed_by}{ct.signed_at?` · ${fmtDateTime(ct.signed_at)}`:""}</div>
                        )}
                        <div style={{fontSize:11,color:"var(--mu)"}}>
                          {ct.sent_at?`Sent ${fmtDateTime(ct.sent_at)}`:ct.contract_date?`Sent ${fmtDate(ct.contract_date)}`:ct.created_at?`Created ${fmtDate(ct.created_at)}`:""}
                          {ct.archived_at?` · Archived ${fmtDate(ct.archived_at)}`:""}
                        </div>
                        {lostPdf&&(
                          <div style={{marginTop:8,fontSize:11,color:"var(--warn-tx)",background:"var(--warn-bg)",border:"1px solid var(--warn-bo)",borderRadius:8,padding:"8px 10px",lineHeight:1.5}}>
                            The PDF for this older version is no longer available — it was replaced when a newer PDF was uploaded. The signature record for this version is preserved above.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── PAYMENTS ── */}
        {tab==="payments"&&(
          <>
            <div className="portal-section-title">Payments</div>
            <div className="portal-section-sub">All payments are processed securely via Stripe. Receipts are emailed automatically.</div>

            <div className="portal-payments-grid">
              <div className="portal-payments-left">
            {/* Summary */}
            <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden",marginBottom:16}}>
              {(()=>{
                const tl=c.tl||[];
                const today=new Date(); today.setHours(0,0,0,0);
                const findDue=(keywords)=>{
                  const step=tl.find(s=>s.d&&keywords.some(k=>(s.l||"").toLowerCase().includes(k)));
                  if(!step?.d) return null;
                  const d=new Date(step.d); return isNaN(d)?null:d;
                };
                const depDue=findDue(["deposit","signing","contract sign","payment"]);
                const balDue=findDue(["balance","final payment","delivery","deliver"]);
                const fmtDue=(due)=>{
                  if(!due) return "Due on signing";
                  const diff=Math.round((due-today)/(1000*60*60*24));
                  if(diff<0) return `Overdue by ${Math.abs(diff)} day${Math.abs(diff)!==1?"s":""}`;
                  if(diff===0) return "Due today";
                  return `Due in ${diff} day${diff!==1?"s":""}`;
                };
                const depStatus=paidDeposit?"Paid":fmtDue(depDue);
                const balStatus=paidBalance?"Paid":!paidDeposit?"Awaiting deposit":fmtDue(balDue)||(balDue?fmtDue(balDue):"Due at delivery");
                return [
                  {l:"Total value",v:c.amt,cls:"g"},
                  {l:depPct>=100?"Full Payment (100%)":`Deposit (${depPct}%)`,v:`${portSym}${fmtMoney(halfDepAmt)}`,cls:paidDeposit?"g":"t",status:depStatus,paid:paidDeposit},
                  ...(depPct<100?[{l:`Balance (${100-depPct}%)`,v:`${portSym}${fmtMoney(halfBalAmt)}`,cls:paidBalance?"g":"",status:balStatus,paid:paidBalance}]:[]),
                ];
              })().map((r,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:i<2?"1px solid var(--bo)":"none"}}>
                  <div>
                    <div style={{fontSize:13,color:"var(--mu)"}}>{r.l}</div>
                    {r.status&&<div style={{fontSize:11,fontWeight:600,color:r.paid?"var(--ok-tx)":r.status.includes("Overdue")?"var(--err-tx)":r.status.includes("Awaiting")?"var(--warn-tx)":"var(--mu)",marginTop:2,display:"flex",alignItems:"center",gap:4}}>{r.paid&&I.check}{r.status}</div>}
                  </div>
                  <div className="num" style={{fontSize:15,fontWeight:700,color:r.cls==="g"?"var(--gm)":r.cls==="t"?"var(--t)":"var(--ink)"}}>{r.v}</div>
                </div>
              ))}
            </div>

            {/* Payment due reminder banners */}
            {(()=>{
              const tl=c.tl||[];
              const today=new Date(); today.setHours(0,0,0,0);
              const findDueDate=(keywords)=>{
                const step=tl.find(s=>s.d&&keywords.some(k=>(s.l||"").toLowerCase().includes(k)));
                if(!step?.d) return null;
                const d=new Date(step.d); return isNaN(d)?null:d;
              };
              const depDue=findDueDate(["deposit","signing","contract sign","full payment","payment"]);
              const balDue=findDueDate(["balance","final payment","delivery","deliver"]);
              const banners=[];
              const reminderSent=!!c.reminderSentAt;
              if(!paidDeposit){
                const depLabel=depPct>=100?"Full payment":"Deposit";
                if(depDue){
                  const diff=Math.round((depDue-today)/(1000*60*60*24));
                  if(diff<0) banners.push({type:"overdue",label:depLabel,days:Math.abs(diff),amt:`${portSym}${fmtMoney(halfDepAmt)}`});
                  else if(signed||reminderSent) banners.push({type:diff===0?"today":"due",label:depLabel,days:diff,amt:`${portSym}${fmtMoney(halfDepAmt)}`});
                } else if(signed||reminderSent) banners.push({type:"due",label:depLabel,days:null,amt:`${portSym}${fmtMoney(halfDepAmt)}`});
              }
              if(depPct<100&&!paidBalance){
                if(balDue){
                  const diff=Math.round((balDue-today)/(1000*60*60*24));
                  if(diff<0) banners.push({type:"overdue",label:"Final payment",days:Math.abs(diff),amt:`${portSym}${fmtMoney(halfBalAmt)}`});
                  else if(paidDeposit) banners.push({type:diff===0?"today":"due",label:"Final payment",days:diff,amt:`${portSym}${fmtMoney(halfBalAmt)}`});
                } else if(paidDeposit) banners.push({type:"due",label:"Final payment",days:null,amt:`${portSym}${fmtMoney(halfBalAmt)}`});
              }
              return banners.map((b,i)=>(
                <div key={i} style={{background:b.type==="overdue"?"var(--err-bg)":b.type==="today"?"var(--warn-bg)":"var(--info-bg)",border:`2px solid ${b.type==="overdue"?"var(--err-bo)":b.type==="today"?"var(--warn-bo)":"var(--warn-bo)"}`,borderRadius:14,padding:"14px 16px",marginBottom:12,display:"flex",alignItems:"flex-start",gap:12}}>
                  <div style={{width:40,height:40,borderRadius:"50%",background:b.type==="overdue"?"#FEE2E2":b.type==="today"?"#FFEDD5":"#FEF3C7",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <span style={{fontSize:20}}>{b.type==="overdue"?"!":"⏰"}</span>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:700,color:b.type==="overdue"?"var(--err-tx)":b.type==="today"?"var(--warn-tx)":"var(--warn-tx)",lineHeight:1.3}}>
                      {b.type==="overdue"?`${b.label} overdue by ${b.days} day${b.days!==1?"s":""}`:b.type==="today"?`${b.label} due today`:b.days!==null?`${b.label} due in ${b.days} day${b.days!==1?"s":""}`:`${b.label} due now`}
                    </div>
                    <div style={{fontSize:12,fontWeight:600,color:b.type==="overdue"?"var(--err-tx)":b.type==="today"?"var(--warn-tx)":"var(--info-tx)",marginTop:3}}>
                      {b.amt} outstanding
                    </div>
                    <div style={{fontSize:11,color:b.type==="overdue"?"#7F1D1D":"#78350F",marginTop:4,lineHeight:1.4}}>
                      {b.type==="overdue"?"This payment is past due. Please make your payment as soon as possible to avoid any disruption.":b.type==="today"?"This payment is due today. Please complete your payment below.":"Please ensure payment is made on time to keep your project on track."}
                    </div>
                  </div>
                </div>
              ));
            })()}
              </div>
              <div className="portal-payments-right">

            {/* Stripe-style payment cards */}
            {payState==="processing"&&(
              <div style={{background:"var(--gl)",borderRadius:14,padding:20,textAlign:"center",marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>{I.hourglass}</div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--gm)"}}>Processing payment…</div>
                <div style={{fontSize:12,color:"var(--mu)",marginTop:4}}>Please don't close this page.</div>
              </div>
            )}
            {payState==="done"&&(
              <div style={{background:"var(--gl)",borderRadius:14,padding:20,textAlign:"center",marginBottom:12}}>
                <div style={{marginBottom:8,color:"var(--gm)",display:"flex",justifyContent:"center"}}>{I.checkCircle}</div>
                <div style={{fontSize:15,fontWeight:600,color:"var(--gm)"}}>All payments complete!</div>
                <div style={{fontSize:12,color:"var(--mu)",marginTop:4}}>A receipt has been sent to your email.</div>
              </div>
            )}
            {payState==="depositDone"&&(
              <div style={{background:"var(--gl)",borderRadius:14,padding:12,marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
                <span style={{display:"flex",color:"var(--gm)"}}>{I.checkCircle}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>Deposit received — thank you!</div>
                  <div style={{fontSize:11,color:"var(--mu)"}}>You can now pay your final balance below.</div>
                </div>
              </div>
            )}
            {payError&&(
              <div style={{background:"var(--err-bg)",border:"1.5px solid var(--err-bo)",borderRadius:14,padding:12,marginBottom:12,display:"flex",alignItems:"flex-start",gap:10}}>
                <span style={{display:"inline-flex",color:"var(--warn-tx)"}}>{I.alertTri}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--err-tx)",marginBottom:2}}>Payment couldn't start</div>
                  <div style={{fontSize:11,color:"var(--err-tx)",opacity:.85}}>{payError}</div>
                </div>
                <button onClick={()=>setPayError(null)} style={{background:"none",border:"none",color:"var(--err-tx)",fontSize:16,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
              </div>
            )}

            {c.archived&&(
              <div style={{background:"var(--warn-bg)",borderRadius:12,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                <span style={{display:"flex"}}>{I.archive}</span>
                <div style={{fontSize:12,color:"var(--info-tx)"}}>This project is archived. Payments are no longer accepted.</div>
              </div>
            )}
            {!c.archived&&!paidDeposit&&!payState&&!signed&&c.contractSentToPortal&&(
              <div style={{background:"var(--warn-bg)",borderRadius:14,border:"1.5px solid var(--warn-bo)",padding:"14px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
                <span style={{display:"flex"}}>{I.sign}</span>
                <div style={{fontSize:12,color:"var(--info-tx)",lineHeight:1.5}}>Please <strong>sign your contract</strong> before making a payment.</div>
              </div>
            )}
            {!c.archived&&!paidDeposit&&!payState&&signed&&(
              acct.stripeConnect?.chargesEnabled?(
                <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid var(--bo)",padding:20,marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}>{depPct>=100?"Pay Full Fee":"Pay Deposit"} · {portSym}{fmtMoney(halfDepAmt)}</div>
                  <div style={{fontSize:12,color:"var(--mu)",marginBottom:16,lineHeight:1.5}}>You'll be taken to Stripe's secure checkout to enter your card details. Your information is never stored by Lancer.</div>
                  <button onClick={()=>handlePay("deposit")} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Pay {depPct>=100?"full fee":"deposit"} {portSym}{fmtMoney(halfDepAmt)} via Stripe
                  </button>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:10}}>
                    <span style={{fontSize:11,color:"var(--mu)"}}>Secured by</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#635BFF"}}>stripe</span>
                  </div>
                </div>
              ):(
                <div style={{background:"var(--warn-bg,#FFFBEB)",borderRadius:14,border:"1.5px solid var(--warn-bo,#F6AD55)",padding:20,marginBottom:12,textAlign:"center"}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>{I.creditcard}</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx,#C05621)",marginBottom:4}}>Payments Not Yet Available</div>
                  <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Your freelancer is still setting up their payment account. They'll let you know once payments are ready.</div>
                </div>
              )
            )}

            {!c.archived&&paidDeposit&&!paidBalance&&!payState&&depPct<100&&( /* balance available immediately after deposit — hidden for full payment */
              acct.stripeConnect?.chargesEnabled?(
                <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid var(--bo)",padding:20,marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:4}}>Pay Final Balance · {portSym}{fmtMoney(halfBalAmt)}</div>
                  <div style={{fontSize:12,color:"var(--mu)",marginBottom:16}}>You can pay your final balance now or wait until delivery — your choice.</div>
                  <button onClick={()=>handlePay("balance")} style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Pay {portSym}{fmtMoney(halfBalAmt)} securely
                  </button>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:10}}>
                    <span style={{fontSize:11,color:"var(--mu)"}}>Powered by</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#635BFF"}}>stripe</span>
                  </div>
                </div>
              ):(
                <div style={{background:"var(--warn-bg,#FFFBEB)",borderRadius:14,border:"1.5px solid var(--warn-bo,#F6AD55)",padding:20,marginBottom:12,textAlign:"center"}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>{I.creditcard}</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx,#C05621)",marginBottom:4}}>Payments Not Yet Available</div>
                  <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>Your freelancer is still setting up their payment account. They'll let you know once payments are ready.</div>
                </div>
              )
            )}

            {(paidDeposit&&(depPct>=100||paidBalance))&&(
              <div style={{background:"var(--gl)",borderRadius:14,padding:20,textAlign:"center"}}>
                <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>{I.party}</div>
                <div style={{fontSize:15,fontWeight:600,color:"var(--gm)"}}>All paid up!</div>
                <div style={{fontSize:12,color:"var(--mu)",marginTop:4}}>You're all set. Receipts have been sent to your email.</div>
              </div>
            )}
              </div>
            </div>

            {/* Additional invoices from freelancer */}
            {(c.invoices||[]).length>0&&(
              <div style={{marginTop:20}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:10}}>Additional Invoices</div>
                <div className="portal-invoices-grid">
                  {(c.invoices||[]).map(inv=>(
                    <div key={inv.id} style={{background:"var(--card)",borderRadius:14,border:`1.5px solid ${inv.paid?"var(--gl)":"var(--bo)"}`,padding:16}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:inv.paid?0:12}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{inv.description}</div>
                          <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{inv.createdAt}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:16,fontWeight:700,color:inv.paid?"var(--gm)":"var(--ink)"}}>{acct.currencySymbol||"$"}{Number(inv.amount).toLocaleString()}</div>
                          {inv.paid&&<div style={{fontSize:11,color:"var(--gm)",fontWeight:600,display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end"}}>{I.check} Paid</div>}
                        </div>
                      </div>
                      {!c.archived&&!inv.paid&&!payState&&(
                        <button onClick={()=>handlePay("invoice",inv.id)}
                          style={{width:"100%",background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Pay {acct.currencySymbol||"$"}{fmtMoney(inv.amount)} via Stripe
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Refunds from freelancer */}
            {(c.refunds||[]).length>0&&(
              <div style={{marginTop:20}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:10}}>Refunds</div>
                <div className="portal-refunds-grid">
                  {(c.refunds||[]).map(r=>(
                    <div key={r.id} style={{background:"var(--gl)",borderRadius:14,border:"1.5px solid var(--bo)",padding:"14px 16px",display:"flex",alignItems:"center",gap:12}}>
                      <div style={{width:38,height:38,borderRadius:10,background:"var(--card)",border:"1.5px solid var(--bo)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>↩</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:600,color:"var(--gm)",marginBottom:2}}>{r.reason}</div>
                        <div style={{fontSize:11,color:"var(--mu)"}}>{r.date} · Refunded via Stripe</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:16,fontWeight:700,color:"var(--gm)"}}>{acct.currencySymbol||"$"}{Number(r.amount).toLocaleString()}</div>
                        <div style={{fontSize:10,fontWeight:600,color:"var(--gm)",marginTop:2,display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end"}}>{I.check} Refunded</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginTop:8}}>Refunds typically arrive within 5–10 business days.</div>
              </div>
            )}

            {/* Cancellation adjustment — shown when cancellation was requested */}
            {c.cancellationRequested&&(
              <div style={{marginTop:20}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:10}}>Cancellation Adjustment</div>
                <div style={{background:"var(--warn-bg)",borderRadius:14,border:"1.5px solid var(--warn-bo)",padding:"16px",display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:38,height:38,borderRadius:10,background:"var(--card)",border:"1.5px solid var(--warn-bo)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"var(--warn-tx)"}}>{I.ban}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>Booking Cancelled</div>
                      <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{c.cancelledAt?new Date(c.cancelledAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):"Date pending"}</div>
                    </div>
                  </div>
                  {(()=>{
                    const sym=acct.currencySymbol||"$";
                    const totalAmt=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
                    const depAmt=c.depositAmt||round2(totalAmt*(c.depositPct||50)/100);
                    const policy=acct.cancellationPolicy||{};
                    const items=[];
                    // Deposit status
                    if(paidDeposit){
                      if(policy.depositRefund&&canCancelFree){
                        items.push({label:"Deposit refund",amount:depAmt,status:"Refundable",statusColor:"var(--ok-tx)"});
                      } else if(!canCancelFree&&policy.latePolicy==="full"){
                        items.push({label:"Deposit refund",amount:depAmt,status:"Refundable",statusColor:"var(--ok-tx)"});
                      } else {
                        items.push({label:"Deposit",amount:depAmt,status:"Non-refundable",statusColor:"var(--err-tx)"});
                      }
                    }
                    // Late cancellation fee
                    if(!canCancelFree&&lateChargeAmt>0){
                      items.push({label:"Cancellation fee",amount:lateChargeAmt,status:"Outstanding",statusColor:"var(--warn-tx)"});
                    }
                    // Refund of overpayment (if paid more than fee)
                    if(!canCancelFree&&policy.latePolicy==="partial"){
                      const refundAmt=Math.round(totalAmt*(policy.lateRefundPct||50)/100);
                      if(refundAmt>0) items.push({label:"Partial refund",amount:refundAmt,status:"Pending",statusColor:"var(--ok-tx)"});
                    }
                    if(items.length===0) items.push({label:"No charges apply",amount:0,status:"Free cancellation",statusColor:"var(--ok-tx)"});
                    return items.map((item,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"var(--card)",borderRadius:10,border:"1px solid var(--bo)"}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{item.label}</div>
                          <div style={{fontSize:10,fontWeight:600,color:item.statusColor,marginTop:2}}>{item.status}</div>
                        </div>
                        {item.amount>0&&<div style={{fontSize:15,fontWeight:700,color:"var(--ink)"}}>{sym}{fmtMoney(item.amount)}</div>}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── MESSAGES ── */}
        {tab==="messages"&&(
          <div className="portal-messages-wrap" style={{display:"flex",flexDirection:"column",height:"100%",minHeight:"60vh"}}>
            <div style={{marginBottom:8}}>
              <div className="portal-section-title">Messages</div>
              <div className="portal-section-sub">Chat directly with {acct.businessName||acct.name}.</div>
            </div>
            {/* Scrollable message list */}
            <div ref={portalMsgsRef} style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:12,paddingBottom:8,minHeight:0}}>
              {msgs.length===0?(
                <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 20px",textAlign:"center",gap:12}}>
                  <div style={{color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.chatBubble}</div>
                  <div style={{fontSize:14,fontWeight:600,color:"var(--ink)"}}>Start the conversation</div>
                  <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6}}>Send a message to {acct.businessName||acct.name}. They'll see it in their Lancer inbox and can reply directly.</div>
                </div>
              ):msgs.map((m,i)=>(
                <div key={i} style={{display:"flex",flexDirection:"column",alignItems:m.f==="client"?"flex-end":"flex-start"}}>
                  {m.f==="them"&&<div style={{fontSize:11,color:"var(--mu)",marginBottom:4,paddingLeft:4}}>{`Sent by ${m._sentBy||acct.name}`}</div>}
                  <div style={{maxWidth:"78%",padding:"11px 14px",borderRadius:16,fontSize:13,lineHeight:1.5,
                    background:m.f==="client"?"var(--g)":"#f0f4f2",
                    color:m.f==="client"?"#fff":"var(--ink)",
                    border:"none",
                    borderBottomRightRadius:m.f==="client"?4:16,
                    borderBottomLeftRadius:m.f==="them"?4:16}}>
                    {m.t}
                  </div>
                  <div style={{fontSize:10,color:"var(--mu)",marginTop:4,paddingLeft:4,paddingRight:4}}>{m.ts}</div>
                </div>
              ))}
            </div>
            {/* Input — always at bottom, never overlapping */}
            {c.archived?(
              <div style={{borderTop:"1px solid var(--bo)",paddingTop:12,flexShrink:0}}>
                <div style={{background:"var(--warn-bg)",borderRadius:10,padding:"10px 14px",fontSize:12,color:"var(--info-tx)",textAlign:"center"}}>This project is archived. Messaging is disabled.</div>
              </div>
            ):(
            <div style={{display:"flex",gap:10,alignItems:"flex-end",borderTop:"1px solid var(--bo)",paddingTop:12,background:"var(--cr)",flexShrink:0}}>
              <textarea
                value={newMsg}
                onChange={e=>setNewMsg(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();handleMsg();}}}
                placeholder={`Message ${acct.name}…`}
                style={{flex:1,background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"11px 14px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",minHeight:44,maxHeight:120,lineHeight:1.5}}
                rows={1}
              />
              <button onClick={handleMsg} style={{width:42,height:42,background:"var(--g)",border:"none",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,color:"#fff",flexShrink:0}}>➤</button>
            </div>
            )}
          </div>
        )}

        {/* ── QUESTIONNAIRE ── */}
        {tab==="intake"&&(
          <div className="portal-intake-wrap">
            {(submitted||c.archived)?(
              <div>
                <div className="intake-done">
                  <div className="intake-done-icon" style={{display:"flex",alignItems:"center",justifyContent:"center"}}>{I.checkCircle}</div>
                  <div className="intake-done-title">Questionnaire submitted</div>
                  <div className="intake-done-sub">{acct.name} has received your answers and will use them to prepare for your project.</div>
                </div>
                <div style={{marginTop:16}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:10}}>Your answers</div>
                  <div style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",overflow:"hidden"}}>
                    {questions.map((q,i)=>{
                      const answer=form[q.key];
                      if(!answer?.trim()) return null;
                      return (
                        <div key={q.key} style={{padding:"12px 14px",borderBottom:i<questions.length-1?"1px solid var(--bo)":"none"}}>
                          <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:4}}>{q.label}</div>
                          <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{answer}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ):(
              <>
                <div className="portal-section-title">Session Details</div>
                <div className="portal-section-sub">Help {acct.name} prepare by answering a few questions. Takes about 3 minutes.</div>

                {/* Progress bar */}
                <div style={{background:"var(--card)",borderRadius:12,padding:"12px 14px",border:"1px solid var(--bo)",marginBottom:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <span style={{fontSize:12,fontWeight:600,color:"var(--gm)"}}>{filled} of {questions.length} answered</span>
                    <span style={{fontSize:11,color:"var(--mu)"}}>Answer at least 1 to submit</span>
                  </div>
                  <div style={{height:6,background:"var(--bo)",borderRadius:99,overflow:"hidden"}}>
                    <div style={{height:"100%",width:((filled/questions.length)*100)+"%",background:filled>=1?"var(--gm)":"var(--t)",borderRadius:99,transition:"width .3s"}}/>
                  </div>
                </div>

                <div className="iform" style={{paddingBottom:90}}>
                  {questions.map((q,i)=>(
                    <div className={"ifield"+(q.type!=="input"?" ifield-wide":"")} key={q.key}>
                      <label className="iflabel">
                        {q.label}
                        {form[q.key]?.trim()&&<span style={{marginLeft:6,color:"var(--gm)",display:"inline-flex",alignItems:"center",verticalAlign:"middle"}}>{I.check}</span>}
                      </label>
                      {q.type==="input"
                        ?<input className="ifinput" placeholder={q.placeholder} value={form[q.key]||""} onChange={e=>setForm(f=>({...f,[q.key]:e.target.value}))}/>
                        :<textarea className="iftarea" placeholder={q.placeholder} value={form[q.key]||""} onChange={e=>setForm(f=>({...f,[q.key]:e.target.value}))}/>
                      }
                    </div>
                  ))}
                </div>

                {/* Sticky submit */}
                <div style={{position:"sticky",bottom:0,background:"var(--cr)",padding:"14px 0 24px",borderTop:"1px solid var(--bo)",marginTop:-1}}>
                  <button
                    className="isubmit"
                    disabled={!canSubmit}
                    onClick={handleIntakeSubmit}
                    style={{opacity:canSubmit?1:.5,fontSize:15,padding:16}}
                  >
                    {canSubmit
                      ?<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:5}}>{I.check}</span> Submit Questionnaire</>
                      :"Answer at least 1 question to submit"
                    }
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Portal footer text (from freelancer branding) ──
            White-label rule: Studio replaces the "Powered by Lancer" default
            with the freelancer's custom footer (or hides the line entirely
            if their custom footer is blank). Pro/Free always shows the
            default "Powered by Lancer" attribution when no custom is set. */}
        {(()=>{
          const wl=isWhiteLabel(acct);
          const customFooter=(acct.footerText||"").trim();
          if(wl&&!customFooter) return null;
          const display=wl?customFooter:(customFooter||"Powered by Lancer");
          return (
            <div style={{marginTop:32,paddingTop:20,borderTop:"1px solid var(--bo)",textAlign:"center",fontSize:11,color:"var(--mu)",lineHeight:1.5}}>
              {display}
            </div>
          );
        })()}

      </div>

      {/* ── Email prompt modal — appears when trying to pay without a valid email ── */}
      {emailPrompt&&(()=>{
        const draft=emailPrompt.draftEmail||"";
        const trimmed=draft.trim();
        const isValid=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)&&trimmed.length>=5;
        const submit=async()=>{
          if(!isValid) return;
          // Persist the email via onContactUpdate (which calls portal_update_contact RPC in portal mode)
          if(onContactUpdate) onContactUpdate(c.id,trimmed,c.phone||"");
          // Mutate the client prop optimistically so the next handlePay sees the new email.
          c.email=trimmed;
          const pendingType=emailPrompt.pendingType;
          const pendingInvoiceId=emailPrompt.pendingInvoiceId;
          setEmailPrompt(null);
          // Small delay to let any state updates settle, then retry checkout
          setTimeout(()=>handlePay(pendingType,pendingInvoiceId),150);
        };
        return(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
            <div style={{background:"var(--cr)",borderRadius:18,padding:24,width:"100%",maxWidth:400,boxShadow:"0 20px 60px rgba(0,0,0,.3)"}}>
              <div style={{display:"flex",justifyContent:"center",marginBottom:10}}>{I.mail}</div>
              <div style={{fontSize:17,fontWeight:700,color:"var(--ink)",marginBottom:6}}>Enter your email to continue</div>
              <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5,marginBottom:16}}>
                We need a valid email address to send you a payment receipt and Stripe confirmation. This will also be saved to your client record.
              </div>
              <input
                type="email"
                autoFocus
                placeholder="you@example.com"
                value={draft}
                onChange={e=>setEmailPrompt(prev=>prev?{...prev,draftEmail:e.target.value}:prev)}
                onKeyDown={e=>{if(e.key==="Enter"&&isValid) submit();}}
                style={{width:"100%",background:"var(--card)",border:"1.5px solid "+(draft&&!isValid?"var(--err-bo)":"var(--bo)"),borderRadius:10,padding:"12px 14px",fontSize:14,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",marginBottom:8}}
              />
              {draft&&!isValid&&<div style={{fontSize:11,color:"var(--err-tx)",marginBottom:8}}>Please enter a valid email address.</div>}
              <div style={{display:"flex",gap:10,marginTop:12}}>
                <button onClick={()=>setEmailPrompt(null)} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                <button onClick={submit} disabled={!isValid} style={{flex:2,background:isValid?"var(--g)":"var(--bo)",color:isValid?"#fff":"var(--mu)",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:isValid?"pointer":"default",fontFamily:"'Inter',sans-serif"}}>Save & Continue</button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

/* ── FREELANCER NOTES ── */
function FreelancerNotes({c, onNotesUpdate}){
  const [editing,setEditing]=useState(false);
  const [text,setText]=useState(c.freelancerNotes||"");
  const [saved,setSaved]=useState(false);
  const locked=!!c.archived;

  // Sync when parent updates c.freelancerNotes (e.g. after reload)
  useEffect(()=>{ if(!editing) setText(c.freelancerNotes||""); },[c.freelancerNotes]);
  // If the client becomes archived while we're editing, exit edit mode
  useEffect(()=>{ if(locked&&editing){ setEditing(false); setText(c.freelancerNotes||""); } },[locked]);

  const save=()=>{
    if(locked) return;
    onNotesUpdate(c.id,text,undefined);
    setEditing(false);
    setSaved(true);
    setTimeout(()=>setSaved(false),2000);
  };

  return (
    <div className="msec" style={{marginTop:12}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Notes</div>
          <div style={{fontSize:10,color:"var(--mu)",marginTop:1}}>These notes are used when generating AI contracts</div>
        </div>
        {!editing&&!locked&&(
          <button onClick={()=>setEditing(true)} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
            {I.edit} {text?"Edit":"Add note"}
          </button>
        )}
        {locked&&<span style={{fontSize:10,color:"var(--mu)",fontStyle:"italic"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Archived</span>}
      </div>
      {editing?(
        <div>
          <textarea
            autoFocus
            value={text}
            onChange={e=>setText(e.target.value)}
            placeholder={`Private notes about ${c.name.split(" ")[0]}. Not visible to the client.`}
            style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--g)",borderRadius:10,padding:"11px 13px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:90}}
          />
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button onClick={save} style={{flex:1,background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              Save
            </button>
            <button onClick={()=>{setText(c.freelancerNotes||"");setEditing(false);}} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"10px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              Cancel
            </button>
          </div>
        </div>
      ):(
        text?(
          <div style={{background:"var(--card)",borderRadius:10,border:"1px solid var(--bo)",padding:"11px 13px",fontSize:13,color:"var(--ink)",lineHeight:1.6,position:"relative"}}>
            {text}
            {saved&&<span style={{position:"absolute",top:8,right:10,fontSize:10,color:"var(--gm)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:3}}>Saved {I.check}</span>}
          </div>
        ):(
          locked?(
            <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"14px 13px",fontSize:12,color:"var(--mu)",fontStyle:"italic",textAlign:"center"}}>No notes on this archived client.</div>
          ):(
            <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"14px 13px",fontSize:12,color:"var(--mu)",fontStyle:"italic",textAlign:"center",cursor:"pointer"}} onClick={()=>setEditing(true)}>
              Tap to add private notes (sensitivities, preferences, reminders)…
            </div>
          )
        )
      )}
    </div>
  );
}

/* ── SURPRISE / SPECIAL ARRANGEMENTS — NOT included in contract ── */
function SurpriseNotes({c, onNotesUpdate}){
  const [editing,setEditing]=useState(false);
  const [text,setText]=useState(c.surpriseNotes||"");
  const [saved,setSaved]=useState(false);
  const locked=!!c.archived;

  // Sync when parent updates c.surpriseNotes
  useEffect(()=>{ if(!editing) setText(c.surpriseNotes||""); },[c.surpriseNotes]);
  useEffect(()=>{ if(locked&&editing){ setEditing(false); setText(c.surpriseNotes||""); } },[locked]);

  const save=()=>{ if(locked) return; onNotesUpdate(c.id,null,text); setEditing(false); setSaved(true); setTimeout(()=>setSaved(false),2000); };
  return (
    <div className="msec" style={{marginTop:8}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Surprise / Special Arrangements <span style={{display:"inline-flex",verticalAlign:"middle",marginLeft:4}}>{I.gift}</span></div>
          <div style={{fontSize:10,color:"var(--mu)",marginTop:1}}>Private only — never included in the contract</div>
        </div>
        {!editing&&!locked&&<button onClick={()=>setEditing(true)} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{text?"Edit":"Add"}</button>}
        {locked&&<span style={{fontSize:10,color:"var(--mu)",fontStyle:"italic"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Archived</span>}
      </div>
      {editing?(
        <div>
          <textarea autoFocus value={text} onChange={e=>setText(e.target.value)}
            placeholder="e.g. Surprise proposal during shoot — don't mention ring. Client wants extra prints for grandma."
            style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--t)",borderRadius:10,padding:"11px 13px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:80}}/>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button onClick={save} style={{flex:1,background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Save</button>
            <button onClick={()=>{setText(c.surpriseNotes||"");setEditing(false);}} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"10px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
          </div>
        </div>
      ):(
        text?<div style={{background:"var(--warn-bg)",borderRadius:10,border:"1px solid var(--info-bo)",padding:"11px 13px",fontSize:13,color:"var(--ink)",lineHeight:1.6,position:"relative"}}>
          <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.gift}</span>{text}{saved&&<span style={{position:"absolute",top:8,right:10,fontSize:10,color:"var(--gm)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:3}}>Saved {I.check}</span>}
        </div>
        :(locked?(
          <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"12px 13px",fontSize:12,color:"var(--mu)",fontStyle:"italic",textAlign:"center"}}>No special arrangements on this archived client.</div>
        ):(
          <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"12px 13px",fontSize:12,color:"var(--mu)",fontStyle:"italic",textAlign:"center",cursor:"pointer"}} onClick={()=>setEditing(true)}>
            Tap to add surprise or special arrangement details…
          </div>
        ))
      )}
    </div>
  );
}


// Module-level cache so briefing persists when modal is closed and reopened
const AI_BRIEFING_CACHE = new Map();

function AiBriefing({c, acct}){
  const cached = AI_BRIEFING_CACHE.get(c.id) || { status: "idle", text: "" };
  const [status, setStatus] = useState(cached.status==="done"?"done":"idle");
  const [text, setText] = useState(cached.text||"");
  const hasIntake = !!(c.intakeSubmitted || (c.intake && Object.keys(c.intake).length > 0));

  const generate = async () => {
    if(acct.billingPlan==="free") return;
    setStatus("loading");
    setText("");
    AI_BRIEFING_CACHE.set(c.id, {status:"loading", text:""});
    const intake = c.intake || {};
    const questions = (c.questionnaireQuestions&&c.questionnaireQuestions.length>0)?c.questionnaireQuestions:getDefaultQuestionnaire(c.vert||acct.vert);
    const answered = questions
      .filter(q => intake[q.key]?.trim())
      .map(q => `- ${q.label}: ${intake[q.key]}`)
      .join("\n");
    const prompt = `You are a pre-project briefing assistant for a ${acct.role||acct.vert} named ${acct.name}.

Client: ${c.name}
Project type: ${c.type}
${answered ? `\nClient questionnaire answers:\n${answered}` : "\nNo questionnaire answers yet."}
Fee: ${c.amt}
Project step: ${c.step}

Write a concise, practical pre-project briefing for ${acct.name} in 3-4 sentences. Focus on: what to prioritise, any client preferences or sensitivities, and one financial or admin note. Write in second person. Be specific. No bullet points.`;

    try {
      const res = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: aiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1000,
          stream: true,
          messages: [{role: "user", content: prompt}]
        })
      });
      if(!res.ok) throw new Error("AI error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let accumulated = "";
      while(true){
        const {done, value} = await reader.read();
        if(done) break;
        buf += decoder.decode(value, {stream: true});
        const lines = buf.split("\n");
        buf = lines.pop();
        for(const line of lines){
          if(line.startsWith("data: ")){
            const data = line.slice(6);
            if(data === "[DONE]") continue;
            try{
              const parsed = JSON.parse(data);
              if(parsed.type === "content_block_delta" && parsed.delta?.type === "text_delta"){
                accumulated += parsed.delta.text;
                setText(accumulated);
                AI_BRIEFING_CACHE.set(c.id, {status:"loading", text:accumulated});
              }
            } catch(e){}
          }
        }
      }
      setStatus("done");
      AI_BRIEFING_CACHE.set(c.id, {status:"done", text:accumulated});
    } catch(e){
      setStatus("error");
      AI_BRIEFING_CACHE.set(c.id, {status:"idle", text:""});
    }
  };

  return (
    <div className="msec" style={{marginTop:16}}>
      <div className="mstl">AI Briefing</div>
      <div className="aicard">
        <div className="aiico">✦</div>
        <div style={{flex:1,minWidth:0}}>
          <div className="ailbl">Pre-session summary</div>

          {/* No intake yet */}
          {status==="idle"&&!hasIntake&&(
            <div style={{fontSize:13,color:"rgba(255,255,255,.5)",lineHeight:1.5}}>
              Briefing available once the client submits their questionnaire. Send them the portal link to get started.
            </div>
          )}

          {/* Intake received — ready to generate */}
          {status==="idle"&&hasIntake&&(
            <div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.65)",marginBottom:12,lineHeight:1.5,display:"flex",alignItems:"center",gap:5}}>
                Questionnaire received <span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> — ready to generate your pre-session briefing.
              </div>
              {acct.billingPlan==="free"?(
                <div>
                  <button disabled style={{background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.5)",border:"1.5px solid rgba(255,255,255,.15)",borderRadius:10,padding:"9px 16px",fontSize:12,fontWeight:600,cursor:"not-allowed",fontFamily:"'Inter',sans-serif"}}>
                    <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Pro — Upgrade to unlock
                  </button>
                  <div style={{fontSize:11,color:"rgba(255,255,255,.4)",marginTop:6,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>Go to Settings <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.chevron}</span> Billing & plan to upgrade.</div>
                </div>
              ):(
                <button onClick={generate} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:10,padding:"9px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  ✦ Generate Briefing
                </button>
              )}
            </div>
          )}

          {/* Loading */}
          {status==="loading"&&!text&&(
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0"}}>
              <div style={{display:"flex",gap:4}}>
                {[0,1,2].map(i=>(
                  <div key={i} style={{width:6,height:6,borderRadius:"50%",background:"rgba(255,255,255,.4)",animation:"pulse 1.2s ease-in-out "+(i*0.2)+"s infinite"}}/>
                ))}
              </div>
              <span style={{fontSize:12,color:"rgba(255,255,255,.5)"}}>Generating briefing…</span>
            </div>
          )}

          {/* Streaming / done text */}
          {text&&<div className="aitxt">{text}</div>}

          {/* Error */}
          {status==="error"&&(
            <div style={{fontSize:13,color:"rgba(255,255,255,.5)"}}>
              Couldn't generate briefing.{" "}
              <span style={{color:"var(--t)",cursor:"pointer",textDecoration:"underline"}} onClick={generate}>Retry</span>
            </div>
          )}

          {/* Regenerate after done */}
          {status==="done"&&acct.billingPlan!=="free"&&(
            <div style={{marginTop:10}}>
              <button onClick={generate} style={{background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.7)",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                ↻ Regenerate
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
    </div>
  );
}


// Phase 4 — Team on this project. Lists full seats + guests, with owner-only
// controls to add/remove full-seat members, swap the project lead, and invite
// guests. Hidden from guests entirely (RLS already gates the underlying data;
// this is just the UI gate). Members see the panel but every mutation
// affordance is gone — they're read-only on assignments.
function TeamOnProjectPanel({c,acct}){
  const isGuest=acct?.workspaceRole==="guest";
  const isOwner=acct?.workspaceRole==="owner"||acct?.workspaceRole==null;
  // effectivePlan so the panel stays visible during a Studio cancellation
  // grace period (until plan_expiry_date) — guest seats on a project shouldn't
  // disappear from the UI the moment the user cancels.
  const isStudio=effectivePlan(acct)==="studio";
  const [members,setMembers]=useState([]);
  const [allWorkspaceMembers,setAllWorkspaceMembers]=useState([]);
  const [loading,setLoading]=useState(false);
  const [mode,setMode]=useState(null); // null | "addMember" | "addGuest"
  const [email,setEmail]=useState("");
  const [memberToAdd,setMemberToAdd]=useState(""); // workspace_member_id selected from dropdown
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [inviteLink,setInviteLink]=useState(null);

  const refresh=async()=>{
    if(!c?.id) return;
    setLoading(true);
    const {data}=await studioListClientMembers(c.id);
    setMembers(data||[]);
    setLoading(false);
  };
  // Pull the full workspace roster so the "+ Add team member" dropdown knows
  // who's available to assign. Filtered down to active full seats not already
  // on the project at render time.
  const refreshRoster=async()=>{
    if(!acct?.workspaceId) return;
    const {data}=await dbGetWorkspaceMembers(acct.workspaceId);
    setAllWorkspaceMembers(Array.isArray(data)?data:[]);
  };
  useEffect(()=>{ refresh(); refreshRoster(); },[c?.id,acct?.workspaceId]);

  // Hide entirely for guests, free/pro accounts, or when no workspace context.
  if(isGuest||!isStudio||!acct?.workspaceId) return null;

  const closeForm=()=>{ setMode(null); setEmail(""); setMemberToAdd(""); setErr(""); setInviteLink(null); };

  // Build the dropdown list: workspace full seats NOT already assigned to this
  // project. Owner is always present on every project so we exclude them too —
  // they can't be unassigned anyway.
  const assignedIds=new Set(members.map(m=>m.workspace_member_id));
  const availableToAdd=allWorkspaceMembers.filter(m=>
    (m.role==="member") && m.status==="active" && !assignedIds.has(m.member_id)
  );

  const submitAddMember=async()=>{
    setErr("");
    if(!memberToAdd){ setErr("Pick a team member"); return; }
    setBusy(true);
    const {error}=await studioAddMemberToClient(c.id,memberToAdd);
    setBusy(false);
    if(error){ setErr(error.message||"Could not add member"); return; }
    closeForm();
    await refresh();
  };

  const submitAddGuest=async()=>{
    setErr(""); setInviteLink(null);
    const v=email.trim();
    if(!v||v.indexOf("@")<2){ setErr("Enter a valid email"); return; }
    setBusy(true);
    const {data,error}=await studioCreateGuestInvite(acct.workspaceId,v,c.id);
    setBusy(false);
    if(error){ setErr(error.message||"Could not invite guest"); return; }
    if(data?.existing_member_id){ closeForm(); await refresh(); return; }
    if(data?.token){ setInviteLink(`${window.location.origin}/?invite=${data.token}`); }
    setEmail("");
    await refresh();
  };

  const removeMember=async(m)=>{
    if(m.role==="owner"){ alert("The workspace owner is always on every project."); return; }
    if(m.role==="guest"){
      if(!confirm("Remove this guest from the project?")) return;
      const {error}=await studioRemoveGuestFromProject(m.workspace_member_id,c.id);
      if(error){ alert(error.message||"Could not remove guest"); return; }
    } else {
      const warn=m.is_lead
        ?`${m.user_name||m.user_email} is the project lead. Removing them reassigns lead to you. Continue?`
        :`Remove ${m.user_name||m.user_email} from this project?`;
      if(!confirm(warn)) return;
      const {error}=await studioRemoveMemberFromClient(c.id,m.workspace_member_id);
      if(error){ alert(error.message||"Could not remove member"); return; }
    }
    await refresh();
  };

  const makeLead=async(m)=>{
    if(m.is_lead) return;
    if(m.role==="guest"){ alert("Guests can't be project lead."); return; }
    const {error}=await studioSetClientLead(c.id,m.workspace_member_id);
    if(error){ alert(error.message||"Could not set lead"); return; }
    await refresh();
  };

  return (
    <div className="msec" style={{marginTop:16}}>
      <div className="mstl" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
        <span>Team on this project</span>
        {isOwner&&(
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>{ if(mode==="addMember") closeForm(); else { setMode("addMember"); setErr(""); setInviteLink(null); } }} style={{background:mode==="addMember"?"var(--bo)":"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",textTransform:"none",letterSpacing:0}}>
              {mode==="addMember"?"Cancel":"+ Add team member"}
            </button>
            <button onClick={()=>{ if(mode==="addGuest") closeForm(); else { setMode("addGuest"); setErr(""); setInviteLink(null); } }} style={{background:mode==="addGuest"?"var(--bo)":"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",textTransform:"none",letterSpacing:0}}>
              {mode==="addGuest"?"Cancel":"+ Add guest"}
            </button>
          </div>
        )}
      </div>
      <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden",marginBottom:10}}>
        {loading?(
          <div style={{fontSize:12,color:"var(--mu)",padding:"14px 16px"}}>Loading…</div>
        ):members.length===0?(
          <div style={{fontSize:12,color:"var(--mu)",padding:"14px 16px"}}>No team members yet.</div>
        ):(
          <div>
            {members.map((m,idx)=>{
              const canBecomeLead=isOwner&&!m.is_lead&&m.role!=="guest";
              const canRemove=isOwner&&m.role!=="owner";
              return (
                <div key={m.member_id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderBottom:idx<members.length-1?"1px solid var(--bo)":"none"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {m.user_name||m.user_email}
                      {m.is_lead&&<span style={{marginLeft:8,fontSize:9,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",color:"var(--gm)",background:"var(--gl)",padding:"2px 6px",borderRadius:99}}>Lead</span>}
                    </div>
                    <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{m.user_email} · {m.role==="owner"?"Owner":m.role==="member"?"Member · Full access":"Guest · This project only"}</div>
                  </div>
                  {canBecomeLead&&(
                    <button onClick={()=>makeLead(m)} title="Make project lead" style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>Make lead</button>
                  )}
                  {canRemove&&(
                    <button onClick={()=>removeMember(m)} title={m.role==="guest"?"Remove from project":"Unassign from project"} style={{background:"none",border:"none",color:"var(--mu)",cursor:"pointer",fontSize:16,padding:"4px 8px",lineHeight:1}}>×</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add full-seat member form — picks from existing workspace roster. */}
      {mode==="addMember"&&(
        <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:14,marginBottom:10,display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5}}>Full-seat members get the same access you have on this project — clients, contracts, intake, messages, timeline. Pick from your existing team. To invite a brand-new teammate, go to Profile → Team first.</div>
          {availableToAdd.length===0?(
            <div style={{fontSize:11,color:"var(--mu)",fontStyle:"italic"}}>All your team members are already on this project.</div>
          ):(
            <div style={{display:"flex",gap:6}}>
              <select value={memberToAdd} onChange={e=>setMemberToAdd(e.target.value)} style={{flex:1,background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:8,padding:"8px 10px",fontSize:12,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif"}}>
                <option value="">Select team member…</option>
                {availableToAdd.map(m=>(
                  <option key={m.member_id} value={m.member_id}>{m.user_name||m.user_email} ({m.user_email})</option>
                ))}
              </select>
              <button onClick={submitAddMember} disabled={busy||!memberToAdd} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:busy?"default":"pointer",opacity:(busy||!memberToAdd)?.6:1,fontFamily:"'Inter',sans-serif"}}>
                {busy?"…":"Add"}
              </button>
            </div>
          )}
          {err&&<div style={{fontSize:11,color:"var(--err-tx)"}}>{err}</div>}
        </div>
      )}

      {/* Add guest form — issues a new invite + email. */}
      {mode==="addGuest"&&(
        <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:14,marginBottom:10,display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5}}>Guests see only this project. They can't access financials, other clients, or settings. £6/mo per guest (5 included with Studio).</div>
          <div style={{display:"flex",gap:6}}>
            <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="contractor@example.com" type="email"
              style={{flex:1,background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:8,padding:"8px 10px",fontSize:12,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif"}}/>
            <button onClick={submitAddGuest} disabled={busy} style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:busy?"default":"pointer",opacity:busy?.6:1,fontFamily:"'Inter',sans-serif"}}>
              {busy?"…":"Invite"}
            </button>
          </div>
          {err&&<div style={{fontSize:11,color:"var(--err-tx)"}}>{err}</div>}
          {inviteLink&&(
            <div style={{background:"var(--ok-bg)",border:"1px solid var(--ok-tx)",borderRadius:8,padding:"8px 10px"}}>
              <div style={{fontSize:11,color:"var(--ok-tx)",fontWeight:600,marginBottom:4}}>Invite created — share this link:</div>
              <div style={{fontSize:11,color:"var(--ink)",wordBreak:"break-all",fontFamily:"monospace"}}>{inviteLink}</div>
              <button onClick={()=>navigator.clipboard?.writeText(inviteLink)} style={{marginTop:6,background:"var(--card)",border:"1px solid var(--bo)",borderRadius:6,padding:"4px 8px",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",color:"var(--ink)"}}>Copy link</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClientActions({c,portalUrl,acct,onPortal,onThreadMessage,isLocked}){
  const [act,setAct]=useState(null);
  const [msgText,setMsgText]=useState(`Hi ${c.name.split(" ")[0]}, just checking in — let me know if you have any questions!`);
  const [sent,setSent]=useState(null);
  const [aiText,setAiText]=useState("");
  const [aiGenerating,setAiGenerating]=useState(false);
  const [aiEditing,setAiEditing]=useState(false);
  const aiGenerated=useRef(false);

  const toggle=a=>{ setAct(x=>x===a?null:a); setSent(null); setAiEditing(false); };
  const handleSend=type=>{
    if(isLocked) return; // Block sending for archived/cancelled/locked clients
    if(type==="msg"&&msgText.trim()&&onThreadMessage){
      onThreadMessage(c.id,msgText.trim());
    }
    setSent(type);
    setTimeout(()=>{ setAct(null); setSent(null); },2000);
  };

  const generateAiFollowUp=async()=>{
    setAiGenerating(true); setAiText(""); setAiEditing(false);
    const ctx=c.status==="pending"?"Their contract is pending review/signing."
      :c.pct<50?"They recently signed on and are in early stages."
      :"The project is well underway and nearing completion.";
    const prompt=`Write a short, warm follow-up message from a freelancer to their client ${c.name.split(" ")[0]}. Context: ${ctx} Service: ${c.service||"creative services"}. Keep it under 60 words, casual and personal. Message body only — no greeting label, no sign-off name, no markdown.`;
    try{
      const res=await fetch(AI_PROXY_URL,{method:"POST",headers:aiHeaders(),
        body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:200,stream:true,messages:[{role:"user",content:prompt}]})});
      if(!res.ok) throw new Error("AI error");
      const reader=res.body.getReader(); const dec=new TextDecoder(); let buf=""; let full="";
      while(true){
        const {done,value}=await reader.read(); if(done) break;
        buf+=dec.decode(value,{stream:true});
        const lines=buf.split("\n"); buf=lines.pop();
        for(const line of lines){
          if(line.startsWith("data: ")){
            try{ const p=JSON.parse(line.slice(6)); if(p.type==="content_block_delta"&&p.delta?.type==="text_delta"){ full+=p.delta.text; setAiText(full); } }catch(e){}
          }
        }
      }
    }catch(e){
      const fallback=c.status==="pending"
        ?`Hi ${c.name.split(" ")[0]}! Just a friendly nudge — your contract is ready for review at your portal. Let me know if you have any questions before signing! 😊`
        :c.pct<50
        ?`Hi ${c.name.split(" ")[0]}! Excited to be working with you. Just checking in to make sure everything looks good. Feel free to reach out anytime! 🙌`
        :`Hi ${c.name.split(" ")[0]}! We're getting close and I can't wait. Just wanted to make sure you have everything you need from me before the big day. Let me know!`;
      setAiText(fallback);
    }
    setAiGenerating(false);
  };

  useEffect(()=>{
    if(act==="ai"&&!aiGenerated.current){ aiGenerated.current=true; generateAiFollowUp(); }
  },[act]);

  const dueDate=c.tl.find(t=>t.s==="pend")?.d||"On receipt";
  const rawAmtForHalf=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
  const halfAmt=fmtMoney(round2(rawAmtForHalf/2));

  return (
    <>


      {/* BOTTOM ACTIONS — hidden when client is locked (archived/cancelled/plan over limit) */}
      {!isLocked&&<>
      <div className="actbtns">
        <button className={`btn${act==="msg"?" p":" s"}`} onClick={()=>toggle("msg")}>Message</button>
      </div>

      {act==="msg"&&(
        <div style={{margin:"10px 20px 0",background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",padding:14}}>
          <div style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>Message {c.name.split(" ")[0]}</div>
          <textarea value={msgText} onChange={e=>setMsgText(e.target.value)} style={{width:"100%",background:"var(--cr)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:80}}/>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button onClick={()=>handleSend("msg")} style={{flex:1,background:sent==="msg"?"var(--ok-tx)":"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              {sent==="msg"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Sent via Portal!</>:<>Send Message <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span></>}
            </button>
            <button onClick={()=>setAct(null)} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"11px 14px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>✕</button>
          </div>
        </div>
      )}

      <div className="actbtns" style={{paddingTop:8}}>
        <button className={`btn${act==="ai"?" s":" t"}`} style={{flex:"none",width:"100%"}} onClick={()=>toggle("ai")}>✦ AI Draft Follow-up</button>
      </div>
      </>}

      {act==="ai"&&!isLocked&&(
        <div style={{margin:"10px 20px 0",background:"var(--g)",borderRadius:14,padding:16}}>
          <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".08em",color:"var(--t)",fontWeight:600,marginBottom:10}}>✦ AI-generated follow-up</div>
          {aiGenerating&&!aiText?(
            <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(255,255,255,.07)",borderRadius:10,padding:12,marginBottom:12}}>
              <div style={{display:"flex",gap:3}}>{[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:"50%",background:"rgba(255,255,255,.5)",animation:"pulse 1.2s ease-in-out "+(i*.2)+"s infinite"}}/>)}</div>
              <span style={{fontSize:12,color:"rgba(255,255,255,.6)"}}>Writing follow-up…</span>
            </div>
          ):aiEditing?(
            <textarea value={aiText} onChange={e=>setAiText(e.target.value)} style={{width:"100%",background:"rgba(255,255,255,.1)",border:"1px solid rgba(255,255,255,.2)",borderRadius:10,padding:12,fontSize:13,fontFamily:"'Inter',sans-serif",color:"rgba(255,255,255,.9)",outline:"none",resize:"none",lineHeight:1.6,minHeight:90,marginBottom:12}}/>
          ):(
            <div style={{fontSize:13,color:"rgba(255,255,255,.85)",lineHeight:1.6,background:"rgba(255,255,255,.07)",borderRadius:10,padding:12,marginBottom:12}}>{aiText}</div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{
              if(onThreadMessage) onThreadMessage(c.id, aiText);
              handleSend("ai");
            }} disabled={aiGenerating||!aiText} style={{flex:1,background:sent==="ai"?"var(--ok-tx)":"var(--t)",color:sent==="ai"?"#fff":"var(--tc)",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:aiGenerating?"default":"pointer",opacity:aiGenerating?.5:1,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
              {sent==="ai"?<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Sent!</>:"Send"}
            </button>
            <button onClick={()=>setAiEditing(e=>!e)} disabled={aiGenerating} style={{flex:1,background:aiEditing?"rgba(255,255,255,.25)":"rgba(255,255,255,.12)",color:"rgba(255,255,255,.8)",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:aiGenerating?"default":"pointer",opacity:aiGenerating?.5:1,fontFamily:"'Inter',sans-serif"}}>{aiEditing?"Done":"Edit"}</button>
            <button onClick={()=>{aiGenerated.current=false;generateAiFollowUp();}} disabled={aiGenerating} style={{flex:1,background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.6)",border:"none",borderRadius:10,padding:"10px",fontSize:12,fontWeight:600,cursor:aiGenerating?"default":"pointer",opacity:aiGenerating?.5:1,fontFamily:"'Inter',sans-serif"}}>↻</button>
          </div>
        </div>
      )}
    </>
  );
}

/* ── SEND LINK PANEL ── */
function SendLinkPanel({c, portalUrl, acct}){
  const isFreeEmail=acct?.billingPlan==="free";
  const [mode,setMode]=useState(null); // null | 'email' | 'sms'
  const [sent,setSent]=useState(null);
  const [emailBody,setEmailBody]=useState(`Hi ${c.name.split(" ")[0]}!\n\nYour client portal is ready — sign your contract, pay your deposit and message me all in one place.\n\n👉 https://${portalUrl}\n\nLooking forward to working with you!`);
  const [emailGenerating,setEmailGenerating]=useState(false);
  const [smsBody,setSmsBody]=useState(`Hi ${c.name.split(" ")[0]}! Your client portal is ready — sign your contract, pay your deposit and message me all in one place 👉 https://${portalUrl}`);
  const [copied,setCopied]=useState(false);

  const [emailAiDone,setEmailAiDone]=useState(false);
  const openEmail=async(force)=>{
    if(!force&&mode==="email"){ setMode(null); return; }
    setMode("email");
    if(isFreeEmail) return;
    if(!force&&emailAiDone) return;
    setEmailAiDone(true);
    setEmailGenerating(true);
    setEmailBody("");
    const prompt=`Write a short, warm email from a creative freelancer to their client ${c.name.split(" ")[0]}, sending them their unique client portal link. The portal lets them sign their contract, pay their deposit, fill in a questionnaire, and send messages — no account needed. Portal link: https://${portalUrl}. Keep it under 80 words, warm and personal. Body only — no subject line, no sign-off, no markdown.`;
    try{
      const res=await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:aiHeaders(),
        body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:200,stream:true,messages:[{role:"user",content:prompt}]})
      });
      if(!res.ok) throw new Error("AI error");
      const reader=res.body.getReader();
      const dec=new TextDecoder();
      let buf="";
      while(true){
        const {done,value}=await reader.read();
        if(done) break;
        buf+=dec.decode(value,{stream:true});
        const lines=buf.split("\n"); buf=lines.pop();
        for(const line of lines){
          if(line.startsWith("data: ")){
            try{ const p=JSON.parse(line.slice(6)); if(p.type==="content_block_delta"&&p.delta?.type==="text_delta") setEmailBody(t=>t+p.delta.text); }catch(e){}
          }
        }
      }
    }catch(e){
      setEmailBody(`Hi ${c.name.split(" ")[0]},\n\nYour client portal is ready! You can sign your contract, pay your deposit, and message me directly — all in one place, no account needed.\n\n👉 https://${portalUrl}\n\nLooking forward to working with you!`);
    }
    setEmailGenerating(false);
  };

  const doSend=(type)=>{ setSent(type); setMode(null); setTimeout(()=>setSent(null),3000); };
  const doCopy=()=>{
    const url=`https://${portalUrl}`;
    if(navigator.clipboard&&navigator.clipboard.writeText){
      navigator.clipboard.writeText(url).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); }).catch(()=>fallbackCopy(url));
    } else { fallbackCopy(url); }
  };
  const fallbackCopy=(text)=>{
    const el=document.createElement("textarea");
    el.value=text; el.setAttribute("readonly","");
    el.style.cssText="position:fixed;top:-9999px;left:-9999px;opacity:0;";
    document.body.appendChild(el);
    el.focus(); el.select();
    try{ document.execCommand("copy"); setCopied(true); setTimeout(()=>setCopied(false),2000); }catch(e){}
    document.body.removeChild(el);
  };

  return (
    <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden"}}>
      {/* URL row */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderBottom:"1px solid var(--bo)",background:"var(--gl)"}}>
        <span style={{display:"inline-flex"}}>{I.linkExt}</span>
        <span style={{fontSize:11,fontWeight:600,color:"var(--gm)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>
          https://{portalUrl}
        </span>
        <button onClick={doCopy} style={{background:copied?"var(--g)":"white",color:copied?"white":"var(--g)",border:"1.5px solid var(--g)",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0,transition:"all .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:4}}>
          {copied?<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Copied</>:"Copy"}
        </button>
      </div>

      {/* Send buttons */}
      <div style={{display:"flex",borderBottom:(mode||sent)?"1px solid var(--bo)":"none"}}>
        {/* Email */}
        <button onClick={openEmail} style={{flex:1,padding:"13px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:5,background:mode==="email"?"var(--gl)":"transparent",border:"none",borderRight:"1px solid var(--bo)",cursor:"pointer",transition:"background .15s"}}>
          <span style={{display:"inline-flex"}}>{I.mail}</span>
          <span style={{fontSize:11,fontWeight:600,color:mode==="email"?"var(--g)":"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",fontFamily:"'Inter',sans-serif",display:"inline-flex",alignItems:"center",gap:3}}>
            {sent==="email"?<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Sent</>:"Email"}
          </span>
          {c.email&&<span style={{fontSize:10,color:"var(--gm)",fontWeight:500}}>{c.email}</span>}
          {!c.email&&<span style={{fontSize:10,color:"var(--t)"}}>No email on file</span>}
        </button>

        {/* SMS */}
        <button onClick={()=>setMode(m=>m==="sms"?null:"sms")} style={{flex:1,padding:"13px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:5,background:mode==="sms"?"var(--gl)":"transparent",border:"none",borderRight:"1px solid var(--bo)",cursor:"pointer",transition:"background .15s"}}>
          <span style={{display:"inline-flex"}}>{I.chatBubble}</span>
          <span style={{fontSize:11,fontWeight:600,color:mode==="sms"?"var(--g)":"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",fontFamily:"'Inter',sans-serif",display:"inline-flex",alignItems:"center",gap:3}}>
            {sent==="sms"?<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Sent</>:"SMS"}
          </span>
          {c.phone&&<span style={{fontSize:10,color:"var(--gm)",fontWeight:500}}>{c.phone}</span>}
          {!c.phone&&<span style={{fontSize:10,color:"var(--t)"}}>No phone on file</span>}
        </button>

        {/* WhatsApp */}
        <button onClick={()=>{ const msg=encodeURIComponent("Hi "+c.name.split(" ")[0]+"! Your portal is ready 👉 https://"+portalUrl); window.open("https://wa.me/"+((c.phone||"").replace(/\D/g,""))+"?text="+msg,"_blank"); }} style={{flex:1,padding:"13px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:5,background:"transparent",border:"none",cursor:"pointer"}}>
          <span style={{display:"inline-flex",width:20,height:20}} aria-label="WhatsApp">
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#25D366" d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.16 5.335 5.495 0 12.05 0c3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/>
              <path fill="#fff" d="M18.041 14.729c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
          </span>
          <span style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".05em",fontFamily:"'Inter',sans-serif"}}>WhatsApp</span>
          {c.phone&&<span style={{fontSize:10,color:"var(--gm)",fontWeight:500}}>{c.phone}</span>}
          {!c.phone&&<span style={{fontSize:10,color:"var(--t)"}}>No phone on file</span>}
        </button>
      </div>

      {/* Email expand */}
      {mode==="email"&&(
        <div style={{padding:14,background:"var(--cr)"}}>
          {c.email&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,background:"var(--gl)",borderRadius:8,padding:"8px 12px"}}>
              <span style={{display:"inline-flex"}}>{I.mail}</span>
              <span style={{fontSize:12,fontWeight:600,color:"var(--gm)"}}>To: {c.email}</span>
            </div>
          )}
          {!c.email&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,background:"var(--tl)",borderRadius:8,padding:"8px 12px"}}>
              <span style={{display:"inline-flex",color:"var(--warn-tx)"}}>{I.alertTri}</span>
              <span style={{fontSize:12,color:"var(--t)"}}>No email on file — add one in Contact Details above first.</span>
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <span style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em"}}>Message</span>
            {!emailGenerating&&!isFreeEmail&&<button onClick={()=>openEmail(true)} style={{background:"none",border:"none",fontSize:11,color:"var(--t)",fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>↻ Regenerate</button>}
            {isFreeEmail&&<span style={{fontSize:10,color:"var(--mu)",fontStyle:"italic"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> AI drafts — Pro</span>}
          </div>
          {emailGenerating&&!emailBody?(
            <div style={{background:"var(--card)",borderRadius:8,padding:14,border:"1px solid var(--bo)",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
              <div style={{display:"flex",gap:3}}>{[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:"50%",background:"var(--gm)",animation:"pulse 1.2s ease-in-out "+(i*.2)+"s infinite"}}/>)}</div>
              <span style={{fontSize:12,color:"var(--mu)"}}>Writing personalised email…</span>
            </div>
          ):(
            <textarea value={emailBody} onChange={e=>setEmailBody(e.target.value)} style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:8,padding:12,fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:110,marginBottom:10}}/>
          )}
          <button onClick={()=>{
            const subject=encodeURIComponent("Your client portal is ready!");
            const body=encodeURIComponent(emailBody);
            window.open(`mailto:${c.email}?subject=${subject}&body=${body}`,"_self");
            doSend("email");
          }} disabled={!c.email||!emailBody||emailGenerating} style={{width:"100%",background:!c.email?"var(--bo)":"var(--g)",color:!c.email?"var(--mu)":"#fff",border:"none",borderRadius:10,padding:"13px",fontSize:13,fontWeight:600,cursor:!c.email?"default":"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            {!c.email?"Add email address first":emailGenerating?"Generating…":<>Open email to {c.email} <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span></>}
          </button>
        </div>
      )}

      {/* SMS expand */}
      {mode==="sms"&&(
        <div style={{padding:14,background:"var(--cr)"}}>
          {c.phone&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,background:"var(--gl)",borderRadius:8,padding:"8px 12px"}}>
              <span style={{display:"inline-flex"}}>{I.smartphone}</span>
              <span style={{fontSize:12,fontWeight:600,color:"var(--gm)"}}>To: {c.phone}</span>
            </div>
          )}
          {!c.phone&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,background:"var(--tl)",borderRadius:8,padding:"8px 12px"}}>
              <span style={{display:"inline-flex",color:"var(--warn-tx)"}}>{I.alertTri}</span>
              <span style={{fontSize:12,color:"var(--t)"}}>No phone number on file — add one in Contact Details above first.</span>
            </div>
          )}
          <div style={{fontSize:11,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>Message</div>
          <textarea value={smsBody} onChange={e=>setSmsBody(e.target.value)} style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:8,padding:12,fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.6,minHeight:80,marginBottom:10}}/>
          <div style={{fontSize:11,color:"var(--mu)",textAlign:"right",marginBottom:8}}>{smsBody.length} chars</div>
          <button onClick={()=>{
            const phone=(c.phone||"").replace(/\D/g,"");
            const body=encodeURIComponent(smsBody);
            const isIOS=/iPad|iPhone|iPod/.test(navigator.userAgent);
            window.open(`sms:${phone}${isIOS?"&":"?"}body=${body}`,"_self");
            doSend("sms");
          }} disabled={!c.phone} style={{width:"100%",background:!c.phone?"var(--bo)":"var(--g)",color:!c.phone?"var(--mu)":"#fff",border:"none",borderRadius:10,padding:"13px",fontSize:13,fontWeight:600,cursor:!c.phone?"default":"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            {!c.phone?"Add phone number first":<>Open SMS to {c.phone} <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span></>}
          </button>
        </div>
      )}

      {/* Sent confirmation */}
      {sent&&(
        <div style={{padding:"12px 14px",background:"var(--gl)",display:"flex",alignItems:"center",gap:10}}>
          <span style={{display:"flex",color:"var(--gm)"}}>{I.checkCircle}</span>
          <span style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>Portal link sent to {sent==="email"?c.email:c.phone}!</span>
        </div>
      )}
    </div>
  );
}

/* ── PAYMENTS PANEL ── */
function PaymentsPanel({c,acct,onPaymentUpdate}){
  const [reminder,setReminder]=useState(null);
  const [reminderText,setReminderText]=useState("");
  const [reminderGenerating,setReminderGenerating]=useState(false);
  const [reminderSent,setReminderSent]=useState(null);
  const reminderLock=useRef(false);
  const [showInvoiceForm,setShowInvoiceForm]=useState(false);
  const [invForm,setInvForm]=useState({description:"",amount:""});
  const [invSaved,setInvSaved]=useState(false);

  const isArchived=c.archived||false;
  const isCancelled=!!c.cancellationRequested;
  const isLocked=isArchived||isCancelled;
  const guardedUpdate=(...args)=>{ if(!isLocked) onPaymentUpdate(...args); };

  const sym=acct.currencySymbol||"$";
  const rawAmt=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
  // When total fee is 0, always show full payment mode regardless of stored depositPct
  const depPct=rawAmt<=0?100:(c.depositPct||50);
  const depAmt=rawAmt<=0?0:(c.depositAmt||round2(rawAmt*depPct/100));
  const balAmt=rawAmt<=0?0:(c.balanceAmt||round2(rawAmt*(100-depPct)/100));
  const depositPaid=c.depositPaid||false;
  const balancePaid=c.balancePaid||false;
  const invoices=c.invoices||[];

  const addInvoice=()=>{
    if(isArchived||!invForm.description.trim()||!invForm.amount) return;
    const inv={
      id:Date.now(),
      description:invForm.description.trim(),
      amount:parseFloat(invForm.amount),
      paid:false,
      createdAt:fmtDate(localToday()),
    };
    guardedUpdate(c.id,"invoices",[...invoices,inv]);
    setInvForm({description:"",amount:""});
    setShowInvoiceForm(false);
    setInvSaved(true);
    setTimeout(()=>setInvSaved(false),2000);
  };

  const markInvoicePaid=(invId)=>{
    if(isArchived) return;
    // Stamp paidAt so Home revenue bars + Metrix can group the income into the
    // correct month (the portal payment path already sets paidAt on success).
    const today=localToday();
    guardedUpdate(c.id,"invoices",invoices.map(inv=>inv.id===invId?{...inv,paid:true,paidAt:today}:inv));
  };

  const makePresetReminder=(type)=>{
    const amt=type==="balance"?balAmt:depAmt;
    const label=type==="balance"?"final balance":depPct>=100?"full payment":"deposit";
    const dueStep=(c.tl||[]).find(s=>s.d&&(s.l||"").toLowerCase().includes(type==="balance"?"balance":"deposit"));
    const dueDate=dueStep?.d?fmtDate(dueStep.d):"";
    if(type==="balance"){
      return `Hi ${c.name.split(" ")[0]},\n\nI hope your project is going well! This is a reminder that your final balance of ${sym}${fmtMoney(amt)}${dueDate?" is due by "+dueDate:""} is now due.\n\nYou can pay securely through your portal:\nlancer-app.com/${acct.slug}/${c.portalSlug}\n\nPlease don't hesitate to reach out if you have any questions.\n\nBest,\n${acct.name}`;
    }
    return `Hi ${c.name.split(" ")[0]},\n\nThank you for booking with me! This is a friendly reminder that your ${label} of ${sym}${fmtMoney(amt)}${dueDate?" is due by "+dueDate:""} is needed to secure your date.\n\nYou can pay securely through your portal:\nlancer-app.com/${acct.slug}/${c.portalSlug}\n\nLooking forward to working with you!\n\n${acct.name}`;
  };

  const isFree=acct.billingPlan==="free";
  const generateReminder=async(type)=>{
    // Ref-based lock prevents double execution from rapid clicks (state is async)
    if(reminderLock.current) return;
    reminderLock.current=true;
    setReminder(type);
    setReminderSent(null);
    // Free plan: empty textarea — user writes their own message (no AI)
    if(isFree){
      setReminderText("");
      setReminderGenerating(false);
      reminderLock.current=false;
      return;
    }
    // Pro plan: clear text so spinner shows, then stream AI response
    setReminderText("");
    setReminderGenerating(true);
    const amt=type==="balance"?balAmt:depAmt;
    const label=type==="balance"?"final balance":depPct>=100?"full payment":"deposit";
    const prompt=`Write a short, warm payment reminder from ${acct.name} (a ${acct.vert}) to their client ${c.name.split(" ")[0]}.

Payment details:
- Amount due: ${acct.currencySymbol||"$"}${amt.toLocaleString()}
- Payment type: ${label}
- Project: ${c.type}
- Portal payment link: lancer-app.com/${acct.slug}/${c.portalSlug}

Write a friendly but clear reminder. Keep it under 60 words. Body only — no subject, no sign-off, no markdown. Mention the amount and include the portal link clearly.`;

    try{
      let aiText="";
      const res=await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:aiHeaders(),
        body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:200,stream:true,messages:[{role:"user",content:prompt}]})
      });
      if(!res.ok) throw new Error("AI error");
      const reader=res.body.getReader(); const dec=new TextDecoder(); let buf="";
      while(true){
        const {done,value}=await reader.read(); if(done) break;
        buf+=dec.decode(value,{stream:true});
        const lines=buf.split("\n"); buf=lines.pop();
        for(const line of lines){
          if(line.startsWith("data: ")){
            try{ const p=JSON.parse(line.slice(6)); if(p.type==="content_block_delta"&&p.delta?.type==="text_delta"){ aiText+=p.delta.text; setReminderText(aiText); } }catch(e){}
          }
        }
      }
      // If AI returned nothing, fall back to preset
      if(!aiText.trim()) setReminderText(makePresetReminder(type));
    }catch(e){
      // On error, fall back to preset template
      setReminderText(makePresetReminder(type));
    }
    setReminderGenerating(false);
    reminderLock.current=false;
  };

  const [showRefundForm,setShowRefundForm]=useState(false);
  const [refundForm,setRefundForm]=useState({amount:"",reason:""});
  const [refundSaved,setRefundSaved]=useState(false);
  const [refundState,setRefundState]=useState(null); // null | "processing" | "done" | "manual-done" | "failed"
  const [showRefundConfirm,setShowRefundConfirm]=useState(false);
  const [refundError,setRefundError]=useState(null);
  // Stripe-charged amount for this client (null = not yet fetched). Used to cap Stripe refunds.
  const [stripePayments,setStripePayments]=useState(null);
  // DB refunds rows keyed by payment_id for computing per-payment remaining refundable.
  const [stripeRefundsDb,setStripeRefundsDb]=useState(null);
  // Which specific Stripe charge to refund. null = none picked yet. Users with >1 charge must choose.
  const [selectedPaymentId,setSelectedPaymentId]=useState(null);
  const refunds=c.refunds||[];

  // Lazy-load payments + refunds history the first time the refund form is opened so we can
  // tell the user exactly how much is refundable per-charge (supports multiple partial refunds).
  useEffect(()=>{
    if(!showRefundForm){ setSelectedPaymentId(null); return; }
    let cancelled=false;
    (async()=>{
      try{
        const [payRes,refRes]=await Promise.all([dbGetPayments(c.id),dbGetRefunds(c.id)]);
        if(!cancelled){
          const pays=payRes?.data||[];
          const refs=refRes?.data||[];
          setStripePayments(pays);
          setStripeRefundsDb(refs);
          // Compute which payments still have headroom (remaining > 0)
          const remainingByPayment=(pid)=>{
            const chargeAmt=Number((pays.find(p=>p.id===pid)||{}).amount||0);
            const refunded=refs
              .filter(r=>r.payment_id===pid&&(!r.stripe_status||r.stripe_status==="succeeded"||r.stripe_status==="pending"))
              .reduce((s,r)=>s+Number(r.amount||0),0);
            return Math.max(0,chargeAmt-refunded);
          };
          const refundable=pays.filter(p=>(p.status==="succeeded"||p.status==="refunded")&&remainingByPayment(p.id)>0);
          if(refundable.length===1) setSelectedPaymentId(refundable[0].id);
        }
      }catch{
        if(!cancelled){ setStripePayments([]); setStripeRefundsDb([]); }
      }
    })();
    return ()=>{ cancelled=true; };
  },[showRefundForm,c.id]);

  // Per-payment remaining refundable — sum of succeeded/pending DB refunds on that payment subtracted from the charge amount.
  const remainingByPayment=(pid)=>{
    const chargeAmt=Number(((stripePayments||[]).find(p=>p.id===pid)||{}).amount||0);
    const refunded=(stripeRefundsDb||[])
      .filter(r=>r.payment_id===pid&&(!r.stripe_status||r.stripe_status==="succeeded"||r.stripe_status==="pending"))
      .reduce((s,r)=>s+Number(r.amount||0),0);
    return Math.max(0,round2(chargeAmt-refunded));
  };
  // Derived: how much has actually been charged through Stripe on this client, minus
  // any refunds that were processed through Stripe. Manual refunds don't reduce this —
  // they track money that moved outside the Stripe balance.
  const stripePaid=(stripePayments||[]).filter(p=>p.status==="succeeded"||p.status==="refunded").reduce((s,p)=>s+Number(p.amount||0),0);
  const stripeRefundedTotal=refunds.filter(r=>r.stripeStatus!=="manual").reduce((s,r)=>s+Number(r.amount||0),0);
  const stripeRefundable=Math.max(0,stripePaid-stripeRefundedTotal);
  // Per-charge refund target (selected from the picker). A payment is refundable if it has remaining > 0,
  // regardless of status — status='refunded' can still have headroom if only partials were taken previously.
  const refundablePayments=(stripePayments||[]).filter(p=>(p.status==="succeeded"||p.status==="refunded")&&remainingByPayment(p.id)>0);
  const selectedPayment=(stripePayments||[]).find(p=>p.id===selectedPaymentId)||null;
  const selectedMaxRefund=selectedPayment?remainingByPayment(selectedPayment.id):0;
  // Label a payment row in the picker using the client's own invoice list for context
  const paymentLabel=(p)=>{
    if(p.type==="deposit") return depPct>=100?"Full payment":"Deposit";
    if(p.type==="balance") return "Final balance";
    if(p.type==="invoice"){
      const inv=(c.invoices||[]).find(i=>i.id===p.invoice_id);
      return inv?("Invoice — "+(inv.description||"Invoice")):"Invoice";
    }
    return "Payment";
  };
  const selectPayment=(id)=>{
    setSelectedPaymentId(id);
    // Prefill amount with the REMAINING refundable value (not the full charge — partial prior refunds may have consumed some).
    const remaining=remainingByPayment(id);
    if(remaining>0) setRefundForm(f=>({...f,amount:String(remaining)}));
  };

  const issueRefund=async ()=>{
    if(!refundForm.amount||!refundForm.reason.trim()) return;
    const refundAmt=parseFloat(refundForm.amount);

    // 1. Validate: refund amount cannot exceed total CRM-tracked payments (deposit + balance + paid invoices)
    const paidInvoicesTotal=(c.invoices||[]).filter(i=>i.paid).reduce((s,i)=>s+(Number(i.amount)||0),0);
    const totalPaid=(depositPaid?depAmt:0)+(balancePaid?balAmt:0)+paidInvoicesTotal;
    const totalRefunded=refunds.reduce((s,r)=>s+Number(r.amount||0),0);
    const maxRefundable=totalPaid-totalRefunded;
    if(refundAmt>maxRefundable&&maxRefundable>0){
      setShowRefundConfirm(false);
      setRefundState("failed");
      setRefundError(`Refund amount (${sym}${fmtMoney(refundAmt)}) exceeds refundable balance (${sym}${fmtMoney(maxRefundable)})`);
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },5000);
      return;
    }

    // 2. Validate: a specific Stripe charge must be chosen (picker). We no longer rely on
    //    "latest succeeded payment" — the user explicitly selects which deposit/balance/invoice to refund.
    if(!selectedPayment){
      setShowRefundConfirm(false);
      setRefundState("failed");
      setRefundError(
        refundablePayments.length===0
          ? `No Stripe charges available to refund. Use "Manual" to record an off-platform refund.`
          : `Choose which Stripe payment to refund.`
      );
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },5000);
      return;
    }

    // 3. Validate: refund amount cannot exceed the chosen payment's REMAINING refundable.
    //    Prior partial refunds may have already consumed some of the charge — cap to remaining.
    if(refundAmt>selectedMaxRefund){
      setShowRefundConfirm(false);
      setRefundState("failed");
      setRefundError(`Refund amount (${sym}${fmtMoney(refundAmt)}) exceeds the remaining refundable on this ${paymentLabel(selectedPayment).toLowerCase()} (${sym}${fmtMoney(selectedMaxRefund)}).`);
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },6000);
      return;
    }

    // 4. Call the Stripe refund edge function with the explicitly chosen payment_intent_id.
    setShowRefundConfirm(false);
    setRefundError(null);
    setRefundState("processing");
    const piId=selectedPayment.stripe_payment_intent_id||null;
    const paymentRowId=selectedPayment.id||null;
    const amountPence = Math.round(refundAmt * 100);
    // Always pull a fresh session — _accessToken in React state can be stale after a background
    // auto-refresh, and the anon-key fallback would 401 server-side (no sub claim) so it just
    // turns into an opaque "Refund failed (HTTP 401)". Bail clearly instead.
    const { data: { session: _refundSession } } = await sb.auth.getSession();
    const token = _refundSession?.access_token;
    if (!token) {
      setRefundState("failed");
      setRefundError("Your session has expired. Please sign in again, then retry the refund.");
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },6000);
      return;
    }

    let res;
    try {
      res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ client_id: c.id, amount_pence: amountPence, reason: refundForm.reason.trim(), payment_intent_id: piId, payment_id: paymentRowId }),
      });
    } catch(netErr) {
      // True network failure (offline, DNS, CORS) — edge function could not be reached.
      setRefundState("failed");
      setRefundError(`Connection error. Check your internet and try again, or use "Manual" to record this refund.`);
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },6000);
      return;
    }

    let data=null;
    try { data = await res.json(); } catch {}

    if (!res.ok) {
      // Stripe or the edge function rejected the refund — surface the real reason instead of faking success.
      const rawMsg = data?.error || `Refund failed (HTTP ${res.status})`;
      // Humanise the common Stripe "amount too large" error
      const friendly = /amount|charge/i.test(rawMsg)
        ? `Stripe rejected the refund: ${rawMsg}. Use "Manual" to record a refund made outside Stripe.`
        : rawMsg;
      setRefundState("failed");
      setRefundError(friendly);
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },8000);
      return;
    }

    // Success
    const refund = {
      id: data?.refund_id || Date.now(),
      amount: refundAmt,
      reason: refundForm.reason.trim(),
      date: fmtDate(localToday()),
      stripeStatus: data?.status || "succeeded",
    };
    onPaymentUpdate(c.id,"refunds",[...refunds,refund]);
    setRefundForm({amount:"",reason:""});
    setShowRefundForm(false);
    setRefundState("done");
    setRefundSaved(true);
    setTimeout(()=>{ setRefundState(null); setRefundSaved(false); },3000);
  };

  const issueManualRefund=()=>{
    if(!refundForm.amount||!refundForm.reason.trim()) return;
    setShowRefundConfirm(false);
    setRefundError(null);
    const refundAmt=parseFloat(refundForm.amount);
    // Same refundable-balance validation as Stripe path
    const paidInvoicesTotal=(c.invoices||[]).filter(i=>i.paid).reduce((s,i)=>s+(Number(i.amount)||0),0);
    const totalPaid=(depositPaid?depAmt:0)+(balancePaid?balAmt:0)+paidInvoicesTotal;
    const totalRefunded=refunds.reduce((s,r)=>s+Number(r.amount||0),0);
    const maxRefundable=totalPaid-totalRefunded;
    if(refundAmt>maxRefundable&&maxRefundable>0){
      setRefundState("failed");
      setRefundError(`Refund amount (${sym}${fmtMoney(refundAmt)}) exceeds refundable balance (${sym}${fmtMoney(maxRefundable)})`);
      setTimeout(()=>{ setRefundState(null); setRefundError(null); },5000);
      return;
    }
    // Record locally — no Stripe API call. Marked "manual" so UI shows "Manual · Recorded".
    const refund={
      id: Date.now(),
      amount: refundAmt,
      reason: refundForm.reason.trim(),
      date: fmtDate(localToday()),
      stripeStatus: "manual",
    };
    onPaymentUpdate(c.id,"refunds",[...refunds,refund]);
    setRefundForm({amount:"",reason:""});
    setShowRefundForm(false);
    setRefundState("manual-done");
    setRefundSaved(true);
    setTimeout(()=>{ setRefundState(null); setRefundSaved(false); },3000);
  };

  const markPaid=(field)=>{ if(isLocked) return; onPaymentUpdate(c.id,field,true); };

  const btn=(bg,col,txt,onClick,disabled)=>(
    <button onClick={onClick} disabled={disabled} style={{background:disabled?"var(--bo)":bg,color:disabled?"var(--mu)":col,border:"none",borderRadius:10,padding:"9px 14px",fontSize:12,fontWeight:600,cursor:disabled?"default":"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:4}}>
      {txt}
    </button>
  );

  return (
    <div className="msec" style={{marginTop:16}}>
      <div className="mstl">Payments</div>
      <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden",marginBottom:10}}>

        {/* Header row */}
        <div style={{display:"flex",padding:"10px 14px",borderBottom:"1px solid var(--bo)",background:"var(--cr)"}}>
          <span style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",flex:1}}>Payment</span>
          <span style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",width:70,textAlign:"right"}}>Amount</span>
          <span style={{fontSize:11,fontWeight:600,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".07em",width:60,textAlign:"right"}}>Status</span>
        </div>

        {/* Deposit / Full Payment row */}
        <div style={{padding:"13px 14px",borderBottom:depPct<100?"1px solid var(--bo)":"none"}}>
          <div style={{display:"flex",alignItems:"center",marginBottom:depositPaid?0:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{depPct>=100?"Full Payment":"Deposit"}</div>
              <div style={{fontSize:11,color:"var(--mu)"}}>{depPct>=100?"100% of total fee":`${depPct}% of total fee`}</div>
            </div>
            <div className="num" style={{width:70,textAlign:"right",fontSize:14,fontWeight:700,color:"var(--ink)"}}>
              {acct.currencySymbol||"$"}{fmtMoney(depAmt)}
            </div>
            <div style={{width:60,textAlign:"right"}}>
              <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:99,background:depositPaid?"var(--gl)":"var(--tl)",color:depositPaid?"var(--gm)":"var(--t)"}}>
                {depositPaid?"Paid":"Unpaid"}
              </span>
            </div>
          </div>
          {!depositPaid&&!isLocked&&(
            <div style={{display:"flex",gap:8}}>
              {btn("var(--t)","#fff",depPct>=100?"Send Payment Reminder":"Send Deposit Reminder",()=>generateReminder("deposit"),false)}
              {btn("var(--gl)","var(--gm)",<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Mark Paid</>,()=>markPaid("depositPaid"),false)}
            </div>
          )}
          {depositPaid&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:4}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:11,color:"var(--gm)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>{I.check} Received</div>
                {acct.stripeConnect?.chargesEnabled&&(()=>{
                  // Data-driven: find the matching Stripe payment row for this deposit
                  const pay=(acct.payments||[]).find(p=>p.clientId===c.id&&p.type==="deposit"&&(p.status==="succeeded"||p.status==="refunded"));
                  if(!pay) return null;
                  const isRefunded=pay.status==="refunded";
                  const inHold=!pay.paidOutAt;
                  const poDate=pay.paidOutAt?new Date(pay.paidOutAt):null;
                  const bg=isRefunded?"var(--err-bg)":inHold?"var(--ok-bg)":"var(--gl)";
                  const col=isRefunded?"var(--err-tx)":inHold?"var(--ok-tx)":"var(--mu)";
                  const text=isRefunded?"Refunded":inHold?"In hold":"Paid out "+poDate.toLocaleDateString();
                  return <span style={{fontSize:9,fontWeight:600,padding:"2px 6px",borderRadius:4,background:bg,color:col}}>{text}</span>;
                })()}
              </div>
              {!isLocked&&<button onClick={()=>guardedUpdate(c.id,"depositPaid",false)}
                style={{background:"none",border:"none",fontSize:11,color:"var(--mu)",cursor:"pointer",fontFamily:"'Inter',sans-serif",textDecoration:"underline"}}>
                Undo
              </button>}
            </div>
          )}
        </div>

        {/* Balance row — hidden when full payment (100%) */}
        {depPct<100&&<div style={{padding:"13px 14px"}}>
          <div style={{display:"flex",alignItems:"center",marginBottom:balancePaid||!depositPaid?0:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>Final Balance</div>
              <div style={{fontSize:11,color:"var(--mu)"}}>{100-depPct}% of total fee</div>
            </div>
            <div className="num" style={{width:70,textAlign:"right",fontSize:14,fontWeight:700,color:"var(--ink)"}}>
              {acct.currencySymbol||"$"}{fmtMoney(balAmt)}
            </div>
            <div style={{width:60,textAlign:"right"}}>
              <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:99,background:balancePaid?"var(--gl)":!depositPaid?"var(--bo)":"var(--tl)",color:balancePaid?"var(--gm)":!depositPaid?"var(--mu)":"var(--t)"}}>
                {balancePaid?"Paid":!depositPaid?"Locked":"Unpaid"}
              </span>
            </div>
          </div>
          {!balancePaid&&depositPaid&&!isLocked&&(
            <div style={{display:"flex",gap:8}}>
              {btn("var(--t)","#fff","Send Balance Reminder",()=>generateReminder("balance"),false)}
              {btn("var(--gl)","var(--gm)",<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Mark Paid</>,()=>markPaid("balancePaid"),false)}
            </div>
          )}
          {!balancePaid&&!depositPaid&&(
            <div style={{fontSize:11,color:"var(--mu)",fontStyle:"italic"}}>Deposit required first</div>
          )}
          {balancePaid&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:4}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:11,color:"var(--gm)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>{I.check} Received</div>
                {acct.stripeConnect?.chargesEnabled&&(()=>{
                  // Data-driven: find the matching Stripe payment row for this balance
                  const pay=(acct.payments||[]).find(p=>p.clientId===c.id&&p.type==="balance"&&(p.status==="succeeded"||p.status==="refunded"));
                  if(!pay) return null;
                  const isRefunded=pay.status==="refunded";
                  const inHold=!pay.paidOutAt;
                  const poDate=pay.paidOutAt?new Date(pay.paidOutAt):null;
                  const bg=isRefunded?"var(--err-bg)":inHold?"var(--ok-bg)":"var(--gl)";
                  const col=isRefunded?"var(--err-tx)":inHold?"var(--ok-tx)":"var(--mu)";
                  const text=isRefunded?"Refunded":inHold?"In hold":"Paid out "+poDate.toLocaleDateString();
                  return <span style={{fontSize:9,fontWeight:600,padding:"2px 6px",borderRadius:4,background:bg,color:col}}>{text}</span>;
                })()}
              </div>
              {!isLocked&&<button onClick={()=>guardedUpdate(c.id,"balancePaid",false)}
                style={{background:"none",border:"none",fontSize:11,color:"var(--mu)",cursor:"pointer",fontFamily:"'Inter',sans-serif",textDecoration:"underline"}}>
                Undo
              </button>}
            </div>
          )}
        </div>}

        {/* Total summary */}
        <div style={{padding:"10px 14px",background:"var(--cr)",borderTop:"1px solid var(--bo)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12,fontWeight:600,color:"var(--mu)"}}>Total project fee</span>
          <span className="num" style={{fontSize:15,fontWeight:700,color:"var(--ink)"}}>{c.amt||"—"}</span>
        </div>
      </div>

      {/* Reminder panel */}
      {reminder&&(
        <div style={{background:"var(--g)",borderRadius:14,padding:16,marginBottom:10}}>
          <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".08em",color:"var(--t)",fontWeight:600,marginBottom:8}}>
            ✦ {reminder==="balance"?"Final Balance":depPct>=100?"Full Payment":"Deposit"} reminder — {c.name.split(" ")[0]}
          </div>
          {reminderGenerating&&!reminderText?(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <div style={{display:"flex",gap:3}}>{[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:"50%",background:"rgba(255,255,255,.4)",animation:`pulse 1.2s ease-in-out ${i*.2}s infinite`}}/>)}</div>
              <span style={{fontSize:12,color:"rgba(255,255,255,.5)"}}>Writing reminder…</span>
              <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
            </div>
          ):(
            <textarea
              value={reminderText}
              onChange={e=>setReminderText(e.target.value)}
              placeholder="Type your reminder message or tap Regen to generate one…"
              style={{width:"100%",background:"rgba(255,255,255,.08)",border:"1.5px solid rgba(255,255,255,.15)",borderRadius:10,padding:12,fontSize:12,fontFamily:"'Inter',sans-serif",color:"rgba(255,255,255,.9)",outline:"none",resize:"none",lineHeight:1.6,minHeight:80,marginBottom:4}}
            />
          )}
          {reminderGenerating&&reminderText&&(
            <div style={{fontSize:10,color:"rgba(255,255,255,.4)",textAlign:"center",marginBottom:6}}>✦ AI is refining your message…</div>
          )}
          {isFree&&(
            <div style={{fontSize:10,color:"rgba(255,255,255,.35)",textAlign:"center",marginBottom:4,fontStyle:"italic"}}>Upgrade to Pro for AI-generated reminders</div>
          )}
          {(reminderText!==null)&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",gap:8}}>
                {isFree?(
                  <button disabled style={{flex:1,background:"rgba(255,255,255,.06)",color:"rgba(255,255,255,.35)",border:"1.5px solid rgba(255,255,255,.1)",borderRadius:10,padding:"9px",fontSize:11,fontWeight:600,cursor:"not-allowed",fontFamily:"'Inter',sans-serif"}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Upgrade to Pro for AI reminders</button>
                ):(
                  <button onClick={()=>generateReminder(reminder)} style={{flex:1,background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.7)",border:"none",borderRadius:10,padding:"9px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>↻ Regen</button>
                )}
                <button onClick={()=>setReminder(null)} style={{background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.5)",border:"none",borderRadius:10,padding:"9px 12px",fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>✕</button>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button
                  disabled={!c.email||!reminderText.trim()}
                  onClick={()=>{
                    if(!c.email||!reminderText.trim()) return;
                    const subject=encodeURIComponent(`Payment reminder — ${reminder==="balance"?"Final Balance":depPct>=100?"Full Payment":"Deposit"}`);
                    const body=encodeURIComponent(reminderText);
                    window.open(`mailto:${c.email}?subject=${subject}&body=${body}`,"_self");
                    onPaymentUpdate(c.id,"reminderSentAt",new Date().toISOString());
                    setReminderSent("email"); setTimeout(()=>{setReminder(null);setReminderSent(null);},2500);
                  }}
                  style={{flex:1,background:reminderSent==="email"?"var(--ok-tx)":(c.email&&reminderText.trim())?"#1e7e54":"rgba(255,255,255,.08)",color:"#fff",border:"none",borderRadius:10,padding:"10px 8px",fontSize:11,fontWeight:600,cursor:(c.email&&reminderText.trim())?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:(c.email&&reminderText.trim())?1:.45,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}
                >
                  {reminderSent==="email"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Opened</>:<>Email{c.email?<> <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span> {c.email.split("@")[0]}</>:""}</>}
                </button>
                <button
                  disabled={!c.phone||!reminderText.trim()}
                  onClick={()=>{
                    if(!c.phone||!reminderText.trim()) return;
                    const phone=(c.phone||"").replace(/\D/g,"");
                    const body=encodeURIComponent(reminderText);
                    const isIOS=/iPad|iPhone|iPod/.test(navigator.userAgent);
                    window.open(`sms:${phone}${isIOS?"&":"?"}body=${body}`,"_self");
                    onPaymentUpdate(c.id,"reminderSentAt",new Date().toISOString());
                    setReminderSent("sms"); setTimeout(()=>{setReminder(null);setReminderSent(null);},2500);
                  }}
                  style={{flex:1,background:reminderSent==="sms"?"var(--ok-tx)":(c.phone&&reminderText.trim())?"var(--t)":"rgba(255,255,255,.08)",color:reminderSent==="sms"?"#fff":(c.phone&&reminderText.trim())?"var(--tc)":"#fff",border:"none",borderRadius:10,padding:"10px 8px",fontSize:11,fontWeight:600,cursor:(c.phone&&reminderText.trim())?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:(c.phone&&reminderText.trim())?1:.45,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}
                >
                  {reminderSent==="sms"?<><span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.check}</span> Opened</>:<>SMS{c.phone?<> <span style={{display:"inline-flex",verticalAlign:"middle"}}>{I.arrow}</span> {c.phone.replace(/\D/g,"").slice(-4).padStart(c.phone.replace(/\D/g,"").length,"•")}</>:""}</>}
                </button>
                <button
                  disabled={!c.phone||!reminderText.trim()}
                  onClick={()=>{
                    if(!c.phone||!reminderText.trim()) return;
                    const msg=encodeURIComponent(reminderText);
                    window.open("https://wa.me/"+((c.phone||"").replace(/\D/g,""))+"?text="+msg,"_blank");
                    onPaymentUpdate(c.id,"reminderSentAt",new Date().toISOString());
                    setReminderSent("wa"); setTimeout(()=>{setReminder(null);setReminderSent(null);},2500);
                  }}
                  style={{flex:1,background:reminderSent==="wa"?"var(--ok-tx)":(c.phone&&reminderText.trim())?"#25D366":"rgba(255,255,255,.08)",color:"#fff",border:"none",borderRadius:10,padding:"10px 8px",fontSize:11,fontWeight:600,cursor:(c.phone&&reminderText.trim())?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:(c.phone&&reminderText.trim())?1:.45}}
                >
                  {reminderSent==="wa"?<><span style={{display:"inline-flex",alignItems:"center",marginRight:3}}>{I.check}</span> Opened</>:"WhatsApp"}
                </button>
              </div>
              {(!c.email||!c.phone)&&(
                <div style={{fontSize:10,color:"rgba(255,255,255,.4)",textAlign:"center"}}>
                  {!c.email&&!c.phone?"Add email and phone in Contact Details to send reminders":!c.email?"Add email in Contact Details to send email":!c.phone?"Add phone in Contact Details to send SMS/WhatsApp":""}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ADDITIONAL INVOICES ── */}
      <div style={{marginTop:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>
            Additional Invoices {invSaved&&<span style={{color:"var(--gm)",fontSize:11,fontWeight:400,display:"inline-flex",alignItems:"center",gap:3}}>{I.check} Sent to portal</span>}
          </div>
          {!isLocked&&<button onClick={()=>setShowInvoiceForm(s=>!s)}
            style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            {showInvoiceForm?"Cancel":"+ New Invoice"}
          </button>}
        </div>

        {showInvoiceForm&&(
          <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid var(--bo)",padding:16,marginBottom:12}}>
            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:12}}>New Invoice</div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Description *</div>
              <input value={invForm.description} onChange={e=>setInvForm(f=>({...f,description:e.target.value}))}
                placeholder="e.g. Drone footage add-on, Extra editing hour…"
                style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}/>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Amount ({sym}) *</div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:13,fontWeight:600,color:"var(--mu)"}}>{sym}</span>
                <input type="number" min={1} value={invForm.amount} onChange={e=>setInvForm(f=>({...f,amount:e.target.value}))}
                  placeholder="0.00"
                  style={{width:"100%",background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",paddingLeft:28,fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}/>
              </div>
            </div>
            <button onClick={addInvoice} disabled={!invForm.description.trim()||!invForm.amount}
              style={{width:"100%",background:invForm.description.trim()&&invForm.amount?"var(--t)":"var(--bo)",color:invForm.description.trim()&&invForm.amount?"var(--tc)":"var(--mu)",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.sendUp}</span>Send to Client Portal
            </button>
          </div>
        )}

        {invoices.length===0&&!showInvoiceForm&&(
          <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"12px 14px",fontSize:12,color:"var(--mu)",textAlign:"center"}}>
            No additional invoices yet. Tap "+ New Invoice" to bill for upsells or ad-hoc extras.
          </div>
        )}

        {invoices.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {invoices.map(inv=>(
              <div key={inv.id} style={{background:"var(--card)",borderRadius:12,border:`1.5px solid ${inv.paid?"var(--gl)":"var(--bo)"}`,padding:"12px 14px",display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:2}}>{inv.description}</div>
                  <div style={{fontSize:11,color:"var(--mu)"}}>{inv.createdAt}</div>
                </div>
                <div style={{fontSize:14,fontWeight:700,color:inv.paid?"var(--gm)":"var(--ink)",flexShrink:0}}>{sym}{fmtMoney(inv.amount)}</div>
                {inv.paid
                  ?<div style={{fontSize:11,fontWeight:700,color:"var(--gm)",padding:"3px 8px",background:"var(--gl)",borderRadius:99,flexShrink:0,display:"inline-flex",alignItems:"center",gap:3}}>{I.check} Paid</div>
                  :<button onClick={()=>markInvoicePaid(inv.id)}
                    style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                    Mark Paid
                  </button>
                }
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── REFUNDS ── */}
      <div id={"refund-section-"+c.id} style={{marginTop:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>
            Refunds {refundSaved&&<span style={{color:"var(--gm)",fontSize:11,fontWeight:400,display:"inline-flex",alignItems:"center",gap:3}}>{I.check} Processed via Stripe</span>}
          </div>
          {!refundState&&<div style={{display:"flex",gap:6}}>
            <button onClick={()=>{
              if(!showRefundForm&&isCancelled&&refunds.length===0){
                // Pre-fill for cancellation refund
                const thread=(acct.threads||[]).find(t=>t.clientId===c.id);
                const lateCancelMsg=(thread?.msgs||[]).find(m=>m.f!=="me"&&/^LATE CANCELLATION REQUEST:/i.test(m.t));
                if(lateCancelMsg){
                  const chargeMatch=lateCancelMsg.t.match(/\[Charge:\s*([^\]]+)\]/);
                  const feeStr=chargeMatch?chargeMatch[1].replace(/[^0-9.]/g,""):"";
                  const fee=parseFloat(feeStr)||0;
                  const refundable=Math.max(0,depAmt-fee);
                  setRefundForm({amount:String(refundable),reason:"Cancellation refund (deposit minus late cancellation fee)"});
                } else {
                  setRefundForm({amount:String(depositPaid?depAmt:0),reason:"Cancellation refund"});
                }
              }
              setShowRefundForm(s=>!s);
            }}
              style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1.5px solid var(--err-bo)",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {showRefundForm?"Cancel":"↩ Issue Refund"}
            </button>
          </div>}
        </div>

        {refundState==="processing"&&(
          <div style={{background:"var(--gl)",borderRadius:14,padding:18,textAlign:"center",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:6}}>{I.hourglass}</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>Processing refund via Stripe…</div>
            <div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>Please wait.</div>
          </div>
        )}

        {refundState==="done"&&(
          <div style={{background:"var(--gl)",borderRadius:14,padding:18,textAlign:"center",marginBottom:12}}>
            <div style={{marginBottom:6,color:"var(--gm)",display:"flex",justifyContent:"center"}}>{I.checkCircle}</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>Refund processed successfully</div>
            <div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>{isCancelled?"Client has been automatically archived.":"The client will receive their funds within 5–10 business days."}</div>
          </div>
        )}

        {refundState==="manual-done"&&(
          <div style={{background:"var(--gl)",borderRadius:14,padding:18,textAlign:"center",marginBottom:12}}>
            <div style={{marginBottom:6,color:"var(--gm)",display:"flex",justifyContent:"center"}}>{I.checkCircle}</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--gm)"}}>Manual refund recorded</div>
            <div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>{isCancelled?"Client has been automatically archived. ":""}Recorded in your books — no Stripe charge was made. Refund the client outside the app.</div>
          </div>
        )}

        {refundState==="failed"&&(
          <div style={{background:"var(--err-bg)",borderRadius:14,padding:18,textAlign:"center",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:6,color:"var(--err-tx)"}}>{I.xCircle}</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--err-tx)"}}>Refund failed</div>
            <div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}>{refundError||"An error occurred. Please try again."}</div>
          </div>
        )}

        {showRefundForm&&!refundState&&(
          <div style={{background:"var(--card)",borderRadius:14,border:"1.5px solid #FECACA",padding:16,marginBottom:12}}>
            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--err-tx)",fontWeight:600,marginBottom:12}}>New Refund</div>
            {/* Payout warning — data-driven from payments.paid_out_at */}
            {acct.stripeConnect?.chargesEnabled&&(()=>{
              // Find all Stripe payments for this client that actually cleared.
              // Only *succeeded* rows are still available for a Stripe refund — refunded rows are spent.
              const clientPays=(acct.payments||[]).filter(p=>p.clientId===c.id&&p.status==="succeeded");
              if(clientPays.length===0) return null;
              const anyHeld=clientPays.some(p=>!p.paidOutAt);
              const allPaidOut=clientPays.every(p=>p.paidOutAt);
              const latestPayout=clientPays.filter(p=>p.paidOutAt).map(p=>new Date(p.paidOutAt)).sort((a,b)=>b-a)[0]||null;
              return(<div style={{background:anyHeld?"var(--ok-bg)":"var(--warn-bg)",border:"1.5px solid "+(anyHeld?"var(--ok-tx)":"var(--warn-bo)"),borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12,lineHeight:1.5}}>
                {anyHeld?(<><div style={{fontWeight:600,color:"var(--ok-tx)",marginBottom:2}}>Refund window open</div><div style={{color:"var(--mu)"}}>Some funds are still held by Stripe. Refunding now won't debit your bank — it comes from the held balance.</div></>)
                :(<><div style={{fontWeight:600,color:"var(--warn-tx)",marginBottom:2}}>Funds have been paid out</div><div style={{color:"var(--mu)"}}>This payment was transferred to your Stripe account{allPaidOut&&latestPayout?" on "+latestPayout.toLocaleDateString():""}. Issuing a refund will debit your Stripe balance. If insufficient, your bank account will be debited.</div></>)}
              </div>);
            })()}
            {/* Refund summary — shows total paid (deposit + balance + paid invoices) vs already refunded */}
            {(()=>{
              const paidInvoicesTotal=(c.invoices||[]).filter(i=>i.paid).reduce((s,i)=>s+(Number(i.amount)||0),0);
              const paidInvoicesCount=(c.invoices||[]).filter(i=>i.paid).length;
              const totalPaid=(depositPaid?depAmt:0)+(balancePaid?balAmt:0)+paidInvoicesTotal;
              const totalRefunded=refunds.reduce((s,r)=>s+Number(r.amount||0),0);
              const maxRefundable=Math.max(0,totalPaid-totalRefunded);
              return totalPaid>0?(
                <div style={{background:"var(--cr)",borderRadius:10,border:"1px solid var(--bo)",padding:"10px 14px",marginBottom:12,display:"flex",flexDirection:"column",gap:4}}>
                  {depositPaid&&<div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--mu)"}}><span>{depPct>=100?"Full payment":"Deposit"}</span><span style={{fontWeight:600}}>{sym}{fmtMoney(depAmt)}</span></div>}
                  {balancePaid&&<div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--mu)"}}><span>Balance</span><span style={{fontWeight:600}}>{sym}{fmtMoney(balAmt)}</span></div>}
                  {paidInvoicesCount>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--mu)"}}><span>Paid invoices ({paidInvoicesCount})</span><span style={{fontWeight:600}}>{sym}{fmtMoney(paidInvoicesTotal)}</span></div>}
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--mu)",borderTop:"1px solid var(--bo)",paddingTop:4}}><span>Total received</span><span style={{fontWeight:600}}>{sym}{fmtMoney(totalPaid)}</span></div>
                  {totalRefunded>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--err-tx)"}}><span>Already refunded</span><span style={{fontWeight:600}}>-{sym}{fmtMoney(totalRefunded)}</span></div>}
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:700,color:"var(--ink)",borderTop:"1px solid var(--bo)",paddingTop:4}}><span>Max refundable</span><span>{sym}{fmtMoney(maxRefundable)}</span></div>
                </div>
              ):null;
            })()}
            {/* Payment picker — which Stripe charge to refund */}
            {acct.stripeConnect?.chargesEnabled&&(()=>{
              if(stripePayments===null||stripeRefundsDb===null) return <div style={{background:"var(--cr)",borderRadius:10,border:"1px solid var(--bo)",padding:"10px 14px",marginBottom:12,fontSize:12,color:"var(--mu)",textAlign:"center"}}>Loading Stripe charges…</div>;
              if(refundablePayments.length===0) return (
                <div style={{background:"var(--warn-bg)",borderRadius:10,border:"1px solid var(--warn-bo)",padding:"10px 14px",marginBottom:12,fontSize:12,lineHeight:1.5}}>
                  <div style={{fontWeight:600,color:"var(--warn-tx)",marginBottom:2}}>No Stripe charges available</div>
                  <div style={{color:"var(--mu)"}}>{stripePayments.length>0?"All Stripe payments on this client have already been fully refunded.":"This client has no Stripe charges."} Use <strong>Manual</strong> to record a refund made outside Stripe.</div>
                </div>
              );
              return (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:6,display:"flex",justifyContent:"space-between"}}>
                    <span>Refund which charge? *</span>
                    <span style={{fontSize:10,fontWeight:500}}>{refundablePayments.length} refundable</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {refundablePayments.map(p=>{
                      const active=selectedPaymentId===p.id;
                      const held=!p.paid_out_at;
                      const chargeAmt=Number(p.amount||0);
                      const remaining=remainingByPayment(p.id);
                      const partial=remaining<chargeAmt; // some refunds already taken
                      return (
                        <div key={p.id} onClick={()=>selectPayment(p.id)}
                          style={{background:active?"var(--tl)":"var(--card)",border:`1.5px solid ${active?"var(--t)":"var(--bo)"}`,borderRadius:10,padding:"10px 12px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",transition:"all .15s"}}>
                          <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${active?"var(--t)":"var(--bo)"}`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {active&&<div style={{width:8,height:8,borderRadius:"50%",background:"var(--t)"}}/>}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{paymentLabel(p)}</div>
                            <div style={{fontSize:10,color:"var(--mu)"}}>{p.created_at?new Date(p.created_at).toLocaleDateString():""} · <span style={{color:held?"var(--ok-tx)":"var(--mu)",fontWeight:600}}>{held?"In hold":"Paid out"}</span>{partial?<> · <span style={{color:"var(--warn-tx)",fontWeight:600}}>Partial refund taken</span></>:null}</div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{sym}{fmtMoney(remaining)}</div>
                            {partial&&<div style={{fontSize:10,color:"var(--mu)",fontVariantNumeric:"tabular-nums"}}>of {sym}{fmtMoney(chargeAmt)}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Reason / Service *</div>
              <input value={refundForm.reason} onChange={e=>setRefundForm(f=>({...f,reason:e.target.value}))}
                placeholder="e.g. Cancellation refund, Partial service not delivered…"
                style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}/>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:"var(--mu)",marginBottom:4,display:"flex",justifyContent:"space-between"}}>
                <span>Refund amount ({sym}) *</span>
                {selectedPayment&&<span style={{fontSize:10,color:"var(--mu)"}}>Max {sym}{fmtMoney(selectedMaxRefund)}</span>}
              </div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:13,fontWeight:600,color:"var(--mu)"}}>{sym}</span>
                <input type="number" min={0} step="0.01" max={selectedPayment?selectedMaxRefund:undefined} value={refundForm.amount} onChange={e=>setRefundForm(f=>({...f,amount:e.target.value}))}
                  placeholder="0.00"
                  style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",paddingLeft:28,fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none"}}/>
              </div>
            </div>
            <div style={{background:"var(--err-bg)",borderRadius:10,border:"1px solid var(--err-bo)",padding:"10px 14px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:12,color:"#B91C1C"}}>Refund processed via Stripe · 5–10 business days</div>
              <div style={{fontSize:13,fontWeight:700,color:"var(--err-tx)"}}>{refundForm.amount?`${sym}${fmtMoney(parseFloat(refundForm.amount))}`:"—"}</div>
            </div>
            {(()=>{
              const stripeReady=acct.stripeConnect?.chargesEnabled&&stripePayments!==null&&refundablePayments.length>0;
              const canSubmit=refundForm.reason.trim()&&refundForm.amount&&(stripeReady?!!selectedPaymentId:true);
              return (
                <button onClick={()=>setShowRefundConfirm(true)} disabled={!canSubmit}
                  style={{width:"100%",background:canSubmit?"#DC2626":"var(--bo)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:canSubmit?"pointer":"default",fontFamily:"'Inter',sans-serif"}}>
                  <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> Process Refund via Stripe
                </button>
              );
            })()}
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:8}}>
              <span style={{fontSize:11,color:"var(--mu)"}}>Powered by</span>
              <span style={{fontSize:12,fontWeight:700,color:"#635BFF"}}>stripe</span>
            </div>
          </div>
        )}

        {/* Refund confirmation modal */}
        {showRefundConfirm&&(()=>{
          const refundAmtNum=parseFloat(refundForm.amount)||0;
          const loadingStripe=stripePayments===null;
          const noRefundable=!loadingStripe&&refundablePayments.length===0;
          const nothingSelected=!loadingStripe&&refundablePayments.length>0&&!selectedPayment;
          const overPerCharge=!!selectedPayment&&refundAmtNum>selectedMaxRefund;
          const stripeBlocked=noRefundable||nothingSelected||overPerCharge;
          const blockReason=noRefundable?(stripePaid<=0?"No Stripe charges on this client.":"All Stripe payments have already been fully refunded.")
                           :nothingSelected?"Select which payment to refund above."
                           :overPerCharge?`Amount exceeds the remaining refundable on this ${paymentLabel(selectedPayment).toLowerCase()} (${sym}${fmtMoney(selectedMaxRefund)}).`
                           :"";
          return (
            <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={()=>setShowRefundConfirm(false)}>
              <div style={{background:"var(--card)",borderRadius:16,padding:"24px 20px",maxWidth:380,width:"100%"}} onClick={e=>e.stopPropagation()}>
                <div style={{display:"flex",justifyContent:"center",marginBottom:8}}>{I.dollar}</div>
                <div style={{fontSize:15,fontWeight:700,color:"var(--ink)",marginBottom:4,textAlign:"center"}}>Confirm Refund</div>
                <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.6,marginBottom:16,textAlign:"center"}}>
                  You are about to issue a <strong>{sym}{refundForm.amount?fmtMoney(refundAmtNum):0}</strong> refund to <strong>{c.name}</strong>.
                  {isCancelled&&" This client will be automatically archived after the refund is processed."}
                  {" "}This action cannot be undone.
                </div>
                <div style={{background:"var(--err-bg)",borderRadius:10,border:"1px solid var(--err-bo)",padding:"10px 14px",marginBottom:12}}>
                  {selectedPayment&&(()=>{
                    const chargeAmt=Number(selectedPayment.amount||0);
                    const partial=selectedMaxRefund<chargeAmt;
                    return (
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                        <span style={{color:"var(--mu)"}}>Charge</span>
                        <span style={{color:"var(--ink)",fontWeight:600}}>{paymentLabel(selectedPayment)} · {sym}{fmtMoney(selectedMaxRefund)}{partial?<span style={{color:"var(--mu)",fontWeight:400}}> of {sym}{fmtMoney(chargeAmt)}</span>:null}</span>
                      </div>
                    );
                  })()}
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}><span style={{color:"var(--mu)"}}>Amount</span><span style={{fontWeight:700,color:"var(--err-tx)"}}>{sym}{refundForm.amount?fmtMoney(refundAmtNum):0}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginTop:4}}><span style={{color:"var(--mu)"}}>Reason</span><span style={{color:"var(--ink)"}}>{refundForm.reason}</span></div>
                </div>
                <div style={{background:stripeBlocked?"var(--err-bg)":"var(--cr)",borderRadius:10,border:`1px solid ${stripeBlocked?"var(--err-bo)":"var(--bo)"}`,padding:"8px 12px",marginBottom:16,fontSize:11,lineHeight:1.5}}>
                  {loadingStripe ? (
                    <span style={{color:"var(--mu)"}}>Checking Stripe balance…</span>
                  ) : (
                    <>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <span style={{color:"var(--mu)"}}>Charged via Stripe</span>
                        <span style={{fontWeight:600,color:"var(--ink)"}}>{sym}{fmtMoney(stripePaid)}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",marginTop:2}}>
                        <span style={{color:"var(--mu)"}}>Refundable via Stripe</span>
                        <span style={{fontWeight:600,color:stripeBlocked?"var(--err-tx)":"var(--ink)"}}>{sym}{fmtMoney(stripeRefundable)}</span>
                      </div>
                      {stripeBlocked&&(
                        <div style={{color:"var(--err-tx)",fontWeight:600,marginTop:6,paddingTop:6,borderTop:"1px solid var(--err-bo)"}}>
                          {blockReason} {noRefundable&&"Use Manual to record an off-platform refund."}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setShowRefundConfirm(false)} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                  <button onClick={issueManualRefund} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1.5px solid var(--ink)",background:"var(--card)",color:"var(--ink)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Manual</button>
                  <button onClick={issueRefund} disabled={stripeBlocked||loadingStripe} style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:(stripeBlocked||loadingStripe)?"var(--bo)":"#DC2626",color:(stripeBlocked||loadingStripe)?"var(--mu)":"#fff",fontSize:13,fontWeight:600,cursor:(stripeBlocked||loadingStripe)?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif"}}>Stripe</button>
                </div>
              </div>
            </div>
          );
        })()}

        {refunds.length===0&&!showRefundForm&&!refundState&&(
          <div style={{background:"var(--cr)",borderRadius:10,border:"1.5px dashed var(--bo)",padding:"10px 14px",fontSize:12,color:"var(--mu)",textAlign:"center"}}>
            No refunds issued. Use "↩ Issue Refund" for cancellations or partial credits.
          </div>
        )}

        {refunds.length>0&&!refundState&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {refunds.map(r=>(
              <div key={r.id} style={{background:"var(--err-bg)",borderRadius:12,border:"1.5px solid var(--err-bo)",padding:"12px 14px",display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--err-tx)",marginBottom:2}}>↩ {r.reason}</div>
                  <div style={{fontSize:11,color:"var(--mu)"}}>{r.date} · {r.stripeStatus==="manual"?"Manual":"via Stripe"}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--err-tx)"}}>{sym}{fmtMoney(r.amount)}</div>
                  <div style={{fontSize:10,color:"var(--gm)",fontWeight:600}}>{r.stripeStatus==="manual"?"Recorded":"Refunded"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function QuestionnaireBuilder({acct, c, onQuestionnaireUpdate, onResendQuestionnaire}){
  const clientVert = c.vert || acct.vert;
  const questions=(c.questionnaireQuestions&&c.questionnaireQuestions.length>0)?c.questionnaireQuestions:getDefaultQuestionnaire(clientVert);
  const [editing,setEditing]=useState(false);
  const [draft,setDraft]=useState([...questions]);
  const [saved,setSaved]=useState(false);
  const [confirmReopen,setConfirmReopen]=useState(false);
  const [showTplPicker,setShowTplPicker]=useState(false);

  // Filter templates to match this client's creative type(s)
  const clientVerts=c.verts||[c.vert].filter(Boolean);
  const matchingTemplates=(acct.questionnaireTemplates||[]).filter(t=>clientVerts.includes(t.vert));

  const applyTemplate=(tpl)=>{
    const newQs=tpl.questions.map(q=>({...q,key:q.key+"_"+Date.now()+"_"+Math.random().toString(36).slice(2,6)}));
    setDraft(d=>[...d,...newQs]);
    setShowTplPicker(false);
  };

  // Sync draft with latest questions when opening editor
  useEffect(()=>{ if(editing) setDraft([...((c.questionnaireQuestions&&c.questionnaireQuestions.length>0)?c.questionnaireQuestions:getDefaultQuestionnaire(clientVert))]); },[editing]);

  const addQ=()=>setDraft(d=>[...d,{key:`q${Date.now()}`,label:"",placeholder:"",type:"input"}]);
  const removeQ=(i)=>setDraft(d=>d.filter((_,idx)=>idx!==i));
  const updateQ=(i,field,val)=>setDraft(d=>d.map((q,idx)=>idx===i?{...q,[field]:val}:q));
  const save=()=>{
    const cleaned=draft.filter(q=>q.label.trim());
    if(cleaned.length===0) return; // need at least 1 question
    onQuestionnaireUpdate(c.id, cleaned);
    setEditing(false);
    setSaved(true);
    setTimeout(()=>setSaved(false),3000);
  };

  if(!editing) return (
    <div className="msec" style={{marginTop:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>
          Questionnaire {saved&&<span style={{color:"var(--gm)",fontSize:11,fontWeight:400,display:"inline-flex",alignItems:"center",gap:3}}>{I.check} Saved</span>}
        </div>
        {!c.archived&&!c.cancellationRequested&&<button onClick={()=>{setDraft([...questions]);setEditing(true);}}
          style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          {I.edit} Edit questions
        </button>}
      </div>

      {c.intakeSubmitted&&Object.keys(c.intake||{}).some(k=>(c.intake[k]||"").trim())?(
        // Submitted — show answers + option to re-open for more detail
        <>
          <div style={{background:"var(--gl)",borderRadius:10,padding:"8px 14px",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--gm)",fontWeight:600}}>
              <span>{I.check}</span> Client has submitted their answers
            </div>
            {!confirmReopen
              ?<button onClick={()=>setConfirmReopen(true)}
                style={{background:"none",border:"none",fontSize:11,color:"var(--t)",cursor:"pointer",fontWeight:600,fontFamily:"'Inter',sans-serif",textDecoration:"underline"}}>
                Re-open ↩
              </button>
              :<div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:11,color:"var(--mu)"}}>Re-open?</span>
                <button onClick={()=>{
                  if(onResendQuestionnaire) onResendQuestionnaire(c.id);
                  setConfirmReopen(false);
                }}
                  style={{background:"var(--t)",color:"var(--tc)",border:"none",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Yes
                </button>
                <button onClick={()=>setConfirmReopen(false)}
                  style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  No
                </button>
              </div>
            }
          </div>
          <div style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",overflow:"hidden"}}>
            {(()=>{
              const intake = c.intake || {};
              // Match answers by question key; fall back to showing all filled answers
              const rows = [];
              questions.forEach(q => {
                if(intake[q.key]?.trim()) rows.push({label:q.label, val:intake[q.key], key:q.key});
              });
              // If template changed and keys don't match, show raw answers
              if(rows.length === 0) {
                Object.entries(intake).forEach(([k,v]) => {
                  if(typeof v==="string"&&v.trim()&&k!=="date") {
                    const matchQ = questions.find(q=>q.key===k);
                    rows.push({label:matchQ?.label||"Answer", val:v, key:k});
                  }
                });
              }
              if(rows.length===0) return <div style={{padding:"12px 14px",fontSize:12,color:"var(--mu)"}}>Answers on file but no matching questions found.</div>;
              return rows.map((r,i)=>(
                <div key={r.key} style={{padding:"12px 14px",borderBottom:i<rows.length-1?"1px solid var(--bo)":"none"}}>
                  <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:4}}>{r.label}</div>
                  <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{r.val}</div>
                </div>
              ));
            })()}
          </div>
        </>
      ):(
        // Not yet submitted — show question list preview + resend nudge
        <>
          <div style={{background:"var(--card)",borderRadius:12,border:"1px solid var(--bo)",overflow:"hidden"}}>
            {questions.slice(0,3).map((q,i)=>(
              <div key={q.key} style={{padding:"10px 14px",borderBottom:i<2?"1px solid var(--bo)":"none",fontSize:12,color:"var(--mu)"}}>
                {i+1}. {q.label||"Untitled question"}
              </div>
            ))}
            {questions.length>3&&<div style={{padding:"8px 14px",fontSize:11,color:"var(--mu)",fontStyle:"italic"}}>+{questions.length-3} more questions</div>}
          </div>
          <div style={{marginTop:8,fontSize:11,color:"var(--mu)"}}>Client will see these questions in their portal. Waiting for them to submit.</div>
        </>
      )}
    </div>
  );

  return (
    <div className="msec" style={{marginTop:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>Edit Questionnaire</div>
        {matchingTemplates.length>0&&(
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowTplPicker(p=>!p)}
              style={{background:"var(--gl)",color:"var(--t)",border:"none",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
              <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.clipboard}</span>Use Template
            </button>
            {showTplPicker&&(
              <>
                <div style={{position:"fixed",inset:0,zIndex:99}} onClick={()=>setShowTplPicker(false)}/>
                <div style={{position:"absolute",right:0,top:"100%",marginTop:6,background:"var(--card)",borderRadius:12,border:"1.5px solid var(--bo)",boxShadow:"0 8px 24px rgba(0,0,0,.12)",zIndex:100,minWidth:220,maxHeight:260,overflowY:"auto",padding:6}}>
                  <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,padding:"6px 10px 4px"}}>Select a template</div>
                  {matchingTemplates.map(t=>(
                    <div key={t.id} onClick={()=>applyTemplate(t)}
                      style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",fontSize:12,color:"var(--ink)",display:"flex",flexDirection:"column",gap:2}}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--gl)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{fontWeight:600}}>{t.name}</div>
                      <div style={{fontSize:10,color:"var(--mu)"}}>{t.questions.length} question{t.questions.length!==1?"s":""}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
        {draft.map((q,i)=>(
          <div key={q.key} style={{background:"var(--card)",borderRadius:12,border:"1.5px solid var(--bo)",padding:12}}>
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <input value={q.label} onChange={e=>updateQ(i,"label",e.target.value)}
                placeholder="Question label e.g. Location or setting"
                style={{flex:1,border:"1px solid var(--bo)",borderRadius:8,padding:"7px 10px",fontSize:12,fontFamily:"'Inter',sans-serif",color:"var(--ink)",background:"var(--input-bg)",outline:"none"}}/>
              <select value={q.type} onChange={e=>updateQ(i,"type",e.target.value)}
                style={{border:"1px solid var(--bo)",borderRadius:8,padding:"7px 8px",fontSize:11,fontFamily:"'Inter',sans-serif",color:"var(--mu)",outline:"none",background:"var(--card)"}}>
                <option value="input">Short</option>
                <option value="textarea">Long</option>
              </select>
              <button onClick={()=>removeQ(i)} style={{background:"none",border:"none",color:"var(--bo)",cursor:"pointer",fontSize:16,padding:"0 4px"}}>✕</button>
            </div>
            <input value={q.placeholder} onChange={e=>updateQ(i,"placeholder",e.target.value)}
              placeholder="Placeholder hint text for client"
              style={{width:"100%",border:"1px solid var(--bo)",borderRadius:8,padding:"7px 10px",fontSize:11,fontFamily:"'Inter',sans-serif",color:"var(--mu)",background:"var(--input-bg)",outline:"none"}}/>
          </div>
        ))}
        <button onClick={addQ} style={{background:"var(--cr)",border:"1.5px dashed var(--bo)",borderRadius:12,padding:"10px",fontSize:12,fontWeight:600,cursor:"pointer",color:"var(--gm)",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          {I.plus} Add question
        </button>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>setEditing(false)} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"11px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
        <button onClick={save} disabled={!draft.some(q=>q.label.trim())} style={{flex:2,background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Save Questions</button>
      </div>
    </div>
  );
}

/* ── CLIENT MODAL ── */
function ClientModal({id,acct,onClose,onPortal,onIntakeSubmit,onContactUpdate,onTimelineUpdate,onPaymentUpdate,onNotesUpdate,onStatusUpdate,onThreadMessage,onQuestionnaireUpdate,onResendQuestionnaire,onDeleteClient,onArchiveClient,onClientEdit,onNavChange}){
  const c=acct.clients.find(x=>x.id===id);
  // ALL hooks MUST be declared before any conditional return (React Rules of Hooks).
  // Use safe fallbacks (c||{}) so hooks always initialise even when c is null.
  const _c=c||{};
  const [editContact,setEditContact]=useState(false);
  const [showArchiveConfirm,setShowArchiveConfirm]=useState(false);
  const [archiveReason,setArchiveReason]=useState(null);
  const [archiveNote,setArchiveNote]=useState("");
  const [emailField,setEmailField]=useState(_c.email||"");
  const [phoneField,setPhoneField]=useState(_c.phone||"");
  const [contactSaved,setContactSaved]=useState(false);
  const [contactCountry,setContactCountry]=useState(()=>{
    if(_c.phone){
      const match=Object.entries(DIAL_CODES).find(([,dial])=>_c.phone.startsWith(dial));
      if(match) return match[0];
    }
    return acct.country||"GB";
  });
  useEffect(()=>{
    if(!c||editContact) return;
    setEmailField(c.email||"");
    setPhoneField(c.phone||"");
    if(c.phone){
      const match=Object.entries(DIAL_CODES).find(([,dial])=>c.phone.startsWith(dial));
      if(match) setContactCountry(match[0]);
    }
  },[c?.email,c?.phone,c?.id]);
  const [tl,setTl]=useState(_c.tl||[]);
  const [tlGenerating,setTlGenerating]=useState(false);
  useEffect(()=>{ if(c) setTl(c.tl||[]); },[c?.id]);
  const [editTl,setEditTl]=useState(false);
  const tlSnapshot=useRef(null);
  const [showEditClient,setShowEditClient]=useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(false);
  const [editForm,setEditForm]=useState({
    name:_c.name||"",
    sessionType:_c.type?.split("·")[0]?.trim()||"",
    date:(_c.tl||[]).find(s=>s.d&&(s.l?.toLowerCase().includes("session")||s.l?.toLowerCase().includes("day")||s.l?.toLowerCase().includes("start")))?.d||"",
    amt:parseFloat((_c.amt||"0").replace(/[^0-9.]/g,""))||"",
    verts:_c.verts||[_c.vert].filter(Boolean),
  });
  useEffect(()=>{
    if(!c||showEditClient) return;
    setEditForm({
      name:c.name,
      sessionType:c.type?.split("·")[0]?.trim()||"",
      date:(c.tl||[]).find(s=>s.d&&(s.l?.toLowerCase().includes("session")||s.l?.toLowerCase().includes("day")||s.l?.toLowerCase().includes("start")))?.d||"",
      amt:parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||"",
      verts:c.verts||[c.vert].filter(Boolean),
    });
  },[c?.name,c?.type,c?.amt,c?.id]);

  // Sync-from-RPC: auto-tick timeline payment steps when DB shows paid
  useEffect(()=>{
    if(!c||String(c.id).startsWith("tmp_")||!onTimelineUpdate) return;
    const isFullPay=(c.depositPct||50)>=100;
    const depRx=isFullPay?/(full\s*payment|^payment\s*due|deposit)/:/deposit/;
    const balRx=/(balance|final\s*payment)/;
    const needsUpdate=(c.tl||[]).some(s=>{
      if(s.s==="done") return false;
      const lbl=(s.l||"").toLowerCase();
      if(c.depositPaid&&depRx.test(lbl)) return true;
      if(c.balancePaid&&balRx.test(lbl)) return true;
      return false;
    });
    if(needsUpdate){
      const today=localToday();
      const nextTl=(c.tl||[]).map(s=>{
        if(s.s==="done") return s;
        const lbl=(s.l||"").toLowerCase();
        if(c.depositPaid&&depRx.test(lbl)) return {...s,s:"done",d:c.depositPaidDate||today};
        if(c.balancePaid&&balRx.test(lbl)) return {...s,s:"done",d:c.balancePaidDate||today};
        return s;
      });
      onTimelineUpdate(c.id,nextTl);
    }
  },[c?.id,c?.depositPaid,c?.balancePaid]);

  // Early return AFTER all hooks — safe for React
  if(!c) return null;
  const v=VERTS.find(x=>x.id===c.vert);
  const _cvd=getClientVertDisplay(c);
  const isCancelled=!!c.cancellationRequested;
  const planOverClientLimit=acct.billingPlan==="free"&&(acct.clients||[]).filter(x=>!x.archived).length>3;
  const isLocked=c.archived||isCancelled||planOverClientLimit;
  const portalUrl=`lancer-app.com/${acct.slug}/${c.portalSlug}${c.portalToken?`?t=${c.portalToken}`:"" }`;
  const setEF=(k,v)=>setEditForm(f=>({...f,[k]:v}));

  const contactEmailValid=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((emailField||"").trim())&&(emailField||"").trim().length>=5;
  const saveContact=()=>{ if(c.archived) return; if(!contactEmailValid) return; onContactUpdate(c.id,emailField.trim(),phoneField); setEditContact(false); setContactSaved(true); setTimeout(()=>setContactSaved(false),2000); };

  const toggleStep=(i)=>{
    if(c.archived) return;
    // Prevent unticking the inquiry milestone
    const step=tl[i];
    if(i===0&&/inquiry/i.test(step?.l)&&step?.s==="done") return;
    const next=tl.map((s,idx)=>{
      if(idx!==i) return s;
      if(s.s==="done") return {...s,s:"pend"};
      if(s.s==="active") return {...s,s:"done"};
      return {...s,s:"done",d:localToday()};
    });
    setTl(next);
    onTimelineUpdate(c.id,next);
  };

  const updateStepLabel=(i,val)=>{
    if(c.archived) return;
    const next=tl.map((s,idx)=>idx===i?{...s,l:val}:s);
    setTl(next);
    onTimelineUpdate(c.id,next);
    // If this is the project-date step, also update the label part of c.type
    if(isProjectDateStep(i,tl)){
      const currentDate=c.projectDate||c.type?.split("·")[1]?.trim()||"";
      onClientEdit&&onClientEdit(c.id,{type:`${val} · ${currentDate||"TBD"}`});
    }
  };

  // Identify the project-date step: the step whose date matches c.projectDate,
  // OR if not set, the first step with a non-empty date that isn't done or a payment step
  const isProjectDateStep=(i,tlArr)=>{
    const step=tlArr[i];
    if(!step) return false;
    // Primary: exact match with stored projectDate
    if(c.projectDate&&step.d===c.projectDate) return true;
    // Secondary: date matches the date embedded in c.type (after "·")
    const typeDate=c.type?.split("·")[1]?.trim();
    if(typeDate&&step.d&&fmtDate(step.d)===typeDate) return true;
    // Tertiary: first step with a date that isn't inquiry/deposit/balance/contract
    if(!c.projectDate){
      const firstSessionIdx=tlArr.findIndex(s=>{
        const lc2=(s.l||"").toLowerCase();
        return s.d && !/inquiry|deposit|balance|contract/i.test(lc2);
      });
      return firstSessionIdx===i;
    }
    return false;
  };

  const updateStepDate=(i,iso)=>{
    const next=tl.map((s,idx)=>idx===i?{...s,d:iso}:s);
    setTl(next);
    onTimelineUpdate(c.id,next);
    // If this is the project-date step, sync to client card + calendar
    if(isProjectDateStep(i,tl)&&iso){
      const sessionLabel=c.type?.split("·")[0]?.trim()||tl[i]?.l||"Session";
      onClientEdit&&onClientEdit(c.id,{
        type:`${sessionLabel} · ${fmtDate(iso)}`,
        projectDate:iso,
        intake:{...(c.intake||{}),date:iso},
      });
    }
  };

  const addStep=()=>{
    const next=[...tl,{l:"New milestone",d:"",s:"pend"}];
    setTl(next);
    onTimelineUpdate(c.id,next);
  };

  const isProtectedStep=(s)=>{const lc=(s?.l||"").toLowerCase();return /deposit/i.test(lc)||/balance|final\s*payment/i.test(lc)||/full\s*payment/i.test(lc)||/contract\s*sent/i.test(lc)||/contract\s*signed/i.test(lc);};
  const removeStep=(i)=>{
    if(isProtectedStep(tl[i])) return;
    const next=tl.filter((_,idx)=>idx!==i);
    setTl(next);
    onTimelineUpdate(c.id,next);
  };

  const moveStep=(i,dir)=>{
    const target=i+dir;
    if(target<0||target>=tl.length) return;
    // Don't swap with the locked inquiry row (index 0)
    if(target===0&&/inquiry/i.test(tl[0]?.l)) return;
    if(i===0&&/inquiry/i.test(tl[0]?.l)) return;
    const next=[...tl];
    [next[i],next[target]]=[next[target],next[i]];
    setTl(next);
    onTimelineUpdate(c.id,next);
  };

  const generateTimeline=async()=>{
    setTlGenerating(true);
    const intake=c.intake||{};
    const questions=(c.questionnaireQuestions&&c.questionnaireQuestions.length>0)?c.questionnaireQuestions:getDefaultQuestionnaire(c.vert||acct.vert);
    const answeredQs=questions.filter(q=>intake[q.key]?.trim()).map(q=>`- ${q.label}: ${intake[q.key]}`).join("\n");
    const sym=acct.currencySymbol||"$";
    const rawAmt=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
    const depPct=rawAmt<=0?100:(c.depositPct||50);
    const depAmt=rawAmt<=0?0:(c.depositAmt||round2(rawAmt*depPct/100));
    const balAmt=rawAmt<=0?0:(c.balanceAmt||round2(rawAmt*(100-depPct)/100));

    // Build service description from verts array or single vert
    const serviceVerts=(c.verts&&c.verts.length>0?c.verts:[c.vert]).filter(Boolean);
    const serviceNames=serviceVerts.map(vid=>getVertLabel(vid)).join(" & ");
    const isMultiService=serviceVerts.length>1;

    const prompt=`You are a project timeline generator for a freelance creative business.

Freelancer: ${acct.name}
Service(s): ${serviceNames}
Client: ${c.name}
Project type: ${c.type}
${intake.date||c.tl?.find(s=>s.d)?.d?`Key date: ${intake.date||c.tl?.find(s=>s.d)?.d}`:""}
Fee: ${c.amt||"TBD"}${depPct>=100?"":" | Deposit: "+sym+fmtMoney(depAmt)+" | Balance: "+sym+fmtMoney(balAmt)}
${answeredQs?`\nClient details:\n${answeredQs}`:""}
${isMultiService?`\nNote: This is a multi-service project combining ${serviceNames}. The timeline should reflect all services.`:""}

Generate a realistic, specific project timeline for this ${serviceNames} project.
Return ONLY a valid JSON array — no markdown, no explanation, no code block.
Format: [{"l":"Step name","d":"YYYY-MM-DD or empty string","s":"done|active|pend"}]

ACTUAL PROJECT STATUS (you must use this — do not assume steps are done unless listed below):
- Contract sent to client: ${c.contractSentToPortal?"YES":"NO"}
- Contract signed by client: ${c.contractStage==="signed"||c.status==="signed"||c.status==="active"?"YES":"NO"}
- Deposit paid: ${c.depositPaid?"YES":"NO"}
- Balance paid: ${c.balancePaid?"YES":"NO"}
- Client status: ${c.status}

Rules:
- 6-9 steps covering the full lifecycle
- First step "Inquiry received" is ALWAYS "done" with today's date ${localToday()}
- ONLY mark a step as "done" if the corresponding status above is YES. If contract not sent → contract steps are "pend". If deposit not paid → deposit step is "pend".
- The FIRST incomplete step should be "active", all later steps should be "pend"
- Use terminology specific to ${serviceNames} (not "gallery delivered" for non-photographers, not "shoot day" for designers)
- MANDATORY steps that MUST be included: "Contract sent", "Contract signed"${depPct>=100?`, and "${sym}${fmtMoney(rawAmt)} payment due"`:`, "${sym}${fmtMoney(depAmt)} deposit due", and "${sym}${fmtMoney(balAmt)} final balance"`} — these must appear in this logical order within the timeline
- Keep step names concise (under 35 chars)
- Use YYYY-MM-DD dates only where the date is actually known — otherwise use empty string ""
- Do NOT invent completed steps that have not actually happened`;

    try{
      const res=await fetch(AI_PROXY_URL,{
        method:"POST",
        headers:aiHeaders(),
        body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:800,messages:[{role:"user",content:prompt}]})
      });
      if(!res.ok) throw new Error("AI error");
      const data=await res.json();
      const raw=data.content?.[0]?.text||"";
      const match=raw.match(/\[[\s\S]*\]/);
      if(match){
        let parsed=JSON.parse(match[0]);
        const createdDate=c.createdAt?c.createdAt.split("T")[0]:localToday();
        const today=localToday();
        const hasInquiry=parsed[0]&&parsed[0].l&&/inquiry/i.test(parsed[0].l);
        if(!hasInquiry){
          parsed=[{l:"Inquiry received",d:createdDate,s:"done"},...parsed];
        } else {
          parsed[0]={...parsed[0],d:createdDate,s:"done"};
        }
        // Enforce mandatory milestones: Contract sent, Contract signed, Deposit, Balance
        const contractSent=c.contractSentToPortal;
        const contractSigned=c.contractStage==="signed"||c.status==="signed"||c.status==="active";
        const mandatorySteps=[
          {pattern:/contract\s*sent/i,fallback:{l:"Contract sent",d:contractSent?today:"",s:contractSent?"done":"pend"}},
          {pattern:/contract\s*signed/i,fallback:{l:"Contract signed",d:contractSigned?today:"",s:contractSigned?"done":"pend"}},
          ...(depPct>=100
            ?[{pattern:/payment|deposit|balance/i,fallback:{l:`${sym}${fmtMoney(rawAmt)} payment due`,d:c.depositPaid?today:"",s:c.depositPaid?"done":"pend"}}]
            :[
              {pattern:/deposit/i,fallback:{l:`${sym}${fmtMoney(depAmt)} deposit due`,d:c.depositPaid?today:"",s:c.depositPaid?"done":"pend"}},
              {pattern:/balance|final\s*payment/i,fallback:{l:`${sym}${fmtMoney(balAmt)} final balance`,d:c.balancePaid?today:"",s:c.balancePaid?"done":"pend"}},
            ]),
        ];
        for(const m of mandatorySteps){
          if(!parsed.some(s=>m.pattern.test(s.l||""))) {
            // Insert before the first "pend" step or at the end
            const firstPend=parsed.findIndex(s=>s.s==="pend");
            if(firstPend>0) parsed.splice(firstPend,0,m.fallback);
            else parsed.push(m.fallback);
          }
        }
        setTl(parsed);
        onTimelineUpdate(c.id,parsed);
      }
    }catch(e){}
    setTlGenerating(false);
  };

  return (
    <div className="mov" onClick={e=>{if(e.target===e.currentTarget) onClose();}}>
      <div className="mov-inner">
      <div className="page-header">
        <button className="page-header-back" onClick={onClose}>{I.arrowLeft}</button>
        <div style={{width:36,height:36,borderRadius:"50%",background:c.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
          {c.portalPhoto?<img src={c.portalPhoto} alt={c.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:c.ini}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:16,fontWeight:600,color:"#fff",lineHeight:1.2}}>{c.name}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.6)",marginTop:1}}>{_cvd.icons[0]} {_cvd.labels.join(" & ")} · {c.type}</div>
        </div>
      </div>
      <div className="mod">

        {/* ARCHIVED BANNER */}
        {c.archived&&(
          <div style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:12,padding:"12px 14px",marginBottom:12,display:"flex",gap:10,alignItems:"center"}}>
            <span style={{flexShrink:0,display:"flex"}}>{I.archive}</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--warn-tx)"}}>Archived Client</div>
              <div style={{fontSize:12,color:"var(--info-tx)",marginTop:2}}>This client is archived and read-only.</div>
              {c.archiveReason&&<div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>Reason: {c.archiveReason==="completed"?<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.checkCircle}</span>Completed</>:c.archiveReason==="cancellation"?<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.ban}</span>Cancellation</>:<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.edit}</span>{c.archiveReason}</>}</div>}
            </div>
          </div>
        )}

        {/* CANCELLATION REQUEST BANNER */}
        {(()=>{
          const thread=(acct.threads||[]).find(t=>t.clientId===c.id);
          const cancelMsgs=(thread?.msgs||[]).filter(m=>m.f!=="me"&&/^(LATE )?CANCELLATION REQUEST:/i.test(m.t));
          if(cancelMsgs.length===0) return null;
          const latest=cancelMsgs[cancelMsgs.length-1];
          const isLate=/^LATE /i.test(latest.t);
          const reason=latest.t.replace(/^(LATE )?CANCELLATION REQUEST:\s*/i,"").replace(/\s*\[.*\]\s*$/,"").trim();
          const chargeMatch=latest.t.match(/\[Charge:\s*([^\]]+)\]/);
          return (
            <div style={{background:"var(--err-bg)",border:"1.5px solid var(--err-bo)",borderRadius:12,padding:"14px 14px",marginBottom:12}}>
              <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{display:"inline-flex",flexShrink:0,color:"var(--warn-tx)"}}>{I.alertTri}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--err-tx)",marginBottom:4}}>{isLate?"Late Cancellation Request":"Cancellation Request"}</div>
                  <div style={{fontSize:12,color:"#7F1D1D",lineHeight:1.6,marginBottom:chargeMatch?6:0}}>"{reason}"</div>
                  {chargeMatch&&<div style={{fontSize:11,fontWeight:600,color:"var(--warn-tx)",background:"var(--warn-bg)",borderRadius:6,padding:"4px 8px",display:"inline-block",marginBottom:4}}>{chargeMatch[1]}</div>}
                  {latest.ts&&<div style={{fontSize:10,color:"var(--mu)",marginTop:4}}>Received {latest.ts}</div>}
                </div>
              </div>
            </div>
          );
        })()}

        {/* CANCELLATION LOCKDOWN BANNER */}
        {isCancelled&&!c.archived&&(()=>{
          const depAmt=c.depositAmt||round2((parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0)*(c.depositPct||50)/100);
          const hasRefund=(c.refunds||[]).length>0;
          const refundScrollTarget=document.getElementById("refund-section-"+c.id);
          return (
            <div style={{background:"var(--err-bg)",border:"1.5px solid var(--err-bo)",borderRadius:12,padding:"16px 14px",marginBottom:12}}>
              <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{flexShrink:0,display:"flex"}}>{I.lock}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--err-tx)",marginBottom:4}}>Client Cancelled</div>
                  <div style={{fontSize:12,color:"var(--err-tx)",lineHeight:1.6,marginBottom:hasRefund?0:10}}>
                    This client has requested cancellation. The client page is now <strong>read-only</strong>. No further edits to timeline, payments, or details are allowed.
                  </div>
                  {!hasRefund&&c.depositPaid&&(
                    <div style={{background:"#fff",border:"1.5px solid var(--err-bo)",borderRadius:10,padding:"10px 14px",marginTop:8}}>
                      <div style={{fontSize:12,fontWeight:600,color:"var(--err-tx)",marginBottom:6}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.alertTri}</span>Refund required — {acct.currencySymbol||"$"}{fmtMoney(depAmt)}</div>
                      <button onClick={()=>{const el=document.getElementById("refund-section-"+c.id);if(el) el.scrollIntoView({behavior:"smooth",block:"center"});}}
                        style={{background:"#DC2626",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        ↩ Issue Refund
                      </button>
                    </div>
                  )}
                  {hasRefund&&(
                    <div style={{marginTop:6,fontSize:12,fontWeight:600,color:"var(--gm)",display:"flex",alignItems:"center",gap:4}}>{I.check} Refund issued</div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── EDIT CLIENT ── */}
        {!isLocked&&(
        <div className="msec" style={{marginBottom:12}}>
          <button onClick={()=>setShowEditClient(s=>!s)}
            style={{width:"100%",background:"var(--gl)",color:"var(--gm)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:4}}>{I.edit}</span>{showEditClient?"Cancel Editing":"Edit Client Details"}
          </button>
          {showEditClient&&(
            <div style={{marginTop:12,background:"var(--card)",borderRadius:14,border:"1.5px solid var(--bo)",padding:16,display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:4}}>Edit Client</div>
              <div>
                <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Client name</div>
                <input className="ifinput" value={editForm.name} onChange={e=>setEF("name",e.target.value)}
                  style={editForm.name&&!isFullName(editForm.name)?{borderColor:"var(--err-bo)"}:undefined}/>
                {editForm.name&&!isFullName(editForm.name)&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}>Please enter both first and last name.</div>}
              </div>
              <div>
                <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Services / project type</div>
                <input className="ifinput" value={editForm.sessionType} onChange={e=>setEF("sessionType",e.target.value)} placeholder="e.g. Wedding, Brand Film…"/>
              </div>
              <div>
                <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Creative services</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {(()=>{
                    const fVerts=(acct.verts||[acct.vert]).filter(Boolean);
                    const expanded=fVerts.flatMap(vid=>{
                      if(vid==="custom"){
                        const roles=Array.isArray(acct.customRole)?acct.customRole:(acct.customRole?[acct.customRole]:[]);
                        return roles.length>0?roles.map(r=>"custom:"+r):["custom"];
                      }
                      return [vid];
                    });
                    return expanded.map(vid=>{
                      const sel=editForm.verts.includes(vid);
                      return <div key={vid} onClick={()=>setEF("verts",sel?editForm.verts.filter(x=>x!==vid):[...editForm.verts,vid])}
                        style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:500,cursor:"pointer",border:`1.5px solid ${sel?"var(--g)":"var(--bo)"}`,background:sel?"var(--g)":"#fff",color:sel?"#fff":"var(--mu)"}}>
                        <span>{getVertIcon(vid)}</span>{getVertLabel(vid)}
                      </div>;
                    });
                  })()}
                </div>
              </div>
              {(()=>{
                const origFee=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
                const feeLocked=origFee>0;
                return(
                <div>
                  <div style={{fontSize:11,color:"var(--mu)",marginBottom:4}}>Total fee ({acct.currencySymbol||"$"}){!feeLocked&&" *"}</div>
                  <div style={{position:"relative"}}>
                    <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:13,fontWeight:600,color:feeLocked?"var(--bo)":"var(--mu)"}}>{acct.currencySymbol||"$"}</span>
                    <input className="ifinput" type="number" value={feeLocked?origFee.toFixed(2):editForm.amt} onChange={e=>{if(!feeLocked) setEditForm(f=>({...f,amt:e.target.value}));}} disabled={feeLocked} readOnly={feeLocked} style={{paddingLeft:26,background:feeLocked?"var(--gl)":undefined,color:feeLocked?"var(--mu)":undefined,cursor:feeLocked?"not-allowed":"text"}}/>
                  </div>
                  <div style={{fontSize:10,color:"var(--mu)",marginTop:4,lineHeight:1.5,fontStyle:feeLocked?"italic":"normal"}}>
                    {feeLocked
                      ?"Total fee is locked once set and cannot be changed."
                      :"Enter the total fee to start. Once saved, this becomes the client's full payment and cannot be changed later."}
                  </div>
                </div>);
              })()}
              <button onClick={()=>{
                // Require first + last name — don't allow save with a single-word name.
                if(!isFullName(editForm.name)){ return; }
                const sym=acct.currencySymbol||"$";
                const origFee=parseFloat((c.amt||"0").replace(/[^0-9.]/g,""))||0;
                const feeLocked=origFee>0;
                const newFee=feeLocked?origFee:(parseFloat(editForm.amt)||0);
                const rawPrimaryVert=editForm.verts[0]||c.vert;
                const primaryVert=rawPrimaryVert.startsWith("custom:")?  "custom":rawPrimaryVert;
                const updates={
                  name:editForm.name.trim()||c.name,
                  ini:editForm.name.trim().split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()||c.ini,
                  type:`${editForm.sessionType||c.type?.split("·")[0]?.trim()} · ${c.type?.split("·")[1]?.trim()||"TBD"}`,
                  vert:primaryVert,
                  verts:editForm.verts.length>0?editForm.verts:c.verts||[c.vert],
                };
                // Fee handling: only allow setting when fee was 0 (first-time entry = full payment promotion)
                if(!feeLocked&&newFee>0){
                  updates.amt=`${sym}${fmtMoney(newFee)}`;
                  updates.depositPct=100;
                  updates.depositAmt=round2(newFee);
                  updates.balanceAmt=0;
                  // Rebuild timeline: replace ANY existing payment-related step (deposit due,
                  // full payment due, payment due) with a single "Full payment due" step,
                  // and drop any "Final balance / Final payment" steps. Avoid duplicates.
                  const existingTl=c.tl||[];
                  const payRx=/(deposit\s*due|full\s*payment\s*due|payment\s*due)/i;
                  const balRx=/(final\s*balance|final\s*payment)/i;
                  const newLabel=`Full payment due — ${sym}${fmtMoney(newFee)}`;
                  let replaced=false;
                  let newTl=existingTl
                    .filter(s=>!balRx.test(s.l||""))
                    .map(s=>{
                      if(payRx.test(s.l||"")){
                        if(replaced) return null; // drop subsequent payment steps to prevent duplicates
                        replaced=true;
                        return {...s,l:newLabel};
                      }
                      return s;
                    })
                    .filter(Boolean);
                  if(!replaced){
                    // No payment step exists — insert after "Contract signed" or "Contract sent"
                    const idx=newTl.findIndex(s=>/contract\s*signed|contract\s*sent/i.test(s.l||""));
                    const insertAt=idx>=0?idx+1:newTl.length;
                    newTl.splice(insertAt,0,{l:newLabel,d:"",s:"pend"});
                  }
                  updates.tl=newTl;
                }
                onClientEdit(c.id,updates);
                setShowEditClient(false);
              }} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Save Changes
              </button>
            </div>
          )}
        </div>
        )}

        {/* CONTACT DETAILS */}
        <div className="msec">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div className="mstl" style={{marginBottom:0}}>Contact Details</div>
            {!isLocked&&<button onClick={()=>setEditContact(e=>!e)} style={{background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {editContact?"Cancel":<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.edit}</span>Edit</>}
            </button>}
          </div>
          <div style={{background:"var(--card)",borderRadius:14,border:"1px solid var(--bo)",overflow:"hidden"}}>
            {editContact?(
              <div style={{padding:14,display:"flex",flexDirection:"column",gap:10}}>
                <div>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",marginBottom:5}}>Email address *</div>
                  <input className="ifinput" type="email" placeholder="client@email.com" value={emailField} onChange={e=>setEmailField(e.target.value)} style={emailField&&!contactEmailValid?{borderColor:"var(--err-bo)"}:undefined}/>
                  {emailField&&!contactEmailValid&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:4}}>Please enter a valid email address.</div>}
                  {!emailField&&<div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>Required — needed to process payments via Stripe.</div>}
                </div>
                <div>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",marginBottom:5}}>Phone number</div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <select
                      value={contactCountry}
                      onChange={e=>{
                        setContactCountry(e.target.value);
                        // Strip old dial code and re-prefix with new one
                        const stripped=phoneField.replace(/^\+\d{1,4}\s*/,"");
                        const newDial=DIAL_CODES[e.target.value]||"+44";
                        setPhoneField(stripped?newDial+" "+stripped:"");
                      }}
                      style={{background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"13px 8px",fontSize:18,fontFamily:"'Inter',sans-serif",color:"var(--mu)",outline:"none",flexShrink:0,width:58}}>
                      {COUNTRIES.map(co=><option key={co.code} value={co.code}>{co.flag}</option>)}
                    </select>
                    <PhoneInput className="ifinput" countryCode={contactCountry} value={phoneField} onChange={setPhoneField} placeholder="Phone number"/>
                  </div>
                </div>
                <button onClick={saveContact} disabled={!contactEmailValid} style={{background:contactSaved?"var(--ok-tx)":(contactEmailValid?"var(--g)":"var(--bo)"),color:contactEmailValid||contactSaved?"#fff":"var(--mu)",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:contactEmailValid?"pointer":"default",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                  {contactSaved?<><span style={{display:"inline-flex",alignItems:"center"}}>{I.check}</span> Saved!</>:"Save Contact Details"}
                </button>
              </div>
            ):(
              <>
                <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderBottom:"1px solid var(--bo)"}}>
                  <span style={{color:"var(--gm)",display:"flex"}}>{I.mail}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:"var(--mu)",marginBottom:2}}>Email</div>
                    <div style={{fontSize:13,fontWeight:500,color:c.email?"var(--ink)":"var(--mu)",fontStyle:c.email?"normal":"italic"}}>{c.email||"Not added yet"}</div>
                  </div>
                  {!c.email&&<span style={{fontSize:11,color:"var(--t)",fontWeight:600}}>Required for email</span>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px"}}>
                  <span style={{color:"var(--gm)",display:"flex"}}>{I.phone}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:"var(--mu)",marginBottom:2}}>Phone</div>
                    <div style={{fontSize:13,fontWeight:500,color:c.phone?"var(--ink)":"var(--mu)",fontStyle:c.phone?"normal":"italic"}}>{c.phone||"Not added yet"}</div>
                  </div>
                  {!c.phone&&<span style={{fontSize:11,color:"var(--t)",fontWeight:600}}>Required for SMS</span>}
                </div>
              </>
            )}
          </div>
        </div>

        {/* AI BRIEFING */}
        <AiBriefing c={c} acct={acct} />

        {/* QUESTIONNAIRE BUILDER */}
        <QuestionnaireBuilder acct={acct} c={c} onQuestionnaireUpdate={onQuestionnaireUpdate} onResendQuestionnaire={onResendQuestionnaire} />

        {/* NOTES */}
        <FreelancerNotes key={c.id+"_notes"} c={c} onNotesUpdate={onNotesUpdate} />
        <SurpriseNotes key={c.id+"_surprise"} c={c} onNotesUpdate={onNotesUpdate} />

        {/* CALENDAR ENTRIES */}
        {(()=>{
          const entries=(acct.calEntries||[]).filter(e=>e.clientId===c.id).sort((a,b)=>a.date.localeCompare(b.date));
          if(entries.length===0) return null;
          return (
            <div className="msec" style={{marginTop:16}}>
              <div className="mstl">Calendar Entries</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {entries.map(e=>(
                  <div key={e.id} onClick={()=>{if(onNavChange){onClose();onNavChange("calendar");}}}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--gl)",borderRadius:10,cursor:"pointer"}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:c.col,flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.subject}</div>
                      <div style={{fontSize:11,color:"var(--mu)"}}>{fmtDate(e.date)}{e.time?` · ${e.time}`:""}</div>
                    </div>
                    <span style={{color:"var(--gm)",display:"flex",alignItems:"center",flexShrink:0}}>{I.chevron}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* SEND UNIQUE LINK */}
        <div className="msec" style={{marginTop:16}}>
          <div className="mstl">Send Unique Portal Link</div>
          <SendLinkPanel c={c} portalUrl={portalUrl} acct={acct} />
        </div>

        {/* PROJECT DETAILS */}
        <div className="msec" style={{marginTop:16}}>
          <div className="mstl">Project Details</div>
          <div className="dgrid">
            <div className="dcell"><div className="dclbl">Total Value</div><div className="dcval g">{canSeeFinancials(acct)?c.amt:"••••"}</div></div>
            <div className="dcell"><div className="dclbl">Progress</div><div className="dcval t">{c.pct}%</div></div>
          </div>
          {/* Status selector */}
          {!isLocked&&<div style={{marginTop:12}}>
            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"var(--mu)",fontWeight:600,marginBottom:8}}>Status</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {STATUS_STAGES.map(s=>{
                const active=c.status===s.key;
                return (
                  <div key={s.key} onClick={()=>onStatusUpdate(c.id,s.key)}
                    style={{padding:"7px 14px",borderRadius:99,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${active?s.col:"var(--bo)"}`,background:active?s.col:"#fff",color:active?"#fff":"var(--mu)",transition:"all .15s"}}>
                    {s.label}
                  </div>
                );
              })}
            </div>
          </div>}
        </div>

        {/* INTERACTIVE TIMELINE */}
        <div className="msec" style={{marginTop:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div className="mstl" style={{marginBottom:0}}>Project Timeline</div>
            {!isLocked&&<div style={{display:"flex",gap:8}}>
              <button onClick={()=>{
                if(editTl){
                  const blocking=tl.filter(s=>{
                    const lc=s.l.toLowerCase();
                    return (lc.includes("deposit")||lc.includes("balance"))&&s.s!=="done"&&!s.d;
                  });
                  if(blocking.length===0){ setEditTl(false); tlSnapshot.current=null; }
                } else {
                  tlSnapshot.current=JSON.parse(JSON.stringify(tl));
                  setEditTl(true);
                }
              }} style={{background:editTl?"var(--t)":"var(--gl)",color:editTl?"#fff":"var(--g)",border:"none",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                {editTl?<>Done {I.check}</>:<><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.edit}</span>Edit</>}
              </button>
              {editTl&&<button onClick={()=>{
                if(tlSnapshot.current){ setTl(tlSnapshot.current); onTimelineUpdate(c.id,tlSnapshot.current); }
                tlSnapshot.current=null; setEditTl(false);
              }} style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"none",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Cancel
              </button>}
              {acct.billingPlan==="free"?(
                <button disabled title="Upgrade to Pro for AI Timeline generation" style={{background:"var(--gl)",color:"var(--mu)",border:"1.5px solid var(--bo)",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:"not-allowed",fontFamily:"'Inter',sans-serif"}}>
                  <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:2}}>{I.lock}</span> AI Generate
                </button>
              ):(
                <button onClick={generateTimeline} disabled={tlGenerating} style={{background:"var(--g)",color:"#fff",border:"none",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",opacity:tlGenerating?.6:1}}>
                  {tlGenerating?"✦ Generating…":"✦ AI Generate"}
                </button>
              )}
            </div>}
          </div>

          {tlGenerating&&(
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 0",marginBottom:8}}>
              <div style={{display:"flex",gap:3}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:"var(--g)",animation:`pulse 1.2s ease-in-out ${i*.2}s infinite`}}/>)}</div>
              <span style={{fontSize:12,color:"var(--mu)"}}>Generating timeline from project details…</span>
              <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
            </div>
          )}

          {editTl?(
            /* EDIT MODE */
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {(()=>{
                const missingRequired=tl.filter(s=>{
                  const lc=s.l.toLowerCase();
                  return (lc.includes("deposit")||lc.includes("balance"))&&s.s!=="done"&&!toISO(s.d);
                });
                return missingRequired.length>0&&(
                  <div style={{background:"var(--warn-bg)",border:"1.5px solid var(--warn-bo)",borderRadius:10,padding:"9px 12px",fontSize:12,color:"var(--warn-tx)",display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{display:"inline-flex",color:"var(--warn-tx)"}}>{I.alertTri}</span>
                    <span>Deposit and balance milestones require a due date before saving.</span>
                  </div>
                );
              })()}
              {tl.map((s,i)=>{
                const lc=s.l.toLowerCase();
                const isInquiry=i===0&&/inquiry/i.test(s.l);
                const isRequired=(lc.includes("deposit")||lc.includes("balance"))&&s.s!=="done";
                const missingDate=isRequired&&!toISO(s.d);
                // Locked inquiry step — render as read-only row
                if(isInquiry) return (
                  <div key={i} style={{background:"var(--gl)",borderRadius:14,border:"1.5px solid var(--g)",overflow:"hidden",display:"flex",alignItems:"center",gap:10,padding:"12px 14px"}}>
                    <div className="tldot done" style={{flexShrink:0}}>{I.check}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:14,fontWeight:500,color:"var(--gm)"}}>{s.l}</div>
                      <div style={{fontSize:11,color:"var(--gm)",marginTop:2}}>{fmtDate(s.d)} · Client created</div>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,color:"var(--gm)",textTransform:"uppercase",letterSpacing:".05em",background:"rgba(43,68,56,.1)",borderRadius:6,padding:"2px 7px"}}>Locked</span>
                  </div>
                );
                return (
                <div key={i} style={{background:"var(--card)",borderRadius:14,border:`1.5px solid ${missingDate?"var(--warn-bo)":s.s==="done"?"var(--g)":s.s==="active"?"var(--t)":"var(--bo)"}`,overflow:"hidden",transition:"border-color .2s"}}>
                  {/* Top row: dot + label + required badge + delete */}
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px"}}>
                    <div
                      className={`tldot ${s.s==="pend"?"pend":s.s}`}
                      style={{flexShrink:0,cursor:"pointer",transition:"background .2s"}}
                      onClick={()=>toggleStep(i)}
                      title="Tap to toggle completion"
                    >
                      {s.s==="done"?I.check:s.s==="active"?I.dot:I.circleO}
                    </div>
                    <input
                      value={s.l}
                      onChange={e=>updateStepLabel(i,e.target.value)}
                      placeholder="Milestone name…"
                      style={{flex:1,border:"none",outline:"none",fontSize:14,fontWeight:500,fontFamily:"'Inter',sans-serif",color:"var(--ink)",background:"transparent",minWidth:0}}
                    />
                    {isRequired&&<span style={{fontSize:9,fontWeight:700,color:"var(--warn-tx)",textTransform:"uppercase",letterSpacing:".06em",flexShrink:0}}>Required</span>}
                    {/* Move up/down */}
                    <div style={{display:"flex",flexDirection:"column",gap:1,flexShrink:0}}>
                      <button onClick={()=>moveStep(i,-1)} disabled={i<=1||( i===1&&/inquiry/i.test(tl[0]?.l))} style={{background:"none",border:"none",color:i<=1?"var(--bo)":"var(--mu)",cursor:i<=1?"default":"pointer",padding:"1px 4px",display:"flex",alignItems:"center",borderRadius:4,fontSize:11,lineHeight:1}} title="Move up">▲</button>
                      <button onClick={()=>moveStep(i,1)} disabled={i>=tl.length-1} style={{background:"none",border:"none",color:i>=tl.length-1?"var(--bo)":"var(--mu)",cursor:i>=tl.length-1?"default":"pointer",padding:"1px 4px",display:"flex",alignItems:"center",borderRadius:4,fontSize:11,lineHeight:1}} title="Move down">▼</button>
                    </div>
                    {isProtectedStep(s)?(
                      <span style={{padding:4,display:"flex",alignItems:"center",flexShrink:0,color:"var(--bo)"}}>{I.lock}</span>
                    ):(
                      <button
                        onClick={()=>removeStep(i)}
                        style={{background:"none",border:"none",color:"var(--bo)",cursor:"pointer",padding:4,display:"flex",alignItems:"center",borderRadius:6,flexShrink:0}}
                        title="Remove milestone"
                      >
                        {I.close}
                      </button>
                    )}
                  </div>

                  {/* Date row */}
                  <div style={{borderTop:`1px solid ${missingDate?"var(--info-bo)":"var(--bo)"}`,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,background:missingDate?"var(--info-bg)":isProjectDateStep(i,tl)?"var(--ok-bg)":"var(--cr)"}}>
                    <span style={{color:missingDate?"var(--warn-tx)":isProjectDateStep(i,tl)?"var(--g)":"var(--mu)",display:"flex",alignItems:"center",flexShrink:0}}>{I.caldate}</span>
                    <div style={{flex:1}}>
                      {toISO(s.d)?(
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <span style={{fontSize:13,fontWeight:600,color:isProjectDateStep(i,tl)?"var(--g)":"var(--ink)"}}>{fmtDate(s.d)}</span>
                          {isProjectDateStep(i,tl)
                            ?<span style={{fontSize:10,background:"var(--g)",color:"#fff",borderRadius:6,padding:"2px 7px",fontWeight:600}}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.caldate}</span>Project date · syncs client card + calendar</span>
                            :<span style={{fontSize:10,background:"var(--gl)",color:"var(--gm)",borderRadius:6,padding:"2px 7px",fontWeight:600,display:"inline-flex",alignItems:"center",gap:3}}>{I.arrow} Calendar</span>
                          }
                        </div>
                      ):(
                        <span style={{fontSize:12,color:missingDate?"var(--warn-tx)":isProjectDateStep(i,tl)?"var(--g)":"var(--mu)",fontStyle:missingDate||isProjectDateStep(i,tl)?"normal":"italic",fontWeight:missingDate||isProjectDateStep(i,tl)?600:400}}>
                          {missingDate?"Date required":isProjectDateStep(i,tl)?"Set project date":"No date set"}
                        </span>
                      )}
                    </div>
                    {/* Date input — styled directly, no invisible overlay */}
                    <div style={{position:"relative",flexShrink:0}}>
                      <input
                        type="date"
                        value={toISO(s.d)||""}
                        min={c.createdAt?c.createdAt.split("T")[0]:localToday()}
                        onChange={e=>updateStepDate(i,e.target.value)}
                        style={{
                          background:missingDate?"var(--warn-bg)":toISO(s.d)?"var(--gl)":"var(--card)",
                          color:missingDate?"var(--warn-tx)":toISO(s.d)?"var(--g)":"var(--mu)",
                          border:`1.5px solid ${missingDate?"var(--warn-bo)":toISO(s.d)?"var(--g)":"var(--bo)"}`,
                          borderRadius:8,padding:"6px 10px",fontSize:11,
                          fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                          WebkitAppearance:"none",appearance:"none",outline:"none",
                          colorScheme:acct.darkMode?"dark":"light",
                          minWidth:110,
                        }}
                      />
                    </div>
                  </div>
                </div>
                );
              })}

              <button
                onClick={addStep}
                style={{background:"var(--gl)",color:"var(--gm)",border:"1.5px dashed var(--bo)",borderRadius:14,padding:"13px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
              >
                {I.plus} Add milestone
              </button>
            </div>
          ):(
            /* VIEW MODE — tap dot to complete */
            <div className="tls">
              {tl.map((s,i)=>(
                <div key={i} className="tlstep" style={{cursor:"pointer",borderRadius:8,padding:"8px 4px"}}>
                  <div className={`tldot ${s.s==="pend"?"pend":s.s}`} onClick={()=>toggleStep(i)}>
                    {s.s==="done"?I.check:s.s==="active"?I.dot:I.circleO}
                  </div>
                  <div style={{flex:1}}>
                    <div className={`tll${s.s==="pend"?" mu":""}`}>{s.l}</div>
                    <div className="tld">
                      {s.d
                        ? <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{color:"var(--mu)",display:"flex"}}>{I.caldate}</span>{fmtDate(s.d)}</span>
                        : <span style={{color:"var(--t)",fontStyle:"italic",fontSize:11}}>No date set</span>
                      }
                    </div>
                  </div>
                  <span style={{fontSize:10,color:s.s==="done"?"var(--gm)":"var(--bo)",marginLeft:"auto",display:"flex",alignItems:"center"}}>
                    {s.s==="done"?I.refresh:I.dot}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ACTIONS */}
        {canSeeFinancials(acct)&&<PaymentsPanel c={c} acct={acct} onPaymentUpdate={onPaymentUpdate}/>}
        <TeamOnProjectPanel c={c} acct={acct}/>
        <ClientActions c={c} portalUrl={portalUrl} acct={acct} onPortal={()=>{onClose();onPortal(c.id);}} onThreadMessage={onThreadMessage} isLocked={isLocked}/>

        {/* ── ARCHIVE / DELETE ── */}
        <div className="msec" style={{marginTop:8,marginBottom:32}}>
          {!c.archived&&!isCancelled&&(
            <>
              <button onClick={()=>setShowArchiveConfirm(true)}
                style={{width:"100%",background:"var(--gl)",color:"var(--mu)",border:"1.5px solid var(--bo)",borderRadius:12,padding:"11px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                <span style={{display:"inline-flex",verticalAlign:"middle",marginRight:3}}>{I.archive}</span> Archive Client
              </button>
              <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginTop:8}}>
                Archived clients are hidden from active views but their data is kept. They still count toward your plan limit.
              </div>
            </>
          )}
          {showArchiveConfirm&&(
            <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={()=>{setShowArchiveConfirm(false);setArchiveReason(null);setArchiveNote("");}}>
              <div style={{background:"var(--card)",borderRadius:16,padding:"24px 20px",maxWidth:380,width:"100%"}} onClick={e=>e.stopPropagation()}>
                <div style={{marginBottom:8,textAlign:"center",color:"var(--mu)",display:"flex",justifyContent:"center"}}>{I.archive}</div>
                <div style={{fontSize:15,fontWeight:700,color:"var(--ink)",marginBottom:4,textAlign:"center"}}>Archive {c.name}?</div>
                <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5,marginBottom:16,textAlign:"center"}}>Select a reason for archiving. This helps with reporting and analytics.</div>
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                  {[{key:"completed",label:"Completed",icon:I.checkCircle,sub:"Project finished successfully"},{key:"cancellation",label:"Cancellation",icon:I.ban,sub:"Client or project was cancelled"},{key:"other",label:"Other",icon:I.edit,sub:"Custom reason"}].map(opt=>(
                    <div key={opt.key} onClick={()=>setArchiveReason(opt.key)}
                      style={{padding:"12px 14px",borderRadius:12,border:`1.5px solid ${archiveReason===opt.key?"var(--g)":"var(--bo)"}`,background:archiveReason===opt.key?"var(--gl)":"var(--card)",cursor:"pointer",transition:"all .15s"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",display:"flex",alignItems:"center",gap:6}}><span style={{display:"inline-flex"}}>{opt.icon}</span>{opt.label}</div>
                      <div style={{fontSize:11,color:"var(--mu)",marginTop:2}}>{opt.sub}</div>
                    </div>
                  ))}
                </div>
                {archiveReason==="other"&&(
                  <textarea value={archiveNote} onChange={e=>setArchiveNote(e.target.value)}
                    placeholder="Enter reason…"
                    style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",color:"var(--ink)",outline:"none",resize:"none",lineHeight:1.5,minHeight:60,marginBottom:12}}/>
                )}
                {archiveReason==="other"&&!archiveNote.trim()&&<div style={{fontSize:11,color:"var(--err-tx)",marginTop:-8,marginBottom:8}}>Please enter a reason to continue</div>}
                <div style={{fontSize:11,color:"var(--mu)",lineHeight:1.5,marginBottom:16,textAlign:"center"}}>Once archived, <strong>{c.name}</strong> will be permanently read-only. All contracts will also be archived.</div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>{setShowArchiveConfirm(false);setArchiveReason(null);setArchiveNote("");}} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                  <button disabled={!archiveReason||(archiveReason==="other"&&!archiveNote.trim())} onClick={()=>{setShowArchiveConfirm(false);onArchiveClient(c.id,archiveReason==="other"?archiveNote.trim():archiveReason);setArchiveReason(null);setArchiveNote("");}} style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:archiveReason?"#F59E0B":"var(--bo)",color:archiveReason?"#fff":"var(--mu)",fontSize:13,fontWeight:600,cursor:archiveReason?"pointer":"default",fontFamily:"'Inter',sans-serif",opacity:archiveReason?1:.5}}>Archive</button>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>{/* end mod */}
      </div>{/* end mov-inner */}
    </div>
  );
}

/* ── ADMIN COMPONENTS ── */
const ADMIN_NAVS=[
  {id:"admin-users",   icon:I.clients,  label:"Users"},
  {id:"admin-support", icon:I.helpChat, label:"Support"},
  {id:"admin-metrics", icon:I.metrix,   label:"Metrix"},
  {id:"admin-home",    icon:I.home,     label:"Dashboard"},
  {id:"admin-links",   icon:I.link,     label:"Services"},
  {id:"admin-ops",     icon:I.calendar, label:"System"},
  {id:"admin-settings",icon:I.settings, label:"Settings"},
];

const AdminBannerLogo=({blend})=><img src={BANNER_B64} alt="Lancer" style={{height:36,objectFit:"contain",...(blend?{mixBlendMode:"screen",opacity:1}:{opacity:.18})}}/>;

function AdminConfirmModal({title,message,danger,onConfirm,onCancel}){
  return(<div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
    <div style={{background:"var(--card)",borderRadius:16,padding:24,maxWidth:380,width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,.2)"}}>
      <div style={{fontSize:17,fontWeight:700,color:"var(--ink)",marginBottom:8}}>{title}</div>
      <div style={{fontSize:13,color:"var(--mu)",lineHeight:1.5,marginBottom:20}}>{message}</div>
      <div style={{display:"flex",gap:10}}>
        <button onClick={onCancel} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Cancel</button>
        <button onClick={onConfirm} style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:danger?"#E53E3E":"var(--g)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{danger?"Confirm":"OK"}</button>
      </div>
    </div>
  </div>);
}

function AdminHome({metrics,users,activity,onNavChange,isDesktop}){
  const todayStr=new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
  const recentSignups=(users||[]).slice().sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0)).slice(0,5);
  const flaggedUsers=(users||[]).filter(u=>(u.user_flags?.banned||u.user_flags?.suspended));
  const m=metrics||{};
  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div style={{background:"linear-gradient(135deg,#1E3A5F 0%,#2C5282 100%)",position:"relative",overflow:"hidden",padding:"48px 24px 20px"}}>
      <div style={{position:"absolute",top:12,right:24}}><AdminBannerLogo blend/></div>
      <div style={{fontSize:11,color:"rgba(255,255,255,.55)",marginBottom:4,letterSpacing:".04em"}}>{todayStr}</div>
      <div className="greet" style={{color:"#fff",marginBottom:16}}>Admin Dashboard</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
        {[{v:m.total_users||0,l:"Total Users",nav:"admin-users"},{v:m.active_now||0,l:"Active Now",nav:"admin-users"},{v:m.total_contracts||0,l:"Contracts",nav:"admin-metrics"},{v:m.total_clients||0,l:"Clients",nav:"admin-metrics"}].map((s,i)=>(
          <div key={i} onClick={()=>onNavChange(s.nav)} style={{background:"rgba(255,255,255,.12)",borderRadius:14,padding:"14px 14px",cursor:"pointer",backdropFilter:"blur(4px)"}}>
            <div style={{fontSize:24,fontWeight:700,color:"#fff",fontVariantNumeric:"tabular-nums"}}>{typeof s.v==="number"?s.v.toLocaleString():s.v}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,.6)",textTransform:"uppercase",letterSpacing:".04em",marginTop:2}}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
    <div style={{padding:"0 24px"}}>
      <div style={{display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr":"1fr",gap:16}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",padding:"16px 0 10px"}}>Recent Signups</div>
          {recentSignups.length===0&&<div style={{padding:"12px 0",fontSize:13,color:"var(--mu)"}}>No users yet</div>}
          {recentSignups.map(u=>(
            <div key={u.id} className="admin-urow" onClick={()=>onNavChange("admin-users")} style={{marginBottom:6}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"var(--g)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:13,flexShrink:0}}>{(u.name||u.email||"?").slice(0,2).toUpperCase()}</div>
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{u.name||u.email}</div>
                <div style={{fontSize:11,color:"var(--mu)"}}>{u.created_at?new Date(u.created_at).toLocaleDateString():""}</div>
              </div>
              <div className={`spil ${u.billing_plan||"free"}`} style={{flexShrink:0}}>{(u.billing_plan||"free").charAt(0).toUpperCase()+(u.billing_plan||"free").slice(1)}</div>
            </div>
          ))}
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",padding:"16px 0 10px"}}>Flagged Users</div>
          {flaggedUsers.length===0&&<div style={{padding:"12px 0",fontSize:13,color:"var(--mu)"}}>No flagged users</div>}
          {flaggedUsers.map(u=>(
            <div key={u.id} className="admin-urow" onClick={()=>onNavChange("admin-users")} style={{marginBottom:6}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:u.user_flags?.banned?"var(--err-tx)":"var(--warn-tx)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:13,flexShrink:0}}>{(u.name||"?").slice(0,2).toUpperCase()}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{u.name||u.email}</div>
                <div style={{display:"flex",gap:4,marginTop:2}}>
                  {u.user_flags?.banned&&<span className="admin-badge ban">Banned</span>}
                  {u.user_flags?.suspended&&<span className="admin-badge sus">Suspended</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    {/* Platform Activity */}
    <div style={{padding:"16px 24px 80px"}}>
      <div style={{fontSize:12,fontWeight:700,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",padding:"0 0 10px"}}>Platform Activity</div>
      {(activity||[]).slice(0,8).map((a,i)=>(
        <div key={i} className="admin-act-row">
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:18,height:18,flexShrink:0,color:"var(--mu)"}}>{a.type==="signup"?I.user:a.type==="cancellation"?I.xCircle:a.type==="contract_signed"?I.sign:I.clipboard}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{a.description||a.type}</div>
            <div style={{fontSize:11,color:"var(--mu)"}}>{a.created_at?new Date(a.created_at).toLocaleDateString():""}{a.user_email?" · "+a.user_email:""}</div>
          </div>
        </div>
      ))}
      {(activity||[]).length===0&&<div style={{fontSize:13,color:"var(--mu)",padding:"8px 0"}}>No recent activity</div>}
    </div>
  </div>);
}

function AdminUsers({users,onSelectUser}){
  const [search,setSearch]=useState("");
  const [filter,setFilter]=useState("all");
  const [sort,setSort]=useState("newest");
  const filters=["all","free","pro","studio","banned","suspended"];
  const filtered=(users||[]).filter(u=>{
    if(search){const s=search.toLowerCase();if(!(u.name||"").toLowerCase().includes(s)&&!(u.email||"").toLowerCase().includes(s))return false;}
    if(filter==="banned")return u.user_flags?.banned;
    if(filter==="suspended")return u.user_flags?.suspended;
    if(filter!=="all")return(u.billing_plan||"free")===filter;
    return true;
  }).sort((a,b)=>{
    if(sort==="newest")return new Date(b.created_at||0)-new Date(a.created_at||0);
    if(sort==="name")return(a.name||"").localeCompare(b.name||"");
    return 0;
  });
  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div className="topbar" style={{position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:12,right:24,color:"var(--ink)"}}><AdminBannerLogo/></div>
      <div className="greet" style={{fontSize:24}}>Users</div>
      <div style={{fontSize:13,color:"var(--mu)",marginTop:2}}>{(users||[]).length} total users</div>
    </div>
    <div style={{position:"relative",padding:"16px 24px 0"}}>
      <div style={{position:"absolute",left:36,top:"50%",marginTop:8,transform:"translateY(-50%)",fontSize:14,color:"var(--mu)",pointerEvents:"none",display:"flex",alignItems:"center"}}>{I.search}</div>
      <input className="sinput" placeholder="Search users..." value={search} onChange={e=>setSearch(e.target.value)}/>
    </div>
    <div className="chips">
      {filters.map(f=><div key={f} className={`chip${filter===f?" on":""}`} onClick={()=>setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</div>)}
      <select value={sort} onChange={e=>setSort(e.target.value)} style={{marginLeft:"auto",padding:"6px 10px",borderRadius:99,fontSize:12,border:"1.5px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontFamily:"'Inter',sans-serif",cursor:"pointer"}}>
        <option value="newest">Newest</option>
        <option value="name">Name</option>
      </select>
    </div>
    <div style={{padding:"0 24px 80px"}}>
      {filtered.map(u=>(
        <div key={u.id} className="admin-urow" onClick={()=>onSelectUser(u)}>
          <div style={{width:40,height:40,borderRadius:"50%",background:"var(--g)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,flexShrink:0}}>{(u.name||u.email||"?").slice(0,2).toUpperCase()}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{u.name||"(no name)"}</div>
            <div style={{fontSize:11,color:"var(--mu)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{u.email}</div>
            {u.last_login_at&&<div style={{fontSize:10,color:"var(--mu)",marginTop:1}}>Last login: {new Date(u.last_login_at).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"})}</div>}
          </div>
          <div style={{display:"flex",gap:4,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}>
            {u.last_login_at&&(Date.now()-new Date(u.last_login_at).getTime()<15*60*1000)&&<span style={{width:8,height:8,borderRadius:"50%",background:"#38A169",flexShrink:0}} title="Online now"/>}
            {u.user_flags?.banned&&<span className="admin-badge ban">Banned</span>}
            {u.user_flags?.suspended&&<span className="admin-badge sus">Suspended</span>}
            <div className={`spil ${u.billing_plan||"free"}`}>{(u.billing_plan||"free").charAt(0).toUpperCase()+(u.billing_plan||"free").slice(1)}</div>
          </div>
        </div>
      ))}
      {filtered.length===0&&<div style={{textAlign:"center",padding:40,color:"var(--mu)",fontSize:14}}>No users match this filter</div>}
    </div>
  </div>);
}

function AdminUserDetail({user,onBack,onBan,onSuspend,onDelete,onResetPassword,onDeleteClient,onUnarchiveClient,onChangePlan}){
  const p=user.profile||user;
  const flags=p.user_flags||{};
  const [localClients,setLocalClients]=useState(user.clients||[]);
  const [localPlan,setLocalPlan]=useState(p.billing_plan||"free");
  const contracts=user.contracts||[];
  const clients=localClients;
  return(<div style={{position:"fixed",inset:0,zIndex:999,background:"var(--cr)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <div className="page-header" style={{background:"var(--g)",color:"#fff",position:"relative",overflow:"hidden",padding:"max(env(safe-area-inset-top),20px) 24px 20px"}}>
      <div style={{position:"absolute",top:12,right:24}}><AdminBannerLogo blend/></div>
      <button onClick={onBack} style={{background:"none",border:"none",color:"#fff",fontSize:14,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6,padding:"8px 0"}}>{I.arrowLeft} Back</button>
      <div style={{display:"flex",alignItems:"center",gap:14,marginTop:8}}>
        <div style={{width:52,height:52,borderRadius:"50%",background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:20,color:"#fff",flexShrink:0}}>{(p.name||p.email||"?").slice(0,2).toUpperCase()}</div>
        <div style={{minWidth:0}}>
          <div style={{fontSize:18,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name||"(no name)"}</div>
          <div style={{fontSize:12,opacity:.7,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.email}</div>
          <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}}>
            <div className={`spil ${p.billing_plan||"free"}`}>{(p.billing_plan||"free").charAt(0).toUpperCase()+(p.billing_plan||"free").slice(1)}</div>
            {flags.banned&&<span className="admin-badge ban">Banned</span>}
            {flags.suspended&&<span className="admin-badge sus">Suspended</span>}
          </div>
        </div>
      </div>
    </div>
    <div style={{flex:1,overflow:"auto",overflowX:"hidden",padding:"20px 24px 80px"}}>
      {/* Stats */}
      <div className="admin-grid" style={{padding:0,marginBottom:20}}>
        <div className="admin-stat"><div className="admin-stat-val">{p.client_count||clients.length||0}</div><div className="admin-stat-lbl">Clients</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{p.contract_count||contracts.length||0}</div><div className="admin-stat-lbl">Contracts</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{p.created_at?new Date(p.created_at).toLocaleDateString():"—"}</div><div className="admin-stat-lbl">Joined</div></div>
        <div className="admin-stat"><div className="admin-stat-val" style={{fontSize:p.last_login_at?13:24}}>{p.last_login_at?new Date(p.last_login_at).toLocaleDateString(undefined,{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"}):"—"}</div><div className="admin-stat-lbl">Last Login</div></div>
      </div>
      {/* Clients */}
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginBottom:8}}>Clients ({clients.length})</div>
      {clients.length===0&&<div style={{fontSize:13,color:"var(--mu)",marginBottom:16}}>No clients</div>}
      {clients.slice(0,20).map((c,i)=>{
        const isArchived=c.archived===true||c.status==="archived";
        return(
        <div key={c.id||i} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px 14px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,opacity:isArchived?0.7:1}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name||"Unnamed"}</div>
            <div style={{fontSize:11,color:"var(--mu)",display:"flex",alignItems:"center",gap:6}}>
              {isArchived?(c.pre_archive_status||"archived"):(c.status||"inquiry")}
              {isArchived&&<span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,background:"var(--warn-bg)",color:"var(--warn-tx)",textTransform:"uppercase",letterSpacing:".04em"}}>Archived</span>}
            </div>
          </div>
          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",flexShrink:0}}>{c.amt||""}</div>
          {isArchived&&onUnarchiveClient&&<button onClick={()=>{if(confirm("Unarchive client \""+( c.name||"Unnamed")+"\"? They will be restored to "+(c.pre_archive_status||"inquiry")+".")){onUnarchiveClient(c.id,c.pre_archive_status);setLocalClients(prev=>prev.map(x=>x.id===c.id?{...x,archived:false,status:c.pre_archive_status||"inquiry"}:x));}}} style={{background:"var(--ok-bg)",border:"1px solid var(--ok-tx)",cursor:"pointer",padding:"4px 10px",borderRadius:6,color:"var(--ok-tx)",fontSize:11,fontWeight:600,flexShrink:0,fontFamily:"'Inter',sans-serif"}} title="Unarchive client">Unarchive</button>}
          {!isArchived&&onDeleteClient&&<button onClick={()=>{if(confirm("Delete client \""+( c.name||"Unnamed")+"\"? This cannot be undone.")){onDeleteClient(c.id);setLocalClients(prev=>prev.filter(x=>x.id!==c.id));}}} style={{background:"none",border:"none",cursor:"pointer",padding:6,borderRadius:6,color:"var(--err-tx)",fontSize:14,lineHeight:1,flexShrink:0}} title="Delete client">{I.trash||"\uD83D\uDDD1"}</button>}
        </div>);
      })}
      {/* Contracts */}
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginTop:16,marginBottom:8}}>Contracts ({contracts.length})</div>
      {contracts.length===0&&<div style={{fontSize:13,color:"var(--mu)",marginBottom:16}}>No contracts</div>}
      {contracts.slice(0,10).map((c,i)=>(
        <div key={i} style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:10,padding:"10px 14px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{minWidth:0,flex:1}}><div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.client_name||c.name||"Unnamed"}</div><div style={{fontSize:11,color:"var(--mu)"}}>{c.status||"draft"}</div></div>
        </div>
      ))}
      {/* Plan Management */}
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginTop:24,marginBottom:8}}>Plan</div>
      <div style={{display:"flex",gap:8,marginBottom:20}}>
        {["free","pro","studio"].map(plan=>{
          const active=localPlan===plan;
          return(<button key={plan} onClick={()=>{if(!active&&onChangePlan){onChangePlan(p.id||user.id,plan);setLocalPlan(plan);}}} style={{flex:1,padding:"12px 8px",borderRadius:10,border:"1.5px solid "+(active?"var(--gm)":"var(--bo)"),background:active?"var(--gl)":"var(--card)",color:active?"var(--gm)":"var(--ink)",fontSize:13,fontWeight:active?700:500,cursor:active?"default":"pointer",fontFamily:"'Inter',sans-serif",transition:"all .15s"}}>{plan.charAt(0).toUpperCase()+plan.slice(1)}</button>);
        })}
      </div>
      {/* Actions */}
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginBottom:12}}>Admin Actions</div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        <button onClick={()=>onBan(p.id||user.id,!flags.banned,flags.banned?"Unbanned by admin":"Banned by admin")} style={{padding:"12px 16px",borderRadius:10,border:"1.5px solid "+(flags.banned?"var(--ok-tx)":"var(--err-tx)"),background:flags.banned?"var(--ok-bg)":"var(--err-bg)",color:flags.banned?"var(--ok-tx)":"var(--err-tx)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{flags.banned?"Unban User":"Ban User"}</button>
        <button onClick={()=>onSuspend(p.id||user.id,!flags.suspended)} style={{padding:"12px 16px",borderRadius:10,border:"1.5px solid var(--warn-tx)",background:"var(--warn-bg)",color:"var(--warn-tx)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{flags.suspended?"Resume User":"Suspend User"}</button>
        <button onClick={()=>onResetPassword(p.email||user.email)} style={{padding:"12px 16px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--card)",color:"var(--ink)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Reset Password</button>
        <button onClick={()=>onDelete(p.id||user.id)} style={{padding:"12px 16px",borderRadius:10,border:"none",background:"var(--err-tx)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Delete User</button>
      </div>
    </div>
  </div>);
}

// Phase 8 — Admin support queue. Sorted by SLA breach risk (most overdue first),
// then by tier (studio → pro → free), then by age. Closed tickets sink to bottom.
function AdminSupport(){
  const [tickets,setTickets]=useState([]);
  const [loading,setLoading]=useState(true);
  const [openTicketId,setOpenTicketId]=useState(null);
  const [filter,setFilter]=useState("open"); // open | all
  const refresh=async()=>{
    setLoading(true);
    const {data}=await adminListTickets();
    setTickets(data||[]);
    setLoading(false);
  };
  useEffect(()=>{ refresh(); },[]);
  const visible=tickets.filter(t=>filter==="all"||t.status!=="closed");

  if(openTicketId){
    return <AdminTicketThread ticketId={openTicketId} onBack={()=>{setOpenTicketId(null);refresh();}}/>;
  }

  return (
    <div style={{flex:1,overflowY:"auto",padding:"0 24px 24px"}}>
      <div className="greet" style={{color:"#fff",marginBottom:8}}>Support</div>
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        <div className={`chip${filter==="open"?" on":""}`} onClick={()=>setFilter("open")}>Open ({tickets.filter(t=>t.status!=="closed").length})</div>
        <div className={`chip${filter==="all"?" on":""}`} onClick={()=>setFilter("all")}>All ({tickets.length})</div>
      </div>
      {loading?(
        <div style={{fontSize:12,color:"var(--mu)"}}>Loading…</div>
      ):visible.length===0?(
        <div style={{fontSize:12,color:"var(--mu)"}}>No tickets.</div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {visible.map(t=>{
            const sla=t.sla_hours;
            const age=Number(t.hours_since_created)||0;
            const breached=sla!=null&&age>sla;
            const approaching=sla!=null&&!breached&&age>sla*0.7;
            const slaColor=t.status==="closed"?"var(--mu)":breached?"var(--err-tx)":approaching?"var(--warn-tx)":"var(--ok-tx)";
            const slaLabel=sla==null?"Best effort":breached?`Overdue · ${(age-sla).toFixed(1)}h past SLA`:approaching?`${(sla-age).toFixed(1)}h to SLA`:`${(sla-age).toFixed(1)}h to SLA`;
            const tierBadge=t.plan_at_creation==="studio"?"STUDIO":t.plan_at_creation==="pro"?"PRO":"FREE";
            const tierColor=t.plan_at_creation==="studio"?"var(--g)":t.plan_at_creation==="pro"?"var(--gm)":"var(--mu)";
            const lastReplyLabel=t.last_author_role==="admin"?"Awaiting user":t.last_author_role==="user"?"User replied":"Awaiting reply";
            return (
              <div key={t.id} onClick={()=>setOpenTicketId(t.id)} style={{background:"var(--card)",border:`1px solid ${breached?"var(--err-tx)":"var(--bo)"}`,borderRadius:12,padding:"12px 14px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{fontSize:9,fontWeight:700,color:"#fff",background:tierColor,padding:"2px 6px",borderRadius:4,letterSpacing:".04em"}}>{tierBadge}</span>
                  {t.severity!=="medium"&&<span style={{fontSize:9,fontWeight:700,color:t.severity==="urgent"?"var(--err-tx)":t.severity==="high"?"var(--warn-tx)":"var(--mu)",textTransform:"uppercase",letterSpacing:".04em"}}>{t.severity}</span>}
                  <span style={{fontSize:10,fontWeight:700,color:slaColor,marginLeft:"auto",whiteSpace:"nowrap"}}>{slaLabel}</span>
                </div>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:4}}>{t.subject}</div>
                <div style={{fontSize:11,color:"var(--mu)"}}>
                  {t.user_name||t.user_email} · {t.user_email}{t.workspace_name?` · ${t.workspace_name}`:""} · {age.toFixed(1)}h ago · {lastReplyLabel}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminTicketThread({ticketId,onBack}){
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [reply,setReply]=useState("");
  const [busy,setBusy]=useState(false);
  const refresh=async()=>{
    setLoading(true);
    const {data}=await supportGetTicket(ticketId);
    setRows(data||[]);
    setLoading(false);
  };
  useEffect(()=>{ refresh(); /* eslint-disable-next-line */ },[ticketId]);
  if(loading) return <div style={{padding:24,fontSize:12,color:"#fff"}}>Loading…</div>;
  const ticket=rows[0];
  if(!ticket) return <div style={{padding:24,color:"#fff"}}><button onClick={onBack} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:14}}>← Back</button><div style={{fontSize:12,marginTop:10,opacity:.7}}>Ticket not found.</div></div>;
  const replies=rows.slice(1).filter(r=>r.reply_id);
  const closed=ticket.status==="closed";
  const send=async(close)=>{
    if(!close && reply.trim().length<1) return;
    setBusy(true);
    const {error}=await adminPostTicketReply(ticketId,reply||"(closed without reply)",close);
    setBusy(false);
    if(error){ alert(error.message||"Could not send"); return; }
    setReply("");
    await refresh();
  };
  return (
    <div style={{flex:1,overflowY:"auto",padding:"0 24px 24px"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,paddingTop:8,marginBottom:14}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:18,padding:"4px 6px",lineHeight:1}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          <div className="greet" style={{color:"#fff",fontSize:22,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ticket.subject}</div>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
        <div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,padding:"12px 14px"}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>User · {new Date(ticket.created_at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
          <div style={{fontSize:13,color:"var(--ink)",whiteSpace:"pre-wrap",lineHeight:1.5}}>{ticket.body}</div>
        </div>
        {replies.map(r=>(
          <div key={r.reply_id} style={{background:r.reply_author_role==="admin"?"var(--gl)":"var(--card)",border:`1px solid ${r.reply_author_role==="admin"?"var(--g)":"var(--bo)"}`,borderRadius:12,padding:"12px 14px"}}>
            <div style={{fontSize:10,fontWeight:700,color:r.reply_author_role==="admin"?"var(--gm)":"var(--mu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>
              {r.reply_author_role==="admin"?"Lancer Support":"User"} · {new Date(r.reply_created_at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
            </div>
            <div style={{fontSize:13,color:"var(--ink)",whiteSpace:"pre-wrap",lineHeight:1.5}}>{r.reply_body}</div>
          </div>
        ))}
      </div>
      {!closed&&(
        <div>
          <textarea value={reply} onChange={e=>setReply(e.target.value)} placeholder="Reply to the user…"
            style={{width:"100%",background:"var(--input-bg)",border:"1.5px solid var(--bo)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"var(--ink)",outline:"none",fontFamily:"'Inter',sans-serif",resize:"vertical",minHeight:100,boxSizing:"border-box",lineHeight:1.5,marginBottom:8}}/>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>send(false)} disabled={busy||reply.trim().length<1} style={{flex:2,background:"var(--g)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:(busy||reply.trim().length<1)?"default":"pointer",opacity:(busy||reply.trim().length<1)?.5:1,fontFamily:"'Inter',sans-serif"}}>{busy?"Sending…":"Send reply"}</button>
            <button onClick={()=>send(true)} disabled={busy} style={{flex:1,background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:600,cursor:busy?"default":"pointer",fontFamily:"'Inter',sans-serif"}}>{reply.trim().length<1?"Close ticket":"Reply & close"}</button>
          </div>
        </div>
      )}
      {closed&&(
        <div style={{fontSize:12,color:"var(--mu)",textAlign:"center",padding:"10px",background:"var(--gl)",borderRadius:10}}>This ticket is closed.</div>
      )}
    </div>
  );
}

function AdminMetrics({metrics,users}){
  const m=metrics||{};
  const planBreakdown=m.plan_breakdown||{};
  const signupsByMonth=m.signups_by_month||[];
  const totalUsers=m.total_users||0;
  const maxSignups=Math.max(...signupsByMonth.map(s=>Number(s.count||s.signups||0)),1);
  const monthNames=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  // Growth: compare last 2 months
  const lastMonth=signupsByMonth.length>=1?Number(signupsByMonth[signupsByMonth.length-1]?.count||0):0;
  const prevMonth=signupsByMonth.length>=2?Number(signupsByMonth[signupsByMonth.length-2]?.count||0):0;
  const growthPct=prevMonth>0?Math.round(((lastMonth-prevMonth)/prevMonth)*100):lastMonth>0?100:0;
  // Plan pie data
  const planColors={free:"#63B3ED",pro:"#4299E1",studio:"#2C5282"};
  const planTotal=Object.values(planBreakdown).reduce((a,b)=>a+Number(b||0),0)||1;
  let pieOffset=0;
  const pieSegments=["free","pro","studio"].map(plan=>{
    const count=Number(planBreakdown[plan]||0);
    const pct=(count/planTotal)*100;
    const seg={plan,count,pct,offset:pieOffset,color:planColors[plan]};
    pieOffset+=pct;
    return seg;
  });
  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div className="topbar" style={{position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:12,right:24,color:"var(--ink)"}}><AdminBannerLogo/></div>
      <div className="greet" style={{fontSize:24}}>Metrix</div>
    </div>
    {/* Primary stats */}
    <div className="admin-grid" style={{marginTop:20,marginBottom:12}}>
      <div className="admin-stat"><div className="admin-stat-val">{totalUsers}</div><div className="admin-stat-lbl">Total Users</div></div>
      <div className="admin-stat"><div className="admin-stat-val">{m.active_30d||0}</div><div className="admin-stat-lbl">Active (30d)</div></div>
      <div className="admin-stat"><div className="admin-stat-val">{m.total_clients||0}</div><div className="admin-stat-lbl">Total Clients</div></div>
      <div className="admin-stat"><div className="admin-stat-val">{m.total_contracts||0}</div><div className="admin-stat-lbl">Total Contracts</div></div>
    </div>
    {/* Secondary stats */}
    <div className="admin-grid" style={{marginBottom:24}}>
      <div className="admin-stat"><div className="admin-stat-val">{m.active_7d||0}</div><div className="admin-stat-lbl">Active (7d)</div></div>
      <div className="admin-stat"><div className="admin-stat-val">{m.total_signed_contracts||0}</div><div className="admin-stat-lbl">Signed Contracts</div></div>
      <div className="admin-stat"><div className="admin-stat-val">{m.archived_clients||0}</div><div className="admin-stat-lbl">Archived Clients</div></div>
      <div className="admin-stat"><div className="admin-stat-val">{m.cancelled_clients||0}</div><div className="admin-stat-lbl">Cancelled Clients</div></div>
    </div>
    {/* Signup chart */}
    <div style={{padding:"0 24px",marginBottom:32}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>User Signups (by month)</div>
        {signupsByMonth.length>=2&&<div style={{fontSize:12,fontWeight:600,color:growthPct>=0?"var(--ok-tx)":"var(--err-tx)",display:"flex",alignItems:"center",gap:4}}>
          <span>{growthPct>=0?"\u25B2":"\u25BC"}</span>
          <span>{Math.abs(growthPct)}% vs prev month</span>
        </div>}
      </div>
      {signupsByMonth.length>0?<div style={{background:"var(--card)",border:"1px solid var(--bo)",borderRadius:14,padding:"20px 16px 12px",position:"relative"}}>
        <div className="adm-chart">
          <div className="adm-chart-grid"><div/><div/><div/><div/></div>
          {signupsByMonth.slice(-12).map((s,i)=>{
            const cnt=Number(s.count||s.signups||0);
            const pct=Math.max(3,cnt/maxSignups*100);
            const mo=parseInt((s.month||"").slice(5,7),10);
            return(<div key={i} className="adm-chart-col">
              <div className="adm-chart-val">{cnt>0?cnt:""}</div>
              <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",width:"100%"}}>
                <div className="adm-chart-bar" style={{height:pct+"%",background:"linear-gradient(180deg,var(--t) 0%,var(--g) 100%)",minHeight:4}}/>
              </div>
              <div className="adm-chart-lbl">{monthNames[(mo-1)%12]||mo}</div>
            </div>);
          })}
        </div>
      </div>:<div style={{fontSize:13,color:"var(--mu)",padding:"8px 0"}}>No signup data yet</div>}
    </div>
    {/* Plan breakdown with pie chart */}
    <div style={{padding:"0 24px 80px"}}>
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginBottom:16}}>Subscription Breakdown</div>
      <div style={{display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
        {/* Donut chart */}
        <div style={{flexShrink:0}}>
          <svg width="140" height="140" viewBox="0 0 36 36" style={{display:"block"}}>
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--bo)" strokeWidth="3" opacity=".3"/>
            {pieSegments.map((seg,i)=>seg.count>0?<circle key={i} cx="18" cy="18" r="15.9" fill="none" stroke={seg.color} strokeWidth="3.5"
              strokeDasharray={`${seg.pct} ${100-seg.pct}`} strokeDashoffset={`${25-seg.offset}`} strokeLinecap="round" style={{transition:"stroke-dasharray .4s"}}/>:null)}
            <text x="18" y="17" textAnchor="middle" fontSize="6" fontWeight="700" fill="var(--ink)">{totalUsers}</text>
            <text x="18" y="22" textAnchor="middle" fontSize="3" fill="var(--mu)">users</text>
          </svg>
        </div>
        {/* Legend + stat cards */}
        <div style={{flex:1,minWidth:200}}>
          {pieSegments.map(seg=>(
            <div key={seg.plan} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid var(--bo)"}}>
              <div style={{width:12,height:12,borderRadius:3,background:seg.color,flexShrink:0}}/>
              <div style={{flex:1,fontSize:13,fontWeight:600,color:"var(--ink)",textTransform:"capitalize"}}>{seg.plan}</div>
              <div style={{fontSize:18,fontWeight:700,color:"var(--ink)",fontVariantNumeric:"tabular-nums"}}>{seg.count}</div>
              <div style={{fontSize:12,color:"var(--mu)",width:45,textAlign:"right"}}>{totalUsers>0?Math.round(seg.count/totalUsers*100):0}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>);
}

function AdminOps({auditLog,activity}){
  const [tab,setTab]=useState("actions");
  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div className="topbar" style={{position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:12,right:24,color:"var(--ink)"}}><AdminBannerLogo/></div>
      <div className="greet" style={{fontSize:24}}>System</div>
    </div>
    <div className="chips">
      <div className={`chip${tab==="actions"?" on":""}`} onClick={()=>setTab("actions")}>Admin Actions</div>
      <div className={`chip${tab==="activity"?" on":""}`} onClick={()=>setTab("activity")}>Platform Activity</div>
    </div>
    <div style={{padding:"0 24px 80px"}}>
      {tab==="actions"&&<>
        {(auditLog||[]).length===0&&<div style={{textAlign:"center",padding:40,color:"var(--mu)",fontSize:14}}>No admin actions recorded</div>}
        {(auditLog||[]).map((entry,i)=>(
          <div key={entry.id||i} className="admin-act-row">
            <div style={{width:32,height:32,borderRadius:8,background:entry.action==="ban"?"var(--err-bg)":entry.action==="delete"?"var(--err-bg)":"var(--gl)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
              {entry.action==="ban"?I.ban:entry.action==="suspend"?I.pause:entry.action==="delete"?I.trash:I.clipboard}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{entry.action}</div>
              <div style={{fontSize:11,color:"var(--mu)"}}>{entry.detail||""}</div>
              <div style={{fontSize:10,color:"var(--mu)",marginTop:2}}>{entry.created_at?new Date(entry.created_at).toLocaleString():""}</div>
            </div>
          </div>
        ))}
      </>}
      {tab==="activity"&&<>
        {(activity||[]).length===0&&<div style={{textAlign:"center",padding:40,color:"var(--mu)",fontSize:14}}>No recent platform activity</div>}
        {(activity||[]).map((a,i)=>(
          <div key={i} className="admin-act-row">
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:18,height:18,flexShrink:0,color:"var(--mu)"}}>{a.type==="signup"?I.user:a.type==="cancellation"?I.xCircle:a.type==="contract_signed"?I.sign:I.clipboard}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{a.description||a.type}</div>
              <div style={{fontSize:11,color:"var(--mu)"}}>{a.user_name||a.user_email||""}</div>
              <div style={{fontSize:10,color:"var(--mu)",marginTop:2}}>{a.created_at?new Date(a.created_at).toLocaleString():""}</div>
            </div>
          </div>
        ))}
      </>}
    </div>
  </div>);
}

function AdminSettings({acct,onAcctUpdate,onSignOut}){
  const [name,setName]=useState(acct?.name||"");
  const [saving,setSaving]=useState(false);
  const saveName=async()=>{
    setSaving(true);
    try{await dbUpsertProfile(acct.id,{name});onAcctUpdate({name});}catch(e){}
    setSaving(false);
  };
  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div className="topbar" style={{position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:12,right:24,color:"var(--ink)"}}><AdminBannerLogo/></div>
      <div className="greet" style={{fontSize:24}}>Settings</div>
    </div>
    <div style={{padding:"20px 24px 80px"}}>
      {/* Profile */}
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginBottom:12}}>Profile</div>
      <div style={{marginBottom:16,maxWidth:480}}>
        <label style={{fontSize:12,color:"var(--mu)",marginBottom:4,display:"block"}}>Name</label>
        <div style={{display:"flex",gap:8}}>
          <input value={name} onChange={e=>setName(e.target.value)} style={{flex:1,padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--input-bg)",color:"var(--ink)",fontSize:14,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
          <button onClick={saveName} disabled={saving||name===acct?.name} style={{padding:"10px 18px",borderRadius:10,border:"none",background:"var(--g)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",opacity:(saving||name===acct?.name)?0.5:1}}>Save</button>
        </div>
      </div>
      <div style={{marginBottom:16,maxWidth:480}}>
        <label style={{fontSize:12,color:"var(--mu)",marginBottom:4,display:"block"}}>Email</label>
        <div style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--bo)",background:"var(--cr)",color:"var(--mu)",fontSize:14}}>{acct?.email||""}</div>
      </div>
      {/* Appearance */}
      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",marginTop:24,marginBottom:12}}>Appearance</div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:"1px solid var(--bo)",maxWidth:480}}>
        <div style={{fontSize:14,color:"var(--ink)"}}>Dark Mode</div>
        <div onClick={()=>onAcctUpdate({darkMode:!acct?.darkMode})} style={{width:44,height:24,borderRadius:12,background:acct?.darkMode?"var(--g)":"var(--bo)",cursor:"pointer",position:"relative",transition:"background .2s"}}>
          <div style={{width:20,height:20,borderRadius:10,background:"#fff",position:"absolute",top:2,left:acct?.darkMode?22:2,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
        </div>
      </div>
      {/* Sign out */}
      <button onClick={onSignOut} style={{marginTop:32,maxWidth:480,width:"100%",padding:"14px 0",borderRadius:12,border:"1.5px solid var(--err-tx)",background:"var(--err-bg)",color:"var(--err-tx)",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Sign Out</button>
    </div>
  </div>);
}

function AdminLinks(){
  const links=[
    {name:"Jira",desc:"Project management and issue tracking for sprint planning and bug tracking.",url:"https://www.atlassian.com/software/jira",color:"#0052CC",icon:I.clipboard},
    {name:"Supabase",desc:"Backend database, authentication, storage, and serverless edge functions.",url:"https://supabase.com/dashboard",color:"#3ECF8E",icon:I.zap},
    {name:"Stripe",desc:"Payment processing, subscriptions, and financial reporting dashboard.",url:"https://dashboard.stripe.com",color:"#635BFF",icon:I.creditcard},
    {name:"GitHub",desc:"Source code repository, version control, pull requests, and CI/CD workflows.",url:"https://github.com",color:"#24292F",icon:I.code},
    {name:"Vercel",desc:"Frontend deployment, hosting, serverless functions, and domain management.",url:"https://vercel.com/dashboard",color:"#000000",icon:I.triangle},
    {name:"Resend",desc:"Transactional email service for receipts, notifications, and 2FA codes.",url:"https://resend.com/overview",color:"#000000",icon:I.mail},
    {name:"GoDaddy",desc:"Domain registration, DNS management, and SSL certificate configuration.",url:"https://www.godaddy.com",color:"#1BDBDB",icon:I.globe},
  ];
  return(<div style={{flex:1,overflow:"auto",overflowX:"hidden"}}>
    <div className="topbar" style={{position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:12,right:24,color:"var(--ink)"}}><AdminBannerLogo/></div>
      <div className="greet" style={{fontSize:24}}>Services</div>
      <div style={{fontSize:13,color:"var(--mu)",marginTop:2}}>External tools & platforms</div>
    </div>
    <div style={{padding:"16px 24px 80px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
      {links.map(l=>(
        <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer" style={{background:"var(--card)",border:"1.5px solid var(--bo)",borderRadius:14,padding:"18px 16px",display:"flex",gap:14,alignItems:"flex-start",textDecoration:"none",cursor:"pointer",transition:"border-color .15s,box-shadow .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=l.color;e.currentTarget.style.boxShadow=`0 2px 12px ${l.color}22`;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--bo)";e.currentTarget.style.boxShadow="none";}}>
          <div style={{width:44,height:44,borderRadius:12,background:l.color,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff"}}>{l.icon}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:700,color:"var(--ink)",marginBottom:4,display:"flex",alignItems:"center",gap:6}}>{l.name}<span style={{fontSize:12,color:"var(--mu)",opacity:.7}}>↗</span></div>
            <div style={{fontSize:12,color:"var(--mu)",lineHeight:1.5}}>{l.desc}</div>
          </div>
        </a>
      ))}
    </div>
  </div>);
}

/* ── PORTAL OTP SESSION HELPERS ── */
// localStorage stores one entry per client_id so a freelancer's collaborators
// (who each get their own portal link) don't share verification state. The
// 30-day expiry is stamped server-side; we mirror it here only as a fast path
// to skip the get_portal_data_v2 round-trip when the session is obviously
// stale. Server-side validation is still authoritative.
const PORTAL_SESSION_PREFIX = "lancer_portal_session_";
const _getPortalSession = (clientId) => {
  if(!clientId) return null;
  try {
    const raw = localStorage.getItem(PORTAL_SESSION_PREFIX + clientId);
    if(!raw) return null;
    const parsed = JSON.parse(raw);
    if(!parsed?.token || !parsed?.expires_at) return null;
    if(new Date(parsed.expires_at).getTime() < Date.now()){
      localStorage.removeItem(PORTAL_SESSION_PREFIX + clientId);
      return null;
    }
    return parsed;
  } catch { return null; }
};
const _setPortalSession = (clientId, token, expiresAt) => {
  if(!clientId || !token) return;
  try { localStorage.setItem(PORTAL_SESSION_PREFIX + clientId, JSON.stringify({token, expires_at: expiresAt})); } catch {}
};
const _clearPortalSession = (clientId) => {
  if(!clientId) return;
  try { localStorage.removeItem(PORTAL_SESSION_PREFIX + clientId); } catch {}
};

/* ── PORTAL OTP GATE ── */
// Sits between portal-loading and portal-url. Renders three stages:
//   "send"     — show masked email + "Send code" button
//   "verify"   — 8-digit code input + resend timer
//   "no_email" — freelancer hasn't recorded the client's email yet
// On success, calls onVerified({clientId, sessionToken, expiresAt}); the App
// persists the session and re-runs the portal data load.
function PortalGate({gateData, freelancerSlug, clientSlug, portalToken, onVerified}){
  const NUM = 8;
  const initialStage = gateData?.error === "no_email" ? "no_email" : "send";
  const [stage, setStage] = useState(initialStage);
  const [maskedEmail, setMaskedEmail] = useState(gateData?.masked_email || "");
  const [digits, setDigits] = useState(Array(NUM).fill(""));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState("");
  const boxRefs = useRef([]);
  if(boxRefs.current.length !== NUM) boxRefs.current = Array(NUM).fill(null);

  useEffect(() => { const iv = setInterval(()=>setTimer(t=>t>0?t-1:0), 1000); return () => clearInterval(iv); }, []);

  const focus = i => { if(boxRefs.current[i]) boxRefs.current[i].focus(); };

  const sendCode = async () => {
    if(sending) return;
    setSending(true); setError("");
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/portal-send-otp`, {
        method: "POST",
        headers: {"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body: JSON.stringify({freelancer_slug: freelancerSlug, client_slug: clientSlug, portal_token: portalToken}),
      });
      const data = await r.json().catch(()=>({}));
      if(data?.ok){
        if(data.masked_email) setMaskedEmail(data.masked_email);
        setStage("verify"); setTimer(60);
        setDigits(Array(NUM).fill("")); setTimeout(()=>focus(0), 50);
      } else if(data?.error === "no_email"){
        setStage("no_email");
      } else {
        setError("Couldn't send code. Please try again.");
      }
    } catch { setError("Network error. Please check your connection."); }
    setSending(false);
  };

  const onChange = (i, val) => {
    if(!/^\d?$/.test(val)) return;
    const next = [...digits]; next[i] = val; setDigits(next);
    if(val && i < NUM - 1) focus(i + 1);
    if(next.filter(Boolean).length === NUM) setTimeout(()=>doVerify(next.join("")), 150);
  };
  const onKeyDown = (i, e) => { if(e.key === "Backspace" && !digits[i] && i > 0) focus(i - 1); };
  const onPaste = e => {
    const txt = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, NUM);
    if(txt.length === NUM){ setDigits(txt.split("")); setTimeout(()=>doVerify(txt), 150); }
  };

  const doVerify = async (code) => {
    if(verifying) return;
    setVerifying(true); setError("");
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/portal-verify-otp`, {
        method: "POST",
        headers: {"Content-Type":"application/json","apikey":SUPABASE_KEY},
        body: JSON.stringify({freelancer_slug: freelancerSlug, client_slug: clientSlug, portal_token: portalToken, code}),
      });
      const data = await r.json().catch(()=>({}));
      if(data?.ok && data.session_token){
        onVerified({clientId: data.client_id, sessionToken: data.session_token, expiresAt: data.expires_at});
        return;
      }
      const map = {
        invalid_code:      "That code wasn't right. Try again.",
        expired:           "Your code expired. Tap Resend to get a new one.",
        too_many_attempts: "Too many attempts. Please request a new code.",
        not_found:         "We couldn't find your portal. Please check the link.",
      };
      setError(map[data?.error] || "Verification failed. Please try again.");
      setDigits(Array(NUM).fill("")); focus(0);
    } catch {
      setError("Network error. Please check your connection.");
      setDigits(Array(NUM).fill("")); focus(0);
    }
    setVerifying(false);
  };

  const resend = async () => {
    if(resending || timer > 0) return;
    setResending(true);
    await sendCode();
    setResending(false);
  };

  return (
    // flex:1 + width:100% so the gate fills the desktop flex-row layout
    // (.app.desktop is flex-direction:row, so without flex:1 we shrink-wrap
    // to the card width and leave the rest of the viewport white).
    <div style={{flex:1,width:"100%",minHeight:"100dvh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"var(--g)",padding:"24px 20px",boxSizing:"border-box"}}>
      <img src={BANNER_B64} alt="Lancer" style={{height:36,objectFit:"contain",mixBlendMode:"screen",marginBottom:28}}/>
      <div style={{maxWidth:420,width:"100%",background:"var(--card)",borderRadius:16,padding:"32px 28px",border:"1px solid var(--bo)",boxShadow:"0 12px 40px rgba(0,0,0,.28)"}}>
        <div style={{textAlign:"center",marginBottom:22}}>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,fontWeight:600,color:"var(--ink)",marginBottom:6}}>
            {gateData?.client_name || "Welcome"}
          </div>
          <div style={{fontSize:12,color:"var(--mu)",textTransform:"uppercase",letterSpacing:".08em",fontWeight:600}}>
            Verify it's you
          </div>
          {gateData?.business_name && (
            <div style={{fontSize:11,color:"var(--mu)",marginTop:8,opacity:.75}}>
              from {gateData.business_name}
            </div>
          )}
        </div>

        {stage === "send" && (
          <>
            <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.55,marginBottom:20,textAlign:"center"}}>
              For your security, we'll send an 8-digit verification code to:
              <div style={{marginTop:10,fontSize:15,fontWeight:600,color:"var(--gm)",fontFamily:"'Inter',sans-serif"}}>{maskedEmail}</div>
            </div>
            {error && <div style={{fontSize:12,color:"#E5484D",marginBottom:12,textAlign:"center"}}>{error}</div>}
            <button
              onClick={sendCode} disabled={sending}
              style={{width:"100%",padding:"14px 20px",background:"var(--gm)",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:sending?"default":"pointer",opacity:sending?.6:1}}>
              {sending ? "Sending…" : "Send code"}
            </button>
            <div style={{fontSize:11,color:"var(--mu)",textAlign:"center",marginTop:14,lineHeight:1.5}}>
              Don't recognise this email? Contact your freelancer to update it.
            </div>
          </>
        )}

        {stage === "verify" && (
          <>
            <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.55,marginBottom:18,textAlign:"center"}}>
              Code sent to <strong style={{color:"var(--gm)"}}>{maskedEmail}</strong>.
              <div style={{fontSize:11,color:"var(--mu)",marginTop:4}}>Check your spam folder if it's not in your inbox.</div>
            </div>
            <div style={{display:"flex",gap:5,justifyContent:"center",marginBottom:14,flexWrap:"nowrap",width:"100%"}} onPaste={onPaste}>
              {digits.map((d,i) => (
                <input key={i} type="tel" inputMode="numeric" maxLength={1} value={d} autoFocus={i===0}
                  ref={el=>{boxRefs.current[i]=el;}} onChange={e=>onChange(i,e.target.value)} onKeyDown={e=>onKeyDown(i,e)}
                  style={{flex:"1 1 0",minWidth:0,maxWidth:40,height:46,textAlign:"center",fontSize:18,fontWeight:600,border:"1px solid var(--bo)",borderRadius:8,background:"var(--bg)",color:"var(--ink)",outline:"none",padding:0,boxSizing:"border-box"}}/>
              ))}
            </div>
            {error && <div style={{fontSize:12,color:"#E5484D",marginBottom:10,textAlign:"center"}}>{error}</div>}
            {verifying && <div style={{fontSize:12,color:"var(--mu)",textAlign:"center",marginBottom:8}}>Verifying…</div>}
            <div style={{textAlign:"center",fontSize:12,color:"var(--mu)"}}>
              {timer > 0
                ? `Resend in 0:${String(timer).padStart(2,"0")}`
                : (
                  <span onClick={resend} style={{color:"var(--gm)",cursor:"pointer",fontWeight:600,textDecoration:"underline"}}>
                    {resending ? "Sending…" : "Resend code"}
                  </span>
                )
              }
            </div>
          </>
        )}

        {stage === "no_email" && (
          <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.6,textAlign:"center"}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>We can't verify it's you yet.</div>
            <div style={{color:"var(--mu)"}}>
              Your freelancer hasn't recorded an email for you. Please ask them to add it before sharing this portal link.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── ROOT ── */
const NAVS=[
  {id:"clients",icon:I.clients,label:"Clients"},
  {id:"contracts",icon:I.contracts,label:"Contracts"},
  {id:"home",icon:I.home,label:"Home"},
  {id:"calendar",icon:I.calendar,label:"Calendar"},
  {id:"inbox",icon:I.inbox,label:"Inbox"},
];

export default function App(){
  // Start immediately in portal-loading if the URL looks like a portal link
  // This skips the splash screen for clients accessing via their unique link
  // Also detect stripe_connect return to avoid flashing the login screen
  const [sc,setSc]=useState(()=>{
    const parts = window.location.pathname.split("/").filter(Boolean);
    const isPortal = parts.length >= 2 &&
      parts.every(p => /^[a-z0-9-]+$/i.test(p)) &&
      !["api","auth","login","signup","dashboard","settings","app"].includes(parts[0]);
    if (isPortal) return "portal-loading";
    // If returning from Stripe connect with a stored session, show a loading state instead of login
    const hasStripeReturn = new URLSearchParams(window.location.search).get("stripe_connect") === "return";
    const hasSession = !!localStorage.getItem("lancer_at");
    if (hasStripeReturn && hasSession) return "portal-loading"; // reuse loading state to avoid login flash
    return "login";
  });
  const [pendingPw,setPendingPw]=useState("");
  const [pendingName,setPendingName]=useState("");
  const [email,setEmail]=useState("");
  const [acct,setAcct]=useState(null);
  const [nav,setNav]=useState("home");
  const [modal,setModal]=useState(null);
  const [portalData,setPortalData]=useState(null);
  // Pre-auth gate data (business name, masked email, brand) shown on the OTP
  // screen before a session token is minted. Also acts as the "we know who
  // they are" anchor so re-fetches after verify use the same slugs+token.
  const [portalGateData,setPortalGateData]=useState(null);
  const [portalSessionToken,setPortalSessionToken]=useState(null);
  const [sbLoading,setSbLoading]=useState(false);
  const [sbError,setSbError]=useState("");
  const isDesktop=useIsDesktop();

  // ── Admin state ──
  const [adminNav,setAdminNav]=useState("admin-home");
  const [adminUsers,setAdminUsers]=useState([]);
  const [adminMetrics,setAdminMetrics]=useState(null);
  const [adminAuditLog,setAdminAuditLog]=useState([]);
  const [adminActivity,setAdminActivity]=useState([]);
  const [adminSelectedUser,setAdminSelectedUser]=useState(null);
  const [adminConfirm,setAdminConfirm]=useState(null);

  const loadAdminData=async()=>{
    // Ensure we have a valid access token before calling admin RPCs
    if(!_accessToken){
      try{ const stored=localStorage.getItem("lancer_at"); if(stored) _accessToken=stored; }catch(e){}
    }
    if(!_refreshToken){
      try{ const stored=localStorage.getItem("lancer_rt"); if(stored) _refreshToken=stored; }catch(e){}
    }
    // Always refresh token before admin RPCs to ensure it's not expired
    if(_refreshToken){
      try{
        const rr=await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`,{
          method:"POST",headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
          body:JSON.stringify({refresh_token:_refreshToken}),
        });
        if(rr.ok){const rd=await rr.json();if(rd.access_token){_accessToken=rd.access_token;_refreshToken=rd.refresh_token||_refreshToken;_saveTokens();}}
      }catch(e){console.error("[Admin] token refresh failed:",e);}
    }
    if(!_accessToken){console.warn("[Admin] No access token available — RPCs will fail"); return;}
    const [u,m,a,p]=await Promise.all([
      dbAdminGetAllUsers().catch(e=>{console.error("[Admin] admin_get_all_users error:",e);return{data:[],error:{message:String(e)}};}),
      dbAdminGetMetrics().catch(e=>{console.error("[Admin] admin_get_metrics error:",e);return{data:null,error:{message:String(e)}};}),
      dbAdminGetAuditLog().catch(e=>{console.error("[Admin] admin_get_audit_log error:",e);return{data:[],error:{message:String(e)}};}),
      dbAdminGetPlatformActivity().catch(e=>{console.error("[Admin] admin_get_platform_activity error:",e);return{data:[],error:{message:String(e)}};}),
    ]);
    // Log raw responses for debugging
    if(u.error) console.error("[Admin] users RPC failed:",u.error,u.data);
    if(m.error) console.error("[Admin] metrics RPC failed:",m.error,m.data);
    if(a.error) console.error("[Admin] audit RPC failed:",a.error,a.data);
    if(p.error) console.error("[Admin] activity RPC failed:",p.error,p.data);
    // RPC jsonb returns: array for users/activity, object for metrics
    const users=Array.isArray(u.data)?u.data:(u.data&&!u.error?[u.data]:[]);
    const metrics=m.data&&typeof m.data==="object"&&!Array.isArray(m.data)&&!m.error?m.data:null;
    const audit=Array.isArray(a.data)?a.data:[];
    const activity=Array.isArray(p.data)?p.data:[];
    console.log("[Admin] loaded:",{users:users.length,metrics:!!metrics,audit:audit.length,activity:activity.length,hasToken:!!_accessToken});
    setAdminUsers(users);
    setAdminMetrics(metrics);
    setAdminAuditLog(audit);
    setAdminActivity(activity);
  };

  // ── Admin auto-refresh: immediate load + poll every 30s + refresh on tab focus ──
  useEffect(()=>{
    if(sc!=="admin") return;
    loadAdminData(); // Immediately fetch data when entering admin mode
    let iv=setInterval(()=>{ loadAdminData(); },30000);
    const onFocus=()=>{ loadAdminData(); };
    const onVisible=()=>{ if(document.visibilityState==="visible") loadAdminData(); };
    window.addEventListener("focus",onFocus);
    document.addEventListener("visibilitychange",onVisible);
    return ()=>{
      clearInterval(iv);
      window.removeEventListener("focus",onFocus);
      document.removeEventListener("visibilitychange",onVisible);
    };
  },[sc]);

  // ── Heartbeat: keep last_login_at fresh every 5 min so "Active" count is accurate ──
  useEffect(()=>{
    if(!acct?.id||(sc!=="app"&&sc!=="admin")) return;
    const beat=()=>{ try{ dbUpdateProfile(acct.id,{last_login_at:new Date().toISOString()}); }catch(e){} };
    const iv=setInterval(beat,5*60*1000);
    return ()=>clearInterval(iv);
  },[acct?.id,sc]);

  // ── Proactive token refresh: keep session alive, refresh before expiry ──
  useEffect(()=>{
    if(!acct?.id||(sc!=="app"&&sc!=="admin")) return;
    // Decode JWT exp claim to know when to refresh
    const getTokenExp=()=>{
      try{
        if(!_accessToken) return 0;
        const payload=JSON.parse(atob(_accessToken.split(".")[1]));
        return (payload.exp||0)*1000; // ms
      }catch(e){return 0;}
    };
    const refresh=()=>{
      if(!_refreshToken) return;
      // Only refresh if token expires within 5 minutes (or already expired)
      const exp=getTokenExp();
      if(exp && exp-Date.now()>5*60*1000) return; // still fresh, skip
      _refreshSession().catch(()=>{});
    };
    // Check every 2 minutes — will only actually refresh when token is near expiry
    refresh(); // refresh immediately on mount if needed
    const iv=setInterval(refresh,2*60*1000);
    // Also refresh when user returns to the tab (may have been asleep for hours)
    const onVisible=()=>{ if(document.visibilityState==="visible") _refreshSession().catch(()=>{}); };
    document.addEventListener("visibilitychange",onVisible);
    return ()=>{ clearInterval(iv); document.removeEventListener("visibilitychange",onVisible); };
  },[acct?.id,sc]);

  // ── On mount: check URL path for portal deep link, then check session ──
  // Apply dark mode whenever acct.darkMode changes
  useEffect(()=>{
    if(acct?.darkMode) document.body.classList.add("dark");
    else document.body.classList.remove("dark");
  },[acct?.darkMode]);

  // Phase 5 — Set browser tab title based on context.
  // - On client portal pages: use the freelancer's business name when present
  //   (so the client's browser tab reads "Henderson Studio" not "Lancer").
  // - On the in-app dashboard: keep "Lancer" (the freelancer's branding tool).
  useEffect(()=>{
    const portalBiz = portalData?.freelancer?.business_name;
    if (sc === "portal-loading" || sc === "portal-url") {
      document.title = portalBiz || "Lancer";
    } else {
      document.title = "Lancer";
    }
  },[sc, portalData?.freelancer?.business_name]);

  // Phase 5 — Apply Studio white-label theme overrides as CSS variables on
  // portal routes only. Removes the override style tag whenever the portal
  // unmounts so it never bleeds into the in-app dashboard.
  //
  // Grace-period fix: compare the freelancer's *effective* plan to "studio",
  // not the raw billing_plan. After cancellation, profiles.billing_plan flips
  // to "free" but cancelled_plan + plan_expiry_date carry the Studio features
  // through to period end — the portal theme must follow that same rule so it
  // doesn't vanish the moment the owner schedules a cancellation.
  useEffect(()=>{
    const isPortal = sc === "portal-url";
    const f = portalData?.freelancer || null;
    const theme = f?.studio_theme;
    const styleEl = document.getElementById("studio-theme-overrides");
    // Remove any existing override on every render — we re-add only if applicable.
    if (styleEl) styleEl.remove();
    if (!isPortal || !f || !theme) return;
    // Mirror effectivePlan(): grace period grants the cancelled plan until
    // plan_expiry_date passes. Compare as ISO date strings for consistency
    // with how the column is stored.
    const today = new Date();
    const todayISO = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
    const inGrace = !!(f.cancelled_plan && f.plan_expiry_date && String(f.plan_expiry_date) >= todayISO);
    const effective = inGrace ? f.cancelled_plan : f.billing_plan;
    if (effective !== "studio") return;
    const css = `:root, body.dark {
      ${theme.primary ? `--g: ${theme.primary} !important;` : ""}
      ${theme.accent  ? `--t: ${theme.accent}  !important;` : ""}
      ${theme.accent  ? `--gm: ${theme.accent} !important;` : ""}
      ${theme.surface ? `--card: ${theme.surface} !important;` : ""}
      ${theme.ink     ? `--ink: ${theme.ink}  !important;` : ""}
    }`;
    const el = document.createElement("style");
    el.id = "studio-theme-overrides";
    el.textContent = css;
    document.head.appendChild(el);
  },[sc, portalData?.freelancer?.billing_plan, portalData?.freelancer?.cancelled_plan, portalData?.freelancer?.plan_expiry_date, portalData?.freelancer?.studio_theme]);

  useEffect(()=>{
    // If password recovery mode, skip session restore — let Login handle the password reset flow
    // Uses module-level flag set synchronously at load, immune to effect ordering race
    if(_recoveryMode){
      // Clear any persisted session so auto-login can't happen
      try{localStorage.removeItem("lancer_at");localStorage.removeItem("lancer_rt");}catch(e){}
      setSc("login");
      return;
    }
    const tryAutoLogin=async(user)=>{
      const loaded=await loadAcctFromDB(user);
      if(!loaded){ setSc("login"); return; }
      // If 2FA is enabled, only allow auto-login if 2FA was already verified this session
      if(loaded.twoFA){
        try{ if(!localStorage.getItem("lancer_2fa_ok")){ _accessToken=null;_refreshToken=null;_saveTokens(); setSc("login"); return; } }catch(e){}
      }
      // Stamp last_login_at on auto-login
      try{ dbUpdateProfile(loaded.id,{last_login_at:new Date().toISOString()}); }catch(e){}
      setAcct(loaded);
      // Safety net: if onboarding's upsert failed previously (e.g. stale token
      // immediately after signup/verify), the profile row will exist (trigger-
      // created) but country will be empty. Route back to onboarding so the
      // user re-picks rather than silently defaulting to USD/US in the UI.
      if(!loaded.country){ setSc("onboard"); return; }
      if(isAdmin(loaded)){ document.body.classList.add("admin"); setSc("admin"); loadAdminData(); }
      else{ setSc("app"); }
    };
    // OTP gate: every portal load goes through portal_gate_lookup first to
    // resolve the client_id and pull the freelancer brand. If we already
    // have a valid session in localStorage for that client_id we try
    // get_portal_data_v2 with it; otherwise we route to the OTP gate.
    // Returns "loaded" | "gate" | "not_found" | "error" so the caller can
    // decide what to do for unrecognised URLs (custom domain → login,
    // canonical → maybe restore freelancer session).
    const initiatePortalLoad = async (freelancerSlug, clientSlug, urlToken) => {
      const gateRes = await fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_gate_lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
        body: JSON.stringify({ p_freelancer_slug: freelancerSlug, p_client_slug: clientSlug, p_portal_token: urlToken||null }),
      }).catch(()=>null);
      const gate = gateRes ? await gateRes.json().catch(()=>null) : null;
      if(!gate || gate.ok === false){
        if(gate?.error === "no_email"){
          setPortalGateData({ ...gate, freelancer_slug: freelancerSlug, client_slug: clientSlug, portal_token: urlToken||null });
          setSc("portal-gate");
          return "gate";
        }
        return "not_found";
      }
      setPortalGateData({ ...gate, freelancer_slug: freelancerSlug, client_slug: clientSlug, portal_token: urlToken||null });

      const session = _getPortalSession(gate.client_id);
      if(session?.token){
        const dataRes = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_portal_data_v2`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
          body: JSON.stringify({ freelancer_slug: freelancerSlug, client_slug: clientSlug, portal_token: urlToken||null, portal_session: session.token }),
        }).catch(()=>null);
        const data = dataRes ? await dataRes.json().catch(()=>null) : null;
        if(data && !data.error && data.client && data.freelancer){
          setPortalSessionToken(session.token);
          setPortalData(data);
          setSc("portal-url");
          return "loaded";
        }
        // Session no longer valid — purge and fall through to gate.
        _clearPortalSession(gate.client_id);
      }
      setPortalSessionToken(null);
      setSc("portal-gate");
      return "gate";
    };

    // Phase 6 — Custom domain detection. If the Host header is NOT the
    // canonical lancer-app.com (or a Vercel preview), look up the workspace
    // via the custom_domain column and treat path as /<client_slug>?t=TOKEN
    // (the freelancer slug is implied by the host).
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const currentHost = window.location.host;
    const onCustomDomain = !isLancerCanonicalHost(currentHost);
    if(onCustomDomain){
      const clientSlug = pathParts[0] || "";
      const urlToken = new URLSearchParams(window.location.search).get("t");
      setSc("portal-loading");
      dbGetWorkspaceByCustomDomain(currentHost).then(ws => {
        if(!ws?.freelancer_slug || !clientSlug){
          // Domain is set up but the path is invalid OR domain isn't claimed yet.
          setTimeout(()=>setSc("login"), 800);
          return;
        }
        return initiatePortalLoad(ws.freelancer_slug, clientSlug, urlToken).then(res => {
          if(res === "not_found" || res === "error") setTimeout(()=>setSc("login"), 800);
        });
      }).catch(() => setTimeout(()=>setSc("login"), 800));
      return;
    }
    // Canonical-domain portal link: /freelancerSlug/clientSlug?t=TOKEN
    const looksLikePortal = pathParts.length >= 2 &&
      pathParts.every(p => /^[a-z0-9-]+$/i.test(p)) && // only slug-like segments
      !["api","auth","login","signup","dashboard","settings","app"].includes(pathParts[0]);
    if(looksLikePortal){
      const [freelancerSlug, clientSlug] = pathParts;
      const urlToken = new URLSearchParams(window.location.search).get("t");
      setSc("portal-loading");
      initiatePortalLoad(freelancerSlug, clientSlug, urlToken).then(res => {
        if(res === "not_found" || res === "error"){
          // Not a valid portal URL — fall back to freelancer auto-login.
          sb.auth.getUser().then(({data:{user}})=>{
            if(user) tryAutoLogin(user);
            else setTimeout(()=>setSc("login"),800);
          });
        }
      }).catch(()=>setTimeout(()=>setSc("login"),800));
      return;
    }
    // Try to restore session — retry refresh if first attempt fails but we have a refresh token
    const attemptRestore=async(retries)=>{
      const {data:{session}}=await dbGetSession();
      if(session?.user){
        await tryAutoLogin(session.user);
        return;
      } else if(session?.access_token){
        const {data:{user}}=await sb.auth.getUser();
        if(user){ await tryAutoLogin(user); return; }
      }
      // Session failed — retry if we still have a refresh token in localStorage
      if(retries>0){
        try{const rt=localStorage.getItem("lancer_rt");if(rt){_refreshToken=rt;}}catch(e){}
        if(_refreshToken){
          console.log("[Auth] Session restore failed, retrying... ("+retries+" left)");
          await new Promise(r=>setTimeout(r,1500));
          await _refreshSession();
          return attemptRestore(retries-1);
        }
      }
      setTimeout(()=>setSc("login"),400);
    };
    attemptRestore(2);
    // Listen for auth state changes
    const { data:{ subscription } } = sb.auth.onAuthStateChange((event,session)=>{
      if(event==="SIGNED_OUT"){ setAcct(null); setSc("login"); }
    });
    return ()=>subscription.unsubscribe();
  },[]);

  const loadAcctFromDB = async (user) => {
    try {
    const safe = (p) => Promise.resolve(p).catch(()=>({data:null}));

    // Workspace switcher: load all the user's memberships, pick the active
    // one based on localStorage > owned > most recent. Stash the full list
    // on acct so the profile menu can render the switcher.
    const { data: workspaces } = await safe(dbListMyWorkspaces());
    const ws = pickActiveWorkspace(workspaces);
    if (!ws) {
      console.error("[loadAcctFromDB] User has no workspace — backfill may have missed this profile.");
      return null;
    }
    // Persist the chosen workspace so subsequent loads default to it even
    // if the user's owned-workspace heuristic would have picked differently.
    storeActiveWorkspace(ws.workspace_id);
    const workspaceId = ws.workspace_id;

    // Fetch profile + business data in parallel, all workspace-scoped.
    // workspaceMembers powers the "Sent by [Name]" labels in the inbox so the
    // viewer can tell which teammate sent each freelancer-side message.
    const [
      { data: profile },
      { data: rawClients },
      { data: contracts },
      { data: rawThreads },
      { data: rawCalEntries },
      { data: rawAuditLog },
      { data: rawPayments },
      { data: rawRefunds },
      { data: rawMembers },
    ] = await Promise.all([
      safe(dbGetProfile(user.id)),
      safe(dbGetClients(workspaceId)),
      safe(dbGetContracts(workspaceId)),
      safe(dbGetThreads(workspaceId)),
      safe(dbGetCalEntries(workspaceId)),
      safe(dbGetAuditLogByWorkspace(workspaceId)),
      safe(dbGetAllPayments()),
      safe(dbGetAllRefunds()),
      safe(dbGetWorkspaceMembers(workspaceId)),
    ]);

    // Build a user_id -> display name map for resolving message sender names.
    // Falls back to email-prefix when a member's profile has no name. Includes
    // the workspace owner (their row is in the members list with role='owner').
    const memberNameById = {};
    (Array.isArray(rawMembers) ? rawMembers : []).forEach(m => {
      if (m.user_id) memberNameById[m.user_id] = m.user_name || (m.user_email ? String(m.user_email).split("@")[0] : "Teammate");
    });

    if (!profile) return null;

    const clientIds = (rawClients || []).map(cl => cl.id);

    // Load timelines and intakes in parallel (needs client IDs)
    let allTimelines = [], allIntakes = [];
    if (clientIds.length > 0) {
      const idList = clientIds.join(",");
      const authHdr = { apikey: SUPABASE_KEY, Authorization: "Bearer " + (_accessToken || SUPABASE_KEY) };
      const [tlRes, intRes] = await Promise.all([
        fetch(SUPABASE_URL + "/rest/v1/timeline_steps?client_id=in.(" + idList + ")&order=position.asc", { headers: authHdr }),
        fetch(SUPABASE_URL + "/rest/v1/client_intake?client_id=in.(" + idList + ")", { headers: authHdr }),
      ]);
      const tlData = await tlRes.json();
      const intData = await intRes.json();
      allTimelines = Array.isArray(tlData) ? tlData : [];
      allIntakes = Array.isArray(intData) ? intData : [];
    }

    // Build lookup maps
    const timelineMap = {};
    (allTimelines || []).forEach(t => {
      if (!timelineMap[t.client_id]) timelineMap[t.client_id] = [];
      timelineMap[t.client_id].push(t);
    });
    (Object.values(timelineMap)).forEach(arr => arr.sort((a,b) => a.position - b.position));

    const intakeMap = {};
    (allIntakes || []).forEach(i => { intakeMap[i.client_id] = i; });

    // Shape client rows into app format
    const clients = (rawClients || []).map(c => {
      const tl = (timelineMap[c.id] || []).map(s => ({
        l: s.label, d: s.step_date || "", s: s.status || "pend"
      }));
      const intake = intakeMap[c.id];
      const pct = calcClientPct(tl, c.status);
      const step = clientStepLabel(c.status, tl);
      return {
        id: c.id,
        name: c.name,
        ini: c.ini || c.name.slice(0,2).toUpperCase(),
        col: c.col || "#1C4D44",
        email: c.email || "",
        phone: c.phone || "",
        portalSlug: c.portal_slug || "",
        portalToken: c.portal_token || "",
        vert: c.vert || "",
        verts: c.verts || [],
        type: c.type || "",
        amt: c.amt || "TBD",
        status: c.status || "inquiry",
        archived: c.archived || false,
        preArchiveStatus: c.pre_archive_status || null,
        feat: c.feat || false,
        tags: c.tags || [],
        step,
        pct,
        tl,
        intake: intake?.answers || {},
        intakeSubmitted: c.intake_submitted || false,
        intakeSent: c.intake_sent || false,
        depositPct: c.deposit_pct || 50,
        depositAmt: c.deposit_amt || 0,
        balanceAmt: c.balance_amt || 0,
        depositPaid: c.deposit_paid || false,
        balancePaid: c.balance_paid || false,
        depositPaidDate: c.deposit_paid_date || null,
        balancePaidDate: c.balance_paid_date || null,
        aiContract: c.ai_contract || null,
        contractSentToPortal: c.contract_sent_to_portal || false,
        contractStage: c.contract_stage || null,
        contractComments: c.contract_comments || null,
        contractPdfUrl: c.contract_pdf_url || null,
        freelancerNotes: c.freelancer_notes || "",
        surpriseNotes: c.surprise_notes || "",
        portalPhoto: c.portal_photo || null,
        projectDate: c.project_date || "",
        reminderSentAt: c.reminder_sent_at || null,
        questionnaireQuestions: c.questionnaire_questions || [],
        cancellationRequested: c.cancellation_requested || false,
        cancelledAt: c.cancelled_at || null,
        refunds: c.refunds || [],
        invoices: c.invoices || [],
        archiveReason: c.archive_reason || null,
        stripeCustomerId: c.stripe_customer_id || null,
        // Phase 7 — lead seat for per-seat analytics aggregation.
        leadSeatId: c.lead_seat_id || null,
      };
    });

    // Shape thread rows — use embedded messages from dbGetThreads (no N+1 queries)
    const threads = (rawThreads || []).map(t => {
      const msgs = (t.messages || []).sort((a,b) => new Date(a.sent_at) - new Date(b.sent_at));
      const client = clients.find(c => c.id === t.client_id);
      const lastMsg = msgs.slice(-1)[0];
      return {
        id: t.id,
        clientId: t.client_id,
        name: client?.name || "Client",
        ini: client?.ini || "??",
        col: client?.col || "#1C4D44",
        unread: t.unread || 0,
        pre: lastMsg?.body || "No messages yet",
        time: lastMsg ? new Date(lastMsg.sent_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}) : "",
        msgs: msgs.map(m => ({
          id: m.id,
          f: m.from_role === "freelancer" ? "me" : "them",
          t: m.body,
          ts: new Date(m.sent_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),
          // Resolved name of the teammate who sent this message. NULL for client
          // messages; for freelancer messages this lets the InboxTab render
          // "Sent by [Name]" so teammates know which colleague replied.
          fromUserId: m.from_user_id || null,
          sentBy: (m.from_role === "freelancer" && m.from_user_id) ? (memberNameById[m.from_user_id] || null) : null,
        })),
      };
    });

    // Shape contract rows into app format (DB columns → app properties)
    const shapedContracts = (contracts || []).map(c => ({
      id: c.id,
      workspace_id: c.workspace_id,
      freelancer_id: c.freelancer_id,
      client_id: c.client_id,
      name: c.client_name || "",
      type: c.type || "",
      val: c.value || "",
      date: c.contract_date || "",
      vert: c.vert || "",
      body: c.body || "",
      status: c.status || "draft",
      archived: c.archived || false,
      signed_at: c.signed_at || null,
      signed_by: c.signed_by || null,
      sent_at: c.sent_at || null,
      archived_at: c.archived_at || null,
      version: c.version || 1,
      terms_snapshot: c.terms_snapshot || null,
      created_at: c.created_at,
      updated_at: c.updated_at,
    }));

    // Shape calendar entries
    const calEntries = (rawCalEntries || []).map(e => ({
      id: e.id,
      clientId: e.client_id,
      subject: e.subject || "",
      date: e.entry_date || "",
      time: e.entry_time || "",
      notes: e.notes || "",
      createdAt: e.created_at,
    }));

    // Shape payments (snake_case → camelCase) so Home/Metrix can aggregate in-hold vs paid-out
    const payments = (rawPayments || []).map(p => ({
      id: p.id,
      clientId: p.client_id,
      type: p.type || "",
      amount: Number(p.amount || 0),
      currency: p.currency || "",
      status: p.status || "",
      invoiceId: p.invoice_id || null,
      refundId: p.refund_id || null,
      stripePaymentIntentId: p.stripe_payment_intent_id || null,
      stripeCheckoutSessionId: p.stripe_checkout_session_id || null,
      stripeAccountId: p.stripe_account_id || null,
      stripePayoutId: p.stripe_payout_id || null,
      paidOutAt: p.paid_out_at || null,
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    }));

    // Shape refunds so the refund picker can compute per-payment remaining headroom for partial refunds.
    const refundsFlat = (rawRefunds || []).map(r => ({
      id: r.id,
      clientId: r.client_id,
      paymentId: r.payment_id || null,
      amount: Number(r.amount || 0),
      reason: r.reason || "",
      stripeRefundId: r.stripe_refund_id || null,
      stripeStatus: r.stripe_status || null,
      createdAt: r.created_at,
    }));

    const result = profileToAcct(profile, clients, shapedContracts, threads);
    result.calEntries = calEntries;
    result.payments = payments;
    result.refundsFlat = refundsFlat;

    // Slice B-2: workspace context. Available everywhere `acct` flows.
    // Phase 3 will surface role + financialVisibility in the UI.
    result.workspaceId = ws.workspace_id;
    result.workspaceName = ws.workspace_name;
    result.workspaceMemberId = ws.member_id;
    result.workspaceRole = ws.role;                          // 'owner' | 'member' | 'guest'
    result.financialVisibility = ws.financial_visibility;    // bool, hides £ when false
    // Workspace-level plan is the source of truth post-Phase 2; for now we
    // mirror profile.billing_plan for backwards compat, preferring workspace.
    result.workspacePlan = ws.plan;
    // Slice D fix: trial countdown + workspace cancellation state for Billing card.
    result.workspaceTrialEndsAt = ws.trial_ends_at || null;
    result.workspacePlanExpiryDate = ws.plan_expiry_date || null;
    result.workspaceCancelledPlan = ws.cancelled_plan || null;
    // Phase 11: surface stripe subscription status for the payment-failure banner.
    // Values from Stripe: active, trialing, past_due, canceled, unpaid, incomplete, etc.
    result.subscriptionStatus = ws.subscription_status || null;
    // Stripe is the source of truth for the renewal date — the previous
    // billing_start_date + N months math broke on month-end dates (May 31 →
    // setMonth() rolls to Jul 1). When this is null (e.g. account never had
    // a Stripe sub), the JSX falls back to the date math.
    result.subscriptionCurrentPeriodEnd = ws.subscription_current_period_end || null;
    result.subscriptionCancelAtPeriodEnd = !!ws.subscription_cancel_at_period_end;
    // Phase 6: custom domain (Vercel-managed). Null if not configured.
    result.workspaceCustomDomain = ws.custom_domain || null;
    // Workspace switcher: full list of workspaces the user belongs to.
    result.workspaces = Array.isArray(workspaces) ? workspaces : [];
    // Workspace members (full members + guests, with role + names) — used
    // by InboxTab to resolve "Sent by [Name]" labels without an extra fetch.
    result.workspaceMembers = Array.isArray(rawMembers) ? rawMembers : [];

    // Load Stripe Connect account status
    try {
      const { data: stripeAcct } = await dbGetStripeAccount(workspaceId);
      if (stripeAcct) {
        result.stripeConnect = {
          accountId: stripeAcct.stripe_account_id,
          chargesEnabled: stripeAcct.charges_enabled || false,
          payoutsEnabled: stripeAcct.payouts_enabled || false,
          detailsSubmitted: stripeAcct.details_submitted || false,
        };
      }
    } catch(e) { /* no Stripe account yet — that's fine */ }

    result.auditLog = (rawAuditLog || []).map(e => ({
      id: e.id,
      clientId: e.client_id,
      contractId: e.contract_id,
      action: e.action,
      detail: e.detail || "",
      actor: e.actor || "freelancer",
      createdAt: e.created_at,
    }));
    return result;
    } catch(e) { console.error("loadAcctFromDB error:", e); return null; }
  };

  const signOut=()=>{
    // Clear local state immediately — don't wait for network
    setEmail(""); setAcct(null); setNav("home"); setModal(null);
    setAdminNav("admin-home"); setAdminUsers([]); setAdminMetrics(null); setAdminAuditLog([]); setAdminActivity([]); setAdminSelectedUser(null); setAdminConfirm(null);
    document.body.classList.remove("admin");
    setSc("login");
    // Clear 2FA verified flag so next login requires 2FA again
    try{localStorage.removeItem("lancer_2fa_ok");}catch(e){}
    // Clear the stored active-workspace so the next user on this browser
    // doesn't inherit the previous user's choice.
    storeActiveWorkspace(null);
    // Fire and forget — best-effort server-side sign out
    dbSignOut().catch(()=>{});
  };

  const handleDeleteAccount=async (userEmail)=>{
    // Only sign-out / navigate away if the RPC actually succeeds. The previous
    // version swallowed the error and ALWAYS routed to /login, which made a
    // failed delete (e.g. FK violation on workspaces) look like a successful
    // delete to the user even though their auth.users row still existed.
    try {
      await dbDeleteAccount();
    } catch(e) {
      console.error("Delete account error:", e);
      const msg=(e&&(e.message||e.error_description))||"";
      try{ alert("Couldn't delete your account: "+(msg||"unknown error")+"\n\nIf this keeps happening, contact support."); }catch(_){}
      return;
    }
    _accessToken=null; _refreshToken=null; _saveTokens();
    try{localStorage.removeItem("lancer_2fa_ok");}catch(e){}
    setEmail(""); setAcct(null); setNav("home"); setModal(null);
    setSc("login");
  };

  // ── Pull-to-refresh: reload everything from DB ──
  const handleAddCalEntry=async(entry)=>{
    const tempId="cal_"+Date.now();
    const newEntry={id:tempId,...entry,createdAt:new Date().toISOString()};
    setAcct(prev=>({...prev,calEntries:[...(prev.calEntries||[]),newEntry]}));
    const {data}=await dbInsertCalEntry({workspace_id:acct.workspaceId,freelancer_id:acct.id,client_id:entry.clientId||null,subject:entry.subject,entry_date:entry.date,entry_time:entry.time||null,notes:entry.notes||null});
    if(data?.id){
      setAcct(prev=>({...prev,calEntries:(prev.calEntries||[]).map(e=>e.id===tempId?{...e,id:data.id}:e)}));
    }
  };

  const handleDeleteCalEntry=async(entryId)=>{
    setAcct(prev=>({...prev,calEntries:(prev.calEntries||[]).filter(e=>e.id!==entryId)}));
    await dbDeleteCalEntry(entryId);
  };

  // Merge a freshly-loaded acct from DB with local-only state (drafts, etc.) so
  // polling / realtime / pull-to-refresh don't wipe in-progress work.
  const mergeWithLocal=(loaded,prev)=>{
    if(!loaded||!prev) return loaded;
    const localDrafts=(prev.clients||[]).filter(c=>c._draft&&String(c.id).startsWith("draft_"));
    if(localDrafts.length===0) return loaded;
    // Avoid double-inserting if somehow the draft id already exists in loaded
    const loadedIds=new Set((loaded.clients||[]).map(c=>c.id));
    const keepDrafts=localDrafts.filter(c=>!loadedIds.has(c.id));
    return {...loaded,clients:[...(loaded.clients||[]),...keepDrafts]};
  };

  const handleRefresh=async()=>{
    const {data:{session}}=await dbGetSession();
    if(session?.user){
      const loaded=await loadAcctFromDB(session.user);
      if(loaded) setAcct(prev=>mergeWithLocal(loaded,prev));
    } else if(session?.access_token){
      // Fallback: get user separately
      const {data:{user}}=await sb.auth.getUser();
      if(user){
        const loaded=await loadAcctFromDB(user);
        if(loaded) setAcct(prev=>mergeWithLocal(loaded,prev));
      }
    }
  };

  // ── Realtime + polling: subscribe to DB changes and auto-refresh ──
  const realtimeRef=useRef(null);
  const realtimeDebounce=useRef(null);
  const pollTimerRef=useRef(null);
  useEffect(()=>{
    if(sc!=="app"||!acct){
      if(realtimeRef.current){ realtimeRef.current.close(); realtimeRef.current=null; }
      if(pollTimerRef.current){ clearInterval(pollTimerRef.current); pollTimerRef.current=null; }
      return;
    }
    if(realtimeRef.current) return;

    const doRefresh=async()=>{
      try{
        const {data:{user}}=await sb.auth.getUser();
        if(user){
          const loaded=await loadAcctFromDB(user);
          if(loaded) setAcct(prev=>mergeWithLocal(loaded,prev));
        }
      }catch(e){}
    };

    const debouncedRefresh=()=>{
      if(realtimeDebounce.current) clearTimeout(realtimeDebounce.current);
      realtimeDebounce.current=setTimeout(doRefresh,800);
    };

    // WebSocket realtime
    realtimeRef.current=createRealtimeSubscription(
      ["clients","client_intake","messages","timeline_steps","contracts","profiles"],
      debouncedRefresh
    );

    // Fallback polling every 5s (catches portal→freelancer changes that bypass Realtime)
    pollTimerRef.current=setInterval(doRefresh,5000);

    // Also refresh when user returns to the app/tab
    const onVisible=()=>{ if(document.visibilityState==="visible") doRefresh(); };
    document.addEventListener("visibilitychange",onVisible);

    return ()=>{
      if(realtimeRef.current){ realtimeRef.current.close(); realtimeRef.current=null; }
      if(realtimeDebounce.current) clearTimeout(realtimeDebounce.current);
      if(pollTimerRef.current){ clearInterval(pollTimerRef.current); pollTimerRef.current=null; }
      document.removeEventListener("visibilitychange",onVisible);
    };
  },[sc,acct?.id]);

  // ── Realtime + polling for portal view: refresh portal data on DB changes ──
  const portalRealtimeRef=useRef(null);
  const portalDebounceRef=useRef(null);
  const portalPollRef=useRef(null);
  useEffect(()=>{
    if(sc!=="portal-url"||!portalData){
      if(portalRealtimeRef.current){ portalRealtimeRef.current.close(); portalRealtimeRef.current=null; }
      if(portalPollRef.current){ clearInterval(portalPollRef.current); portalPollRef.current=null; }
      return;
    }

    const fetchPortal=()=>{
      const pathParts=window.location.pathname.split("/").filter(Boolean);
      if(pathParts.length<2) return;
      const urlToken=new URLSearchParams(window.location.search).get("t");
      const sessionTok = portalSessionToken || _getPortalSession(portalData?.client?.id)?.token || null;
      if(!sessionTok) return; // No session — bail; gate flow handles re-auth.
      // NOTE: do NOT append query params (e.g. `?_t=...`) to this RPC URL.
      // PostgREST treats extra query params as column filters and returns a
      // PGRST100 "failed to parse filter" error, which silently breaks the
      // poll (catch() swallows it, state never updates). POST requests are
      // not cached by browsers anyway; `cache: "no-store"` plus the
      // no-cache headers are sufficient to guarantee freshness.
      fetch(`${SUPABASE_URL}/rest/v1/rpc/get_portal_data_v2`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY,"Cache-Control":"no-cache","Pragma":"no-cache"},
        cache:"no-store",
        body:JSON.stringify({freelancer_slug:pathParts[0],client_slug:pathParts[1],portal_token:urlToken||null,portal_session:sessionTok})
      }).then(r=>r.json()).then(data=>{
        if(data&&data.client&&data.freelancer){ setPortalData(data); return; }
        if(data?.error==="unauthorized"){
          // Session expired or revoked mid-poll — kick back to gate.
          const cid=portalData?.client?.id;
          if(cid) _clearPortalSession(cid);
          setPortalSessionToken(null);
          setSc("portal-gate");
        }
      }).catch(()=>{});
    };

    const debouncedReload=()=>{
      if(portalDebounceRef.current) clearTimeout(portalDebounceRef.current);
      portalDebounceRef.current=setTimeout(fetchPortal,800);
    };

    // WebSocket realtime — only set up once per portal session
    // Includes profiles so branding/freelancer detail changes are caught in real time
    if(!portalRealtimeRef.current){
      portalRealtimeRef.current=createRealtimeSubscription(
        ["clients","client_intake","messages","timeline_steps","contracts","profiles","invoices","refunds"],
        debouncedReload
      );
    }

    // Fallback polling every 2s — needed because Realtime respects RLS and the
    // anon portal can't see freelancer messages via postgres_changes, so polling
    // is the primary path for freelancer→client messages. 2s keeps latency low
    // while realtime still catches client-side and profile/contract changes.
    if(portalPollRef.current) clearInterval(portalPollRef.current);
    portalPollRef.current=setInterval(fetchPortal,2000);

    // Immediate refresh when client returns to tab
    const onPortalVisible=()=>{ if(document.visibilityState==="visible") fetchPortal(); };
    document.addEventListener("visibilitychange",onPortalVisible);

    return ()=>{
      if(portalDebounceRef.current) clearTimeout(portalDebounceRef.current);
      if(portalPollRef.current){ clearInterval(portalPollRef.current); portalPollRef.current=null; }
      document.removeEventListener("visibilitychange",onPortalVisible);
      // Note: do NOT close portalRealtimeRef here — it's only closed on sc/portalData transition above
    };
  },[sc,portalData?.client?.id,portalSessionToken]);

  // ── helper to get current user id ──
  const getUid=async()=>{ const {data:{user}}=await sb.auth.getUser(); return user?.id||null; };
  const handleAcctUpdate=(fields)=>{
    setAcct(prev=>({...prev,...fields}));
    // Persist to Supabase — map app field names to DB column names
    // Use acct.id directly rather than fetching user each time
    const userId = acct?.id;
    const doSave = (userId) => {
      if(!userId) return;
      const dbFields={};
      if(fields.name!==undefined) dbFields.name=fields.name;
      if(fields.email!==undefined) dbFields.email=fields.email;
      if(fields.ini!==undefined) dbFields.ini=fields.ini;
      if(fields.vert!==undefined) dbFields.vert=fields.vert;
      if(fields.verts!==undefined) dbFields.verts=fields.verts;
      if(fields.role!==undefined) dbFields.role=fields.role;
      if(fields.customRole!==undefined) dbFields.custom_role=Array.isArray(fields.customRole)?JSON.stringify(fields.customRole):(fields.customRole||null);
      if(fields.profilePic!==undefined) dbFields.profile_pic=fields.profilePic;
      if(fields.tagline!==undefined) dbFields.tagline=fields.tagline;
      if(fields.businessName!==undefined) dbFields.business_name=fields.businessName;
      if(fields.footerText!==undefined) dbFields.footer_text=fields.footerText;
      if(fields.cancellationPolicy!==undefined) dbFields.cancellation_policy=fields.cancellationPolicy;
      if(fields.phone!==undefined) dbFields.phone=fields.phone;
      if(fields.twoFA!==undefined) dbFields.two_fa=fields.twoFA;
      if(fields.billingPlan!==undefined) dbFields.billing_plan=fields.billingPlan;
      // billing_start_date drives the "next bill date" calculation that the
      // cancel flow uses for the grace-period expiry. Without this mapping,
      // a fresh upgrade only lived in React state — the DB column stayed
      // NULL, and on the next reload the cancel flow fell through to
      // today's date for the expiry, so the UI showed "Cancels [today]"
      // instead of "Cancels [next billing date]".
      if(fields.billingStartDate!==undefined) dbFields.billing_start_date=fields.billingStartDate;
      if(fields.planExpiryDate!==undefined) dbFields.plan_expiry_date=fields.planExpiryDate;
      if(fields.cancelledPlan!==undefined) dbFields.cancelled_plan=fields.cancelledPlan;
      if(fields.scheduledPlan!==undefined) dbFields.scheduled_plan=fields.scheduledPlan;
      if(fields.payoutHoldDays!==undefined) dbFields.payout_hold_days=fields.payoutHoldDays;
      if(fields.darkMode!==undefined) dbFields.dark_mode=fields.darkMode;
      if(fields.questionnaireTemplate!==undefined) dbFields.questionnaire_template=fields.questionnaireTemplate;
      if(fields.questionnaireTemplates!==undefined) dbFields.questionnaire_templates=fields.questionnaireTemplates;
      if(fields.studioTheme!==undefined) dbFields.studio_theme=fields.studioTheme;
      if(Object.keys(dbFields).length>0) dbUpdateProfile(userId,dbFields);
    };
    if(userId) doSave(userId);
    else sb.auth.getUser().then(({data:{user}})=>{ if(user) doSave(user.id); });
  };

  const handleIntakeSubmit=(clientId,formData)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,intake:{...c.intake,...formData},intakeSubmitted:true,intakeSent:true}:c)}));
    if(!String(clientId).startsWith("tmp_")){
      dbUpsertIntake(clientId, formData, true);
      dbUpdateClient(clientId, {intake_submitted:true, intake_sent:true});
    }
  };

  const handleAddClient=(clientData)=>{
    // Remove draft client
    if(clientData._removeDraft){
      setAcct(prev=>({...prev,clients:prev.clients.filter(c=>c.id!==clientData.id)}));
      return;
    }
    // Draft client: create with draft status, no DB write
    if(clientData._draft){
      const draftId=clientData._draftId||("draft_"+Date.now());
      const ini2=clientData.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
      const cols2=["#1C4D44","#9CCBBB","#4A7C6F","#6B4F9E","#4A5568","#2A7C6F","#8B5E9E"];
      const col2=cols2[parseInt(draftId.replace(/\D/g,"").slice(-3)||0)%cols2.length];
      setAcct(prev=>({...prev,clients:[...prev.clients,{
        id:draftId, name:clientData.name, ini:ini2, col:col2,
        vert:clientData.vert||"", verts:clientData.verts||[],
        type:`${clientData.sessionType||"Draft"} · TBD`,
        status:"inquiry", _draft:true,
        email:clientData.email||"", phone:clientData.phone||"",
        amt:"TBD", tl:[], depositPct:50, depositAmt:0, balanceAmt:0,
        depositPaid:false, balancePaid:false, intake:{},
        // Defensive defaults so any code path that reads these fields on an
        // incomplete draft never throws (e.g. Home's Active Clients carousel
        // calls c.tags.map, which would crash the whole tree on undefined).
        tags:[], step:"", pct:0, feat:false, refunds:[], invoices:[],
      }]}));
      return draftId;
    }
    const cols=["#1C4D44","#9CCBBB","#4A7C6F","#6B4F9E","#4A5568","#2A7C6F","#8B5E9E"];
    const slug=clientData.name.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")
      + "-" + Math.random().toString(36).slice(2,6);
    const ini=clientData.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
    const rawAmt=parseFloat(clientData.amt||0);
    const depPct=rawAmt<=0?100:(clientData.depositPct||50);
    const depAmt=rawAmt<=0?0:(clientData._depAmt||round2(rawAmt*depPct/100));
    const balAmt=rawAmt<=0?0:(clientData._balAmt||round2(rawAmt*(100-depPct)/100));
    const sym=acct.currencySymbol||"$";
    const isFullUpfront=depPct>=100||balAmt<=0;
    const timeline=clientData._timeline||[
      {l:"Inquiry received",d:localToday(),s:"done"},
      {l:"Contract sent",d:"",s:"pend"},
      {l:isFullUpfront?`Full payment due — ${sym}${fmtMoney(depAmt)}`:`Deposit due — ${sym}${fmtMoney(depAmt)}`,d:"",s:"pend"},
      {l:clientData.sessionType||"Session",d:clientData.date||"",s:"pend"},
      {l:"Delivery",d:"",s:"pend"},
      isFullUpfront?null:{l:`Final balance — ${sym}${fmtMoney(balAmt)}`,d:"",s:"pend"},
    ].filter(Boolean);
    const tempId="tmp_"+Date.now();
    const col=cols[Math.abs(tempId.split("").reduce((a,c)=>a+c.charCodeAt(0),0))%cols.length];
    const pct=calcClientPct(timeline,"inquiry");
    const step=clientStepLabel("inquiry",timeline);
    const newClient={
      id:tempId, name:clientData.name, ini, col,
      vert:clientData.vert||"", verts:clientData.verts||[],
      type:`${clientData.sessionType} · ${clientData.date?fmtDate(clientData.date):"TBD"}`,
      step, pct, tags:[],
      amt:rawAmt?`${sym}${fmtMoney(rawAmt)}`:"TBD",
      status:"inquiry", feat:false,
      email:clientData.email||"", phone:clientData.phone||"",
      portalSlug:slug, portalToken:"",
      intake:{date:clientData.date||""}, intakeSubmitted:false, intakeSent:false,
      depositPct:depPct, depositAmt:depAmt, balanceAmt:balAmt,
      depositPaid:false, balancePaid:false, tl:timeline,
      projectDate:clientData.date||"",
      questionnaireQuestions:getDefaultQuestionnaire(clientData.vert||acct.vert),
    };
    setAcct(prev=>({...prev,clients:[...prev.clients,newClient]}));

    // ── Persist to Supabase and swap temp ID for real UUID ──
    const uid=acct.id;
    dbInsertClient({
      workspace_id: acct.workspaceId,
      lead_seat_id: acct.workspaceMemberId,
      freelancer_id: uid,
      name: clientData.name,
      ini, col,
      email: clientData.email||"",
      phone: clientData.phone||"",
      portal_slug: slug,
      vert: clientData.vert||"",
      verts: clientData.verts||[],
      session_type: clientData.sessionType||"",
      type: `${clientData.sessionType} · ${clientData.date?fmtDate(clientData.date):"TBD"}`,
      amt: rawAmt?`${sym}${fmtMoney(rawAmt)}`:"TBD",
      status: "inquiry",
      deposit_pct: depPct,
      deposit_amt: depAmt,
      balance_amt: balAmt,
      project_date: clientData.date||null,
      questionnaire_questions: getDefaultQuestionnaire(clientData.vert||acct.vert),
    }).then(({data:dbClient,error})=>{
      if(dbClient?.id){
        const realId=dbClient.id;
        const portalToken=dbClient.portal_token||"";
        // Swap temp ID → real UUID in state
        setAcct(prev=>({...prev,clients:prev.clients.map(c=>
          c.id===tempId?{...c,id:realId,portalSlug:slug,portalToken}:c
        )}));
        // If modal is still showing the temp client, update it to the real ID
        setModal(prev => prev===tempId ? realId : prev);
        // Save timeline to DB
        dbReplaceTimeline(realId,timeline);
        // Flush any pending data changes that happened while client was temp
        // Read current client state and sync everything to DB
        setAcct(prev => {
          const current = prev.clients.find(cl => cl.id === realId);
          if(current) {
            // Sync notes, contact, intake if they were updated while temp
            const dbFields = {};
            if(current.freelancerNotes) dbFields.freelancer_notes = current.freelancerNotes;
            if(current.surpriseNotes) dbFields.surprise_notes = current.surpriseNotes;
            if(current.email) dbFields.email = current.email;
            if(current.phone) dbFields.phone = current.phone;
            if(Object.keys(dbFields).length > 0) dbUpdateClient(realId, dbFields);
            if(current.intakeSubmitted && current.intake && Object.keys(current.intake).length > 0) {
              dbUpsertIntake(realId, current.intake, true);
            }
          }
          return prev; // no state change, just side effects
        });
        return realId;
      }
    });
    return tempId;
  };

  const handleContactUpdate=(clientId,email,phone)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,email,phone}:c)}));
    if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{email,phone});
  };

  const handleTimelineUpdate=(clientId,newTl)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
      if(c.id!==clientId) return c;
      const pct=calcClientPct(newTl,c.status);
      const step=clientStepLabel(c.status,newTl);
      return {...c,tl:newTl,pct,step};
    })}));
    if(!String(clientId).startsWith("tmp_")) dbReplaceTimeline(clientId,newTl);
  };

  const handleAuditLog=(clientId,contractId,action,detail,actor)=>{
    const entry={
      workspace_id:acct.workspaceId,
      freelancer_id:acct.id,
      client_id:clientId||null,
      contract_id:contractId||null,
      action,
      detail:detail||"",
      actor:actor||"freelancer",
    };
    // Optimistic: add to local state
    const localEntry={...entry,id:"aud_"+Date.now(),created_at:new Date().toISOString()};
    setAcct(prev=>({...prev,auditLog:[localEntry,...(prev.auditLog||[])]}));
    dbInsertAuditLog(entry);
  };

  const handleContractSave=async (clientId,contractText,statusOverride)=>{
    const client=acct.clients.find(c=>c.id===clientId);
    const status=statusOverride||"draft";
    // "complete" marks a signed contract as completed
    if(status==="complete"){
      const existing=acct.contracts.find(c=>c.client_id===clientId&&!c.archived);
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,status:"complete",contractStage:"completed"}:c)}));
      dbUpdateClient(clientId,{status:"complete",contract_stage:"completed"});
      if(existing){
        setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===existing.id?{...c,status:"completed"}:c)}));
        dbUpdateContract(existing.id,{status:"completed"});
      }
      handleAuditLog(clientId,existing?.id||null,"completed","Contract marked as completed");
      return;
    }
    // Check if a contract already exists for this client
    const existingDraft=acct.contracts.find(c=>c.client_id===clientId&&!c.archived&&c.status==="draft");
    const existingActive=acct.contracts.find(c=>c.client_id===clientId&&!c.archived&&c.status!=="draft");
    if(existingDraft){
      // Update the existing draft — safe, doesn't touch sent/signed contracts
      setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===existingDraft.id?{...c,body:contractText,status:"draft"}:c)}));
      dbUpdateContract(existingDraft.id,{body:contractText,status:"draft"});
      handleAuditLog(clientId,existingDraft.id,"draft_saved","Contract draft saved");
      // Update client record to reflect draft
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,aiContract:contractText}:c)}));
      dbUpdateClient(clientId,{ai_contract:contractText});
    } else if(client) {
      // Create a NEW draft contract (never overwrite sent/signed contracts)
      const maxVer=acct.contracts.filter(c=>c.client_id===clientId).reduce((m,c)=>Math.max(m,c.version||1),0);
      const tempId=Date.now();
      const newContract={id:tempId,workspace_id:acct.workspaceId,freelancer_id:acct.id,client_id:clientId,name:client.name,type:client.type||"",val:client.amt||"",date:localToday(),vert:client.vert||"",body:contractText,status:"draft",archived:false,version:maxVer+1};
      setAcct(prev=>({...prev,contracts:[newContract,...prev.contracts]}));
      const {data}=await dbInsertContract({workspace_id:acct.workspaceId,freelancer_id:acct.id,client_id:clientId,client_name:client.name,type:client.type||"",value:client.amt||"",contract_date:localToday(),vert:client.vert||"",body:contractText,status:"draft",version:maxVer+1});
      if(data?.id){
        setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===tempId?{...c,id:data.id}:c)}));
        handleAuditLog(clientId,data.id,"created","Contract v"+(maxVer+1)+" created as draft");
      }
      // Update client record
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,aiContract:contractText,contractStage:existingActive?"draft":c.contractStage||"draft"}:c)}));
      if(!existingActive) dbUpdateClient(clientId,{ai_contract:contractText,contract_stage:"draft"});
    }
  };

  const handleContractSend=(clientId,contractText)=>{
    const client=acct.clients.find(c=>c.id===clientId);
    const today=localToday();
    const nowISO=new Date().toISOString();
    // Auto-append freelancer signature block when sending
    const freelancerSigBlock=`\n\n**Service Provider Signature**\n\nSigned by: **${acct.name}**\nDate: **${fmtDateLong(today)}**\n\nThis contract has been electronically signed by the service provider.`;
    const contractWithSig=contractText+freelancerSigBlock;
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
      if(c.id!==clientId) return c;
      const updatedTl=(c.tl||[]).map(s=>/contract\s*sent/i.test(s.l||"")&&s.s!=="done"?{...s,s:"done",d:today}:s);
      const hasContractSent=updatedTl.some(s=>/contract\s*sent/i.test(s.l||""));
      if(!hasContractSent){
        const firstPend=updatedTl.findIndex(s=>s.s==="pend");
        if(firstPend>0) updatedTl.splice(firstPend,0,{l:"Contract sent",d:today,s:"done"});
        else updatedTl.push({l:"Contract sent",d:today,s:"done"});
      }
      return {...c,aiContract:contractWithSig,contractSentToPortal:true,
        contractStage:"awaiting_signature",contractComments:null,
        status:["inquiry","pending"].includes(c.status)?"pending":c.status,
        tl:updatedTl};
    })}));
    dbUpdateClient(clientId,{
      ai_contract:contractWithSig,contract_sent_to_portal:true,
      contract_stage:"awaiting_signature",contract_comments:null,
      status:"pending",
    });
    const updatedClient=acct.clients.find(c=>c.id===clientId);
    if(updatedClient){
      const updatedTl=(updatedClient.tl||[]).map(s=>/contract\s*sent/i.test(s.l||"")&&s.s!=="done"?{...s,s:"done",d:today}:s);
      dbReplaceTimeline(clientId,updatedTl);
    }
    // Archive any previous sent/signed/completed (non-draft) contracts for this client
    const prevActive=acct.contracts.filter(c=>c.client_id===clientId&&!c.archived&&c.status!=="draft");
    // Find the current draft (if sending from a draft)
    const existingDraft=acct.contracts.find(c=>c.client_id===clientId&&!c.archived&&c.status==="draft");
    // Find existing awaiting/sent_back contract (resend case — update in place)
    const existingAwaiting=acct.contracts.find(c=>c.client_id===clientId&&!c.archived&&(c.status==="awaiting_signature"||c.status==="sent_back"));
    const isResend=!!existingAwaiting;
    if(isResend){
      // Resend: update the existing awaiting contract in place
      setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===existingAwaiting.id?{...c,body:contractWithSig,status:"awaiting_signature",date:today,sent_at:nowISO}:c)}));
      dbUpdateContract(existingAwaiting.id,{body:contractWithSig,status:"awaiting_signature",contract_date:today,sent_at:nowISO});
      handleAuditLog(clientId,existingAwaiting.id,"resubmitted","Contract resubmitted to portal");
      handleAuditLog(clientId,existingAwaiting.id,"signed","Contract signed by "+acct.name+" (service provider)","freelancer");
    } else {
      // New send: archive previous signed/completed contracts, then create or promote draft
      if(prevActive.length>0){
        const archiveIds=prevActive.map(c=>c.id);
        setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>archiveIds.includes(c.id)?{...c,archived:true,archived_at:nowISO}:c)}));
        archiveIds.forEach(id=>{
          dbUpdateContract(id,{archived:true,archived_at:nowISO});
          handleAuditLog(clientId,id,"archived","Previous contract archived (replaced by new version)");
        });
      }
      const maxVer=acct.contracts.filter(c=>c.client_id===clientId).reduce((m,c)=>Math.max(m,c.version||1),0);
      const newVer=prevActive.length>0?maxVer+1:Math.max(maxVer,1);
      if(existingDraft){
        // Promote draft to sent
        setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===existingDraft.id?{...c,body:contractWithSig,status:"awaiting_signature",date:today,sent_at:nowISO,version:newVer}:c)}));
        dbUpdateContract(existingDraft.id,{body:contractWithSig,status:"awaiting_signature",contract_date:today,sent_at:nowISO,version:newVer});
        handleAuditLog(clientId,existingDraft.id,"sent","Contract v"+newVer+" sent to portal for signature");
        handleAuditLog(clientId,existingDraft.id,"signed","Contract signed by "+acct.name+" (service provider)","freelancer");
      } else if(client) {
        const tempId=Date.now();
        const newContract={id:tempId,workspace_id:acct.workspaceId,freelancer_id:acct.id,client_id:clientId,name:client.name,type:client.type||"",val:client.amt||"",date:today,vert:client.vert||"",body:contractWithSig,status:"awaiting_signature",archived:false,version:newVer,sent_at:nowISO};
        setAcct(prev=>({...prev,contracts:[newContract,...prev.contracts]}));
        dbInsertContract({workspace_id:acct.workspaceId,freelancer_id:acct.id,client_id:clientId,client_name:client.name,type:client.type||"",value:client.amt||"",contract_date:today,vert:client.vert||"",body:contractWithSig,status:"awaiting_signature",version:newVer,sent_at:nowISO}).then(({data})=>{
          if(data?.id){
            setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===tempId?{...c,id:data.id}:c)}));
            handleAuditLog(clientId,data.id,"sent","Contract v"+newVer+" sent to portal for signature");
            handleAuditLog(clientId,data.id,"signed","Contract signed by "+acct.name+" (service provider)","freelancer");
          }
        });
      }
    }
  };

  const handleContractArchive=(contractId)=>{
    const contract=acct.contracts.find(c=>c.id===contractId);
    const nowISO=new Date().toISOString();
    setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>c.id===contractId?{...c,archived:true,archived_at:nowISO,status:"archived"}:c)}));
    dbUpdateContract(contractId,{archived:true,archived_at:nowISO,status:"archived"});
    handleAuditLog(contract?.client_id||null,contractId,"archived","Contract archived");
  };

  const handleContractDelete=(contractId)=>{
    const contract=acct.contracts.find(c=>c.id===contractId);
    if(!contract) return;
    // Signed/completed contracts can never be deleted — only archived
    if(contract.status==="signed"||contract.status==="completed"){
      handleContractArchive(contractId);
      return;
    }
    const clientId=contract.client_id;
    handleAuditLog(clientId||null,contractId,"deleted","Contract deleted");
    // Only clear client fields if this was the only non-archived contract for the client
    const otherContracts=acct.contracts.filter(c=>c.client_id===clientId&&c.id!==contractId&&!c.archived);
    setAcct(prev=>({...prev,
      contracts:prev.contracts.filter(c=>c.id!==contractId),
      clients:clientId&&otherContracts.length===0?prev.clients.map(c=>c.id===clientId?{...c,aiContract:null,contractStage:null,contractSentToPortal:false,contractPdfUrl:null,signedContract:null}:c):prev.clients
    }));
    dbDeleteContract(contractId);
    if(clientId&&otherContracts.length===0&&!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{ai_contract:null,contract_stage:null,contract_sent_to_portal:false,contract_pdf_url:null});
  };

  const handlePdfUpload=(clientId,pdfUrl)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,contractPdfUrl:pdfUrl}:c)}));
    if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{contract_pdf_url:pdfUrl});
  };

  const handleContractComments=(clientId,comments)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,contractComments:comments,contractStage:"sent_back"}:c)}));
    dbUpdateClient(clientId,{contract_comments:comments,contract_stage:"sent_back"});
    const existing=acct.contracts.find(c=>c.client_id===clientId);
    handleAuditLog(clientId,existing?.id||null,"sent_back",`Client sent back with comments: ${(comments||"").slice(0,100)}`,"client");
  };

  const handleContractSigned=(clientId,signerName,signedDateStr)=>{
    const today=signedDateStr||localToday();
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
      if(c.id!==clientId) return c;
      const original=c.aiContract||"";
      const sigBlock=`\n\n**Client Signature**\n\nSigned by: **${signerName||c.name}**\nDate: **${fmtDateLong(today)}**\n\nThis contract has been electronically signed and is legally binding.`;
      const signedContract=original+sigBlock;
      let updatedTl=(c.tl||[]).map(s=>/contract\s*signed/i.test(s.l||"")&&s.s!=="done"?{...s,s:"done",d:today}:s);
      updatedTl=updatedTl.map(s=>/contract\s*sent/i.test(s.l||"")&&s.s!=="done"?{...s,s:"done",d:today}:s);
      if(!updatedTl.some(s=>/contract\s*signed/i.test(s.l||""))){
        const depositIdx=updatedTl.findIndex(s=>/deposit/i.test(s.l||""));
        if(depositIdx>0) updatedTl.splice(depositIdx,0,{l:"Contract signed",d:today,s:"done"});
        else updatedTl.push({l:"Contract signed",d:today,s:"done"});
      }
      return {...c,contractStage:"signed",status:"signed",signedContract,signedBy:signerName||c.name,signedAt:today,tl:updatedTl};
    })}));
    const cl0=acct.clients.find(c=>c.id===clientId);
    const signedAiContract=(cl0?.aiContract||"")+`\n\n**Client Signature**\n\nSigned by: **${signerName||cl0?.name||""}**\nDate: **${fmtDateLong(today)}**\n\nThis contract has been electronically signed and is legally binding.`;
    dbUpdateClient(clientId,{contract_stage:"signed",status:"signed",ai_contract:signedAiContract});
    const cl=acct.clients.find(c=>c.id===clientId);
    if(cl){
      let updatedTl=(cl.tl||[]).map(s=>/contract\s*signed/i.test(s.l||"")&&s.s!=="done"?{...s,s:"done",d:today}:s);
      updatedTl=updatedTl.map(s=>/contract\s*sent/i.test(s.l||"")&&s.s!=="done"?{...s,s:"done",d:today}:s);
      dbReplaceTimeline(clientId,updatedTl);
    }
    // Update ALL non-archived contract entries for this client with signed status
    const clientContracts=acct.contracts.filter(c=>c.client_id===clientId&&!c.archived);
    if(clientContracts.length>0){
      const cl2=acct.clients.find(c2=>c2.id===clientId);
      const sigBlock=`\n\n**Client Signature**\n\nSigned by: **${signerName||clientContracts[0].name}**\nDate: **${fmtDateLong(today)}**\n\nThis contract has been electronically signed and is legally binding.`;
      const contractIds=new Set(clientContracts.map(c=>c.id));
      // Build snapshot: freeze freelancer's current cancellation policy + client financial terms
      const termsSnapshot={
        cancellation_policy: acct.cancellationPolicy||{},
        deposit_pct: cl2?.depositPct||50,
        amt: cl2?.amt||"",
        snapshot_at: new Date().toISOString(),
      };
      setAcct(prev=>({...prev,contracts:prev.contracts.map(c=>{
        if(!contractIds.has(c.id)) return c;
        const signedBody=(cl2?.aiContract||c.body||"")+sigBlock;
        return {...c,status:"signed",body:signedBody,signed_at:today,signed_by:signerName||cl2?.name||c.name,terms_snapshot:termsSnapshot};
      })}));
      clientContracts.forEach(c=>{
        const signedBody=(cl2?.aiContract||c.body||"")+sigBlock;
        dbUpdateContract(c.id,{status:"signed",body:signedBody,signed_at:today,signed_by:signerName||null,terms_snapshot:termsSnapshot});
        handleAuditLog(clientId,c.id,"signed",`Contract signed by ${signerName||"client"}`,"client");
      });
    }
  };

  const handlePaymentUpdate=(clientId,field,val)=>{
    // Handle portalPhoto separately — not a payment field
    if(field==="portalPhoto"){
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,portalPhoto:val}:c)}));
      if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{portal_photo:val});
      return;
    }
    // Handle reminderSentAt separately
    if(field==="reminderSentAt"){
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,reminderSentAt:val}:c)}));
      if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{reminder_sent_at:val});
      return;
    }
    // Handle status changes (e.g. cancellation resets)
    if(field==="status"){
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
        if(c.id!==clientId) return c;
        const updated={...c,status:val};
        updated.pct=calcClientPct(updated.tl,updated.status);
        updated.step=clientStepLabel(updated.status,updated.tl);
        return updated;
      })}));
      if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{status:val});
      return;
    }
    // Handle cancellationRequested flag
    if(field==="cancellationRequested"){
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,cancellationRequested:val,cancelledAt:val?new Date().toISOString():null}:c)}));
      if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{cancellation_requested:val,cancelled_at:val?new Date().toISOString():null});
      if(val) handleAuditLog(clientId,null,"cancellation_requested","Client requested cancellation","client");
      return;
    }
    // Handle refunds array — auto-archive cancelled clients after refund
    if(field==="refunds"){
      // Compute the new refund for audit (last item in the new array)
      const newRefund=Array.isArray(val)&&val.length>0?val[val.length-1]:null;
      setAcct(prev=>{
        const client=prev.clients.find(c=>c.id===clientId);
        const wasCancelled=client?.cancellationRequested;
        const hadNoRefunds=!(client?.refunds||[]).length;
        const nowHasRefund=Array.isArray(val)&&val.length>0;
        // Auto-archive if this is the first refund on a cancelled client
        if(wasCancelled&&hadNoRefunds&&nowHasRefund){
          const nowISO=new Date().toISOString();
          const preStatus=client?.status;
          const contractsToArchive=prev.contracts.filter(ct=>ct.client_id===clientId&&!ct.archived);
          if(!String(clientId).startsWith("tmp_")){
            dbUpdateClient(clientId,{refunds:val,archived:true,pre_archive_status:preStatus,status:"archived",archive_reason:"cancellation"});
            contractsToArchive.forEach(ct=>{
              dbUpdateContract(ct.id,{archived:true,archived_at:nowISO});
            });
          }
          return {...prev,
            clients:prev.clients.map(c=>c.id===clientId?{...c,refunds:val,archived:true,status:"archived",preArchiveStatus:preStatus,archiveReason:"cancellation"}:c),
            contracts:prev.contracts.map(ct=>ct.client_id===clientId&&!ct.archived?{...ct,archived:true,archived_at:nowISO}:ct),
          };
        }
        // Normal refund (non-cancelled client)
        if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{refunds:val});
        return {...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,refunds:val}:c)};
      });
      // Audit trail: log refund issuance + auto-archive
      if(newRefund){
        const isManual=newRefund.stripeStatus==="manual";
        handleAuditLog(clientId,null,"refund_issued",`${isManual?"Manual":"Stripe"} refund: ${newRefund.amount} — ${newRefund.reason}`);
        const client=acct?.clients?.find(c=>c.id===clientId);
        if(client?.cancellationRequested&&!(client?.refunds||[]).length){
          handleAuditLog(clientId,null,"auto_archived","Client auto-archived after cancellation refund");
        }
        // Send refund receipt for MANUAL refunds only. Stripe refunds get their email from the
        // stripe-refund edge function (fireRefundReceipt) using the DB refund row's UUID for
        // atomic idempotency; firing from here on the Stripe path would risk a double-send
        // because the client-side refund.id is the Stripe re_... ID, not the DB UUID.
        if(isManual&&client?.email){
          fetch(`${SUPABASE_URL}/functions/v1/send-refund-receipt`,{
            method:"POST",
            headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
            body:JSON.stringify({
              client_email:client.email,
              client_name:client.name,
              freelancer_name:acct.businessName||acct.name,
              original_payment:"Payment",
              amount:fmtMoney(newRefund.amount||0),
              currency_symbol:acct.currencySymbol||"$",
              date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
              reason:newRefund.reason||"",
            }),
          }).catch(e=>console.error("[Refund Receipt] manual send failed:",e));
        }
      }
      return;
    }
    // Handle invoices array. This branch fires for ANY replacement of the invoices array,
    // including the freelancer's "Mark paid" button (markInvoicePaid). We diff prev→new to
    // find invoices that just flipped from unpaid→paid and send a receipt for each. For the
    // Stripe-paid invoice flow the portal calls onPaymentUpdate with field="invoicePaid"
    // (separate branch below) so there's no double-send from this path.
    if(field==="invoices"){
      const prevClient=acct?.clients?.find(c=>c.id===clientId);
      const prevInvs=prevClient?.invoices||[];
      const newlyPaid=(val||[]).filter(inv=>{
        if(!inv||!inv.paid) return false;
        const prev=prevInvs.find(p=>String(p.id)===String(inv.id));
        return !prev?.paid;
      });
      setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,invoices:val}:c)}));
      if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{invoices:val});
      if(newlyPaid.length&&prevClient?.email){
        newlyPaid.forEach(inv=>{
          (async()=>{
            let paymentId=null;
            try{
              const{data:pRow}=await sb.from("payments")
                .select("id").eq("client_id",clientId).eq("type","invoice").eq("invoice_id",String(inv.id))
                .eq("status","succeeded").order("created_at",{ascending:false}).limit(1).maybeSingle();
              paymentId=pRow?.id||null;
            }catch(e){ /* non-fatal; fall through to unconditional send */ }
            fetch(`${SUPABASE_URL}/functions/v1/send-payment-receipt`,{
              method:"POST",
              headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
              body:JSON.stringify({
                client_email:prevClient.email,
                client_name:prevClient.name,
                freelancer_name:acct.businessName||acct.name,
                payment_type:"Invoice — "+(inv.description||"Additional charge"),
                amount:fmtMoney(inv.amount||0),
                currency_symbol:acct.currencySymbol||"$",
                date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
                payment_id:paymentId,
              }),
            }).catch(e=>console.error("[Receipt] manual invoice send failed:",e));
          })();
        });
      }
      return;
    }
    // Handle invoicePaid — invoices array sent by the portal after a successful invoice payment
    if(field==="invoicePaid"){
      const invoicesArr=val?.invoices||null;
      if(invoicesArr){
        setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,invoices:invoicesArr}:c)}));
        if(!String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,{invoices:invoicesArr});
      }
      // Send receipt for the paid invoice. When the invoice was Stripe-paid via the portal,
      // the webhook will have already sent & stamped payments.receipt_sent_at — passing
      // payment_id through makes the edge function's atomic claim a no-op on the second call.
      // When the freelancer marks the invoice paid manually (no Stripe row), we pass no
      // payment_id and the email sends unconditionally.
      const cl=acct?.clients?.find(c=>c.id===clientId);
      const paidInv=val?.invoiceId&&invoicesArr?invoicesArr.find(inv=>inv.id===val.invoiceId&&inv.paid):null;
      if(cl?.email&&paidInv){
        (async()=>{
          let paymentId=null;
          try{
            const{data:pRow}=await sb.from("payments")
              .select("id").eq("client_id",clientId).eq("type","invoice").eq("invoice_id",val.invoiceId)
              .eq("status","succeeded").order("created_at",{ascending:false}).limit(1).maybeSingle();
            paymentId=pRow?.id||null;
          }catch(e){ /* non-fatal; fall through to unconditional send */ }
          fetch(`${SUPABASE_URL}/functions/v1/send-payment-receipt`,{
            method:"POST",
            headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
            body:JSON.stringify({
              client_email:cl.email,
              client_name:cl.name,
              freelancer_name:acct.businessName||acct.name,
              payment_type:"Invoice — "+(paidInv.description||"Additional charge"),
              amount:fmtMoney(paidInv.amount||0),
              currency_symbol:acct.currencySymbol||"$",
              date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
              payment_id:paymentId,
            }),
          }).catch(e=>console.error("[Receipt] invoice send failed:",e));
        })();
      }
      return;
    }
    const today=localToday();
    // Shared helper to mark matching timeline steps as done
    const markDone=(tl,regex)=>(tl||[]).map(s=>{
      if(s.s==="done") return s;
      const lbl=(s.l||"").toLowerCase();
      return regex.test(lbl)?{...s,s:"done",d:today}:s;
    });
    let nextTl=null;
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
      if(c.id!==clientId) return c;
      const updated={...c,[field]:val};
      if(field==="depositPaid"&&val&&c.status==="signed") updated.status="active";
      if(field==="balancePaid"&&val) updated.status="delivered";
      if(field==="depositPaid"&&val){
        const isFullPay=(c.depositPct||50)>=100;
        // Full pay: match "Full payment due", "Payment due", or "Deposit due"
        // Partial: match "Deposit due" only (avoids touching "Final payment" / "Final balance")
        const regex=isFullPay?/(full\s*payment|^payment\s*due|deposit)/:/deposit/;
        updated.tl=markDone(c.tl,regex);
        updated.depositPaidDate=today;
      }
      if(field==="balancePaid"&&val){
        updated.tl=markDone(c.tl,/(balance|final\s*payment)/);
        updated.balancePaidDate=today;
      }
      // Undo path: if the freelancer unmarks payment, also revert the step to "pend"
      const undoRegex=field==="depositPaid"&&!val?((c.depositPct||50)>=100?/(full\s*payment|^payment\s*due|deposit)/:/deposit/):field==="balancePaid"&&!val?/(balance|final\s*payment)/:null;
      if(undoRegex){
        updated.tl=(c.tl||[]).map(s=>undoRegex.test((s.l||"").toLowerCase())?{...s,s:"pend",d:""}:s);
        if(field==="depositPaid") updated.depositPaidDate=null;
        if(field==="balancePaid") updated.balancePaidDate=null;
      }
      updated.pct=calcClientPct(updated.tl,updated.status);
      updated.step=clientStepLabel(updated.status,updated.tl);
      nextTl=updated.tl;
      return updated;
    })}));
    const dbUp={[field==="depositPaid"?"deposit_paid":"balance_paid"]:val};
    if(field==="depositPaid"&&val){ dbUp.deposit_paid_date=today; dbUp.status="active"; }
    if(field==="balancePaid"&&val){ dbUp.balance_paid_date=today; dbUp.status="delivered"; }
    if(field==="depositPaid"&&!val) dbUp.deposit_paid_date=null;
    if(field==="balancePaid"&&!val) dbUp.balance_paid_date=null;
    if(!String(clientId).startsWith("tmp_")){
      dbUpdateClient(clientId,dbUp);
      // Persist the updated timeline inline (no stale closure)
      if(nextTl) dbReplaceTimeline(clientId,nextTl);
    }
    // Send payment receipt email when a payment is confirmed (not on undo). For Stripe-paid
    // flows the webhook already sent & stamped payments.receipt_sent_at; we look up the row
    // and pass payment_id so the edge function's atomic claim no-ops the second call. For
    // freelancer manual-mark (no Stripe row), payment_id will be null and the email sends.
    if(val&&(field==="depositPaid"||field==="balancePaid")){
      const cl=acct?.clients?.find(c=>c.id===clientId);
      if(cl?.email){
        const rawAmt=parseFloat((cl.amt||"0").replace(/[^0-9.]/g,""))||0;
        const depPct=rawAmt<=0?100:(cl.depositPct||50);
        const isFullPay=depPct>=100;
        const depAmt=cl.depositAmt||round2(rawAmt*depPct/100);
        const balAmt=cl.balanceAmt||round2(rawAmt*(100-depPct)/100);
        const paymentType=field==="depositPaid"?(isFullPay?"Full Payment":"Deposit"):"Final Balance";
        const paymentAmt=field==="depositPaid"?fmtMoney(depAmt):fmtMoney(balAmt);
        const lookupType=field==="depositPaid"?"deposit":"balance";
        (async()=>{
          let paymentId=null;
          try{
            const{data:pRow}=await sb.from("payments")
              .select("id").eq("client_id",clientId).eq("type",lookupType)
              .eq("status","succeeded").order("created_at",{ascending:false}).limit(1).maybeSingle();
            paymentId=pRow?.id||null;
          }catch(e){ /* non-fatal; fall through to unconditional send */ }
          fetch(`${SUPABASE_URL}/functions/v1/send-payment-receipt`,{
            method:"POST",
            headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
            body:JSON.stringify({
              client_email:cl.email,
              client_name:cl.name,
              freelancer_name:acct.businessName||acct.name,
              payment_type:paymentType,
              amount:paymentAmt,
              currency_symbol:acct.currencySymbol||"$",
              date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
              payment_id:paymentId,
            }),
          }).catch(e=>console.error("[Receipt] send failed:",e));
        })();
      }
    }
  };

  const handleNotesUpdate=(clientId,notes,surpriseNotes)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
      if(c.id!==clientId) return c;
      const upd={...c};
      if(notes!==null&&notes!==undefined) upd.freelancerNotes=notes;
      if(surpriseNotes!==undefined) upd.surpriseNotes=surpriseNotes;
      return upd;
    })}));
    const dbUp={};
    if(notes!==null&&notes!==undefined) dbUp.freelancer_notes=notes;
    if(surpriseNotes!==undefined) dbUp.surprise_notes=surpriseNotes;
    if(Object.keys(dbUp).length>0 && !String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,dbUp);
  };

  const handleQuestionnaireUpdate=(clientId, questions)=>{
    // Save per-client questionnaire questions
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>
      c.id===clientId?{...c,questionnaireQuestions:questions}:c
    )}));
    // Persist to clients table
    if(!String(clientId).startsWith("tmp_") && !String(clientId).startsWith("draft_")){
      dbUpdateClient(clientId, { questionnaire_questions: questions });
    }
  };

  const handleResendQuestionnaire=(clientId)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,intakeSubmitted:false}:c)}));
    dbUpdateClient(clientId,{intake_submitted:false});
    // Also clear submitted_at in client_intake so it doesn't show as submitted on reload
    sb.from("client_intake").update({submitted_at:null,updated_at:new Date().toISOString()}).eq("client_id",clientId)._exec();
  };

  const handleStatusUpdate=(clientId,status)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>c.id===clientId?{...c,status}:c)}));
    dbUpdateClient(clientId,{status});
  };

  const handleMarkRead=(threadId)=>{
    // Clear unread count in local state immediately
    setAcct(prev=>({...prev,threads:prev.threads.map(t=>
      t.id===threadId?{...t,unread:0}:t
    )}));
    // Persist to DB
    sb.rpc("mark_thread_read",{p_thread_id:threadId}).catch(()=>{});
  };

  const handleDeleteClient=(clientId)=>{
    setAcct(prev=>({...prev,clients:prev.clients.filter(c=>c.id!==clientId)}));
    setModal(null);
    dbDeleteClient(clientId);
  };

  const handleArchiveClient=(clientId,reason)=>{
    const nowISO=new Date().toISOString();
    // Archive client + all their contracts in one state update, use prev for DB calls too
    setAcct(prev=>{
      const preStatus=prev.clients.find(c=>c.id===clientId)?.status;
      const contractsToArchive=prev.contracts.filter(c=>c.client_id===clientId&&!c.archived);
      // Persist to DB using latest state (not stale closure)
      dbUpdateClient(clientId,{archived:true,pre_archive_status:preStatus,status:"archived",archive_reason:reason||null,contract_stage:null,contract_sent_to_portal:false});
      contractsToArchive.forEach(c=>{
        dbUpdateContract(c.id,{archived:true,archived_at:nowISO,status:"archived"});
        handleAuditLog(clientId,c.id,"archived","Contract auto-archived (client archived)");
      });
      // Delete calendar entries for this client
      sb.from("calendar_entries").delete().eq("client_id",clientId)._exec();
      return {
        ...prev,
        clients:prev.clients.map(c=>c.id===clientId?{...c,preArchiveStatus:c.status,status:"archived",archived:true,archiveReason:reason||null,contractStage:null,contractSentToPortal:false}:c),
        contracts:prev.contracts.map(c=>c.client_id===clientId&&!c.archived?{...c,archived:true,archived_at:nowISO,status:"archived"}:c),
        calEntries:(prev.calEntries||[]).filter(e=>e.clientId!==clientId),
      };
    });
    setModal(null);
  };


  const handleClientEdit=(clientId,updates)=>{
    setAcct(prev=>({...prev,clients:prev.clients.map(c=>{
      if(c.id!==clientId) return c;
      const merged={...c,...updates};
      // Recompute progress + step label if timeline changed
      if(updates.tl){
        merged.pct=calcClientPct(updates.tl,merged.status);
        merged.step=clientStepLabel(merged.status,updates.tl);
      }
      return merged;
    })}));
    const dbUp={};
    if(updates.name) dbUp.name=updates.name;
    if(updates.ini) dbUp.ini=updates.ini;
    if(updates.type) dbUp.type=updates.type;
    if(updates.vert) dbUp.vert=updates.vert;
    if(updates.verts) dbUp.verts=updates.verts;
    if(updates.amt) dbUp.amt=updates.amt;
    if(updates.depositPct!==undefined) dbUp.deposit_pct=updates.depositPct;
    if(updates.depositAmt!==undefined) dbUp.deposit_amt=updates.depositAmt;
    if(updates.balanceAmt!==undefined) dbUp.balance_amt=updates.balanceAmt;
    if(Object.keys(dbUp).length>0 && !String(clientId).startsWith("tmp_")) dbUpdateClient(clientId,dbUp);
    // Persist timeline replacement if provided
    if(updates.tl && !String(clientId).startsWith("tmp_")) dbReplaceTimeline(clientId,updates.tl);
  };

  const handleThreadMessage=async (clientId,text)=>{
    const now=new Date();
    const ts=now.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
    const msg={f:"me",t:text,ts:`Today ${ts}`};
    // Optimistic update — update existing thread OR create new one locally
    setAcct(prev=>{
      const hasThread=prev.threads.some(t=>t.clientId===clientId);
      if(hasThread){
        return {...prev,threads:prev.threads.map(t=>
          t.clientId===clientId?{...t,pre:text,time:"Just now",msgs:[...(t.msgs||[]),msg]}:t
        )};
      }
      const client=prev.clients.find(cl=>cl.id===clientId);
      return {...prev,threads:[...prev.threads,{
        id:"tmp_"+clientId,clientId,
        name:client?.name||"Client",ini:client?.ini||"??",col:client?.col||"#1C4D44",
        unread:0,pre:text,time:"Just now",msgs:[msg],
      }]};
    });
    // Persist to DB — upsert thread then insert message
    try {
      const threadId = await dbUpsertThread(acct.workspaceId, acct.id, clientId);
      if(threadId){
        // Pass acct.id so the portal can attribute the message to whichever
        // team member sent it (not just the workspace owner).
        await dbInsertMessage(threadId, "freelancer", text, acct.id);
        // Swap tmp thread id for real DB id
        setAcct(prev=>({...prev,threads:prev.threads.map(t=>
          t.clientId===clientId&&String(t.id).startsWith("tmp_")?{...t,id:threadId}:t
        )}));
      }
    } catch(e){ console.warn("Thread message persist failed",e); }
  };

  const handlePortalMessage=async (clientId,text,threadId)=>{
    const now=new Date();
    const ts=now.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
    const msg={f:"them",t:text,ts:`Today ${ts}`};
    // Optimistic update — bumps unread count for freelancer
    setAcct(prev=>({...prev,threads:prev.threads.map(t=>
      t.clientId===clientId?{...t,pre:text,time:"Just now",unread:(t.unread||0)+1,msgs:[...(t.msgs||[]),msg]}:t
    )}));
    // Persist to DB via RPC (validates portal token server-side)
    try {
      const tid = threadId || acct.threads.find(t=>t.clientId===clientId)?.id;
      if(tid){
        await sb.rpc("portal_send_message",{
          p_thread_id: tid,
          p_client_id: clientId,
          p_portal_token: acct.clients.find(c=>c.id===clientId)?.portalToken||"",
          p_body: text,
        });
      } else {
        // Create thread first then send
        const newThreadId=await dbUpsertThread(acct.workspaceId,acct.id,clientId);
        if(newThreadId){
          await sb.rpc("portal_send_message",{
            p_thread_id:newThreadId,
            p_client_id:clientId,
            p_portal_token:acct.clients.find(c=>c.id===clientId)?.portalToken||"",
            p_body:text,
          });
        }
      }
    } catch(e){ console.warn("Portal message persist failed",e); }
  };




  // ── Admin action handlers ──
  const handleAdminBan=async(userId,banned,reason)=>{
    setAdminConfirm({title:banned?"Ban User":"Unban User",message:banned?"This user will be locked out of their account.":"This user will regain access to their account.",danger:banned,onConfirm:async()=>{
      try{await dbAdminBanUser(userId,banned,reason);await dbAdminInsertAuditLog({admin_id:acct.id,target_user_id:userId,action:banned?"ban":"unban",detail:reason||""});}catch(e){console.error(e);}
      setAdminConfirm(null);setAdminSelectedUser(null);loadAdminData();
    }});
  };
  const handleAdminSuspend=async(userId,suspended)=>{
    setAdminConfirm({title:suspended?"Suspend User":"Resume User",message:suspended?"This user will be temporarily suspended.":"This user will be reactivated.",danger:false,onConfirm:async()=>{
      try{await dbAdminSuspendUser(userId,suspended);await dbAdminInsertAuditLog({admin_id:acct.id,target_user_id:userId,action:suspended?"suspend":"resume",detail:""});}catch(e){console.error(e);}
      setAdminConfirm(null);setAdminSelectedUser(null);loadAdminData();
    }});
  };
  const handleAdminDelete=(userId)=>{
    setAdminConfirm({title:"Delete User",message:"This will permanently delete this user and all their data. This cannot be undone.",danger:true,onConfirm:async()=>{
      try{await dbAdminDeleteUser(userId);await dbAdminInsertAuditLog({admin_id:acct.id,target_user_id:userId,action:"delete",detail:"User permanently deleted"});}catch(e){console.error(e);}
      setAdminConfirm(null);setAdminSelectedUser(null);loadAdminData();
    }});
  };
  const handleAdminDeleteClient=async(clientId)=>{
    try{
      await dbAdminDeleteClient(clientId);
      await dbAdminInsertAuditLog({admin_id:acct.id,target_user_id:null,action:"delete",detail:"Client "+clientId+" deleted by admin"});
      loadAdminData();
    }catch(e){console.error("[Admin] delete client error:",e);}
  };
  const handleAdminUnarchiveClient=async(clientId,preArchiveStatus)=>{
    try{
      const restoredStatus=preArchiveStatus||"inquiry";
      await dbAdminUnarchiveClient(clientId,restoredStatus);
      await dbAdminInsertAuditLog({admin_id:acct.id,target_user_id:null,action:"unarchive",detail:"Client "+clientId+" unarchived by admin (restored to "+restoredStatus+")"});
      loadAdminData();
    }catch(e){console.error("[Admin] unarchive client error:",e);}
  };
  const handleAdminChangePlan=async(userId,newPlan)=>{
    try{
      await dbUpdateProfile(userId,{billing_plan:newPlan,cancelled_plan:null,plan_expiry_date:null,billing_start_date:newPlan==="free"?null:localToday()});
      await dbAdminInsertAuditLog({admin_id:acct.id,target_user_id:userId,action:"plan_change",detail:"Plan changed to "+newPlan+" by admin"});
      loadAdminData();
    }catch(e){console.error("[Admin] change plan error:",e);}
  };
  const handleAdminResetPassword=(userEmail)=>{
    setAdminConfirm({title:"Reset Password",message:`A password reset email will be sent to ${userEmail}.`,danger:false,onConfirm:async()=>{
      try{
        const res=await fetch(`${SUPABASE_URL}/auth/v1/recover`,{
          method:"POST",
          headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
          body:JSON.stringify({email:userEmail,redirect_to:window.location.origin})
        });
        if(!res.ok){const d=await res.json().catch(()=>({}));console.error("Reset password error:",d);}
      }catch(e){console.error(e);}
      setAdminConfirm(null);
    }});
  };
  const handleAdminSelectUser=async(user)=>{
    try{
      const{data}=await dbAdminGetUserDetail(user.id);
      // data = {profile:{...}, clients:[...], contracts:[...]}
      if(data&&data.profile){
        setAdminSelectedUser({profile:data.profile,clients:data.clients||[],contracts:data.contracts||[],id:user.id});
      } else {
        setAdminSelectedUser({profile:user,clients:[],contracts:[],id:user.id});
      }
    }catch(e){setAdminSelectedUser({profile:user,clients:[],contracts:[],id:user.id});}
  };

  // ── Plan enforcement: when a paid plan expires and user is back on free with >3 active clients,
  // lock all client cards and show an enforcement banner until they archive down to 3 or upgrade. ──
  // Drafts are incomplete WIP and don't count toward the limit.
  const freeActiveCount=acct?(acct.clients||[]).filter(c=>!c.archived&&!c._draft).length:0;
  // Plan-over-limit is a WORKSPACE-level constraint. Use effectivePlan so a
  // member viewing the owner's Studio workspace doesn't see "upgrade" prompts
  // based on their personal Free plan.
  const planOverLimit=acct&&effectivePlan(acct)==="free"&&freeActiveCount>3;
  const [showPlanLockBanner,setShowPlanLockBanner]=useState(false);

  // Guard function: replaces setModal for all onOpen callbacks
  const guardedOpenClient=(clientId)=>{
    if(planOverLimit){
      setShowPlanLockBanner(true);
      return;
    }
    setModal(clientId);
  };

  // Called by PortalGate after portal-verify-otp succeeds. Persists the
  // session, then fetches the full portal payload via get_portal_data_v2 so
  // we can render ClientPortal. If the data fetch fails (shouldn't, but
  // network), fall back to the gate so the user can retry.
  const handlePortalVerified = async ({clientId, sessionToken, expiresAt}) => {
    _setPortalSession(clientId, sessionToken, expiresAt);
    setPortalSessionToken(sessionToken);
    const g = portalGateData;
    if(!g?.freelancer_slug || !g?.client_slug){ setSc("login"); return; }
    setSc("portal-loading");
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_portal_data_v2`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
        body: JSON.stringify({ freelancer_slug: g.freelancer_slug, client_slug: g.client_slug, portal_token: g.portal_token, portal_session: sessionToken }),
      });
      const data = await r.json().catch(()=>null);
      if(data && !data.error && data.client && data.freelancer){
        setPortalData(data);
        setSc("portal-url");
        return;
      }
    } catch {}
    setSc("portal-gate");
  };

  return (
    <>
      <style>{S}</style>
      <div className={`app${isDesktop?" desktop":""}`}>
        {sc==="portal-loading"&&(
          <div style={{flex:1,width:"100%",minHeight:"100dvh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"var(--g)",padding:"24px 20px",boxSizing:"border-box"}}>
            <img src={BANNER_B64} alt="Lancer" style={{height:36,objectFit:"contain",mixBlendMode:"screen",marginBottom:28}}/>
            <div style={{textAlign:"center"}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600,color:"#fff",marginBottom:12}}>{portalData?.freelancer?.business_name||portalGateData?.business_name||"Lancer"}</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.5)"}}>Loading your portal…</div>
            </div>
          </div>
        )}
        {sc==="portal-gate"&&portalGateData&&(
          <PortalGate
            gateData={portalGateData}
            freelancerSlug={portalGateData.freelancer_slug}
            clientSlug={portalGateData.client_slug}
            portalToken={portalGateData.portal_token}
            onVerified={handlePortalVerified}
          />
        )}
        {sc==="portal-url"&&portalData&&(()=>{
          // Contract snapshotting: prefer the frozen cancellation policy from the most recent
          // signed contract so the client always sees the terms they originally agreed to,
          // even if the freelancer has since changed their live policy.
          // Include archived contracts — a signed v1 may be archived when v2 is sent,
          // but its snapshot should still apply until v2 is signed with a new snapshot.
          const _signedContracts=(portalData.contracts||[]).filter(ct=>ct.status==="signed"&&ct.terms_snapshot);
          _signedContracts.sort((a,b)=>new Date(b.signed_at||0)-new Date(a.signed_at||0));
          const _snapshotPolicy=_signedContracts[0]?.terms_snapshot?.cancellation_policy;
          const _effectivePolicy=_snapshotPolicy&&Object.keys(_snapshotPolicy).length>0?_snapshotPolicy:(portalData.freelancer.cancellation_policy||null);
          return(
          <ClientPortal
            c={{
              ...portalData.client,
              id: portalData.client.id,
              name: portalData.client.name,
              ini: portalData.client.ini,
              col: portalData.client.col,
              type: portalData.client.type,
              amt: portalData.client.amt,
              status: portalData.client.status,
              depositPaid: portalData.client.deposit_paid,
              balancePaid: portalData.client.balance_paid,
              depositAmt: portalData.client.deposit_amt,
              balanceAmt: portalData.client.balance_amt,
              depositPct: portalData.client.deposit_pct,
              aiContract: portalData.client.ai_contract,
              contractStage: portalData.client.contract_stage,
              contractSentToPortal: portalData.client.contract_sent_to_portal,
              contractPdfUrl: portalData.client.contract_pdf_url||null,
              intake: portalData.intake?.answers||{},
              intakeSubmitted: portalData.client.intake_submitted||false,
              tl: (portalData.timeline||[]).map(s=>({l:s.label||s.l, d:s.step_date||s.d||"", s:s.status||s.s||"pend"})),
              vert: portalData.client.vert||"",
              questionnaireQuestions: portalData.client.questionnaire_questions||[],
              portalSlug: portalData.client.portal_slug,
              portalToken: portalData.client.portal_token||"",
              projectDate: portalData.client.project_date,
              archived: portalData.client.archived||false,
              portalPhoto: portalData.client.portal_photo||null,
              reminderSentAt: portalData.client.reminder_sent_at||null,
              invoices: portalData.invoices||[],
              cancellationPolicy: _effectivePolicy,
              // Pre-seed messages from the RPC result. _sk is an ISO sort key used
              // by the portal to merge optimistic client sends with server state
              // and to guarantee correct chronological ordering after every poll.
              // _read carries the server-side read_by_client flag so the unread
              // badge survives page refresh (it's derived from server state, not
              // a local counter). _mid carries the server-assigned message UUID
              // which the sync effect uses to drop optimistic pending copies
              // once the server has echoed them back — without this, the same
              // message appears twice (optimistic + server echo).
              _portalMsgs: (portalData.messages||[]).map(m=>({_mid:m.id,f:m.from_role==="freelancer"?"them":"client",t:m.body,ts:new Date(m.sent_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),_sk:m.sent_at,_read:!!m.read_by_client,_sentBy:m.sent_by_name||null})),
              // Contract version history from RPC
              _portalContracts: (portalData.contracts||[]),
            }}
            acct={{
              ...profileToAcct(portalData.freelancer,[],[]),
              name: portalData.freelancer.name,
              email: portalData.freelancer.email||"",
              phone: portalData.freelancer.phone||"",
              slug: portalData.freelancer.slug||"",
              currencySymbol: portalData.freelancer.currency_symbol||"£",
              businessName: portalData.freelancer.business_name||"",
              tagline: portalData.freelancer.tagline||"",
              footerText: portalData.freelancer.footer_text||"",
              studioTheme: portalData.freelancer.studio_theme||null,
              questionnaireTemplate: (portalData.freelancer.questionnaire_template&&portalData.freelancer.questionnaire_template.length>0)?portalData.freelancer.questionnaire_template:null,
              cancellationPolicy: _effectivePolicy,
              stripeConnect: portalData.stripe?.charges_enabled ? {
                accountId: portalData.stripe.stripe_account_id,
                chargesEnabled: portalData.stripe.charges_enabled,
                payoutsEnabled: portalData.stripe.payouts_enabled,
              } : null,
            }}
            onBack={()=>{ window.history.pushState({},"","/"); setSc("login"); }}
            onIntakeSubmit={(clientId,data)=>{
              // Gated v2: validates session_token + portal_token server-side.
              const token=portalData?.client?.portal_token;
              const sess=portalSessionToken||_getPortalSession(clientId)?.token;
              if(!sess) return;
              fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_submit_intake_v2`,{
                method:"POST",
                headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_answers:data})
              });
            }}
            onContractComments={(clientId,comments)=>{
              const token=portalData?.client?.portal_token;
              const sess=portalSessionToken||_getPortalSession(clientId)?.token;
              if(!sess) return;
              fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_contract_comments_v2`,{
                method:"POST",
                headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_comments:comments})
              });
            }}
            onPaymentUpdate={(clientId,field,val)=>{
              const token=portalData?.client?.portal_token;
              const sess=portalSessionToken||_getPortalSession(clientId)?.token;
              if(!sess) return;
              if(field==="portalPhoto"){
                fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_update_photo_v2`,{
                  method:"POST",
                  headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                  body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_photo:val})
                });
                return;
              }
              if(field==="invoicePaid"){
                // val = { id: invoiceId, invoices: updatedArray }
                const invoiceId=val?.id;
                if(!invoiceId) return;
                fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_mark_payment_v2`,{
                  method:"POST",
                  headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                  body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_payment_type:"invoice",p_invoice_id:String(invoiceId)})
                });
                // Optimistically update local portal data so the UI reflects the paid state
                // get_portal_data returns invoices at the top level (derived from clients.invoices jsonb)
                if(val?.invoices) setPortalData(prev=>prev?{...prev,invoices:val.invoices,client:{...prev.client,invoices:val.invoices}}:prev);
                return;
              }
              const payType=field==="depositPaid"?"deposit":"balance";
              fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_mark_payment_v2`,{
                method:"POST",
                headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_payment_type:payType})
              });
            }}
            onPortalMessage={(clientId,text)=>{
              // Returns a Promise<{id, thread_id, ok} | null> so the child can
              // attach the server-assigned id to its optimistic copy and the
              // sync/dedup path can drop the pending once the server echoes
              // the same id back through polling.
              const threadId=portalData?.thread_id;
              const token=portalData?.client?.portal_token;
              const sess=portalSessionToken||_getPortalSession(clientId)?.token;
              if(!token||!sess) return Promise.resolve(null);
              return fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_send_message_v2`,{
                method:"POST",
                headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                body:JSON.stringify({p_session_token:sess,p_thread_id:threadId||null,p_client_id:clientId,p_portal_token:token,p_body:text})
              }).then(r=>r.json()).catch(()=>null);
            }}
            onContractSigned={(clientId,signerName,signedDate)=>{
              const token=portalData?.client?.portal_token;
              const sess=portalSessionToken||_getPortalSession(clientId)?.token;
              if(!sess) return;
              fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_sign_contract_v2`,{
                method:"POST",
                headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_signer_name:signerName||null,p_signed_at:signedDate||null})
              });
              // Note: don't call handleContractSigned here — acct is null in portal mode
              // The RPC saves to DB, and ClientPortal's local setSigned(true) handles the UI
            }}
            onContactUpdate={(clientId,email,phone)=>{
              const token=portalData?.client?.portal_token;
              const sess=portalSessionToken||_getPortalSession(clientId)?.token;
              if(token&&sess){
                fetch(`${SUPABASE_URL}/rest/v1/rpc/portal_update_contact_v2`,{
                  method:"POST",
                  headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY},
                  body:JSON.stringify({p_session_token:sess,p_client_id:clientId,p_portal_token:token,p_email:email||"",p_phone:phone||""})
                });
              }
            }}
          />);
        })()}
        {sc==="login"   &&<Login
          onSignIn={async (userOrAcct, em, pw)=>{
            // Load profile from DB
            const loaded = await loadAcctFromDB(userOrAcct);
            if(loaded){
              // Stamp last_login_at
              try{ dbUpdateProfile(loaded.id,{last_login_at:new Date().toISOString()}); }catch(e){}
              setAcct(loaded);
              // Safety net: onboarding may have half-completed (upsert silently
              // failed). If country was never saved, send them back through
              // onboarding so they re-pick instead of defaulting to USD/US.
              if(!loaded.country){ setSc("onboard"); return; }
              if(isAdmin(loaded)){ document.body.classList.add("admin"); setSc("admin"); loadAdminData(); }
              else{ setSc("app"); }
            } else { setAcct(profileToAcct({id:userOrAcct.id,email:userOrAcct.email,name:userOrAcct.user_metadata?.name||""})); setSc("onboard"); }
          }}
          onGoSignUp={()=>setSc("signup")}
        />}
        {sc==="signup"  &&<SignUp
          onNext={(em,pw,nm)=>{setEmail(em);setPendingPw(pw);setPendingName(nm);setSc("verify");}}
          onGoSignIn={()=>setSc("login")}
        />}
        {sc==="verify"  &&<Verify email={email} password={pendingPw} onDone={(authData)=>{ if(authData?.access_token){ _accessToken=authData.access_token; if(authData.refresh_token) _refreshToken=authData.refresh_token; _saveTokens(); } setSc("onboard"); }} onBack={()=>setSc("signup")}/>}
        {sc==="onboard" &&<Onboarding onDone={async (data)=>{
          const country=COUNTRIES.find(c=>c.code===data.country)||{code:"US",name:"United States",flag:"🇺🇸",currency:"USD",symbol:"$"};
          const vertsArr=data.verts||[data.vert];
          const name=pendingName||email.split("@")[0].replace(/[._]/g," ").replace(/\b\w/g,l=>l.toUpperCase());
          // NOTE: Do NOT compute a slug here. The handle_new_user() trigger on
          // auth.users INSERT already assigned a guaranteed-unique slug (with a
          // random suffix on collision). Sending our own slug in the upsert
          // below re-introduces the un-suffixed base form, which can collide
          // with an existing user's slug and fail the profiles_slug_key unique
          // constraint — surfacing as "We couldn't save your account details".
          // Normalize customRole to array form for display string
          const customRoleArr=Array.isArray(data.customRole)?data.customRole:(data.customRole?[data.customRole]:[]);
          const customRoleDisplay=customRoleArr.join(" & ");
          const role=vertsArr.map(vid=>vid==="custom"?customRoleDisplay:VERTS.find(v=>v.id===vid)?.title).filter(Boolean).join(" & ");

          // ── Resolve an authenticated user.id before touching the DB ──
          // Without this, the upsert below goes through but RLS rejects it,
          // leaving a trigger-created profile row with country=NULL. Then
          // profileToAcct's currency fallback ("USD") makes it look like the
          // account reverted to US on re-login.
          let user = null;
          try {
            const { data: ud } = await sb.auth.getUser();
            user = ud?.user;
          } catch(e){}
          if(!user && pendingPw) {
            try {
              const { data: sd } = await sb.auth.signInWithPassword(email, pendingPw);
              user = sd?.user;
            } catch(e){}
          }
          if(!user?.id) {
            // Token likely stale after signup/verify — refresh once and retry
            try { await _refreshSession(); const { data: ud2 } = await sb.auth.getUser(); user = ud2?.user; } catch(e){}
          }
          if(!user?.id) {
            console.error("[Onboarding] No authenticated user — routing to login");
            setSc("login");
            return;
          }

          // Read existing profile so we don't clobber name / billing_plan /
          // questionnaire_template when an existing user re-enters onboarding
          // (e.g. Slice B-2 routes country-less profiles back here, and we
          // shouldn't reset their paid plan to "free" or wipe their name).
          let existingProfile = null;
          try { const { data: ep } = await dbGetProfile(user.id); existingProfile = ep || null; } catch(e){}

          const profileData = {
            id: user.id,
            // Prefer existing name; fall back to derived name only if empty.
            name: (existingProfile?.name && existingProfile.name.trim()) ? existingProfile.name : name,
            email: user.email||email,
            ini: ((existingProfile?.name||name)||"").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
            // slug intentionally omitted — trigger-assigned, unique-safe.
            vert: data.vert||"custom",
            verts: vertsArr,
            custom_role: customRoleArr.length>0?JSON.stringify(customRoleArr):null,
            role,
            country: country.code,
            currency: country.currency,
            currency_symbol: country.symbol,
            // Preserve existing billing plan; only default to "free" for genuinely new users.
            billing_plan: existingProfile?.billing_plan || "free",
            // Preserve existing questionnaire template if it has content.
            questionnaire_template: (existingProfile?.questionnaire_template && existingProfile.questionnaire_template.length > 0)
              ? existingProfile.questionnaire_template
              : [],
          };

          // ── Persist with error-check + one retry.
          // dbUpsertProfile returns {data,error}; it NEVER throws on HTTP/RLS
          // failures, so try/catch alone was silently hiding bad writes.
          let saveErr = null;
          const r1 = await dbUpsertProfile(user.id, profileData);
          if(r1?.error) {
            try { await _refreshSession(); } catch(e){}
            const r2 = await dbUpsertProfile(user.id, profileData);
            saveErr = r2?.error || null;
          }
          if(saveErr) {
            console.error("[Onboarding] Failed to save profile:", saveErr);
            // Don't proceed into the app with an unsaved country — the next
            // login would appear to "revert" everything to default (USD/US).
            // tryAutoLogin has a safety-net redirect back here on missing country.
            alert("We couldn't save your account details. Please check your connection and try again.");
            return;
          }

          // ── Verify the write actually landed with the country we chose,
          // and grab the server-authoritative row so our local state picks up
          // the trigger-assigned slug (which we deliberately didn't overwrite).
          let savedProfile = null;
          try {
            const { data: saved } = await dbGetProfile(user.id);
            if(saved) {
              savedProfile = saved;
              if(saved.country !== country.code) {
                await dbUpdateProfile(user.id, { country: country.code, currency: country.currency, currency_symbol: country.symbol });
                savedProfile = { ...saved, country: country.code, currency: country.currency, currency_symbol: country.symbol };
              }
            }
          } catch(e){}

          // Merge: prefer server row (has real slug + any trigger-applied defaults),
          // fall back to profileData (what we just wrote) for anything missing.
          const freshAcct = profileToAcct(savedProfile || profileData, [], []);
          setAcct(freshAcct);
          setPendingPw(""); setPendingName("");
          setSc("app");
        }}/>}
        {sc==="admin"&&acct&&<>
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",position:"relative"}}>
            <div style={{display:adminNav==="admin-home"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminHome metrics={adminMetrics} users={adminUsers} activity={adminActivity} onNavChange={setAdminNav} isDesktop={isDesktop}/></div>
            <div style={{display:adminNav==="admin-users"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminUsers users={adminUsers} onSelectUser={handleAdminSelectUser}/></div>
            <div style={{display:adminNav==="admin-support"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminSupport/></div>
            <div style={{display:adminNav==="admin-metrics"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminMetrics metrics={adminMetrics} users={adminUsers}/></div>
            <div style={{display:adminNav==="admin-links"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminLinks/></div>
            <div style={{display:adminNav==="admin-ops"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminOps auditLog={adminAuditLog} activity={adminActivity}/></div>
            <div style={{display:adminNav==="admin-settings"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><AdminSettings acct={acct} onAcctUpdate={handleAcctUpdate} onSignOut={signOut}/></div>
          </div>
          <div className="bnav">
            {isDesktop&&<div className="bnav-logo">Lancer <span>Admin</span></div>}
            <div className="bnav-tabs">
              {ADMIN_NAVS.map(n=>(
                <div key={n.id} className={`ni${adminNav===n.id?" on":""}`} onClick={()=>setAdminNav(n.id)}>
                  <div className="nicon">{n.icon}</div>
                  <div className="nlbl" style={n.id==="admin-home"?{fontWeight:700,fontSize:12}:undefined}>{n.label}</div>
                </div>
              ))}
            </div>
            <div className="bnav-footer">Lancer by MMetrix<div style={{fontSize:9,fontWeight:500,opacity:.7,marginTop:2,letterSpacing:".04em"}}>v{APP_VERSION}</div></div>
          </div>
          {adminSelectedUser&&<AdminUserDetail user={adminSelectedUser} onBack={()=>setAdminSelectedUser(null)} onBan={handleAdminBan} onSuspend={handleAdminSuspend} onDelete={handleAdminDelete} onResetPassword={handleAdminResetPassword} onDeleteClient={handleAdminDeleteClient} onUnarchiveClient={handleAdminUnarchiveClient} onChangePlan={handleAdminChangePlan}/>}
          {adminConfirm&&<AdminConfirmModal title={adminConfirm.title} message={adminConfirm.message} danger={adminConfirm.danger} onConfirm={adminConfirm.onConfirm} onCancel={()=>setAdminConfirm(null)}/>}
        </>}
        {sc==="app"&&acct&&<>
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",position:"relative"}}>
              <div style={{display:nav==="home"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><PullToRefresh onRefresh={handleRefresh}><Home acct={acct} onOpen={guardedOpenClient} onSignOut={signOut} onNavChange={setNav} onAcctUpdate={handleAcctUpdate} onDeleteAccount={handleDeleteAccount} planOverLimit={planOverLimit}/></PullToRefresh></div>
              <div style={{display:nav==="clients"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><PullToRefresh onRefresh={handleRefresh}><ClientsTab acct={acct} onOpen={guardedOpenClient} onAddClient={id=>{if(planOverLimit){setShowPlanLockBanner(true);return;}const newId=handleAddClient(id);if(newId&&!String(newId).startsWith("draft_"))guardedOpenClient(newId);}} onAcctUpdate={handleAcctUpdate} onSignOut={signOut} onDeleteAccount={handleDeleteAccount} planOverLimit={planOverLimit} onNavChange={setNav}/></PullToRefresh></div>
              <div style={{display:nav==="calendar"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><PullToRefresh onRefresh={handleRefresh}><CalTab acct={acct} onOpen={guardedOpenClient} onAddCalEntry={handleAddCalEntry} onDeleteCalEntry={handleDeleteCalEntry}/></PullToRefresh></div>
              <div style={{display:nav==="inbox"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><PullToRefresh onRefresh={handleRefresh}><InboxTab acct={acct} onThreadMessage={handleThreadMessage} onMarkRead={handleMarkRead} isDesktop={isDesktop}/></PullToRefresh></div>
              <div style={{display:nav==="contracts"?"flex":"none",flexDirection:"column",flex:1,overflow:"hidden"}}><PullToRefresh onRefresh={handleRefresh}><ContractsTab acct={acct} onContractSave={handleContractSave} onContractSend={handleContractSend} onContractArchive={handleContractArchive} onContractDelete={handleContractDelete} onPdfUpload={handlePdfUpload}/></PullToRefresh></div>
            </div>
            <div className="bnav">
              {isDesktop&&<div className="bnav-logo">Lancer</div>}
              <div className="bnav-tabs">
                {(()=>{
                  const archivedClientIds=new Set((acct.clients||[]).filter(c=>c.archived).map(c=>c.id));
                  const inboxUnread=acct.threads.filter(t=>t.unread>0&&!archivedClientIds.has(t.clientId)).length;
                  return NAVS.map(n=>(
                    <div key={n.id} className={`ni${nav===n.id?" on":""}`} onClick={()=>setNav(n.id)}>
                      <div className="nicon" style={{position:"relative"}}>
                        {n.icon}
                        {n.id==="inbox"&&inboxUnread>0&&(
                          <div style={{position:"absolute",top:-2,right:-4,width:9,height:9,borderRadius:"50%",background:"#E5484D",border:"2px solid var(--cr)",flexShrink:0}}/>
                        )}
                      </div>
                      <div className="nlbl" style={n.id==="home"?{fontWeight:700,fontSize:12}:undefined}>{n.label}</div>
                    </div>
                  ));
                })()}
              </div>
              <div className="bnav-footer">Lancer by MMetrix<div style={{fontSize:9,fontWeight:500,opacity:.7,marginTop:2,letterSpacing:".04em"}}>v{APP_VERSION}</div></div>
            </div>
            {modal&&<ClientModal id={modal} acct={acct} onClose={()=>setModal(null)} onPortal={()=>{}} onIntakeSubmit={handleIntakeSubmit} onContactUpdate={handleContactUpdate} onTimelineUpdate={handleTimelineUpdate} onPaymentUpdate={handlePaymentUpdate} onNotesUpdate={handleNotesUpdate} onStatusUpdate={handleStatusUpdate} onThreadMessage={handleThreadMessage} onQuestionnaireUpdate={handleQuestionnaireUpdate} onResendQuestionnaire={handleResendQuestionnaire} onDeleteClient={handleDeleteClient} onArchiveClient={handleArchiveClient} onClientEdit={handleClientEdit} onNavChange={setNav}/>}
            {/* Plan enforcement lock banner — auto-hides when enough clients are archived */}
            {showPlanLockBanner&&planOverLimit&&(()=>{
              const lockActiveClients=(acct.clients||[]).filter(c=>!c.archived);
              const lockNeedToArchive=lockActiveClients.length-3;
              return(
              <div onClick={()=>setShowPlanLockBanner(false)} style={{position:"fixed",inset:0,zIndex:900,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
                <div onClick={e=>e.stopPropagation()} style={{background:"var(--cr)",borderRadius:20,padding:28,width:"100%",maxWidth:420,maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
                  <div style={{textAlign:"center",marginBottom:12,color:"var(--mu)"}}>{I.lock}</div>
                  <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:"var(--ink)",textAlign:"center",marginBottom:8}}>Clients Locked</div>
                  <div style={{fontSize:13,color:"var(--mu)",lineHeight:1.6,textAlign:"center",marginBottom:16}}>
                    You have <strong style={{color:"var(--ink)"}}>{lockActiveClients.length} active clients</strong> but the Free plan allows 3. Archive {lockNeedToArchive>0?lockNeedToArchive:1} client{lockNeedToArchive>1?"s":""} to unlock.
                  </div>
                  <div style={{flex:1,overflowY:"auto",marginBottom:16,display:"flex",flexDirection:"column",gap:6,scrollbarWidth:"none"}}>
                    {lockActiveClients.map(cl=>(
                      <div key={cl.id} style={{display:"flex",alignItems:"center",gap:10,background:"var(--card)",border:"1px solid var(--bo)",borderRadius:12,padding:"10px 12px"}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:cl.col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>{cl.portalPhoto?<img src={cl.portalPhoto} alt={cl.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:cl.ini}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cl.name}</div>
                          <div style={{fontSize:11,color:"var(--mu)"}}>{cl.type?.split("·")[0]?.trim()||"Client"}</div>
                        </div>
                        <button onClick={()=>{handleArchiveClient(cl.id,"plan_downgrade");}} style={{background:"var(--err-bg)",color:"var(--err-tx)",border:"1px solid var(--err-bo)",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                          Archive
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:8,flexShrink:0}}>
                    <button onClick={()=>{setShowPlanLockBanner(false);}} style={{width:"100%",background:"var(--gl)",color:"var(--gm)",border:"none",borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>);
            })()}
        </>}
      </div>
    </>
  );
}
